// Сгенерировано из shared/openapi/openapi.json (@hey-api/openapi-ts) — руками не править.
// Перегенерировать: make -C admin api-generate; проверка рассинхрона: make -C admin api-check.

export type ClientOptions = {
    baseUrl: `${string}://${string}` | (string & {});
};

/**
 * AcceptedOut
 *
 * `202`: событие для SRV (`exp.booking.reminder_due`) записано; доставку выполняет SRV.
 */
export type AcceptedOut = {
    /**
     * Booking Id
     */
    booking_id: string;
    /**
     * Queued
     */
    queued: boolean;
    /**
     * Delivery
     *
     * `srv_events` — пуш и письмо отправляет модуль уведомлений
     */
    delivery: string;
};

/**
 * AccuracyKind
 */
export const AccuracyKind = {
    Calibration: 'calibration',
    BlindOnline: 'blind_online',
    BlindOffline: 'blind_offline'
} as const;

/**
 * AccuracyKind
 */
export type AccuracyKind = typeof AccuracyKind[keyof typeof AccuracyKind];

/**
 * AchievementOut
 */
export type AchievementOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Condition
     */
    condition: string;
    /**
     * Target
     */
    target: number;
    /**
     * Progress
     */
    progress: number;
    /**
     * Awarded At
     */
    awarded_at: string | null;
    /**
     * Progress Label
     */
    progress_label: string;
    /**
     * Toast
     */
    toast: string;
};

/**
 * AchievementsOut
 */
export type AchievementsOut = {
    /**
     * Items
     */
    items: Array<AchievementOut>;
    /**
     * Awarded
     */
    awarded: number;
    /**
     * Total
     */
    total: number;
    /**
     * Header
     */
    header: string;
};

/**
 * ActionOut
 */
export type ActionOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Reason
     */
    reason?: string | null;
    /**
     * Fields
     */
    fields?: Array<FieldOut>;
};

/**
 * ActionResultOut
 */
export type ActionResultOut = {
    /**
     * Ok
     */
    ok: boolean;
    /**
     * Message
     */
    message: string;
    /**
     * Data
     */
    data?: {
        [key: string]: unknown;
    } | null;
};

/**
 * ActivityGroupOut
 */
export type ActivityGroupOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Items
     */
    items: Array<ActivityItemOut>;
};

/**
 * ActivityItemOut
 */
export type ActivityItemOut = {
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
    link: SectionLinkOut | null;
};

/**
 * ActivityModule
 *
 * Модуль-источник записи «Истории действий» (4.3.23; значения — lower_snake_case).
 */
export const ActivityModule = {
    Acc: 'acc',
    Ord: 'ord',
    Lrn: 'lrn',
    Gam: 'gam',
    Tst: 'tst',
    Exp: 'exp',
    Cnt: 'cnt'
} as const;

/**
 * ActivityModule
 *
 * Модуль-источник записи «Истории действий» (4.3.23; значения — lower_snake_case).
 */
export type ActivityModule = typeof ActivityModule[keyof typeof ActivityModule];

/**
 * AdminAccountOut
 */
export type AdminAccountOut = {
    /**
     * Country Editable
     */
    country_editable: boolean;
    /**
     * Deletion Sla Days
     */
    deletion_sla_days: number;
};

/**
 * AdminAuthOut
 */
export type AdminAuthOut = {
    /**
     * Providers
     */
    providers: Array<AuthProviderCode>;
};

/**
 * AdminBrandOut
 */
export type AdminBrandOut = {
    /**
     * Name
     */
    name: string;
    bonus_name: LocalizedText;
    /**
     * Logo Url
     *
     * Логотип платформы для экрана входа; null — не загружен
     */
    logo_url?: string | null;
};

/**
 * AdminCancelIn
 */
export type AdminCancelIn = {
    reason: CancelReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * AdminCatalogOut
 */
export type AdminCatalogOut = {
    /**
     * Delivery Options
     */
    delivery_options: Array<DeliveryOption>;
    /**
     * Sales Channels
     */
    sales_channels: Array<SalesChannel>;
    channel_hint: LocalizedText;
};

/**
 * AdminCityOut
 */
export type AdminCityOut = {
    name: LocalizedText;
    /**
     * Lat
     */
    lat: number;
    /**
     * Lng
     */
    lng: number;
};

/**
 * AdminCommerceOut
 */
export type AdminCommerceOut = {
    wine_sales_mode: WineSalesMode;
    /**
     * Partner Code
     */
    partner_code: string | null;
};

/**
 * AdminConfigOut
 */
export type AdminConfigOut = {
    /**
     * Market Code
     */
    market_code: string;
    brand: AdminBrandOut;
    /**
     * Languages
     */
    languages: Array<Language>;
    default_language: Language;
    currency: Currency;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Admin Timezones
     */
    admin_timezones: Array<string>;
    /**
     * Platforms
     */
    platforms: Array<ClientPlatform>;
    /**
     * Age Min
     */
    age_min: number;
    commerce: AdminCommerceOut;
    shipping: AdminShippingOut;
    maps: AdminMapsOut;
    geo: AdminGeoOut;
    auth: AdminAuthOut;
    payments: AdminPaymentsOut;
    video: AdminVideoOut;
    content: AdminContentOut;
    catalog: AdminCatalogOut;
    exp: AdminExpOut;
    gam: AdminGamOut;
    account: AdminAccountOut;
    support: AdminSupportOut;
    /**
     * Platform Launched At
     */
    platform_launched_at: string | null;
    /**
     * Legal Documents Base Url
     */
    legal_documents_base_url: string;
    /**
     * Web Base Url
     */
    web_base_url: string;
};

/**
 * AdminContentOut
 */
export type AdminContentOut = {
    /**
     * Translation
     *
     * Автоперевод контента en↔es (D13)
     */
    translation: boolean;
};

/**
 * AdminExpOut
 */
export type AdminExpOut = {
    /**
     * Event Timezones
     */
    event_timezones: Array<EventTimezoneOut>;
};

/**
 * AdminGamOut
 */
export type AdminGamOut = {
    /**
     * Learning Discount Channels
     */
    learning_discount_channels: Array<ClientPlatform>;
    /**
     * Wine Discount Applies
     */
    wine_discount_applies: boolean;
};

/**
 * AdminGeoOut
 */
export type AdminGeoOut = {
    /**
     * Default Country
     *
     * ISO alpha-2 страны по умолчанию (`geo.default_country`)
     */
    default_country: string;
    /**
     * Точка по умолчанию для центра карты (`geo.default_city`)
     */
    default_city: AdminCityOut;
};

/**
 * AdminLoginIn
 */
export type AdminLoginIn = {
    /**
     * Email
     *
     * Почта — логин
     */
    email: string;
    /**
     * Password
     */
    password: string;
};

/**
 * AdminLoginOut
 */
export type AdminLoginOut = {
    tokens: TokensOut;
    me: AdminMeOut;
};

/**
 * AdminMapsOut
 *
 * Карты в админке (контур и центр региона, координаты винодельни; BR-GEO-64).
 */
export type AdminMapsOut = {
    provider: MapProvider;
    /**
     * Fallback Tiles
     *
     * Цепочка источников тайлов при сбое провайдера (`maps.fallback_tiles`), по порядку; атрибуция выводится всегда
     */
    fallback_tiles: Array<TileSource>;
    /**
     * Tile Error Threshold
     *
     * Сколько ошибок тайлов подряд до переключения на следующий источник
     */
    tile_error_threshold: number;
    /**
     * Js Api Key
     *
     * Ключ JS API карт админки (Яндекс JS API 3 / Google Maps JavaScript API) — только у включённой и настроенной интеграции карт с действующей лицензией (17.8.2, BR-INT-SVC-28); иначе null — подложка из цепочки фолбэка
     */
    js_api_key?: string | null;
    /**
     * Map Id
     *
     * Google Map ID (стиль карты)
     */
    map_id?: string | null;
    /**
     * Geocoder Available
     *
     * «Найти по адресу» / «Определить адрес» доступны (`/admin/geo*`, 17.8.6)
     */
    geocoder_available?: boolean;
    /**
     * Suggest Available
     *
     * Подсказки адреса (`/admin/geo/suggest`) доступны: RU — при ключе Геосаджеста, World — Places API (New)
     */
    suggest_available?: boolean;
};

/**
 * AdminMeOut
 */
export type AdminMeOut = {
    user: AdminUserOut;
    member: AdminMemberOut;
    /**
     * Nav
     */
    nav: Array<NavItemOut>;
    scope: ScopeOut;
};

/**
 * AdminMePatchIn
 *
 * `{admin_locale?, phone?}`. Отсутствующее поле не меняется; `phone: null` очищает телефон;
 * `admin_locale: null` недопустим (колонка обязательна).
 */
export type AdminMePatchIn = {
    admin_locale?: Language | null;
    /**
     * Phone
     */
    phone?: string | null;
};

/**
 * AdminMemberOut
 */
export type AdminMemberOut = {
    /**
     * Id
     */
    id: string;
    role: TeamRole;
    /**
     * Role Label
     *
     * Название роли на языке админки
     */
    role_label: string;
    status: TeamMemberStatus;
    /**
     * Phone
     */
    phone: string | null;
    admin_locale: Language;
    /**
     * Registered At
     */
    registered_at: string;
    /**
     * Last Login At
     */
    last_login_at: string | null;
};

/**
 * AdminPaymentsOut
 */
export type AdminPaymentsOut = {
    /**
     * Gateway
     *
     * Допустимые шлюзы; несколько — активный выбирается в настройках интеграций
     */
    gateway: Array<PaymentGateway>;
    /**
     * Methods
     */
    methods: Array<PaymentMethod>;
    min_amount: Money;
};

/**
 * AdminRefreshOut
 */
export type AdminRefreshOut = {
    tokens: TokensOut;
};

/**
 * AdminReviewOut
 */
export type AdminReviewOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Author Name
     */
    author_name: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Edited At
     */
    edited_at: string | null;
    /**
     * Format Title
     */
    format_title: string;
    /**
     * Rating
     */
    rating: number;
    /**
     * Text
     */
    text: string;
};

/**
 * AdminReviewSummaryOut
 */
export type AdminReviewSummaryOut = {
    /**
     * Avg
     */
    avg: number | null;
    /**
     * Count
     */
    count: number;
    /**
     * Below 4
     *
     * Оценок ниже 4
     */
    below_4: number;
};

/**
 * AdminReviewsOut
 */
export type AdminReviewsOut = {
    summary: AdminReviewSummaryOut;
    /**
     * Items
     */
    items: Array<AdminReviewOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * AdminShippingOut
 */
export type AdminShippingOut = {
    carrier: ShippingCarrier;
    destination_scope: DestinationScope;
    /**
     * Pickup Enabled
     */
    pickup_enabled: boolean;
    /**
     * Age Check On Delivery
     */
    age_check_on_delivery: boolean;
    cost_to_buyer: ShippingCostToBuyer;
};

/**
 * AdminSupportOut
 */
export type AdminSupportOut = {
    /**
     * Email
     */
    email: string;
};

/**
 * AdminUserOut
 */
export type AdminUserOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Email
     */
    email: string;
    /**
     * Full Name
     */
    full_name: string | null;
    /**
     * Avatar Url
     */
    avatar_url: string | null;
    locale: Language;
    phone: PhoneOut | null;
    /**
     * Password Changed At
     */
    password_changed_at: string | null;
    /**
     * Password Is Temporary
     */
    password_is_temporary: boolean;
};

/**
 * AdminVideoOut
 */
export type AdminVideoOut = {
    provider: VideoProvider;
    /**
     * Max Upload Gb
     */
    max_upload_gb: number;
    /**
     * Playback Rates
     */
    playback_rates: Array<number>;
};

/**
 * AgeGateIn
 */
export type AgeGateIn = {
    /**
     * Answer
     */
    answer: 'adult' | 'underage';
};

/**
 * AgeGateOut
 */
export type AgeGateOut = {
    /**
     * Age Confirmed
     */
    age_confirmed: boolean;
    /**
     * Underage Locked
     */
    underage_locked: boolean;
    /**
     * Changes Left
     *
     * Сколько раз ещё можно изменить ответ «Мне меньше 18»
     */
    changes_left: number;
};

/**
 * AgingPotential
 */
export const AgingPotential = {
    DrinkNow: 'drink_now',
    UpTo3Y: 'up_to_3y',
    Y35: 'y3_5',
    Y510: 'y5_10',
    Y10Plus: 'y10_plus'
} as const;

/**
 * AgingPotential
 */
export type AgingPotential = typeof AgingPotential[keyof typeof AgingPotential];

/**
 * AlertCan
 */
export type AlertCan = {
    /**
     * Resolve
     */
    resolve?: boolean;
};

/**
 * AlertIn
 */
export type AlertIn = {
    kind: BookingTarget;
    /**
     * Region Id
     */
    region_id?: string | null;
};

/**
 * AlertOut
 */
export type AlertOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Integration Key
     */
    integration_key: string;
    /**
     * Integration Title
     */
    integration_title: string;
    /**
     * Type
     */
    type: string;
    /**
     * Type Label
     */
    type_label: string;
    severity: AlertSeverity;
    /**
     * Severity Label
     */
    severity_label: string;
    /**
     * Message
     */
    message: string;
    /**
     * First Seen At
     */
    first_seen_at: string;
    /**
     * Last Seen At
     */
    last_seen_at: string;
    /**
     * Occurrences
     */
    occurrences: number;
    status: AlertStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Resolved At
     */
    resolved_at: string | null;
    /**
     * Resolved By
     */
    resolved_by: string | null;
    /**
     * Resolve Comment
     */
    resolve_comment: string | null;
    can: AlertCan;
};

/**
 * AlertResolveIn
 */
export type AlertResolveIn = {
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * AlertSeverity
 */
export const AlertSeverity = { Critical: 'critical', Warning: 'warning' } as const;

/**
 * AlertSeverity
 */
export type AlertSeverity = typeof AlertSeverity[keyof typeof AlertSeverity];

/**
 * AlertStatus
 */
export const AlertStatus = { Open: 'open', Resolved: 'resolved' } as const;

/**
 * AlertStatus
 */
export type AlertStatus = typeof AlertStatus[keyof typeof AlertStatus];

/**
 * AlertsOut
 */
export type AlertsOut = {
    /**
     * Items
     */
    items: Array<ExpAlertOut>;
};

/**
 * AnswerIn
 */
export type AnswerIn = {
    /**
     * Question Id
     */
    question_id: string;
    /**
     * Option Ids
     */
    option_ids: Array<string>;
};

/**
 * AnswerOut
 */
export type AnswerOut = {
    /**
     * Is Correct
     */
    is_correct: boolean;
    /**
     * Correct Option Ids
     */
    correct_option_ids: Array<string>;
    /**
     * Correct Count
     */
    correct_count: number;
    /**
     * Answered Count
     */
    answered_count: number;
    /**
     * Label
     *
     * «Верно» / «Неверно — правильный ответ отмечен зелёным»
     */
    label: string;
};

/**
 * AnswerType
 */
export const AnswerType = { Single: 'single', Multiple: 'multiple' } as const;

/**
 * AnswerType
 */
export type AnswerType = typeof AnswerType[keyof typeof AnswerType];

/**
 * AppAccountOut
 */
export type AppAccountOut = {
    /**
     * Country Editable
     */
    country_editable: boolean;
    /**
     * Country Note
     */
    country_note: string | null;
};

/**
 * AppBrandOut
 */
export type AppBrandOut = {
    /**
     * Name
     */
    name: string;
    /**
     * Bonus Name
     */
    bonus_name: string;
    /**
     * Logo Url
     *
     * Логотип платформы (раздел админки `brand`); null — не загружен
     */
    logo_url?: string | null;
};

/**
 * AppCommerceOut
 */
export type AppCommerceOut = {
    wine_sales_mode: WineSalesMode;
    /**
     * Partner Name
     *
     * Имя партнёра по вину — `settings.display_name` интеграции партнёра (INT-DLV). null — режим platform или интеграция ещё не настроена
     */
    partner_name: string | null;
};

/**
 * AppConfigOut
 */
export type AppConfigOut = {
    /**
     * Market Code
     */
    market_code: string;
    brand: AppBrandOut;
    locale: AppLocaleOut;
    currency: Currency;
    /**
     * Auth Providers
     *
     * Включённые и настроенные способы входа в порядке `auth.providers` (17.7.1)
     */
    auth_providers: Array<AuthProviderCode>;
    /**
     * Age Min
     */
    age_min: number;
    commerce: AppCommerceOut;
    account: AppAccountOut;
    support: AppSupportOut;
    market_info: AppMarketInfoOut;
    market: AppMarketSchemasAppMarketOut;
    features: AppFeaturesOut;
    /**
     * Публичные параметры включённых интеграций (BR-INT-08): client_id входа, ключи карт, VAPID — то же, что `GET /api/v1/app/integrations/client-config`
     */
    integrations: ClientIntegrationsOut;
    /**
     * Min Client Version
     */
    min_client_version: {
        [key in ClientPlatform]?: string;
    };
    maps: AppMapsOut;
    geo: AppGeoOut;
    video: AppVideoOut;
    search: AppSearchOut;
    links: AppLinksOut;
    push: AppPushOut;
    passport: AppPassportOut;
};

/**
 * AppCountryListOut
 */
export type AppCountryListOut = {
    /**
     * Items
     */
    items: Array<AppCountryOut>;
};

/**
 * AppCountryOut
 */
export type AppCountryOut = {
    /**
     * Code
     */
    code: string;
    /**
     * Name
     */
    name: string;
    /**
     * Sort Order
     */
    sort_order: number;
    /**
     * Picture Url
     */
    picture_url: string | null;
    /**
     * Regions Count
     *
     * Опубликованные регионы
     */
    regions_count: number;
    /**
     * Is Live
     *
     * Есть опубликованные регионы; иначе — «скоро»
     */
    is_live: boolean;
};

/**
 * AppDictionaryCountriesOut
 */
export type AppDictionaryCountriesOut = {
    /**
     * Items
     */
    items: Array<AppDictionaryCountryOut>;
};

/**
 * AppDictionaryCountryOut
 *
 * Страна приложения (ACC 4.8.2 `GET /dictionaries/countries`).
 */
export type AppDictionaryCountryOut = {
    /**
     * Code
     *
     * ISO 3166-1 alpha-2, верхний регистр (`User.country_code`)
     */
    code: string;
    /**
     * Name
     */
    name: string;
    /**
     * active — можно выбрать; soon — «скоро»
     */
    status: AppMarketStatus;
    /**
     * Currency
     *
     * Валюта цен инсталляции
     */
    currency: string;
    /**
     * Phone Code
     *
     * Телефонный код страны
     */
    phone_code: string | null;
};

/**
 * AppFeaturesOut
 *
 * ARCH-11 `features`: параметры, от которых строится интерфейс клиента.
 */
export type AppFeaturesOut = {
    wine_sales_mode: WineSalesMode;
    shipping_carrier: ShippingCarrier;
    /**
     * Pickup Enabled
     */
    pickup_enabled: boolean;
    /**
     * Recognition Enabled
     *
     * Распознавание этикеток доступно: `recognition.enabled` рынка и интеграция FastCork включена и настроена (BR-INT-SVC-59)
     */
    recognition_enabled: boolean;
    /**
     * Content Translation
     */
    content_translation: boolean;
    /**
     * Pwa Enabled
     */
    pwa_enabled: boolean;
    /**
     * Learning Discount Channels
     */
    learning_discount_channels: Array<ClientPlatform>;
    /**
     * Wine Discount Applies
     */
    wine_discount_applies: boolean;
    /**
     * Payment Methods
     */
    payment_methods: Array<PaymentMethod>;
    /**
     * Digital Billing
     */
    digital_billing: {
        [key in ClientPlatform]?: DigitalBillingChannel;
    };
};

/**
 * AppGeoOut
 */
export type AppGeoOut = {
    /**
     * You Here Radius Km
     */
    you_here_radius_km: number;
    /**
     * Position Max Age Min
     */
    position_max_age_min: number;
    /**
     * Location Ttl Hours
     */
    location_ttl_hours: number;
};

/**
 * AppLinksOut
 */
export type AppLinksOut = {
    /**
     * Web Base Url
     */
    web_base_url: string;
    /**
     * App Web Base Url
     */
    app_web_base_url: string;
    /**
     * Legal Documents Base Url
     */
    legal_documents_base_url: string;
    /**
     * Account Deletion Web Url
     */
    account_deletion_web_url: string;
    /**
     * Return Scheme App
     */
    return_scheme_app: string;
};

/**
 * AppLocaleOut
 */
export type AppLocaleOut = {
    /**
     * Languages
     */
    languages: Array<Language>;
    default: Language;
    /**
     * Язык ответа (по Accept-Language)
     */
    current: Language;
};

/**
 * AppMapsOut
 */
export type AppMapsOut = {
    provider: MapProvider;
    /**
     * Fallback Tiles
     */
    fallback_tiles: Array<TileSource>;
    /**
     * Tile Error Threshold
     */
    tile_error_threshold: number;
};

/**
 * AppMarketCan
 */
export type AppMarketCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Set Status
     */
    set_status?: boolean;
    /**
     * Deactivate
     *
     * Можно перевести в «Скоро» (страна по умолчанию всегда активна)
     */
    deactivate?: boolean;
};

/**
 * AppMarketCreateIn
 */
export type AppMarketCreateIn = {
    /**
     * Country Code
     *
     * Код страны из системного справочника, любой регистр
     */
    country_code: string;
    status?: AppMarketStatus;
    /**
     * Sort Order
     */
    sort_order?: number | null;
    /**
     * Note
     */
    note?: string | null;
};

/**
 * AppMarketInfoOut
 */
export type AppMarketInfoOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Rows
     */
    rows: Array<MarketInfoRowOut>;
    /**
     * Summary
     */
    summary: string;
};

/**
 * AppMarketPatchIn
 */
export type AppMarketPatchIn = {
    /**
     * Sort Order
     */
    sort_order?: number | null;
    /**
     * Note
     */
    note?: string | null;
};

/**
 * AppMarketStatus
 *
 * Страна приложения (ACC 4.8.2 `status`): `active` — доступна для выбора, `soon` — «скоро».
 */
export const AppMarketStatus = { Active: 'active', Soon: 'soon' } as const;

/**
 * AppMarketStatus
 *
 * Страна приложения (ACC 4.8.2 `status`): `active` — доступна для выбора, `soon` — «скоро».
 */
export type AppMarketStatus = typeof AppMarketStatus[keyof typeof AppMarketStatus];

/**
 * AppMarketStatusIn
 */
export type AppMarketStatusIn = {
    status: AppMarketStatus;
};

/**
 * AppMedia
 */
export type AppMedia = {
    /**
     * Url
     */
    url: string;
    kind: MediaKind;
    placement: MediaPlacement;
    /**
     * Caption
     */
    caption?: string | null;
};

/**
 * AppPair
 */
export type AppPair = {
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * AppPassportOut
 */
export type AppPassportOut = {
    /**
     * Share Targets
     */
    share_targets: Array<ShareTarget>;
};

/**
 * AppPushOut
 */
export type AppPushOut = {
    /**
     * Providers
     */
    providers: Array<PushProvider>;
    /**
     * Web Push Ios Min Version
     */
    web_push_ios_min_version: string | null;
};

/**
 * AppRefreshIn
 */
export type AppRefreshIn = {
    /**
     * Refresh Token
     *
     * Непрозрачный токен `rt_…`
     */
    refresh_token: string;
};

/**
 * AppRefreshOut
 */
export type AppRefreshOut = {
    tokens: TokensOut;
};

/**
 * AppSearchOut
 */
export type AppSearchOut = {
    /**
     * Popular Queries
     */
    popular_queries: Array<string>;
};

/**
 * AppStep
 */
export type AppStep = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
};

/**
 * AppSupportOut
 */
export type AppSupportOut = {
    /**
     * Email
     */
    email: string;
    hours: SupportHoursOut;
    /**
     * Hours Text
     */
    hours_text: string;
};

/**
 * AppVideoOut
 */
export type AppVideoOut = {
    /**
     * Playback Rates
     */
    playback_rates: Array<number>;
    /**
     * Progress Heartbeat Sec
     */
    progress_heartbeat_sec: number;
    /**
     * Complete Threshold
     */
    complete_threshold: number;
};

/**
 * AppleUserIn
 *
 * Имя и почта, которые Apple передаёт только при первой авторизации (BR-INT-SVC-25).
 */
export type AppleUserIn = {
    /**
     * First Name
     */
    first_name?: string | null;
    /**
     * Last Name
     */
    last_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
};

/**
 * ApplicantRole
 */
export const ApplicantRole = {
    Sommelier: 'sommelier',
    Winemaker: 'winemaker',
    Critic: 'critic',
    Teacher: 'teacher',
    Writer: 'writer'
} as const;

/**
 * ApplicantRole
 */
export type ApplicantRole = typeof ApplicantRole[keyof typeof ApplicantRole];

/**
 * ApplicationActionsOut
 */
export type ApplicationActionsOut = {
    /**
     * Withdraw
     */
    withdraw: boolean;
    /**
     * Edit
     *
     * «Изменить заявку» при `needs_info` (OQ-CNT-07)
     */
    edit: boolean;
    /**
     * Reapply
     *
     * «Подать заново» — через 30 дней после отказа
     */
    reapply: boolean;
    /**
     * Reapply From
     */
    reapply_from: string | null;
    /**
     * Open Studio
     */
    open_studio: boolean;
};

/**
 * ApplicationAdminOut
 */
export type ApplicationAdminOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Member Id
     */
    member_id: string;
    /**
     * User Id
     */
    user_id: string;
    /**
     * Email
     */
    email: string | null;
    status: ExpertApplicationStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Status Line
     */
    status_line: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * City
     */
    city: string;
    /**
     * Applicant Role
     */
    applicant_role: string;
    /**
     * Applicant Role Label
     */
    applicant_role_label: string;
    /**
     * Experience
     */
    experience: string;
    /**
     * Experience Label
     */
    experience_label: string;
    /**
     * Specializations
     */
    specializations: Array<string>;
    /**
     * Specializations Label
     */
    specializations_label: string;
    /**
     * Qualifications
     */
    qualifications: Array<string>;
    /**
     * Qualifications Label
     */
    qualifications_label: string;
    /**
     * Formats
     */
    formats: Array<string>;
    /**
     * Formats Label
     */
    formats_label: string;
    diploma: ContentRefOut | null;
    /**
     * Links
     */
    links: string | null;
    /**
     * About
     */
    about: string;
    /**
     * Author Terms Label
     */
    author_terms_label: string | null;
    /**
     * Passport Snapshot
     */
    passport_snapshot: string | null;
    /**
     * Submitted At
     */
    submitted_at: string;
    /**
     * Decided At
     */
    decided_at: string | null;
    /**
     * Decided By
     */
    decided_by: string | null;
    /**
     * Decision Comment
     */
    decision_comment: string | null;
    /**
     * Can Decide
     */
    can_decide: boolean;
};

/**
 * ApplicationDecisionIn
 */
export type ApplicationDecisionIn = {
    /**
     * Decision
     */
    decision: 'approve' | 'needs_info' | 'reject';
    /**
     * Comment
     *
     * Обязателен для «Запросить уточнения» и «Отклонить»
     */
    comment?: string | null;
};

/**
 * ApplicationDecisionOut
 */
export type ApplicationDecisionOut = {
    /**
     * Id
     */
    id: string;
    status: ExpertApplicationStatus;
    /**
     * Member Status
     */
    member_status: string;
    /**
     * Decided At
     */
    decided_at: string | null;
    /**
     * Decided By
     */
    decided_by: string | null;
    /**
     * Status Line
     */
    status_line: string;
    /**
     * Toast
     */
    toast: string;
};

/**
 * ApplicationIn
 */
export type ApplicationIn = {
    /**
     * Full Name
     */
    full_name: string;
    /**
     * City
     */
    city: string;
    applicant_role: ApplicantRole;
    experience: ExpertExperience;
    /**
     * Specializations
     */
    specializations: Array<ExpertSpecialization>;
    /**
     * Qualifications
     */
    qualifications: Array<ExpertQualification>;
    /**
     * Diploma Media Id
     */
    diploma_media_id?: string | null;
    /**
     * Diploma File Name
     */
    diploma_file_name?: string | null;
    /**
     * Links
     */
    links?: string | null;
    /**
     * Formats
     */
    formats: Array<ExpertFormat>;
    /**
     * About
     */
    about: string;
    /**
     * Author Terms Version Id
     */
    author_terms_version_id?: string | null;
};

/**
 * ApplicationOut
 */
export type ApplicationOut = {
    /**
     * Id
     */
    id: string;
    status: ExpertApplicationStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    /**
     * Submitted At
     */
    submitted_at: string;
    /**
     * Submitted Label
     */
    submitted_label: string;
    /**
     * Decided At
     */
    decided_at: string | null;
    /**
     * Comment
     */
    comment: string | null;
    /**
     * Steps
     */
    steps: Array<ContentStepOut>;
    /**
     * Summary
     */
    summary: Array<SummaryRowOut>;
    actions: ApplicationActionsOut;
    /**
     * Form
     *
     * Анкета — для «Изменить заявку»
     */
    form: {
        [key: string]: unknown;
    };
    /**
     * Toast
     */
    toast?: string | null;
};

/**
 * ApplicationStateOut
 */
export type ApplicationStateOut = {
    application: ApplicationOut | null;
    gate: ContentGateOut;
};

/**
 * ApproveIn
 */
export type ApproveIn = {
    room_type: RoomKind;
    agreed_amount?: Money | null;
    /**
     * Agreed Date From
     */
    agreed_date_from?: string | null;
    /**
     * Agreed Date To
     */
    agreed_date_to?: string | null;
};

/**
 * AromaGroup
 */
export const AromaGroup = {
    Berries: 'berries',
    Citrus: 'citrus',
    OtherFruit: 'other_fruit',
    Flowers: 'flowers',
    Herbs: 'herbs',
    Spices: 'spices',
    OakVanilla: 'oak_vanilla',
    Smoky: 'smoky',
    Mineral: 'mineral',
    Earthy: 'earthy',
    Other: 'other'
} as const;

/**
 * AromaGroup
 */
export type AromaGroup = typeof AromaGroup[keyof typeof AromaGroup];

/**
 * AttachIn
 */
export type AttachIn = {
    /**
     * Target Type
     */
    target_type: 'set' | 'tasting' | 'lesson' | 'course' | 'publication';
    /**
     * Target Id
     */
    target_id: string;
};

/**
 * AttachmentOut
 */
export type AttachmentOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Url
     *
     * Подписанная ссылка на 15 минут (персональный файл)
     */
    url: string | null;
    /**
     * Content Type
     */
    content_type: string | null;
};

/**
 * AttemptOptionOut
 */
export type AttemptOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Text
     */
    text: string;
};

/**
 * AttemptQuestionOut
 */
export type AttemptQuestionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Text
     */
    text: string;
    answer_type: AnswerType;
    /**
     * Topic
     */
    topic: string | null;
    /**
     * Options
     */
    options: Array<AttemptOptionOut>;
};

/**
 * AttemptStartIn
 */
export type AttemptStartIn = {
    /**
     * Lesson Id
     */
    lesson_id?: string | null;
    /**
     * Country Code
     */
    country_code?: string | null;
};

/**
 * AttemptStartOut
 */
export type AttemptStartOut = {
    /**
     * Attempt Id
     */
    attempt_id: string;
    /**
     * Test Id
     */
    test_id: string;
    kind: TestKind;
    /**
     * Title
     */
    title: string;
    /**
     * Header
     */
    header: string;
    /**
     * Questions
     */
    questions: Array<AttemptQuestionOut>;
    /**
     * Total
     */
    total: number;
    /**
     * Xp
     *
     * XP-монета: сумма за результат без ошибок (GAM)
     */
    xp: number | null;
    /**
     * Xp Received
     *
     * XP за этот тест / страну / проверку уже получены
     */
    xp_received: boolean;
};

/**
 * AttendanceRow
 */
export type AttendanceRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Booking Number
     */
    booking_number: string;
    target: TargetOut;
    host: RefOut | null;
    winery: RefOut | null;
    region: RefOut | null;
    /**
     * Kind Label
     */
    kind_label: string | null;
    /**
     * Format Label
     */
    format_label: string | null;
    /**
     * Purpose Label
     */
    purpose_label: string | null;
    user: RefOut;
    /**
     * People
     */
    people: number;
    /**
     * Attended At
     */
    attended_at: string;
};

/**
 * AuditActorRole
 */
export const AuditActorRole = {
    Admin: 'admin',
    Winemaker: 'winemaker',
    Expert: 'expert',
    System: 'system',
    Anonymous: 'anonymous'
} as const;

/**
 * AuditActorRole
 */
export type AuditActorRole = typeof AuditActorRole[keyof typeof AuditActorRole];

/**
 * AuditActorType
 */
export const AuditActorType = {
    TeamMember: 'team_member',
    System: 'system',
    Anonymous: 'anonymous'
} as const;

/**
 * AuditActorType
 */
export type AuditActorType = typeof AuditActorType[keyof typeof AuditActorType];

/**
 * AuditCardOut
 */
export type AuditCardOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     *
     * Время действия (UTC); админка показывает в своём поясе
     */
    occurred_at: string;
    actor_type: AuditActorType;
    /**
     * Actor Id
     */
    actor_id: string | null;
    /**
     * Actor Name
     *
     * ФИО на момент действия или «Система»
     */
    actor_name: string;
    actor_role: AuditActorRole;
    /**
     * Actor Role Label
     *
     * Администратор / Винодел / Эксперт / Система
     */
    actor_role_label: string;
    category: AuditCategory;
    /**
     * Category Label
     */
    category_label: string;
    /**
     * Action
     *
     * Код действия, например `moderation.decided`
     */
    action: string;
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Entity Type
     */
    entity_type: string | null;
    /**
     * Entity Type Label
     */
    entity_type_label: string | null;
    /**
     * Entity Id
     */
    entity_id: string | null;
    /**
     * Entity Label
     */
    entity_label: string | null;
    /**
     * Entity
     *
     * «{Тип} · {название или id}»; «—», если сущности нет
     */
    entity: string;
    /**
     * Summary
     *
     * «Было → стало» (BR-SRV-62)
     */
    summary: string;
    /**
     * Reason
     *
     * Причина / комментарий
     */
    reason: string | null;
    /**
     * Ip
     */
    ip: string | null;
    /**
     * User Agent
     *
     * Устройство (user agent)
     */
    user_agent: string | null;
    /**
     * Session Id
     */
    session_id: string | null;
    /**
     * Trace Id
     *
     * Идентификатор запроса
     */
    trace_id: string | null;
    /**
     * Changes
     */
    changes: Array<AuditChangeOut>;
    /**
     * Карточка участника команды
     */
    actor_link: ModerationLinkOut | null;
    /**
     * «Открыть запись» — если раздел известен и запись существует
     */
    entity_link: ModerationLinkOut | null;
};

/**
 * AuditCategory
 *
 * BR-SRV-63: категория — по коду действия, для быстрых фильтров реестра.
 */
export const AuditCategory = {
    Status: 'status',
    Price: 'price',
    Links: 'links',
    XpBonus: 'xp_bonus',
    Accuracy: 'accuracy',
    Content: 'content',
    Access: 'access',
    Auth: 'auth',
    Notifications: 'notifications',
    Settings: 'settings',
    Other: 'other'
} as const;

/**
 * AuditCategory
 *
 * BR-SRV-63: категория — по коду действия, для быстрых фильтров реестра.
 */
export type AuditCategory = typeof AuditCategory[keyof typeof AuditCategory];

/**
 * AuditChangeOut
 */
export type AuditChangeOut = {
    /**
     * Field
     */
    field: string;
    /**
     * Label
     */
    label: string;
    /**
     * Before
     */
    before?: unknown;
    /**
     * After
     */
    after?: unknown;
    /**
     * Before Text
     *
     * «Было» для таблицы «Изменения»; секретное — «скрыто»
     */
    before_text: string;
    /**
     * After Text
     *
     * «Стало»; секретное — «скрыто»
     */
    after_text: string;
    /**
     * Masked
     */
    masked?: boolean;
};

/**
 * AuditExportIn
 *
 * Фильтры экспорта — как у `GET /audit` (13.8.2).
 */
export type AuditExportIn = {
    category?: AuditCategory | null;
    /**
     * Actor Id
     */
    actor_id?: string | null;
    /**
     * Entity Type
     */
    entity_type?: string | null;
    /**
     * Entity Id
     */
    entity_id?: string | null;
    /**
     * From
     */
    from?: string | null;
    /**
     * To
     */
    to?: string | null;
    /**
     * Q
     */
    q?: string | null;
    /**
     * Filters
     *
     * Фильтры по столбцам (подстрока)
     */
    filters?: {
        [key: string]: string;
    };
    /**
     * Sort
     */
    sort?: 'occurred_at' | '-occurred_at' | null;
};

/**
 * AuditExportOut
 */
export type AuditExportOut = {
    /**
     * Export Id
     */
    export_id: string;
    /**
     * ready — файл готов (`url`); pending — больше 100 000 строк: файл собирается в фоне, ссылка придёт на почту администратора; failed — ошибка
     */
    status: AuditExportStatus;
    /**
     * Url
     *
     * Ссылка на CSV (подписанная, ограниченного срока)
     */
    url: string | null;
    /**
     * Rows
     */
    rows: number;
    /**
     * Expires At
     *
     * До какого времени действует ссылка
     */
    expires_at: string | null;
};

/**
 * AuditExportStatus
 */
export const AuditExportStatus = {
    Pending: 'pending',
    Ready: 'ready',
    Failed: 'failed'
} as const;

/**
 * AuditExportStatus
 */
export type AuditExportStatus = typeof AuditExportStatus[keyof typeof AuditExportStatus];

/**
 * AuditHistoryItemOut
 *
 * Запись журнала аудита (SRV 13.3.17), где участник — исполнитель.
 */
export type AuditHistoryItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Action
     */
    action: string;
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Entity Type
     */
    entity_type: string | null;
    /**
     * Entity Label
     */
    entity_label: string | null;
    /**
     * Summary
     */
    summary: string;
};

/**
 * AuditPageOut
 */
export type AuditPageOut = {
    /**
     * Items
     */
    items: Array<AuditRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
};

/**
 * AuditRowOut
 */
export type AuditRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     *
     * Время действия (UTC); админка показывает в своём поясе
     */
    occurred_at: string;
    actor_type: AuditActorType;
    /**
     * Actor Id
     */
    actor_id: string | null;
    /**
     * Actor Name
     *
     * ФИО на момент действия или «Система»
     */
    actor_name: string;
    actor_role: AuditActorRole;
    /**
     * Actor Role Label
     *
     * Администратор / Винодел / Эксперт / Система
     */
    actor_role_label: string;
    category: AuditCategory;
    /**
     * Category Label
     */
    category_label: string;
    /**
     * Action
     *
     * Код действия, например `moderation.decided`
     */
    action: string;
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Entity Type
     */
    entity_type: string | null;
    /**
     * Entity Type Label
     */
    entity_type_label: string | null;
    /**
     * Entity Id
     */
    entity_id: string | null;
    /**
     * Entity Label
     */
    entity_label: string | null;
    /**
     * Entity
     *
     * «{Тип} · {название или id}»; «—», если сущности нет
     */
    entity: string;
    /**
     * Summary
     *
     * «Было → стало» (BR-SRV-62)
     */
    summary: string;
};

/**
 * AuthCodePurpose
 */
export const AuthCodePurpose = {
    Registration: 'registration',
    Login: 'login',
    PasswordReset: 'password_reset',
    EmailChange: 'email_change',
    PasswordChange: 'password_change',
    AccountDeletion: 'account_deletion'
} as const;

/**
 * AuthCodePurpose
 */
export type AuthCodePurpose = typeof AuthCodePurpose[keyof typeof AuthCodePurpose];

/**
 * AuthOut
 */
export type AuthOut = {
    tokens: TokensOut;
    user: UserMeOut;
    guest_merge: GuestMergeOut;
};

/**
 * AuthProviderCode
 */
export const AuthProviderCode = {
    Email: 'email',
    Vk: 'vk',
    Yandex: 'yandex',
    Google: 'google',
    Apple: 'apple'
} as const;

/**
 * AuthProviderCode
 */
export type AuthProviderCode = typeof AuthProviderCode[keyof typeof AuthProviderCode];

/**
 * AuthorBriefOut
 */
export type AuthorBriefOut = {
    /**
     * Member Id
     */
    member_id: string | null;
    /**
     * Name
     */
    name: string;
    /**
     * Role Label
     */
    role_label: string | null;
    /**
     * Label
     */
    label: string;
    /**
     * Target
     */
    target: string | null;
};

/**
 * AuthorKind
 */
export const AuthorKind = {
    Platform: 'platform',
    Expert: 'expert',
    Winemaker: 'winemaker'
} as const;

/**
 * AuthorKind
 */
export type AuthorKind = typeof AuthorKind[keyof typeof AuthorKind];

/**
 * AuthorOut
 */
export type AuthorOut = {
    /**
     * Member Id
     *
     * null — автор «платформа» (`brand.name`)
     */
    member_id: string | null;
    /**
     * Name
     */
    name: string;
    /**
     * Is Platform
     */
    is_platform: boolean;
};

/**
 * AuthorPageOut
 */
export type AuthorPageOut = {
    /**
     * Member Id
     */
    member_id: string;
    /**
     * Kind
     */
    kind: 'expert' | 'maker';
    /**
     * Header
     *
     * «Эксперт» / «Винодел»
     */
    header: string;
    /**
     * Name
     */
    name: string;
    /**
     * Role Label
     */
    role_label: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Photos
     */
    photos: Array<string>;
    /**
     * Bio
     */
    bio: string | null;
    /**
     * Socials
     */
    socials: Array<SocialOut>;
    /**
     * Meta
     *
     * Счётчики / «{опыт} · {N публикаций}»
     */
    meta: string | null;
    /**
     * Subscribers Count
     */
    subscribers_count: number;
    /**
     * Subscribers Label
     */
    subscribers_label: string;
    /**
     * Following
     */
    following: boolean;
    /**
     * Can Follow
     */
    can_follow: boolean;
    /**
     * Stories
     */
    stories: Array<StoryBubbleOut>;
    /**
     * Sections
     */
    sections: Array<PageSectionOut>;
    /**
     * Wineries
     */
    wineries: Array<SectionItemOut>;
    /**
     * Publications
     */
    publications: Array<PublicationCardOut>;
    /**
     * Publications Total
     */
    publications_total: number;
    /**
     * Share Target
     */
    share_target: string;
};

/**
 * AuthorProfileCan
 */
export type AuthorProfileCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Publish
     */
    publish?: boolean;
    /**
     * Hide
     */
    hide?: boolean;
};

/**
 * AuthorProfileIn
 *
 * «Заполнить профиль» (4.7.4). Статус меняется только «Опубликовать» / «Скрыть».
 */
export type AuthorProfileIn = {
    /**
     * Display Name
     */
    display_name: string;
    /**
     * До 80 символов
     */
    work_region?: LocalizedText | null;
    /**
     * До 60 символов
     */
    experience?: LocalizedText | null;
    /**
     * Social Telegram
     */
    social_telegram?: string | null;
    /**
     * Social Vk
     */
    social_vk?: string | null;
    /**
     * Social Video
     */
    social_video?: string | null;
    /**
     * Social Site
     */
    social_site?: string | null;
    /**
     * Photo File Id
     *
     * Загрузка `author_photo`: квадратное JPG/PNG от 400 px, до 10 МБ
     */
    photo_file_id?: string | null;
    /**
     * Photo Url
     *
     * «Или ссылка на фото» (https)
     */
    photo_url?: string | null;
    /**
     * Gallery File Ids
     *
     * Загрузки `author_gallery`, до 10, в порядке показа
     */
    gallery_file_ids?: Array<string>;
    /**
     * Specialties
     *
     * 1–6, до 60 символов каждая
     */
    specialties: Array<LocalizedText>;
    /**
     * «О себе», до 2 000 символов
     */
    bio?: LocalizedText | null;
};

/**
 * AuthorProfileOut
 *
 * Профиль автора. `id = null` — профиль ещё не сохранён («Заполнить профиль»).
 */
export type AuthorProfileOut = {
    /**
     * Id
     */
    id: string | null;
    /**
     * Member Id
     */
    member_id: string;
    /**
     * Display Name
     */
    display_name: string | null;
    work_region: LocalizedText | null;
    experience: LocalizedText | null;
    /**
     * Social Telegram
     */
    social_telegram: string | null;
    /**
     * Social Vk
     */
    social_vk: string | null;
    /**
     * Social Video
     */
    social_video: string | null;
    /**
     * Social Site
     */
    social_site: string | null;
    /**
     * Socials Count
     *
     * Заполнено ссылок из 4
     */
    socials_count: number;
    photo: AppModulesAccSchemasCommonFileRefOut | null;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * Gallery
     */
    gallery: Array<AppModulesAccSchemasCommonFileRefOut>;
    /**
     * Specialties
     */
    specialties: Array<LocalizedText>;
    bio: LocalizedText | null;
    status: AuthorProfileStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Published At
     */
    published_at: string | null;
    /**
     * Updated At
     */
    updated_at: string | null;
    can: AuthorProfileCan;
};

/**
 * AuthorProfileStatus
 */
export const AuthorProfileStatus = { Published: 'published', Draft: 'draft' } as const;

/**
 * AuthorProfileStatus
 */
export type AuthorProfileStatus = typeof AuthorProfileStatus[keyof typeof AuthorProfileStatus];

/**
 * AuthorStoriesOut
 */
export type AuthorStoriesOut = {
    author: ContentAuthorOut;
    /**
     * Profile Target
     */
    profile_target: string | null;
    /**
     * Stories
     */
    stories: Array<StoryOut>;
    /**
     * Empty Text
     *
     * «У этого автора пока нет свежих кадров.»
     */
    empty_text: string | null;
};

/**
 * AvailabilityErrorOut
 */
export type AvailabilityErrorOut = {
    /**
     * Code
     */
    code: string;
    /**
     * Message
     */
    message: string;
    /**
     * Details
     */
    details?: {
        [key: string]: unknown;
    } | null;
};

/**
 * AvailabilityIn
 */
export type AvailabilityIn = {
    /**
     * Items
     */
    items: Array<AvailabilityItemIn>;
};

/**
 * AvailabilityItemIn
 */
export type AvailabilityItemIn = {
    /**
     * Object Type
     */
    object_type: 'wine' | 'set' | 'set_item' | 'drop';
    /**
     * Object Id
     */
    object_id: string;
    /**
     * Qty
     */
    qty: number;
};

/**
 * AvailabilityItemOut
 */
export type AvailabilityItemOut = {
    /**
     * Object Type
     */
    object_type: string;
    /**
     * Object Id
     */
    object_id: string;
    /**
     * Ok
     */
    ok: boolean;
    /**
     * Available
     */
    available: number | null;
    /**
     * Max Qty
     */
    max_qty: number;
    error?: AvailabilityErrorOut | null;
};

/**
 * AvailabilityOut
 */
export type AvailabilityOut = {
    /**
     * Items
     */
    items: Array<AvailabilityItemOut>;
};

/**
 * AvatarIn
 */
export type AvatarIn = {
    /**
     * File Id
     *
     * `upload_id` загрузки с назначением `avatar`
     */
    file_id: string;
};

/**
 * AvatarOut
 */
export type AvatarOut = {
    /**
     * Avatar Url
     */
    avatar_url: string | null;
    /**
     * Avatar Thumb Url
     */
    avatar_thumb_url: string | null;
};

/**
 * BillingAppStorePurchaseIn
 */
export type BillingAppStorePurchaseIn = {
    /**
     * Signed Transaction
     */
    signed_transaction: string;
    /**
     * Transaction Id
     */
    transaction_id: string;
};

/**
 * BillingGooglePlayPurchaseIn
 */
export type BillingGooglePlayPurchaseIn = {
    /**
     * Product Id
     */
    product_id: string;
    /**
     * Purchase Token
     */
    purchase_token: string;
    /**
     * Order Id
     */
    order_id?: string | null;
    /**
     * Price Amount Micros
     */
    price_amount_micros?: number | null;
    /**
     * Price Currency
     */
    price_currency?: string | null;
};

/**
 * BillingIntentIn
 */
export type BillingIntentIn = {
    /**
     * Item Type
     */
    item_type: 'lesson' | 'region_course' | 'expert_course';
    /**
     * Item Id
     */
    item_id: string;
};

/**
 * BillingIntentOut
 */
export type BillingIntentOut = {
    /**
     * Intent Id
     *
     * = `order_id` заказа ORD
     */
    intent_id: string;
    /**
     * Order Id
     */
    order_id: string;
    store: StoreCode;
    /**
     * Store Product Id
     */
    store_product_id: string;
    /**
     * Account Token
     *
     * App Store `appAccountToken` (uuid), Google `obfuscatedAccountId` (hex SHA-256 от id пользователя), RuStore `appUserId` (id пользователя)
     */
    account_token: string;
};

/**
 * BillingProductCheckOut
 */
export type BillingProductCheckOut = {
    /**
     * Available
     */
    available: boolean;
    used_by?: BillingProductUsedByOut | null;
};

/**
 * BillingProductOut
 */
export type BillingProductOut = {
    /**
     * Item Type
     */
    item_type: 'lesson' | 'region_course' | 'expert_course';
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Store Product Id
     *
     * ID продукта в сторе канала; null — продукт не заведён (кнопка покупки показывает «Сейчас недоступно для покупки»)
     */
    store_product_id: string | null;
    /**
     * Active
     *
     * Продаётся в сторе (`StoreProduct.status = active`)
     */
    active: boolean;
};

/**
 * BillingProductUsedByOut
 */
export type BillingProductUsedByOut = {
    /**
     * Item Type
     */
    item_type: 'lesson' | 'region_course' | 'expert_course';
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Title
     */
    title: string;
};

/**
 * BillingProductsOut
 */
export type BillingProductsOut = {
    store: StoreCode;
    /**
     * Items
     */
    items: Array<BillingProductOut>;
};

/**
 * BillingProductsSeenIn
 */
export type BillingProductsSeenIn = {
    store: StoreCode;
    /**
     * Items
     */
    items: Array<BillingSeenItemIn>;
};

/**
 * BillingPurchaseIn
 */
export type BillingPurchaseIn = {
    store: StoreCode;
    /**
     * Intent Id
     */
    intent_id?: string | null;
    app_store?: BillingAppStorePurchaseIn | null;
    google_play?: BillingGooglePlayPurchaseIn | null;
    rustore?: BillingRuStorePurchaseIn | null;
};

/**
 * BillingPurchaseOut
 */
export type BillingPurchaseOut = {
    /**
     * Status
     */
    status: 'granted' | 'pending' | 'revoked';
    /**
     * Order Id
     */
    order_id: string;
    /**
     * Item Type
     */
    item_type: 'lesson' | 'region_course' | 'expert_course';
    /**
     * Item Id
     */
    item_id: string;
};

/**
 * BillingRuStorePurchaseIn
 */
export type BillingRuStorePurchaseIn = {
    /**
     * Product Id
     */
    product_id: string;
    /**
     * Purchase Id
     */
    purchase_id: string;
    /**
     * Invoice Id
     */
    invoice_id?: string | null;
    /**
     * Order Id
     */
    order_id?: string | null;
};

/**
 * BillingSeenItemIn
 */
export type BillingSeenItemIn = {
    /**
     * Store Product Id
     */
    store_product_id: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Display Price
     */
    display_price?: string | null;
    /**
     * Price Amount Micros
     */
    price_amount_micros?: number | null;
    /**
     * Currency
     */
    currency?: string | null;
};

/**
 * BillingSyncIn
 */
export type BillingSyncIn = {
    store: StoreCode;
    /**
     * Purchases
     */
    purchases: Array<BillingSyncPurchaseIn>;
};

/**
 * BillingSyncOut
 */
export type BillingSyncOut = {
    /**
     * Results
     */
    results: Array<BillingSyncResultOut>;
};

/**
 * BillingSyncPurchaseIn
 */
export type BillingSyncPurchaseIn = {
    app_store?: BillingAppStorePurchaseIn | null;
    google_play?: BillingGooglePlayPurchaseIn | null;
    rustore?: BillingRuStorePurchaseIn | null;
    /**
     * Intent Id
     */
    intent_id?: string | null;
};

/**
 * BillingSyncResultOut
 */
export type BillingSyncResultOut = {
    /**
     * Store Transaction Id
     */
    store_transaction_id: string | null;
    /**
     * Status
     */
    status: 'granted' | 'pending' | 'revoked' | 'failed';
    /**
     * Order Id
     */
    order_id?: string | null;
    /**
     * Error Code
     */
    error_code?: string | null;
};

/**
 * BlindClimate
 *
 * Климат в определении вина вслепую (имя схемы уникально: у GEO свой `Climate`).
 */
export const BlindClimate = {
    Continental: 'continental',
    ModeratelyContinental: 'moderately_continental',
    Maritime: 'maritime',
    Mediterranean: 'mediterranean',
    Mountain: 'mountain'
} as const;

/**
 * BlindClimate
 *
 * Климат в определении вина вслепую (имя схемы уникально: у GEO свой `Climate`).
 */
export type BlindClimate = typeof BlindClimate[keyof typeof BlindClimate];

/**
 * BlockOut
 */
export type BlockOut = {
    /**
     * Type
     */
    type: 'heading' | 'paragraph' | 'quote' | 'list' | 'image';
    /**
     * Text
     */
    text?: string | null;
    /**
     * Items
     */
    items?: Array<string> | null;
    /**
     * Url
     */
    url?: string | null;
    /**
     * Caption
     */
    caption?: string | null;
};

/**
 * BodyBlockOut
 */
export type BodyBlockOut = {
    /**
     * Type
     */
    type: string;
    /**
     * Inline
     */
    inline?: Array<{
        [key: string]: unknown;
    }> | null;
    /**
     * Items
     */
    items?: Array<Array<{
        [key: string]: unknown;
    }>> | null;
    /**
     * Media Id
     */
    media_id?: string | null;
    /**
     * Caption
     */
    caption?: string | null;
    /**
     * Url
     *
     * Вставленное фото/видео — ссылка
     */
    url?: string | null;
};

/**
 * BookingAdmin
 */
export type BookingAdmin = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string;
    status: BookingStatus;
    /**
     * Status Label
     */
    status_label: string;
    booking_type: BookingType;
    /**
     * Type Label
     */
    type_label: string;
    target: TargetOut;
    user: RefOut;
    /**
     * Organizer Label
     */
    organizer_label: string;
    /**
     * When Label
     */
    when_label: string;
    /**
     * Slot Starts At
     */
    slot_starts_at: string | null;
    /**
     * Slot Ends At
     */
    slot_ends_at: string | null;
    departure: DepartureOut | null;
    /**
     * Flexible Dates
     */
    flexible_dates: boolean;
    /**
     * Agreed Date From
     */
    agreed_date_from: string | null;
    /**
     * Agreed Date To
     */
    agreed_date_to: string | null;
    /**
     * Adults
     */
    adults: number;
    /**
     * Sober Guests
     */
    sober_guests: number;
    /**
     * Children
     */
    children: Array<ChildAgeGroup>;
    room_type: RoomKind | null;
    /**
     * Rooms Count
     */
    rooms_count: number | null;
    /**
     * Note
     */
    note: string | null;
    /**
     * Holder Name
     */
    holder_name: string;
    /**
     * Place Label
     */
    place_label: string;
    /**
     * Guests Label
     */
    guests_label: string;
    amount: Money;
    calc_amount: Money;
    agreed_amount: Money | null;
    /**
     * Amount Label
     */
    amount_label: string;
    /**
     * Payment Label
     */
    payment_label: string;
    /**
     * Entry Label
     */
    entry_label: string;
    /**
     * Cancel Policy Text
     */
    cancel_policy_text: string | null;
    /**
     * Queue Label
     */
    queue_label: string | null;
    /**
     * Reason Label
     */
    reason_label: string | null;
    /**
     * Close Comment
     */
    close_comment: string | null;
    /**
     * Hold Until
     */
    hold_until: string | null;
    /**
     * Paid At
     */
    paid_at: string | null;
    refund_amount: Money | null;
    /**
     * Calc Title
     */
    calc_title: string;
    /**
     * Calc Lines
     */
    calc_lines: Array<LineOut>;
    calc_total: LineOut;
    /**
     * Calc Note
     */
    calc_note: string;
    /**
     * Event Lifecycle Label
     */
    event_lifecycle_label: string | null;
    /**
     * Event Meta
     */
    event_meta: string;
    order: OrderRefOut | null;
    kit_order: OrderRefOut | null;
    /**
     * Links
     */
    links: {
        [key: string]: ExpLinkOut;
    };
    /**
     * Attendance Count
     */
    attendance_count: number;
    /**
     * History
     */
    history: Array<HistoryOut>;
    /**
     * Attend Label
     */
    attend_label: string | null;
    /**
     * Created At
     */
    created_at: string;
    can: BookingAdminCan;
};

/**
 * BookingAdminCan
 */
export type BookingAdminCan = {
    /**
     * Approve
     */
    approve?: boolean;
    /**
     * Decline
     */
    decline?: boolean;
    /**
     * Cancel
     */
    cancel?: boolean;
    /**
     * Remind Payment
     */
    remind_payment?: boolean;
    /**
     * Mark Attended
     */
    mark_attended?: boolean;
    /**
     * Mark No Show
     */
    mark_no_show?: boolean;
    /**
     * Offer Seat
     */
    offer_seat?: boolean;
    /**
     * Remove From Waitlist
     */
    remove_from_waitlist?: boolean;
};

/**
 * BookingBrief
 */
export type BookingBrief = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string;
    status: BookingStatus;
    /**
     * Status Label
     */
    status_label: string;
    amount: Money;
    /**
     * Hold Until
     */
    hold_until?: string | null;
    /**
     * Waitlist Position
     */
    waitlist_position?: number | null;
};

/**
 * BookingCan
 */
export type BookingCan = {
    /**
     * Cancel
     */
    cancel?: boolean;
    /**
     * Pay
     */
    pay?: boolean;
    /**
     * Edit
     */
    edit?: boolean;
    /**
     * Conference
     */
    conference?: boolean;
};

/**
 * BookingCreateIn
 */
export type BookingCreateIn = {
    /**
     * tasting | excursion
     */
    target_type: BookingTarget;
    /**
     * Target Id
     */
    target_id: string;
    /**
     * Slot Starts At
     */
    slot_starts_at?: string | null;
    /**
     * Adults
     */
    adults?: number;
    /**
     * Sober Guests
     */
    sober_guests?: number;
};

/**
 * BookingCreatedOut
 */
export type BookingCreatedOut = {
    booking: BookingOut;
    order: OrderBrief | null;
};

/**
 * BookingOut
 */
export type BookingOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string;
    target_type: BookingTarget;
    /**
     * Target Id
     */
    target_id: string;
    booking_type: BookingType;
    /**
     * Title
     */
    title: string;
    winery?: RefOut | null;
    /**
     * Slot Starts At
     */
    slot_starts_at: string | null;
    /**
     * Slot Ends At
     */
    slot_ends_at: string | null;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * When Label
     */
    when_label: string;
    /**
     * Adults
     */
    adults: number;
    /**
     * Sober Guests
     */
    sober_guests: number;
    /**
     * Holder Name
     */
    holder_name: string;
    amount: Money;
    /**
     * Amount Label
     */
    amount_label: string;
    status: BookingStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Hold Until
     */
    hold_until: string | null;
    /**
     * Waitlist Position
     */
    waitlist_position: number | null;
    /**
     * Cancel Policy Text
     */
    cancel_policy_text: string;
    /**
     * Paid At
     */
    paid_at: string | null;
    refund_amount: Money | null;
    order: OrderBrief | null;
    departure?: DepartureRef | null;
    /**
     * Flexible Dates
     */
    flexible_dates?: boolean;
    /**
     * Agreed Date From
     */
    agreed_date_from?: string | null;
    /**
     * Agreed Date To
     */
    agreed_date_to?: string | null;
    /**
     * Children
     */
    children?: Array<ChildAgeGroup>;
    room_type?: RoomKind | null;
    /**
     * Rooms Count
     */
    rooms_count?: number | null;
    /**
     * Note
     */
    note?: string | null;
    calc?: TripCalcOut | null;
    /**
     * Amount Is Final
     */
    amount_is_final?: boolean;
    /**
     * Decline Reason
     */
    decline_reason?: string | null;
    /**
     * Close Comment
     */
    close_comment?: string | null;
    /**
     * Warnings
     */
    warnings?: Array<string>;
    can: BookingCan;
};

/**
 * BookingRow
 */
export type BookingRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string;
    user: RefOut;
    booking_type: BookingType;
    /**
     * Type Label
     */
    type_label: string;
    target: TargetOut;
    /**
     * Organizer Label
     */
    organizer_label: string;
    /**
     * When Label
     */
    when_label: string;
    /**
     * Starts At
     */
    starts_at: string | null;
    /**
     * People
     */
    people: number;
    amount: Money | null;
    /**
     * Amount Label
     */
    amount_label: string;
    status: BookingStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Created At
     */
    created_at: string;
    can: BookingAdminCan;
};

/**
 * BookingStatus
 */
export const BookingStatus = {
    PendingApproval: 'pending_approval',
    AwaitingPayment: 'awaiting_payment',
    Confirmed: 'confirmed',
    Waitlisted: 'waitlisted',
    Attended: 'attended',
    NoShow: 'no_show',
    Cancelled: 'cancelled',
    CancelledByOrganizer: 'cancelled_by_organizer',
    Declined: 'declined'
} as const;

/**
 * BookingStatus
 */
export type BookingStatus = typeof BookingStatus[keyof typeof BookingStatus];

/**
 * BookingTarget
 */
export const BookingTarget = {
    Tasting: 'tasting',
    Excursion: 'excursion',
    Trip: 'trip'
} as const;

/**
 * BookingTarget
 */
export type BookingTarget = typeof BookingTarget[keyof typeof BookingTarget];

/**
 * BookingType
 */
export const BookingType = {
    TastingOnline: 'tasting_online',
    TastingOffline: 'tasting_offline',
    BlindOnline: 'blind_online',
    BlindOffline: 'blind_offline',
    Excursion: 'excursion',
    Trip: 'trip'
} as const;

/**
 * BookingType
 */
export type BookingType = typeof BookingType[keyof typeof BookingType];

/**
 * BottleVolume
 */
export const BottleVolume = {
    L0375: 'l0_375',
    L075: 'l0_75',
    L15: 'l1_5'
} as const;

/**
 * BottleVolume
 */
export type BottleVolume = typeof BottleVolume[keyof typeof BottleVolume];

/**
 * BrandLogoFileOut
 */
export type BrandLogoFileOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Url
     */
    url: string;
    /**
     * Name
     */
    name: string | null;
    /**
     * Content Type
     */
    content_type: string | null;
    /**
     * Size
     */
    size: number | null;
};

/**
 * BrandLogoIn
 */
export type BrandLogoIn = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * File Name
     */
    file_name?: string | null;
};

/**
 * BrandLogoOut
 */
export type BrandLogoOut = {
    /**
     * Platform
     */
    platform: string;
    file: BrandLogoFileOut | null;
    /**
     * Updated At
     */
    updated_at: string | null;
    /**
     * Updated By
     */
    updated_by: string | null;
    /**
     * Hint
     */
    hint: string;
    /**
     * Can
     */
    can: {
        [key: string]: boolean;
    };
    /**
     * Toast
     */
    toast?: string | null;
};

/**
 * BreakdownLessonOut
 */
export type BreakdownLessonOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Number
     */
    number: string;
    /**
     * Title
     */
    title: string;
    /**
     * Open
     */
    open: boolean;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Right Label
     */
    right_label: string;
};

/**
 * BronzeOut
 */
export type BronzeOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    /**
     * Xp
     */
    xp: number | null;
    study: StudyCardOut;
    taste: TasteCardOut;
    /**
     * Tasks
     */
    tasks: Array<TaskOut>;
};

/**
 * CalibrationArchiveReason
 *
 * Общий механизм архива админки: Не актуально / Дубликат / Партнёр расторг / Ошибка данных.
 */
export const CalibrationArchiveReason = {
    NotRelevant: 'not_relevant',
    Duplicate: 'duplicate',
    ContractTerminated: 'contract_terminated',
    DataError: 'data_error'
} as const;

/**
 * CalibrationArchiveReason
 *
 * Общий механизм архива админки: Не актуально / Дубликат / Партнёр расторг / Ошибка данных.
 */
export type CalibrationArchiveReason = typeof CalibrationArchiveReason[keyof typeof CalibrationArchiveReason];

/**
 * CalibrationStatus
 */
export const CalibrationStatus = {
    Draft: 'draft',
    Published: 'published',
    Archived: 'archived'
} as const;

/**
 * CalibrationStatus
 */
export type CalibrationStatus = typeof CalibrationStatus[keyof typeof CalibrationStatus];

/**
 * CampaignAudienceEstimateOut
 */
export type CampaignAudienceEstimateOut = {
    /**
     * Count
     */
    count: number;
    /**
     * Label
     *
     * «Получат примерно {N} пользователей с согласием на рассылки»
     */
    label: string;
    channel: CampaignChannel;
    /**
     * Segment
     */
    segment: string | null;
    /**
     * Available
     *
     * Сегмент доступен (провайдер подключён)
     */
    available: boolean;
    /**
     * Email Days
     *
     * Письмо: суток на рассылку при квоте `mail.marketing_daily_quota`
     */
    email_days?: number | null;
    /**
     * Email Days Label
     */
    email_days_label?: string | null;
};

/**
 * CampaignCan
 */
export type CampaignCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Schedule
     */
    schedule?: boolean;
    /**
     * Launch
     */
    launch?: boolean;
    /**
     * Stop
     */
    stop?: boolean;
    /**
     * Resume
     */
    resume?: boolean;
    /**
     * Cancel
     */
    cancel?: boolean;
};

/**
 * CampaignCardOut
 */
export type CampaignCardOut = {
    /**
     * Id
     */
    id: string;
    channel: CampaignChannel;
    /**
     * Channel Label
     */
    channel_label: string;
    /**
     * Title
     *
     * Заголовок на языке администратора
     */
    title: string;
    /**
     * Text
     */
    text: string;
    image: CampaignImageOut | null;
    link: CampaignLinkOut | null;
    mode: CampaignMode;
    /**
     * Mode Label
     */
    mode_label: string;
    /**
     * Schedule Label
     *
     * «разово · {дата} · {время}» / «регулярно · …»
     */
    schedule_label: string;
    /**
     * Send At
     */
    send_at: string | null;
    /**
     * Send At Timezone
     */
    send_at_timezone: string | null;
    /**
     * Weekdays
     */
    weekdays: Array<CampaignWeekday>;
    /**
     * Local Time
     */
    local_time: string | null;
    status: CampaignStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Runs Count
     *
     * Завершённых запусков (вычисляется)
     */
    runs_count: number;
    /**
     * Last Run Recipients
     *
     * Получателей последнего запуска
     */
    last_run_recipients: number | null;
    /**
     * Sent Label
     *
     * «—» / «{N} раз · {M} получателей»
     */
    sent_label: string;
    /**
     * Next Run At
     *
     * Ближайший запуск (вычисляется)
     */
    next_run_at: string | null;
    /**
     * Row Action
     */
    row_action: 'schedule' | 'launch' | 'stop' | 'resume' | 'cancel' | null;
    /**
     * Row Action Label
     */
    row_action_label: string | null;
    /**
     * Audience Label
     */
    audience_label: string;
    segment: CampaignSegmentOut | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    can: CampaignCan;
    texts_title: LocalizedText;
    texts_text: LocalizedText;
    /**
     * Image File Id
     */
    image_file_id: string | null;
    link_type: CampaignLinkType;
    /**
     * Link Id
     */
    link_id: string | null;
    /**
     * Preview
     *
     * «Так увидит пользователь · пуш: …» (13.7.3.3)
     */
    preview: string;
    /**
     * Read Only
     *
     * «Отправляется» / «Отправлено» — карточка только читается
     */
    read_only: boolean;
    /**
     * Runs
     */
    runs: Array<CampaignRunReportOut>;
    /**
     * History
     */
    history: Array<CampaignHistoryOut>;
    /**
     * Toast
     *
     * Тост действия (13.7.3.2–13.7.3.3)
     */
    toast?: string | null;
};

/**
 * CampaignChannel
 */
export const CampaignChannel = { Push: 'push', Email: 'email' } as const;

/**
 * CampaignChannel
 */
export type CampaignChannel = typeof CampaignChannel[keyof typeof CampaignChannel];

/**
 * CampaignChannelOptionOut
 */
export type CampaignChannelOptionOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
    /**
     * Hint
     */
    hint: string;
    /**
     * Title Label
     */
    title_label: string;
    /**
     * Title Max
     */
    title_max: number;
    /**
     * Text Max
     */
    text_max: number;
    /**
     * Link Hint
     */
    link_hint: string;
    /**
     * Image Hint
     */
    image_hint: string;
};

/**
 * CampaignCountersOut
 */
export type CampaignCountersOut = {
    /**
     * Total
     *
     * «Конструктор ({N})» — без фильтров
     */
    total: number;
    /**
     * Push
     */
    push: number;
    /**
     * Email
     */
    email: number;
};

/**
 * CampaignDeliveryStatsOut
 */
export type CampaignDeliveryStatsOut = {
    /**
     * Sent
     */
    sent?: number;
    /**
     * Failed
     */
    failed?: number;
    /**
     * Dropped
     */
    dropped?: number;
    /**
     * Pending
     */
    pending?: number;
};

/**
 * CampaignFormOut
 *
 * Справочник модалки «Новое уведомление» (13.7.3.3).
 */
export type CampaignFormOut = {
    /**
     * Overline
     */
    overline: string;
    /**
     * Text
     */
    text: string;
    /**
     * Footer
     */
    footer: string;
    /**
     * Channels
     */
    channels: Array<CampaignChannelOptionOut>;
    /**
     * Link Types
     */
    link_types: Array<CampaignLinkTypeOptionOut>;
    /**
     * Modes
     */
    modes: Array<CampaignOptionOut>;
    /**
     * Weekdays
     */
    weekdays: Array<CampaignOptionOut>;
    /**
     * Timezones
     */
    timezones: Array<CampaignOptionOut>;
    /**
     * Default Timezone
     */
    default_timezone: string;
    /**
     * Time Hint
     */
    time_hint: string;
    /**
     * Min Lead Minutes
     */
    min_lead_minutes: number;
    /**
     * Image Types
     */
    image_types: Array<string>;
    /**
     * Image Max Size
     */
    image_max_size: number;
    /**
     * Audience Label
     */
    audience_label: string;
    /**
     * Segments
     */
    segments: Array<CampaignSegmentOptionOut>;
};

/**
 * CampaignHistoryOut
 */
export type CampaignHistoryOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Actor Name
     */
    actor_name: string;
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Summary
     */
    summary: string;
};

/**
 * CampaignImageOut
 */
export type CampaignImageOut = {
    file: FileOut;
    /**
     * Label
     *
     * «▣ {файл картинки}»
     */
    label: string;
};

/**
 * CampaignIn
 *
 * Тело модалки «Новое уведомление» (13.7.3.3) + `intent`: `draft` — «Черновик»,
 * `schedule` — «Запланировать» (разовая), `launch` — «Запустить» (регулярная).
 */
export type CampaignIn = {
    /**
     * Intent
     */
    intent?: 'draft' | 'schedule' | 'launch';
    channel?: CampaignChannel;
    /**
     * Заголовок пуша (≤ 60) / тема письма (≤ 120)
     */
    title: LocalizedText;
    /**
     * Текст пуша (≤ 180) / письма (≤ 2000)
     */
    text: LocalizedText;
    /**
     * Image File Id
     *
     * Картинка — загрузка `POST /admin/uploads` (`campaign_image`)
     */
    image_file_id?: string | null;
    link_type?: CampaignLinkType;
    /**
     * Link Id
     *
     * Обязателен, если `link_type ≠ none`
     */
    link_id?: string | null;
    mode?: CampaignMode;
    /**
     * Send At
     *
     * Разовая: момент отправки (ISO 8601 с поясом)
     */
    send_at?: string | null;
    /**
     * Send At Timezone
     *
     * Разовая: пояс из `time.admin_timezones` (по умолчанию — пояс инсталляции)
     */
    send_at_timezone?: string | null;
    /**
     * Weekdays
     */
    weekdays?: Array<CampaignWeekday>;
    /**
     * Local Time
     *
     * Регулярная: «ЧЧ:ММ» по поясу пользователя
     */
    local_time?: string | null;
    /**
     * Segment
     *
     * Расширение: сегмент (`GET …/campaigns/form` → `segments`); null — все пользователи с согласием
     */
    segment?: string | null;
    /**
     * Segment Params
     */
    segment_params?: {
        [key: string]: string;
    };
};

/**
 * CampaignLinkOptionOut
 */
export type CampaignLinkOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Label
     */
    label: string;
};

/**
 * CampaignLinkOptionsOut
 */
export type CampaignLinkOptionsOut = {
    link_type: CampaignLinkType;
    /**
     * Available
     */
    available: boolean;
    /**
     * Items
     */
    items: Array<CampaignLinkOptionOut>;
};

/**
 * CampaignLinkOut
 */
export type CampaignLinkOut = {
    type: CampaignLinkType;
    /**
     * Type Label
     */
    type_label: string;
    /**
     * Id
     */
    id: string;
    /**
     * Title
     *
     * Название объекта (для вина — «{название} {винтаж}»)
     */
    title: string | null;
    /**
     * Label
     *
     * «→ {тип объекта} · {название}»
     */
    label: string;
    /**
     * Section
     *
     * Раздел админки карточки объекта
     */
    section: string | null;
    /**
     * Live
     *
     * Опубликован сейчас; null — тип недоступен
     */
    live: boolean | null;
    /**
     * Available
     *
     * Модуль-владелец типа подключён
     */
    available: boolean;
};

/**
 * CampaignLinkType
 *
 * «Что открыть» (13.3.15).
 */
export const CampaignLinkType = {
    None: 'none',
    Wine: 'wine',
    WineSet: 'wine_set',
    Drop: 'drop',
    Course: 'course',
    Lesson: 'lesson',
    Tasting: 'tasting',
    Excursion: 'excursion',
    Trip: 'trip',
    Publication: 'publication',
    Region: 'region',
    Winery: 'winery'
} as const;

/**
 * CampaignLinkType
 *
 * «Что открыть» (13.3.15).
 */
export type CampaignLinkType = typeof CampaignLinkType[keyof typeof CampaignLinkType];

/**
 * CampaignLinkTypeOptionOut
 */
export type CampaignLinkTypeOptionOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Section
     */
    section: string | null;
};

/**
 * CampaignMode
 */
export const CampaignMode = { Once: 'once', Recurring: 'recurring' } as const;

/**
 * CampaignMode
 */
export type CampaignMode = typeof CampaignMode[keyof typeof CampaignMode];

/**
 * CampaignOptionOut
 */
export type CampaignOptionOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
};

/**
 * CampaignPatchIn
 *
 * Частичное тело: переданные поля меняются, `intent` — переход (необязателен). `null`
 * допустим для картинки, объекта, времени, пояса, сегмента; канал, тексты, «Что открыть»,
 * периодичность, дни недели и параметры сегмента очистить нельзя (422).
 */
export type CampaignPatchIn = {
    /**
     * Intent
     */
    intent?: 'draft' | 'schedule' | 'launch' | null;
    channel?: CampaignChannel | null;
    title?: LocalizedText | null;
    text?: LocalizedText | null;
    /**
     * Image File Id
     */
    image_file_id?: string | null;
    link_type?: CampaignLinkType | null;
    /**
     * Link Id
     */
    link_id?: string | null;
    mode?: CampaignMode | null;
    /**
     * Send At
     */
    send_at?: string | null;
    /**
     * Send At Timezone
     */
    send_at_timezone?: string | null;
    /**
     * Weekdays
     */
    weekdays?: Array<CampaignWeekday> | null;
    /**
     * Local Time
     */
    local_time?: string | null;
    /**
     * Segment
     */
    segment?: string | null;
    /**
     * Segment Params
     */
    segment_params?: {
        [key: string]: string;
    } | null;
};

/**
 * CampaignRowOut
 */
export type CampaignRowOut = {
    /**
     * Id
     */
    id: string;
    channel: CampaignChannel;
    /**
     * Channel Label
     */
    channel_label: string;
    /**
     * Title
     *
     * Заголовок на языке администратора
     */
    title: string;
    /**
     * Text
     */
    text: string;
    image: CampaignImageOut | null;
    link: CampaignLinkOut | null;
    mode: CampaignMode;
    /**
     * Mode Label
     */
    mode_label: string;
    /**
     * Schedule Label
     *
     * «разово · {дата} · {время}» / «регулярно · …»
     */
    schedule_label: string;
    /**
     * Send At
     */
    send_at: string | null;
    /**
     * Send At Timezone
     */
    send_at_timezone: string | null;
    /**
     * Weekdays
     */
    weekdays: Array<CampaignWeekday>;
    /**
     * Local Time
     */
    local_time: string | null;
    status: CampaignStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Runs Count
     *
     * Завершённых запусков (вычисляется)
     */
    runs_count: number;
    /**
     * Last Run Recipients
     *
     * Получателей последнего запуска
     */
    last_run_recipients: number | null;
    /**
     * Sent Label
     *
     * «—» / «{N} раз · {M} получателей»
     */
    sent_label: string;
    /**
     * Next Run At
     *
     * Ближайший запуск (вычисляется)
     */
    next_run_at: string | null;
    /**
     * Row Action
     */
    row_action: 'schedule' | 'launch' | 'stop' | 'resume' | 'cancel' | null;
    /**
     * Row Action Label
     */
    row_action_label: string | null;
    /**
     * Audience Label
     */
    audience_label: string;
    segment: CampaignSegmentOut | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    can: CampaignCan;
};

/**
 * CampaignRunReportOut
 *
 * Отчёт о запуске: получатели отобраны в момент запуска; доставки — по факту (push — статус
 * SRV, письмо — статус Mailer).
 */
export type CampaignRunReportOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Run Date
     */
    run_date: string;
    /**
     * Started At
     */
    started_at: string;
    /**
     * Finished At
     */
    finished_at: string | null;
    /**
     * State
     */
    state: 'running' | 'finished';
    /**
     * State Label
     */
    state_label: string;
    /**
     * Recipients
     *
     * Отобрано получателей
     */
    recipients: number;
    /**
     * Sent
     *
     * Доставлено хотя бы по одному каналу
     */
    sent: number;
    /**
     * Failed
     *
     * Ни одна доставка не прошла
     */
    failed: number;
    /**
     * Skipped
     *
     * Пропущено (нет согласия, нет канала, блокировка)
     */
    skipped: number;
    /**
     * Pending
     *
     * Ещё не отправлено или доставляется
     */
    pending: number;
    /**
     * Cancelled
     *
     * Рассылку остановили до отправки
     */
    cancelled: number;
    /**
     * Feed Only
     *
     * Пуш не ушёл (провайдер не подключён) — только лента
     */
    feed_only: number;
    /**
     * Skipped By Reason
     */
    skipped_by_reason: {
        [key: string]: number;
    };
    push: CampaignDeliveryStatsOut;
    email: CampaignDeliveryStatsOut;
    /**
     * Notes
     */
    notes: Array<string>;
};

/**
 * CampaignSegmentOptionOut
 */
export type CampaignSegmentOptionOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Hint
     */
    hint: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Params
     */
    params: Array<CampaignSegmentParamOut>;
};

/**
 * CampaignSegmentOut
 */
export type CampaignSegmentOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Params
     */
    params: {
        [key: string]: string;
    };
    /**
     * Params Label
     *
     * Подписи параметров: «Вино · Красностоп 2021»
     */
    params_label: string | null;
    /**
     * Available
     */
    available: boolean;
};

/**
 * CampaignSegmentParamOut
 */
export type CampaignSegmentParamOut = {
    /**
     * Name
     */
    name: string;
    /**
     * Label
     */
    label: string;
    /**
     * Kind
     */
    kind: 'choice' | 'ref';
    /**
     * Ref
     */
    ref: string | null;
    /**
     * Choices
     */
    choices: Array<CampaignOptionOut>;
};

/**
 * CampaignStatus
 */
export const CampaignStatus = {
    Draft: 'draft',
    Scheduled: 'scheduled',
    Sending: 'sending',
    Sent: 'sent',
    Active: 'active',
    Stopped: 'stopped'
} as const;

/**
 * CampaignStatus
 */
export type CampaignStatus = typeof CampaignStatus[keyof typeof CampaignStatus];

/**
 * CampaignWeekday
 */
export const CampaignWeekday = {
    Mon: 'mon',
    Tue: 'tue',
    Wed: 'wed',
    Thu: 'thu',
    Fri: 'fri',
    Sat: 'sat',
    Sun: 'sun'
} as const;

/**
 * CampaignWeekday
 */
export type CampaignWeekday = typeof CampaignWeekday[keyof typeof CampaignWeekday];

/**
 * CampaignsPageOut
 */
export type CampaignsPageOut = {
    /**
     * Items
     */
    items: Array<CampaignRowOut>;
    /**
     * Total
     */
    total: number;
    counters: CampaignCountersOut;
    /**
     * Lead
     */
    lead: string;
    /**
     * Empty Title
     */
    empty_title: string;
    /**
     * Empty Text
     */
    empty_text: string;
    can?: ListCan;
};

/**
 * CancelEventIn
 */
export type CancelEventIn = {
    /**
     * Reason
     *
     * «Причина отмены» (OQ-EXP-03)
     */
    reason: string;
};

/**
 * CancelEventOut
 */
export type CancelEventOut = {
    /**
     * Id
     */
    id: string;
    lifecycle: EventLifecycle | null;
    /**
     * Cancelled Bookings
     */
    cancelled_bookings: number;
    /**
     * Refunds Requested
     */
    refunds_requested: number;
};

/**
 * CancelPreviewOut
 */
export type CancelPreviewOut = {
    /**
     * Allowed
     */
    allowed: boolean;
    paid_amount: Money | null;
    /**
     * Refund Pct
     */
    refund_pct: number | null;
    refund_amount: Money | null;
    /**
     * Policy Text
     */
    policy_text: string;
    /**
     * Reason If Not Allowed
     */
    reason_if_not_allowed: string | null;
    /**
     * Message
     */
    message: string;
};

/**
 * CancelReason
 */
export const CancelReason = {
    GuestRequest: 'guest_request',
    NotPaidInTime: 'not_paid_in_time',
    EventCancelledOrMoved: 'event_cancelled_or_moved',
    Duplicate: 'duplicate',
    RemovedFromWaitlist: 'removed_from_waitlist'
} as const;

/**
 * CancelReason
 */
export type CancelReason = typeof CancelReason[keyof typeof CancelReason];

/**
 * CapacityOut
 */
export type CapacityOut = {
    /**
     * Daily Limit
     */
    daily_limit: number;
    /**
     * Used 24H
     */
    used_24h: number;
    /**
     * Available Now
     *
     * Можно отправить рассылкой прямо сейчас
     */
    available_now: number;
    /**
     * Estimated Days
     *
     * «Рассылка займёт ≈ N суток» (BR-INT-SVC-40)
     */
    estimated_days: number;
    /**
     * Marketing Daily Quota
     */
    marketing_daily_quota: number;
    /**
     * Marketing Used 24H
     */
    marketing_used_24h: number;
};

/**
 * CardCan
 */
export type CardCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Cancel
     */
    cancel?: boolean;
    /**
     * Manage Bookings
     */
    manage_bookings?: boolean;
};

/**
 * CareDictionariesOut
 */
export type CareDictionariesOut = {
    /**
     * Topics
     */
    topics: Array<TopicOut>;
    /**
     * Priorities
     */
    priorities: Array<PriorityOut>;
    /**
     * Statuses
     */
    statuses: Array<AppModulesAccSchemasCareStatusOut>;
    /**
     * Channels
     */
    channels: Array<ChannelOut>;
    /**
     * Priorities Note
     */
    priorities_note: string;
    /**
     * Statuses Note
     */
    statuses_note: string;
};

/**
 * CareRow
 */
export type CareRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    user: TicketUserOut;
    priority: CodeLabel;
    topic: CodeLabel;
    /**
     * Subject
     */
    subject: string;
    channel: SupportChannel;
    /**
     * Channel Label
     */
    channel_label: string;
    assignee: AppModulesAccSchemasCommonNamedRef | null;
    /**
     * Assignee Label
     */
    assignee_label: string;
    /**
     * Wait Seconds
     *
     * Ожидание без пауз (BR-ACC-44)
     */
    wait_seconds: number;
    /**
     * Wait Label
     */
    wait_label: string;
    status: TicketStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Archived
     */
    archived: boolean;
    /**
     * Sla Due At
     */
    sla_due_at: string;
    /**
     * Created At
     */
    created_at: string;
    can: CareRowCan;
};

/**
 * CareRowCan
 */
export type CareRowCan = {
    /**
     * View
     */
    view?: boolean;
    /**
     * Update
     */
    update?: boolean;
    /**
     * Take
     */
    take?: boolean;
    /**
     * Reply
     */
    reply?: boolean;
    /**
     * Resolve
     */
    resolve?: boolean;
    /**
     * Wait User
     */
    wait_user?: boolean;
    /**
     * Reopen
     */
    reopen?: boolean;
    /**
     * Close
     */
    close?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
};

/**
 * CareTicket
 */
export type CareTicket = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    status: TicketStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Archived
     */
    archived: boolean;
    /**
     * Archived At
     */
    archived_at: string | null;
    /**
     * Archive Reason
     */
    archive_reason: string | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    user: TicketUserOut;
    /**
     * Contact Name
     */
    contact_name: string | null;
    /**
     * Reply Email
     */
    reply_email: string;
    channel: SupportChannel;
    /**
     * Channel Label
     */
    channel_label: string;
    topic: CodeLabel;
    priority: CodeLabel;
    /**
     * Subject
     */
    subject: string;
    /**
     * Ref Text
     */
    ref_text: string | null;
    /**
     * Related
     */
    related: Array<RelatedItem>;
    assignee: AppModulesAccSchemasCommonNamedRef | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Wait Seconds
     */
    wait_seconds: number;
    /**
     * Wait Label
     */
    wait_label: string;
    sla: SlaOut;
    /**
     * Resolution
     */
    resolution: string | null;
    /**
     * Resolved At
     */
    resolved_at: string | null;
    /**
     * Closed At
     */
    closed_at: string | null;
    /**
     * Messages
     */
    messages: Array<MessageOut>;
    /**
     * History
     */
    history: Array<HistoryEntryOut>;
    can: CareTicketCan;
};

/**
 * CareTicketCan
 */
export type CareTicketCan = {
    /**
     * View
     */
    view?: boolean;
    /**
     * Update
     */
    update?: boolean;
    /**
     * Take
     */
    take?: boolean;
    /**
     * Reply
     */
    reply?: boolean;
    /**
     * Resolve
     */
    resolve?: boolean;
    /**
     * Wait User
     */
    wait_user?: boolean;
    /**
     * Reopen
     */
    reopen?: boolean;
    /**
     * Close
     */
    close?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
};

/**
 * CartGroup
 */
export const CartGroup = {
    Learning: 'learning',
    Wine: 'wine',
    Experience: 'experience'
} as const;

/**
 * CartGroup
 */
export type CartGroup = typeof CartGroup[keyof typeof CartGroup];

/**
 * CartItemType
 */
export const CartItemType = {
    Wine: 'wine',
    Set: 'set',
    Drop: 'drop',
    LessonSet: 'lesson_set',
    BlindKit: 'blind_kit',
    BlindWine: 'blind_wine',
    Course: 'course',
    Lesson: 'lesson',
    ExpertCourse: 'expert_course',
    TastingSeat: 'tasting_seat',
    BlindSeat: 'blind_seat',
    Booking: 'booking',
    Trip: 'trip'
} as const;

/**
 * CartItemType
 */
export type CartItemType = typeof CartItemType[keyof typeof CartItemType];

/**
 * CatalogMediaOut
 */
export type CatalogMediaOut = {
    /**
     * Id
     */
    id: string;
    media_type: MediaType;
    placement: MediaPlacement;
    /**
     * Role Label
     */
    role_label: string;
    /**
     * File Id
     */
    file_id: string | null;
    /**
     * Source Url
     */
    source_url: string | null;
    /**
     * Url
     *
     * Ссылка для показа (файл хранилища или source_url)
     */
    url: string | null;
    /**
     * Thumb Url
     */
    thumb_url: string | null;
    caption: LocalizedText | null;
    /**
     * Position
     */
    position: number;
};

/**
 * CatalogPageOut[DropRowOut]
 */
export type CatalogPageOutDropRowOut = {
    /**
     * Items
     */
    items: Array<DropRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои» (6.2.2)
     */
    notice?: string | null;
};

/**
 * CatalogPageOut[SetRowOut]
 */
export type CatalogPageOutSetRowOut = {
    /**
     * Items
     */
    items: Array<SetRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои» (6.2.2)
     */
    notice?: string | null;
};

/**
 * CatalogPageOut[WineRowOut]
 */
export type CatalogPageOutWineRowOut = {
    /**
     * Items
     */
    items: Array<WineRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои» (6.2.2)
     */
    notice?: string | null;
};

/**
 * CatalogStatus
 */
export const CatalogStatus = {
    Draft: 'draft',
    OnModeration: 'on_moderation',
    NeedsRevision: 'needs_revision',
    Published: 'published',
    Unpublished: 'unpublished',
    Rejected: 'rejected',
    Archived: 'archived'
} as const;

/**
 * CatalogStatus
 */
export type CatalogStatus = typeof CatalogStatus[keyof typeof CatalogStatus];

/**
 * CatalogType
 *
 * Тип каталожной записи: владелец медиа, ревизии, истории (6.3.6, 6.3.11, 6.3.12).
 */
export const CatalogType = {
    Wine: 'wine',
    Set: 'set',
    Drop: 'drop'
} as const;

/**
 * CatalogType
 *
 * Тип каталожной записи: владелец медиа, ревизии, истории (6.3.6, 6.3.11, 6.3.12).
 */
export type CatalogType = typeof CatalogType[keyof typeof CatalogType];

/**
 * ChannelOut
 */
export type ChannelOut = {
    code: SupportChannel;
    /**
     * Label
     */
    label: string;
};

/**
 * ChannelSalesOut
 */
export type ChannelSalesOut = {
    /**
     * Channel
     */
    channel: 'delivery' | 'in_set';
    /**
     * Label
     */
    label: string;
    /**
     * Bottles
     */
    bottles: number;
    /**
     * Orders
     */
    orders: number;
    amount: Money;
    /**
     * Amount Label
     */
    amount_label: string;
    /**
     * Share Pct
     */
    share_pct: number;
};

/**
 * CharacteristicOut
 */
export type CharacteristicOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
    link?: AppModulesCatSchemasAppNamedRef | null;
};

/**
 * CheckResultOut
 */
export type CheckResultOut = {
    /**
     * Ok
     */
    ok: boolean;
    /**
     * Message
     */
    message: string;
    /**
     * Duration Ms
     */
    duration_ms?: number;
};

/**
 * CheckoutStatus
 */
export const CheckoutStatus = {
    Open: 'open',
    Paid: 'paid',
    Failed: 'failed',
    Expired: 'expired',
    Cancelled: 'cancelled',
    Superseded: 'superseded'
} as const;

/**
 * CheckoutStatus
 */
export type CheckoutStatus = typeof CheckoutStatus[keyof typeof CheckoutStatus];

/**
 * ChildAgeGroup
 */
export const ChildAgeGroup = {
    Under6: 'under_6',
    Age712: 'age_7_12',
    Age1317: 'age_13_17'
} as const;

/**
 * ChildAgeGroup
 */
export type ChildAgeGroup = typeof ChildAgeGroup[keyof typeof ChildAgeGroup];

/**
 * ChildCalcOut
 */
export type ChildCalcOut = {
    age_group: ChildAgeGroup;
    /**
     * Pct
     */
    pct: number;
    amount: Money;
};

/**
 * CitiesOut
 */
export type CitiesOut = {
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Items
     */
    items: Array<AppModulesAccSchemasAppCityOut>;
};

/**
 * ClientCityOut
 */
export type ClientCityOut = {
    /**
     * Name
     */
    name: string;
    /**
     * Lat
     */
    lat: number;
    /**
     * Lon
     */
    lon: number;
};

/**
 * ClientIntegrationsOut
 *
 * Публичные параметры включённых интеграций (BR-INT-08): не секреты. Пока интеграция
 * выключена, не настроена или её адаптер ещё не подключён — значения `null`.
 */
export type ClientIntegrationsOut = {
    /**
     * Auth Providers
     *
     * Включённые и настроенные способы входа в порядке `auth.providers`
     */
    auth_providers: Array<AuthProviderCode>;
    google_client_ids: GoogleClientIdsOut;
    /**
     * Vk Client Id
     */
    vk_client_id?: string | null;
    /**
     * Yandex Client Id
     */
    yandex_client_id?: string | null;
    /**
     * Apple Services Id
     */
    apple_services_id?: string | null;
    maps: MapsClientOut;
    push: PushClientOut;
    video: VideoClientOut;
    recognition: RecognitionClientOut;
    geo: GeoClientOut;
    payments: PaymentsClientOut;
};

/**
 * ClientPlatform
 */
export const ClientPlatform = {
    Pwa: 'pwa',
    AndroidRustore: 'android_rustore',
    IosAppstore: 'ios_appstore',
    AndroidGplay: 'android_gplay'
} as const;

/**
 * ClientPlatform
 */
export type ClientPlatform = typeof ClientPlatform[keyof typeof ClientPlatform];

/**
 * Climate
 */
export const Climate = {
    TemperateContinental: 'temperate_continental',
    Mediterranean: 'mediterranean',
    Maritime: 'maritime',
    SemiDesert: 'semi_desert',
    Mountain: 'mountain'
} as const;

/**
 * Climate
 */
export type Climate = typeof Climate[keyof typeof Climate];

/**
 * ClubConfigOut
 */
export type ClubConfigOut = {
    /**
     * Level Id
     */
    level_id: string;
    /**
     * Club Min Xp
     */
    club_min_xp: number;
    /**
     * Step Xp
     */
    step_xp: number;
};

/**
 * ClubGoalOut
 */
export type ClubGoalOut = {
    /**
     * Reward Id
     */
    reward_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Description
     */
    description: string | null;
    /**
     * Cost
     */
    cost: number;
    /**
     * Icon Url
     */
    icon_url: string | null;
    /**
     * Have
     *
     * Баланс; до клуба — 0
     */
    have: number;
    /**
     * Left
     */
    left: number;
    /**
     * Reached
     */
    reached: boolean;
    /**
     * Progress Label
     *
     * «have / cost»
     */
    progress_label: string;
    /**
     * Text
     */
    text: string;
};

/**
 * ClubOut
 */
export type ClubOut = {
    /**
     * Open
     */
    open: boolean;
    /**
     * Club Min Xp
     */
    club_min_xp: number | null;
    /**
     * Step Xp
     */
    step_xp: number | null;
    /**
     * Bonus Earned
     */
    bonus_earned: number;
    /**
     * Bonus Spent
     */
    bonus_spent: number;
    /**
     * Bonus Balance
     */
    bonus_balance: number;
    /**
     * Xp To Next Bonus
     */
    xp_to_next_bonus: number | null;
    /**
     * Text
     *
     * Текст листа «Бонусы Wine Club» (11.6.2)
     */
    text: string | null;
    /**
     * Card Header
     *
     * «бонусы открыты» / «откроются на 10 000 XP»
     */
    card_header: string | null;
    /**
     * Card Title
     *
     * «N бонусов» / «Бонусы Wine Club»
     */
    card_title: string | null;
    /**
     * Card Text
     *
     * Текст карточки бонусов экрана «XP и уровни»
     */
    card_text: string | null;
};

/**
 * CntPage[DocumentRowOut]
 */
export type CntPageDocumentRowOut = {
    /**
     * Items
     */
    items: Array<DocumentRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои»
     */
    notice?: string | null;
};

/**
 * CntPage[LikeLogRowOut]
 */
export type CntPageLikeLogRowOut = {
    /**
     * Items
     */
    items: Array<LikeLogRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои»
     */
    notice?: string | null;
};

/**
 * CntPage[PublicationRowOut]
 */
export type CntPagePublicationRowOut = {
    /**
     * Items
     */
    items: Array<PublicationRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои»
     */
    notice?: string | null;
};

/**
 * CntPage[StoryRowOut]
 */
export type CntPageStoryRowOut = {
    /**
     * Items
     */
    items: Array<StoryRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои»
     */
    notice?: string | null;
};

/**
 * CntPage[SubscriptionLogRowOut]
 */
export type CntPageSubscriptionLogRowOut = {
    /**
     * Items
     */
    items: Array<SubscriptionLogRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои»
     */
    notice?: string | null;
};

/**
 * CodeLabel
 */
export type CodeLabel = {
    /**
     * Code
     */
    code: string;
    /**
     * Label
     */
    label: string;
};

/**
 * CodeLoginIn
 */
export type CodeLoginIn = {
    /**
     * Email
     */
    email: string;
    /**
     * Code
     */
    code: string;
};

/**
 * CodeRequestIn
 */
export type CodeRequestIn = {
    /**
     * Email
     */
    email: string;
    purpose: AuthCodePurpose;
};

/**
 * CodeSentOut
 */
export type CodeSentOut = {
    /**
     * Ttl Sec
     *
     * Срок действия кода, секунды (600)
     */
    ttl_sec: number;
    /**
     * Resend After Sec
     *
     * Повторная отправка — не раньше, секунды (59)
     */
    resend_after_sec: number;
};

/**
 * CodeVerifyIn
 */
export type CodeVerifyIn = {
    /**
     * Email
     */
    email: string;
    purpose: AuthCodePurpose;
    /**
     * Code
     */
    code: string;
};

/**
 * CollectedOut
 */
export type CollectedOut = {
    /**
     * Level
     */
    level: 'bronze' | 'gold';
    /**
     * Title
     */
    title: string;
    /**
     * Label
     */
    label: string;
};

/**
 * CommentAuthorOut
 */
export type CommentAuthorOut = {
    /**
     * Name
     */
    name: string;
    /**
     * Label
     *
     * «автор» / роль / «участник»
     */
    label: string;
};

/**
 * CommentIn
 */
export type CommentIn = {
    /**
     * Comment
     *
     * Замечания для автора
     */
    comment: string;
};

/**
 * CommentListOut
 */
export type CommentListOut = {
    /**
     * Items
     */
    items: Array<CommentOut>;
    /**
     * Total
     */
    total: number;
    /**
     * Total Label
     *
     * «{N} ответов» — комментарии читателей
     */
    total_label: string;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * More Label
     *
     * «Ещё {N} ответов — открываются по мере обсуждения»
     */
    more_label: string | null;
};

/**
 * CommentOut
 */
export type CommentOut = {
    /**
     * Id
     */
    id: string;
    author: CommentAuthorOut;
    /**
     * Text
     */
    text: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Time Label
     */
    time_label: string;
    reply: CommentReplyOut | null;
};

/**
 * CommentReplyOut
 */
export type CommentReplyOut = {
    /**
     * Id
     */
    id: string;
    author: CommentAuthorOut;
    /**
     * Text
     */
    text: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Time Label
     */
    time_label: string;
};

/**
 * CompleteIn
 */
export type CompleteIn = {
    /**
     * Course Id
     */
    course_id?: string | null;
};

/**
 * CompleteOut
 */
export type CompleteOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Completed At
     */
    completed_at: string;
    /**
     * Already Completed
     */
    already_completed: boolean;
    course_progress: CourseProgressBriefOut | null;
    section_completed: SectionCompletedOut | null;
    /**
     * Course Completed
     */
    course_completed: boolean;
    reward: RewardOut | null;
    win: WinOut | null;
    /**
     * Guest First Lesson
     */
    guest_first_lesson: boolean;
};

/**
 * ConferenceOut
 */
export type ConferenceOut = {
    /**
     * Url
     */
    url: string;
};

/**
 * ConfirmIn
 */
export type ConfirmIn = {
    /**
     * Confirm
     *
     * Подтверждение действия (true)
     */
    confirm: true;
};

/**
 * ConsentItemIn
 */
export type ConsentItemIn = {
    kind: ConsentKind;
    /**
     * Document Version Id
     *
     * Версия документа CNT (`personal_data_consent`, `privacy_policy`), опубликованная на момент согласия
     */
    document_version_id?: string | null;
};

/**
 * ConsentKind
 */
export const ConsentKind = {
    Age18Confirmation: 'age_18_confirmation',
    PersonalDataConsent: 'personal_data_consent',
    PrivacyPolicy: 'privacy_policy'
} as const;

/**
 * ConsentKind
 */
export type ConsentKind = typeof ConsentKind[keyof typeof ConsentKind];

/**
 * ConsentsOut
 */
export type ConsentsOut = {
    /**
     * Items
     */
    items: Array<AppModulesAccSchemasAppConsentOut>;
};

/**
 * ContentArchiveIn
 */
export type ContentArchiveIn = {
    reason: ContentArchiveReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * ContentArchiveReason
 *
 * Причина архивирования (12.3.2, 12.3.9 — общая модалка).
 */
export const ContentArchiveReason = {
    NotActual: 'not_actual',
    Duplicate: 'duplicate',
    PartnerTerminated: 'partner_terminated',
    DataError: 'data_error'
} as const;

/**
 * ContentArchiveReason
 *
 * Причина архивирования (12.3.2, 12.3.9 — общая модалка).
 */
export type ContentArchiveReason = typeof ContentArchiveReason[keyof typeof ContentArchiveReason];

/**
 * ContentAuthorOut
 */
export type ContentAuthorOut = {
    /**
     * Member Id
     *
     * null — платформа (`brand.name`)
     */
    member_id: string | null;
    /**
     * Name
     */
    name: string;
    /**
     * Role Label
     *
     * «Сомелье», «Винодел»…; у платформы — null
     */
    role_label: string | null;
    /**
     * Kicker
     *
     * «{роль} · {винодельня | регион}» (BR-CNT-37)
     */
    kicker: string | null;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * Initial
     */
    initial: string;
    /**
     * Target
     *
     * `expert:{id}` / `maker:{id}`; null — страница автора недоступна
     */
    target: string | null;
};

/**
 * ContentGateOut
 */
export type ContentGateOut = {
    /**
     * Gate
     */
    gate: string;
    /**
     * Gate Title
     */
    gate_title: string;
    /**
     * Passed
     */
    passed: boolean;
    required_tier: ContentGateTierOut | null;
    current_tier: ContentGateTierOut | null;
    /**
     * Progress
     */
    progress: {
        [key: string]: unknown;
    } | null;
    /**
     * Note
     *
     * «Откроется на ступени …»; null — гейт пройден
     */
    note: string | null;
};

/**
 * ContentGateTierOut
 */
export type ContentGateTierOut = {
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Xp Min
     */
    xp_min?: number | null;
    /**
     * Accuracy Min
     */
    accuracy_min?: number | null;
};

/**
 * ContentHistoryOut
 */
export type ContentHistoryOut = {
    /**
     * At
     */
    at: string;
    /**
     * Actor
     */
    actor: string;
    /**
     * Text
     */
    text: string;
};

/**
 * ContentIn
 *
 * Описание, детали, шаги, медиа (у экскурсии — ещё «Как добраться»).
 */
export type ContentIn = {
    description?: LocalizedText | null;
    /**
     * Details
     */
    details?: Array<PairIn> | null;
    /**
     * Steps
     */
    steps?: Array<StepIn> | null;
    /**
     * Route
     */
    route?: Array<PairIn> | null;
    /**
     * Media
     */
    media?: Array<EventMediaIn> | null;
};

/**
 * ContentLanguage
 */
export const ContentLanguage = { En: 'en', Es: 'es' } as const;

/**
 * ContentLanguage
 */
export type ContentLanguage = typeof ContentLanguage[keyof typeof ContentLanguage];

/**
 * ContentMediaOut
 */
export type ContentMediaOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Kind
     */
    kind: string;
    /**
     * Url
     */
    url: string | null;
    /**
     * Preview Url
     */
    preview_url?: string | null;
    /**
     * Caption
     */
    caption?: string | null;
    /**
     * Status
     *
     * ready | processing
     */
    status: string;
};

/**
 * ContentOptionOut
 */
export type ContentOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle?: string | null;
    /**
     * Type
     */
    type?: string | null;
};

/**
 * ContentRefOut
 *
 * Отметка или связанный объект: название и переход.
 */
export type ContentRefOut = {
    /**
     * Type
     */
    type: string;
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle?: string | null;
    /**
     * Target
     *
     * Ключ экрана; null — перехода нет
     */
    target?: string | null;
};

/**
 * ContentStatus
 */
export const ContentStatus = {
    Draft: 'draft',
    OnModeration: 'on_moderation',
    NeedsRevision: 'needs_revision',
    Published: 'published',
    Unpublished: 'unpublished',
    Rejected: 'rejected',
    Archived: 'archived'
} as const;

/**
 * ContentStatus
 */
export type ContentStatus = typeof ContentStatus[keyof typeof ContentStatus];

/**
 * ContentStepOut
 */
export type ContentStepOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Done
     */
    done: boolean;
    /**
     * Current
     */
    current: boolean;
    /**
     * Note
     */
    note: string;
};

/**
 * ContentTargetOut
 *
 * Переход на экран приложения: `target` — ключ экрана (`bottle:{id}`, `region:{id}`,
 * `post:{id}`, `path-lesson:{регион}.{nn}`…).
 */
export type ContentTargetOut = {
    /**
     * Screen
     */
    screen: string;
    /**
     * Id
     */
    id?: string | null;
    /**
     * Target
     */
    target: string;
};

/**
 * ContinentOut
 */
export type ContinentOut = {
    /**
     * Code
     */
    code: string;
    /**
     * Name
     */
    name: string;
    /**
     * Count
     *
     * Стран на табе: карточки и «Скоро»
     */
    count: number;
    /**
     * Countries
     */
    countries: Array<CountryCardOut>;
    /**
     * Soon
     */
    soon: Array<SoonChipOut>;
    /**
     * Empty Title
     */
    empty_title: string | null;
    /**
     * Empty Text
     */
    empty_text: string | null;
};

/**
 * ContourIn
 */
export type ContourIn = {
    polygon: GeoPolygonIn | null;
};

/**
 * CountryBrief
 */
export type CountryBrief = {
    /**
     * Code
     */
    code: string;
    /**
     * Name
     */
    name: string;
};

/**
 * CountryCan
 */
export type CountryCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * CountryCardOut
 */
export type CountryCardOut = {
    /**
     * Code
     */
    code: string;
    /**
     * Name
     */
    name: string;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Your Country
     */
    your_country: boolean;
    /**
     * Has Update
     */
    has_update: boolean;
    /**
     * State
     */
    state: 'done' | 'progress' | 'new';
    /**
     * Subtitle
     */
    subtitle: string;
    /**
     * Regions Total
     */
    regions_total: number;
    /**
     * Collected
     */
    collected: number;
    /**
     * Stamps
     */
    stamps: number;
    /**
     * Lessons Count
     */
    lessons_count: number;
    /**
     * Target
     */
    target: string;
};

/**
 * CountryCreateIn
 */
export type CountryCreateIn = {
    /**
     * Code
     *
     * ISO 3166-1 alpha-2 (SystemCountry)
     */
    code: string;
    /**
     * Sort Order
     */
    sort_order?: number | null;
    /**
     * Illustration File Id
     */
    illustration_file_id?: string | null;
    /**
     * Picture File Id
     */
    picture_file_id?: string | null;
    /**
     * Facts
     */
    facts?: Array<CountryFactIn>;
    /**
     * Quiz Ids
     */
    quiz_ids?: Array<string>;
    /**
     * Note
     */
    note?: string | null;
};

/**
 * CountryDetailOut
 */
export type CountryDetailOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Code
     */
    code: string;
    name: LocalizedText | null;
    /**
     * Sort Order
     */
    sort_order: number;
    /**
     * Sort Order Label
     */
    sort_order_label: string;
    /**
     * Regions Count
     */
    regions_count: number;
    /**
     * Note
     */
    note: string | null;
    illustration: AppModulesGeoSchemasCommonFileRefOut | null;
    picture: AppModulesGeoSchemasCommonFileRefOut | null;
    /**
     * Regions
     */
    regions: Array<CountryRegionOut>;
    /**
     * Facts
     */
    facts: Array<AppModulesGeoSchemasAdminFactOut>;
    /**
     * Quizzes
     */
    quizzes: Array<CountryQuizOut>;
    can: CountryCan;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * CountryFactIn
 */
export type CountryFactIn = {
    title?: LocalizedText | null;
    text?: LocalizedText | null;
};

/**
 * CountryOut
 */
export type CountryOut = {
    /**
     * Code
     *
     * ISO 3166-1 alpha-2, верхний регистр
     */
    code: string;
    /**
     * Name
     */
    name: string;
};

/**
 * CountryPatchIn
 */
export type CountryPatchIn = {
    /**
     * Sort Order
     */
    sort_order?: number | null;
    /**
     * Note
     */
    note?: string | null;
    /**
     * Illustration File Id
     */
    illustration_file_id?: string | null;
    /**
     * Picture File Id
     */
    picture_file_id?: string | null;
};

/**
 * CountryQuizOut
 */
export type CountryQuizOut = {
    /**
     * Test Id
     */
    test_id: string;
    title: LocalizedText | null;
    /**
     * Source
     */
    source: 'auto' | 'manual';
};

/**
 * CountryRef
 */
export type CountryRef = {
    /**
     * Id
     */
    id: string;
    /**
     * Code
     */
    code: string;
    name: LocalizedText | null;
};

/**
 * CountryRefOut
 */
export type CountryRefOut = {
    /**
     * Code
     *
     * ISO 3166-1 alpha-2, верхний регистр
     */
    code: string;
    /**
     * Name
     */
    name: string;
};

/**
 * CountryRegionOut
 */
export type CountryRegionOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
    status: RegionStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Counts
     *
     * wineries, wines, courses
     */
    counts: {
        [key: string]: number;
    };
};

/**
 * CountryRowOut
 */
export type CountryRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Code
     */
    code: string;
    name: LocalizedText | null;
    /**
     * Regions Count
     *
     * Регионы страны в любом статусе, кроме архивных
     */
    regions_count: number;
    /**
     * Sort Order
     */
    sort_order: number;
    /**
     * Sort Order Label
     */
    sort_order_label: string;
    can: CountryCan;
};

/**
 * CountryScreenOut
 */
export type CountryScreenOut = {
    country: PathCountryOut;
    /**
     * Facts
     */
    facts: Array<FactItemOut>;
    /**
     * Facts Label
     */
    facts_label: string;
    /**
     * Facts Updated
     */
    facts_updated: boolean;
    quiz: PathCountryQuizOut | null;
    /**
     * Quiz Updated
     */
    quiz_updated: boolean;
    /**
     * Xp Total
     */
    xp_total: number | null;
    /**
     * Regions
     */
    regions: Array<RegionNodeOut>;
    /**
     * Regions Label
     */
    regions_label: string;
};

/**
 * CountsOut
 */
export type CountsOut = {
    /**
     * Bottles
     */
    bottles: number;
    /**
     * Sets
     *
     * Включая сеты вслепую
     */
    sets: number;
    /**
     * Drops
     *
     * Как в списке дропов: опубликованные и «Завершённые» (BR-CAT-47)
     */
    drops: number;
};

/**
 * CourseAccessOut
 */
export type CourseAccessOut = {
    /**
     * Opened
     */
    opened: boolean;
    /**
     * Purchased
     */
    purchased: boolean;
    /**
     * Free
     */
    free: boolean;
};

/**
 * CourseCan
 */
export type CourseCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Submit
     */
    submit?: boolean;
    /**
     * Submit Edits
     */
    submit_edits?: boolean;
    /**
     * Waiting
     *
     * Эксперт: неактивная кнопка «Ждёт решения администратора»
     */
    waiting?: boolean;
    /**
     * Decide
     */
    decide?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Edit Price
     */
    edit_price?: boolean;
    /**
     * Edit Store Products
     */
    edit_store_products?: boolean;
    /**
     * Edit Program
     */
    edit_program?: boolean;
};

/**
 * CourseCardOut
 */
export type CourseCardOut = {
    /**
     * Course Id
     */
    course_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string;
    /**
     * Note
     */
    note: string | null;
    /**
     * Opened
     */
    opened: boolean;
    offer: LearnOfferOut | null;
    /**
     * Target
     */
    target: string;
};

/**
 * CourseCreateIn
 */
export type CourseCreateIn = {
    title: LocalizedText;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Is Main
     *
     * «Обязательный курс региона»: учитывается только с регионом; только admin
     */
    is_main?: boolean;
    /**
     * Duration Min
     */
    duration_min?: number | null;
    /**
     * Is Paid
     */
    is_paid?: boolean;
    price?: LearnPriceIn | null;
    description: LocalizedText;
    /**
     * Theses
     */
    theses?: Array<LocalizedText>;
    /**
     * Media
     */
    media?: Array<MediaItemIn>;
    related?: RelatedIn | null;
    /**
     * Store Products
     */
    store_products?: {
        [key: string]: StoreProductIn | null;
    } | null;
};

/**
 * CourseInfoOut
 */
export type CourseInfoOut = {
    /**
     * Id
     */
    id: string;
    kind: CourseKind;
    /**
     * Title
     */
    title: string;
    region: LearnRefOut | null;
    /**
     * Description
     */
    description: Array<BlockOut>;
    /**
     * Theses
     */
    theses: Array<string>;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Lessons Count
     */
    lessons_count: number;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Duration Label
     */
    duration_label: string;
    /**
     * Xp Total
     */
    xp_total: number | null;
    /**
     * Header
     */
    header: string;
};

/**
 * CourseKind
 */
export const CourseKind = {
    Intro: 'intro',
    RegionMain: 'region_main',
    Additional: 'additional'
} as const;

/**
 * CourseKind
 */
export type CourseKind = typeof CourseKind[keyof typeof CourseKind];

/**
 * CourseLessonItemOut
 */
export type CourseLessonItemOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Number
     */
    number: string;
    /**
     * Title
     */
    title: string;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Is Paid
     */
    is_paid: boolean;
    /**
     * Open
     */
    open: boolean;
    /**
     * State
     */
    state: 'done' | 'next' | 'open' | 'locked';
    price: Money | null;
    /**
     * Likes
     */
    likes: number;
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Label
     */
    label: string;
    /**
     * Target
     */
    target: string;
};

/**
 * CourseLikeOut
 */
export type CourseLikeOut = {
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Likes Count
     */
    likes_count: number;
};

/**
 * CourseOptionOut
 */
export type CourseOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
};

/**
 * CourseOut
 */
export type CourseOut = {
    /**
     * Id
     */
    id: string;
    kind: CourseKind;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Main Label
     *
     * «Обязательный курс … · даёт штамп» / «Дополнительный»
     */
    main_label: string | null;
    title: LocalizedText | null;
    region: LearnRefOut | null;
    author: AuthorOut;
    /**
     * Duration Min
     */
    duration_min: number | null;
    /**
     * Duration Total
     *
     * `duration_min` или сумма длительностей уроков
     */
    duration_total: number;
    /**
     * Lessons Count
     */
    lessons_count: number;
    /**
     * Tests Count
     */
    tests_count: number;
    /**
     * Is Paid
     */
    is_paid: boolean;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string;
    store_products: StoreProductsOut;
    description: LocalizedText | null;
    /**
     * Theses
     */
    theses: Array<LocalizedText>;
    /**
     * Media
     */
    media: Array<LearnMediaOut>;
    related: RelatedOut;
    /**
     * Sections
     */
    sections: Array<ProgramSectionOut>;
    /**
     * Moderation Note
     */
    moderation_note: string | null;
    status: ContentStatus;
    /**
     * Status Label
     */
    status_label: string;
    display_status: ContentStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    published_revision: LearnRevisionOut | null;
    pending_revision: LearnRevisionOut | null;
    /**
     * Has Unsubmitted Changes
     */
    has_unsubmitted_changes: boolean;
    /**
     * Edits Hint
     */
    edits_hint: string | null;
    /**
     * First Published At
     */
    first_published_at: string | null;
    archive_reason: LearnArchiveReason | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Usages
     */
    usages: Array<LearnUsageItemOut>;
    can: CourseCan;
    /**
     * Message
     *
     * Тост после создания
     */
    message?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * CoursePatchIn
 */
export type CoursePatchIn = {
    title?: LocalizedText | null;
    /**
     * Region Id
     *
     * null — «Без региона»
     */
    region_id?: string | null;
    /**
     * Duration Min
     */
    duration_min?: number | null;
    /**
     * Is Paid
     */
    is_paid?: boolean | null;
    price?: LearnPriceIn | null;
    description?: LocalizedText | null;
    /**
     * Theses
     */
    theses?: Array<LocalizedText> | null;
    related?: RelatedIn | null;
    /**
     * Store Products
     */
    store_products?: {
        [key: string]: StoreProductIn | null;
    } | null;
};

/**
 * CourseProgressBriefOut
 */
export type CourseProgressBriefOut = {
    /**
     * Done
     */
    done: number;
    /**
     * All
     */
    all: number;
    /**
     * Pct
     */
    pct: number;
    /**
     * Completed
     */
    completed: boolean;
    /**
     * Next Lesson Id
     */
    next_lesson_id: string | null;
};

/**
 * CourseRowOut
 */
export type CourseRowOut = {
    /**
     * Id
     */
    id: string;
    kind: CourseKind;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Region Id
     */
    region_id: string | null;
    /**
     * Region Label
     */
    region_label: string;
    /**
     * Access Label
     *
     * «Бесплатно целиком» / «Бесплатно» / «{цена}»
     */
    access_label: string;
    /**
     * Is Paid
     */
    is_paid: boolean;
    price: Money | null;
    /**
     * Lessons Count
     */
    lessons_count: number;
    /**
     * Tests Count
     */
    tests_count: number;
    /**
     * Duration Min
     *
     * `duration_min` или сумма уроков программы
     */
    duration_min: number | null;
    /**
     * Duration Label
     */
    duration_label: string;
    status: ContentStatus;
    display_status: ContentStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * CourseScreenOut
 */
export type CourseScreenOut = {
    course: CourseInfoOut;
    author: AuthorBriefOut;
    access: CourseAccessOut;
    progress: ProgressOut;
    offer: LearnOfferOut | null;
    /**
     * Lessons
     */
    lessons: Array<CourseLessonItemOut>;
    cta: CtaOut;
    /**
     * Subtitle
     */
    subtitle: string;
    reactions: CourseLikeOut;
};

/**
 * CourseStatsOut
 */
export type CourseStatsOut = {
    /**
     * Purchases
     *
     * Активные доступы к курсу
     */
    purchases: number;
    /**
     * Revoked
     */
    revoked: number;
    /**
     * Started
     */
    started: number;
    /**
     * Completed
     */
    completed: number;
    test_pass: TestPassOut;
    /**
     * Likes
     */
    likes: number;
};

/**
 * CredentialsOut
 */
export type CredentialsOut = {
    /**
     * Login
     */
    login: string;
    /**
     * Temporary Password
     *
     * Показывается один раз — только в этом ответе (BR-ACC-56)
     */
    temporary_password: string;
    /**
     * Where
     *
     * Где действует логин
     */
    where: string;
};

/**
 * CtaOut
 */
export type CtaOut = {
    /**
     * Label
     */
    label: string;
    /**
     * Action
     */
    action: 'open' | 'complete' | 'test' | 'cart' | 'checkout' | 'store' | 'none';
    /**
     * Target
     */
    target?: string | null;
    /**
     * Test Id
     */
    test_id?: string | null;
    /**
     * Enabled
     */
    enabled?: boolean;
};

/**
 * Currency
 */
export const Currency = { Rub: 'RUB', Eur: 'EUR' } as const;

/**
 * Currency
 */
export type Currency = typeof Currency[keyof typeof Currency];

/**
 * CurrentOwnerOut
 */
export type CurrentOwnerOut = {
    /**
     * Id
     */
    id: string | null;
    /**
     * Name
     */
    name: string | null;
};

/**
 * CursorPage[ExcursionCard]
 */
export type CursorPageExcursionCard = {
    /**
     * Items
     */
    items: Array<ExcursionCard>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * CursorPage[TastingCard]
 */
export type CursorPageTastingCard = {
    /**
     * Items
     */
    items: Array<TastingCard>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * CursorPage[TripCard]
 */
export type CursorPageTripCard = {
    /**
     * Items
     */
    items: Array<TripCard>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * CursorPage[TstPurchasedWineOut]
 */
export type CursorPageTstPurchasedWineOut = {
    /**
     * Items
     */
    items: Array<TstPurchasedWineOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * CursorPage[UserHistoryItem]
 */
export type CursorPageUserHistoryItem = {
    /**
     * Items
     */
    items: Array<UserHistoryItem>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * DashboardAttentionOut
 */
export type DashboardAttentionOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Count
     */
    count: number;
    /**
     * Count Label
     */
    count_label: string;
    /**
     * Items
     */
    items: Array<DashboardAttentionRowOut>;
};

/**
 * DashboardAttentionRowOut
 */
export type DashboardAttentionRowOut = {
    /**
     * Rule
     */
    rule: string;
    /**
     * Tone
     */
    tone: 'bad' | 'warn';
    /**
     * Title
     */
    title: string;
    /**
     * Note
     */
    note: string;
    link: DashboardLinkOut | null;
};

/**
 * DashboardCatalogTileOut
 */
export type DashboardCatalogTileOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Group
     */
    group: string;
    /**
     * Group Label
     */
    group_label: string;
    /**
     * Label
     */
    label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Total
     */
    total: number | null;
    /**
     * Published
     */
    published: number | null;
    /**
     * On Moderation
     */
    on_moderation: number | null;
    /**
     * Added
     *
     * Создано за период и не в архиве (вид «Динамика»)
     */
    added: number | null;
    /**
     * Value Label
     *
     * Обзор — всего; «Динамика» — «+N»
     */
    value_label: string;
    /**
     * Sub
     */
    sub: string;
    /**
     * Link
     */
    link: string;
};

/**
 * DashboardCommunityOut
 */
export type DashboardCommunityOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Funnel
     */
    funnel: Array<DashboardFigureOut>;
    /**
     * Tiles
     */
    tiles: Array<DashboardFigureOut>;
};

/**
 * DashboardFigureOut
 *
 * Плитка воронки и вклада «Сообщества».
 */
export type DashboardFigureOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Value
     */
    value: number | null;
    /**
     * Value Label
     */
    value_label: string;
    /**
     * Sub
     */
    sub: string | null;
    /**
     * Link
     */
    link: string | null;
    /**
     * Unavailable Reason
     */
    unavailable_reason?: string | null;
};

/**
 * DashboardHeaderOut
 */
export type DashboardHeaderOut = {
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
    /**
     * Lead
     */
    lead: string;
    /**
     * Period Dates
     *
     * «{даты периода} · данные на …» — даты
     */
    period_dates: string;
    /**
     * Data As Of
     *
     * Время последнего пересчёта агрегатов
     */
    data_as_of: string | null;
};

/**
 * DashboardLinkOut
 */
export type DashboardLinkOut = {
    /**
     * Section
     */
    section: string;
    /**
     * Id
     */
    id?: string | null;
};

/**
 * DashboardMiniOut
 */
export type DashboardMiniOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Value
     */
    value: Money | number | null;
    /**
     * Value Label
     */
    value_label: string;
    /**
     * Delta Pct
     */
    delta_pct?: number | null;
    /**
     * Delta Label
     */
    delta_label?: string | null;
    /**
     * Sub
     */
    sub?: string | null;
    /**
     * Link
     */
    link?: string | null;
    /**
     * Unavailable Reason
     */
    unavailable_reason?: string | null;
};

/**
 * DashboardModerationOut
 */
export type DashboardModerationOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<DashboardModerationRowOut>;
    /**
     * Action Label
     */
    action_label: string | null;
    action_link: DashboardLinkOut | null;
};

/**
 * DashboardModerationRowOut
 */
export type DashboardModerationRowOut = {
    /**
     * Number
     */
    number: number;
    /**
     * Title
     */
    title: string;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Age Label
     */
    age_label: string;
    /**
     * Returned
     */
    returned: boolean;
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    link: DashboardLinkOut | null;
};

/**
 * DashboardNowOut
 */
export type DashboardNowOut = {
    moderation: DashboardModerationOut;
    schedule: DashboardScheduleOut;
    attention: DashboardAttentionOut;
};

/**
 * DashboardOptionOut
 */
export type DashboardOptionOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
};

/**
 * DashboardOut
 */
export type DashboardOut = {
    /**
     * Role
     */
    role: 'admin' | 'winemaker' | 'expert';
    /**
     * View
     *
     * Только администратор
     */
    view: 'overview' | 'dynamics' | null;
    period: DashboardPeriodOut;
    header: DashboardHeaderOut;
    /**
     * Views
     *
     * Переключатель вида (только admin)
     */
    views: Array<DashboardOptionOut>;
    /**
     * Periods
     *
     * Переключатель периода по роли и виду
     */
    periods: Array<DashboardOptionOut>;
    /**
     * Months
     */
    months: Array<DashboardOptionOut>;
    /**
     * Years
     */
    years: Array<DashboardOptionOut>;
    revenue?: DashboardRevenueOut | null;
    community?: DashboardCommunityOut | null;
    /**
     * Catalog
     */
    catalog?: Array<DashboardCatalogTileOut> | null;
    /**
     * Catalog Title
     */
    catalog_title?: string | null;
    /**
     * Catalog Lead
     */
    catalog_lead?: string | null;
    series?: DashboardSeriesOut | null;
    /**
     * Structure
     */
    structure?: Array<DashboardStructureItemOut> | null;
    /**
     * Structure Title
     */
    structure_title?: string | null;
    /**
     * Structure Center
     */
    structure_center?: string | null;
    /**
     * Structure Available
     */
    structure_available?: boolean | null;
    /**
     * Tiles
     */
    tiles?: Array<DashboardTileOut> | null;
    now?: DashboardNowOut | null;
};

/**
 * DashboardPeriodOut
 */
export type DashboardPeriodOut = {
    /**
     * Kind
     */
    kind: 'week' | 'month' | 'year' | 'all' | 'custom';
    /**
     * Start
     */
    start: string;
    /**
     * End
     */
    end: string;
    /**
     * Label
     *
     * «за 7 дней», «за сентябрь 2026», «за 2026 год», …
     */
    label: string;
    /**
     * Dates Label
     *
     * Даты периода в шапке
     */
    dates_label: string;
    /**
     * Compare Start
     */
    compare_start: string | null;
    /**
     * Compare End
     */
    compare_end: string | null;
    /**
     * Compare Note
     *
     * «к прошлым 7 дням», «к прошлому месяцу», …
     */
    compare_note: string | null;
    /**
     * Month
     */
    month?: string | null;
    /**
     * Year
     */
    year?: number | null;
};

/**
 * DashboardRevenueOut
 */
export type DashboardRevenueOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Link
     *
     * Раздел «Распределение выручки» (ORD)
     */
    link: string;
    /**
     * Link Label
     */
    link_label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Unavailable Reason
     */
    unavailable_reason: string | null;
    gross: Money | null;
    /**
     * Gross Label
     */
    gross_label: string;
    /**
     * Delta Pct
     */
    delta_pct: number | null;
    /**
     * Delta Label
     */
    delta_label: string | null;
    /**
     * Delta Note
     */
    delta_note: string | null;
    /**
     * Period Note
     *
     * «Всё время»: «{N} мес. · с {месяц год}»
     */
    period_note: string | null;
    /**
     * Split
     */
    split: Array<DashboardSplitOut>;
    /**
     * Minis
     */
    minis: Array<DashboardMiniOut>;
};

/**
 * DashboardScheduleOut
 */
export type DashboardScheduleOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Count
     */
    count: number;
    /**
     * Count Label
     */
    count_label: string;
    /**
     * Items
     */
    items: Array<DashboardScheduleRowOut>;
};

/**
 * DashboardScheduleRowOut
 */
export type DashboardScheduleRowOut = {
    /**
     * Starts At
     */
    starts_at: string;
    /**
     * Day Label
     *
     * «6 сен.»
     */
    day_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Kind
     */
    kind: string;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Taken
     */
    taken: number;
    /**
     * Seats
     */
    seats: number | null;
    /**
     * Fill Pct
     */
    fill_pct: number | null;
    /**
     * Fill Label
     *
     * «{занято} из {мест}», «мест нет», «{N} человек»
     */
    fill_label: string;
    /**
     * Full
     */
    full: boolean;
    link: DashboardLinkOut;
};

/**
 * DashboardSeriesOut
 */
export type DashboardSeriesOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Scale
     */
    scale: 'days' | 'weeks' | 'months' | 'period_weeks' | 'years';
    /**
     * Scale Label
     */
    scale_label: string;
    /**
     * Unit
     */
    unit: 'money' | 'count';
    /**
     * Available
     */
    available: boolean;
    /**
     * Unavailable Reason
     */
    unavailable_reason: string | null;
    /**
     * Points
     *
     * Пары «подпись, значение»; будущие точки — null
     */
    points: Array<[
        string,
        number | null
    ]>;
    /**
     * Total
     */
    total: Money | number | null;
    /**
     * Total Label
     */
    total_label: string | null;
    /**
     * Peak Label
     */
    peak_label: string | null;
};

/**
 * DashboardSplitOut
 */
export type DashboardSplitOut = {
    /**
     * Key
     */
    key: 'platform' | 'experts' | 'winemakers';
    /**
     * Label
     */
    label: string;
    amount: Money | null;
    /**
     * Pct
     */
    pct: number | null;
};

/**
 * DashboardStructureItemOut
 */
export type DashboardStructureItemOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Pct
     */
    pct: number | null;
    amount?: Money | null;
    /**
     * Value
     */
    value?: number | null;
    /**
     * Value Label
     */
    value_label: string;
};

/**
 * DashboardTileOut
 */
export type DashboardTileOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Value
     */
    value: Money | number | null;
    /**
     * Value Label
     */
    value_label: string;
    /**
     * Delta Pct
     */
    delta_pct: number | null;
    /**
     * Delta Label
     */
    delta_label: string | null;
    /**
     * Note
     */
    note: string | null;
    /**
     * Spark
     */
    spark: Array<number | null>;
    /**
     * Unavailable Reason
     */
    unavailable_reason?: string | null;
};

/**
 * Decanting
 */
export const Decanting = {
    None: 'none',
    M1520: 'm15_20',
    M3040: 'm30_40',
    H1: 'h1',
    H2Plus: 'h2_plus'
} as const;

/**
 * Decanting
 */
export type Decanting = typeof Decanting[keyof typeof Decanting];

/**
 * Decision
 *
 * Решение модерации (8.8.2 `decision`); у ревизии — каким решением она закрыта.
 */
export const Decision = {
    Publish: 'publish',
    NeedsRevision: 'needs_revision',
    Reject: 'reject'
} as const;

/**
 * Decision
 *
 * Решение модерации (8.8.2 `decision`); у ревизии — каким решением она закрыта.
 */
export type Decision = typeof Decision[keyof typeof Decision];

/**
 * DecisionIn
 */
export type DecisionIn = {
    decision: Decision;
    /**
     * Comment
     *
     * Обязателен для «На доработку» и «Отклонить»
     */
    comment?: string | null;
};

/**
 * DeclineIn
 */
export type DeclineIn = {
    reason: DeclineReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * DeclineReason
 */
export const DeclineReason = {
    NoSeats: 'no_seats',
    NoRooms: 'no_rooms',
    GroupMismatch: 'group_mismatch',
    GuestAsked: 'guest_asked'
} as const;

/**
 * DeclineReason
 */
export type DeclineReason = typeof DeclineReason[keyof typeof DeclineReason];

/**
 * DefaultPointOut
 */
export type DefaultPointOut = {
    /**
     * Lat
     */
    lat: number;
    /**
     * Lng
     */
    lng: number;
    /**
     * City
     */
    city: string;
};

/**
 * DeleteIn
 */
export type DeleteIn = {
    /**
     * Confirm Title
     *
     * Точное название записи
     */
    confirm_title: string;
};

/**
 * DeleteMeIn
 */
export type DeleteMeIn = {
    /**
     * Password
     */
    password?: string | null;
    /**
     * Code
     */
    code?: string | null;
    /**
     * Confirm
     *
     * «Понимаю, что аккаунт нельзя будет восстановить»
     */
    confirm: boolean;
};

/**
 * DeletionCheckOut
 */
export type DeletionCheckOut = {
    /**
     * Allowed
     */
    allowed: boolean;
    /**
     * Reason
     */
    reason: 'team_member' | 'active_orders' | null;
    /**
     * Message
     *
     * Текст ограничения (BR-ACC-43)
     */
    message: string | null;
    /**
     * Items
     */
    items: Array<DeletionItemOut>;
    /**
     * Confirm With
     *
     * Подтверждение: пароль, если задан, иначе код из письма (BR-ACC-41)
     */
    confirm_with: 'password' | 'code';
};

/**
 * DeletionItemOut
 */
export type DeletionItemOut = {
    /**
     * Type
     */
    type: string;
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Status
     */
    status: string;
};

/**
 * DeliveryCarrier
 */
export const DeliveryCarrier = { DhlExpress: 'dhl_express', Pickup: 'pickup' } as const;

/**
 * DeliveryCarrier
 */
export type DeliveryCarrier = typeof DeliveryCarrier[keyof typeof DeliveryCarrier];

/**
 * DeliveryChannel
 */
export const DeliveryChannel = { Push: 'push', Email: 'email' } as const;

/**
 * DeliveryChannel
 */
export type DeliveryChannel = typeof DeliveryChannel[keyof typeof DeliveryChannel];

/**
 * DeliveryMethod
 */
export const DeliveryMethod = { Courier: 'courier', Pickup: 'pickup' } as const;

/**
 * DeliveryMethod
 */
export type DeliveryMethod = typeof DeliveryMethod[keyof typeof DeliveryMethod];

/**
 * DeliveryOption
 */
export const DeliveryOption = {
    PartnerSeller: 'partner_seller',
    DhlExpress: 'dhl_express',
    Pickup: 'pickup'
} as const;

/**
 * DeliveryOption
 */
export type DeliveryOption = typeof DeliveryOption[keyof typeof DeliveryOption];

/**
 * DeliveryOut
 */
export type DeliveryOut = {
    /**
     * Id
     */
    id: string;
    channel: DeliveryChannel;
    /**
     * Channel Label
     */
    channel_label: string;
    provider: DeliveryProvider | null;
    /**
     * Push — статус доставки SRV; почта — по письму Mailer (вычисляется)
     */
    status: DeliveryStatus | null;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Attempts
     */
    attempts: number;
    /**
     * Last Error Code
     */
    last_error_code: string | null;
    /**
     * Sent At
     */
    sent_at: string | null;
    /**
     * Device Id
     */
    device_id?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Mail Message Id
     */
    mail_message_id?: string | null;
};

/**
 * DeliveryProvider
 */
export const DeliveryProvider = {
    Fcm: 'fcm',
    Apns: 'apns',
    WebPush: 'web_push',
    RustorePush: 'rustore_push',
    YandexPostbox: 'yandex_postbox',
    GoogleSmtpRelay: 'google_smtp_relay',
    Console: 'console'
} as const;

/**
 * DeliveryProvider
 */
export type DeliveryProvider = typeof DeliveryProvider[keyof typeof DeliveryProvider];

/**
 * DeliveryStatus
 */
export const DeliveryStatus = {
    Queued: 'queued',
    Sending: 'sending',
    Sent: 'sent',
    Failed: 'failed',
    Dropped: 'dropped'
} as const;

/**
 * DeliveryStatus
 */
export type DeliveryStatus = typeof DeliveryStatus[keyof typeof DeliveryStatus];

/**
 * DepartureApp
 */
export type DepartureApp = {
    /**
     * Id
     */
    id: string;
    /**
     * Date From
     */
    date_from: string;
    /**
     * Date To
     */
    date_to: string;
    /**
     * Dates Label
     */
    dates_label: string;
    /**
     * Seats Free
     */
    seats_free: number;
    /**
     * Label
     */
    label: string;
    /**
     * Available
     */
    available: boolean;
};

/**
 * DepartureIn
 */
export type DepartureIn = {
    /**
     * Id
     */
    id?: string | null;
    /**
     * Date From
     */
    date_from: string;
    /**
     * Date To
     */
    date_to: string;
    /**
     * Seats
     */
    seats?: number | null;
};

/**
 * DepartureOut
 */
export type DepartureOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Date From
     */
    date_from: string;
    /**
     * Date To
     */
    date_to: string;
    /**
     * Dates Label
     */
    dates_label: string;
    /**
     * Seats
     */
    seats: number;
    /**
     * Seats Taken
     */
    seats_taken: number;
    /**
     * Seats Free
     */
    seats_free: number;
    /**
     * Taken Label
     */
    taken_label: string;
    /**
     * Free Label
     */
    free_label: string;
};

/**
 * DepartureRef
 */
export type DepartureRef = {
    /**
     * Id
     */
    id: string;
    /**
     * Date From
     */
    date_from: string;
    /**
     * Date To
     */
    date_to: string;
};

/**
 * DeparturesIn
 */
export type DeparturesIn = {
    /**
     * Departures
     */
    departures: Array<DepartureIn>;
};

/**
 * DestinationScope
 */
export const DestinationScope = { None: 'none', Eu27: 'eu27' } as const;

/**
 * DestinationScope
 */
export type DestinationScope = typeof DestinationScope[keyof typeof DestinationScope];

/**
 * DeviceOut
 */
export type DeviceOut = {
    /**
     * Device Id
     */
    device_id: string;
    /**
     * Guest Profile Id
     *
     * Текущий гостевой профиль устройства (у вошедшего — перенесённый в аккаунт)
     */
    guest_profile_id: string | null;
    guest_gate: GuestGateOut;
};

/**
 * DevicePatchIn
 *
 * `{locale?, push_token?, push_enabled?}`; `push_token: null` — токен удалён.
 */
export type DevicePatchIn = {
    locale?: Language | null;
    /**
     * Push Token
     */
    push_token?: string | null;
    /**
     * Push Enabled
     */
    push_enabled?: boolean | null;
};

/**
 * DeviceRegisterIn
 *
 * `PUT /devices/current` (FR-ACC-02): платформа совпадает с `X-Client-Platform`.
 */
export type DeviceRegisterIn = {
    platform: ClientPlatform;
    /**
     * App Version
     */
    app_version?: string | null;
    /**
     * Os Version
     */
    os_version?: string | null;
    locale: Language;
    push_provider?: PushProvider | null;
    /**
     * Push Token
     */
    push_token?: string | null;
    /**
     * Push Enabled
     *
     * Разрешение ОС на уведомления
     */
    push_enabled?: boolean;
    /**
     * Timezone
     *
     * IANA-пояс устройства (INT-SVC 17.5.4)
     */
    timezone?: string | null;
};

/**
 * DevicesSummaryOut
 */
export type DevicesSummaryOut = {
    /**
     * Active Count
     *
     * Устройства с действующей сессией приложения
     */
    active_count: number;
    /**
     * Push Enabled
     *
     * Хотя бы на одном из них пуши разрешены и активны
     */
    push_enabled: boolean;
};

/**
 * DiarySource
 */
export const DiarySource = {
    Manual: 'manual',
    Catalog: 'catalog',
    Recognition: 'recognition',
    Order: 'order',
    Tasting: 'tasting'
} as const;

/**
 * DiarySource
 */
export type DiarySource = typeof DiarySource[keyof typeof DiarySource];

/**
 * DigitalBillingChannel
 *
 * `billing.digital`: чем оплачивается обучение на платформе клиента.
 */
export const DigitalBillingChannel = {
    PaymentsGateway: 'payments_gateway',
    RustorePay: 'rustore_pay',
    AppStoreIap: 'app_store_iap',
    GooglePlayBilling: 'google_play_billing'
} as const;

/**
 * DigitalBillingChannel
 *
 * `billing.digital`: чем оплачивается обучение на платформе клиента.
 */
export type DigitalBillingChannel = typeof DigitalBillingChannel[keyof typeof DigitalBillingChannel];

/**
 * DiscountOut
 */
export type DiscountOut = {
    /**
     * Kind
     */
    kind: 'level' | 'first_region';
    /**
     * Pct
     */
    pct: number;
    amount: Money;
    /**
     * Label
     */
    label: string;
};

/**
 * DiscountsOut
 */
export type DiscountsOut = {
    learning: LearningDiscountOut;
    wine: WineDiscountOut;
    first_region: FirstRegionOut;
};

/**
 * DispatchCardOut
 */
export type DispatchCardOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Scheduled For
     */
    scheduled_for: string;
    /**
     * Processed At
     */
    processed_at: string | null;
    /**
     * Template Code
     */
    template_code: string;
    /**
     * Template Name
     */
    template_name: string;
    section: NotificationSection | null;
    recipient: RecipientOut;
    status: DispatchStatus;
    /**
     * Status Label
     */
    status_label: string;
    skip_reason: SkipReason | null;
    /**
     * Skip Reason Label
     */
    skip_reason_label: string | null;
    /**
     * Channels
     *
     * Фактические каналы: push, email, feed
     */
    channels: Array<string>;
    /**
     * Rendered Title
     */
    rendered_title: string;
    /**
     * Rendered Body
     */
    rendered_body: string;
    /**
     * Locale
     */
    locale: string;
    /**
     * Params
     */
    params: {
        [key: string]: unknown;
    };
    /**
     * Variant
     */
    variant: string | null;
    /**
     * Deeplink
     */
    deeplink: string | null;
    /**
     * Subject Type
     */
    subject_type: string | null;
    /**
     * Subject Id
     */
    subject_id: string | null;
    /**
     * Condition
     */
    condition: string | null;
    /**
     * Source Event Id
     */
    source_event_id: string | null;
    /**
     * Feed Id
     */
    feed_id: string | null;
    /**
     * Feed Read At
     */
    feed_read_at: string | null;
    /**
     * Deliveries
     */
    deliveries: Array<DeliveryOut>;
};

/**
 * DispatchRowOut
 */
export type DispatchRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Scheduled For
     */
    scheduled_for: string;
    /**
     * Processed At
     */
    processed_at: string | null;
    /**
     * Template Code
     */
    template_code: string;
    /**
     * Template Name
     */
    template_name: string;
    section: NotificationSection | null;
    recipient: RecipientOut;
    status: DispatchStatus;
    /**
     * Status Label
     */
    status_label: string;
    skip_reason: SkipReason | null;
    /**
     * Skip Reason Label
     */
    skip_reason_label: string | null;
    /**
     * Channels
     *
     * Фактические каналы: push, email, feed
     */
    channels: Array<string>;
    /**
     * Rendered Title
     */
    rendered_title: string;
    /**
     * Rendered Body
     */
    rendered_body: string;
};

/**
 * DispatchStatus
 */
export const DispatchStatus = {
    Scheduled: 'scheduled',
    Processing: 'processing',
    Done: 'done',
    Skipped: 'skipped',
    Cancelled: 'cancelled'
} as const;

/**
 * DispatchStatus
 */
export type DispatchStatus = typeof DispatchStatus[keyof typeof DispatchStatus];

/**
 * DispatchesPageOut
 */
export type DispatchesPageOut = {
    /**
     * Items
     */
    items: Array<DispatchRowOut>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
};

/**
 * DocKey
 */
export const DocKey = {
    UserAgreement: 'user_agreement',
    PrivacyPolicy: 'privacy_policy',
    PdConsent: 'pd_consent',
    AuthorTerms: 'author_terms',
    Other: 'other'
} as const;

/**
 * DocKey
 */
export type DocKey = typeof DocKey[keyof typeof DocKey];

/**
 * DocumentAdminOut
 */
export type DocumentAdminOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    doc_key: DocKey;
    /**
     * Is Platform
     */
    is_platform: boolean;
    owner_kind: DocumentOwnerKind;
    /**
     * Owner Label
     */
    owner_label: string;
    lang: Language;
    status: DocumentStatus;
    /**
     * Status Label
     */
    status_label: string;
    visibility: DocumentVisibility;
    /**
     * Version
     */
    version: string | null;
    /**
     * Effective From
     */
    effective_from: string | null;
    /**
     * File Label
     */
    file_label: string | null;
    /**
     * File Url
     */
    file_url: string | null;
    /**
     * Url
     */
    url: string | null;
    /**
     * Decision Comment
     */
    decision_comment: string | null;
    /**
     * Notice
     *
     * Документ без файла: «Текст загружает администратор»
     */
    notice: string | null;
    archive_reason: ContentArchiveReason | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Versions
     */
    versions: Array<DocumentVersionOut>;
    usage: DocumentUsageOut;
    /**
     * History
     */
    history: Array<ContentHistoryOut>;
    can: DocumentCan;
    /**
     * Toast
     */
    toast?: string | null;
    /**
     * Notification Queued
     *
     * «Обновлены документы» поставлено в рассылку (BR-CNT-81)
     */
    notification_queued?: boolean | null;
};

/**
 * DocumentCan
 */
export type DocumentCan = {
    /**
     * Replace
     */
    replace?: boolean;
    /**
     * Rename
     */
    rename?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Decide
     */
    decide?: boolean;
};

/**
 * DocumentCreateIn
 */
export type DocumentCreateIn = {
    /**
     * Name
     */
    name: string;
    /**
     * Winery Id
     */
    winery_id?: string | null;
    /**
     * Media Id
     */
    media_id?: string | null;
    /**
     * Url
     */
    url?: string | null;
    /**
     * File Name
     */
    file_name?: string | null;
};

/**
 * DocumentDecisionIn
 */
export type DocumentDecisionIn = {
    /**
     * Decision
     */
    decision: 'accept' | 'reject';
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * DocumentOwnerKind
 */
export const DocumentOwnerKind = {
    Platform: 'platform',
    Member: 'member',
    Winery: 'winery'
} as const;

/**
 * DocumentOwnerKind
 */
export type DocumentOwnerKind = typeof DocumentOwnerKind[keyof typeof DocumentOwnerKind];

/**
 * DocumentPatchIn
 */
export type DocumentPatchIn = {
    /**
     * Name
     */
    name: string;
};

/**
 * DocumentRowOut
 */
export type DocumentRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    doc_key: DocKey;
    /**
     * File Label
     *
     * Имя файла или адрес без `https://`
     */
    file_label: string | null;
    /**
     * Version
     */
    version: string | null;
    /**
     * Effective From
     */
    effective_from: string | null;
    lang: Language;
    owner_kind: DocumentOwnerKind;
    /**
     * Owner Label
     */
    owner_label: string;
    status: DocumentStatus;
    /**
     * Status Label
     */
    status_label: string;
    visibility: DocumentVisibility;
    can: DocumentCan;
};

/**
 * DocumentStatus
 *
 * `draft` — расширение (docs/decisions.md): документ платформы из `make seed` без
 * загруженной версии («файл загружает администратор»).
 */
export const DocumentStatus = {
    Draft: 'draft',
    OnReview: 'on_review',
    Published: 'published',
    Rejected: 'rejected',
    Archived: 'archived'
} as const;

/**
 * DocumentStatus
 *
 * `draft` — расширение (docs/decisions.md): документ платформы из `make seed` без
 * загруженной версии («файл загружает администратор»).
 */
export type DocumentStatus = typeof DocumentStatus[keyof typeof DocumentStatus];

/**
 * DocumentUsageOut
 */
export type DocumentUsageOut = {
    /**
     * Applications
     */
    applications: Array<ContentRefOut>;
    /**
     * Consents
     *
     * `[{version, count}]` — согласия ACC
     */
    consents: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * DocumentVersionIn
 */
export type DocumentVersionIn = {
    /**
     * Media Id
     */
    media_id?: string | null;
    /**
     * Url
     */
    url?: string | null;
    /**
     * File Name
     */
    file_name?: string | null;
    /**
     * Name
     */
    name?: string | null;
};

/**
 * DocumentVersionOut
 */
export type DocumentVersionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Version
     */
    version: string;
    /**
     * File Label
     */
    file_label: string | null;
    /**
     * File Url
     */
    file_url: string | null;
    /**
     * Url
     */
    url: string | null;
    /**
     * Effective From
     */
    effective_from: string;
    /**
     * Uploaded By
     */
    uploaded_by: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Current
     */
    current: boolean;
};

/**
 * DocumentVisibility
 */
export const DocumentVisibility = { Public: 'public', Moderators: 'moderators' } as const;

/**
 * DocumentVisibility
 */
export type DocumentVisibility = typeof DocumentVisibility[keyof typeof DocumentVisibility];

/**
 * DropAdmin
 */
export type DropAdmin = {
    /**
     * Id
     */
    id: string;
    /**
     * Sku
     */
    sku: string;
    name: LocalizedText | null;
    /**
     * Title
     */
    title: string;
    target: DropTargetOut;
    winery: RefOut | null;
    region: RefOut | null;
    /**
     * Batch Qty
     */
    batch_qty: number;
    /**
     * Sold Qty
     */
    sold_qty: number;
    /**
     * Reserved Qty
     */
    reserved_qty: number;
    /**
     * Remaining
     */
    remaining: number;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Limit Per User
     */
    limit_per_user: number | null;
    /**
     * Effective Limit
     */
    effective_limit: number;
    /**
     * Limit Label
     *
     * «N бутылок» или «— (по умолчанию 1)»
     */
    limit_label: string;
    /**
     * Delivery Options
     */
    delivery_options: Array<DeliveryOption>;
    /**
     * Delivery Labels
     */
    delivery_labels: Array<string>;
    /**
     * Early Access
     */
    early_access: boolean;
    /**
     * Early Access Label
     */
    early_access_label: string;
    /**
     * Порог гейта GAM; null — GAM нет
     */
    early_access_tier: TierOut | null;
    /**
     * Early Start At
     */
    early_start_at: string | null;
    /**
     * Start At
     */
    start_at: string | null;
    /**
     * Timezone
     */
    timezone: string;
    sales_state: DropSalesState;
    /**
     * Sales State Label
     *
     * Черновик без даты старта — «Черновик»
     */
    sales_state_label: string;
    /**
     * Started
     *
     * Старт наступил: действуют ограничения BR-CAT-48
     */
    started: boolean;
    description: LocalizedText | null;
    conditions: LocalizedText | null;
    /**
     * Ended At
     */
    ended_at: string | null;
    /**
     * Media
     */
    media: Array<CatalogMediaOut>;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Reminders Count
     */
    reminders_count: number;
    /**
     * Orders Count
     */
    orders_count: number;
    status: CatalogStatus;
    /**
     * Status Label
     */
    status_label: string;
    display_status: CatalogStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Published At
     */
    published_at: string | null;
    review: ReviewNoteOut | null;
    archive_reason: AppModulesCatModelsArchiveReason | null;
    /**
     * Archive Reason Label
     */
    archive_reason_label: string | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    revision: RevisionOut | null;
    revision_preview?: DropAdmin | null;
    can: DropCan;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * DropCan
 */
export type DropCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Edit Target
     *
     * Цель меняется только до публикации
     */
    edit_target?: boolean;
    /**
     * Edit Schedule
     *
     * Старт, ранний доступ, цена — до старта (BR-CAT-48)
     */
    edit_schedule?: boolean;
    /**
     * Edit Media
     */
    edit_media?: boolean;
    /**
     * Submit
     */
    submit?: boolean;
    /**
     * Publish
     */
    publish?: boolean;
    /**
     * Return
     */
    return?: boolean;
    /**
     * Reject
     */
    reject?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Awaiting Decision
     */
    awaiting_decision?: boolean;
};

/**
 * DropCreateIn
 */
export type DropCreateIn = {
    /**
     * До 120 символов
     */
    name: LocalizedText;
    target_type: DropTargetType;
    /**
     * Target Id
     *
     * Опубликованное вино или сет (BR-CAT-42)
     */
    target_id: string;
    /**
     * Batch Qty
     */
    batch_qty: number;
    price: MoneyIn;
    /**
     * Limit Per User
     *
     * Пусто — 1 (OQ-CAT-08)
     */
    limit_per_user?: number | null;
    /**
     * Delivery Options
     */
    delivery_options?: Array<DeliveryOption>;
    /**
     * Early Access
     */
    early_access?: boolean;
    /**
     * Early Start At
     */
    early_start_at?: string | null;
    /**
     * Start At
     *
     * Общий старт; обязателен при отправке на модерацию
     */
    start_at?: string | null;
    /**
     * Timezone
     *
     * IANA
     */
    timezone?: string | null;
    description: LocalizedText;
    conditions?: LocalizedText | null;
    /**
     * Media
     */
    media?: Array<AppModulesCatSchemasAdminMediaIn>;
};

/**
 * DropDetailOut
 *
 * `DropDetail` (6.8.1, пример).
 */
export type DropDetailOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    target: DropTargetAppOut;
    winery: AppModulesCatSchemasAppNamedRef | null;
    region: AppModulesCatSchemasAppNamedRef | null;
    price: Money;
    /**
     * Batch Qty
     */
    batch_qty: number;
    /**
     * Remaining
     */
    remaining: number;
    sales_state: DropSalesState;
    /**
     * State For User
     */
    state_for_user: 'live' | 'upcoming' | 'sold';
    early_access: EarlyAccessOut;
    /**
     * Start At
     */
    start_at: string | null;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Limit Per User
     *
     * Действующий лимит (пустой — 1, OQ-CAT-08)
     */
    limit_per_user: number;
    /**
     * User Bought Qty
     */
    user_bought_qty: number;
    /**
     * Reminder Set
     */
    reminder_set: boolean;
    /**
     * Conditions
     */
    conditions: string | null;
    /**
     * Description
     */
    description: string | null;
    /**
     * Purchase Action
     */
    purchase_action: 'cart' | 'partner' | 'none';
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Header
     *
     * «Идёт сейчас» / «Ограниченный выпуск»
     */
    header: string;
    /**
     * Limit Note
     */
    limit_note: string;
    /**
     * Sold Qty
     */
    sold_qty: number;
    /**
     * Notes
     *
     * Ноты целевого вина
     */
    notes: Array<string>;
    /**
     * Media
     */
    media: Array<MediaAppOut>;
    /**
     * Characteristics
     */
    characteristics: Array<CharacteristicOut>;
    /**
     * Target Items
     *
     * Цель — сет: «Что внутри»
     */
    target_items: Array<SetItemAppOut>;
    /**
     * Same Hands
     *
     * «От тех же рук» (распродан/завершён)
     */
    same_hands: Array<WineListItem>;
    /**
     * Max Qty
     */
    max_qty: number;
    /**
     * Purchase Label
     */
    purchase_label: string | null;
    /**
     * Is Saved
     *
     * Дроп в «Сохранённом» пользователя (закладка CNT)
     */
    is_saved: boolean;
};

/**
 * DropListItem
 */
export type DropListItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * State For User
     */
    state_for_user: 'live' | 'upcoming' | 'sold';
    sales_state: DropSalesState;
    /**
     * Meta
     *
     * «{винодельня} · {регион} · тираж {партия}»
     */
    meta: string;
    winery: AppModulesCatSchemasAppNamedRef | null;
    region: AppModulesCatSchemasAppNamedRef | null;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Remaining
     */
    remaining: number;
    /**
     * Right Label
     *
     * «осталось N» / «по расписанию»
     */
    right_label: string | null;
    /**
     * Start At
     */
    start_at: string | null;
    /**
     * Early Start At
     */
    early_start_at: string | null;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Cover Url
     */
    cover_url: string | null;
};

/**
 * DropPageOut
 */
export type DropPageOut = {
    /**
     * Items
     */
    items: Array<DropListItem>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * DropPatchIn
 */
export type DropPatchIn = {
    name?: LocalizedText | null;
    target_type?: DropTargetType | null;
    /**
     * Target Id
     */
    target_id?: string | null;
    /**
     * Batch Qty
     */
    batch_qty?: number | null;
    price?: MoneyIn | null;
    /**
     * Limit Per User
     */
    limit_per_user?: number | null;
    /**
     * Delivery Options
     */
    delivery_options?: Array<DeliveryOption> | null;
    /**
     * Early Access
     */
    early_access?: boolean | null;
    /**
     * Early Start At
     */
    early_start_at?: string | null;
    /**
     * Start At
     */
    start_at?: string | null;
    /**
     * Timezone
     */
    timezone?: string | null;
    description?: LocalizedText | null;
    conditions?: LocalizedText | null;
};

/**
 * DropRowOut
 */
export type DropRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Sku
     */
    sku: string;
    /**
     * Name
     */
    name: string;
    /**
     * Target Label
     */
    target_label: string;
    /**
     * Start At
     */
    start_at: string | null;
    /**
     * Early Start At
     */
    early_start_at: string | null;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Batch Qty
     */
    batch_qty: number;
    /**
     * Remaining
     */
    remaining: number;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    sales_state: DropSalesState;
    /**
     * Sales State Label
     */
    sales_state_label: string;
    status: CatalogStatus;
    display_status: CatalogStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * DropSalesState
 */
export const DropSalesState = {
    Pending: 'pending',
    Upcoming: 'upcoming',
    EarlyAccess: 'early_access',
    Active: 'active',
    SoldOut: 'sold_out',
    Ended: 'ended'
} as const;

/**
 * DropSalesState
 */
export type DropSalesState = typeof DropSalesState[keyof typeof DropSalesState];

/**
 * DropTargetAppOut
 */
export type DropTargetAppOut = {
    /**
     * Type
     */
    type: 'wine' | 'set';
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Vintage
     */
    vintage?: number | null;
};

/**
 * DropTargetOptionOut
 */
export type DropTargetOptionOut = {
    type: DropTargetType;
    /**
     * Id
     */
    id: string;
    /**
     * Label
     */
    label: string;
};

/**
 * DropTargetOut
 */
export type DropTargetOut = {
    type: DropTargetType;
    /**
     * Id
     */
    id: string;
    /**
     * Label
     *
     * «Название Винтаж» или «Сет «Название»»
     */
    label: string;
    status: CatalogStatus;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * DropTargetType
 */
export const DropTargetType = { Wine: 'wine', Set: 'set' } as const;

/**
 * DropTargetType
 */
export type DropTargetType = typeof DropTargetType[keyof typeof DropTargetType];

/**
 * DropTargetsOut
 */
export type DropTargetsOut = {
    /**
     * Items
     */
    items: Array<DropTargetOptionOut>;
};

/**
 * EarlyAccessOut
 */
export type EarlyAccessOut = {
    /**
     * Enabled
     */
    enabled: boolean;
    /**
     * Starts At
     */
    starts_at: string | null;
    /**
     * Required Tier
     *
     * `{rank, roman, name}` из GAM; null — GAM не подключён
     */
    required_tier: {
        [key: string]: unknown;
    } | null;
    /**
     * User Passes
     */
    user_passes: boolean;
};

/**
 * EmailChangeIn
 */
export type EmailChangeIn = {
    /**
     * New Email
     */
    new_email: string;
};

/**
 * EmailConfirmIn
 */
export type EmailConfirmIn = {
    /**
     * New Email
     */
    new_email: string;
    /**
     * Code
     */
    code: string;
};

/**
 * EntitlementItemOut
 */
export type EntitlementItemOut = {
    item_type: ItemType;
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Granted At
     */
    granted_at: string;
    /**
     * Source
     */
    source: string;
    /**
     * Source Label
     */
    source_label: string;
};

/**
 * EntitlementPageOut
 */
export type EntitlementPageOut = {
    /**
     * Items
     */
    items: Array<EntitlementItemOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * ErrorBody
 */
export type ErrorBody = {
    /**
     * Code
     *
     * Машиночитаемый код ошибки (ТЗ 3.1.3, коды модулей)
     */
    code: string;
    /**
     * Message
     *
     * Текст для пользователя на языке запроса
     */
    message: string;
    /**
     * Details
     */
    details?: {
        [key: string]: unknown;
    } | null;
    /**
     * Trace Id
     */
    trace_id?: string | null;
};

/**
 * ErrorResponse
 */
export type ErrorResponse = {
    error: ErrorBody;
};

/**
 * EventArchiveIn
 */
export type EventArchiveIn = {
    reason: EventArchiveReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * EventArchiveReason
 */
export const EventArchiveReason = {
    NotRelevant: 'not_relevant',
    Duplicate: 'duplicate',
    PartnerTerminated: 'partner_terminated',
    DataError: 'data_error'
} as const;

/**
 * EventArchiveReason
 */
export type EventArchiveReason = typeof EventArchiveReason[keyof typeof EventArchiveReason];

/**
 * EventItem
 */
export type EventItem = {
    type: BookingTarget;
    /**
     * Id
     */
    id: string;
    /**
     * Starts At
     */
    starts_at: string;
    /**
     * Month
     */
    month: string;
    /**
     * Month Label
     */
    month_label: string;
    /**
     * Day Label
     */
    day_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     */
    meta: string;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Is Full
     */
    is_full: boolean;
    /**
     * Is Blind
     */
    is_blind?: boolean;
};

/**
 * EventLifecycle
 */
export const EventLifecycle = {
    Open: 'open',
    Full: 'full',
    Finished: 'finished',
    Cancelled: 'cancelled'
} as const;

/**
 * EventLifecycle
 */
export type EventLifecycle = typeof EventLifecycle[keyof typeof EventLifecycle];

/**
 * EventMediaIn
 *
 * Фото — файл (`exp_image`, JPG/PNG, COM-13) или ссылка; видео — только ссылкой.
 * Первый файл — обложка (BR-EXP-09).
 */
export type EventMediaIn = {
    /**
     * Id
     *
     * id существующего элемента (правка)
     */
    id?: string | null;
    /**
     * File Id
     */
    file_id?: string | null;
    /**
     * Source Url
     */
    source_url?: string | null;
    kind?: MediaKind;
    placement?: MediaPlacement | null;
    caption?: LocalizedText | null;
};

/**
 * EventMediaOut
 */
export type EventMediaOut = {
    /**
     * Id
     */
    id: string;
    kind: MediaKind;
    placement: MediaPlacement;
    /**
     * File Id
     */
    file_id: string | null;
    /**
     * Source Url
     */
    source_url: string | null;
    /**
     * Url
     */
    url: string | null;
    /**
     * Thumb Url
     */
    thumb_url: string | null;
    caption: LocalizedText | null;
};

/**
 * EventTimezoneOut
 */
export type EventTimezoneOut = {
    /**
     * Tz
     */
    tz: string;
    label: LocalizedText | null;
};

/**
 * EventsOut
 */
export type EventsOut = {
    /**
     * Items
     */
    items: Array<EventItem>;
};

/**
 * ExchangeCan
 */
export type ExchangeCan = {
    /**
     * View
     */
    view: boolean;
    /**
     * Complete
     */
    complete: boolean;
    /**
     * Decline
     */
    decline: boolean;
};

/**
 * ExchangeCardOut
 */
export type ExchangeCardOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string;
    user: GamUserRefOut;
    /**
     * Type Label
     *
     * «Обмен бонусов» (тип строки «Бронирований»)
     */
    type_label: string;
    reward: ExchangeRewardOut;
    executor_type: ExecutorType;
    /**
     * Executor
     *
     * «Организатор»: винодельня, эксперт или бренд
     */
    executor: string;
    /**
     * When Label
     *
     * Дата заявки; после выдачи — «<дата> · выдано»
     */
    when_label: string;
    /**
     * Persons
     */
    persons: number;
    /**
     * Cost
     */
    cost: number;
    /**
     * Amount Label
     *
     * «N бонусов»
     */
    amount_label: string;
    /**
     * Status
     */
    status: 'requested' | 'paid' | 'declined';
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Requested At
     */
    requested_at: string;
    /**
     * Due Date
     */
    due_date: string;
    /**
     * Overdue
     */
    overdue: boolean;
    /**
     * Paid At
     */
    paid_at: string | null;
    /**
     * Declined At
     */
    declined_at: string | null;
    order: ExchangeOrderOut | null;
    can: ExchangeCan;
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string;
    /**
     * Reward Description
     */
    reward_description: string | null;
    reward_kind: RewardKind;
    /**
     * Reward Kind Label
     */
    reward_kind_label: string;
    /**
     * Payment Label
     */
    payment_label: string;
    /**
     * Bonus Balance
     */
    bonus_balance: number | null;
    /**
     * Balance Label
     */
    balance_label: string | null;
    /**
     * Requested Label
     */
    requested_label: string;
    /**
     * Due Label
     */
    due_label: string;
    /**
     * Given Text
     */
    given_text: string | null;
    /**
     * Team Note
     */
    team_note: string | null;
    decline_reason: GamDeclineReason | null;
    /**
     * Decline Reason Label
     */
    decline_reason_label: string | null;
    /**
     * Decline Comment
     */
    decline_comment: string | null;
    /**
     * Executor Winery Id
     */
    executor_winery_id: string | null;
    /**
     * Executor Expert Id
     */
    executor_expert_id: string | null;
    /**
     * Steps
     */
    steps: Array<ExchangeStepOut>;
    /**
     * Note
     */
    note: string;
    /**
     * Rules
     */
    rules: Array<string>;
    reward_panel: ExchangeRewardPanelOut;
    /**
     * User Exchanges Count
     */
    user_exchanges_count: number;
    /**
     * User Exchanges Label
     */
    user_exchanges_label: string;
    /**
     * Order Panel
     */
    order_panel: string | null;
    /**
     * History
     */
    history: Array<ExchangeHistoryOut>;
};

/**
 * ExchangeCompleteIn
 */
export type ExchangeCompleteIn = {
    /**
     * Given Text
     *
     * «Что выдано пользователю»
     */
    given_text: string;
    /**
     * Executor Winery Id
     *
     * Исполнитель-винодельня (награда «Винодельня»)
     */
    executor_winery_id?: string | null;
    /**
     * Executor Expert Id
     *
     * Исполнитель-эксперт (награда «Эксперт»)
     */
    executor_expert_id?: string | null;
    /**
     * Team Note
     */
    team_note?: string | null;
};

/**
 * ExchangeCreateIn
 */
export type ExchangeCreateIn = {
    /**
     * Reward Id
     */
    reward_id: string;
};

/**
 * ExchangeCreatedOut
 */
export type ExchangeCreatedOut = {
    exchange: ExchangeItemOut;
    /**
     * Bonus Balance
     */
    bonus_balance: number;
    /**
     * Toast
     */
    toast: string;
};

/**
 * ExchangeDeclineIn
 */
export type ExchangeDeclineIn = {
    reason: GamDeclineReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * ExchangeHistoryOut
 */
export type ExchangeHistoryOut = {
    /**
     * At
     */
    at: string | null;
    /**
     * Who
     */
    who: string;
    /**
     * Text
     */
    text: string;
};

/**
 * ExchangeItemOut
 */
export type ExchangeItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     *
     * «B-NNNN»
     */
    number: string;
    /**
     * Reward Id
     */
    reward_id: string;
    /**
     * Reward Title
     */
    reward_title: string;
    /**
     * Icon Url
     */
    icon_url: string | null;
    /**
     * Cost
     */
    cost: number;
    /**
     * Status
     */
    status: 'requested' | 'paid' | 'declined';
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Requested At
     */
    requested_at: string;
    /**
     * Due Date
     */
    due_date: string;
    /**
     * Paid At
     */
    paid_at: string | null;
    /**
     * Decline Reason
     */
    decline_reason: string | null;
    /**
     * Decline Reason Text
     */
    decline_reason_text: string | null;
    /**
     * Subtitle
     *
     * «N бонусов · заявка от D месяц»
     */
    subtitle: string;
    /**
     * Text
     *
     * Текст карточки по статусу (11.6.4)
     */
    text: string;
    /**
     * Order Id
     */
    order_id: string | null;
    /**
     * Order Number
     */
    order_number: string | null;
};

/**
 * ExchangeOrderOut
 */
export type ExchangeOrderOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string | null;
};

/**
 * ExchangeRewardOut
 */
export type ExchangeRewardOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
};

/**
 * ExchangeRewardPanelOut
 */
export type ExchangeRewardPanelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Cost Label
     */
    cost_label: string;
    /**
     * Executor Label
     */
    executor_label: string;
    /**
     * Status
     */
    status: string | null;
    /**
     * Status Label
     */
    status_label: string | null;
    /**
     * Note
     */
    note: string;
};

/**
 * ExchangeRowOut
 */
export type ExchangeRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string;
    user: GamUserRefOut;
    /**
     * Type Label
     *
     * «Обмен бонусов» (тип строки «Бронирований»)
     */
    type_label: string;
    reward: ExchangeRewardOut;
    executor_type: ExecutorType;
    /**
     * Executor
     *
     * «Организатор»: винодельня, эксперт или бренд
     */
    executor: string;
    /**
     * When Label
     *
     * Дата заявки; после выдачи — «<дата> · выдано»
     */
    when_label: string;
    /**
     * Persons
     */
    persons: number;
    /**
     * Cost
     */
    cost: number;
    /**
     * Amount Label
     *
     * «N бонусов»
     */
    amount_label: string;
    /**
     * Status
     */
    status: 'requested' | 'paid' | 'declined';
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Requested At
     */
    requested_at: string;
    /**
     * Due Date
     */
    due_date: string;
    /**
     * Overdue
     */
    overdue: boolean;
    /**
     * Paid At
     */
    paid_at: string | null;
    /**
     * Declined At
     */
    declined_at: string | null;
    order: ExchangeOrderOut | null;
    can: ExchangeCan;
};

/**
 * ExchangeStepOut
 */
export type ExchangeStepOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    /**
     * State
     */
    state: 'done' | 'current' | 'next';
};

/**
 * ExcursionAdmin
 */
export type ExcursionAdmin = {
    /**
     * Id
     */
    id: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Subtitle
     */
    subtitle: string;
    title: LocalizedText;
    winery: RefOut | null;
    /**
     * Duration Min
     */
    duration_min: number | null;
    /**
     * Duration Label
     */
    duration_label: string;
    /**
     * Seats Per Slot
     */
    seats_per_slot: number;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    cancel_policy: ExcursionCancelPolicy;
    /**
     * Cancel Policy Label
     */
    cancel_policy_label: string;
    schedule_mode: ScheduleMode;
    /**
     * Schedule Mode Label
     */
    schedule_mode_label: string;
    /**
     * Starts At
     */
    starts_at: string | null;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Date Label
     */
    date_label: string | null;
    min_notice: MinNotice | null;
    /**
     * Min Notice Label
     */
    min_notice_label: string | null;
    /**
     * Season Until
     */
    season_until: string | null;
    /**
     * Week Slots
     */
    week_slots: Array<WeekSlotOut>;
    /**
     * Week Days
     */
    week_days: Array<WeekDayOut>;
    /**
     * Seats Week Total
     */
    seats_week_total: number;
    /**
     * Booked People
     */
    booked_people: number;
    description: LocalizedText | null;
    /**
     * Details
     */
    details: Array<PairOut>;
    /**
     * Steps
     */
    steps: Array<StepOut>;
    /**
     * Route
     */
    route: Array<PairOut>;
    /**
     * Route Defaults
     *
     * «Дорога» и «Трансфер» из профиля винодельни (если в экскурсии пусто)
     */
    route_defaults: Array<PairOut>;
    /**
     * Media
     */
    media: Array<EventMediaOut>;
    /**
     * Reviews
     */
    reviews: Array<ExcursionReviewOut>;
    /**
     * Rating Avg
     */
    rating_avg: number | null;
    /**
     * Reviews Count
     */
    reviews_count: number;
    /**
     * Summary
     */
    summary: Array<ExpTileOut>;
    /**
     * Cancelled At
     */
    cancelled_at: string | null;
    /**
     * Cancel Reason
     */
    cancel_reason: string | null;
    moderation: ModerationOut;
    /**
     * Archived At
     */
    archived_at: string | null;
    archive_reason: EventArchiveReason | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    can: CardCan;
};

/**
 * ExcursionCancelPolicy
 */
export const ExcursionCancelPolicy = {
    Free48H: 'free_48h',
    Free24H: 'free_24h',
    NoRefund: 'no_refund'
} as const;

/**
 * ExcursionCancelPolicy
 */
export type ExcursionCancelPolicy = typeof ExcursionCancelPolicy[keyof typeof ExcursionCancelPolicy];

/**
 * ExcursionCard
 */
export type ExcursionCard = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Overline
     */
    overline: string;
    winery: RefOut;
    region: RefOut | null;
    /**
     * Duration Min
     */
    duration_min: number | null;
    /**
     * Duration Label
     */
    duration_label: string | null;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Cover Url
     */
    cover_url: string | null;
    schedule_mode: ScheduleMode;
    /**
     * Next Slot At
     */
    next_slot_at: string | null;
    /**
     * Attended
     */
    attended?: boolean;
    /**
     * Status Chip
     */
    status_chip?: string | null;
};

/**
 * ExcursionCreateIn
 */
export type ExcursionCreateIn = {
    title: LocalizedText;
    /**
     * Winery Id
     */
    winery_id: string;
    /**
     * Duration Min
     */
    duration_min?: number | null;
    /**
     * Seats Per Slot
     */
    seats_per_slot: number;
    price: Money;
    cancel_policy?: ExcursionCancelPolicy;
    schedule_mode?: ScheduleMode;
    /**
     * Starts At
     */
    starts_at?: string | null;
    /**
     * Timezone
     */
    timezone?: string | null;
    min_notice?: MinNotice | null;
    /**
     * Season Until
     */
    season_until?: string | null;
    /**
     * Week Slots
     */
    week_slots?: Array<WeekSlotIn>;
    description?: LocalizedText | null;
    /**
     * Details
     */
    details?: Array<PairIn> | null;
    /**
     * Steps
     */
    steps?: Array<StepIn> | null;
    /**
     * Route
     */
    route?: Array<PairIn> | null;
    /**
     * Media
     */
    media?: Array<EventMediaIn>;
};

/**
 * ExcursionDetail
 */
export type ExcursionDetail = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Overline
     */
    overline: string;
    winery: RefOut;
    region: RefOut | null;
    /**
     * Duration Min
     */
    duration_min: number | null;
    /**
     * Duration Label
     */
    duration_label: string | null;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Cover Url
     */
    cover_url: string | null;
    schedule_mode: ScheduleMode;
    /**
     * Next Slot At
     */
    next_slot_at: string | null;
    /**
     * Attended
     */
    attended?: boolean;
    /**
     * Status Chip
     */
    status_chip?: string | null;
    /**
     * Published
     */
    published: boolean;
    /**
     * Description
     */
    description: string | null;
    /**
     * Seats Per Slot
     */
    seats_per_slot: number;
    /**
     * Group Label
     */
    group_label: string;
    /**
     * Hours Label
     */
    hours_label: string | null;
    /**
     * Visit Status Label
     */
    visit_status_label: string;
    /**
     * Details
     */
    details: Array<AppPair>;
    /**
     * Steps
     */
    steps: Array<AppStep>;
    /**
     * Route
     */
    route: Array<AppPair>;
    /**
     * Media
     */
    media: Array<AppMedia>;
    /**
     * Cancel Policy Text
     */
    cancel_policy_text: string;
    /**
     * Has Slots
     */
    has_slots: boolean;
    /**
     * Other Formats
     */
    other_formats: Array<RefOut>;
    me: MeOut | null;
};

/**
 * ExcursionPatchIn
 */
export type ExcursionPatchIn = {
    title?: LocalizedText | null;
    /**
     * Winery Id
     */
    winery_id?: string | null;
    /**
     * Duration Min
     */
    duration_min?: number | null;
    /**
     * Seats Per Slot
     */
    seats_per_slot?: number | null;
    price?: Money | null;
    cancel_policy?: ExcursionCancelPolicy | null;
    schedule_mode?: ScheduleMode | null;
    /**
     * Starts At
     */
    starts_at?: string | null;
    /**
     * Timezone
     */
    timezone?: string | null;
    min_notice?: MinNotice | null;
    /**
     * Season Until
     */
    season_until?: string | null;
};

/**
 * ExcursionReviewOut
 */
export type ExcursionReviewOut = {
    /**
     * Author
     */
    author: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Rating
     */
    rating: number;
    /**
     * Text
     */
    text: string;
};

/**
 * ExcursionRow
 */
export type ExcursionRow = {
    /**
     * Id
     */
    id: string;
    title: LocalizedText;
    /**
     * Title Text
     */
    title_text: string;
    winery: RefOut | null;
    /**
     * Duration Min
     */
    duration_min: number | null;
    /**
     * Duration Label
     */
    duration_label: string;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    schedule_mode: ScheduleMode;
    /**
     * Week Slots Count
     */
    week_slots_count: number;
    /**
     * Slots Label
     */
    slots_label: string;
    moderation_status: ModerationStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Is Live
     */
    is_live: boolean;
    /**
     * Archived
     */
    archived: boolean;
    can: RowCan;
};

/**
 * ExcursionSlotOut
 */
export type ExcursionSlotOut = {
    /**
     * Starts At
     */
    starts_at: string;
    /**
     * Ends At
     */
    ends_at: string;
    /**
     * Capacity
     */
    capacity: number;
    /**
     * Free
     */
    free: number;
    /**
     * Label
     */
    label: string;
};

/**
 * ExecutorType
 */
export const ExecutorType = {
    Platform: 'platform',
    Expert: 'expert',
    Winery: 'winery'
} as const;

/**
 * ExecutorType
 */
export type ExecutorType = typeof ExecutorType[keyof typeof ExecutorType];

/**
 * ExpAlertOut
 */
export type ExpAlertOut = {
    kind: BookingTarget;
    /**
     * Region Id
     */
    region_id: string | null;
};

/**
 * ExpLinkOut
 *
 * Переход в раздел админки с фильтрами реестра.
 */
export type ExpLinkOut = {
    /**
     * Section
     */
    section: string;
    /**
     * Filters
     */
    filters?: {
        [key: string]: string;
    };
};

/**
 * ExpReferenceOut
 */
export type ExpReferenceOut = {
    /**
     * Timezones
     */
    timezones: Array<TimezoneOut>;
    /**
     * Default Timezone
     */
    default_timezone: string;
    /**
     * Currency
     */
    currency: string;
    /**
     * Hosts
     */
    hosts: Array<HostOut>;
    /**
     * Wineries
     */
    wineries: Array<RefOut>;
    /**
     * Regions
     */
    regions: Array<RefOut>;
    /**
     * Countries
     */
    countries: Array<RefOut>;
    /**
     * Formats
     */
    formats: Array<OptionOut>;
    /**
     * Kinds
     */
    kinds: Array<OptionOut>;
    /**
     * Climates
     */
    climates: Array<OptionOut>;
    /**
     * Schedule Modes
     */
    schedule_modes: Array<OptionOut>;
    /**
     * Min Notices
     */
    min_notices: Array<OptionOut>;
    /**
     * Weekdays
     */
    weekdays: Array<OptionOut>;
    /**
     * Excursion Cancel Policies
     */
    excursion_cancel_policies: Array<OptionOut>;
    /**
     * Trip Cancel Policies
     */
    trip_cancel_policies: Array<OptionOut>;
    /**
     * Room Types
     */
    room_types: Array<OptionOut>;
    /**
     * Child Age Groups
     */
    child_age_groups: Array<OptionOut>;
    /**
     * Archive Reasons
     */
    archive_reasons: Array<OptionOut>;
    /**
     * Cancel Reasons
     */
    cancel_reasons: Array<OptionOut>;
    /**
     * Decline Reasons
     */
    decline_reasons: Array<OptionOut>;
};

/**
 * ExpTileOut
 *
 * Плитка «Сводки» карточки события.
 */
export type ExpTileOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
    /**
     * Caption
     */
    caption?: string | null;
    link?: ExpLinkOut | null;
};

/**
 * ExperienceTagIn
 */
export type ExperienceTagIn = {
    type: ExperienceType;
    /**
     * Id
     */
    id: string;
};

/**
 * ExperienceType
 */
export const ExperienceType = {
    Tasting: 'tasting',
    Excursion: 'excursion',
    Trip: 'trip'
} as const;

/**
 * ExperienceType
 */
export type ExperienceType = typeof ExperienceType[keyof typeof ExperienceType];

/**
 * ExpertApplicationDecisionIn
 */
export type ExpertApplicationDecisionIn = {
    /**
     * Decision
     */
    decision: 'approve' | 'needs_info' | 'reject';
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * ExpertApplicationOut
 *
 * Анкета заявки эксперта (CNT 12.3.8) в карточке участника.
 */
export type ExpertApplicationOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Submitted At
     */
    submitted_at: string;
    /**
     * Decided At
     */
    decided_at: string | null;
    decided_by: AppModulesAccSchemasCommonNamedRef | null;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * City
     */
    city: string;
    applicant_role: CodeLabel;
    experience: CodeLabel;
    /**
     * Specializations
     */
    specializations: Array<CodeLabel>;
    /**
     * Qualifications
     */
    qualifications: Array<CodeLabel>;
    /**
     * Diploma Document Id
     *
     * Виден только модератору (CNT)
     */
    diploma_document_id: string | null;
    /**
     * Links
     */
    links: string | null;
    /**
     * Formats
     */
    formats: Array<CodeLabel>;
    about: LocalizedText | null;
    /**
     * Author Terms Version Id
     */
    author_terms_version_id: string | null;
    /**
     * Author Terms Accepted At
     */
    author_terms_accepted_at: string | null;
    /**
     * Passport Snapshot
     *
     * Ступень и точность на момент подачи (GAM)
     */
    passport_snapshot: string | null;
    /**
     * Decision Comment
     */
    decision_comment: string | null;
};

/**
 * ExpertApplicationStatus
 */
export const ExpertApplicationStatus = {
    Review: 'review',
    NeedsInfo: 'needs_info',
    Approved: 'approved',
    Rejected: 'rejected',
    Withdrawn: 'withdrawn'
} as const;

/**
 * ExpertApplicationStatus
 */
export type ExpertApplicationStatus = typeof ExpertApplicationStatus[keyof typeof ExpertApplicationStatus];

/**
 * ExpertExperience
 */
export const ExpertExperience = {
    Y13: 'y1_3',
    Y35: 'y3_5',
    Y510: 'y5_10',
    Y10Plus: 'y10_plus'
} as const;

/**
 * ExpertExperience
 */
export type ExpertExperience = typeof ExpertExperience[keyof typeof ExpertExperience];

/**
 * ExpertFormat
 */
export const ExpertFormat = {
    Stories: 'stories',
    Publications: 'publications',
    OnlineTastings: 'online_tastings',
    WineryMeetings: 'winery_meetings',
    AuthorSets: 'author_sets'
} as const;

/**
 * ExpertFormat
 */
export type ExpertFormat = typeof ExpertFormat[keyof typeof ExpertFormat];

/**
 * ExpertOptionOut
 */
export type ExpertOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * ExpertQualification
 */
export const ExpertQualification = {
    Wset2: 'wset2',
    Wset3: 'wset3',
    WsetDiploma: 'wset_diploma',
    CourtOfMasterSommeliers: 'court_of_master_sommeliers',
    SommelierDiploma: 'sommelier_diploma',
    Other: 'other'
} as const;

/**
 * ExpertQualification
 */
export type ExpertQualification = typeof ExpertQualification[keyof typeof ExpertQualification];

/**
 * ExpertSpecialization
 */
export const ExpertSpecialization = {
    Russia: 'russia',
    Spain: 'spain',
    France: 'france',
    Italy: 'italy',
    Sparkling: 'sparkling',
    Red: 'red',
    White: 'white',
    Fortified: 'fortified'
} as const;

/**
 * ExpertSpecialization
 */
export type ExpertSpecialization = typeof ExpertSpecialization[keyof typeof ExpertSpecialization];

/**
 * ExtraGroupOut
 */
export type ExtraGroupOut = {
    /**
     * Key
     */
    key: 'learning' | 'tastings' | 'visits' | 'wines';
    /**
     * Title
     */
    title: string;
    /**
     * Note
     */
    note: string | null;
    /**
     * Tasks
     */
    tasks: Array<TaskOut>;
};

/**
 * ExtraOut
 */
export type ExtraOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    /**
     * Xp Left
     */
    xp_left: number | null;
    /**
     * Groups
     */
    groups: Array<ExtraGroupOut>;
};

/**
 * FacetGrapeOut
 */
export type FacetGrapeOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Label
     *
     * Короткое имя («Красностоп») или название
     */
    label: string;
    /**
     * Names
     *
     * Название и синонимы (BR-CAT-39)
     */
    names: Array<string>;
};

/**
 * FacetOptionOut
 */
export type FacetOptionOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
};

/**
 * FacetRegionOut
 */
export type FacetRegionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    country: CountryOut;
};

/**
 * FacetsOut
 */
export type FacetsOut = {
    /**
     * Regions
     */
    regions: Array<FacetRegionOut>;
    /**
     * Grapes
     */
    grapes: Array<FacetGrapeOut>;
    /**
     * Kinds
     */
    kinds: Array<FacetOptionOut>;
    /**
     * Tastes
     */
    tastes: Array<FacetOptionOut>;
    /**
     * Author Types
     *
     * Сеты: «Кто собрал»
     */
    author_types: Array<FacetOptionOut>;
    /**
     * Formats
     *
     * Сеты: «Формат»
     */
    formats: Array<FacetOptionOut>;
    /**
     * States
     *
     * Дропы: «Статус»
     */
    states: Array<FacetOptionOut>;
};

/**
 * FactIn
 */
export type FactIn = {
    /**
     * До 100 символов
     */
    title: LocalizedText;
    /**
     * До 300 символов
     */
    text: LocalizedText;
};

/**
 * FactItemOut
 */
export type FactItemOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
};

/**
 * FactPatchIn
 */
export type FactPatchIn = {
    title?: LocalizedText | null;
    text?: LocalizedText | null;
};

/**
 * FeedActionsOut
 */
export type FeedActionsOut = {
    like?: FeedLikeOut | null;
    /**
     * Save
     *
     * `{saved}`; null — нет
     */
    save?: {
        [key: string]: boolean;
    } | null;
    /**
     * Share
     */
    share?: boolean;
    /**
     * To Diary
     */
    to_diary?: boolean;
};

/**
 * FeedAuthorOut
 */
export type FeedAuthorOut = {
    /**
     * Member Id
     */
    member_id?: string | null;
    /**
     * Name
     */
    name: string;
    /**
     * Sub
     */
    sub?: string | null;
    /**
     * Kicker
     */
    kicker?: string | null;
    /**
     * Photo Url
     */
    photo_url?: string | null;
    /**
     * Initial
     */
    initial?: string | null;
};

/**
 * FeedCardOut
 *
 * Карточка ленты (12.6.1): регион, урок, сет, винодельня, дегустация, дроп, публикация,
 * рекомендация, хвост; `events_block` — «Афиша недели».
 */
export type FeedCardOut = {
    /**
     * Item Type
     */
    item_type: string;
    /**
     * Key
     */
    key?: string | null;
    /**
     * Topic
     */
    topic?: string | null;
    /**
     * Topic Label
     */
    topic_label?: string | null;
    /**
     * Rule
     *
     * Правило рекомендации R1–R12
     */
    rule?: string | null;
    author?: FeedAuthorOut | null;
    /**
     * Overline
     */
    overline?: string | null;
    /**
     * Kicker
     */
    kicker?: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text?: string | null;
    /**
     * Meta
     */
    meta?: string | null;
    /**
     * Pics
     */
    pics?: Array<string>;
    price?: Money | null;
    /**
     * Price Label
     */
    price_label?: string | null;
    /**
     * Progress
     */
    progress?: {
        [key: string]: number;
    } | null;
    /**
     * Cta
     */
    cta?: string | null;
    link?: ContentTargetOut | null;
    open?: ContentTargetOut | null;
    /**
     * Time Label
     */
    time_label?: string | null;
    actions?: FeedActionsOut | null;
    /**
     * Events
     */
    events?: Array<FeedEventOut> | null;
};

/**
 * FeedEventOut
 */
export type FeedEventOut = {
    /**
     * Tasting Id
     */
    tasting_id: string;
    /**
     * Day
     */
    day: string;
    /**
     * Month
     */
    month: string;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     */
    meta: string;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string | null;
    /**
     * Target
     */
    target: string;
};

/**
 * FeedImageOut
 */
export type FeedImageOut = {
    /**
     * Type
     *
     * photo, stamp, blind, face, coin
     */
    type: string;
    /**
     * Url
     */
    url?: string | null;
};

/**
 * FeedItemOut
 */
export type FeedItemOut = {
    /**
     * Id
     *
     * `notification_id` пуша — для `POST /notifications/read`
     */
    id: string;
    kind: InboxKind;
    /**
     * Kind Label
     *
     * Надпись над заголовком: «Заказ», «Дегустация»…
     */
    kind_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     */
    meta?: string | null;
    image?: FeedImageOut | null;
    /**
     * Deeplink
     *
     * Маршрут приложения, куда ведёт тап
     */
    deeplink: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Read
     */
    read: boolean;
    /**
     * Day Group
     *
     * BR-SRV-46: группа по дню в поясе устройства
     */
    day_group: 'today' | 'yesterday' | 'earlier';
    /**
     * Day Label
     *
     * «Сегодня», «Вчера», «Ранее»
     */
    day_label: string;
    /**
     * Time Label
     *
     * «ЧЧ:ММ» (сегодня, вчера) или «D месяц» (ранее)
     */
    time_label: string;
};

/**
 * FeedLikeOut
 */
export type FeedLikeOut = {
    /**
     * Owner
     */
    owner: 'CNT' | 'CAT' | 'LRN';
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Count
     */
    count: number;
};

/**
 * FeedOut
 */
export type FeedOut = {
    /**
     * Stories
     *
     * Первая страница; далее — null
     */
    stories: Array<StoryRingOut> | null;
    recommendations: RecommendationsOut | null;
    world_title: WorldTitleOut | null;
    /**
     * Items
     */
    items: Array<FeedCardOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * FeedPageOut
 */
export type FeedPageOut = {
    /**
     * Items
     */
    items: Array<FeedItemOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Unread Count
     */
    unread_count: number;
    /**
     * Unread Label
     *
     * Надзаголовок: «{N} новых уведомлений» или «Всё прочитано» (13.6.1)
     */
    unread_label: string;
};

/**
 * FieldOptionOut
 */
export type FieldOptionOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
};

/**
 * FieldOut
 *
 * Поле формы настроек (таблицы «Настройки в админке» глав INT).
 */
export type FieldOut = {
    /**
     * Name
     */
    name: string;
    type: FieldType;
    /**
     * Label
     */
    label: string;
    /**
     * Required
     */
    required: boolean;
    required_if?: RequiredIfOut | null;
    /**
     * Secret
     */
    secret: boolean;
    /**
     * Readonly
     */
    readonly?: boolean;
    /**
     * Default
     */
    default?: unknown;
    /**
     * Options
     */
    options?: Array<FieldOptionOut>;
    /**
     * Min
     */
    min?: number | null;
    /**
     * Max
     */
    max?: number | null;
    /**
     * Max Length
     */
    max_length?: number | null;
    /**
     * Pattern
     */
    pattern?: string | null;
    /**
     * Hint
     */
    hint?: string | null;
};

/**
 * FieldType
 */
export const FieldType = {
    Text: 'text',
    Int: 'int',
    Decimal: 'decimal',
    Bool: 'bool',
    Select: 'select',
    Email: 'email',
    EmailList: 'email_list',
    Url: 'url',
    Date: 'date',
    Secret: 'secret',
    SecretFile: 'secret_file',
    Multiselect: 'multiselect',
    StringList: 'string_list',
    KeyValue: 'key_value',
    Json: 'json'
} as const;

/**
 * FieldType
 */
export type FieldType = typeof FieldType[keyof typeof FieldType];

/**
 * FileOut
 */
export type FileOut = {
    /**
     * Upload Id
     */
    upload_id: string;
    /**
     * Purpose
     */
    purpose: string;
    status: FileStatus;
    /**
     * Content Type
     */
    content_type: string | null;
    /**
     * Size
     */
    size: number | null;
    /**
     * Url
     *
     * Публичный файл — постоянная ссылка; персональный — на 15 минут
     */
    url: string | null;
    variants_status: FileVariantsStatus;
    /**
     * Variants
     */
    variants: Array<FileVariantOut>;
};

/**
 * FileStatus
 */
export const FileStatus = {
    Pending: 'pending',
    Uploaded: 'uploaded',
    Ready: 'ready',
    Rejected: 'rejected',
    Expired: 'expired',
    Deleted: 'deleted'
} as const;

/**
 * FileStatus
 */
export type FileStatus = typeof FileStatus[keyof typeof FileStatus];

/**
 * FileVariantOut
 */
export type FileVariantOut = {
    /**
     * Name
     */
    name: string;
    /**
     * Url
     */
    url: string;
    /**
     * Width
     */
    width: number;
    /**
     * Height
     */
    height: number;
};

/**
 * FileVariantsStatus
 */
export const FileVariantsStatus = {
    None: 'none',
    Pending: 'pending',
    Ready: 'ready',
    Failed: 'failed'
} as const;

/**
 * FileVariantsStatus
 */
export type FileVariantsStatus = typeof FileVariantsStatus[keyof typeof FileVariantsStatus];

/**
 * FinishOut
 */
export type FinishOut = {
    /**
     * Score
     */
    score: number;
    /**
     * Total
     */
    total: number;
    /**
     * Passed
     */
    passed: boolean;
    /**
     * Perfect
     */
    perfect: boolean;
    /**
     * First Perfect
     */
    first_perfect: boolean;
    /**
     * Xp Awarded
     */
    xp_awarded: number | null;
    /**
     * Xp Already Received
     */
    xp_already_received: boolean;
    /**
     * Band
     */
    band?: 'low' | 'mid' | 'high' | null;
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string | null;
    /**
     * Xp Label
     */
    xp_label: string | null;
    /**
     * Recommendations
     */
    recommendations?: Array<RecommendationOut> | null;
    lesson_completed?: LessonCompletedOut | null;
    primary: LinkOut | null;
    secondary: LinkOut | null;
};

/**
 * FirstRegionOut
 */
export type FirstRegionOut = {
    /**
     * Status
     */
    status: 'unavailable' | 'available' | 'reserved' | 'used';
    /**
     * Pct
     */
    pct: number | null;
    /**
     * Used At
     */
    used_at: string | null;
};

/**
 * Flow
 */
export const Flow = {
    Ship: 'ship',
    Pickup: 'pickup',
    Kit: 'kit',
    Learn: 'learn',
    Live: 'live',
    Visit: 'visit',
    Trip: 'trip',
    Bonus: 'bonus',
    Partner: 'partner'
} as const;

/**
 * Flow
 */
export type Flow = typeof Flow[keyof typeof Flow];

/**
 * FollowStateOut
 */
export type FollowStateOut = {
    /**
     * Following
     */
    following: boolean;
    /**
     * Subscribers Count
     */
    subscribers_count: number;
    /**
     * Subscribers Label
     */
    subscribers_label: string;
    /**
     * Toast
     */
    toast: string;
};

/**
 * FormatOut
 */
export type FormatOut = {
    type: BookingTarget;
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Starts At
     */
    starts_at?: string | null;
};

/**
 * FormatsOut
 */
export type FormatsOut = {
    winery: RefOut;
    /**
     * Items
     */
    items: Array<FormatOut>;
};

/**
 * GalleryItemOut
 */
export type GalleryItemOut = {
    /**
     * Url
     */
    url: string;
    /**
     * Caption
     */
    caption: string | null;
};

/**
 * GamAdminExchangesPageOut
 */
export type GamAdminExchangesPageOut = {
    /**
     * Items
     */
    items: Array<ExchangeRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
};

/**
 * GamConfigOut
 */
export type GamConfigOut = {
    /**
     * Xp Rules
     *
     * Активные правила (справочник XP)
     */
    xp_rules: Array<XpTileOut>;
    /**
     * Xp Groups
     */
    xp_groups: Array<XpGroupOut>;
    /**
     * Levels
     */
    levels: Array<LevelOut>;
    /**
     * Tiers
     */
    tiers: Array<GamTierOut>;
    /**
     * Gates
     */
    gates: Array<GateInfoOut>;
    club: ClubConfigOut | null;
    /**
     * Intro Lessons Total
     */
    intro_lessons_total: number;
    tiles: TilesVisibilityOut;
    onboarding: OnboardingOut;
    /**
     * Exchange Rules
     *
     * Правила обмена BR-GAM-33
     */
    exchange_rules: Array<string>;
    /**
     * Bonus Name
     */
    bonus_name: string;
    /**
     * Brand Name
     */
    brand_name: string;
};

/**
 * GamDeclineReason
 */
export const GamDeclineReason = {
    RewardUnavailable: 'reward_unavailable',
    ContactFailed: 'contact_failed',
    UserRequest: 'user_request',
    RulesViolation: 'rules_violation'
} as const;

/**
 * GamDeclineReason
 */
export type GamDeclineReason = typeof GamDeclineReason[keyof typeof GamDeclineReason];

/**
 * GamExchangesPageOut
 */
export type GamExchangesPageOut = {
    /**
     * Items
     */
    items: Array<ExchangeItemOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Total
     */
    total: number;
    /**
     * Total Label
     */
    total_label: string;
};

/**
 * GamGateTierOut
 */
export type GamGateTierOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Title
     */
    title: string;
};

/**
 * GamHistoryItemOut
 */
export type GamHistoryItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Type
     */
    type: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Day
     */
    day: number;
    /**
     * Month Short
     */
    month_short: string;
    /**
     * Group
     *
     * «Октябрь 2026» — группа по месяцу
     */
    group: string;
    /**
     * Title
     *
     * «Что сделали»
     */
    title: string;
    /**
     * Change
     *
     * «Что это изменило»
     */
    change: string;
    /**
     * Link
     */
    link: string;
};

/**
 * GamHistoryPageOut
 */
export type GamHistoryPageOut = {
    /**
     * Items
     */
    items: Array<GamHistoryItemOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Years
     */
    years: Array<number>;
    /**
     * Months
     */
    months: Array<HistoryMonthOut>;
    /**
     * Total
     */
    total: number;
    /**
     * First At
     */
    first_at: string | null;
    /**
     * Header
     *
     * «N записей · с <месяц> <год>»
     */
    header: string | null;
};

/**
 * GamMeOut
 */
export type GamMeOut = {
    /**
     * Xp
     */
    xp: number;
    /**
     * Xp Max
     */
    xp_max: number;
    /**
     * Levels
     *
     * Переключатель уровней (без начального) с «уровень открыт» / «ещё N XP»
     */
    levels: Array<LevelOut>;
    /**
     * Selected Level Id
     *
     * Выбран по умолчанию: следующий недостигнутый или последний (11.6.1)
     */
    selected_level_id: string | null;
    level: MeLevelOut | null;
    level_by_xp: HeldLevelOut | null;
    next_level: NextLevelOut | null;
    /**
     * Levels Subtitle
     */
    levels_subtitle: string;
    discounts: DiscountsOut;
    next_perk: NextPerkOut;
    club: ClubOut;
    club_goal: ClubGoalOut | null;
    /**
     * Club Goal Empty Text
     */
    club_goal_empty_text: string;
    tier: MeTierOut | null;
    next_tier: NextTierOut | null;
    /**
     * Accuracy Pct
     */
    accuracy_pct: number;
    /**
     * Gates
     */
    gates: Array<GateStateOut>;
    /**
     * Intro Completed
     */
    intro_completed: boolean;
    /**
     * Intro Lessons Total
     */
    intro_lessons_total: number;
    /**
     * Intro Lessons Done
     */
    intro_lessons_done: number;
    /**
     * Coins Onboarding Seen
     */
    coins_onboarding_seen: boolean;
    level_bar: LevelBarOut;
    /**
     * Exchange Rules
     */
    exchange_rules: Array<string>;
    /**
     * Club Level Id
     */
    club_level_id: string | null;
};

/**
 * GamMePatchIn
 */
export type GamMePatchIn = {
    /**
     * Coins Onboarding Seen
     *
     * Онбординг XP показан (явный null — 422)
     */
    coins_onboarding_seen?: boolean | null;
    /**
     * Club Goal Reward Id
     *
     * Цель накопления — активная награда вида «Цель»; null — сбросить
     */
    club_goal_reward_id?: string | null;
};

/**
 * GamRowCan
 */
export type GamRowCan = {
    /**
     * Update
     */
    update: boolean;
    /**
     * Delete
     */
    delete: boolean;
};

/**
 * GamRuleHistoryEntryOut
 */
export type GamRuleHistoryEntryOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Actor Name
     */
    actor_name: string;
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Summary
     */
    summary: string;
};

/**
 * GamSectionLinkOut
 */
export type GamSectionLinkOut = {
    /**
     * Section
     */
    section: string;
    /**
     * Filters
     */
    filters: {
        [key: string]: string;
    };
};

/**
 * GamStampOut
 */
export type GamStampOut = {
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Region Name
     */
    region_name: string;
    /**
     * Level
     */
    level: 'bronze' | 'gold';
    /**
     * Awarded At
     */
    awarded_at: string;
    /**
     * Date Label
     *
     * ДД.ММ.ГГГГ
     */
    date_label: string;
    /**
     * Image Url
     *
     * Медальон региона (GEO)
     */
    image_url: string | null;
    /**
     * Basis
     */
    basis: string;
    /**
     * Line
     */
    line: string;
    /**
     * Received Label
     */
    received_label: string;
    /**
     * Explored Label
     */
    explored_label: string;
    /**
     * Explain
     */
    explain: string;
    /**
     * Target
     */
    target: string;
};

/**
 * GamStatsOut
 */
export type GamStatsOut = {
    /**
     * Users Total
     */
    users_total: number;
    /**
     * Users Tier2 Plus
     */
    users_tier2_plus: number;
    /**
     * Users Club
     */
    users_club: number;
    /**
     * Xp Accrued
     */
    xp_accrued: number;
    stamps: StatsStampsOut;
    /**
     * By Tier
     */
    by_tier: Array<StatsTierOut>;
    /**
     * By Level
     */
    by_level: Array<StatsLevelOut>;
};

/**
 * GamStepOut
 */
export type GamStepOut = {
    /**
     * Done
     */
    done: boolean;
    /**
     * N
     */
    n: number;
    /**
     * All
     */
    all: number;
};

/**
 * GamTierOut
 */
export type GamTierOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Title
     *
     * «III Первооткрыватель»
     */
    title: string;
    /**
     * Xp Min
     */
    xp_min: number;
    /**
     * Accuracy Min
     */
    accuracy_min: number;
    /**
     * Unlocks
     */
    unlocks: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Xp Label
     *
     * «3 000 XP», у первой — «старт»
     */
    xp_label: string;
    /**
     * Accuracy Label
     *
     * «точность 35», у первой — «без условий»
     */
    accuracy_label: string;
};

/**
 * GamUserRefOut
 */
export type GamUserRefOut = {
    /**
     * Id
     */
    id: string | null;
    /**
     * Name
     */
    name: string | null;
};

/**
 * GateInfoOut
 */
export type GateInfoOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Description
     */
    description: string;
    /**
     * Owner Module
     */
    owner_module: string;
    /**
     * Min Rank
     */
    min_rank: number | null;
    /**
     * Min Tier Id
     */
    min_tier_id: string | null;
};

/**
 * GateOut
 *
 * Гейт ступени (BR-EXP-20): без GAM — закрыт, порог неизвестен (`required_tier = null`).
 */
export type GateOut = {
    /**
     * Gate
     */
    gate: string;
    /**
     * Allowed
     */
    allowed: boolean;
    required_tier?: GateTierOut | null;
    current_tier?: GateTierOut | null;
};

/**
 * GateStateOut
 */
export type GateStateOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Min Rank
     */
    min_rank: number | null;
    /**
     * Allowed
     */
    allowed: boolean;
};

/**
 * GateTierOut
 */
export type GateTierOut = {
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
};

/**
 * GateTitleOut
 */
export type GateTitleOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
};

/**
 * GeoClientOut
 */
export type GeoClientOut = {
    default_city: ClientCityOut;
};

/**
 * GeoConfigOut
 */
export type GeoConfigOut = {
    default_point: DefaultPointOut;
    /**
     * Default Country
     *
     * Код страны по умолчанию (нижний регистр)
     */
    default_country: string;
    maps: GeoMapsOut;
    /**
     * Popular Queries
     */
    popular_queries: Array<string>;
    /**
     * You Here Radius Km
     */
    you_here_radius_km: number;
    /**
     * Position Max Age Min
     */
    position_max_age_min: number;
};

/**
 * GeoMapsOut
 */
export type GeoMapsOut = {
    provider: MapProvider;
    /**
     * Fallback Tiles
     */
    fallback_tiles: Array<TileSource>;
    /**
     * Tile Error Threshold
     */
    tile_error_threshold: number;
};

/**
 * GeoPointIn
 *
 * Также строка «широта · долгота», BR-GEO-58
 */
export type GeoPointIn = {
    /**
     * Lat
     */
    lat: number;
    /**
     * Lng
     */
    lng: number;
};

/**
 * GeoPointOut
 */
export type GeoPointOut = {
    /**
     * Lat
     */
    lat: number;
    /**
     * Lng
     */
    lng: number;
};

/**
 * GeoPolygonIn
 *
 * GeoJSON Polygon (WGS 84): одно внешнее кольцо `[[lng, lat], …]`, ≥ 3 различные вершины.
 */
export type GeoPolygonIn = {
    /**
     * Type
     */
    type: string;
    /**
     * Coordinates
     */
    coordinates: Array<Array<Array<number>>>;
};

/**
 * GeoPolygonOut
 */
export type GeoPolygonOut = {
    /**
     * Type
     */
    type?: string;
    /**
     * Coordinates
     */
    coordinates: Array<Array<Array<number>>>;
};

/**
 * GeocodeCandidateOut
 */
export type GeocodeCandidateOut = {
    /**
     * Formatted Address
     */
    formatted_address: string;
    /**
     * Lat
     */
    lat: number;
    /**
     * Lon
     */
    lon: number;
    /**
     * Precision
     */
    precision: 'exact' | 'street' | 'locality' | 'other';
    /**
     * Precision Label
     */
    precision_label: string;
    /**
     * Country Code
     *
     * ISO alpha-2 страны точки
     */
    country_code: string | null;
    /**
     * Provider Ref
     *
     * Google `place_id` (можно хранить бессрочно, BR-INT-SVC-36)
     */
    provider_ref?: string | null;
};

/**
 * GeocodeDetailsOut
 */
export type GeocodeDetailsOut = {
    item: GeocodeCandidateOut | null;
};

/**
 * GeocodeListOut
 *
 * `GET /admin/geo/geocode`: первый — точка «Найти по адресу», остальные (до 5) — выбор.
 */
export type GeocodeListOut = {
    /**
     * Items
     */
    items: Array<GeocodeCandidateOut>;
};

/**
 * GeocodeReverseOut
 */
export type GeocodeReverseOut = {
    /**
     * Предлагаемый адрес; null — не найден
     */
    item: GeocodeCandidateOut | null;
    /**
     * Warning
     *
     * BR-INT-SVC-35: «Точка вне страны региона «…» — проверьте координаты» (при `region_id`); сохранение не блокируется
     */
    warning?: string | null;
};

/**
 * GeocodeSuggestionOut
 */
export type GeocodeSuggestionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Text
     */
    text: string;
    /**
     * Secondary
     */
    secondary: string | null;
};

/**
 * GeocodeSuggestionsOut
 */
export type GeocodeSuggestionsOut = {
    /**
     * Items
     */
    items: Array<GeocodeSuggestionOut>;
};

/**
 * GoldGroupOut
 */
export type GoldGroupOut = {
    /**
     * Key
     */
    key: 'tours' | 'trips';
    /**
     * Title
     */
    title: string;
    /**
     * Done
     */
    done: number;
    /**
     * All
     */
    all: number;
    /**
     * Tasks
     */
    tasks: Array<TaskOut>;
};

/**
 * GoldOut
 */
export type GoldOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Groups
     */
    groups: Array<GoldGroupOut>;
    /**
     * Empty Title
     */
    empty_title: string | null;
    /**
     * Empty Text
     */
    empty_text: string | null;
};

/**
 * GoogleClientIdsOut
 */
export type GoogleClientIdsOut = {
    /**
     * Web
     */
    web?: string | null;
    /**
     * Android
     */
    android?: string | null;
    /**
     * Ios
     */
    ios?: string | null;
};

/**
 * GrapeOptionOut
 */
export type GrapeOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Short Name
     */
    short_name: string | null;
    /**
     * Aliases
     */
    aliases: Array<string>;
};

/**
 * GrapeOut
 */
export type GrapeOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
};

/**
 * GrapeShareAppOut
 */
export type GrapeShareAppOut = {
    /**
     * Name
     */
    name: string;
    /**
     * Share Pct
     */
    share_pct: number;
};

/**
 * GrapeShareIn
 */
export type GrapeShareIn = {
    /**
     * Grape Id
     */
    grape_id: string;
    /**
     * Share Pct
     *
     * 1…100; один сорт без доли — 100 % (BR-CAT-05)
     */
    share_pct?: number | null;
};

/**
 * GrapeShareOut
 */
export type GrapeShareOut = {
    /**
     * Grape Id
     */
    grape_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Share Pct
     */
    share_pct: number;
};

/**
 * GrapesIn
 */
export type GrapesIn = {
    /**
     * Items
     */
    items: Array<GrapeShareIn>;
};

/**
 * GuestConsentsIn
 */
export type GuestConsentsIn = {
    /**
     * Items
     */
    items: Array<ConsentItemIn>;
};

/**
 * GuestGateOut
 */
export type GuestGateOut = {
    /**
     * Age Confirmed
     *
     * Пройден «18+» на этом устройстве
     */
    age_confirmed: boolean;
    /**
     * Underage Locked
     *
     * Дважды «Мне меньше 18» — вход без аккаунта закрыт
     */
    underage_locked: boolean;
    /**
     * Onboarding Completed
     *
     * Пройден шаг «Что вам ближе»
     */
    onboarding_completed: boolean;
};

/**
 * GuestLocationIn
 */
export type GuestLocationIn = {
    /**
     * Country Code
     */
    country_code: string;
    /**
     * City Name
     */
    city_name: string;
    /**
     * City Suggestion Id
     */
    city_suggestion_id?: string | null;
};

/**
 * GuestLocationOut
 */
export type GuestLocationOut = {
    /**
     * Country Code
     */
    country_code: string;
    /**
     * City Name
     */
    city_name: string | null;
    /**
     * City Suggestion Id
     */
    city_suggestion_id: string | null;
};

/**
 * GuestMergeOut
 */
export type GuestMergeOut = {
    /**
     * Performed
     *
     * Гостевой прогресс устройства перенесён в аккаунт
     */
    performed: boolean;
    /**
     * Guest Profile Id
     */
    guest_profile_id: string | null;
    /**
     * Mode
     */
    mode: 'registration' | 'login' | null;
};

/**
 * GuestsIn
 */
export type GuestsIn = {
    visit_mode: VisitMode;
    /**
     * Group Capacity
     *
     * Расширение: вместимость вместе с режимом
     */
    group_capacity?: number | null;
    /**
     * Open Hours
     */
    open_hours?: Array<OpenHoursIn>;
    /**
     * Slots
     */
    slots?: Array<SlotIn>;
    /**
     * Stay Enabled
     */
    stay_enabled?: boolean;
    /**
     * Rooms
     */
    rooms?: Array<RoomIn>;
    /**
     * Booking Phone
     */
    booking_phone?: string | null;
};

/**
 * GuestsOut
 */
export type GuestsOut = {
    visit_mode: VisitMode;
    /**
     * Group Capacity
     */
    group_capacity: number | null;
    /**
     * Open Hours
     */
    open_hours: Array<OpenHoursOut>;
    /**
     * Slots
     */
    slots: Array<SlotOut>;
    /**
     * Stay Enabled
     */
    stay_enabled: boolean;
    /**
     * Rooms
     */
    rooms: Array<RoomOut>;
    /**
     * Booking Phone
     */
    booking_phone: string | null;
    summary: GuestsSummaryOut;
};

/**
 * GuestsSummaryOut
 */
export type GuestsSummaryOut = {
    /**
     * Schedule
     *
     * Сводка часов / слотов (BR-GEO-56)
     */
    schedule: string;
    /**
     * Stay
     *
     * Сводка ночлега (BR-GEO-57)
     */
    stay: string | null;
    /**
     * Warnings
     */
    warnings: Array<string>;
};

/**
 * HealthOut
 */
export type HealthOut = {
    /**
     * Status
     */
    status: 'ok' | 'degraded';
    /**
     * Database
     */
    database: boolean;
    /**
     * Redis
     */
    redis: boolean;
};

/**
 * HeldLevelOut
 */
export type HeldLevelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Held Label
     *
     * ««Исследователь» откроется после вводного курса»
     */
    held_label: string;
};

/**
 * HideReason
 */
export const HideReason = {
    OutOfStock: 'out_of_stock',
    SeasonClosed: 'season_closed',
    ContentEdits: 'content_edits',
    PartnerDecision: 'partner_decision'
} as const;

/**
 * HideReason
 */
export type HideReason = typeof HideReason[keyof typeof HideReason];

/**
 * HistoryEntryOut
 */
export type HistoryEntryOut = {
    /**
     * At
     */
    at: string;
    /**
     * Kind
     */
    kind: 'status' | 'message';
    from_status?: TicketStatus | null;
    to_status?: TicketStatus | null;
    /**
     * Label
     */
    label: string;
    actor_kind?: StatusActorKind | null;
    /**
     * Actor Name
     */
    actor_name?: string | null;
    /**
     * Note
     */
    note?: string | null;
};

/**
 * HistoryMonthOut
 */
export type HistoryMonthOut = {
    /**
     * Year
     */
    year: number;
    /**
     * Month
     */
    month: number;
    /**
     * Label
     */
    label: string;
    /**
     * Count
     */
    count: number;
    /**
     * Count Label
     */
    count_label: string;
};

/**
 * HistoryOut
 */
export type HistoryOut = {
    /**
     * At
     */
    at: string;
    /**
     * Actor
     */
    actor: string;
    /**
     * Action
     */
    action: string;
    /**
     * Text
     */
    text: string;
};

/**
 * HostOut
 */
export type HostOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Role
     */
    role: string;
    /**
     * Role Label
     */
    role_label?: string | null;
    /**
     * Photo Url
     */
    photo_url?: string | null;
};

/**
 * HowToGetOut
 */
export type HowToGetOut = {
    /**
     * Distance Km
     */
    distance_km: number;
    /**
     * Road
     */
    road: string | null;
    /**
     * Transfer
     */
    transfer: string | null;
};

/**
 * IdName
 *
 * Ссылка на объект для админки: название — LocalizedText целиком (COM-09).
 */
export type IdName = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
};

/**
 * IdentificationField
 */
export const IdentificationField = {
    Grape: 'grape',
    Country: 'country',
    Region: 'region',
    Climate: 'climate'
} as const;

/**
 * IdentificationField
 */
export type IdentificationField = typeof IdentificationField[keyof typeof IdentificationField];

/**
 * InboxKind
 *
 * Вид записи ленты (13.3.10); `none` — в ленту не попадает.
 */
export const InboxKind = {
    Order: 'order',
    Drop: 'drop',
    Trip: 'trip',
    Event: 'event',
    Blind: 'blind',
    Learn: 'learn',
    Xp: 'xp',
    Badge: 'badge',
    Account: 'account',
    Care: 'care',
    Content: 'content',
    Promo: 'promo',
    None: 'none'
} as const;

/**
 * InboxKind
 *
 * Вид записи ленты (13.3.10); `none` — в ленту не попадает.
 */
export type InboxKind = typeof InboxKind[keyof typeof InboxKind];

/**
 * IntPackagePresetIn
 */
export type IntPackagePresetIn = {
    /**
     * Name
     */
    name: string;
    /**
     * Bottles 075 Equivalent
     */
    bottles_075_equivalent: number;
    /**
     * Gross Weight Kg
     */
    gross_weight_kg: number | string;
    /**
     * Length Cm
     */
    length_cm: number;
    /**
     * Width Cm
     */
    width_cm: number;
    /**
     * Height Cm
     */
    height_cm: number;
    /**
     * Active
     */
    active?: boolean;
};

/**
 * IntPackagePresetOut
 */
export type IntPackagePresetOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Bottles 075 Equivalent
     */
    bottles_075_equivalent: number;
    /**
     * Gross Weight Kg
     */
    gross_weight_kg: number | string;
    /**
     * Length Cm
     */
    length_cm: number;
    /**
     * Width Cm
     */
    width_cm: number;
    /**
     * Height Cm
     */
    height_cm: number;
    /**
     * Dimensions Label
     */
    dimensions_label: string;
    /**
     * Active
     */
    active: boolean;
    can: IntShipperLocationCan;
};

/**
 * IntPackagePresetsPage
 */
export type IntPackagePresetsPage = {
    /**
     * Items
     */
    items: Array<IntPackagePresetOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
};

/**
 * IntPartnerCardOut
 *
 * Вкладки карточки «Партнёр ТБН» (16.10.1.1) сверх общих полей интеграции.
 */
export type IntPartnerCardOut = {
    /**
     * Credentials
     */
    credentials: Array<IntPartnerCredentialOut>;
    can_create_credential: IntPartnerCreateAllowed;
    /**
     * Signing Keys
     */
    signing_keys: Array<IntPartnerSigningKeyOut>;
    /**
     * Jwks Url
     */
    jwks_url: string;
    /**
     * Return Url
     */
    return_url: string;
    webhook: IntPartnerWebhookBlock;
    /**
     * Last Inbound At
     *
     * Последний успешный входящий запрос
     */
    last_inbound_at: string | null;
    /**
     * Last Catalog Pull At
     *
     * Последний запрос фида
     */
    last_catalog_pull_at: string | null;
    /**
     * Catalog Stale
     *
     * Фид не запрашивался больше 24 часов
     */
    catalog_stale: boolean;
    /**
     * Feed Items
     */
    feed_items: number;
    /**
     * Test Link Available
     *
     * «Сгенерировать тестовую ссылку» (режим Тест)
     */
    test_link_available: boolean;
};

/**
 * IntPartnerCatalogStatePage
 */
export type IntPartnerCatalogStatePage = {
    /**
     * Items
     */
    items: Array<IntPartnerCatalogStateRow>;
    /**
     * Total
     */
    total: number;
    /**
     * Last Catalog Pull At
     */
    last_catalog_pull_at: string | null;
    /**
     * Stale Warning
     */
    stale_warning: string | null;
};

/**
 * IntPartnerCatalogStateRow
 */
export type IntPartnerCatalogStateRow = {
    /**
     * Item Type
     */
    item_type: string;
    /**
     * Item Type Label
     */
    item_type_label: string;
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Sku
     */
    sku: string;
    /**
     * Partner Sku
     */
    partner_sku: string | null;
    /**
     * Partner Available
     */
    partner_available: string;
    /**
     * Partner Available Label
     */
    partner_available_label: string;
    /**
     * Partner Stock
     */
    partner_stock: number | null;
    /**
     * Partner Price
     */
    partner_price: {
        [key: string]: unknown;
    } | null;
    /**
     * Feed Status
     */
    feed_status: string;
    /**
     * Feed Status Label
     */
    feed_status_label: string;
    /**
     * Feed Version
     */
    feed_version: number;
    /**
     * In Feed Since
     */
    in_feed_since: string;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Reported At
     */
    reported_at: string | null;
};

/**
 * IntPartnerCreateAllowed
 */
export type IntPartnerCreateAllowed = {
    /**
     * Test
     */
    test: boolean;
    /**
     * Prod
     */
    prod: boolean;
    /**
     * Hint
     *
     * «Отзовите один из ключей», если ключей режима уже два
     */
    hint: string | null;
};

/**
 * IntPartnerCredentialCreated
 */
export type IntPartnerCredentialCreated = {
    /**
     * Key Id
     */
    key_id: string;
    /**
     * Secret
     *
     * Секрет показывается только в этом ответе
     */
    secret: string;
    mode: IntegrationMode;
    /**
     * Expires At
     */
    expires_at: string | null;
    /**
     * Message
     */
    message: string;
};

/**
 * IntPartnerCredentialIn
 */
export type IntPartnerCredentialIn = {
    mode: IntegrationMode;
    /**
     * Expires At
     */
    expires_at?: string | null;
};

/**
 * IntPartnerCredentialOut
 *
 * API-ключ партнёра (16.3.2): секрет не отдаётся — только последние 4 символа.
 */
export type IntPartnerCredentialOut = {
    /**
     * Key Id
     */
    key_id: string;
    mode: IntegrationMode;
    /**
     * Mode Label
     */
    mode_label: string;
    /**
     * Secret Last4
     */
    secret_last4: string;
    /**
     * Status
     */
    status: 'active' | 'revoked' | 'expired';
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Last Used At
     */
    last_used_at: string | null;
    /**
     * Expires At
     */
    expires_at: string | null;
    /**
     * Revoked At
     */
    revoked_at: string | null;
    /**
     * Can Revoke
     */
    can_revoke: boolean;
};

/**
 * IntPartnerHandoffPage
 */
export type IntPartnerHandoffPage = {
    /**
     * Items
     */
    items: Array<IntPartnerHandoffRow>;
    /**
     * Total
     */
    total: number;
};

/**
 * IntPartnerHandoffRow
 */
export type IntPartnerHandoffRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * User Id
     */
    user_id: string;
    /**
     * User Name
     */
    user_name: string | null;
    /**
     * Item Type
     */
    item_type: string;
    /**
     * Item Type Label
     */
    item_type_label: string;
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Qty
     */
    qty: number;
    /**
     * Amount Hint
     */
    amount_hint: {
        [key: string]: unknown;
    };
    /**
     * Eligible Early Access
     */
    eligible_early_access: boolean;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Order Id
     */
    order_id: string | null;
    /**
     * Order Number
     */
    order_number: number | null;
    /**
     * Expires At
     */
    expires_at: string;
};

/**
 * IntPartnerNotifyAllOut
 */
export type IntPartnerNotifyAllOut = {
    /**
     * Events
     */
    events: number;
};

/**
 * IntPartnerSignatureCheckIn
 *
 * «Проверить подпись» (16.10.1.1): строка запроса партнёра и его подпись.
 */
export type IntPartnerSignatureCheckIn = {
    /**
     * Method
     */
    method: string;
    /**
     * Path
     *
     * Путь с query-строкой
     */
    path: string;
    /**
     * Timestamp
     */
    timestamp: string;
    /**
     * Nonce
     */
    nonce: string;
    /**
     * Body
     */
    body?: string;
    /**
     * Key Id
     */
    key_id: string;
    /**
     * Signature
     */
    signature: string;
};

/**
 * IntPartnerSignatureCheckOut
 */
export type IntPartnerSignatureCheckOut = {
    /**
     * Valid
     */
    valid: boolean;
    /**
     * Message
     */
    message: string;
    /**
     * String To Sign
     *
     * Строка для подписи по BR-INT-DLV-02
     */
    string_to_sign: string;
};

/**
 * IntPartnerSigningKeyCreated
 */
export type IntPartnerSigningKeyCreated = {
    /**
     * Kid
     */
    kid: string;
    /**
     * Previous Kid
     */
    previous_kid: string | null;
};

/**
 * IntPartnerSigningKeyOut
 */
export type IntPartnerSigningKeyOut = {
    /**
     * Kid
     */
    kid: string;
    /**
     * Status
     */
    status: 'active' | 'retiring' | 'retired';
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Activated At
     */
    activated_at: string;
    /**
     * Retiring At
     */
    retiring_at: string | null;
};

/**
 * IntPartnerSkuIn
 */
export type IntPartnerSkuIn = {
    /**
     * Partner Sku
     */
    partner_sku: string | null;
};

/**
 * IntPartnerTestLinkIn
 */
export type IntPartnerTestLinkIn = {
    /**
     * Item Type
     */
    item_type: 'wine' | 'set' | 'drop' | 'blind_kit';
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Qty
     */
    qty?: number;
};

/**
 * IntPartnerTestLinkOut
 */
export type IntPartnerTestLinkOut = {
    /**
     * Redirect Url
     */
    redirect_url: string;
    /**
     * Handoff Id
     */
    handoff_id: string;
    /**
     * Expires At
     */
    expires_at: string;
};

/**
 * IntPartnerWebhookBlock
 */
export type IntPartnerWebhookBlock = {
    /**
     * Url
     */
    url: string | null;
    /**
     * Events
     */
    events: Array<string>;
    /**
     * Secret Set
     */
    secret_set: boolean;
    /**
     * Failed Count
     */
    failed_count: number;
    /**
     * Recent
     *
     * Последние 50 вебхуков
     */
    recent: Array<IntPartnerWebhookOut>;
};

/**
 * IntPartnerWebhookOut
 */
export type IntPartnerWebhookOut = {
    /**
     * Event Id
     */
    event_id: string;
    /**
     * Event Type
     */
    event_type: string;
    /**
     * Event Label
     */
    event_label: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Attempts
     */
    attempts: number;
    /**
     * Last Http Status
     */
    last_http_status: number | null;
    /**
     * Last Error
     */
    last_error: string | null;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Next Attempt At
     */
    next_attempt_at: string | null;
    /**
     * Can Resend
     */
    can_resend: boolean;
};

/**
 * IntPartnerWebhookTestOut
 */
export type IntPartnerWebhookTestOut = {
    /**
     * Http Status
     */
    http_status: number | null;
    /**
     * Duration Ms
     */
    duration_ms: number;
    /**
     * Error
     */
    error: string | null;
    /**
     * Message
     */
    message: string;
};

/**
 * IntPickupIn
 */
export type IntPickupIn = {
    /**
     * Shipper Location Id
     */
    shipper_location_id: string;
    /**
     * Date
     */
    date: string;
};

/**
 * IntPickupOut
 */
export type IntPickupOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Shipper Location Id
     */
    shipper_location_id: string;
    /**
     * Shipper Location Name
     */
    shipper_location_name: string;
    /**
     * Pickup Date
     */
    pickup_date: string;
    /**
     * Ready At
     */
    ready_at: string;
    /**
     * Close At
     */
    close_at: string;
    /**
     * Dispatch Confirmation Number
     */
    dispatch_confirmation_number: string | null;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Shipments Count
     */
    shipments_count: number;
    /**
     * Error Message
     */
    error_message: string | null;
};

/**
 * IntReferenceCardOut
 *
 * Справочная карточка без API (СДЭК, 16.10.1.2).
 */
export type IntReferenceCardOut = {
    /**
     * Description
     */
    description: string;
    /**
     * Tracking Url Template
     */
    tracking_url_template: string | null;
    /**
     * Source Integration Key
     *
     * Где настраивается шаблон ссылки
     */
    source_integration_key: string | null;
};

/**
 * IntShipmentAccepted
 */
export type IntShipmentAccepted = {
    /**
     * Id
     */
    id: string;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * IntShipmentCan
 */
export type IntShipmentCan = {
    /**
     * Download Label
     */
    download_label?: boolean;
    /**
     * Retry
     */
    retry?: boolean;
    /**
     * Update Address
     */
    update_address?: boolean;
    /**
     * Cancel
     */
    cancel?: boolean;
    /**
     * Refresh Tracking
     */
    refresh_tracking?: boolean;
    /**
     * Update Excise
     */
    update_excise?: boolean;
    /**
     * Request Pickup
     */
    request_pickup?: boolean;
    /**
     * Cancel Pickup
     */
    cancel_pickup?: boolean;
};

/**
 * IntShipmentEventOut
 */
export type IntShipmentEventOut = {
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Source
     */
    source: string;
    /**
     * Code
     */
    code: string | null;
    /**
     * Status Code
     */
    status_code: string | null;
    /**
     * Description
     */
    description: string | null;
    /**
     * Location
     */
    location: string | null;
    /**
     * Mapped Status
     */
    mapped_status: string | null;
    /**
     * Mapped Status Label
     */
    mapped_status_label: string | null;
};

/**
 * IntShipmentOut
 */
export type IntShipmentOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Order Id
     */
    order_id: string;
    /**
     * Order Label
     */
    order_label: string;
    /**
     * Delivery Id
     */
    delivery_id: string;
    /**
     * Carrier
     */
    carrier: string;
    /**
     * Carrier Label
     */
    carrier_label: string;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Tracking Number
     */
    tracking_number: string | null;
    /**
     * Tracking Url
     */
    tracking_url: string | null;
    /**
     * Product Code
     */
    product_code: string | null;
    /**
     * Planned Shipping At
     */
    planned_shipping_at: string | null;
    /**
     * Estimated Delivery At
     */
    estimated_delivery_at: string | null;
    /**
     * Delivered At
     */
    delivered_at: string | null;
    /**
     * Shipper Location Id
     */
    shipper_location_id: string;
    /**
     * Shipper Location Name
     */
    shipper_location_name: string;
    /**
     * Receiver
     */
    receiver: {
        [key: string]: unknown;
    };
    /**
     * Packages
     */
    packages: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Items
     */
    items: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Service Codes
     */
    service_codes: Array<string>;
    declared_value: IntShippingMoney;
    carrier_cost: IntShippingMoney | null;
    /**
     * Excise Document Ref
     */
    excise_document_ref: string | null;
    /**
     * Excise Required
     *
     * Другая страна ЕС — нужен номер e-SAD (BR-INT-DLV-57)
     */
    excise_required: boolean;
    pickup: IntShipmentPickupOut | null;
    /**
     * Last Error Code
     */
    last_error_code: string | null;
    /**
     * Last Error Message
     */
    last_error_message: string | null;
    /**
     * Attempts
     */
    attempts: number;
    /**
     * Events
     */
    events: Array<IntShipmentEventOut>;
    can: IntShipmentCan;
};

/**
 * IntShipmentPatchIn
 */
export type IntShipmentPatchIn = {
    /**
     * Excise Document Ref
     */
    excise_document_ref: string | null;
};

/**
 * IntShipmentPickupOut
 */
export type IntShipmentPickupOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Pickup Date
     */
    pickup_date: string;
    /**
     * Dispatch Confirmation Number
     */
    dispatch_confirmation_number: string | null;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * IntShipmentReceiverIn
 */
export type IntShipmentReceiverIn = {
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Phone
     */
    phone: string;
    /**
     * Email
     */
    email: string;
    /**
     * Address Line1
     */
    address_line1: string;
    /**
     * Address Line2
     */
    address_line2?: string | null;
    /**
     * Address Line3
     */
    address_line3?: string | null;
    /**
     * Postal Code
     */
    postal_code: string;
    /**
     * City
     */
    city: string;
    /**
     * Province
     */
    province?: string | null;
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Delivery Note
     */
    delivery_note?: string | null;
};

/**
 * IntShipmentRetryIn
 */
export type IntShipmentRetryIn = {
    receiver?: IntShipmentReceiverIn | null;
};

/**
 * IntShipmentTrackingOut
 */
export type IntShipmentTrackingOut = {
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Events
     */
    events: Array<IntShipmentEventOut>;
};

/**
 * IntShipperLocationCan
 */
export type IntShipperLocationCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * IntShipperLocationIn
 */
export type IntShipperLocationIn = {
    /**
     * Name
     */
    name: string;
    /**
     * Winery Id
     */
    winery_id?: string | null;
    /**
     * Company Name
     */
    company_name: string;
    /**
     * Contact Name
     */
    contact_name: string;
    /**
     * Phone
     */
    phone: string;
    /**
     * Email
     */
    email: string;
    /**
     * Address Line1
     */
    address_line1: string;
    /**
     * Address Line2
     */
    address_line2?: string | null;
    /**
     * Address Line3
     */
    address_line3?: string | null;
    /**
     * Postal Code
     */
    postal_code: string;
    /**
     * City
     */
    city: string;
    /**
     * Province Code
     */
    province_code?: string | null;
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Excise Warehouse Ref
     */
    excise_warehouse_ref?: string | null;
    /**
     * Pickup Mode
     */
    pickup_mode: 'regular' | 'on_demand';
    /**
     * Pickup Ready Time
     */
    pickup_ready_time: string;
    /**
     * Pickup Close Time
     */
    pickup_close_time: string;
    /**
     * Pickup Cutoff Time
     */
    pickup_cutoff_time: string;
    /**
     * Timezone
     */
    timezone?: string | null;
    /**
     * Business Days
     */
    business_days: Array<number>;
    /**
     * Active
     */
    active?: boolean;
};

/**
 * IntShipperLocationOut
 */
export type IntShipperLocationOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Winery Id
     */
    winery_id: string | null;
    /**
     * Winery Name
     */
    winery_name: string | null;
    /**
     * Company Name
     */
    company_name: string;
    /**
     * Contact Name
     */
    contact_name: string;
    /**
     * Phone
     */
    phone: string;
    /**
     * Email
     */
    email: string;
    /**
     * Address Line1
     */
    address_line1: string;
    /**
     * Address Line2
     */
    address_line2: string | null;
    /**
     * Address Line3
     */
    address_line3: string | null;
    /**
     * Postal Code
     */
    postal_code: string;
    /**
     * City
     */
    city: string;
    /**
     * Province Code
     */
    province_code: string | null;
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Excise Warehouse Ref
     */
    excise_warehouse_ref: string | null;
    /**
     * Pickup Mode
     */
    pickup_mode: string;
    /**
     * Pickup Mode Label
     */
    pickup_mode_label: string;
    /**
     * Pickup Ready Time
     */
    pickup_ready_time: string;
    /**
     * Pickup Close Time
     */
    pickup_close_time: string;
    /**
     * Pickup Cutoff Time
     */
    pickup_cutoff_time: string;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Business Days
     */
    business_days: Array<number>;
    /**
     * Active
     */
    active: boolean;
    can: IntShipperLocationCan;
};

/**
 * IntShipperLocationsPage
 */
export type IntShipperLocationsPage = {
    /**
     * Items
     */
    items: Array<IntShipperLocationOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
};

/**
 * IntShippingCardOut
 *
 * Карточка «DHL Express» (16.10.1.3): предусловия и состояние отправлений.
 */
export type IntShippingCardOut = {
    /**
     * Alcohol Approved
     */
    alcohol_approved: boolean;
    /**
     * Label Errors
     *
     * Отправлений с ошибкой создания
     */
    label_errors: number;
    /**
     * Shipper Locations Active
     */
    shipper_locations_active: number;
    /**
     * Package Presets Active
     */
    package_presets_active: number;
    /**
     * Countries Enabled
     *
     * Стран с включёнными продажами (налоговый профиль)
     */
    countries_enabled: number;
    push: IntShippingPushOut | null;
};

/**
 * IntShippingMoney
 */
export type IntShippingMoney = {
    /**
     * Amount
     */
    amount: number;
    /**
     * Currency
     */
    currency: string;
};

/**
 * IntShippingPushOut
 */
export type IntShippingPushOut = {
    /**
     * Enabled
     */
    enabled: boolean;
    /**
     * Active Subscriptions
     */
    active_subscriptions: number;
    /**
     * Failed Subscriptions
     */
    failed_subscriptions: number;
    /**
     * Last Push At
     */
    last_push_at: string | null;
};

/**
 * IntTaxProfileIn
 */
export type IntTaxProfileIn = {
    /**
     * Valid From
     */
    valid_from: string;
    /**
     * Vat Rate Standard
     */
    vat_rate_standard: number | string;
    excise_still_wine_per_hl: IntShippingMoney;
    excise_sparkling_wine_per_hl: IntShippingMoney;
    excise_intermediate_per_hl?: IntShippingMoney | null;
    /**
     * Excise Basis
     */
    excise_basis: 'per_hl_product' | 'per_hl_alcohol';
    /**
     * Tax Representative
     */
    tax_representative?: string | null;
    /**
     * Registration Ref
     */
    registration_ref?: string | null;
    /**
     * Enabled For Sales
     */
    enabled_for_sales: boolean;
};

/**
 * IntTaxProfileOut
 */
export type IntTaxProfileOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Country Name
     */
    country_name: string;
    /**
     * Valid From
     */
    valid_from: string;
    /**
     * Vat Rate Standard
     */
    vat_rate_standard: number | string;
    excise_still_wine_per_hl: IntShippingMoney;
    excise_sparkling_wine_per_hl: IntShippingMoney;
    excise_intermediate_per_hl: IntShippingMoney | null;
    /**
     * Excise Basis
     */
    excise_basis: string;
    /**
     * Tax Representative
     */
    tax_representative: string | null;
    /**
     * Registration Ref
     */
    registration_ref: string | null;
    /**
     * Enabled For Sales
     */
    enabled_for_sales: boolean;
    /**
     * Current
     *
     * Действует сейчас (последний `valid_from` ≤ сегодня)
     */
    current: boolean;
    /**
     * Dhl Allowed
     *
     * Страна в «Странах доставки» карточки DHL
     */
    dhl_allowed: boolean;
};

/**
 * IntTaxProfilesPage
 */
export type IntTaxProfilesPage = {
    /**
     * Items
     */
    items: Array<IntTaxProfileOut>;
    /**
     * Total
     */
    total: number;
};

/**
 * IntegrationActionIn
 *
 * Тело действия провайдера: `send_test_email` — `{to}`, `send_test_push` — `{user_email}`.
 */
export type IntegrationActionIn = {
    /**
     * To
     */
    to?: string | null;
    /**
     * User Email
     */
    user_email?: string | null;
    /**
     * Shipper Location Id
     */
    shipper_location_id?: string | null;
    /**
     * Country Code
     */
    country_code?: string | null;
    /**
     * Postal Code
     */
    postal_code?: string | null;
    /**
     * City
     */
    city?: string | null;
    /**
     * Bottles
     */
    bottles?: number | null;
};

/**
 * IntegrationCan
 */
export type IntegrationCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Test
     */
    test?: boolean;
    /**
     * Enable
     */
    enable?: boolean;
    /**
     * Disable
     */
    disable?: boolean;
    /**
     * Activate
     */
    activate?: boolean;
    /**
     * Actions
     */
    actions?: boolean;
    /**
     * Open Log
     */
    open_log?: boolean;
};

/**
 * IntegrationCard
 */
export type IntegrationCard = {
    /**
     * Key
     */
    key: string;
    port: IntegrationPort;
    /**
     * Group
     */
    group: string;
    /**
     * Group Label
     */
    group_label: string;
    /**
     * Provider
     */
    provider: string;
    /**
     * Title
     */
    title: string;
    mode: IntegrationMode;
    /**
     * Mode Label
     */
    mode_label: string;
    /**
     * Enabled
     */
    enabled: boolean;
    /**
     * Is Active
     */
    is_active: boolean | null;
    status: IntegrationStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Availability Note
     */
    availability_note: string | null;
    /**
     * Settings
     *
     * Несекретные параметры
     */
    settings: {
        [key: string]: unknown;
    };
    /**
     * Secrets
     */
    secrets: {
        [key: string]: SecretStateOut;
    };
    /**
     * Fields
     */
    fields: Array<FieldOut>;
    /**
     * Read Only
     *
     * Адреса вебхуков, redirect URI и т. п.
     */
    read_only: {
        [key: string]: string;
    };
    /**
     * Read Only Fields
     */
    read_only_fields: Array<ReadOnlyFieldOut>;
    /**
     * Last Check At
     */
    last_check_at: string | null;
    last_check_result: CheckResultOut | null;
    /**
     * Last Success At
     */
    last_success_at: string | null;
    /**
     * Last Error At
     */
    last_error_at: string | null;
    /**
     * Last Error
     */
    last_error: string | null;
    metrics: MetricsOut;
    /**
     * Recent Errors
     */
    recent_errors: Array<LogBriefOut>;
    /**
     * Alerts
     */
    alerts: Array<AlertOut>;
    /**
     * Warnings
     */
    warnings: Array<string>;
    /**
     * Actions
     */
    actions: Array<ActionOut>;
    /**
     * Degradation
     *
     * Текст подтверждения «Выключить» (14.10.1)
     */
    degradation: string;
    /**
     * History
     */
    history: Array<AppIntegrationsSchemasHistoryItemOut>;
    mail?: MailTilesOut | null;
    push?: PushTilesOut | null;
    payments?: PaymentTilesOut | null;
    store?: StoreTilesOut | null;
    recognition?: RecognitionTilesOut | null;
    translation?: TranslationTilesOut | null;
    video?: VideoTilesOut | null;
    partner?: IntPartnerCardOut | null;
    shipping?: IntShippingCardOut | null;
    reference?: IntReferenceCardOut | null;
    can: IntegrationCan;
};

/**
 * IntegrationMode
 */
export const IntegrationMode = { Test: 'test', Prod: 'prod' } as const;

/**
 * IntegrationMode
 */
export type IntegrationMode = typeof IntegrationMode[keyof typeof IntegrationMode];

/**
 * IntegrationPort
 */
export const IntegrationPort = {
    Payment: 'payment',
    StoreBilling: 'store_billing',
    Partner: 'partner',
    Shipping: 'shipping',
    Auth: 'auth',
    Map: 'map',
    Geocoder: 'geocoder',
    Mail: 'mail',
    Push: 'push',
    Recognizer: 'recognizer',
    Translator: 'translator',
    Video: 'video'
} as const;

/**
 * IntegrationPort
 */
export type IntegrationPort = typeof IntegrationPort[keyof typeof IntegrationPort];

/**
 * IntegrationRow
 */
export type IntegrationRow = {
    /**
     * Key
     */
    key: string;
    port: IntegrationPort;
    /**
     * Group
     *
     * Группа реестра (14.10.1): payments, auth, mail, …
     */
    group: string;
    /**
     * Group Label
     */
    group_label: string;
    /**
     * Provider
     */
    provider: string;
    /**
     * Title
     */
    title: string;
    mode: IntegrationMode;
    /**
     * Mode Label
     */
    mode_label: string;
    /**
     * Enabled
     */
    enabled: boolean;
    /**
     * Is Active
     *
     * RU: активный платёжный шлюз (BR-INT-02)
     */
    is_active: boolean | null;
    status: IntegrationStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Available
     *
     * Адаптер провайдера реализован на этом этапе; false — карточка есть, подключение появится с главой ТЗ (`availability_note`)
     */
    available: boolean;
    /**
     * Last Check At
     */
    last_check_at: string | null;
    last_check_result: CheckResultOut | null;
    /**
     * Last Success At
     */
    last_success_at: string | null;
    /**
     * Errors 24H
     *
     * Записей журнала за 24 ч с результатом не success/duplicate
     */
    errors_24h: number;
    /**
     * Open Alerts
     */
    open_alerts: number;
    /**
     * Платёжный шлюз (15.12.1): способы оплаты, незавершённые платежи, последний вебхук; у остальных интеграций — null
     */
    payments?: PaymentTilesOut | null;
    /**
     * Стор (15.12.2): продукты, покупки без acknowledge, уведомления; у остальных интеграций — null
     */
    store?: StoreTilesOut | null;
    can: IntegrationCan;
};

/**
 * IntegrationSaveOut
 */
export type IntegrationSaveOut = {
    /**
     * Key
     */
    key: string;
    port: IntegrationPort;
    /**
     * Group
     */
    group: string;
    /**
     * Group Label
     */
    group_label: string;
    /**
     * Provider
     */
    provider: string;
    /**
     * Title
     */
    title: string;
    mode: IntegrationMode;
    /**
     * Mode Label
     */
    mode_label: string;
    /**
     * Enabled
     */
    enabled: boolean;
    /**
     * Is Active
     */
    is_active: boolean | null;
    status: IntegrationStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Available
     */
    available: boolean;
    /**
     * Availability Note
     */
    availability_note: string | null;
    /**
     * Settings
     *
     * Несекретные параметры
     */
    settings: {
        [key: string]: unknown;
    };
    /**
     * Secrets
     */
    secrets: {
        [key: string]: SecretStateOut;
    };
    /**
     * Fields
     */
    fields: Array<FieldOut>;
    /**
     * Read Only
     *
     * Адреса вебхуков, redirect URI и т. п.
     */
    read_only: {
        [key: string]: string;
    };
    /**
     * Read Only Fields
     */
    read_only_fields: Array<ReadOnlyFieldOut>;
    /**
     * Last Check At
     */
    last_check_at: string | null;
    last_check_result: CheckResultOut | null;
    /**
     * Last Success At
     */
    last_success_at: string | null;
    /**
     * Last Error At
     */
    last_error_at: string | null;
    /**
     * Last Error
     */
    last_error: string | null;
    metrics: MetricsOut;
    /**
     * Recent Errors
     */
    recent_errors: Array<LogBriefOut>;
    /**
     * Alerts
     */
    alerts: Array<AlertOut>;
    /**
     * Warnings
     */
    warnings: Array<string>;
    /**
     * Actions
     */
    actions: Array<ActionOut>;
    /**
     * Degradation
     *
     * Текст подтверждения «Выключить» (14.10.1)
     */
    degradation: string;
    /**
     * History
     */
    history: Array<AppIntegrationsSchemasHistoryItemOut>;
    mail?: MailTilesOut | null;
    push?: PushTilesOut | null;
    payments?: PaymentTilesOut | null;
    store?: StoreTilesOut | null;
    recognition?: RecognitionTilesOut | null;
    translation?: TranslationTilesOut | null;
    video?: VideoTilesOut | null;
    partner?: IntPartnerCardOut | null;
    shipping?: IntShippingCardOut | null;
    reference?: IntReferenceCardOut | null;
    can: IntegrationCan;
    /**
     * Проверка подключения после сохранения (если интеграция включена)
     */
    check_result: CheckResultOut | null;
};

/**
 * IntegrationStatus
 *
 * Вычисляемый статус (14.6.1, 17.15.1) — в БД не хранится.
 */
export const IntegrationStatus = {
    NotConfigured: 'not_configured',
    Connected: 'connected',
    Error: 'error',
    Disabled: 'disabled'
} as const;

/**
 * IntegrationStatus
 *
 * Вычисляемый статус (14.6.1, 17.15.1) — в БД не хранится.
 */
export type IntegrationStatus = typeof IntegrationStatus[keyof typeof IntegrationStatus];

/**
 * IntegrationUpdateIn
 */
export type IntegrationUpdateIn = {
    /**
     * test | prod; не передан — без изменений
     */
    mode?: IntegrationMode | null;
    /**
     * Settings
     *
     * Несекретные поля; не переданные — без изменений, `null` — очистить
     */
    settings?: {
        [key: string]: unknown;
    };
    /**
     * Secrets
     *
     * Секреты: новое значение заменяет, `null` очищает, отсутствующее поле — без изменений (14.11)
     */
    secrets?: {
        [key: string]: string | null;
    };
};

/**
 * IntegrationsPage
 */
export type IntegrationsPage = {
    /**
     * Items
     */
    items: Array<IntegrationRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
};

/**
 * IntroCourseOut
 */
export type IntroCourseOut = {
    course: CourseInfoOut;
    author: AuthorBriefOut;
    progress: ProgressOut;
    /**
     * Label
     *
     * «Бесплатно · 17 уроков · 850 XP»
     */
    label: string;
    cta: CtaOut;
    /**
     * Sections
     */
    sections: Array<IntroSectionOut>;
    reactions: CourseLikeOut;
};

/**
 * IntroLessonItemOut
 */
export type IntroLessonItemOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Number
     */
    number: string;
    /**
     * Title
     */
    title: string;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * State
     */
    state: 'done' | 'next' | 'open';
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Likes
     */
    likes: number;
    /**
     * Set Label
     */
    set_label: string | null;
    /**
     * Target
     */
    target: string;
};

/**
 * IntroSectionOut
 */
export type IntroSectionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Title
     */
    title: string;
    /**
     * Result Text
     */
    result_text: string | null;
    /**
     * Achievement Title
     */
    achievement_title: string | null;
    /**
     * Done
     */
    done: number;
    /**
     * All
     */
    all: number;
    /**
     * Pct
     */
    pct: number;
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Completed
     */
    completed: boolean;
    /**
     * Label
     */
    label: string;
    /**
     * Target
     */
    target: string;
    /**
     * Lessons
     */
    lessons: Array<IntroLessonItemOut>;
};

/**
 * ItemType
 *
 * Объект права доступа (8.3.20) и предложения цены.
 */
export const ItemType = { Course: 'course', Lesson: 'lesson' } as const;

/**
 * ItemType
 *
 * Объект права доступа (8.3.20) и предложения цены.
 */
export type ItemType = typeof ItemType[keyof typeof ItemType];

/**
 * KitBottleOut
 */
export type KitBottleOut = {
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Position
     */
    position: number;
    price: Money;
    /**
     * Is This Broadcast
     */
    is_this_broadcast?: boolean;
};

/**
 * KitOut
 *
 * Набор к эфиру вслепую (CAT, BR-EXP-25).
 */
export type KitOut = {
    /**
     * Set Id
     */
    set_id: string;
    /**
     * Title
     */
    title: string;
    price: Money;
    /**
     * Bottles
     */
    bottles: Array<KitBottleOut>;
    saving: Money | null;
    /**
     * Orderable
     */
    orderable: boolean;
    /**
     * Closed Label
     */
    closed_label?: string | null;
};

/**
 * LadderTierOut
 */
export type LadderTierOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Title
     *
     * «III Первооткрыватель»
     */
    title: string;
    /**
     * Xp Min
     */
    xp_min: number;
    /**
     * Accuracy Min
     */
    accuracy_min: number;
    /**
     * Unlocks
     */
    unlocks: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Xp Label
     *
     * «3 000 XP», у первой — «старт»
     */
    xp_label: string;
    /**
     * Accuracy Label
     *
     * «точность 35», у первой — «без условий»
     */
    accuracy_label: string;
    /**
     * Reached
     */
    reached: boolean;
};

/**
 * Language
 */
export const Language = {
    Ru: 'ru',
    En: 'en',
    Es: 'es'
} as const;

/**
 * Language
 */
export type Language = typeof Language[keyof typeof Language];

/**
 * LearnArchiveIn
 */
export type LearnArchiveIn = {
    reason: LearnArchiveReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * LearnArchiveReason
 */
export const LearnArchiveReason = {
    NotRelevant: 'not_relevant',
    Duplicate: 'duplicate',
    PartnerTerminated: 'partner_terminated',
    DataError: 'data_error'
} as const;

/**
 * LearnArchiveReason
 */
export type LearnArchiveReason = typeof LearnArchiveReason[keyof typeof LearnArchiveReason];

/**
 * LearnHistoryItemOut
 */
export type LearnHistoryItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * At
     */
    at: string;
    /**
     * Actor Name
     */
    actor_name: string;
    /**
     * Text
     */
    text: string;
};

/**
 * LearnHistoryPageOut
 */
export type LearnHistoryPageOut = {
    /**
     * Items
     */
    items: Array<LearnHistoryItemOut>;
    /**
     * Total
     */
    total: number;
};

/**
 * LearnLogRowOut
 */
export type LearnLogRowOut = {
    /**
     * Id
     *
     * Ключ строки: субъект × урок
     */
    id: string;
    lesson: LearnRefOut;
    course: LearnRefOut | null;
    subject: LogSubjectOut;
    /**
     * At
     */
    at: string;
    /**
     * Test Passed
     *
     * null — у урока нет теста («—»)
     */
    test_passed: boolean | null;
    /**
     * Test Label
     */
    test_label: string;
};

/**
 * LearnMediaIn
 */
export type LearnMediaIn = {
    /**
     * Items
     *
     * Порядок — порядок показа; первый элемент — обложка
     */
    items: Array<MediaItemIn>;
};

/**
 * LearnMediaOut
 */
export type LearnMediaOut = {
    /**
     * Id
     */
    id: string;
    placement: LearnMediaPlacement;
    /**
     * Role Label
     */
    role_label: string;
    /**
     * File Id
     */
    file_id: string | null;
    /**
     * Source Url
     */
    source_url: string | null;
    /**
     * Url
     */
    url: string | null;
    caption: LocalizedText | null;
};

/**
 * LearnMediaPlacement
 */
export const LearnMediaPlacement = { Cover: 'cover', Gallery: 'gallery' } as const;

/**
 * LearnMediaPlacement
 */
export type LearnMediaPlacement = typeof LearnMediaPlacement[keyof typeof LearnMediaPlacement];

/**
 * LearnOfferOut
 */
export type LearnOfferOut = {
    /**
     * Item
     *
     * `course:{id}` / `lesson:{id}`
     */
    item: string;
    /**
     * Owned
     */
    owned: boolean;
    /**
     * In Cart
     */
    in_cart: boolean;
    /**
     * Purchasable
     *
     * false — покупка этого объекта отдельно недоступна
     */
    purchasable: boolean;
    price?: Money | null;
    discount?: DiscountOut | null;
    /**
     * Зачёт купленных уроков (gateway)
     */
    credit?: Money | null;
    to_pay?: Money | null;
    lessons_separately?: Money | null;
    /**
     * Выгода против покупки по одному
     */
    savings?: Money | null;
    /**
     * Канал store: справочно
     */
    reference_price?: Money | null;
    /**
     * Button
     */
    button?: string | null;
    /**
     * Note
     */
    note?: string | null;
};

/**
 * LearnOffersOut
 */
export type LearnOffersOut = {
    /**
     * Channel
     */
    channel: 'gateway' | 'store';
    /**
     * Store
     *
     * `billing.digital` платформы (store)
     */
    store?: string | null;
    /**
     * Items
     */
    items: Array<LearnOfferOut>;
};

/**
 * LearnPage[CourseRowOut]
 */
export type LearnPageCourseRowOut = {
    /**
     * Items
     */
    items: Array<CourseRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои» (8.2.2)
     */
    notice?: string | null;
};

/**
 * LearnPage[LearnLogRowOut]
 */
export type LearnPageLearnLogRowOut = {
    /**
     * Items
     */
    items: Array<LearnLogRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои» (8.2.2)
     */
    notice?: string | null;
};

/**
 * LearnPage[LessonRowOut]
 */
export type LearnPageLessonRowOut = {
    /**
     * Items
     */
    items: Array<LessonRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои» (8.2.2)
     */
    notice?: string | null;
};

/**
 * LearnPage[TestRowOut]
 */
export type LearnPageTestRowOut = {
    /**
     * Items
     */
    items: Array<TestRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер правила «только свои» (8.2.2)
     */
    notice?: string | null;
};

/**
 * LearnPriceIn
 */
export type LearnPriceIn = {
    /**
     * Amount
     *
     * Минимальные единицы (копейки / центы), > 0
     */
    amount: number;
    currency: Currency;
};

/**
 * LearnRefOut
 */
export type LearnRefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * LearnRevisionOut
 */
export type LearnRevisionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Version
     */
    version: number;
    state: LearnRevisionState;
    /**
     * Is Edit
     *
     * Правки опубликованной записи
     */
    is_edit: boolean;
    decision: Decision | null;
    /**
     * Submitted At
     */
    submitted_at: string | null;
    /**
     * Decided At
     */
    decided_at: string | null;
    /**
     * Note
     */
    note: string | null;
};

/**
 * LearnRevisionState
 */
export const LearnRevisionState = {
    Submitted: 'submitted',
    Published: 'published',
    Superseded: 'superseded',
    Rejected: 'rejected'
} as const;

/**
 * LearnRevisionState
 */
export type LearnRevisionState = typeof LearnRevisionState[keyof typeof LearnRevisionState];

/**
 * LearnStatusOut
 */
export type LearnStatusOut = {
    status: ContentStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Плашка с учётом правок на модерации
     */
    display_status: ContentStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Ревизия, созданная действием
     */
    revision: LearnRevisionOut | null;
};

/**
 * LearnUsageItemOut
 */
export type LearnUsageItemOut = {
    /**
     * Kind
     */
    kind: string;
    /**
     * Label
     */
    label: string;
    /**
     * Count
     */
    count?: number | null;
};

/**
 * LearningDiscountOut
 */
export type LearningDiscountOut = {
    /**
     * Pct
     */
    pct: number;
    /**
     * Applies On Platform
     *
     * Скидка действует на платформе запроса (D3)
     */
    applies_on_platform: boolean;
    /**
     * Visible
     */
    visible: boolean;
};

/**
 * LedgerItemOut
 */
export type LedgerItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Amount
     *
     * > 0 — начисление, < 0 — сторно
     */
    amount: number;
    /**
     * Kind
     */
    kind: 'accrual' | 'reversal';
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Date Label
     *
     * «Сегодня», «Вчера», «14 марта», «14 марта 2025»
     */
    date_label: string;
    /**
     * Link
     *
     * Ключ экрана объекта-основания
     */
    link: string | null;
    /**
     * Event Type
     */
    event_type: string;
};

/**
 * LedgerObjectOut
 */
export type LedgerObjectOut = {
    /**
     * Type
     *
     * lesson | test | diary_entry | order | booking | region
     */
    type: string;
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string | null;
};

/**
 * LedgerPageOut
 */
export type LedgerPageOut = {
    /**
     * Items
     */
    items: Array<LedgerItemOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Total
     */
    total: number;
    /**
     * Subtitle
     *
     * «За что и когда · N начислений» / пустой журнал
     */
    subtitle: string;
};

/**
 * LegalAcceptIn
 */
export type LegalAcceptIn = {
    /**
     * Version Ids
     */
    version_ids: Array<string>;
};

/**
 * LegalDocumentOut
 */
export type LegalDocumentOut = {
    doc_key: DocKey;
    /**
     * Name
     */
    name: string;
    /**
     * Lang
     */
    lang: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Version
     */
    version: string;
    /**
     * Effective From
     */
    effective_from: string;
    /**
     * Url
     *
     * Публичная ссылка `{legal.documents_base_url}{doc_key}?lang=`
     */
    url: string;
    /**
     * File Url
     *
     * Файл или ссылка действующей версии
     */
    file_url: string | null;
    /**
     * Acceptance Required
     *
     * Пользователь не принял действующую версию (BR-ACC-10); гостю — null
     */
    acceptance_required: boolean | null;
};

/**
 * LessonAccessOut
 */
export type LessonAccessOut = {
    /**
     * Open
     */
    open: boolean;
    /**
     * Reason
     */
    reason: 'free' | 'purchased' | 'course' | 'paid';
};

/**
 * LessonCan
 */
export type LessonCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Submit
     */
    submit?: boolean;
    /**
     * Submit Edits
     */
    submit_edits?: boolean;
    /**
     * Waiting
     *
     * Эксперт: неактивная кнопка «Ждёт решения администратора»
     */
    waiting?: boolean;
    /**
     * Decide
     */
    decide?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Edit Price
     */
    edit_price?: boolean;
    /**
     * Edit Store Products
     */
    edit_store_products?: boolean;
    /**
     * Edit Tests
     */
    edit_tests?: boolean;
};

/**
 * LessonCompletedOut
 */
export type LessonCompletedOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    course_progress: CourseProgressBriefOut | null;
    section_completed: SectionCompletedOut | null;
    win: WinOut | null;
};

/**
 * LessonCourseOut
 */
export type LessonCourseOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    kind: CourseKind;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Can Edit
     */
    can_edit: boolean;
};

/**
 * LessonCourseRefOut
 */
export type LessonCourseRefOut = {
    /**
     * Id
     */
    id: string;
    kind: CourseKind;
    /**
     * Title
     */
    title: string;
    /**
     * Region Id
     */
    region_id: string | null;
};

/**
 * LessonCreateIn
 */
export type LessonCreateIn = {
    title: LocalizedText;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Is Paid
     */
    is_paid?: boolean;
    price?: LearnPriceIn | null;
    /**
     * Текст урока: абзацы через пустую строку, «## » подзаголовок, «> » цитата, «- » список, «![подпись](https://…)» изображение, ссылки «[текст](https://…)»
     */
    body: LocalizedText;
    /**
     * Theses
     */
    theses?: Array<LocalizedText>;
    /**
     * Course Ids
     */
    course_ids?: Array<string>;
    related?: RelatedIn | null;
    /**
     * Media
     */
    media?: Array<MediaItemIn>;
    /**
     * Store Products
     */
    store_products?: {
        [key: string]: StoreProductIn | null;
    } | null;
};

/**
 * LessonNavItemOut
 */
export type LessonNavItemOut = {
    /**
     * Number
     */
    number: string;
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * State
     */
    state: 'done' | 'current' | 'open' | 'locked';
};

/**
 * LessonOptionOut
 */
export type LessonOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Course Id
     */
    course_id: string | null;
    /**
     * Is Intro
     */
    is_intro: boolean;
};

/**
 * LessonOut
 */
export type LessonOut = {
    /**
     * Id
     */
    id: string;
    title: LocalizedText | null;
    region: LearnRefOut | null;
    author: AuthorOut;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Is Paid
     */
    is_paid: boolean;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * In Intro
     */
    in_intro: boolean;
    store_products: StoreProductsOut;
    body: LocalizedText | null;
    /**
     * Theses
     */
    theses: Array<LocalizedText>;
    /**
     * Media
     */
    media: Array<LearnMediaOut>;
    related: RelatedOut;
    /**
     * Tests
     */
    tests: Array<LessonTestOut>;
    /**
     * Test Rules Label
     */
    test_rules_label: string;
    /**
     * Courses
     */
    courses: Array<LessonCourseOut>;
    /**
     * Courses Hint
     */
    courses_hint: string | null;
    video: VideoOut;
    /**
     * Moderation Note
     */
    moderation_note: string | null;
    status: ContentStatus;
    /**
     * Status Label
     */
    status_label: string;
    display_status: ContentStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    published_revision: LearnRevisionOut | null;
    pending_revision: LearnRevisionOut | null;
    /**
     * Has Unsubmitted Changes
     */
    has_unsubmitted_changes: boolean;
    /**
     * Edits Hint
     */
    edits_hint: string | null;
    /**
     * First Published At
     */
    first_published_at: string | null;
    archive_reason: LearnArchiveReason | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Usages
     */
    usages: Array<LearnUsageItemOut>;
    can: LessonCan;
    /**
     * Message
     */
    message?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * LessonPatchIn
 */
export type LessonPatchIn = {
    title?: LocalizedText | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Duration Min
     */
    duration_min?: number | null;
    /**
     * Is Paid
     */
    is_paid?: boolean | null;
    price?: LearnPriceIn | null;
    body?: LocalizedText | null;
    /**
     * Theses
     */
    theses?: Array<LocalizedText> | null;
    /**
     * Course Ids
     */
    course_ids?: Array<string> | null;
    related?: RelatedIn | null;
    /**
     * Store Products
     */
    store_products?: {
        [key: string]: StoreProductIn | null;
    } | null;
};

/**
 * LessonRowOut
 */
export type LessonRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Region Id
     */
    region_id: string | null;
    /**
     * Region Label
     */
    region_label: string;
    /**
     * Cost Label
     */
    cost_label: string;
    /**
     * Is Paid
     */
    is_paid: boolean;
    price: Money | null;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Duration Label
     */
    duration_label: string;
    /**
     * Courses Count
     */
    courses_count: number;
    /**
     * Courses Label
     */
    courses_label: string;
    /**
     * Tests Count
     */
    tests_count: number;
    /**
     * In Intro
     */
    in_intro: boolean;
    status: ContentStatus;
    display_status: ContentStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * LessonScreenOut
 */
export type LessonScreenOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    course: LessonCourseRefOut | null;
    /**
     * Number
     */
    number: string;
    /**
     * Index
     */
    index: number;
    /**
     * Count
     */
    count: number;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Is Paid
     */
    is_paid: boolean;
    access: LessonAccessOut;
    /**
     * Completed
     */
    completed: boolean;
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
    video: LessonVideoOut;
    /**
     * Body
     */
    body?: Array<BlockOut> | null;
    /**
     * Theses
     */
    theses?: Array<string> | null;
    /**
     * Tests
     */
    tests?: Array<LessonTestItemOut> | null;
    /**
     * Completion Mode
     */
    completion_mode: 'button' | 'test';
    /**
     * Nav
     */
    nav: Array<LessonNavItemOut>;
    next: NextLessonOut | null;
    /**
     * Last Text
     */
    last_text: string | null;
    reactions: ReactionsOut;
    /**
     * Header
     */
    header: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Completed Label
     */
    completed_label: string | null;
    offer?: LearnOfferOut | null;
    course_offer?: LearnOfferOut | null;
    paywall?: PaywallOut | null;
    /**
     * Lesson Set
     *
     * «Сет к уроку» вводного курса — карточка сета CAT
     */
    lesson_set?: {
        [key: string]: unknown;
    } | null;
    cta: CtaOut;
    secondary: LinkOut | null;
};

/**
 * LessonStatsOut
 */
export type LessonStatsOut = {
    /**
     * Sold
     *
     * Доступы к уроку, выданные покупкой урока поштучно
     */
    sold: number;
    sales_sum: Money;
    /**
     * Completions
     */
    completions: number;
    test_pass: TestPassOut;
    /**
     * Likes
     */
    likes: number;
    /**
     * Courses
     */
    courses: Array<LearnRefOut>;
};

/**
 * LessonTestIn
 */
export type LessonTestIn = {
    /**
     * Test Id
     */
    test_id: string;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Order
     */
    order?: number | null;
};

/**
 * LessonTestItemOut
 */
export type LessonTestItemOut = {
    /**
     * Test Id
     */
    test_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Required
     */
    required: boolean;
    /**
     * Questions
     */
    questions: number;
    /**
     * Pass From
     */
    pass_from: number;
    /**
     * State
     */
    state: 'not_started' | 'passed' | 'perfect' | 'failed';
    /**
     * Label
     */
    label: string;
    /**
     * State Label
     */
    state_label: string;
    /**
     * Target
     */
    target: string;
};

/**
 * LessonTestOut
 */
export type LessonTestOut = {
    /**
     * Test Id
     */
    test_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Is Required
     */
    is_required: boolean;
    /**
     * Mode Label
     */
    mode_label: string;
    /**
     * Position
     */
    position: number;
    /**
     * Questions
     */
    questions: number;
    status: ContentStatus;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * LessonTestsOut
 */
export type LessonTestsOut = {
    /**
     * Items
     */
    items: Array<LessonTestOut>;
};

/**
 * LessonVideoOut
 */
export type LessonVideoOut = {
    /**
     * Has Video
     */
    has_video: boolean;
    /**
     * Status
     */
    status: string | null;
    /**
     * Duration Sec
     */
    duration_sec: number | null;
    /**
     * Thumbnail Url
     */
    thumbnail_url: string | null;
    /**
     * Position Sec
     */
    position_sec?: number | null;
};

/**
 * LevelBarOut
 */
export type LevelBarOut = {
    /**
     * Overline
     */
    overline: string;
    /**
     * Tiles
     */
    tiles: Array<LevelTileOut>;
    /**
     * Scale Max
     */
    scale_max: number | null;
    /**
     * Scale Marks
     */
    scale_marks: Array<number>;
    /**
     * Next Perk
     */
    next_perk: string | null;
};

/**
 * LevelCheckResultOut
 */
export type LevelCheckResultOut = {
    /**
     * Score
     */
    score: number;
    /**
     * Total
     */
    total: number;
    /**
     * Band
     */
    band: 'low' | 'mid' | 'high';
    /**
     * Perfect Ever
     */
    perfect_ever: boolean;
    /**
     * Recommendations
     */
    recommendations: Array<RecommendationOut>;
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    primary: LinkOut;
    secondary: LinkOut;
};

/**
 * LevelOut
 */
export type LevelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Index
     *
     * 0 — начальный уровень, далее 1…K
     */
    index: number;
    /**
     * Name
     */
    name: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Min Xp
     */
    min_xp: number;
    /**
     * Min Xp Label
     */
    min_xp_label: string;
    /**
     * Perks
     *
     * Пункты «Что даёт»
     */
    perks: Array<string>;
    /**
     * Learning Pct
     */
    learning_pct: number;
    /**
     * Wine Pct
     */
    wine_pct: number;
    /**
     * Learning Label
     *
     * «−5 %» или «нет»
     */
    learning_label: string;
    /**
     * Wine Label
     */
    wine_label: string;
    /**
     * Bonuses Label
     *
     * «1 / 1 000» у уровня с бонусами, иначе «—»
     */
    bonuses_label: string;
    one_time_reward: OneTimeRewardOut;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * State Label
     *
     * «уровень открыт» / «ещё N XP» (для пользователя)
     */
    state_label?: string | null;
};

/**
 * LevelTileOut
 */
export type LevelTileOut = {
    /**
     * Key
     */
    key: 'lessons' | 'wine' | 'bonuses';
    /**
     * Title
     */
    title: string;
    /**
     * Value
     */
    value: string;
    /**
     * Locked
     */
    locked: boolean;
};

/**
 * LibraryItemOut
 */
export type LibraryItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Type
     */
    type: 'lesson' | 'test';
    /**
     * Title
     */
    title: string;
    status: ContentStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Author
     */
    author: string;
    /**
     * Duration Min
     */
    duration_min?: number | null;
    /**
     * Is Paid
     */
    is_paid?: boolean | null;
    price?: Money | null;
    kind?: TestKind | null;
    /**
     * Kind Label
     */
    kind_label?: string | null;
    /**
     * Questions
     */
    questions?: number | null;
    /**
     * Used In
     */
    used_in: number;
    /**
     * Used In Label
     */
    used_in_label: string;
};

/**
 * LibraryOut
 */
export type LibraryOut = {
    /**
     * Items
     */
    items: Array<LibraryItemOut>;
};

/**
 * LikeIn
 */
export type LikeIn = {
    object_type: LikeObjectType;
    /**
     * Object Id
     */
    object_id: string;
};

/**
 * LikeLogRowOut
 */
export type LikeLogRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * User Id
     */
    user_id: string | null;
    /**
     * User Name
     */
    user_name: string;
    /**
     * Object Type
     */
    object_type: string;
    /**
     * Object Type Label
     */
    object_type_label: string;
    /**
     * Object Id
     */
    object_id: string;
    /**
     * Object Title
     */
    object_title: string;
    /**
     * At
     */
    at: string;
};

/**
 * LikeObjectType
 */
export const LikeObjectType = { Wine: 'wine', Set: 'set' } as const;

/**
 * LikeObjectType
 */
export type LikeObjectType = typeof LikeObjectType[keyof typeof LikeObjectType];

/**
 * LikeOut
 */
export type LikeOut = {
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Likes Count
     */
    likes_count: number;
};

/**
 * LikeStateOut
 */
export type LikeStateOut = {
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Likes Count
     */
    likes_count: number;
};

/**
 * LikedItemOut
 */
export type LikedItemOut = {
    object_type: LikeObjectType;
    /**
     * Object Id
     */
    object_id: string;
    /**
     * Liked At
     */
    liked_at: string;
    wine: WineListItem | null;
    set: SetListItem | null;
};

/**
 * LikedPageOut
 */
export type LikedPageOut = {
    /**
     * Items
     */
    items: Array<LikedItemOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * LikesOut
 */
export type LikesOut = {
    /**
     * Total
     */
    total: number;
    /**
     * Last 30 Days
     */
    last_30_days: number;
};

/**
 * LineOut
 */
export type LineOut = {
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * LinkAttachIn
 */
export type LinkAttachIn = {
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Confirm Move
     */
    confirm_move?: boolean;
};

/**
 * LinkCandidateListOut
 */
export type LinkCandidateListOut = {
    /**
     * Tab
     */
    tab: string;
    /**
     * Items
     */
    items: Array<LinkCandidateOut>;
};

/**
 * LinkCandidateOut
 */
export type LinkCandidateOut = {
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Текущий регион (вкладки региона) или винодельня (вкладки винодельни)
     */
    current_region: CurrentOwnerOut | null;
};

/**
 * LinkListOut
 */
export type LinkListOut = {
    /**
     * Tab
     */
    tab: string;
    /**
     * Items
     */
    items: Array<AppModulesGeoSchemasAdminLinkItemOut>;
};

/**
 * LinkOptionOut
 */
export type LinkOptionOut = {
    target_type: StoryLinkTarget;
    /**
     * Target Id
     */
    target_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Own
     */
    own: boolean;
};

/**
 * LinkOut
 */
export type LinkOut = {
    /**
     * Label
     */
    label: string;
    /**
     * Target
     *
     * Ключ экрана приложения: `path-intro`, `path-region:{rid}`, …
     */
    target: string;
};

/**
 * LinkSource
 *
 * Метка связи с регионом (BR-GEO-52): по полю сущности или ручная привязка.
 */
export const LinkSource = { Auto: 'auto', Manual: 'manual' } as const;

/**
 * LinkSource
 *
 * Метка связи с регионом (BR-GEO-52): по полю сущности или ручная привязка.
 */
export type LinkSource = typeof LinkSource[keyof typeof LinkSource];

/**
 * LinkedIdentityOut
 */
export type LinkedIdentityOut = {
    /**
     * Provider
     */
    provider: string;
    /**
     * Provider Label
     */
    provider_label: string;
    /**
     * Linked At
     */
    linked_at: string;
    /**
     * Last Used At
     */
    last_used_at: string | null;
};

/**
 * ListCan
 *
 * Права на уровне реестра (fail-closed: по умолчанию всё запрещено).
 */
export type ListCan = {
    /**
     * Create
     */
    create?: boolean;
    /**
     * Export
     */
    export?: boolean;
};

/**
 * LocalizedText
 *
 * Текст на языках инсталляции (COM-09).
 *
 * RU: ``{"ru": "..."}``; World: ``{"en", "es", "source_lang", "translation_status"}``.
 * Полноту по языкам рынка проверяет `require_languages` (MarketConfig, модули при записи).
 * При сериализации (ответы админки, JSONB) объект содержит ровно ключи текущего рынка —
 * `localized_keys()`; схема OpenAPI описывает все поля как необязательные.
 */
export type LocalizedText = {
    /**
     * Ru
     */
    ru?: string | null;
    /**
     * En
     */
    en?: string | null;
    /**
     * Es
     */
    es?: string | null;
    source_lang?: Language | null;
    translation_status?: TranslationStatus | null;
};

/**
 * LogActorType
 */
export const LogActorType = {
    User: 'user',
    Admin: 'admin',
    System: 'system',
    Provider: 'provider',
    Partner: 'partner'
} as const;

/**
 * LogActorType
 */
export type LogActorType = typeof LogActorType[keyof typeof LogActorType];

/**
 * LogBriefOut
 */
export type LogBriefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Requested At
     */
    requested_at: string;
    /**
     * Operation
     */
    operation: string;
    result: LogResult;
    /**
     * Result Label
     */
    result_label: string;
    /**
     * Error Code
     */
    error_code: string | null;
    /**
     * Error Message
     */
    error_message: string | null;
};

/**
 * LogCard
 */
export type LogCard = {
    /**
     * Id
     */
    id: string;
    /**
     * Integration Key
     */
    integration_key: string;
    /**
     * Integration Title
     */
    integration_title: string;
    /**
     * Provider
     */
    provider: string;
    environment: IntegrationMode;
    direction: LogDirection;
    /**
     * Direction Label
     */
    direction_label: string;
    /**
     * Operation
     */
    operation: string;
    /**
     * Provider Method
     */
    provider_method: string | null;
    /**
     * Http Method
     */
    http_method: string | null;
    /**
     * Response Status
     */
    response_status: number | null;
    result: LogResult;
    /**
     * Result Label
     */
    result_label: string;
    /**
     * Error Code
     */
    error_code: string | null;
    category: LogCategory;
    /**
     * Requested At
     */
    requested_at: string;
    /**
     * Responded At
     */
    responded_at: string | null;
    /**
     * Duration Ms
     */
    duration_ms: number | null;
    /**
     * Attempt
     */
    attempt: number;
    /**
     * Entity Type
     */
    entity_type: string | null;
    /**
     * Entity Id
     */
    entity_id: string | null;
    /**
     * Entity Title
     */
    entity_title: string | null;
    /**
     * Trace Id
     */
    trace_id: string;
    /**
     * Url
     */
    url: string | null;
    /**
     * Request Headers
     */
    request_headers: {
        [key: string]: unknown;
    } | null;
    /**
     * Request Body
     */
    request_body: string | null;
    /**
     * Response Headers
     */
    response_headers: {
        [key: string]: unknown;
    } | null;
    /**
     * Response Body
     */
    response_body: string | null;
    /**
     * Error Message
     */
    error_message: string | null;
    /**
     * Idempotency Key
     */
    idempotency_key: string | null;
    /**
     * Correlation
     */
    correlation: {
        [key: string]: unknown;
    } | null;
    actor_type: LogActorType;
    /**
     * Actor Id
     */
    actor_id: string | null;
};

/**
 * LogCategory
 */
export const LogCategory = { Financial: 'financial', Technical: 'technical' } as const;

/**
 * LogCategory
 */
export type LogCategory = typeof LogCategory[keyof typeof LogCategory];

/**
 * LogDirection
 */
export const LogDirection = {
    Outbound: 'outbound',
    Inbound: 'inbound',
    Internal: 'internal',
    Job: 'job'
} as const;

/**
 * LogDirection
 */
export type LogDirection = typeof LogDirection[keyof typeof LogDirection];

/**
 * LogResult
 */
export const LogResult = {
    Success: 'success',
    ProviderError: 'provider_error',
    ClientError: 'client_error',
    ServerError: 'server_error',
    Timeout: 'timeout',
    NetworkError: 'network_error',
    Rejected: 'rejected',
    Duplicate: 'duplicate',
    Skipped: 'skipped'
} as const;

/**
 * LogResult
 */
export type LogResult = typeof LogResult[keyof typeof LogResult];

/**
 * LogRow
 */
export type LogRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Integration Key
     */
    integration_key: string;
    /**
     * Integration Title
     */
    integration_title: string;
    /**
     * Provider
     */
    provider: string;
    environment: IntegrationMode;
    direction: LogDirection;
    /**
     * Direction Label
     */
    direction_label: string;
    /**
     * Operation
     */
    operation: string;
    /**
     * Provider Method
     */
    provider_method: string | null;
    /**
     * Http Method
     */
    http_method: string | null;
    /**
     * Response Status
     */
    response_status: number | null;
    result: LogResult;
    /**
     * Result Label
     */
    result_label: string;
    /**
     * Error Code
     */
    error_code: string | null;
    category: LogCategory;
    /**
     * Requested At
     */
    requested_at: string;
    /**
     * Responded At
     */
    responded_at: string | null;
    /**
     * Duration Ms
     */
    duration_ms: number | null;
    /**
     * Attempt
     */
    attempt: number;
    /**
     * Entity Type
     */
    entity_type: string | null;
    /**
     * Entity Id
     */
    entity_id: string | null;
    /**
     * Entity Title
     */
    entity_title: string | null;
    /**
     * Trace Id
     */
    trace_id: string;
};

/**
 * LogSubjectOut
 */
export type LogSubjectOut = {
    /**
     * User Id
     */
    user_id: string | null;
    /**
     * Name
     */
    name: string;
    /**
     * Is Guest
     */
    is_guest: boolean;
};

/**
 * LogsPage
 */
export type LogsPage = {
    /**
     * Items
     */
    items: Array<LogRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
};

/**
 * MailTilesOut
 *
 * Плитки карточки почтовой интеграции (17.9.8).
 */
export type MailTilesOut = {
    /**
     * Sent 24H
     */
    sent_24h: number;
    /**
     * Daily Limit
     */
    daily_limit: number;
    /**
     * Queued
     */
    queued: number;
    /**
     * Deferred
     */
    deferred: number;
    /**
     * Errors 24H
     */
    errors_24h: number;
};

/**
 * MapProvider
 */
export const MapProvider = { Yandex: 'yandex', Google: 'google' } as const;

/**
 * MapProvider
 */
export type MapProvider = typeof MapProvider[keyof typeof MapProvider];

/**
 * MapZoneChoiceIn
 *
 * OQ-GEO-01: существующая зона страны (`id`) или новое название (`new_name`).
 */
export type MapZoneChoiceIn = {
    /**
     * Id
     */
    id?: string | null;
    new_name?: LocalizedText | null;
};

/**
 * MapZoneListOut
 */
export type MapZoneListOut = {
    /**
     * Items
     */
    items: Array<MapZoneOut>;
};

/**
 * MapZoneOut
 */
export type MapZoneOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
    /**
     * Regions Count
     */
    regions_count: number;
};

/**
 * MapsClientOut
 */
export type MapsClientOut = {
    provider: MapProvider;
    /**
     * Js Api Key
     */
    js_api_key?: string | null;
    /**
     * Mapkit Key
     */
    mapkit_key?: string | null;
    /**
     * Google Android Key
     */
    google_android_key?: string | null;
    /**
     * Google Ios Key
     */
    google_ios_key?: string | null;
};

/**
 * MarketInfoRowOut
 */
export type MarketInfoRowOut = {
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * MarketingIn
 */
export type MarketingIn = {
    /**
     * Push
     */
    push?: boolean | null;
    /**
     * Email
     */
    email?: boolean | null;
};

/**
 * MarketingOut
 */
export type MarketingOut = {
    /**
     * Push
     */
    push: boolean;
    /**
     * Email
     */
    email: boolean;
    /**
     * Text Version
     *
     * Версия текста согласия (MarketingConsentLog)
     */
    text_version: string;
    /**
     * Text
     *
     * Текст согласия под переключателем «Подборки и новости»
     */
    text: string;
};

/**
 * MeLevelOut
 */
export type MeLevelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Index
     *
     * 0 — начальный уровень, далее 1…K
     */
    index: number;
    /**
     * Name
     */
    name: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Min Xp
     */
    min_xp: number;
    /**
     * Min Xp Label
     */
    min_xp_label: string;
    /**
     * Perks
     *
     * Пункты «Что даёт»
     */
    perks: Array<string>;
    /**
     * Learning Pct
     */
    learning_pct: number;
    /**
     * Wine Pct
     */
    wine_pct: number;
    /**
     * Learning Label
     *
     * «−5 %» или «нет»
     */
    learning_label: string;
    /**
     * Wine Label
     */
    wine_label: string;
    /**
     * Bonuses Label
     *
     * «1 / 1 000» у уровня с бонусами, иначе «—»
     */
    bonuses_label: string;
    one_time_reward: OneTimeRewardOut;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * State Label
     *
     * «уровень открыт» / «ещё N XP» (для пользователя)
     */
    state_label?: string | null;
    /**
     * Of
     *
     * K — число уровней без начального
     */
    of: number;
    /**
     * Held
     *
     * Уровень по XP выше, но удержан до вводного курса
     */
    held: boolean;
    /**
     * Overline
     *
     * «уровень i из K» / «стартовый уровень»
     */
    overline: string;
};

/**
 * MeLocationIn
 */
export type MeLocationIn = {
    /**
     * Country Code
     */
    country_code?: string | null;
    /**
     * City Name
     */
    city_name: string;
    /**
     * City Suggestion Id
     */
    city_suggestion_id?: string | null;
};

/**
 * MeOut
 *
 * Блок `me` для авторизованного пользователя (9.8.1).
 */
export type MeOut = {
    booking: BookingBrief | null;
    /**
     * Waitlisted
     */
    waitlisted: boolean;
    /**
     * Attended
     */
    attended?: boolean;
    gate?: GateOut | null;
};

/**
 * MePasswordIn
 */
export type MePasswordIn = {
    /**
     * Current Password
     */
    current_password?: string | null;
    /**
     * Code
     */
    code?: string | null;
    /**
     * New Password
     */
    new_password: string;
};

/**
 * MePatchIn
 *
 * `{full_name?, birth_date?, phone?, city_name?, city_suggestion_id?, locale?, expert_mode?}`;
 * отсутствующее поле не меняется, `null` у обязательных полей профиля — 422. `consents` —
 * согласия при дозаполнении аккаунта, созданного в «Команде» (BR-ACC-09, 4.6.4).
 */
export type MePatchIn = {
    /**
     * Full Name
     */
    full_name?: string | null;
    /**
     * Birth Date
     */
    birth_date?: string | null;
    phone?: PhoneIn | null;
    /**
     * City Name
     */
    city_name?: string | null;
    /**
     * City Suggestion Id
     */
    city_suggestion_id?: string | null;
    locale?: Language | null;
    /**
     * Expert Mode
     */
    expert_mode?: boolean | null;
    /**
     * Consents
     */
    consents?: Array<ConsentItemIn> | null;
};

/**
 * MeTierOut
 */
export type MeTierOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Title
     *
     * «III Первооткрыватель»
     */
    title: string;
    /**
     * Xp Min
     */
    xp_min: number;
    /**
     * Accuracy Min
     */
    accuracy_min: number;
    /**
     * Unlocks
     */
    unlocks: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Xp Label
     *
     * «3 000 XP», у первой — «старт»
     */
    xp_label: string;
    /**
     * Accuracy Label
     *
     * «точность 35», у первой — «без условий»
     */
    accuracy_label: string;
    /**
     * Reached At
     */
    reached_at: string | null;
};

/**
 * MediaAppOut
 */
export type MediaAppOut = {
    type: MediaType;
    placement: MediaPlacement;
    /**
     * Url
     */
    url: string;
    /**
     * Caption
     */
    caption: string | null;
};

/**
 * MediaItemIn
 */
export type MediaItemIn = {
    /**
     * File Id
     *
     * `upload_id` загрузки `lrn_image` (JPG/PNG/WebP/HEIC)
     */
    file_id?: string | null;
    /**
     * Source Url
     *
     * Ссылка https
     */
    source_url?: string | null;
    /**
     * Подпись, до 200
     */
    caption?: LocalizedText | null;
};

/**
 * MediaKind
 */
export const MediaKind = { Photo: 'photo', Video: 'video' } as const;

/**
 * MediaKind
 */
export type MediaKind = typeof MediaKind[keyof typeof MediaKind];

/**
 * MediaListOut
 */
export type MediaListOut = {
    /**
     * Items
     */
    items: Array<CatalogMediaOut>;
};

/**
 * MediaOut
 */
export type MediaOut = {
    /**
     * Id
     */
    id: string;
    slot: MediaSlot;
    /**
     * MediaItem.role; null — одиночное изображение
     */
    role: MediaRole | null;
    /**
     * File Id
     */
    file_id: string | null;
    /**
     * Url
     *
     * Файл (оригинал) или ссылка на видео
     */
    url: string | null;
    /**
     * Preview Url
     *
     * Производный размер 640 px (если готов)
     */
    preview_url: string | null;
    /**
     * Content Type
     */
    content_type: string | null;
    /**
     * Width
     */
    width: number | null;
    /**
     * Height
     */
    height: number | null;
    caption: LocalizedText | null;
    /**
     * Position
     */
    position: number;
};

/**
 * MediaPlacement
 */
export const MediaPlacement = { Cover: 'cover', Gallery: 'gallery' } as const;

/**
 * MediaPlacement
 */
export type MediaPlacement = typeof MediaPlacement[keyof typeof MediaPlacement];

/**
 * MediaRole
 *
 * MediaItem.role (5.3.1).
 */
export const MediaRole = {
    Cover: 'cover',
    Gallery: 'gallery',
    Video: 'video'
} as const;

/**
 * MediaRole
 *
 * MediaItem.role (5.3.1).
 */
export type MediaRole = typeof MediaRole[keyof typeof MediaRole];

/**
 * MediaSlot
 *
 * Место медиа (5.8.2 `slot`): MediaItem (cover/gallery/video) и одиночные изображения.
 */
export const MediaSlot = {
    Cover: 'cover',
    Gallery: 'gallery',
    Video: 'video',
    Illustration: 'illustration',
    StampBronze: 'stamp_bronze',
    StampGold: 'stamp_gold',
    Specialization: 'specialization'
} as const;

/**
 * MediaSlot
 *
 * Место медиа (5.8.2 `slot`): MediaItem (cover/gallery/video) и одиночные изображения.
 */
export type MediaSlot = typeof MediaSlot[keyof typeof MediaSlot];

/**
 * MediaType
 */
export const MediaType = { Photo: 'photo', Video: 'video' } as const;

/**
 * MediaType
 */
export type MediaType = typeof MediaType[keyof typeof MediaType];

/**
 * MemberBlockIn
 */
export type MemberBlockIn = {
    /**
     * Reason
     */
    reason?: string | null;
};

/**
 * MemberCan
 */
export type MemberCan = {
    /**
     * View
     */
    view?: boolean;
    /**
     * Update
     */
    update?: boolean;
    /**
     * Change Role
     */
    change_role?: boolean;
    /**
     * Reset Password
     */
    reset_password?: boolean;
    /**
     * Block
     */
    block?: boolean;
    /**
     * Unblock
     */
    unblock?: boolean;
    /**
     * Decide Application
     */
    decide_application?: boolean;
    /**
     * Edit Author Profile
     */
    edit_author_profile?: boolean;
};

/**
 * MemberCard
 */
export type MemberCard = {
    /**
     * Id
     */
    id: string;
    /**
     * User Id
     */
    user_id: string;
    /**
     * Full Name
     */
    full_name: string | null;
    role: TeamRole;
    /**
     * Role Label
     */
    role_label: string;
    status: TeamMemberStatus;
    /**
     * Status Label
     */
    status_label: string;
    source: TeamMemberSource;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone: string | null;
    /**
     * Registered At
     */
    registered_at: string;
    registered_by: AppModulesAccSchemasCommonNamedRef | null;
    /**
     * Last Login At
     */
    last_login_at: string | null;
    /**
     * App Enabled
     *
     * Есть аккаунт приложения — ссылка на карточку пользователя
     */
    app_enabled: boolean;
    user_status: UserStatus;
    /**
     * Winery Ids
     */
    winery_ids: Array<string>;
    /**
     * Wineries
     */
    wineries: Array<RefLabelOut>;
    /**
     * Region Ids
     */
    region_ids: Array<string>;
    /**
     * Regions
     */
    regions: Array<RefLabelOut>;
    /**
     * Linked Objects
     */
    linked_objects: string;
    /**
     * App Tier
     */
    app_tier: string | null;
    /**
     * Decision Comment
     */
    decision_comment: string | null;
    expert_application: ExpertApplicationOut | null;
    /**
     * Только для винодела и эксперта; `id = null` — профиль ещё не заполнен
     */
    author_profile: AuthorProfileOut | null;
    /**
     * History
     *
     * Последние действия участника по журналу аудита (до 20)
     */
    history: Array<AuditHistoryItemOut>;
    can: MemberCan;
};

/**
 * MemberCreateIn
 */
export type MemberCreateIn = {
    /**
     * Full Name
     */
    full_name: string;
    role?: TeamRole;
    /**
     * Email
     */
    email: string;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Winery Ids
     *
     * Только для винодела (GEO)
     */
    winery_ids?: Array<string>;
    /**
     * Region Ids
     *
     * Только для эксперта (GEO)
     */
    region_ids?: Array<string>;
};

/**
 * MemberCreatedOut
 */
export type MemberCreatedOut = {
    member: MemberRow;
    credentials: CredentialsOut;
};

/**
 * MemberPatchIn
 *
 * `{full_name?, role?, email?, phone?, winery_ids?, region_ids?}`. `phone: null` очищает
 * телефон; имя, роль и почта обязательны — `null` недопустим.
 */
export type MemberPatchIn = {
    /**
     * Full Name
     */
    full_name?: string | null;
    role?: TeamRole | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Winery Ids
     */
    winery_ids?: Array<string> | null;
    /**
     * Region Ids
     */
    region_ids?: Array<string> | null;
};

/**
 * MemberRow
 */
export type MemberRow = {
    /**
     * Id
     */
    id: string;
    /**
     * User Id
     */
    user_id: string;
    /**
     * Full Name
     */
    full_name: string | null;
    role: TeamRole;
    /**
     * Role Label
     */
    role_label: string;
    /**
     * Email
     */
    email: string;
    /**
     * Linked Objects
     *
     * Винодел — винодельни через « · »; эксперт — регионы и «{N} курсов»; заявитель — «Заявка из приложения · {город}»; администратор — «—»
     */
    linked_objects: string;
    /**
     * App Tier
     *
     * Ступень паспорта (GAM); null — нет аккаунта приложения или данных GAM
     */
    app_tier: string | null;
    /**
     * Last Login At
     *
     * null — «ещё не входил»
     */
    last_login_at: string | null;
    /**
     * Registered At
     */
    registered_at: string;
    status: TeamMemberStatus;
    /**
     * Status Label
     */
    status_label: string;
    source: TeamMemberSource;
    can: MemberCan;
};

/**
 * MessageAuthorKind
 */
export const MessageAuthorKind = { User: 'user', Support: 'support' } as const;

/**
 * MessageAuthorKind
 */
export type MessageAuthorKind = typeof MessageAuthorKind[keyof typeof MessageAuthorKind];

/**
 * MessageKind
 */
export const MessageKind = {
    Initial: 'initial',
    Addition: 'addition',
    Reply: 'reply'
} as const;

/**
 * MessageKind
 */
export type MessageKind = typeof MessageKind[keyof typeof MessageKind];

/**
 * MessageOut
 */
export type MessageOut = {
    /**
     * Id
     */
    id: string;
    author: MessageAuthorKind;
    kind: MessageKind;
    /**
     * Author Name
     */
    author_name: string | null;
    /**
     * Text
     */
    text: string;
    /**
     * Delivered Channels
     *
     * Куда ответ фактически доставлен: `app` — сразу (виден в заявке), `email`/`push` — после отправки SRV
     */
    delivered_channels: Array<string>;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Attachments
     */
    attachments: Array<AttachmentOut>;
};

/**
 * MetricsOut
 */
export type MetricsOut = {
    /**
     * Calls 24H
     */
    calls_24h: number;
    /**
     * Error Rate 24H
     *
     * Доля ошибок 0…1
     */
    error_rate_24h: number;
    /**
     * P50 Ms
     */
    p50_ms: number | null;
    /**
     * P95 Ms
     */
    p95_ms: number | null;
    /**
     * Calls 7D
     */
    calls_7d: number;
    /**
     * Error Rate 7D
     */
    error_rate_7d: number;
    /**
     * P50 Ms 7D
     */
    p50_ms_7d: number | null;
    /**
     * P95 Ms 7D
     */
    p95_ms_7d: number | null;
};

/**
 * MinNotice
 */
export const MinNotice = {
    D1: 'd1',
    D3: 'd3',
    D7: 'd7'
} as const;

/**
 * MinNotice
 */
export type MinNotice = typeof MinNotice[keyof typeof MinNotice];

/**
 * ModerationActionIn
 */
export type ModerationActionIn = {
    [key: string]: never;
};

/**
 * ModerationCountersOut
 */
export type ModerationCountersOut = {
    /**
     * Waiting
     *
     * Ждут решения — бейдж пункта меню
     */
    waiting: number;
    /**
     * Returned
     *
     * На доработке у авторов
     */
    returned: number;
    /**
     * Overdue
     *
     * Ждут дольше 48 часов
     */
    overdue: number;
    /**
     * Decided In Session
     *
     * Решено текущим администратором за сессию входа
     */
    decided_in_session: number;
    /**
     * By Group
     *
     * Ждут решения по группам
     */
    by_group: {
        [key: string]: number;
    };
    /**
     * Returned By Group
     *
     * На доработке по группам
     */
    returned_by_group: {
        [key: string]: number;
    };
};

/**
 * ModerationDecisionIn
 */
export type ModerationDecisionIn = {
    decision: ModerationDecisionKind;
    /**
     * Comment
     *
     * Обязателен для return, reject, request_info (BR-SRV-09)
     */
    comment?: string | null;
};

/**
 * ModerationDecisionKind
 *
 * Решение администратора (13.3.5).
 */
export const ModerationDecisionKind = {
    Publish: 'publish',
    Return: 'return',
    Reject: 'reject',
    ApproveApplication: 'approve_application',
    RequestInfo: 'request_info',
    AcceptDocument: 'accept_document',
    Unpublish: 'unpublish'
} as const;

/**
 * ModerationDecisionKind
 *
 * Решение администратора (13.3.5).
 */
export type ModerationDecisionKind = typeof ModerationDecisionKind[keyof typeof ModerationDecisionKind];

/**
 * ModerationDecisionOptionOut
 *
 * Кнопка модалки «Решение» (13.7.2), слева направо.
 */
export type ModerationDecisionOptionOut = {
    decision: ModerationDecisionKind;
    /**
     * Label
     */
    label: string;
    /**
     * Comment Required
     */
    comment_required: boolean;
};

/**
 * ModerationDecisionOut
 */
export type ModerationDecisionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Kind Label
     */
    kind_label: string;
    group: ModerationGroup;
    /**
     * Title
     */
    title: string;
    decision: ModerationDecisionKind;
    /**
     * Decision Label
     */
    decision_label: string;
    resulting_status: ModerationEntityStatus;
    /**
     * Resulting Status Label
     */
    resulting_status_label: string;
    /**
     * Comment
     */
    comment: string | null;
    /**
     * Decided By
     */
    decided_by: string | null;
    /**
     * Decided By Name
     */
    decided_by_name: string;
    /**
     * Decided At
     */
    decided_at: string;
    /**
     * Wait Hours
     */
    wait_hours: number;
    link: ModerationLinkOut | null;
};

/**
 * ModerationDecisionsPageOut
 */
export type ModerationDecisionsPageOut = {
    /**
     * Items
     */
    items: Array<ModerationDecisionOut>;
    /**
     * Total
     */
    total: number;
    /**
     * By Group
     *
     * Число решений выборки по группам
     */
    by_group: {
        [key: string]: number;
    };
};

/**
 * ModerationDiffItemOut
 */
export type ModerationDiffItemOut = {
    /**
     * Field
     */
    field: string;
    /**
     * Label
     */
    label: string;
    /**
     * Before
     */
    before?: unknown;
    /**
     * After
     */
    after?: unknown;
};

/**
 * ModerationEntityStatus
 *
 * Статус модерируемой сущности в словаре SRV: общая модель публикации (13.5.1), заявка
 * эксперта и документ (13.3.6) и состояния записи (архив, отзыв). Модули-владельцы со своими
 * статусами (GEO) переводят их в этот словарь в адаптере.
 */
export const ModerationEntityStatus = {
    Draft: 'draft',
    OnModeration: 'on_moderation',
    NeedsRevision: 'needs_revision',
    Published: 'published',
    Rejected: 'rejected',
    Unpublished: 'unpublished',
    Archived: 'archived',
    OnReview: 'on_review',
    NeedsInfo: 'needs_info',
    Approved: 'approved',
    Withdrawn: 'withdrawn'
} as const;

/**
 * ModerationEntityStatus
 *
 * Статус модерируемой сущности в словаре SRV: общая модель публикации (13.5.1), заявка
 * эксперта и документ (13.3.6) и состояния записи (архив, отзыв). Модули-владельцы со своими
 * статусами (GEO) переводят их в этот словарь в адаптере.
 */
export type ModerationEntityStatus = typeof ModerationEntityStatus[keyof typeof ModerationEntityStatus];

/**
 * ModerationGroup
 *
 * Группа очереди: Каталог / Обучение / Впечатления / Люди / Документы (13.3.4).
 */
export const ModerationGroup = {
    Catalog: 'catalog',
    Learning: 'learning',
    Experiences: 'experiences',
    People: 'people',
    Documents: 'documents'
} as const;

/**
 * ModerationGroup
 *
 * Группа очереди: Каталог / Обучение / Впечатления / Люди / Документы (13.3.4).
 */
export type ModerationGroup = typeof ModerationGroup[keyof typeof ModerationGroup];

/**
 * ModerationLinkOut
 */
export type ModerationLinkOut = {
    /**
     * Section
     *
     * Ключ раздела админки (`wineries`, `team`, …)
     */
    section: string;
    /**
     * Id
     *
     * id карточки в разделе
     */
    id: string;
};

/**
 * ModerationNoteOut
 */
export type ModerationNoteOut = {
    /**
     * Comment
     */
    comment: string;
    /**
     * Author
     */
    author: string | null;
    /**
     * At
     */
    at: string | null;
};

/**
 * ModerationOut
 */
export type ModerationOut = {
    status: ModerationStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Note
     */
    note: string | null;
    /**
     * Is Live
     */
    is_live: boolean;
    /**
     * Has Pending Revision
     */
    has_pending_revision: boolean;
};

/**
 * ModerationQueueInfoOut
 */
export type ModerationQueueInfoOut = {
    state: ModerationQueueState;
    /**
     * Queued At
     */
    queued_at: string;
    /**
     * Returned At
     */
    returned_at: string | null;
    /**
     * Age Hours
     */
    age_hours: number;
    /**
     * Age Label
     */
    age_label: string;
    /**
     * Overdue
     */
    overdue: boolean;
    /**
     * What To Check
     */
    what_to_check: string | null;
    /**
     * Last Comment
     */
    last_comment: string | null;
};

/**
 * ModerationQueueItemOut
 */
export type ModerationQueueItemOut = {
    /**
     * Number
     *
     * Порядковый номер в текущей выборке (колонка «№»)
     */
    number: number;
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Kind Label
     *
     * Тип: «Вино», «Курс», «Заявка эксперта»…
     */
    kind_label: string;
    group: ModerationGroup;
    /**
     * Group Label
     */
    group_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * What To Check
     *
     * «Что проверить» (BR-SRV-14)
     */
    what_to_check: string | null;
    /**
     * Last Comment
     *
     * Последние замечания автору / комментарий
     */
    last_comment: string | null;
    /**
     * Note Label
     *
     * «Что проверить» / «Заявка» / «Документ»
     */
    note_label: string;
    /**
     * Comment Label
     *
     * «Комментарий автору» / «Комментарий заявителю»
     */
    comment_label: string;
    status: ModerationEntityStatus;
    /**
     * Status Label
     */
    status_label: string;
    state: ModerationQueueState;
    /**
     * Queued At
     */
    queued_at: string;
    /**
     * Returned At
     */
    returned_at: string | null;
    /**
     * Age Hours
     *
     * Часы в очереди (от queued_at или returned_at)
     */
    age_hours: number;
    /**
     * Age Label
     *
     * BR-SRV-13: «только что», «5 ч», «3 дня», «… назад»
     */
    age_label: string;
    /**
     * Overdue
     *
     * BR-SRV-12: ждёт решения дольше 48 часов
     */
    overdue: boolean;
    /**
     * Queue Note
     *
     * «{автор} · в очереди {возраст}.» — текст модалки
     */
    queue_note: string;
    /**
     * Revision Id
     */
    revision_id: string | null;
    /**
     * Decisions
     *
     * Кнопки решения (пусто — «Открыть →»)
     */
    decisions: Array<ModerationDecisionOptionOut>;
    /**
     * Карточка материала
     */
    link: ModerationLinkOut | null;
};

/**
 * ModerationQueuePageOut
 */
export type ModerationQueuePageOut = {
    /**
     * Items
     */
    items: Array<ModerationQueueItemOut>;
    /**
     * Total
     */
    total: number;
    counters: ModerationCountersOut;
    /**
     * Sla Label
     *
     * «Срок проверки — 48 часов · просрочено: N»
     */
    sla_label: string;
};

/**
 * ModerationQueueState
 *
 * Состояние элемента очереди (13.3.4, 13.5.5).
 */
export const ModerationQueueState = {
    Waiting: 'waiting',
    Returned: 'returned',
    Closed: 'closed'
} as const;

/**
 * ModerationQueueState
 *
 * Состояние элемента очереди (13.3.4, 13.5.5).
 */
export type ModerationQueueState = typeof ModerationQueueState[keyof typeof ModerationQueueState];

/**
 * ModerationRevisionOut
 */
export type ModerationRevisionOut = {
    /**
     * Id
     */
    id: string;
    status: ModerationRevisionStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Diff
     */
    diff: Array<ModerationDiffItemOut>;
    /**
     * Base Published At
     */
    base_published_at: string;
    /**
     * Submitted At
     */
    submitted_at: string | null;
    /**
     * Decided At
     */
    decided_at: string | null;
    /**
     * Live Version Label
     *
     * «В приложении — опубликованная версия от {дата}»
     */
    live_version_label: string | null;
};

/**
 * ModerationRevisionStatus
 *
 * Ревизия опубликованной записи (13.3.3, 13.5.2).
 */
export const ModerationRevisionStatus = {
    Editing: 'editing',
    Submitted: 'submitted',
    Approved: 'approved',
    Returned: 'returned',
    Rejected: 'rejected',
    Discarded: 'discarded'
} as const;

/**
 * ModerationRevisionStatus
 *
 * Ревизия опубликованной записи (13.3.3, 13.5.2).
 */
export type ModerationRevisionStatus = typeof ModerationRevisionStatus[keyof typeof ModerationRevisionStatus];

/**
 * ModerationStateOut
 *
 * Состояние модерации записи (13.8.2 `GET /moderation/{entity_type}/{id}`).
 */
export type ModerationStateOut = {
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Title
     */
    title: string;
    moderation_status: ModerationEntityStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Is Live
     */
    is_live: boolean;
    /**
     * Published At
     */
    published_at: string | null;
    /**
     * Submitted At
     */
    submitted_at: string | null;
    /**
     * Moderation Note
     *
     * «Замечания модерации» автору
     */
    moderation_note: string | null;
    /**
     * Note By
     *
     * Кто оставил замечания
     */
    note_by: string | null;
    /**
     * Note At
     */
    note_at: string | null;
    revision: ModerationRevisionOut | null;
    /**
     * History
     *
     * Решения по записи, новые сверху
     */
    history: Array<ModerationDecisionOut>;
    /**
     * Available Actions
     *
     * Действия текущего участника: submit, submit_revision, publish, return, reject, approve_application, request_info, accept_document, unpublish, archive, restore
     */
    available_actions: Array<string>;
    /**
     * Decisions
     *
     * Кнопки модалки «Решение»
     */
    decisions: Array<ModerationDecisionOptionOut>;
    /**
     * Awaiting Decision
     *
     * Владелец: «Ждёт решения администратора» (кнопка неактивна)
     */
    awaiting_decision: boolean;
    queue: ModerationQueueInfoOut | null;
};

/**
 * ModerationStatus
 *
 * `moderation_status` рабочей версии (13.3.2).
 */
export const ModerationStatus = {
    Draft: 'draft',
    OnModeration: 'on_moderation',
    NeedsRevision: 'needs_revision',
    Published: 'published',
    Rejected: 'rejected',
    Unpublished: 'unpublished'
} as const;

/**
 * ModerationStatus
 *
 * `moderation_status` рабочей версии (13.3.2).
 */
export type ModerationStatus = typeof ModerationStatus[keyof typeof ModerationStatus];

/**
 * Money
 */
export type Money = {
    /**
     * Amount
     *
     * Сумма в минимальных единицах (копейки / центы)
     */
    amount: number;
    currency: Currency;
};

/**
 * MoneyIn
 */
export type MoneyIn = {
    /**
     * Amount
     *
     * Минимальные единицы (копейки / центы), > 0
     */
    amount: number;
    currency: Currency;
};

/**
 * MoreFromAuthorOut
 */
export type MoreFromAuthorOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Profile Target
     */
    profile_target: string;
    /**
     * Items
     */
    items: Array<MoreItemOut>;
};

/**
 * MoreItemOut
 */
export type MoreItemOut = {
    /**
     * Kind
     */
    kind: 'tasting' | 'set';
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string | null;
    /**
     * Target
     */
    target: string;
};

/**
 * MyExperience
 */
export type MyExperience = {
    /**
     * Booking Id
     */
    booking_id: string;
    kind: BookingTarget;
    /**
     * Target Id
     */
    target_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Meta
     */
    meta: string;
    status: BookingStatus;
    /**
     * Status Chip
     */
    status_chip: string;
    amount: Money;
    /**
     * Amount Label
     */
    amount_label: string;
    /**
     * Starts At
     */
    starts_at: string | null;
    /**
     * Open
     *
     * Куда ведёт тап: checkout | trip-status | card
     */
    open: string;
};

/**
 * MyOut
 */
export type MyOut = {
    /**
     * Items
     */
    items: Array<MyExperience>;
    /**
     * Registered
     */
    registered: number;
    /**
     * Attended
     */
    attended: number;
};

/**
 * MyReviewOut
 */
export type MyReviewOut = {
    review: ReviewOut | null;
    /**
     * Formats
     */
    formats: Array<ReviewFormatOut>;
    default_format: ReviewFormatOut;
    /**
     * Can Review
     */
    can_review: boolean;
};

/**
 * NavItemOut
 */
export type NavItemOut = {
    /**
     * Group Key
     *
     * Стабильный ключ группы меню
     */
    group_key: string;
    /**
     * Group
     *
     * Название группы на языке админки
     */
    group: string;
    /**
     * Key
     *
     * Ключ раздела (ТЗ 4.2.2.1)
     */
    key: string;
    /**
     * Label
     *
     * Название пункта на языке админки
     */
    label: string;
    /**
     * Badge
     *
     * Бейдж пункта: «Забота» — обращения `new` + `overdue` не в архиве; null или 0 — бейдж не показывается
     */
    badge?: number | null;
};

/**
 * NearbyOut
 */
export type NearbyOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Author Name
     */
    author_name: string;
    /**
     * Author Photo Url
     */
    author_photo_url: string | null;
    /**
     * Author Initial
     */
    author_initial: string;
    /**
     * Time Label
     */
    time_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     *
     * «{N} отметок · {N} ответов»
     */
    meta: string;
    /**
     * Target
     */
    target: string;
};

/**
 * NeedOut
 */
export type NeedOut = {
    /**
     * K
     */
    k: 'xp' | 'accuracy';
    /**
     * V
     */
    v: number;
    /**
     * All
     */
    all: number;
    /**
     * Done
     */
    done: boolean;
    /**
     * Label
     *
     * «N из M» или «N — выполнено»
     */
    label: string;
};

/**
 * NextLessonOut
 */
export type NextLessonOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Number
     */
    number: string;
    /**
     * Title
     */
    title: string;
    /**
     * Locked
     */
    locked: boolean;
    /**
     * Likes
     */
    likes: number;
    /**
     * Label
     */
    label: string;
};

/**
 * NextLevelOut
 */
export type NextLevelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Min Xp
     */
    min_xp: number;
    /**
     * Xp Left
     */
    xp_left: number;
};

/**
 * NextPerkOut
 */
export type NextPerkOut = {
    /**
     * Kind
     */
    kind: 'held' | 'discount' | 'club' | 'bonus' | 'none';
    /**
     * Text
     */
    text: string | null;
    /**
     * Xp Left
     */
    xp_left: number | null;
    /**
     * Label
     *
     * Строка целиком: «до первого бонуса · … · 4 880 XP»
     */
    label: string | null;
};

/**
 * NextTierOut
 */
export type NextTierOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Title
     */
    title: string;
    /**
     * Unlocks
     */
    unlocks: string | null;
    /**
     * Need
     */
    need: Array<NeedOut>;
    /**
     * Done Count
     */
    done_count: number;
    /**
     * Hint
     *
     * Подсказка BR-GAM-42
     */
    hint: string;
    /**
     * Card Label
     */
    card_label: string;
};

/**
 * NotesIn
 */
export type NotesIn = {
    /**
     * Items
     *
     * Ноты, каждая до 60 символов
     */
    items: Array<LocalizedText>;
};

/**
 * NotificationAudience
 */
export const NotificationAudience = {
    User: 'user',
    Buyer: 'buyer',
    GuestOfBooking: 'guest_of_booking',
    Applicant: 'applicant',
    Traveller: 'traveller',
    Subscribers: 'subscribers',
    TierMin: 'tier_min',
    Waitlist: 'waitlist',
    ReminderSubscribers: 'reminder_subscribers',
    Likers: 'likers',
    AllUsers: 'all_users',
    TeamMember: 'team_member'
} as const;

/**
 * NotificationAudience
 */
export type NotificationAudience = typeof NotificationAudience[keyof typeof NotificationAudience];

/**
 * NotificationChannels
 */
export const NotificationChannels = {
    Push: 'push',
    Email: 'email',
    PushEmail: 'push_email'
} as const;

/**
 * NotificationChannels
 */
export type NotificationChannels = typeof NotificationChannels[keyof typeof NotificationChannels];

/**
 * NotificationItemOut
 */
export type NotificationItemOut = {
    /**
     * Key
     */
    key: 'drops' | 'events' | 'learning' | 'subscriptions';
    /**
     * Label
     */
    label: string;
    /**
     * Hint
     */
    hint: string;
    /**
     * Enabled
     */
    enabled: boolean;
};

/**
 * NotificationPreferencesOut
 */
export type NotificationPreferencesOut = {
    /**
     * Drops
     *
     * «Старт дропов» — пуш за 10 минут до открытия
     */
    drops: boolean;
    /**
     * Events
     *
     * «События» — дегустации, экскурсии и поездки
     */
    events: boolean;
    /**
     * Learning
     *
     * «Обучение» — новые уроки и напоминания
     */
    learning: boolean;
    /**
     * Subscriptions
     *
     * «Подписки» — публикации экспертов и виноделов
     */
    subscriptions: boolean;
    /**
     * «Подборки и новости» (согласие на рекламу); гостю — null (переключатель не показывается)
     */
    marketing: MarketingOut | null;
    /**
     * Push Permission
     *
     * Разрешение на уведомления устройства
     */
    push_permission: 'granted' | 'denied' | 'default';
};

/**
 * NotificationSection
 *
 * Раздел шаблона для фильтра админки (13.3.7).
 */
export const NotificationSection = {
    Account: 'account',
    Passport: 'passport',
    Learning: 'learning',
    Catalog: 'catalog',
    Orders: 'orders',
    Bookings: 'bookings',
    Trips: 'trips',
    Tastings: 'tastings',
    Content: 'content',
    Diary: 'diary',
    Care: 'care',
    Documents: 'documents',
    Notify: 'notify',
    Moderation: 'moderation'
} as const;

/**
 * NotificationSection
 *
 * Раздел шаблона для фильтра админки (13.3.7).
 */
export type NotificationSection = typeof NotificationSection[keyof typeof NotificationSection];

/**
 * NotificationSettingsIn
 */
export type NotificationSettingsIn = {
    /**
     * Drops
     */
    drops: boolean;
    /**
     * Events
     */
    events: boolean;
    /**
     * Learning
     */
    learning: boolean;
    /**
     * Subscriptions
     */
    subscriptions: boolean;
};

/**
 * NotificationSettingsOut
 */
export type NotificationSettingsOut = {
    /**
     * Drops
     */
    drops: boolean;
    /**
     * Events
     */
    events: boolean;
    /**
     * Learning
     */
    learning: boolean;
    /**
     * Subscriptions
     */
    subscriptions: boolean;
    /**
     * Push Enabled
     *
     * Разрешение ОС на уведомления этого устройства (null — неизвестно)
     */
    push_enabled: boolean | null;
};

/**
 * NotificationSettingsPatchIn
 *
 * Частичный объект: переданные переключатели сохраняются сразу (13.6.2).
 */
export type NotificationSettingsPatchIn = {
    /**
     * Drops
     */
    drops?: boolean | null;
    /**
     * Events
     */
    events?: boolean | null;
    /**
     * Learning
     */
    learning?: boolean | null;
    /**
     * Subscriptions
     */
    subscriptions?: boolean | null;
    /**
     * Только с аккаунтом: гость — `401 account_required`
     */
    marketing?: MarketingIn | null;
};

/**
 * NotificationsOut
 */
export type NotificationsOut = {
    /**
     * Enabled Count
     */
    enabled_count: number;
    /**
     * Total
     */
    total: number;
    /**
     * Summary
     */
    summary: string;
    /**
     * Items
     */
    items: Array<NotificationItemOut>;
};

/**
 * NotifySubscriptionListOut
 */
export type NotifySubscriptionListOut = {
    /**
     * Items
     */
    items: Array<NotifySubscriptionOut>;
};

/**
 * NotifySubscriptionOut
 */
export type NotifySubscriptionOut = {
    topic: NotifyTopic;
    /**
     * Region Id
     */
    region_id: string | null;
};

/**
 * NotifyTopic
 */
export const NotifyTopic = {
    Wines: 'wines',
    Sets: 'sets',
    Drops: 'drops',
    Tastings: 'tastings',
    Excursions: 'excursions',
    Trips: 'trips'
} as const;

/**
 * NotifyTopic
 */
export type NotifyTopic = typeof NotifyTopic[keyof typeof NotifyTopic];

/**
 * OAuthCompleteIn
 */
export type OAuthCompleteIn = {
    /**
     * State
     */
    state: string;
    /**
     * Code
     */
    code?: string | null;
    /**
     * Device Id
     *
     * VK ID: device_id
     */
    device_id?: string | null;
    /**
     * Access Token
     */
    access_token?: string | null;
    /**
     * Id Token
     */
    id_token?: string | null;
    apple_user?: AppleUserIn | null;
    /**
     * Redirect Uri
     */
    redirect_uri?: string | null;
    /**
     * Error
     *
     * Ошибка провайдера из колбэка (`access_denied` — отмена, BR-INT-SVC-14)
     */
    error?: string | null;
};

/**
 * OAuthFlow
 */
export const OAuthFlow = { Native: 'native', Redirect: 'redirect' } as const;

/**
 * OAuthFlow
 */
export type OAuthFlow = typeof OAuthFlow[keyof typeof OAuthFlow];

/**
 * OAuthHandoffIn
 */
export type OAuthHandoffIn = {
    /**
     * Handoff Code
     */
    handoff_code: string;
};

/**
 * OAuthPollIn
 */
export type OAuthPollIn = {
    /**
     * State
     */
    state: string;
};

/**
 * OAuthProvider
 */
export const OAuthProvider = {
    Vk: 'vk',
    Yandex: 'yandex',
    Google: 'google',
    Apple: 'apple'
} as const;

/**
 * OAuthProvider
 */
export type OAuthProvider = typeof OAuthProvider[keyof typeof OAuthProvider];

/**
 * OAuthResultOut
 *
 * Результат входа через провайдера (17.18.1): `complete`, `handoff`, `poll`.
 *
 * `status = completed` и `session = null` — вход завершён в другом окне (BR-INT-SVC-12):
 * сессию заберёт приложение `poll`.
 */
export type OAuthResultOut = {
    status: OAuthStateStatus;
    session: OAuthSessionOut | null;
    user: OAuthUserOut | null;
    /**
     * Is New
     */
    is_new: boolean | null;
    /**
     * Profile Completion Required
     */
    profile_completion_required: boolean | null;
    /**
     * Missing
     */
    missing: Array<string>;
    /**
     * Linked By Email
     */
    linked_by_email: boolean | null;
    prefill: PrefillOut | null;
    guest_merge: GuestMergeOut | null;
    /**
     * Return Route
     */
    return_route: string | null;
    /**
     * Error Code
     */
    error_code: string | null;
};

/**
 * OAuthSessionOut
 */
export type OAuthSessionOut = {
    /**
     * Access Token
     */
    access_token: string;
    /**
     * Refresh Token
     */
    refresh_token: string;
    /**
     * Expires In
     */
    expires_in: number;
    /**
     * Refresh Expires In
     */
    refresh_expires_in: number;
};

/**
 * OAuthStartIn
 */
export type OAuthStartIn = {
    flow: OAuthFlow;
    client_platform: ClientPlatform;
    /**
     * Return Route
     */
    return_route?: string | null;
};

/**
 * OAuthStartOut
 */
export type OAuthStartOut = {
    /**
     * State
     */
    state: string;
    /**
     * Nonce
     */
    nonce: string | null;
    /**
     * Code Challenge
     */
    code_challenge: string | null;
    /**
     * Authorize Url
     *
     * Redirect-поток: страница провайдера
     */
    authorize_url: string | null;
    /**
     * Redirect Uri
     */
    redirect_uri: string;
    /**
     * Expires At
     */
    expires_at: string;
};

/**
 * OAuthStateStatus
 */
export const OAuthStateStatus = {
    Started: 'started',
    Completed: 'completed',
    Failed: 'failed',
    Expired: 'expired',
    Consumed: 'consumed'
} as const;

/**
 * OAuthStateStatus
 */
export type OAuthStateStatus = typeof OAuthStateStatus[keyof typeof OAuthStateStatus];

/**
 * OAuthUserOut
 */
export type OAuthUserOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Email
     *
     * null — почта ещё не указана (соцвход без почты)
     */
    email: string | null;
    /**
     * Email Verified
     */
    email_verified: boolean;
    /**
     * Full Name
     */
    full_name: string | null;
    /**
     * Initial
     *
     * Первая буква имени — вместо фото
     */
    initial: string | null;
    /**
     * Birth Date
     */
    birth_date: string | null;
    phone: PhoneOut | null;
    /**
     * Phone Display
     *
     * «+7 926 771-05-19»
     */
    phone_display: string | null;
    /**
     * Country Code
     */
    country_code: string | null;
    /**
     * City Name
     */
    city_name: string | null;
    /**
     * City Suggestion Id
     */
    city_suggestion_id: string | null;
    /**
     * Avatar Url
     */
    avatar_url: string | null;
    /**
     * Avatar Thumb Url
     *
     * 128 × 128 (когда готов вариант)
     */
    avatar_thumb_url: string | null;
    locale: Language;
    currency: Currency;
    /**
     * Profile Complete
     */
    profile_complete: boolean;
    /**
     * Missing
     *
     * Незаполненные обязательные поля (BR-ACC-12): email, full_name, birth_date, phone, country_code, city_name, consents
     */
    missing: Array<string>;
    /**
     * Has Password
     */
    has_password: boolean;
    /**
     * Password Changed At
     */
    password_changed_at: string | null;
    /**
     * Registration Method
     */
    registration_method: string;
    /**
     * Roles
     *
     * Активные роли в команде: expert, winemaker, admin
     */
    roles: Array<string>;
    /**
     * Expert Mode
     */
    expert_mode: boolean;
    /**
     * Expert Badge
     *
     * Показывать «Эксперт · подтверждён»
     */
    expert_badge: boolean;
    /**
     * Created At
     */
    created_at: string;
    labels: UserMeLabels;
    /**
     * Name
     *
     * Имя и фамилия (= `full_name`, 17.18.1)
     */
    name: string | null;
};

/**
 * OfferOut
 */
export type OfferOut = {
    state: OfferState;
    /**
     * State Label
     */
    state_label: string;
    /**
     * Title
     *
     * «В наличии · 64 бутылки» / «Нет в наличии» / …
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string | null;
    /**
     * Price Caption
     *
     * «за бутылку 0,75 л» / «в винодельне · …»
     */
    price_caption: string | null;
    /**
     * Available
     */
    available: number | null;
    /**
     * Max Qty
     */
    max_qty: number;
    /**
     * Seller Label
     */
    seller_label: string | null;
    /**
     * Ship Offer
     */
    ship_offer: string | null;
    /**
     * Purchase Action
     */
    purchase_action: 'cart' | 'partner' | 'none';
    /**
     * Purchase Label
     */
    purchase_label: string | null;
};

/**
 * OfferState
 */
export const OfferState = {
    Buy: 'buy',
    Set: 'set',
    Out: 'out',
    WineryOnly: 'winery_only'
} as const;

/**
 * OfferState
 */
export type OfferState = typeof OfferState[keyof typeof OfferState];

/**
 * OnboardingAppOut
 */
export type OnboardingAppOut = {
    /**
     * Screens
     */
    screens: Array<OnboardingScreenAppOut>;
};

/**
 * OnboardingGetOut
 */
export type OnboardingGetOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Label
     */
    label: string;
};

/**
 * OnboardingGoalOut
 */
export type OnboardingGoalOut = {
    /**
     * Position
     */
    position: number;
    /**
     * Title
     *
     * Формулировка на момент выбора (title_snapshot)
     */
    title: string;
};

/**
 * OnboardingGoalsIn
 */
export type OnboardingGoalsIn = {
    /**
     * Item Ids
     *
     * Цели в порядке выбора (первая — основная); пусто — «Пропустить»
     */
    item_ids: Array<string>;
};

/**
 * OnboardingItemAppOut
 */
export type OnboardingItemAppOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Code
     */
    code: string;
    /**
     * Title
     */
    title: string;
    /**
     * Caption
     */
    caption: string;
    /**
     * Icon Url
     */
    icon_url: string | null;
};

/**
 * OnboardingItemCan
 */
export type OnboardingItemCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * OnboardingItemCreateIn
 */
export type OnboardingItemCreateIn = {
    /**
     * Карточка — заголовок; цель — полная формулировка
     */
    title: LocalizedText;
    /**
     * Карточка — подпись; цель — подпись на плитке
     */
    caption: LocalizedText;
    /**
     * Icon File Id
     *
     * Загрузка `onboarding_icon` (SVG)
     */
    icon_file_id: string;
};

/**
 * OnboardingItemOut
 */
export type OnboardingItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Screen Id
     */
    screen_id: string;
    /**
     * Code
     *
     * Стабильный ключ (для целей — аналитика и лента)
     */
    code: string;
    /**
     * Position
     */
    position: number;
    title: LocalizedText;
    caption: LocalizedText;
    icon: AppModulesAccSchemasCommonFileRefOut | null;
    can: OnboardingItemCan;
};

/**
 * OnboardingItemPatchIn
 */
export type OnboardingItemPatchIn = {
    title?: LocalizedText | null;
    caption?: LocalizedText | null;
    /**
     * Icon File Id
     */
    icon_file_id?: string | null;
    /**
     * Position
     */
    position?: number | null;
};

/**
 * OnboardingMoveIn
 */
export type OnboardingMoveIn = {
    /**
     * Direction
     */
    direction: 'up' | 'down';
};

/**
 * OnboardingMoveOut
 */
export type OnboardingMoveOut = {
    /**
     * Items
     *
     * Все экраны в новом порядке
     */
    items: Array<OnboardingScreenRow>;
};

/**
 * OnboardingOut
 */
export type OnboardingOut = {
    learn: RangeOut | null;
    taste: RangeOut | null;
    get: OnboardingGetOut;
};

/**
 * OnboardingScreenAppOut
 */
export type OnboardingScreenAppOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Key
     *
     * Ключ экрана приложения: onboard-age, onboard-what, …
     */
    key: string;
    kind: OnboardingScreenKind;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Image Url
     *
     * Фото на весь экран (только `welcome`)
     */
    image_url: string | null;
    /**
     * «Шаг N из M» (BR-ACC-35); у приветствия — null
     */
    step: OnboardingStepAppOut | null;
    /**
     * Items
     */
    items: Array<OnboardingItemAppOut>;
};

/**
 * OnboardingScreenCan
 */
export type OnboardingScreenCan = {
    /**
     * View
     */
    view?: boolean;
    /**
     * Update
     */
    update?: boolean;
    /**
     * Move Up
     */
    move_up?: boolean;
    /**
     * Move Down
     */
    move_down?: boolean;
    /**
     * Hide
     */
    hide?: boolean;
    /**
     * Show
     */
    show?: boolean;
    /**
     * Add Item
     */
    add_item?: boolean;
};

/**
 * OnboardingScreenFixed
 */
export const OnboardingScreenFixed = { First: 'first' } as const;

/**
 * OnboardingScreenFixed
 */
export type OnboardingScreenFixed = typeof OnboardingScreenFixed[keyof typeof OnboardingScreenFixed];

/**
 * OnboardingScreenKind
 */
export const OnboardingScreenKind = {
    Welcome: 'welcome',
    Intro: 'intro',
    Goals: 'goals'
} as const;

/**
 * OnboardingScreenKind
 */
export type OnboardingScreenKind = typeof OnboardingScreenKind[keyof typeof OnboardingScreenKind];

/**
 * OnboardingScreenOut
 */
export type OnboardingScreenOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Screen Key
     */
    screen_key: string;
    kind: OnboardingScreenKind;
    /**
     * Name
     */
    name: string;
    fixed: OnboardingScreenFixed | null;
    /**
     * Position
     */
    position: number;
    title: LocalizedText;
    subtitle: LocalizedText | null;
    /**
     * Только welcome: фото на весь экран 3:4
     */
    background: AppModulesAccSchemasCommonFileRefOut | null;
    status: OnboardingScreenStatus;
    /**
     * Status Label
     */
    status_label: string;
    step: OnboardingStepOut;
    /**
     * Items
     */
    items: Array<OnboardingItemOut>;
    can: OnboardingScreenCan;
};

/**
 * OnboardingScreenPatchIn
 *
 * `{title?, subtitle?, background_file_id?}`: `subtitle: null` / `background_file_id: null`
 * — удалить; заголовок обязателен.
 */
export type OnboardingScreenPatchIn = {
    /**
     * До 60 символов
     */
    title?: LocalizedText | null;
    /**
     * До 160 символов
     */
    subtitle?: LocalizedText | null;
    /**
     * Background File Id
     *
     * Загрузка `onboarding_background` (JPG/PNG, вертикальное 3:4) — только welcome
     */
    background_file_id?: string | null;
};

/**
 * OnboardingScreenRow
 */
export type OnboardingScreenRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Screen Key
     *
     * Ключ экрана приложения: onboard-age, onboard-what, …
     */
    screen_key: string;
    kind: OnboardingScreenKind;
    /**
     * Name
     */
    name: string;
    /**
     * Position
     */
    position: number;
    title: LocalizedText;
    subtitle: LocalizedText | null;
    /**
     * Illustrations
     */
    illustrations: string;
    /**
     * Items Count
     */
    items_count: number;
    status: OnboardingScreenStatus;
    /**
     * Status Label
     */
    status_label: string;
    fixed: OnboardingScreenFixed | null;
    step: OnboardingStepOut;
    can: OnboardingScreenCan;
};

/**
 * OnboardingScreenStatus
 */
export const OnboardingScreenStatus = { Shown: 'shown', Hidden: 'hidden' } as const;

/**
 * OnboardingScreenStatus
 */
export type OnboardingScreenStatus = typeof OnboardingScreenStatus[keyof typeof OnboardingScreenStatus];

/**
 * OnboardingStepAppOut
 */
export type OnboardingStepAppOut = {
    /**
     * Number
     */
    number: number;
    /**
     * Total
     */
    total: number;
};

/**
 * OnboardingStepOut
 */
export type OnboardingStepOut = {
    /**
     * Number
     *
     * «Шаг N из M»; null — закреплённый или скрытый экран
     */
    number: number | null;
    /**
     * Total
     *
     * M — показываемые незакреплённые экраны (BR-ACC-35)
     */
    total: number;
    /**
     * Label
     */
    label: string;
};

/**
 * OnboardingVisibilityIn
 */
export type OnboardingVisibilityIn = {
    status: OnboardingScreenStatus;
};

/**
 * OneTimeRewardOut
 */
export type OneTimeRewardOut = {
    /**
     * Type
     */
    type: 'none' | 'first_region_discount' | 'club_bonuses';
    /**
     * Value
     */
    value: number | null;
    /**
     * Text
     */
    text: string | null;
};

/**
 * OneTimeRewardType
 */
export const OneTimeRewardType = {
    None: 'none',
    FirstRegionDiscount: 'first_region_discount',
    ClubBonuses: 'club_bonuses'
} as const;

/**
 * OneTimeRewardType
 */
export type OneTimeRewardType = typeof OneTimeRewardType[keyof typeof OneTimeRewardType];

/**
 * OpenHoursIn
 */
export type OpenHoursIn = {
    /**
     * Weekday
     *
     * 1 — понедельник
     */
    weekday: number;
    /**
     * From
     */
    from: string;
    /**
     * To
     */
    to: string;
};

/**
 * OpenHoursOut
 */
export type OpenHoursOut = {
    /**
     * Weekday
     */
    weekday: number;
    /**
     * From
     */
    from: string;
    /**
     * To
     */
    to: string;
};

/**
 * OptionOut
 */
export type OptionOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
};

/**
 * OrdAcceptedOut
 */
export type OrdAcceptedOut = {
    /**
     * Accepted
     */
    accepted?: boolean;
    /**
     * Toast
     */
    toast: string;
};

/**
 * OrdAddressIn
 */
export type OrdAddressIn = {
    /**
     * Recipient Name
     */
    recipient_name: string;
    /**
     * Phone
     */
    phone: string;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Country Code
     */
    country_code: string;
    /**
     * City
     */
    city: string;
    /**
     * Postal Code
     */
    postal_code: string;
    /**
     * Line1
     */
    line1: string;
    /**
     * Line2
     */
    line2?: string | null;
    /**
     * Courier Note
     */
    courier_note?: string | null;
    /**
     * Is Default
     */
    is_default?: boolean | null;
};

/**
 * OrdAddressOut
 */
export type OrdAddressOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Recipient Name
     */
    recipient_name: string;
    /**
     * Phone
     */
    phone: string;
    /**
     * Email
     */
    email: string;
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Country Name
     */
    country_name: string | null;
    /**
     * City
     */
    city: string;
    /**
     * Postal Code
     */
    postal_code: string;
    /**
     * Line1
     */
    line1: string;
    /**
     * Line2
     */
    line2: string | null;
    /**
     * Courier Note
     */
    courier_note: string | null;
    /**
     * Is Default
     */
    is_default: boolean;
    /**
     * Validated At
     */
    validated_at: string | null;
    /**
     * Label
     *
     * «{получатель}, {line1}, {индекс} {город}, {страна}»
     */
    label: string;
};

/**
 * OrdAddressPatchIn
 */
export type OrdAddressPatchIn = {
    /**
     * Recipient Name
     */
    recipient_name?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Country Code
     */
    country_code?: string | null;
    /**
     * City
     */
    city?: string | null;
    /**
     * Postal Code
     */
    postal_code?: string | null;
    /**
     * Line1
     */
    line1?: string | null;
    /**
     * Line2
     */
    line2?: string | null;
    /**
     * Courier Note
     */
    courier_note?: string | null;
    /**
     * Is Default
     */
    is_default?: boolean | null;
};

/**
 * OrdAddressSuggestion
 */
export type OrdAddressSuggestion = {
    /**
     * City
     */
    city: string;
    /**
     * Postal Code
     */
    postal_code: string;
};

/**
 * OrdAddressValidateIn
 */
export type OrdAddressValidateIn = {
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Postal Code
     */
    postal_code: string;
    /**
     * City
     */
    city: string;
};

/**
 * OrdAddressValidateOut
 */
export type OrdAddressValidateOut = {
    /**
     * Valid
     */
    valid: boolean;
    /**
     * Suggestions
     */
    suggestions: Array<OrdAddressSuggestion>;
};

/**
 * OrdAddressesOut
 */
export type OrdAddressesOut = {
    /**
     * Items
     */
    items: Array<OrdAddressOut>;
    /**
     * Countries
     *
     * Разрешённые страны доставки
     */
    countries: Array<{
        [key: string]: string;
    }>;
};

/**
 * OrdAdminItem
 */
export type OrdAdminItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Kind
     */
    kind: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Qty
     */
    qty: number;
    unit_price: Money;
    line_total: Money;
    link?: OrdLink | null;
};

/**
 * OrdAdminOrderCard
 */
export type OrdAdminOrderCard = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string;
    group: OrderGroup;
    order_type: OrderType;
    /**
     * Type Label
     */
    type_label: string;
    flow: Flow;
    status: OrderStatus;
    status_pill: OrdPill;
    payment_status: PaymentStatus;
    payment: OrdPill;
    /**
     * Placed At
     */
    placed_at: string;
    /**
     * Paid At
     */
    paid_at: string | null;
    total: Money;
    /**
     * Data
     */
    data: Array<OrdRow>;
    /**
     * Items
     */
    items: Array<OrdAdminItem>;
    items_total: Money;
    stages: OrdStages;
    /**
     * Panels
     */
    panels: Array<OrdPanel>;
    /**
     * History
     */
    history: Array<OrdHistoryRow>;
    modal: OrdStatusModal | null;
    can: OrdCardCan;
    /**
     * Notice
     */
    notice?: string | null;
};

/**
 * OrdAdminOrderRow
 */
export type OrdAdminOrderRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    /**
     * Placed At
     */
    placed_at: string;
    /**
     * Buyer
     */
    buyer: string;
    /**
     * Buyer Id
     */
    buyer_id: string | null;
    group: OrderGroup;
    order_type: OrderType;
    /**
     * Type Label
     */
    type_label: string;
    flow: Flow;
    /**
     * Composition
     */
    composition: string;
    /**
     * Seller
     */
    seller: string;
    total: Money;
    /**
     * Total Label
     */
    total_label: string;
    payment: OrdPill;
    payment_status: PaymentStatus;
    status: OrderStatus;
    status_pill: OrdPill;
    can: OrdAdminRowCan;
};

/**
 * OrdAdminRowCan
 */
export type OrdAdminRowCan = {
    /**
     * View
     */
    view?: boolean;
};

/**
 * OrdAttemptView
 *
 * `PaymentAttemptView` (INT-PAY 15.13.1).
 */
export type OrdAttemptView = {
    /**
     * Id
     */
    id: string;
    /**
     * Checkout Session Id
     */
    checkout_session_id: string;
    /**
     * Order Ids
     */
    order_ids: Array<string>;
    /**
     * Order Number
     */
    order_number: string;
    /**
     * Seq
     */
    seq: number;
    /**
     * Provider
     */
    provider: string;
    /**
     * Environment
     */
    environment: string;
    /**
     * Method
     */
    method: string;
    /**
     * Method Title
     */
    method_title: string;
    /**
     * Status
     */
    status: string;
    amount: Money;
    /**
     * Next Action
     */
    next_action: {
        [key: string]: unknown;
    } | null;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Failure
     */
    failure: {
        [key: string]: string | null;
    } | null;
    /**
     * Card
     */
    card: {
        [key: string]: string | null;
    } | null;
    /**
     * Paid At
     */
    paid_at: string | null;
};

/**
 * OrdCancelIn
 */
export type OrdCancelIn = {
    expected_refund_amount?: Money | null;
};

/**
 * OrdCancelQuoteOut
 */
export type OrdCancelQuoteOut = {
    /**
     * Cancellable
     */
    cancellable: boolean;
    refund_amount: Money;
    paid_amount: Money;
    /**
     * Rule Text
     */
    rule_text: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
};

/**
 * OrdCardCan
 */
export type OrdCardCan = {
    /**
     * Change Status
     */
    change_status?: boolean;
    /**
     * Comment
     */
    comment?: boolean;
    /**
     * Payment Reminder
     */
    payment_reminder?: boolean;
    /**
     * Link User
     */
    link_user?: boolean;
    /**
     * Payments
     */
    payments?: boolean;
    /**
     * Open Booking
     */
    open_booking?: boolean;
};

/**
 * OrdCardResult
 */
export type OrdCardResult = {
    /**
     * Toast
     */
    toast: string;
    card: OrdAdminOrderCard;
};

/**
 * OrdCartGroupOut
 */
export type OrdCartGroupOut = {
    group: CartGroup;
    /**
     * Title
     */
    title: string;
    /**
     * Rule
     */
    rule: string;
    /**
     * Items
     */
    items: Array<OrdCartItemOut>;
    subtotal: Money;
    discount: Money;
    total: Money;
    /**
     * Discount Label
     */
    discount_label: string | null;
    /**
     * Button
     *
     * «Оформить {обучение|вино|впечатления} · {сумма}»
     */
    button: string;
};

/**
 * OrdCartItemIn
 */
export type OrdCartItemIn = {
    item_type: CartItemType;
    /**
     * Ref Id
     *
     * Товар / курс / урок / событие (место) / бронь EXP / заказ поездки
     */
    ref_id: string;
    /**
     * Qty
     */
    qty?: number;
    /**
     * Meta
     *
     * Подпись позиции клиента
     */
    meta?: string | null;
};

/**
 * OrdCartItemOut
 */
export type OrdCartItemOut = {
    /**
     * Item Key
     */
    item_key: string;
    item_type: CartItemType;
    /**
     * Ref Id
     */
    ref_id: string;
    group: CartGroup;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     */
    meta: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Qty
     */
    qty: number;
    /**
     * Fixed Qty
     */
    fixed_qty: boolean;
    /**
     * Max Qty
     *
     * Верхняя граница счётчика (BR-ORD-05)
     */
    max_qty: number | null;
    /**
     * Цена по текущему каталогу (BR-ORD-09)
     */
    unit_price: Money;
    /**
     * Цена в момент добавления
     */
    unit_price_shown: Money;
    /**
     * Price Changed
     */
    price_changed: boolean;
    line_total: Money;
    /**
     * Badge
     *
     * «один раз, навсегда» / «одно место на аккаунт»
     */
    badge: string | null;
    /**
     * Available
     */
    available: boolean;
    /**
     * Notice
     *
     * Почему позицию нельзя оформить
     */
    notice: string | null;
    /**
     * Target
     *
     * Экран приложения позиции: `bottle:{id}`, `course:{id}`…
     */
    target: string | null;
};

/**
 * OrdCartMergeIn
 */
export type OrdCartMergeIn = {
    /**
     * Device Id
     *
     * `X-Device-Id` устройства, на котором собрана корзина гостя
     */
    device_id: string;
};

/**
 * OrdCartOut
 */
export type OrdCartOut = {
    /**
     * Groups
     */
    groups: Array<OrdCartGroupOut>;
    /**
     * Positions
     */
    positions: number;
    /**
     * Groups Count
     */
    groups_count: number;
    total: Money;
    discount_total: Money;
    /**
     * Header
     *
     * «{N позиций} · {M заказов}»
     */
    header: string;
    /**
     * Title
     */
    title: string;
    /**
     * Note
     *
     * BR-ORD-01 — если групп больше одной
     */
    note: string | null;
    /**
     * Total Label
     *
     * «Всего в корзине · скидка −{сумма}»
     */
    total_label: string;
    /**
     * Empty Title
     */
    empty_title: string | null;
    /**
     * Empty Text
     */
    empty_text: string | null;
    /**
     * Removed
     *
     * Позиции, убранные как уже купленные (BR-ORD-07)
     */
    removed?: Array<string>;
};

/**
 * OrdCheckoutIn
 */
export type OrdCheckoutIn = {
    group: CartGroup;
    payment_method: PaymentMethodCode;
    /**
     * Saved Card Id
     */
    saved_card_id?: string | null;
    /**
     * Address Id
     */
    address_id?: string | null;
    delivery_method?: DeliveryMethod | null;
    /**
     * Gift Wrap
     */
    gift_wrap?: boolean;
};

/**
 * OrdCheckoutOut
 */
export type OrdCheckoutOut = {
    session: OrdSessionOut;
    /**
     * Orders
     */
    orders: Array<OrdOrderBrief>;
    /**
     * Попытка оплаты; null — сумма 0, заказы подтверждены без оплаты
     */
    payment: OrdAttemptView | null;
};

/**
 * OrdCheckoutStatusOut
 */
export type OrdCheckoutStatusOut = {
    /**
     * Id
     */
    id: string;
    status: CheckoutStatus;
    group: CartGroup;
    total: Money;
    /**
     * Orders
     */
    orders: Array<OrdOrderBrief>;
    payment: OrdAttemptView | null;
    /**
     * Экран результата: `paid` — «Оплата прошла», `failed`/`expired` — «Оплата не прошла», `open` — null (ожидание подтверждения)
     */
    result: OrdResultScreen | null;
};

/**
 * OrdChip
 *
 * Чип статуса в приложении (7.6.6): `act` — активный, `wait` — ожидание, `done` —
 * завершён, `off` — отменён.
 */
export type OrdChip = {
    /**
     * Text
     */
    text: string;
    /**
     * Tone
     */
    tone: 'act' | 'wait' | 'done' | 'off';
};

/**
 * OrdClientResultIn
 */
export type OrdClientResultIn = {
    /**
     * Result
     */
    result: 'completed' | 'cancelled' | 'failed';
    /**
     * Error Code
     */
    error_code?: string | null;
};

/**
 * OrdCommentIn
 */
export type OrdCommentIn = {
    /**
     * Text
     */
    text: string;
};

/**
 * OrdCondition
 */
export type OrdCondition = {
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * OrdDeliveryAddressIn
 */
export type OrdDeliveryAddressIn = {
    /**
     * Country Code
     */
    country_code: string;
    /**
     * City
     */
    city: string;
    /**
     * Postal Code
     */
    postal_code: string;
    /**
     * Line1
     */
    line1: string;
    /**
     * Line2
     */
    line2?: string | null;
    /**
     * Courier Note
     */
    courier_note?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
};

/**
 * OrdDeliveryCan
 *
 * Форма правки 7.7.3: до передачи в доставку — все поля, после — только «Плановая дата».
 */
export type OrdDeliveryCan = {
    /**
     * Update
     */
    update: boolean;
    /**
     * Update Recipient
     */
    update_recipient: boolean;
    /**
     * Update Address
     */
    update_address: boolean;
    /**
     * Update Carrier
     */
    update_carrier: boolean;
};

/**
 * OrdDeliveryCard
 */
export type OrdDeliveryCard = {
    /**
     * Id
     */
    id: string;
    /**
     * Order Id
     */
    order_id: string;
    /**
     * Order Label
     */
    order_label: string;
    /**
     * Recipient
     */
    recipient: string;
    /**
     * Phone
     */
    phone: string | null;
    /**
     * Address
     */
    address: string;
    /**
     * Address Snapshot
     */
    address_snapshot: {
        [key: string]: unknown;
    };
    carrier: DeliveryCarrier;
    /**
     * Carrier Label
     */
    carrier_label: string;
    /**
     * Planned From
     */
    planned_from: string | null;
    /**
     * Planned To
     */
    planned_to: string | null;
    /**
     * Planned
     */
    planned: string | null;
    status: OrderDeliveryStatus;
    status_pill: OrdPill;
    /**
     * Ship From
     */
    ship_from: string;
    /**
     * Gift Wrap
     */
    gift_wrap: boolean;
    /**
     * Data
     */
    data: Array<OrdRow>;
    /**
     * Shipments
     *
     * Отправления DHL (INT-DLV 16.10.4): `{id, status, status_label, tracking_number, tracking_url, estimated_delivery_at, last_error_message, label_available}`; подробности и действия — `/admin/shipments/{id}`
     */
    shipments: Array<{
        [key: string]: unknown;
    }>;
    /**
     * History
     */
    history: Array<OrdHistoryRow>;
    can: OrdDeliveryCan;
};

/**
 * OrdDeliveryInfo
 */
export type OrdDeliveryInfo = {
    /**
     * Address
     */
    address: string | null;
    /**
     * Tracking Number
     */
    tracking_number: string | null;
    /**
     * Tracking Url
     */
    tracking_url: string | null;
    /**
     * Planned
     */
    planned: string | null;
    /**
     * Status
     */
    status: string | null;
    /**
     * Carrier
     */
    carrier: string | null;
};

/**
 * OrdDeliveryPatchIn
 */
export type OrdDeliveryPatchIn = {
    /**
     * Recipient Name
     */
    recipient_name?: string | null;
    address?: OrdDeliveryAddressIn | null;
    carrier?: DeliveryCarrier | null;
    /**
     * Planned From
     */
    planned_from?: string | null;
    /**
     * Planned To
     */
    planned_to?: string | null;
};

/**
 * OrdDeliveryRow
 */
export type OrdDeliveryRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Order Id
     */
    order_id: string;
    /**
     * Order Number
     */
    order_number: number;
    /**
     * Order Label
     */
    order_label: string;
    /**
     * Recipient
     */
    recipient: string;
    /**
     * Address
     */
    address: string;
    carrier: DeliveryCarrier;
    /**
     * Carrier Label
     */
    carrier_label: string;
    /**
     * Tracking Number
     */
    tracking_number: string | null;
    /**
     * Planned
     */
    planned: string | null;
    /**
     * Planned From
     */
    planned_from: string | null;
    /**
     * Planned To
     */
    planned_to: string | null;
    status: OrderDeliveryStatus;
    status_pill: OrdPill;
};

/**
 * OrdDetailItem
 */
export type OrdDetailItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Kind
     */
    kind: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Qty
     */
    qty: number;
    unit_price: Money;
    line_total: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Target
     */
    target: string | null;
};

/**
 * OrdDetailRow
 */
export type OrdDetailRow = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
    /**
     * Copyable
     *
     * Показать действие «Копировать»
     */
    copyable?: boolean;
};

/**
 * OrdDiscountOut
 */
export type OrdDiscountOut = {
    /**
     * Level Name
     */
    level_name: string | null;
    /**
     * Pct
     */
    pct: string;
    amount: Money;
    /**
     * Label
     */
    label: string | null;
};

/**
 * OrdDiscountsOut
 */
export type OrdDiscountsOut = {
    /**
     * Level Name
     */
    level_name: string | null;
    /**
     * Learning Pct
     */
    learning_pct: string;
    /**
     * Wine Pct
     */
    wine_pct: string;
    /**
     * Learning Applies
     */
    learning_applies: boolean;
    /**
     * Wine Applies
     */
    wine_applies: boolean;
};

/**
 * OrdEntryBlock
 */
export type OrdEntryBlock = {
    /**
     * Title
     */
    title: string;
    /**
     * Name
     */
    name: string;
    /**
     * Caption
     */
    caption: string;
    /**
     * Note
     */
    note: string;
};

/**
 * OrdHandoffIn
 */
export type OrdHandoffIn = {
    /**
     * Item Type
     */
    item_type: 'wine' | 'set' | 'drop' | 'blind_kit';
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Qty
     */
    qty?: number;
    /**
     * Source Screen
     */
    source_screen?: string | null;
};

/**
 * OrdHandoffOut
 */
export type OrdHandoffOut = {
    /**
     * Handoff Id
     */
    handoff_id: string;
    /**
     * Redirect Url
     */
    redirect_url: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Qty
     */
    qty: number;
    /**
     * Qty Adjusted
     */
    qty_adjusted: boolean;
    amount_hint: Money;
    /**
     * Eligible Early Access
     */
    eligible_early_access: boolean;
};

/**
 * OrdHandoffStatusOut
 */
export type OrdHandoffStatusOut = {
    /**
     * Handoff Id
     */
    handoff_id: string;
    /**
     * Status
     */
    status: string;
    /**
     * Order Id
     */
    order_id: string | null;
    /**
     * Order Status
     */
    order_status: string | null;
    /**
     * Item
     */
    item: {
        [key: string]: unknown;
    };
};

/**
 * OrdHistoryRow
 */
export type OrdHistoryRow = {
    /**
     * Id
     */
    id: string;
    /**
     * At
     */
    at: string;
    /**
     * Actor
     */
    actor: string;
    /**
     * Actor Kind
     */
    actor_kind: string;
    /**
     * Text
     */
    text: string;
};

/**
 * OrdLink
 *
 * Переход в раздел админки: `section` — ключ раздела, `id` — запись, `filters` — фильтры.
 */
export type OrdLink = {
    /**
     * Section
     */
    section: string;
    /**
     * Id
     */
    id?: string | null;
    /**
     * Filters
     */
    filters?: {
        [key: string]: string;
    };
};

/**
 * OrdLinkUserIn
 */
export type OrdLinkUserIn = {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * OrdMethodOut
 */
export type OrdMethodOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Kind
     */
    kind: 'method' | 'saved_card';
    method: PaymentMethodCode;
};

/**
 * OrdNextStep
 */
export type OrdNextStep = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
};

/**
 * OrdOrderBrief
 */
export type OrdOrderBrief = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    order_type: OrderType;
    flow: Flow;
    status: OrderStatus;
    /**
     * Seller Label
     */
    seller_label: string;
    total: Money;
};

/**
 * OrdOrderCard
 */
export type OrdOrderCard = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    /**
     * Type Label
     */
    type_label: string;
    group: OrderGroup;
    flow: Flow;
    order_type: OrderType;
    status: OrderStatus;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     */
    meta: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    chip: OrdChip;
    /**
     * Section
     */
    section: 'active' | 'done';
    total: Money;
    /**
     * Total Struck
     */
    total_struck: boolean;
    /**
     * Placed At
     */
    placed_at: string;
    /**
     * Target
     *
     * `order:{id}` или `trip-status:{trip_id}`
     */
    target: string;
    partner?: OrdPartnerBrief | null;
    /**
     * Tracking Line
     */
    tracking_line?: string | null;
};

/**
 * OrdOrderCounts
 */
export type OrdOrderCounts = {
    /**
     * Active
     */
    active: number;
    /**
     * Done
     */
    done: number;
};

/**
 * OrdOrderDetail
 */
export type OrdOrderDetail = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    /**
     * Type Label
     */
    type_label: string;
    group: OrderGroup;
    flow: Flow;
    order_type: OrderType;
    status: OrderStatus;
    chip: OrdChip;
    /**
     * Placed At
     */
    placed_at: string;
    /**
     * Placed Label
     */
    placed_label: string;
    /**
     * Head
     */
    head: string;
    /**
     * Sub
     */
    sub: string | null;
    /**
     * Timeline
     */
    timeline: Array<OrdTimelineStep>;
    /**
     * Timeline Title
     */
    timeline_title: string | null;
    /**
     * Items Title
     */
    items_title: string;
    /**
     * Items
     */
    items: Array<OrdDetailItem>;
    /**
     * Details
     */
    details: Array<OrdDetailRow>;
    entry: OrdEntryBlock | null;
    totals: OrdTotals;
    payment: OrdPaymentInfo;
    delivery: OrdDeliveryInfo | null;
    /**
     * Shipments
     *
     * Отправления DHL (World, INT-DLV 16.9.4)
     */
    shipments?: Array<OrdShipmentInfo>;
    /**
     * Partner
     */
    partner: {
        [key: string]: unknown;
    } | null;
    /**
     * Booking Id
     */
    booking_id: string | null;
    /**
     * Event Id
     */
    event_id: string | null;
    /**
     * Hold Expires At
     */
    hold_expires_at: string | null;
    pay_amount: Money | null;
    /**
     * Checkout Session Id
     */
    checkout_session_id: string | null;
    /**
     * Actions
     *
     * Действия: pay, cancel, cancel_booking, track, receipt, rebook, open_card, route, open_trip, calendar, open_tasting, continue_course, diary
     */
    actions: Array<string>;
    /**
     * Cancel Confirm
     */
    cancel_confirm: string | null;
    /**
     * Receipt Mode
     */
    receipt_mode: 'ofd' | 'email' | 'store' | 'partner' | 'none';
    /**
     * Receipt Toast
     */
    receipt_toast: string | null;
};

/**
 * OrdOrdersPage
 */
export type OrdOrdersPage = {
    /**
     * Items
     */
    items: Array<OrdOrderCard>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    counts: OrdOrderCounts;
    /**
     * Header
     */
    header: string;
};

/**
 * OrdOwnedRefsOut
 */
export type OrdOwnedRefsOut = {
    /**
     * Item Keys
     */
    item_keys: Array<string>;
};

/**
 * OrdPage[OrdAdminOrderRow]
 */
export type OrdPageOrdAdminOrderRow = {
    /**
     * Items
     */
    items: Array<OrdAdminOrderRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только объекты, связанные с вашими винодельнями: …» (7.2.2)
     */
    notice?: string | null;
};

/**
 * OrdPage[OrdDeliveryRow]
 */
export type OrdPageOrdDeliveryRow = {
    /**
     * Items
     */
    items: Array<OrdDeliveryRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только объекты, связанные с вашими винодельнями: …» (7.2.2)
     */
    notice?: string | null;
};

/**
 * OrdPage[OrdRevenueRow]
 */
export type OrdPageOrdRevenueRow = {
    /**
     * Items
     */
    items: Array<OrdRevenueRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только объекты, связанные с вашими винодельнями: …» (7.2.2)
     */
    notice?: string | null;
};

/**
 * OrdPage[OrdWinePartnerOut]
 */
export type OrdPageOrdWinePartnerOut = {
    /**
     * Items
     */
    items: Array<OrdWinePartnerOut>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только объекты, связанные с вашими винодельнями: …» (7.2.2)
     */
    notice?: string | null;
};

/**
 * OrdPanel
 */
export type OrdPanel = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Rows
     */
    rows: Array<OrdRow>;
    /**
     * Note
     */
    note?: string | null;
    link?: OrdLink | null;
};

/**
 * OrdPartnerBrief
 */
export type OrdPartnerBrief = {
    /**
     * Name
     */
    name: string | null;
    /**
     * Partner Order Number
     */
    partner_order_number: string;
    /**
     * Status
     */
    status: string;
    /**
     * Tracking Number
     */
    tracking_number: string | null;
    /**
     * Tracking Url
     */
    tracking_url: string | null;
};

/**
 * OrdPartnerItemIn
 */
export type OrdPartnerItemIn = {
    /**
     * Item Type
     */
    item_type: 'wine' | 'set' | 'drop' | 'blind_kit';
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Qty
     */
    qty: number;
};

/**
 * OrdPartnerOrderIn
 *
 * «Добавить заказ партнёра» (INT-DLV 16.10.3).
 */
export type OrdPartnerOrderIn = {
    /**
     * Wine Partner Id
     */
    wine_partner_id: string;
    /**
     * Partner Order Number
     */
    partner_order_number: string;
    /**
     * User Id
     */
    user_id: string;
    /**
     * Items
     */
    items: Array<OrdPartnerItemIn>;
    /**
     * Сумма у партнёра
     */
    amount: Money;
    /**
     * Status
     */
    status: 'passed_to_partner' | 'paid_at_partner' | 'partner_shipping' | 'delivered';
    /**
     * Tracking Number
     */
    tracking_number?: string | null;
};

/**
 * OrdPaymentAttemptRow
 */
export type OrdPaymentAttemptRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Seq
     */
    seq: number;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Provider
     */
    provider: string;
    /**
     * Provider Label
     */
    provider_label: string;
    /**
     * Environment
     */
    environment: string;
    /**
     * Environment Label
     */
    environment_label: string;
    /**
     * Method
     */
    method: string;
    /**
     * Method Label
     */
    method_label: string;
    /**
     * Card Mask
     */
    card_mask: string | null;
    amount: Money;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Failure Code
     */
    failure_code: string | null;
    /**
     * Failure Provider Code
     */
    failure_provider_code: string | null;
    /**
     * Provider Payment Id
     */
    provider_payment_id: string | null;
    log_link: OrdLink;
};

/**
 * OrdPaymentIn
 */
export type OrdPaymentIn = {
    payment_method: PaymentMethodCode;
    /**
     * Saved Card Id
     */
    saved_card_id?: string | null;
};

/**
 * OrdPaymentInfo
 */
export type OrdPaymentInfo = {
    /**
     * Label
     */
    label: string | null;
    /**
     * Paid At
     */
    paid_at: string | null;
    status: PaymentStatus;
};

/**
 * OrdPaymentMethodsOut
 */
export type OrdPaymentMethodsOut = {
    /**
     * Provider
     */
    provider: string | null;
    /**
     * Environment
     */
    environment: string | null;
    /**
     * Methods
     */
    methods: Array<OrdMethodOut>;
    /**
     * Saved Cards
     */
    saved_cards: Array<OrdSavedCardOut>;
    /**
     * Default Method Id
     */
    default_method_id: string | null;
    /**
     * Available
     */
    available: boolean;
    /**
     * Notice
     *
     * «Оплата временно недоступна — Попробуйте позже»
     */
    notice: string | null;
    /**
     * Test Mode Notice
     */
    test_mode_notice: string | null;
};

/**
 * OrdPaymentRefundRow
 */
export type OrdPaymentRefundRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Requested At
     */
    requested_at: string;
    amount: Money;
    /**
     * Initiated By
     */
    initiated_by: string;
    /**
     * Initiated By Label
     */
    initiated_by_label: string;
    /**
     * Reason
     */
    reason: string;
    /**
     * Reason Label
     */
    reason_label: string;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Provider Refund Id
     */
    provider_refund_id: string | null;
};

/**
 * OrdPaymentsBlockOut
 */
export type OrdPaymentsBlockOut = {
    /**
     * Attempts
     */
    attempts: Array<OrdPaymentAttemptRow>;
    /**
     * Refunds
     */
    refunds: Array<OrdPaymentRefundRow>;
    /**
     * Receipts
     */
    receipts: Array<OrdReceiptRow>;
    store_purchase: OrdStorePurchaseRow | null;
};

/**
 * OrdPayoutIn
 */
export type OrdPayoutIn = {
    /**
     * Entry Ids
     */
    entry_ids: Array<string>;
    /**
     * Paid On
     */
    paid_on: string;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * OrdPayoutOut
 */
export type OrdPayoutOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Partner Member Id
     */
    partner_member_id: string;
    /**
     * Partner Name
     */
    partner_name: string;
    amount: Money;
    /**
     * Paid On
     */
    paid_on: string;
    /**
     * Comment
     */
    comment: string | null;
    /**
     * Entries
     */
    entries: number;
    /**
     * Toast
     */
    toast: string;
};

/**
 * OrdPill
 *
 * Пилюля реестра админки (7.7.1): тон `ok` / `warn` / `bad` / `mute` / `gold`.
 */
export type OrdPill = {
    /**
     * Label
     */
    label: string;
    /**
     * Tone
     */
    tone: 'ok' | 'warn' | 'bad' | 'mute' | 'gold';
};

/**
 * OrdPriceChange
 */
export type OrdPriceChange = {
    /**
     * Item Key
     */
    item_key: string;
    /**
     * Title
     */
    title: string;
    old: Money;
    new: Money;
    /**
     * Toast
     */
    toast: string;
};

/**
 * OrdQuoteIn
 */
export type OrdQuoteIn = {
    group: CartGroup;
    /**
     * Address Id
     */
    address_id?: string | null;
    delivery_method?: DeliveryMethod | null;
};

/**
 * OrdQuoteItemOut
 */
export type OrdQuoteItemOut = {
    /**
     * Item Key
     */
    item_key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     */
    meta: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Qty
     */
    qty: number;
    unit_price: Money;
    line_total: Money;
    /**
     * Qty Label
     */
    qty_label: string;
};

/**
 * OrdQuoteItemRef
 */
export type OrdQuoteItemRef = {
    item_type: CartItemType;
    /**
     * Item Id
     */
    item_id: string;
    /**
     * Qty
     */
    qty?: number;
};

/**
 * OrdQuoteOut
 */
export type OrdQuoteOut = {
    group: CartGroup;
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
    /**
     * Lead
     */
    lead: string;
    /**
     * Items
     */
    items: Array<OrdQuoteItemOut>;
    subtotal: Money;
    discount: OrdDiscountOut | null;
    shipping: OrdShippingOut | null;
    total: Money;
    /**
     * Payment Methods
     */
    payment_methods: Array<OrdMethodOut>;
    /**
     * Saved Cards
     */
    saved_cards: Array<OrdSavedCardOut>;
    /**
     * Default Method Id
     */
    default_method_id: string | null;
    /**
     * Payments Available
     */
    payments_available: boolean;
    /**
     * Payments Notice
     */
    payments_notice: string | null;
    /**
     * Test Mode Notice
     */
    test_mode_notice: string | null;
    /**
     * Conditions
     */
    conditions: Array<OrdCondition>;
    /**
     * Price Changes
     */
    price_changes: Array<OrdPriceChange>;
    /**
     * Pay Button
     */
    pay_button: string;
};

/**
 * OrdReasonOption
 */
export type OrdReasonOption = {
    /**
     * Code
     */
    code: string;
    /**
     * Label
     */
    label: string;
};

/**
 * OrdReceiptOut
 */
export type OrdReceiptOut = {
    /**
     * Kind
     */
    kind: string;
    /**
     * Stage
     */
    stage: string;
    /**
     * Title
     */
    title: string;
    total: Money;
    /**
     * Status
     */
    status: string;
    /**
     * Ofd Url
     */
    ofd_url: string | null;
    /**
     * Registered At
     */
    registered_at: string | null;
};

/**
 * OrdReceiptRow
 */
export type OrdReceiptRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Kind Label
     */
    kind_label: string;
    total: Money;
    /**
     * Status
     */
    status: string;
    /**
     * Fiscal
     */
    fiscal: string | null;
    /**
     * Ofd Url
     */
    ofd_url: string | null;
};

/**
 * OrdReceiptsOut
 */
export type OrdReceiptsOut = {
    /**
     * Items
     */
    items: Array<OrdReceiptOut>;
    /**
     * Toast
     */
    toast: string | null;
};

/**
 * OrdRefundBrief
 */
export type OrdRefundBrief = {
    /**
     * Id
     */
    id: string;
    amount: Money;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * OrdRefundIn
 */
export type OrdRefundIn = {
    reason: RefundReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * OrdResultScreen
 *
 * Тексты экранов `pay-ok` / `pay-fail` (7.6.4, 7.6.5).
 */
export type OrdResultScreen = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    /**
     * Amount Label
     */
    amount_label: string;
    chip: OrdChip;
    /**
     * Steps
     */
    steps: Array<OrdNextStep>;
    /**
     * Primary Button
     */
    primary_button: string;
    /**
     * Primary Target
     */
    primary_target: string | null;
    /**
     * Secondary Button
     */
    secondary_button: string;
};

/**
 * OrdRevenueKpi
 */
export type OrdRevenueKpi = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    value: Money;
    /**
     * Note
     */
    note: string;
};

/**
 * OrdRevenueRecipient
 */
export type OrdRevenueRecipient = {
    /**
     * Member Id
     */
    member_id: string | null;
    /**
     * Name
     */
    name: string;
    role: PartnerRole | null;
    /**
     * Role Label
     */
    role_label: string | null;
    /**
     * Sellers
     */
    sellers: string | null;
    /**
     * Positions
     */
    positions: number;
    turnover: Money;
    received: Money;
    platform: Money | null;
    to_pay: Money | null;
};

/**
 * OrdRevenueRow
 */
export type OrdRevenueRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Sold At
     */
    sold_at: string;
    /**
     * Order Id
     */
    order_id: string;
    /**
     * Order Number
     */
    order_number: number;
    /**
     * Order Label
     */
    order_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    group: CartGroup;
    /**
     * Group Label
     */
    group_label: string;
    /**
     * Is Service Line
     */
    is_service_line: boolean;
    /**
     * Partner Member Id
     */
    partner_member_id: string | null;
    /**
     * Partner Name
     */
    partner_name: string;
    partner_role: PartnerRole | null;
    partner_link: OrdLink | null;
    amount: Money;
    /**
     * Share Pct
     */
    share_pct: number;
    /**
     * Share Label
     */
    share_label: string | null;
    partner_amount: Money | null;
    platform_amount: Money | null;
    payout_status: PayoutStatus;
    payout_pill: OrdPill;
    /**
     * Payout Id
     */
    payout_id: string | null;
    /**
     * Selectable
     */
    selectable: boolean;
};

/**
 * OrdRevenueSummaryOut
 */
export type OrdRevenueSummaryOut = {
    /**
     * Period Label
     */
    period_label: string;
    /**
     * Kpis
     */
    kpis: Array<OrdRevenueKpi>;
    /**
     * Recipients
     */
    recipients: Array<OrdRevenueRecipient>;
    /**
     * Footer
     */
    footer: string;
    /**
     * Months
     *
     * Месяцы с продажами (YYYY-MM) для фильтра «Месяц»
     */
    months: Array<string>;
    /**
     * Recipients Filter
     */
    recipients_filter: Array<{
        [key: string]: string;
    }>;
};

/**
 * OrdRow
 *
 * Строка «подпись — значение» карточки.
 */
export type OrdRow = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
    link?: OrdLink | null;
    /**
     * Hint
     */
    hint?: string | null;
};

/**
 * OrdSavedCardOut
 */
export type OrdSavedCardOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Brand
     */
    brand: string;
    /**
     * Last4
     */
    last4: string | null;
};

/**
 * OrdSessionOut
 */
export type OrdSessionOut = {
    /**
     * Id
     */
    id: string;
    status: CheckoutStatus;
    group: CartGroup;
    subtotal: Money;
    discount_total: Money;
    shipping_total: Money;
    total: Money;
    /**
     * Hold Expires At
     */
    hold_expires_at: string | null;
};

/**
 * OrdShareLine
 */
export type OrdShareLine = {
    group: CartGroup;
    /**
     * Label
     */
    label: string;
    /**
     * Partner Pct
     */
    partner_pct: number;
    /**
     * Platform Pct
     */
    platform_pct: number;
    /**
     * Text
     */
    text: string;
};

/**
 * OrdSharesIn
 */
export type OrdSharesIn = {
    /**
     * Learn Pct
     */
    learn_pct: number;
    /**
     * Wine Pct
     */
    wine_pct: number;
    /**
     * Exp Pct
     */
    exp_pct: number;
};

/**
 * OrdSharesOut
 */
export type OrdSharesOut = {
    /**
     * Member Id
     */
    member_id: string;
    role: PartnerRole;
    /**
     * Custom
     */
    custom: boolean;
    /**
     * Note
     */
    note: string;
    /**
     * Learn Pct
     */
    learn_pct: number;
    /**
     * Wine Pct
     */
    wine_pct: number;
    /**
     * Exp Pct
     */
    exp_pct: number;
    /**
     * Lines
     */
    lines: Array<OrdShareLine>;
    /**
     * Can Update
     */
    can_update: boolean;
    revenue_link: OrdLink;
};

/**
 * OrdShipmentInfo
 *
 * Отправление DHL заказа (INT-DLV 16.9.4): несколько точек отправки — несколько отправлений
 * («Отправление {index} из {total} · {позиции}»).
 */
export type OrdShipmentInfo = {
    /**
     * Id
     */
    id: string;
    /**
     * Index
     */
    index: number;
    /**
     * Total
     */
    total: number;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Tracking Number
     */
    tracking_number: string | null;
    /**
     * Tracking Url
     *
     * Ссылка DHL на языке пользователя
     */
    tracking_url: string | null;
    /**
     * Estimated Delivery At
     */
    estimated_delivery_at: string | null;
    /**
     * Delivered At
     */
    delivered_at: string | null;
    /**
     * Items
     */
    items: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Exception
     *
     * Описание события DHL при проблеме доставки
     */
    exception: string | null;
    /**
     * Exception At
     */
    exception_at: string | null;
};

/**
 * OrdShippingOption
 */
export type OrdShippingOption = {
    /**
     * Carrier
     */
    carrier: string;
    /**
     * Product Code
     */
    product_code: string;
    price: Money | null;
    customer_price: Money;
    /**
     * Estimated Delivery At
     */
    estimated_delivery_at: string | null;
    /**
     * Transit Text
     */
    transit_text: string;
};

/**
 * OrdShippingOut
 */
export type OrdShippingOut = {
    cost: Money;
    /**
     * Cost Label
     */
    cost_label: string;
    /**
     * Eta From
     */
    eta_from: string | null;
    /**
     * Eta To
     */
    eta_to: string | null;
    /**
     * Transit Text
     */
    transit_text: string;
    /**
     * Eta Line
     */
    eta_line: string | null;
    /**
     * Term Line
     */
    term_line: string | null;
    /**
     * Fallback
     */
    fallback: boolean;
    /**
     * Pickup Available
     */
    pickup_available: boolean;
    /**
     * Pickup Label
     */
    pickup_label: string | null;
    /**
     * Courier Label
     */
    courier_label: string;
    delivery_method: DeliveryMethod;
    /**
     * Address Id
     */
    address_id: string | null;
    /**
     * Address Label
     */
    address_label: string | null;
    /**
     * Gift Wrap Label
     */
    gift_wrap_label: string;
    /**
     * Age Check
     */
    age_check: string;
};

/**
 * OrdShippingQuoteIn
 */
export type OrdShippingQuoteIn = {
    address: OrdAddressValidateIn;
    /**
     * Items
     */
    items?: Array<OrdQuoteItemRef>;
};

/**
 * OrdShippingQuoteOut
 */
export type OrdShippingQuoteOut = {
    /**
     * Options
     */
    options: Array<OrdShippingOption>;
    /**
     * Fallback
     */
    fallback: boolean;
};

/**
 * OrdStageStep
 */
export type OrdStageStep = {
    status: OrderStatus;
    /**
     * Label
     */
    label: string;
    /**
     * Description
     */
    description: string | null;
    /**
     * Done
     */
    done: boolean;
    /**
     * Current
     */
    current: boolean;
    /**
     * Caption
     */
    caption?: string | null;
};

/**
 * OrdStages
 */
export type OrdStages = {
    /**
     * Title
     */
    title: string;
    /**
     * Steps
     */
    steps: Array<OrdStageStep>;
};

/**
 * OrdStatusIn
 */
export type OrdStatusIn = {
    to_status: OrderStatus;
    refund?: OrdRefundIn | null;
    /**
     * Reason
     *
     * Причина отката этапа партнёрского заказа
     */
    reason?: string | null;
    /**
     * Confirm
     *
     * Подтверждение «Доставлен» до передачи в СДЭК (INT-DLV 16.5.2)
     */
    confirm?: boolean;
};

/**
 * OrdStatusModal
 */
export type OrdStatusModal = {
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
    /**
     * Lead
     */
    lead: string;
    /**
     * Note
     */
    note: string;
    /**
     * Options
     */
    options: Array<OrdStatusOption>;
    /**
     * Refund Reasons
     */
    refund_reasons: Array<OrdReasonOption>;
    refund_amount: Money | null;
    /**
     * Refund Line
     */
    refund_line: string | null;
    /**
     * Reason Required For
     *
     * Этапы, для которых нужна причина (откат партнёрского)
     */
    reason_required_for?: Array<OrderStatus>;
};

/**
 * OrdStatusOption
 */
export type OrdStatusOption = {
    status: OrderStatus;
    /**
     * Label
     */
    label: string;
    /**
     * Description
     */
    description: string | null;
    /**
     * Enabled
     */
    enabled: boolean;
    /**
     * Hint
     */
    hint: string | null;
};

/**
 * OrdStatusResult
 */
export type OrdStatusResult = {
    /**
     * Id
     */
    id: string;
    status: OrderStatus;
    payment_status: PaymentStatus;
    refund: OrdRefundBrief | null;
    /**
     * Toast
     */
    toast: string;
    card: OrdAdminOrderCard;
};

/**
 * OrdStorePurchaseRow
 *
 * 15.12.4 «Покупка в сторе»: стор, среда, транзакция, цена в сторе, витрина, статус,
 * подтверждение (Google).
 */
export type OrdStorePurchaseRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Store
     */
    store: string;
    /**
     * Store Label
     */
    store_label: string;
    /**
     * Environment
     */
    environment: string;
    /**
     * Environment Label
     */
    environment_label: string;
    /**
     * Store Transaction Id
     */
    store_transaction_id: string;
    /**
     * Price
     *
     * «Цена в сторе: {сумма} {валюта}» (BR-INT-PAY-35)
     */
    price: string | null;
    /**
     * Storefront
     */
    storefront: string | null;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Acknowledged
     *
     * Google: acknowledge выполнен; иначе null
     */
    acknowledged: boolean | null;
    /**
     * Purchased At
     */
    purchased_at: string;
    /**
     * Revoked At
     */
    revoked_at: string | null;
};

/**
 * OrdTimelineStep
 */
export type OrdTimelineStep = {
    /**
     * Code
     */
    code: string;
    /**
     * Title
     */
    title: string;
    /**
     * Detail
     */
    detail: string | null;
    /**
     * Done
     */
    done: boolean;
    /**
     * At
     */
    at?: string | null;
};

/**
 * OrdTotals
 */
export type OrdTotals = {
    /**
     * Items Label
     */
    items_label: string;
    items: Money;
    discount: Money | null;
    /**
     * Discount Label
     */
    discount_label: string | null;
    /**
     * Shipping Label
     */
    shipping_label: string | null;
    /**
     * Final Label
     */
    final_label: string;
    final: Money;
    /**
     * Payment Line
     */
    payment_line: string | null;
};

/**
 * OrdTrackingEntry
 */
export type OrdTrackingEntry = {
    /**
     * Carrier
     */
    carrier: string;
    /**
     * Tracking Number
     */
    tracking_number: string | null;
    /**
     * Tracking Url
     */
    tracking_url: string | null;
    /**
     * Estimated From
     */
    estimated_from: string | null;
    /**
     * Estimated To
     */
    estimated_to: string | null;
    /**
     * Events
     */
    events: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * OrdTrackingOut
 */
export type OrdTrackingOut = {
    /**
     * Steps
     */
    steps: Array<OrdTimelineStep>;
    /**
     * Tracking
     */
    tracking: Array<OrdTrackingEntry>;
    /**
     * Toast
     *
     * «Трек придёт в пуше…», если трека ещё нет (BR-ORD-48)
     */
    toast: string | null;
};

/**
 * OrdWinePartnerCan
 */
export type OrdWinePartnerCan = {
    /**
     * Update
     */
    update: boolean;
    /**
     * Delete
     */
    delete: boolean;
};

/**
 * OrdWinePartnerIn
 */
export type OrdWinePartnerIn = {
    /**
     * Name
     */
    name: string;
    /**
     * Sells
     */
    sells: string;
    /**
     * Receives
     */
    receives?: string | null;
    /**
     * Reward
     */
    reward?: string | null;
    status: WinePartnerStatus;
};

/**
 * OrdWinePartnerOut
 */
export type OrdWinePartnerOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Sells
     */
    sells: string;
    /**
     * Link
     *
     * Адрес перехода из настроек интеграции `partner_tbn`
     */
    link: string | null;
    /**
     * Link Hint
     */
    link_hint: string;
    /**
     * Receives
     */
    receives: string | null;
    /**
     * Reward
     */
    reward: string | null;
    status: WinePartnerStatus;
    status_pill: OrdPill;
    /**
     * Orders Count
     */
    orders_count: number;
    can: OrdWinePartnerCan;
};

/**
 * OrdWinePartnerPatchIn
 */
export type OrdWinePartnerPatchIn = {
    /**
     * Name
     */
    name?: string | null;
    /**
     * Sells
     */
    sells?: string | null;
    /**
     * Receives
     */
    receives?: string | null;
    /**
     * Reward
     */
    reward?: string | null;
    status?: WinePartnerStatus | null;
};

/**
 * OrderBrief
 */
export type OrderBrief = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string | null;
    /**
     * Type
     */
    type?: string | null;
    amount?: Money | null;
};

/**
 * OrderDeliveryStatus
 */
export const OrderDeliveryStatus = {
    Preparing: 'preparing',
    InTransit: 'in_transit',
    ReadyForPickup: 'ready_for_pickup',
    Delivered: 'delivered',
    Overdue: 'overdue',
    Cancelled: 'cancelled'
} as const;

/**
 * OrderDeliveryStatus
 */
export type OrderDeliveryStatus = typeof OrderDeliveryStatus[keyof typeof OrderDeliveryStatus];

/**
 * OrderGroup
 */
export const OrderGroup = {
    Learning: 'learning',
    Wine: 'wine',
    Experience: 'experience',
    Bonus: 'bonus'
} as const;

/**
 * OrderGroup
 */
export type OrderGroup = typeof OrderGroup[keyof typeof OrderGroup];

/**
 * OrderRefOut
 */
export type OrderRefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     */
    number: string | null;
};

/**
 * OrderStatus
 *
 * Этапы всех потоков (7.5.1); какие допустимы потоку — `flows.STAGES`.
 */
export const OrderStatus = {
    Created: 'created',
    Paid: 'paid',
    Packing: 'packing',
    Packed: 'packed',
    Shipped: 'shipped',
    Delivered: 'delivered',
    ReadyForPickup: 'ready_for_pickup',
    PickedUp: 'picked_up',
    AccessGranted: 'access_granted',
    SeatConfirmed: 'seat_confirmed',
    EntryConfirmed: 'entry_confirmed',
    Attended: 'attended',
    NoShow: 'no_show',
    Agreed: 'agreed',
    SeatsConfirmed: 'seats_confirmed',
    TripCompleted: 'trip_completed',
    Requested: 'requested',
    BonusAgreed: 'bonus_agreed',
    Fulfilled: 'fulfilled',
    PassedToPartner: 'passed_to_partner',
    PaidAtPartner: 'paid_at_partner',
    PartnerShipping: 'partner_shipping',
    Cancelled: 'cancelled',
    Refunded: 'refunded'
} as const;

/**
 * OrderStatus
 *
 * Этапы всех потоков (7.5.1); какие допустимы потоку — `flows.STAGES`.
 */
export type OrderStatus = typeof OrderStatus[keyof typeof OrderStatus];

/**
 * OrderType
 */
export const OrderType = {
    WineBottle: 'wine_bottle',
    WineSet: 'wine_set',
    WineAuthorSet: 'wine_author_set',
    WineDrop: 'wine_drop',
    WineBlindKit: 'wine_blind_kit',
    LearningCourse: 'learning_course',
    LearningLesson: 'learning_lesson',
    ExpOnlineTasting: 'exp_online_tasting',
    ExpWineryTasting: 'exp_winery_tasting',
    ExpBlindOffline: 'exp_blind_offline',
    ExpExcursion: 'exp_excursion',
    ExpTrip: 'exp_trip',
    BonusExchange: 'bonus_exchange'
} as const;

/**
 * OrderType
 */
export type OrderType = typeof OrderType[keyof typeof OrderType];

/**
 * OriginCan
 */
export type OriginCan = {
    /**
     * Update
     */
    update?: boolean;
};

/**
 * OriginCreateIn
 */
export type OriginCreateIn = {
    /**
     * Code
     */
    code: string;
    level: OriginLevel;
    /**
     * До 200 символов
     */
    full_name: LocalizedText;
    /**
     * Country Id
     */
    country_id: string;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * OriginLevel
 */
export const OriginLevel = {
    National: 'national',
    Highest: 'highest',
    Regional: 'regional',
    Local: 'local',
    Land: 'land'
} as const;

/**
 * OriginLevel
 */
export type OriginLevel = typeof OriginLevel[keyof typeof OriginLevel];

/**
 * OriginOptionOut
 */
export type OriginOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Code
     */
    code: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Label
     */
    label: string;
};

/**
 * OriginOut
 */
export type OriginOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Code
     */
    code: string;
    full_name: LocalizedText | null;
    country: CountryRef;
    level: OriginLevel;
    /**
     * Level Label
     */
    level_label: string;
    /**
     * Comment
     */
    comment: string | null;
    can: OriginCan;
};

/**
 * OriginPatchIn
 */
export type OriginPatchIn = {
    /**
     * Code
     */
    code?: string | null;
    level?: OriginLevel | null;
    full_name?: LocalizedText | null;
    /**
     * Country Id
     */
    country_id?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * OwnedGroupOut
 */
export type OwnedGroupOut = {
    /**
     * Section
     */
    section: string;
    /**
     * Title
     */
    title: string;
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<OwnedItemOut>;
    link: SectionLinkOut | null;
};

/**
 * OwnedItemOut
 */
export type OwnedItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     */
    meta: string | null;
    /**
     * Status
     */
    status: string | null;
};

/**
 * OwnedSummaryOut
 */
export type OwnedSummaryOut = {
    /**
     * Groups
     *
     * Группы модулей-владельцев с записями участника; модуль без провайдера пока не показывает группу
     */
    groups: Array<OwnedGroupOut>;
    /**
     * Total
     */
    total: number;
};

/**
 * PageSectionOut
 */
export type PageSectionOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Number
     */
    number: string;
    /**
     * Title
     */
    title: string;
    /**
     * Count
     */
    count: number;
    /**
     * Items
     */
    items: Array<SectionItemOut>;
};

/**
 * Page[AlertOut]
 */
export type PageAlertOut = {
    /**
     * Items
     */
    items: Array<AlertOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[AppMarketOut]
 */
export type PageAppMarketOut = {
    /**
     * Items
     */
    items: Array<AppModulesGeoSchemasAdminAppMarketOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[AttendanceRow]
 */
export type PageAttendanceRow = {
    /**
     * Items
     */
    items: Array<AttendanceRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только …» для не-администратора (9.2.2)
     */
    notice?: string | null;
};

/**
 * Page[BookingRow]
 */
export type PageBookingRow = {
    /**
     * Items
     */
    items: Array<BookingRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только …» для не-администратора (9.2.2)
     */
    notice?: string | null;
};

/**
 * Page[CareRow]
 */
export type PageCareRow = {
    /**
     * Items
     */
    items: Array<CareRow>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[CountryRowOut]
 */
export type PageCountryRowOut = {
    /**
     * Items
     */
    items: Array<CountryRowOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[ExcursionRow]
 */
export type PageExcursionRow = {
    /**
     * Items
     */
    items: Array<ExcursionRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только …» для не-администратора (9.2.2)
     */
    notice?: string | null;
};

/**
 * Page[MemberRow]
 */
export type PageMemberRow = {
    /**
     * Items
     */
    items: Array<MemberRow>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[OnboardingScreenRow]
 */
export type PageOnboardingScreenRow = {
    /**
     * Items
     */
    items: Array<OnboardingScreenRow>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[OriginOut]
 */
export type PageOriginOut = {
    /**
     * Items
     */
    items: Array<OriginOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[RegionRowOut]
 */
export type PageRegionRowOut = {
    /**
     * Items
     */
    items: Array<AppModulesGeoSchemasAdminRegionRowOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[SuppressionOut]
 */
export type PageSuppressionOut = {
    /**
     * Items
     */
    items: Array<SuppressionOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * Page[TastingRow]
 */
export type PageTastingRow = {
    /**
     * Items
     */
    items: Array<TastingRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только …» для не-администратора (9.2.2)
     */
    notice?: string | null;
};

/**
 * Page[TripRow]
 */
export type PageTripRow = {
    /**
     * Items
     */
    items: Array<TripRow>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только …» для не-администратора (9.2.2)
     */
    notice?: string | null;
};

/**
 * Page[UserRow]
 */
export type PageUserRow = {
    /**
     * Items
     */
    items: Array<UserRow>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
};

/**
 * PairIn
 */
export type PairIn = {
    label: LocalizedText;
    value: LocalizedText;
};

/**
 * PairOut
 */
export type PairOut = {
    label: LocalizedText;
    value: LocalizedText;
};

/**
 * PairingCategory
 */
export const PairingCategory = {
    RedMeat: 'red_meat',
    Game: 'game',
    Poultry: 'poultry',
    Fish: 'fish',
    Seafood: 'seafood',
    VegMushrooms: 'veg_mushrooms',
    HardCheese: 'hard_cheese',
    SoftCheese: 'soft_cheese',
    PastaRice: 'pasta_rice',
    Desserts: 'desserts',
    Aperitif: 'aperitif'
} as const;

/**
 * PairingCategory
 */
export type PairingCategory = typeof PairingCategory[keyof typeof PairingCategory];

/**
 * PairingsIn
 */
export type PairingsIn = {
    /**
     * Categories
     */
    categories?: Array<PairingCategory>;
    custom?: LocalizedText | null;
};

/**
 * PartnerApiAvailabilityIn
 */
export type PartnerApiAvailabilityIn = {
    /**
     * Items
     */
    items: Array<PartnerApiAvailabilityItemIn>;
};

/**
 * PartnerApiAvailabilityItemIn
 */
export type PartnerApiAvailabilityItemIn = {
    /**
     * Id
     */
    id: string;
    /**
     * Partner Sku
     */
    partner_sku?: string | null;
    /**
     * Availability
     */
    availability: 'available' | 'out_of_stock' | 'not_carried';
    /**
     * Stock
     */
    stock?: number | null;
    partner_price?: PartnerApiMoney | null;
};

/**
 * PartnerApiAvailabilityOut
 */
export type PartnerApiAvailabilityOut = {
    /**
     * Accepted
     */
    accepted: number;
    /**
     * Rejected
     */
    rejected: Array<PartnerApiRejected>;
};

/**
 * PartnerApiBuyerIn
 */
export type PartnerApiBuyerIn = {
    /**
     * Email
     */
    email?: string | null;
    /**
     * Phone
     */
    phone?: string | null;
    /**
     * Name
     */
    name?: string | null;
};

/**
 * PartnerApiCatalogItem
 *
 * Позиция фида; поля по типу — таблица 16.6.4 (лишние поля партнёр игнорирует).
 */
export type PartnerApiCatalogItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Sku
     */
    sku: string | null;
    /**
     * Type
     */
    type: 'wine' | 'set' | 'drop' | 'blind_kit';
    /**
     * Status
     */
    status: 'active' | 'removed';
    /**
     * Version
     */
    version: number;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Title
     */
    title: string;
    /**
     * Description
     */
    description: string;
    /**
     * Images
     */
    images: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Price Hint
     */
    price_hint: {
        [key: string]: unknown;
    } | null;
    /**
     * Volume L
     */
    volume_l: number | null;
    /**
     * Composition Hidden From Customer
     */
    composition_hidden_from_customer: boolean;
    /**
     * Age Restriction
     */
    age_restriction: number;
    /**
     * Restrictions
     */
    restrictions: Array<string>;
    /**
     * Availability
     */
    availability: string | null;
    /**
     * App Url
     */
    app_url: string | null;
    [key: string]: unknown;
};

/**
 * PartnerApiCatalogPage
 */
export type PartnerApiCatalogPage = {
    /**
     * Items
     */
    items: Array<PartnerApiCatalogItem>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Server Time
     */
    server_time: string;
};

/**
 * PartnerApiDeliveryIn
 */
export type PartnerApiDeliveryIn = {
    /**
     * Carrier
     */
    carrier?: 'cdek' | 'other' | null;
    /**
     * Method
     */
    method?: 'pickup_point' | 'courier' | null;
    /**
     * City
     */
    city?: string | null;
    /**
     * Point Code
     */
    point_code?: string | null;
    /**
     * Address Display
     */
    address_display?: string | null;
    /**
     * Recipient Name
     */
    recipient_name?: string | null;
    /**
     * Tracking Number
     */
    tracking_number?: string | null;
    /**
     * Estimated From
     */
    estimated_from?: string | null;
    /**
     * Estimated To
     */
    estimated_to?: string | null;
};

/**
 * PartnerApiEventIn
 */
export type PartnerApiEventIn = {
    /**
     * Event Id
     */
    event_id: string;
    /**
     * Status
     */
    status: 'awaiting_payment' | 'paid' | 'assembling' | 'handed_to_carrier' | 'in_delivery' | 'delivered' | 'cancelled' | 'refunded';
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Tracking Number
     */
    tracking_number?: string | null;
    /**
     * Estimated From
     */
    estimated_from?: string | null;
    /**
     * Estimated To
     */
    estimated_to?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Reason
     */
    reason?: string | null;
    refund?: PartnerApiRefundIn | null;
    /**
     * Carrier Reported Delivered At
     */
    carrier_reported_delivered_at?: string | null;
};

/**
 * PartnerApiEventOut
 */
export type PartnerApiEventOut = {
    /**
     * Applied
     */
    applied: boolean;
    /**
     * Status
     */
    status: string;
    /**
     * Previous Status
     */
    previous_status: string;
    /**
     * Tracking Url
     */
    tracking_url: string | null;
};

/**
 * PartnerApiHold
 */
export type PartnerApiHold = {
    /**
     * Hold Id
     */
    hold_id: string;
    /**
     * Qty
     */
    qty: number;
    /**
     * Expires At
     */
    expires_at: string;
};

/**
 * PartnerApiItemIn
 */
export type PartnerApiItemIn = {
    /**
     * Catalog Item Id
     */
    catalog_item_id?: string | null;
    /**
     * Partner Sku
     */
    partner_sku?: string | null;
    /**
     * Type
     */
    type: 'wine' | 'set' | 'drop' | 'blind_kit';
    /**
     * Title
     */
    title?: string | null;
    /**
     * Qty
     */
    qty: number;
    unit_price: PartnerApiMoney;
    line_total: PartnerApiMoney;
};

/**
 * PartnerApiJwksOut
 */
export type PartnerApiJwksOut = {
    /**
     * Keys
     *
     * JWK Set (P-256, ES256)
     */
    keys: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * PartnerApiMoney
 */
export type PartnerApiMoney = {
    /**
     * Amount
     *
     * Минимальные единицы валюты (копейки)
     */
    amount: number;
    /**
     * Currency
     */
    currency: string;
};

/**
 * PartnerApiOrderCreated
 */
export type PartnerApiOrderCreated = {
    /**
     * Order Id
     */
    order_id: string;
    /**
     * Order Number
     */
    order_number: string;
    /**
     * Partner Order Number
     */
    partner_order_number: string;
    /**
     * Match Status
     */
    match_status: string;
    /**
     * Status
     */
    status: string;
    /**
     * Tracking Url
     */
    tracking_url: string | null;
    /**
     * Warnings
     */
    warnings: Array<string>;
};

/**
 * PartnerApiOrderEventOut
 */
export type PartnerApiOrderEventOut = {
    /**
     * Event Id
     */
    event_id: string | null;
    /**
     * Source
     */
    source: string;
    /**
     * Status
     */
    status: string | null;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Received At
     */
    received_at: string;
    /**
     * Applied
     */
    applied: boolean;
    /**
     * Not Applied Reason
     */
    not_applied_reason: string | null;
    /**
     * Comment
     */
    comment: string | null;
};

/**
 * PartnerApiOrderIn
 */
export type PartnerApiOrderIn = {
    /**
     * Ref
     */
    ref?: string | null;
    /**
     * Partner Order Number
     */
    partner_order_number: string;
    /**
     * Ordered At
     */
    ordered_at: string;
    /**
     * Status
     */
    status: 'awaiting_payment' | 'paid' | 'assembling' | 'handed_to_carrier' | 'in_delivery';
    buyer?: PartnerApiBuyerIn | null;
    /**
     * Items
     */
    items: Array<PartnerApiItemIn>;
    totals: PartnerApiTotalsIn;
    payment: PartnerApiPaymentIn;
    delivery?: PartnerApiDeliveryIn | null;
    /**
     * Receipt Url
     */
    receipt_url?: string | null;
    /**
     * Hold Id
     */
    hold_id?: string | null;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * PartnerApiOrderOut
 */
export type PartnerApiOrderOut = {
    /**
     * Order Id
     */
    order_id: string;
    /**
     * Order Number
     */
    order_number: string;
    /**
     * Partner Order Number
     */
    partner_order_number: string;
    /**
     * Ordered At
     */
    ordered_at: string;
    /**
     * Status
     */
    status: string;
    /**
     * Status Set By
     */
    status_set_by: 'partner' | 'admin';
    /**
     * Match Status
     */
    match_status: string;
    /**
     * Delivered At
     */
    delivered_at: string | null;
    /**
     * Buyer
     */
    buyer: {
        [key: string]: unknown;
    } | null;
    /**
     * Items
     */
    items: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Totals
     */
    totals: {
        [key: string]: unknown;
    };
    /**
     * Payment
     */
    payment: {
        [key: string]: unknown;
    };
    /**
     * Delivery
     */
    delivery: {
        [key: string]: unknown;
    };
    /**
     * Tracking Url
     */
    tracking_url: string | null;
    /**
     * Receipt Url
     */
    receipt_url: string | null;
    /**
     * Comment
     */
    comment: string | null;
    /**
     * Is Test
     */
    is_test: boolean;
    /**
     * Updated At
     */
    updated_at: string;
    /**
     * Events
     */
    events: Array<PartnerApiOrderEventOut>;
};

/**
 * PartnerApiOrderPatchIn
 */
export type PartnerApiOrderPatchIn = {
    delivery?: PartnerApiDeliveryIn | null;
    /**
     * Receipt Url
     */
    receipt_url?: string | null;
    buyer?: PartnerApiBuyerIn | null;
    /**
     * Comment
     */
    comment?: string | null;
    /**
     * Items
     */
    items?: Array<PartnerApiItemIn> | null;
    totals?: PartnerApiTotalsIn | null;
};

/**
 * PartnerApiOrdersPage
 */
export type PartnerApiOrdersPage = {
    /**
     * Items
     */
    items: Array<PartnerApiOrderOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * PartnerApiPaymentIn
 */
export type PartnerApiPaymentIn = {
    /**
     * Method
     */
    method: 'card' | 'sbp' | 'sberpay' | 'tpay' | 'cash_on_delivery' | 'other';
    /**
     * Status
     */
    status: 'pending' | 'paid';
    /**
     * Paid At
     */
    paid_at?: string | null;
};

/**
 * PartnerApiPingOut
 */
export type PartnerApiPingOut = {
    /**
     * Ok
     */
    ok: boolean;
    /**
     * Partner
     */
    partner: string;
    mode: IntegrationMode;
    /**
     * Server Time
     */
    server_time: string;
};

/**
 * PartnerApiRefundIn
 */
export type PartnerApiRefundIn = {
    amount: PartnerApiMoney;
    /**
     * Refunded At
     */
    refunded_at?: string | null;
    /**
     * Reason
     */
    reason?: string | null;
};

/**
 * PartnerApiRejected
 */
export type PartnerApiRejected = {
    /**
     * Id
     */
    id: string;
    /**
     * Error
     */
    error: string;
};

/**
 * PartnerApiTotalsIn
 */
export type PartnerApiTotalsIn = {
    items_total: PartnerApiMoney;
    delivery_total: PartnerApiMoney;
    discount_total: PartnerApiMoney;
    grand_total: PartnerApiMoney;
};

/**
 * PartnerApiVerifyIn
 */
export type PartnerApiVerifyIn = {
    /**
     * Ref
     */
    ref: string;
    /**
     * Qty
     */
    qty?: number | null;
    /**
     * Hold
     */
    hold?: boolean;
};

/**
 * PartnerApiVerifyItem
 */
export type PartnerApiVerifyItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Type
     */
    type: 'wine' | 'set' | 'drop' | 'blind_kit';
    /**
     * Availability
     */
    availability: string;
};

/**
 * PartnerApiVerifyOut
 */
export type PartnerApiVerifyOut = {
    /**
     * Valid
     */
    valid: boolean;
    /**
     * Handoff Id
     */
    handoff_id: string;
    /**
     * Partner User Ref
     */
    partner_user_ref: string;
    item: PartnerApiVerifyItem;
    /**
     * Qty Allowed
     */
    qty_allowed: number;
    /**
     * Eligible Early Access
     */
    eligible_early_access: boolean;
    /**
     * Sale Window
     */
    sale_window: 'early' | 'general' | null;
    hold: PartnerApiHold | null;
    /**
     * Expires At
     */
    expires_at: string;
};

/**
 * PartnerRole
 */
export const PartnerRole = { Expert: 'expert', Winemaker: 'winemaker' } as const;

/**
 * PartnerRole
 */
export type PartnerRole = typeof PartnerRole[keyof typeof PartnerRole];

/**
 * PassportCellOut
 */
export type PassportCellOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Caption
     */
    caption: string;
    /**
     * Target
     */
    target?: string | null;
};

/**
 * PassportCellsOut
 */
export type PassportCellsOut = {
    xp: PassportCellOut;
    accuracy: PassportCellOut;
    stamps: PassportCellOut;
    specializations: PassportCellOut;
};

/**
 * PassportLevelOut
 */
export type PassportLevelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Index
     *
     * 0 — начальный уровень, далее 1…K
     */
    index: number;
    /**
     * Name
     */
    name: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Min Xp
     */
    min_xp: number;
    /**
     * Min Xp Label
     */
    min_xp_label: string;
    /**
     * Perks
     *
     * Пункты «Что даёт»
     */
    perks: Array<string>;
    /**
     * Learning Pct
     */
    learning_pct: number;
    /**
     * Wine Pct
     */
    wine_pct: number;
    /**
     * Learning Label
     *
     * «−5 %» или «нет»
     */
    learning_label: string;
    /**
     * Wine Label
     */
    wine_label: string;
    /**
     * Bonuses Label
     *
     * «1 / 1 000» у уровня с бонусами, иначе «—»
     */
    bonuses_label: string;
    one_time_reward: OneTimeRewardOut;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * State Label
     *
     * «уровень открыт» / «ещё N XP» (для пользователя)
     */
    state_label?: string | null;
    /**
     * Held
     */
    held: boolean;
    /**
     * Privileges
     */
    privileges: Array<string>;
    /**
     * Progress Label
     */
    progress_label: string;
};

/**
 * PassportOut
 */
export type PassportOut = {
    /**
     * Guest
     */
    guest: boolean;
    /**
     * Owner Name
     *
     * null — гость (приложение показывает «Гость»)
     */
    owner_name: string | null;
    /**
     * Owner Label
     */
    owner_label: string;
    /**
     * Issued At
     */
    issued_at: string;
    tier: PassportTierOut | null;
    next_tier: NextTierOut | null;
    /**
     * Need
     */
    need: Array<NeedOut>;
    /**
     * Xp
     */
    xp: number;
    /**
     * Accuracy Pct
     */
    accuracy_pct: number;
    /**
     * Accuracy Points
     *
     * Накопленные баллы точности (TST)
     */
    accuracy_points: number;
    /**
     * Tastings Count
     */
    tastings_count: number;
    /**
     * Stamps Count
     */
    stamps_count: number;
    /**
     * Specializations Count
     */
    specializations_count: number;
    cells: PassportCellsOut;
    /**
     * Stamp Slots
     *
     * 6 слотов; null — пустой слот
     */
    stamp_slots: Array<StampSlotOut | null>;
    /**
     * Stamps
     */
    stamps: Array<GamStampOut>;
    /**
     * Stamps Text
     */
    stamps_text: string;
    /**
     * Stamps Empty Text
     */
    stamps_empty_text: string;
    /**
     * Mrz
     *
     * Две строки по 26 символов
     */
    mrz: Array<string>;
    level: PassportLevelOut | null;
    /**
     * Ladder Text
     */
    ladder_text: string;
    /**
     * Ladder
     */
    ladder: Array<LadderTierOut>;
    /**
     * Guest Banner
     */
    guest_banner: string | null;
    /**
     * Share Targets
     */
    share_targets: Array<ShareTargetOut>;
};

/**
 * PassportTierOut
 */
export type PassportTierOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Title
     *
     * «III Первооткрыватель»
     */
    title: string;
    /**
     * Xp Min
     */
    xp_min: number;
    /**
     * Accuracy Min
     */
    accuracy_min: number;
    /**
     * Unlocks
     */
    unlocks: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Xp Label
     *
     * «3 000 XP», у первой — «старт»
     */
    xp_label: string;
    /**
     * Accuracy Label
     *
     * «точность 35», у первой — «без условий»
     */
    accuracy_label: string;
    /**
     * Reached At
     */
    reached_at: string | null;
};

/**
 * PasswordChangeIn
 */
export type PasswordChangeIn = {
    /**
     * Current Password
     */
    current_password: string;
    /**
     * New Password
     */
    new_password: string;
};

/**
 * PasswordLoginIn
 */
export type PasswordLoginIn = {
    /**
     * Email
     */
    email: string;
    /**
     * Password
     */
    password: string;
};

/**
 * PasswordResetIn
 */
export type PasswordResetIn = {
    /**
     * Verification Token
     */
    verification_token: string;
    /**
     * New Password
     */
    new_password: string;
};

/**
 * PasswordResetOut
 */
export type PasswordResetOut = {
    credentials: CredentialsOut;
};

/**
 * PathCountryOut
 */
export type PathCountryOut = {
    /**
     * Code
     */
    code: string;
    /**
     * Name
     */
    name: string;
    /**
     * Illustration Url
     */
    illustration_url: string | null;
    /**
     * Picture Url
     */
    picture_url: string | null;
    /**
     * Short Label
     */
    short_label: string;
    /**
     * Regions Count
     */
    regions_count: number;
    /**
     * Lessons Count
     */
    lessons_count: number;
    /**
     * Meta
     *
     * «{N} регионов · {X} XP» (X — по данным GAM)
     */
    meta: string;
};

/**
 * PathCountryQuizOut
 */
export type PathCountryQuizOut = {
    /**
     * Test Id
     */
    test_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Questions
     */
    questions: number;
    /**
     * State
     */
    state: 'not_started' | 'done';
    /**
     * Score
     */
    score: number | null;
    /**
     * Total
     */
    total: number | null;
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Xp Received
     */
    xp_received: boolean;
    /**
     * Label
     */
    label: string;
    /**
     * Target
     */
    target: string;
};

/**
 * PathCountryRefOut
 */
export type PathCountryRefOut = {
    /**
     * Code
     *
     * ISO 3166-1 alpha-2, нижний регистр
     */
    code: string;
    /**
     * Name
     */
    name: string;
};

/**
 * PathIntroOut
 */
export type PathIntroOut = {
    /**
     * Course Id
     */
    course_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Completed
     */
    completed: boolean;
    /**
     * Lessons Total
     */
    lessons_total: number;
    /**
     * Done
     */
    done: number;
    /**
     * Pct
     */
    pct: number;
    /**
     * Next Lesson Id
     */
    next_lesson_id: string | null;
    /**
     * Next Number
     */
    next_number: string | null;
    /**
     * Free Label
     */
    free_label: string;
    /**
     * Label
     *
     * «Шаг первый» / «Урок {N} из 17» / «Вводный курс пройден …»
     */
    label: string;
    /**
     * Button
     *
     * «Начать» / «Продолжить»
     */
    button: string;
    /**
     * Xp Per Lesson
     */
    xp_per_lesson: number | null;
    /**
     * Xp Earned
     */
    xp_earned: number | null;
    /**
     * Right Label
     *
     * «{N} XP» (набранные за курс) или «17 уроков»
     */
    right_label: string;
    /**
     * Target
     */
    target: string;
};

/**
 * PathLevelCheckOut
 */
export type PathLevelCheckOut = {
    /**
     * Test Id
     */
    test_id: string;
    /**
     * Questions
     */
    questions: number;
    /**
     * Done
     */
    done: boolean;
    /**
     * Score
     */
    score: number | null;
    /**
     * Total
     */
    total: number | null;
    /**
     * Label
     */
    label: string;
    /**
     * Target
     */
    target: string;
};

/**
 * PathOut
 */
export type PathOut = {
    intro: PathIntroOut | null;
    level_check: PathLevelCheckOut | null;
    /**
     * Collected
     */
    collected: number;
    /**
     * Regions Total
     */
    regions_total: number;
    /**
     * Collected Label
     */
    collected_label: string;
    /**
     * Default Continent
     */
    default_continent: string;
    /**
     * Continents
     */
    continents: Array<ContinentOut>;
};

/**
 * PayOut
 */
export type PayOut = {
    /**
     * Order Id
     */
    order_id: string;
    /**
     * Order Number
     */
    order_number: string;
    amount: Money;
    /**
     * Hold Until
     */
    hold_until: string | null;
};

/**
 * PaymentGateway
 */
export const PaymentGateway = {
    Sber: 'sber',
    Tbank: 'tbank',
    Alfa: 'alfa',
    Stripe: 'stripe'
} as const;

/**
 * PaymentGateway
 */
export type PaymentGateway = typeof PaymentGateway[keyof typeof PaymentGateway];

/**
 * PaymentMethod
 */
export const PaymentMethod = {
    Card: 'card',
    Sberpay: 'sberpay',
    Sbp: 'sbp',
    Tpay: 'tpay',
    ApplePay: 'apple_pay',
    GooglePay: 'google_pay'
} as const;

/**
 * PaymentMethod
 */
export type PaymentMethod = typeof PaymentMethod[keyof typeof PaymentMethod];

/**
 * PaymentMethodCode
 *
 * Способ оплаты попытки (15.4.1 `method`, ORD `CheckoutSession.payment_method`).
 */
export const PaymentMethodCode = {
    Card: 'card',
    SavedCard: 'saved_card',
    Sberpay: 'sberpay',
    Sbp: 'sbp',
    Tpay: 'tpay',
    ApplePay: 'apple_pay',
    GooglePay: 'google_pay'
} as const;

/**
 * PaymentMethodCode
 *
 * Способ оплаты попытки (15.4.1 `method`, ORD `CheckoutSession.payment_method`).
 */
export type PaymentMethodCode = typeof PaymentMethodCode[keyof typeof PaymentMethodCode];

/**
 * PaymentStatus
 */
export const PaymentStatus = {
    NotRequired: 'not_required',
    Awaiting: 'awaiting',
    Paid: 'paid',
    RefundPending: 'refund_pending',
    PartiallyRefunded: 'partially_refunded',
    Refunded: 'refunded',
    PaidAtPartner: 'paid_at_partner',
    CancelledAtPartner: 'cancelled_at_partner',
    RefundedAtPartner: 'refunded_at_partner',
    PaidWithBonus: 'paid_with_bonus'
} as const;

/**
 * PaymentStatus
 */
export type PaymentStatus = typeof PaymentStatus[keyof typeof PaymentStatus];

/**
 * PaymentTilesOut
 *
 * Плитки карточки платёжного шлюза (15.12.1).
 */
export type PaymentTilesOut = {
    /**
     * Methods
     *
     * Включённые способы оплаты (подписи)
     */
    methods: Array<string>;
    /**
     * Pending Count
     *
     * Незавершённых платежей (created, pending, authorized)
     */
    pending_count: number;
    /**
     * Last Webhook At
     *
     * Последнее уведомление провайдера
     */
    last_webhook_at: string | null;
    /**
     * Has Recent Payments
     *
     * Есть незавершённые оплаты или оплаты моложе окна возвратов (BR-INT-PAY-37): «Отключить» недоступно
     */
    has_recent_payments: boolean;
};

/**
 * PaymentsClientOut
 *
 * Публичные параметры оплаты для клиента (15.7: `Stripe.publishableKey`,
 * `Stripe.merchantIdentifier`, `Stripe.urlScheme`; Google Pay `testEnv`). Пока Stripe не
 * включён и не настроен — `null`.
 */
export type PaymentsClientOut = {
    /**
     * Stripe Publishable Key
     */
    stripe_publishable_key?: string | null;
    /**
     * Apple Pay Merchant Id
     */
    apple_pay_merchant_id?: string | null;
    /**
     * Google Pay Test Env
     */
    google_pay_test_env?: boolean | null;
    /**
     * Url Scheme
     *
     * Схема deep link возврата (`payments.return_scheme_app`)
     */
    url_scheme: string;
};

/**
 * PayoutStatus
 *
 * BR-ORD-105 — вычисляется, не хранится.
 */
export const PayoutStatus = {
    NotAccrued: 'not_accrued',
    AwaitingPayment: 'awaiting_payment',
    NoPayout: 'no_payout',
    AfterFulfilment: 'after_fulfilment',
    AfterEvent: 'after_event',
    ToPay: 'to_pay',
    Paid: 'paid'
} as const;

/**
 * PayoutStatus
 *
 * BR-ORD-105 — вычисляется, не хранится.
 */
export type PayoutStatus = typeof PayoutStatus[keyof typeof PayoutStatus];

/**
 * PaywallOut
 */
export type PaywallOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    price: Money | null;
    lesson_offer: LearnOfferOut | null;
    course_card: CourseCardOut | null;
};

/**
 * PeopleOut
 */
export type PeopleOut = {
    /**
     * Tab
     */
    tab: 'experts' | 'makers';
    /**
     * Regions
     */
    regions: Array<PeopleRegionOut>;
    /**
     * Following
     */
    following: Array<PersonRowOut>;
    /**
     * Items
     */
    items: Array<PersonRowOut>;
    /**
     * Total
     */
    total: number;
    /**
     * Following Total
     */
    following_total: number;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Empty Text
     */
    empty_text: string | null;
};

/**
 * PeopleRegionOut
 */
export type PeopleRegionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
};

/**
 * PersonOut
 */
export type PersonOut = {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Member Id
     *
     * Участник команды — страница `expert:` / `maker:` (CNT)
     */
    member_id?: string | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Role
     */
    role: string;
    /**
     * Avatar Url
     */
    avatar_url?: string | null;
    /**
     * Для винодела — винодельня
     */
    winery?: ZoneBrief | null;
};

/**
 * PersonRowOut
 */
export type PersonRowOut = {
    /**
     * Member Id
     */
    member_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Role Label
     */
    role_label: string;
    /**
     * Kicker
     *
     * Роль; у винодела — «{роль} · {винодельня}»
     */
    kicker: string;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * Initial
     */
    initial: string;
    /**
     * Meta
     *
     * Счётчики (BR-CNT-63) или регион
     */
    meta: string | null;
    /**
     * Subscribers Count
     */
    subscribers_count: number;
    /**
     * Subscribers Label
     */
    subscribers_label: string;
    /**
     * Following
     */
    following: boolean;
    /**
     * Target
     */
    target: string;
};

/**
 * PhoneCodeOut
 */
export type PhoneCodeOut = {
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Name
     */
    name: string;
    /**
     * Dial Code
     */
    dial_code: string;
    /**
     * Mask
     */
    mask: string | null;
    /**
     * Length
     *
     * Длина национального номера; null — переменная
     */
    length: number | null;
    /**
     * Is Default
     */
    is_default: boolean;
};

/**
 * PhoneCodesOut
 */
export type PhoneCodesOut = {
    /**
     * Default Dial Code
     */
    default_dial_code: string;
    /**
     * Items
     */
    items: Array<PhoneCodeOut>;
};

/**
 * PhoneIn
 *
 * Телефон: код страны (лист выбора) + номер; маска и длина — BR-ACC-13.
 */
export type PhoneIn = {
    /**
     * Dial Code
     */
    dial_code: string;
    /**
     * Number
     */
    number: string;
};

/**
 * PhoneOut
 */
export type PhoneOut = {
    /**
     * Dial Code
     */
    dial_code: string;
    /**
     * Number
     */
    number: string;
};

/**
 * PickerItemOut
 */
export type PickerItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Label
     *
     * «Название · Винтаж»
     */
    label: string;
    /**
     * Winery
     */
    winery: string;
    /**
     * Region
     */
    region: string;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    volume: BottleVolume;
    /**
     * Volume Label
     */
    volume_label: string;
    status: CatalogStatus;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * PickerOut
 */
export type PickerOut = {
    /**
     * Items
     */
    items: Array<PickerItemOut>;
};

/**
 * PlaybackOut
 */
export type PlaybackOut = {
    /**
     * Video Asset Id
     */
    video_asset_id: string;
    /**
     * Hls Url
     */
    hls_url: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Duration Sec
     */
    duration_sec: number | null;
    /**
     * Resume From Sec
     */
    resume_from_sec: number;
    /**
     * Thumbnail Url
     */
    thumbnail_url: string | null;
};

/**
 * PointsRegionOut
 */
export type PointsRegionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    center: GeoPointOut;
    /**
     * Distance Km
     */
    distance_km: number;
};

/**
 * PopularOut
 */
export type PopularOut = {
    /**
     * Set
     */
    set: {
        [key: string]: unknown;
    } | null;
    /**
     * Tasting
     */
    tasting: {
        [key: string]: unknown;
    } | null;
    winery: AppModulesGeoSchemasAppWineryRowOut | null;
};

/**
 * PreferenceCategory
 *
 * BR-SRV-25: `service` нельзя отключить.
 */
export const PreferenceCategory = {
    Service: 'service',
    Drops: 'drops',
    Events: 'events',
    Learning: 'learning',
    Subscriptions: 'subscriptions'
} as const;

/**
 * PreferenceCategory
 *
 * BR-SRV-25: `service` нельзя отключить.
 */
export type PreferenceCategory = typeof PreferenceCategory[keyof typeof PreferenceCategory];

/**
 * PrefillOut
 *
 * Предзаполнение экрана «О вас» данными провайдера (BR-INT-SVC-18) — пользователь
 * подтверждает их сам.
 */
export type PrefillOut = {
    /**
     * Full Name
     */
    full_name: string | null;
    /**
     * Birth Date
     */
    birth_date: string | null;
    /**
     * Avatar Url
     */
    avatar_url: string | null;
};

/**
 * PriceChangeIn
 */
export type PriceChangeIn = {
    new_price: MoneyIn;
    /**
     * Effective At
     *
     * null — «Сразу»
     */
    effective_at?: string | null;
};

/**
 * PriceChangeOut
 */
export type PriceChangeOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Wine Id
     */
    wine_id: string;
    old_price: Money;
    new_price: Money;
    /**
     * Old Price Label
     */
    old_price_label: string;
    /**
     * New Price Label
     */
    new_price_label: string;
    /**
     * Effective At
     */
    effective_at: string | null;
    /**
     * Applied At
     */
    applied_at: string | null;
    state: PriceChangeState;
    /**
     * State Label
     */
    state_label: string;
    /**
     * Created At
     */
    created_at: string;
};

/**
 * PriceChangeState
 */
export const PriceChangeState = {
    Scheduled: 'scheduled',
    Applied: 'applied',
    Cancelled: 'cancelled'
} as const;

/**
 * PriceChangeState
 */
export type PriceChangeState = typeof PriceChangeState[keyof typeof PriceChangeState];

/**
 * PriorityOut
 */
export type PriorityOut = {
    code: SupportPriorityCode;
    name: LocalizedText;
    /**
     * Label
     */
    label: string;
    /**
     * Sla Value
     */
    sla_value: number;
    sla_unit: SlaUnit;
    /**
     * Sla Label
     */
    sla_label: string;
    hint: LocalizedText | null;
    /**
     * Hint Label
     */
    hint_label: string | null;
    /**
     * Position
     */
    position: number;
};

/**
 * ProgramIn
 */
export type ProgramIn = {
    /**
     * Sections
     */
    sections: Array<ProgramSectionIn>;
};

/**
 * ProgramLessonIn
 */
export type ProgramLessonIn = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Order
     */
    order?: number | null;
    /**
     * Required
     */
    required?: boolean;
    /**
     * Tests
     *
     * null — тесты урока не меняются; [] — отвязать все
     */
    tests?: Array<ProgramTestIn> | null;
};

/**
 * ProgramLessonOut
 */
export type ProgramLessonOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Position
     */
    position: number;
    /**
     * Is Required
     */
    is_required: boolean;
    /**
     * Duration Min
     */
    duration_min: number;
    status: ContentStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Is Paid
     */
    is_paid: boolean;
    price: Money | null;
    /**
     * Access Label
     *
     * «бесплатный» / «платный · {цена}»
     */
    access_label: string;
    /**
     * Used In
     *
     * В скольких курсах используется урок
     */
    used_in: number;
    /**
     * Used In Label
     */
    used_in_label: string;
    /**
     * Has Video
     */
    has_video: boolean;
    /**
     * Can Edit Tests
     */
    can_edit_tests: boolean;
    /**
     * Tests
     */
    tests: Array<ProgramTestOut>;
};

/**
 * ProgramSavedOut
 */
export type ProgramSavedOut = {
    /**
     * Sections
     */
    sections: number;
    /**
     * Lessons
     */
    lessons: number;
    /**
     * Tests
     */
    tests: number;
    /**
     * Message
     */
    message: string;
};

/**
 * ProgramSectionIn
 */
export type ProgramSectionIn = {
    /**
     * Id
     */
    id?: string | null;
    title?: LocalizedText | null;
    /**
     * Is Root
     */
    is_root?: boolean;
    /**
     * Order
     */
    order?: number | null;
    result_text?: LocalizedText | null;
    achievement_title?: LocalizedText | null;
    /**
     * Lessons
     */
    lessons?: Array<ProgramLessonIn>;
};

/**
 * ProgramSectionOut
 */
export type ProgramSectionOut = {
    /**
     * Id
     */
    id: string;
    title: LocalizedText | null;
    /**
     * Is Root
     */
    is_root: boolean;
    /**
     * Position
     */
    position: number;
    result_text: LocalizedText | null;
    achievement_title: LocalizedText | null;
    /**
     * Lessons
     */
    lessons: Array<ProgramLessonOut>;
};

/**
 * ProgramTestIn
 */
export type ProgramTestIn = {
    /**
     * Test Id
     */
    test_id: string;
    /**
     * Required
     */
    required?: boolean;
};

/**
 * ProgramTestOut
 */
export type ProgramTestOut = {
    /**
     * Test Id
     */
    test_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Is Required
     */
    is_required: boolean;
    /**
     * Mode Label
     */
    mode_label: string;
    /**
     * Questions
     */
    questions: number;
    status: ContentStatus;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * ProgressOut
 */
export type ProgressOut = {
    /**
     * Done
     */
    done: number;
    /**
     * All
     */
    all: number;
    /**
     * Pct
     */
    pct: number;
    /**
     * Completed
     */
    completed: boolean;
    /**
     * Next Lesson Id
     */
    next_lesson_id: string | null;
    /**
     * Next Number
     */
    next_number: string | null;
    /**
     * Label
     *
     * «{done} из {all} · {pct}%»
     */
    label: string;
    /**
     * Xp Earned
     */
    xp_earned: number | null;
};

/**
 * PublicationAdminOut
 */
export type PublicationAdminOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Overline
     *
     * «Контент · публикация»
     */
    overline: string;
    /**
     * Subtitle
     *
     * «Публикации выходят без модерации — сразу после сохранения»
     */
    subtitle: string;
    title: LocalizedText;
    /**
     * Title Text
     */
    title_text: string;
    status: PublicationStatus;
    /**
     * Status Label
     */
    status_label: string;
    kind: PublicationKind;
    /**
     * Kind Label
     */
    kind_label: string;
    source: PublicationSource;
    author_kind: AuthorKind;
    /**
     * Author Member Id
     */
    author_member_id: string | null;
    winery: ContentRefOut | null;
    region: ContentRefOut | null;
    /**
     * Wines
     */
    wines: Array<ContentRefOut>;
    /**
     * Experiences
     */
    experiences: Array<ContentRefOut>;
    /**
     * Reading Label
     *
     * «{N} мин чтения · {N} слов · {N изображений}»
     */
    reading_label: string;
    lead: LocalizedText | null;
    /**
     * Body
     *
     * Блоки тела по языкам `{<язык>: {blocks}}`
     */
    body: {
        [key: string]: unknown;
    };
    /**
     * Разметка редактора по языкам (форма)
     */
    body_markup: LocalizedText;
    /**
     * Media
     */
    media: Array<ContentMediaOut>;
    facts: PublicationFactsOut;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Views Count
     */
    views_count: number;
    /**
     * Comments Count
     */
    comments_count: number;
    /**
     * Published At
     */
    published_at: string | null;
    /**
     * First Published At
     */
    first_published_at: string | null;
    archive_reason: ContentArchiveReason | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * History
     */
    history: Array<ContentHistoryOut>;
    can: PublicationCan;
    /**
     * Toast
     */
    toast?: string | null;
};

/**
 * PublicationCan
 */
export type PublicationCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Publish
     */
    publish?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * PublicationCardOut
 *
 * Карточка публикации (BR-CNT-37; блок «Публикации» региона и винодельни 12.6.7).
 */
export type PublicationCardOut = {
    /**
     * Id
     */
    id: string;
    kind: PublicationKind;
    /**
     * Kind Label
     *
     * «Статья» / «Новость»
     */
    kind_label: string;
    author: ContentAuthorOut;
    /**
     * Title
     */
    title: string;
    /**
     * Excerpt
     *
     * Первые 300 символов текста (BR-CNT-37)
     */
    excerpt: string;
    /**
     * Pics
     *
     * До 4 фото: медиа публикации или фото винодельни, вина, региона
     */
    pics: Array<string>;
    /**
     * Has Video
     */
    has_video: boolean;
    region: ContentRefOut | null;
    winery: ContentRefOut | null;
    /**
     * Первое отмеченное вино — кнопка «Открыть вино»
     */
    wine: ContentRefOut | null;
    /**
     * Первое отмеченное впечатление
     */
    experience: ContentRefOut | null;
    /**
     * Published At
     */
    published_at: string;
    /**
     * Time Label
     */
    time_label: string;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Comments Count
     */
    comments_count: number;
    /**
     * Views Count
     */
    views_count: number;
    /**
     * Own
     *
     * Своя публикация автора — переход в `expert-comments:{id}`
     */
    own: boolean;
    /**
     * Target
     *
     * `post:{id}`; своя — `expert-comments:{id}`
     */
    target: string;
};

/**
 * PublicationCommentIn
 */
export type PublicationCommentIn = {
    /**
     * Text
     */
    text: string;
};

/**
 * PublicationCreateIn
 */
export type PublicationCreateIn = {
    title: LocalizedText;
    /**
     * Разметка редактора по языкам рынка (12.3.2.1)
     */
    body: LocalizedText;
    /**
     * Winery Id
     */
    winery_id?: string | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Wine Ids
     */
    wine_ids?: Array<string>;
    /**
     * Experiences
     */
    experiences?: Array<ExperienceTagIn>;
    /**
     * Media Ids
     */
    media_ids?: Array<string>;
};

/**
 * PublicationCreatedOut
 */
export type PublicationCreatedOut = {
    /**
     * Id
     */
    id: string;
    status: PublicationStatus;
    kind: PublicationKind;
    /**
     * Published At
     */
    published_at: string | null;
    /**
     * Read Minutes
     */
    read_minutes: number;
    /**
     * Toast
     */
    toast: string;
};

/**
 * PublicationDeleteIn
 */
export type PublicationDeleteIn = {
    /**
     * Confirm Title
     */
    confirm_title: string;
};

/**
 * PublicationFactsOut
 */
export type PublicationFactsOut = {
    /**
     * Author
     */
    author: string;
    /**
     * Region
     */
    region: string;
    /**
     * Wines
     */
    wines: string;
    /**
     * Experiences
     */
    experiences: string;
    /**
     * Date
     */
    date: string | null;
    /**
     * Likes Label
     */
    likes_label: string;
    /**
     * Views
     */
    views: number;
    /**
     * Comments
     */
    comments: number;
    /**
     * Source Label
     */
    source_label: string;
};

/**
 * PublicationKind
 */
export const PublicationKind = { Article: 'article', News: 'news' } as const;

/**
 * PublicationKind
 */
export type PublicationKind = typeof PublicationKind[keyof typeof PublicationKind];

/**
 * PublicationListOut
 */
export type PublicationListOut = {
    /**
     * Items
     */
    items: Array<PublicationCardOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Total
     */
    total: number;
};

/**
 * PublicationPatchIn
 */
export type PublicationPatchIn = {
    title?: LocalizedText | null;
    body?: LocalizedText | null;
    /**
     * Winery Id
     */
    winery_id?: string | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Wine Ids
     */
    wine_ids?: Array<string> | null;
    /**
     * Experiences
     */
    experiences?: Array<ExperienceTagIn> | null;
    /**
     * Media Ids
     */
    media_ids?: Array<string> | null;
};

/**
 * PublicationRowOut
 */
export type PublicationRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     *
     * Заголовок на языке интерфейса
     */
    title: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Links Label
     *
     * «{Регион} · {N вин} · {N впечатлений}»
     */
    links_label: string | null;
    /**
     * Date
     *
     * `published_at`; черновик — дата изменения
     */
    date: string | null;
    status: PublicationStatus;
    /**
     * Status Label
     */
    status_label: string;
    kind: PublicationKind;
    /**
     * Kind Label
     */
    kind_label: string;
    source: PublicationSource;
    can: PublicationCan;
};

/**
 * PublicationScreenOut
 */
export type PublicationScreenOut = {
    /**
     * Id
     */
    id: string;
    kind: PublicationKind;
    /**
     * Kind Label
     *
     * «Статья» / «Новость»
     */
    kind_label: string;
    author: ContentAuthorOut;
    /**
     * Title
     */
    title: string;
    /**
     * Excerpt
     *
     * Первые 300 символов текста (BR-CNT-37)
     */
    excerpt: string;
    /**
     * Pics
     *
     * До 4 фото: медиа публикации или фото винодельни, вина, региона
     */
    pics: Array<string>;
    /**
     * Has Video
     */
    has_video: boolean;
    region: ContentRefOut | null;
    winery: ContentRefOut | null;
    /**
     * Первое отмеченное вино — кнопка «Открыть вино»
     */
    wine: ContentRefOut | null;
    /**
     * Первое отмеченное впечатление
     */
    experience: ContentRefOut | null;
    /**
     * Published At
     */
    published_at: string;
    /**
     * Time Label
     */
    time_label: string;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Comments Count
     */
    comments_count: number;
    /**
     * Views Count
     */
    views_count: number;
    /**
     * Own
     *
     * Своя публикация автора — переход в `expert-comments:{id}`
     */
    own: boolean;
    /**
     * Target
     *
     * `post:{id}`; своя — `expert-comments:{id}`
     */
    target: string;
    /**
     * Body
     */
    body: Array<BodyBlockOut>;
    /**
     * Media
     */
    media: Array<ContentMediaOut>;
    /**
     * Wines
     */
    wines: Array<ContentRefOut>;
    /**
     * Experiences
     */
    experiences: Array<ContentRefOut>;
    /**
     * Read Minutes
     */
    read_minutes: number;
    /**
     * Comments Label
     *
     * «{N} ответов»
     */
    comments_label: string;
    /**
     * Can Comment
     *
     * Поле «Ваш ответ» (не автор публикации)
     */
    can_comment: boolean;
    /**
     * Reply Target
     *
     * Автору — «Ответить читателям»
     */
    reply_target: string | null;
    more_from_author: MoreFromAuthorOut | null;
    /**
     * Nearby
     */
    nearby: Array<NearbyOut>;
};

/**
 * PublicationSource
 */
export const PublicationSource = { App: 'app', Admin: 'admin' } as const;

/**
 * PublicationSource
 */
export type PublicationSource = typeof PublicationSource[keyof typeof PublicationSource];

/**
 * PublicationStatus
 */
export const PublicationStatus = {
    Draft: 'draft',
    Published: 'published',
    Archived: 'archived'
} as const;

/**
 * PublicationStatus
 */
export type PublicationStatus = typeof PublicationStatus[keyof typeof PublicationStatus];

/**
 * PublicationViewsIn
 */
export type PublicationViewsIn = {
    /**
     * Publication Ids
     */
    publication_ids: Array<string>;
};

/**
 * PurchaseBreakdownOut
 */
export type PurchaseBreakdownOut = {
    /**
     * Opened
     */
    opened: boolean;
    /**
     * Paid Lessons
     */
    paid_lessons: number;
    /**
     * Minutes
     */
    minutes: number;
    /**
     * Meta
     */
    meta: string;
    lessons_separately: Money | null;
    course_price: Money | null;
    discount: DiscountOut | null;
    credit: Money | null;
    to_pay: Money | null;
    savings: Money | null;
    /**
     * Savings Pct
     */
    savings_pct: number | null;
    /**
     * Chips
     */
    chips: Array<string>;
    /**
     * Lessons
     */
    lessons: Array<BreakdownLessonOut>;
    /**
     * Course Button
     */
    course_button: string | null;
    lesson_offer: LearnOfferOut | null;
    /**
     * Opened Label
     */
    opened_label: string | null;
};

/**
 * PushClientOut
 */
export type PushClientOut = {
    /**
     * Vapid Public Key
     */
    vapid_public_key?: string | null;
};

/**
 * PushProvider
 */
export const PushProvider = {
    WebPush: 'web_push',
    RustorePush: 'rustore_push',
    Fcm: 'fcm',
    Apns: 'apns'
} as const;

/**
 * PushProvider
 */
export type PushProvider = typeof PushProvider[keyof typeof PushProvider];

/**
 * PushTilesOut
 *
 * Плитки карточки push-интеграции (17.10.10).
 */
export type PushTilesOut = {
    /**
     * Active Devices
     *
     * Активных устройств этого провайдера
     */
    active_devices: number;
    /**
     * Sent 24H
     *
     * Принято провайдером за 24 часа
     */
    sent_24h: number;
    /**
     * Invalid Tokens 24H
     *
     * Недействительных токенов за 24 часа
     */
    invalid_tokens_24h: number;
    /**
     * Errors 24H
     *
     * Ошибок за 24 часа (записи журнала)
     */
    errors_24h: number;
};

/**
 * QuestionIn
 */
export type QuestionIn = {
    /**
     * Id
     */
    id?: string | null;
    text: LocalizedText;
    answer_type?: AnswerType;
    topic?: LocalizedText | null;
    /**
     * Review Lesson Id
     */
    review_lesson_id?: string | null;
    /**
     * Options
     */
    options: Array<TestOptionIn>;
};

/**
 * QuestionOut
 */
export type QuestionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Position
     */
    position: number;
    text: LocalizedText | null;
    answer_type: AnswerType;
    /**
     * Answer Type Label
     */
    answer_type_label: string;
    topic: LocalizedText | null;
    review_lesson: LearnRefOut | null;
    /**
     * Options
     */
    options: Array<TestOptionOut>;
    /**
     * Has Correct
     */
    has_correct: boolean;
};

/**
 * QuestionsSavedOut
 */
export type QuestionsSavedOut = {
    /**
     * Questions
     */
    questions: Array<QuestionOut>;
    /**
     * Without Correct
     */
    without_correct: number;
    /**
     * Message
     */
    message: string;
};

/**
 * QuizzesAttachIn
 */
export type QuizzesAttachIn = {
    /**
     * Test Ids
     */
    test_ids: Array<string>;
};

/**
 * RangeOut
 */
export type RangeOut = {
    /**
     * Min
     */
    min: number;
    /**
     * Max
     */
    max: number;
    /**
     * Title
     */
    title: string;
    /**
     * Label
     *
     * «25–300 XP»
     */
    label: string;
};

/**
 * RatingOut
 */
export type RatingOut = {
    /**
     * Avg
     */
    avg: number;
    /**
     * Count
     */
    count: number;
};

/**
 * ReactionKind
 */
export const ReactionKind = { Like: 'like', Save: 'save' } as const;

/**
 * ReactionKind
 */
export type ReactionKind = typeof ReactionKind[keyof typeof ReactionKind];

/**
 * ReactionsOut
 */
export type ReactionsOut = {
    /**
     * Saved
     */
    saved: boolean;
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Likes Count
     */
    likes_count: number;
};

/**
 * ReadIn
 *
 * `{ids: [...]}` — отметить записи (тап по записи, открытие пуша) или `{all: true}` —
 * «Прочитано» (все записи во всех вкладках, BR-SRV-48).
 */
export type ReadIn = {
    /**
     * Ids
     */
    ids?: Array<string> | null;
    /**
     * All
     */
    all?: boolean | null;
};

/**
 * ReadOnlyFieldOut
 */
export type ReadOnlyFieldOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * ReadOut
 */
export type ReadOut = {
    /**
     * Unread Count
     */
    unread_count: number;
};

/**
 * RecipientOut
 */
export type RecipientOut = {
    /**
     * Kind
     */
    kind: 'user' | 'device' | 'member' | 'email';
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Id
     */
    id?: string | null;
    /**
     * Label
     *
     * ФИО / почта / «Гость · устройство …»
     */
    label: string;
};

/**
 * RecognitionClientOut
 */
export type RecognitionClientOut = {
    /**
     * Enabled
     */
    enabled: boolean;
};

/**
 * RecognitionStatus
 */
export const RecognitionStatus = {
    Processing: 'processing',
    Recognized: 'recognized',
    NotRecognized: 'not_recognized',
    Failed: 'failed'
} as const;

/**
 * RecognitionStatus
 */
export type RecognitionStatus = typeof RecognitionStatus[keyof typeof RecognitionStatus];

/**
 * RecognitionTilesOut
 *
 * Плитки карточки распознавания FastCork (17.11.8).
 */
export type RecognitionTilesOut = {
    /**
     * Recognitions 24H
     *
     * Распознаваний за 24 часа
     */
    recognitions_24h: number;
    /**
     * Recognitions 30D
     *
     * Распознаваний за 30 дней
     */
    recognitions_30d: number;
    /**
     * Not Recognized Pct
     *
     * Не распознано, % (30 дней)
     */
    not_recognized_pct: number | null;
    /**
     * Matched Pct
     *
     * Сопоставлено с каталогом, % от распознанных (30 дней)
     */
    matched_pct: number | null;
    /**
     * Wrong Wine Pct
     *
     * Отмечено «Не то вино», % от сопоставленных (30 дней, BR-INT-SVC-68)
     */
    wrong_wine_pct: number | null;
    /**
     * Avg Response Ms
     *
     * Среднее время ответа FastCork, мс
     */
    avg_response_ms: number | null;
    /**
     * Errors 24H
     *
     * Ошибок распознавания за 24 часа
     */
    errors_24h: number;
    /**
     * Balance Estimate
     *
     * Остаток запросов по оценке: «Пополнено, запросов» минус успешные вызовы с даты пополнения; null — пополнение не задано
     */
    balance_estimate: number | null;
};

/**
 * RecognitionTranslation
 */
export const RecognitionTranslation = {
    NotRequired: 'not_required',
    Translated: 'translated',
    Failed: 'failed'
} as const;

/**
 * RecognitionTranslation
 */
export type RecognitionTranslation = typeof RecognitionTranslation[keyof typeof RecognitionTranslation];

/**
 * RecommendationOut
 */
export type RecommendationOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Number
     */
    number: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Errors
     */
    errors: number;
    /**
     * Target
     */
    target: string;
};

/**
 * RecommendationsOut
 */
export type RecommendationsOut = {
    /**
     * Computed At
     */
    computed_at: string;
    /**
     * Time Label
     *
     * «подобрано сегодня» / «подобрано вчера»
     */
    time_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string;
    /**
     * Items
     */
    items: Array<FeedCardOut>;
};

/**
 * RefClimate
 */
export const RefClimate = {
    Continental: 'continental',
    ModeratelyContinental: 'moderately_continental',
    Maritime: 'maritime',
    Mediterranean: 'mediterranean',
    Mountain: 'mountain'
} as const;

/**
 * RefClimate
 */
export type RefClimate = typeof RefClimate[keyof typeof RefClimate];

/**
 * RefLabelOut
 */
export type RefLabelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     *
     * Название из модуля-владельца; null — модуль не подключён
     */
    name: string | null;
};

/**
 * RefOut
 */
export type RefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * ReferenceOut
 */
export type ReferenceOut = {
    /**
     * Grapes
     */
    grapes: Array<GrapeOptionOut>;
    /**
     * Pairings
     */
    pairings: Array<OptionOut>;
    /**
     * Colors
     */
    colors: Array<OptionOut>;
    /**
     * Serve Temps
     */
    serve_temps: Array<OptionOut>;
    /**
     * Decantings
     */
    decantings: Array<OptionOut>;
    /**
     * Potentials
     */
    potentials: Array<OptionOut>;
    /**
     * Volumes
     */
    volumes: Array<OptionOut>;
    /**
     * Channels
     *
     * `catalog.sales_channels` рынка
     */
    channels: Array<OptionOut>;
    /**
     * Delivery Options
     *
     * `catalog.delivery_options` рынка
     */
    delivery_options: Array<OptionOut>;
    /**
     * Origin Classifications
     *
     * Для `region_id`: классификации страны региона; пусто без региона
     */
    origin_classifications: Array<OriginOptionOut>;
    /**
     * Гейт `drop_early_access` (GAM)
     */
    early_access_tier: TierOut | null;
    /**
     * Kinds
     */
    kinds: Array<OptionOut>;
    /**
     * Sugars
     */
    sugars: Array<OptionOut>;
    /**
     * Taste Axes
     */
    taste_axes: Array<OptionOut>;
    /**
     * Archive Reasons
     */
    archive_reasons: Array<OptionOut>;
    /**
     * Portions
     */
    portions: Array<OptionOut>;
    /**
     * Set Formats
     */
    set_formats: Array<OptionOut>;
    /**
     * Media Types
     */
    media_types: Array<OptionOut>;
    /**
     * Media Placements
     */
    media_placements: Array<OptionOut>;
    /**
     * Timezones
     */
    timezones: Array<string>;
    /**
     * Default Timezone
     */
    default_timezone: string;
    currency: Currency;
    /**
     * Channel Hint
     */
    channel_hint: string;
    /**
     * Wineries
     *
     * Винодельни, доступные пользователю
     */
    wineries: Array<WineryOptionOut>;
    /**
     * Regions
     *
     * Регионы для выбора (неархивные)
     */
    regions: Array<RegionOptionOut>;
    /**
     * Experts
     */
    experts: Array<ExpertOptionOut>;
    /**
     * Tastings
     *
     * Онлайн-дегустации EXP (вслепую — первыми)
     */
    tastings: Array<TastingRefOut>;
    /**
     * Courses
     */
    courses: Array<CourseOptionOut>;
    /**
     * Lessons
     */
    lessons: Array<LessonOptionOut>;
};

/**
 * RefreshIn
 */
export type RefreshIn = {
    /**
     * Refresh Token
     *
     * Непрозрачный токен `rt_…`
     */
    refresh_token: string;
};

/**
 * RefundReason
 *
 * Причина возврата — единый перечень ORD, EXP и INT-PAY (7.3.10 `Refund.reason`,
 * 15.4.2 `PaymentRefund.reason_code`).
 */
export const RefundReason = {
    CustomerRequest: 'customer_request',
    UserCancel: 'user_cancel',
    WineReturn14D: 'wine_return_14d',
    EventCancelled: 'event_cancelled',
    OrganizerCancelled: 'organizer_cancelled',
    BookingCancelledByGuest: 'booking_cancelled_by_guest',
    DuplicateBooking: 'duplicate_booking',
    DuplicatePayment: 'duplicate_payment',
    OrderError: 'order_error',
    StockUnavailable: 'stock_unavailable',
    StoreRefund: 'store_refund',
    Other: 'other'
} as const;

/**
 * RefundReason
 *
 * Причина возврата — единый перечень ORD, EXP и INT-PAY (7.3.10 `Refund.reason`,
 * 15.4.2 `PaymentRefund.reason_code`).
 */
export type RefundReason = typeof RefundReason[keyof typeof RefundReason];

/**
 * RegionBriefOut
 */
export type RegionBriefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    country: PathCountryRefOut;
    /**
     * Image Url
     */
    image_url: string | null;
};

/**
 * RegionCan
 */
export type RegionCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Publish
     */
    publish?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Attach
     */
    attach?: boolean;
};

/**
 * RegionCourseOut
 */
export type RegionCourseOut = {
    course: CourseInfoOut;
    author: AuthorBriefOut;
    access: CourseAccessOut;
    progress: ProgressOut;
    offer: LearnOfferOut | null;
    /**
     * Lessons
     */
    lessons: Array<CourseLessonItemOut>;
    cta: CtaOut;
    /**
     * Subtitle
     */
    subtitle: string;
    reactions: CourseLikeOut;
    purchase_breakdown: PurchaseBreakdownOut;
};

/**
 * RegionCreateIn
 */
export type RegionCreateIn = {
    name: LocalizedText;
    /**
     * Country Id
     */
    country_id: string;
    map_zone?: MapZoneChoiceIn | null;
    center: GeoPointIn;
    contour?: GeoPolygonIn | null;
    description?: LocalizedText | null;
    /**
     * Origin Id
     */
    origin_id: string;
    zone_type: ZoneType;
    /**
     * Area Ha
     */
    area_ha: number;
    climate: Climate;
    /**
     * Soils
     */
    soils: Array<Soil>;
    /**
     * Grape Ids
     */
    grape_ids?: Array<string>;
    /**
     * Styles
     */
    styles: Array<WineStyle>;
    harvest?: LocalizedText | null;
    season: TripSeason;
    /**
     * Sort Order
     */
    sort_order?: number | null;
    /**
     * Media
     */
    media?: Array<AppModulesGeoSchemasCommonMediaIn>;
};

/**
 * RegionDetailOut
 */
export type RegionDetailOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
    country: CountryRef;
    map_zone: IdName | null;
    /**
     * Sort Order
     */
    sort_order: number;
    center: GeoPointOut;
    contour: GeoPolygonOut | null;
    /**
     * Contour Points
     */
    contour_points: number;
    description: LocalizedText | null;
    origin: AppModulesGeoSchemasAdminOriginRefOut;
    zone_type: ZoneType;
    /**
     * Area Ha
     */
    area_ha: number;
    climate: Climate;
    /**
     * Soils
     */
    soils: Array<Soil>;
    /**
     * Grape Ids
     */
    grape_ids: Array<string>;
    /**
     * Grapes
     */
    grapes: Array<GrapeOut>;
    /**
     * Styles
     */
    styles: Array<WineStyle>;
    harvest: LocalizedText | null;
    season: TripSeason;
    status: RegionStatus;
    /**
     * Status Label
     */
    status_label: string;
    hide_reason: HideReason | null;
    archive_reason: AppModulesGeoModelsArchiveReason | null;
    /**
     * Status Comment
     */
    status_comment: string | null;
    /**
     * Published At
     */
    published_at: string | null;
    /**
     * Media
     */
    media: Array<MediaOut>;
    images: RegionImagesOut;
    /**
     * Facts
     */
    facts: Array<AppModulesGeoSchemasAdminFactOut>;
    /**
     * Links Counts
     *
     * Счётчики вкладок «Связанные объекты»: experts, wineries, wines, sets, learn, events, taste, content
     */
    links_counts: {
        [key: string]: number;
    };
    can: RegionCan;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * RegionDictionaryItemOut
 *
 * Регион в справочнике для форм (винодельня, вино, урок, событие…).
 */
export type RegionDictionaryItemOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
    /**
     * Label
     *
     * Название на языке запроса — подпись пункта списка
     */
    label: string;
    country: CountryRef;
    status: RegionStatus;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * RegionDictionaryOut
 */
export type RegionDictionaryOut = {
    /**
     * Items
     */
    items: Array<RegionDictionaryItemOut>;
};

/**
 * RegionImagesOut
 */
export type RegionImagesOut = {
    illustration: MediaOut | null;
    stamp_bronze: MediaOut | null;
    stamp_gold: MediaOut | null;
    specialization: MediaOut | null;
};

/**
 * RegionListOut
 */
export type RegionListOut = {
    /**
     * Items
     */
    items: Array<AppModulesGeoSchemasAppRegionRowOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Total
     */
    total: number;
    /**
     * Done Count
     */
    done_count: number;
};

/**
 * RegionNodeOut
 */
export type RegionNodeOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * State
     */
    state: 'collected' | 'started' | 'new';
    /**
     * Stamp
     */
    stamp: 'bronze' | 'gold' | null;
    /**
     * Has New
     */
    has_new: boolean;
    /**
     * Lessons Count
     */
    lessons_count: number;
    /**
     * Xp Left
     */
    xp_left: number | null;
    /**
     * Label
     */
    label: string;
    /**
     * Target
     */
    target: string;
};

/**
 * RegionOptionOut
 */
export type RegionOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    country: CountryRefOut;
};

/**
 * RegionOverviewOut
 */
export type RegionOverviewOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    country: CountryBrief;
    map_zone: ZoneBrief | null;
    /**
     * Illustration Url
     */
    illustration_url: string | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
    center: GeoPointOut;
    /**
     * Distance Km
     */
    distance_km: number;
    /**
     * Description
     */
    description: string | null;
    /**
     * Facts
     */
    facts: Array<AppModulesGeoSchemasAppFactOut>;
    /**
     * Wines
     *
     * Сеты и вина региона (карточки CAT)
     */
    wines: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Wineries
     */
    wineries: Array<RegionWineryOut>;
    /**
     * Experts
     */
    experts: Array<PersonOut>;
    /**
     * Experts Total
     */
    experts_total: number;
    /**
     * Tastings
     *
     * Дегустации (карточки EXP)
     */
    tastings: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Excursions
     *
     * Экскурсии (карточки EXP)
     */
    excursions: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Gallery
     */
    gallery: Array<GalleryItemOut>;
    /**
     * Publications
     *
     * Публикации (карточки CNT)
     */
    publications: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Включённые «Сообщить о новых» по региону (BR-GEO-42)
     */
    subscriptions: RegionSubscriptionsOut;
};

/**
 * RegionPatchIn
 */
export type RegionPatchIn = {
    name?: LocalizedText | null;
    /**
     * Country Id
     */
    country_id?: string | null;
    map_zone?: MapZoneChoiceIn | null;
    center?: GeoPointIn | null;
    description?: LocalizedText | null;
    /**
     * Origin Id
     */
    origin_id?: string | null;
    zone_type?: ZoneType | null;
    /**
     * Area Ha
     */
    area_ha?: number | null;
    climate?: Climate | null;
    /**
     * Soils
     */
    soils?: Array<Soil> | null;
    /**
     * Grape Ids
     */
    grape_ids?: Array<string> | null;
    /**
     * Styles
     */
    styles?: Array<WineStyle> | null;
    harvest?: LocalizedText | null;
    season?: TripSeason | null;
    /**
     * Sort Order
     */
    sort_order?: number | null;
};

/**
 * RegionPointsOut
 */
export type RegionPointsOut = {
    region: PointsRegionOut;
    /**
     * Wineries
     */
    wineries: Array<WineryPinOut>;
    /**
     * Tastings
     *
     * Офлайн-дегустации региона (EXP)
     */
    tastings: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Excursions
     *
     * Экскурсии региона (EXP)
     */
    excursions: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Bounds
     */
    bounds: {
        [key: string]: {
            [key: string]: number;
        };
    } | null;
};

/**
 * RegionProgressOut
 */
export type RegionProgressOut = {
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Country Code
     */
    country_code: string;
    /**
     * Status
     */
    status: 'done' | 'exploring' | 'first_wine' | 'wish' | 'closed';
    /**
     * Status Text
     */
    status_text: string;
    /**
     * Pct
     */
    pct: number;
    study: GamStepOut;
    taste: TasteStepOut;
    visit: VisitStepOut;
    /**
     * Stamp Level
     */
    stamp_level: 'bronze' | 'gold' | null;
    /**
     * Stamp Image Url
     */
    stamp_image_url: string | null;
    /**
     * Xp Got
     */
    xp_got: number;
    /**
     * Xp Total
     */
    xp_total: number;
    /**
     * Target
     */
    target: string;
};

/**
 * RegionStatus
 */
export const RegionStatus = {
    Draft: 'draft',
    Published: 'published',
    Hidden: 'hidden',
    Archived: 'archived'
} as const;

/**
 * RegionStatus
 */
export type RegionStatus = typeof RegionStatus[keyof typeof RegionStatus];

/**
 * RegionSubscriptionsOut
 */
export type RegionSubscriptionsOut = {
    /**
     * Wines
     */
    wines: boolean;
    /**
     * Tastings
     */
    tastings: boolean;
    /**
     * Excursions
     */
    excursions: boolean;
};

/**
 * RegionSummaryOut
 */
export type RegionSummaryOut = {
    region: RegionBriefOut;
    stamp: StampOut;
    study: ScaleOut;
    taste: ScaleOut;
    visit: ScaleOut;
    tiles: TilesOut;
    /**
     * Hint
     */
    hint: string;
    cta: LinkOut;
};

/**
 * RegionWineryOut
 */
export type RegionWineryOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Distance Km
     */
    distance_km: number;
    visit_mode: VisitMode;
    /**
     * Teaser
     *
     * Первое предложение описания
     */
    teaser: string | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
};

/**
 * RegionsOut
 */
export type RegionsOut = {
    /**
     * Items
     */
    items: Array<RegionProgressOut>;
};

/**
 * RegisterIn
 *
 * Шаг «О вас» регистрации по почте (BR-ACC-11, пример 4.8.2.1).
 */
export type RegisterIn = {
    /**
     * Verification Token
     */
    verification_token: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Birth Date
     */
    birth_date: string;
    phone: PhoneIn;
    /**
     * Country Code
     *
     * Нет — страна гостя / рынка по умолчанию
     */
    country_code?: string | null;
    /**
     * City Name
     */
    city_name: string;
    /**
     * City Suggestion Id
     */
    city_suggestion_id?: string | null;
    /**
     * Password
     */
    password: string;
    /**
     * Avatar File Id
     */
    avatar_file_id?: string | null;
    /**
     * Consents
     *
     * Только если у гостевого профиля устройства нет согласий (BR-ACC-09)
     */
    consents?: Array<ConsentItemIn>;
};

/**
 * RegistrationMethod
 */
export const RegistrationMethod = {
    Email: 'email',
    Vk: 'vk',
    Yandex: 'yandex',
    Google: 'google',
    Apple: 'apple',
    Team: 'team'
} as const;

/**
 * RegistrationMethod
 */
export type RegistrationMethod = typeof RegistrationMethod[keyof typeof RegistrationMethod];

/**
 * RelatedIn
 */
export type RelatedIn = {
    /**
     * Wine Ids
     */
    wine_ids?: Array<string>;
    /**
     * Set Ids
     */
    set_ids?: Array<string>;
    /**
     * Drop Ids
     */
    drop_ids?: Array<string>;
    /**
     * Tasting Ids
     */
    tasting_ids?: Array<string>;
};

/**
 * RelatedItem
 *
 * Связанная запись (4.3.18 `related`). С `id` — проверяется модулем-владельцем записи.
 */
export type RelatedItem = {
    /**
     * Type
     */
    type: 'order' | 'booking' | 'event' | 'trip' | 'course' | 'lesson' | 'refund' | 'other';
    /**
     * Id
     */
    id?: string | null;
    /**
     * Label
     */
    label: string;
};

/**
 * RelatedItemOut
 */
export type RelatedItemOut = {
    /**
     * Object Type
     */
    object_type: 'wine' | 'set' | 'drop' | 'tasting';
    /**
     * Object Id
     */
    object_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Status Label
     */
    status_label: string | null;
};

/**
 * RelatedOut
 */
export type RelatedOut = {
    /**
     * Wines
     */
    wines: Array<RelatedItemOut>;
    /**
     * Sets
     */
    sets: Array<RelatedItemOut>;
    /**
     * Drops
     */
    drops: Array<RelatedItemOut>;
    /**
     * Tastings
     */
    tastings: Array<RelatedItemOut>;
};

/**
 * ReplyIn
 */
export type ReplyIn = {
    /**
     * Text
     */
    text: string;
};

/**
 * RequiredIfOut
 */
export type RequiredIfOut = {
    /**
     * Field
     */
    field: string;
    /**
     * Value
     */
    value: string;
};

/**
 * RetranslateIn
 */
export type RetranslateIn = {
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Field Paths
     */
    field_paths: Array<string>;
};

/**
 * RetranslateJobOut
 */
export type RetranslateJobOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Field Path
     */
    field_path: string;
    status: TranslationJobStatus;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * RetranslateOut
 */
export type RetranslateOut = {
    /**
     * Jobs
     */
    jobs: Array<RetranslateJobOut>;
};

/**
 * ReviewAuthorOut
 */
export type ReviewAuthorOut = {
    /**
     * Display Name
     */
    display_name: string;
    /**
     * Avatar Url
     */
    avatar_url: string | null;
};

/**
 * ReviewFormatOut
 */
export type ReviewFormatOut = {
    /**
     * Excursion Id
     */
    excursion_id: string | null;
    /**
     * Title
     */
    title: string;
};

/**
 * ReviewIn
 */
export type ReviewIn = {
    /**
     * Rating
     *
     * 1…5; без оценки — «Поставьте оценку»
     */
    rating?: number | null;
    /**
     * Excursion Id
     */
    excursion_id?: string | null;
    /**
     * Text
     */
    text?: string;
};

/**
 * ReviewNoteIn
 */
export type ReviewNoteIn = {
    /**
     * Note
     *
     * Замечания для автора
     */
    note: string;
};

/**
 * ReviewNoteOut
 */
export type ReviewNoteOut = {
    /**
     * Note
     */
    note: string;
    /**
     * By Name
     */
    by_name: string | null;
    /**
     * Label
     *
     * «{имя} · администратор»
     */
    label: string;
};

/**
 * ReviewOut
 */
export type ReviewOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Winery Id
     */
    winery_id: string;
    /**
     * Rating
     */
    rating: number;
    /**
     * Excursion Id
     */
    excursion_id: string | null;
    /**
     * Format Title
     */
    format_title: string;
    /**
     * Text
     */
    text: string;
    author: ReviewAuthorOut;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Edited At
     */
    edited_at: string | null;
    /**
     * Is Mine
     */
    is_mine: boolean;
};

/**
 * ReviewSavedOut
 */
export type ReviewSavedOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Winery Id
     */
    winery_id: string;
    /**
     * Rating
     */
    rating: number;
    /**
     * Excursion Id
     */
    excursion_id: string | null;
    /**
     * Format Title
     */
    format_title: string;
    /**
     * Text
     */
    text: string;
    author: ReviewAuthorOut;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Edited At
     */
    edited_at: string | null;
    /**
     * Is Mine
     */
    is_mine: boolean;
    winery_rating: RatingOut;
};

/**
 * ReviewSummaryOut
 */
export type ReviewSummaryOut = {
    /**
     * Avg
     */
    avg: number;
    /**
     * Count
     */
    count: number;
    /**
     * Distribution
     */
    distribution: Array<StarShareOut>;
};

/**
 * RevisionOut
 */
export type RevisionOut = {
    /**
     * Id
     */
    id: string;
    state: RevisionState;
    /**
     * State Label
     */
    state_label: string;
    /**
     * Changed Fields
     */
    changed_fields: Array<string>;
    /**
     * Changed Labels
     */
    changed_labels: Array<string>;
    /**
     * Submitted At
     */
    submitted_at: string | null;
    /**
     * Decided At
     */
    decided_at: string | null;
    /**
     * Review Note
     */
    review_note: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * RevisionState
 */
export const RevisionState = {
    Editing: 'editing',
    OnModeration: 'on_moderation',
    Applied: 'applied',
    Returned: 'returned',
    Rejected: 'rejected'
} as const;

/**
 * RevisionState
 */
export type RevisionState = typeof RevisionState[keyof typeof RevisionState];

/**
 * RewardCreateIn
 */
export type RewardCreateIn = {
    /**
     * «Награда», до 80
     */
    title: LocalizedText;
    /**
     * Icon File Id
     */
    icon_file_id?: string | null;
    /**
     * Cost Bonuses
     */
    cost_bonuses: number;
    kind: RewardKind;
    /**
     * До 300
     */
    description?: LocalizedText | null;
    executor_type: ExecutorType;
    status?: RewardStatus;
    /**
     * Sort
     */
    sort?: number;
};

/**
 * RewardItemOut
 */
export type RewardItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Description
     */
    description: string | null;
    /**
     * Kind
     */
    kind: 'reward' | 'goal';
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Cost
     */
    cost: number;
    /**
     * Cost Label
     */
    cost_label: string;
    /**
     * Icon Url
     */
    icon_url: string | null;
    /**
     * Affordable
     *
     * Баланса хватает (гость — false)
     */
    affordable: boolean;
    /**
     * Missing
     */
    missing: number;
    /**
     * Missing Label
     *
     * «N бонусов · не хватает M»
     */
    missing_label: string | null;
    /**
     * Button Label
     *
     * «Обменять N»
     */
    button_label: string;
};

/**
 * RewardKind
 */
export const RewardKind = { Reward: 'reward', Goal: 'goal' } as const;

/**
 * RewardKind
 */
export type RewardKind = typeof RewardKind[keyof typeof RewardKind];

/**
 * RewardOut
 */
export type RewardOut = {
    /**
     * Xp
     */
    xp: number;
    /**
     * Total Xp
     */
    total_xp: number;
    /**
     * Rank
     */
    rank: string;
    /**
     * To Next
     */
    to_next: number | null;
};

/**
 * RewardPatchIn
 */
export type RewardPatchIn = {
    title?: LocalizedText | null;
    /**
     * Icon File Id
     */
    icon_file_id?: string | null;
    /**
     * Cost Bonuses
     */
    cost_bonuses?: number | null;
    kind?: RewardKind | null;
    description?: LocalizedText | null;
    executor_type?: ExecutorType | null;
    status?: RewardStatus | null;
    /**
     * Sort
     */
    sort?: number | null;
};

/**
 * RewardRowOut
 */
export type RewardRowOut = {
    /**
     * Id
     */
    id: string;
    title: LocalizedText;
    /**
     * Title Label
     */
    title_label: string;
    /**
     * Cost Bonuses
     */
    cost_bonuses: number;
    /**
     * Cost Label
     */
    cost_label: string;
    kind: RewardKind;
    /**
     * Kind Label
     */
    kind_label: string;
    description: LocalizedText | null;
    /**
     * Description Label
     */
    description_label: string | null;
    executor_type: ExecutorType;
    /**
     * Executor Label
     */
    executor_label: string;
    /**
     * Icon File Id
     */
    icon_file_id: string | null;
    /**
     * Icon Url
     */
    icon_url: string | null;
    status: RewardStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Sort
     */
    sort: number;
    /**
     * Exchanges Count
     */
    exchanges_count: number;
    can: GamRowCan;
};

/**
 * RewardStatus
 */
export const RewardStatus = { Active: 'active', Hidden: 'hidden' } as const;

/**
 * RewardStatus
 */
export type RewardStatus = typeof RewardStatus[keyof typeof RewardStatus];

/**
 * RewardsOut
 */
export type RewardsOut = {
    /**
     * Items
     */
    items: Array<RewardItemOut>;
};

/**
 * RewardsPageOut
 */
export type RewardsPageOut = {
    /**
     * Items
     */
    items: Array<RewardRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
};

/**
 * RoomApp
 */
export type RoomApp = {
    type: RoomKind;
    /**
     * Label
     */
    label: string;
    /**
     * Description
     */
    description: string;
    supplement: Money | null;
    /**
     * Available With Children
     */
    available_with_children: boolean;
};

/**
 * RoomIn
 */
export type RoomIn = {
    name?: LocalizedText | null;
    type?: RoomType;
    /**
     * Beds
     */
    beds?: number;
    price_per_night?: Money | null;
};

/**
 * RoomKind
 */
export const RoomKind = {
    Double: 'double',
    Twin: 'twin',
    Single: 'single',
    Family: 'family'
} as const;

/**
 * RoomKind
 */
export type RoomKind = typeof RoomKind[keyof typeof RoomKind];

/**
 * RoomOut
 */
export type RoomOut = {
    name: LocalizedText | null;
    type: RoomType;
    /**
     * Type Label
     */
    type_label: string;
    /**
     * Beds
     */
    beds: number;
    price_per_night: Money | null;
    /**
     * Shown In App
     *
     * Номер с местами и ценой виден в приложении
     */
    shown_in_app: boolean;
};

/**
 * RoomType
 */
export const RoomType = {
    Standard: 'standard',
    Family: 'family',
    Suite: 'suite',
    Apartments: 'apartments',
    GuestHouse: 'guest_house'
} as const;

/**
 * RoomType
 */
export type RoomType = typeof RoomType[keyof typeof RoomType];

/**
 * RouteOut
 */
export type RouteOut = {
    region: RegionBriefOut;
    stamp: StampOut;
    progress: RouteProgressOut;
    xp: XpPairOut | null;
    main_course: StudyCardOut;
    bronze: BronzeOut;
    gold: GoldOut;
    extra: ExtraOut;
    collected: CollectedOut | null;
    /**
     * Buttons
     */
    buttons: Array<LinkOut>;
};

/**
 * RouteProgressOut
 */
export type RouteProgressOut = {
    /**
     * Done
     */
    done: number;
    /**
     * Steps
     */
    steps: number;
    /**
     * Pct
     */
    pct: number;
    /**
     * Bar Pct
     *
     * Среднее долей «Изучение» и «Вкус» (BR-LRN-73)
     */
    bar_pct: number;
    /**
     * Label
     */
    label: string;
};

/**
 * RowCan
 */
export type RowCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * RuleCan
 */
export type RuleCan = {
    /**
     * Update
     */
    update: boolean;
    /**
     * Enable
     */
    enable: boolean;
    /**
     * Disable
     */
    disable: boolean;
    /**
     * Delete
     */
    delete: boolean;
};

/**
 * SalesChannel
 */
export const SalesChannel = {
    AtWinery: 'at_winery',
    InSet: 'in_set',
    Delivery: 'delivery',
    PartnerShipping: 'partner_shipping'
} as const;

/**
 * SalesChannel
 */
export type SalesChannel = typeof SalesChannel[keyof typeof SalesChannel];

/**
 * SavedCountsOut
 */
export type SavedCountsOut = {
    /**
     * Wine
     */
    wine: number;
    /**
     * Set
     */
    set: number;
    /**
     * Experience
     */
    experience: number;
    /**
     * Winery
     */
    winery: number;
    /**
     * Lesson
     */
    lesson: number;
    /**
     * Total
     */
    total: number;
};

/**
 * SavedIn
 */
export type SavedIn = {
    /**
     * Target Type
     */
    target_type: 'wine' | 'set' | 'drop' | 'tasting' | 'excursion' | 'trip' | 'winery' | 'lesson';
    /**
     * Target Id
     */
    target_id: string;
};

/**
 * SavedItemOut
 */
export type SavedItemOut = {
    /**
     * Target Type
     */
    target_type: string;
    /**
     * Target Id
     */
    target_id: string;
    /**
     * Saved At
     */
    saved_at: string;
    /**
     * Target
     */
    target: string;
    /**
     * Card
     *
     * Карточка модуля-владельца (CAT, GEO, LRN, EXP)
     */
    card: {
        [key: string]: unknown;
    };
};

/**
 * SavedLessonOut
 */
export type SavedLessonOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Region
     */
    region: string | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Saved At
     */
    saved_at: string;
    /**
     * Target
     */
    target: string;
};

/**
 * SavedLessonPageOut
 */
export type SavedLessonPageOut = {
    /**
     * Items
     */
    items: Array<SavedLessonOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * SavedOut
 */
export type SavedOut = {
    /**
     * Tab
     */
    tab: 'wine' | 'set' | 'experience' | 'winery' | 'lesson';
    counts: SavedCountsOut;
    /**
     * Items
     */
    items: Array<SavedItemOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Empty
     *
     * Пустое состояние вкладки (12.6.8)
     */
    empty: {
        [key: string]: string;
    } | null;
};

/**
 * SavedStateOut
 */
export type SavedStateOut = {
    /**
     * Saved
     */
    saved: boolean;
    /**
     * Toast
     */
    toast: string;
};

/**
 * ScaleOut
 */
export type ScaleOut = {
    /**
     * Done
     */
    done: number;
    /**
     * All
     */
    all: number;
    /**
     * Label
     */
    label: string;
};

/**
 * ScheduleMode
 */
export const ScheduleMode = {
    Once: 'once',
    Weekly: 'weekly',
    OnRequest: 'on_request'
} as const;

/**
 * ScheduleMode
 */
export type ScheduleMode = typeof ScheduleMode[keyof typeof ScheduleMode];

/**
 * ScheduledPriceOut
 */
export type ScheduledPriceOut = {
    /**
     * Id
     */
    id: string;
    new_price: Money;
    /**
     * New Price Label
     */
    new_price_label: string;
    /**
     * Effective At
     */
    effective_at: string | null;
};

/**
 * ScopeOut
 */
export type ScopeOut = {
    /**
     * Winery Ids
     *
     * Винодельни винодела (OwnershipScope, COM-05); для admin и expert — пусто
     */
    winery_ids: Array<string>;
};

/**
 * SearchCountsOut
 */
export type SearchCountsOut = {
    /**
     * All
     */
    all: number;
    /**
     * Regions
     */
    regions: number;
    /**
     * Wines
     */
    wines: number;
    /**
     * Sets
     */
    sets: number;
    /**
     * Wineries
     */
    wineries: number;
    /**
     * Experiences
     */
    experiences: number;
    /**
     * Lessons
     */
    lessons: number;
    /**
     * People
     */
    people: number;
};

/**
 * SearchGroupsOut
 */
export type SearchGroupsOut = {
    /**
     * Regions
     */
    regions: Array<AppModulesGeoSchemasAppRegionRowOut>;
    /**
     * Wines
     */
    wines: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Sets
     */
    sets: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Wineries
     */
    wineries: Array<AppModulesGeoSchemasAppWineryRowOut>;
    /**
     * Experiences
     */
    experiences: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Lessons
     */
    lessons: Array<{
        [key: string]: unknown;
    }>;
    /**
     * People
     */
    people: Array<{
        [key: string]: unknown;
    }>;
};

/**
 * SecretStateOut
 *
 * BR-INT-07: значение секрета не отдаётся никогда — только признак и 4 последних символа.
 */
export type SecretStateOut = {
    /**
     * Is Set
     */
    is_set: boolean;
    /**
     * Last4
     */
    last4?: string | null;
};

/**
 * SectionCompletedOut
 */
export type SectionCompletedOut = {
    /**
     * Section Id
     */
    section_id: string;
    /**
     * Number
     */
    number: number;
    /**
     * Title
     */
    title: string;
    /**
     * Achievement Title
     */
    achievement_title: string | null;
};

/**
 * SectionCountOut
 */
export type SectionCountOut = {
    section: NotificationSection;
    /**
     * Label
     */
    label: string;
    /**
     * Count
     */
    count: number;
};

/**
 * SectionItemOut
 */
export type SectionItemOut = {
    /**
     * Type
     */
    type: string;
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Overline
     */
    overline?: string | null;
    /**
     * Subtitle
     */
    subtitle?: string | null;
    /**
     * Meta
     */
    meta?: string | null;
    /**
     * State
     */
    state?: string | null;
    /**
     * Image Url
     */
    image_url?: string | null;
    price?: Money | null;
    /**
     * Price Label
     */
    price_label?: string | null;
    /**
     * Target
     */
    target: string;
};

/**
 * SectionLinkOut
 */
export type SectionLinkOut = {
    /**
     * Section
     *
     * Ключ раздела админки (ТЗ 4.2.2.1)
     */
    section: string;
    /**
     * Filters
     *
     * Фильтры реестра: `?filter[key]=value`
     */
    filters: {
        [key: string]: string;
    };
};

/**
 * SeenSection
 */
export const SeenSection = { Facts: 'facts', Quiz: 'quiz' } as const;

/**
 * SeenSection
 */
export type SeenSection = typeof SeenSection[keyof typeof SeenSection];

/**
 * SensoryParam
 */
export const SensoryParam = {
    Sweetness: 'sweetness',
    Acidity: 'acidity',
    Body: 'body',
    Tannin: 'tannin',
    AromaIntensity: 'aroma_intensity',
    Finish: 'finish'
} as const;

/**
 * SensoryParam
 */
export type SensoryParam = typeof SensoryParam[keyof typeof SensoryParam];

/**
 * ServeTemp
 */
export const ServeTemp = {
    T68: 't6_8',
    T810: 't8_10',
    T1012: 't10_12',
    T1214: 't12_14',
    T1416: 't14_16',
    T1618: 't16_18',
    T1820: 't18_20'
} as const;

/**
 * ServeTemp
 */
export type ServeTemp = typeof ServeTemp[keyof typeof ServeTemp];

/**
 * SetAdmin
 */
export type SetAdmin = {
    /**
     * Id
     */
    id: string;
    /**
     * Sku
     */
    sku: string;
    name: LocalizedText | null;
    /**
     * Title
     */
    title: string;
    format: SetFormat;
    /**
     * Format Label
     */
    format_label: string;
    blind_tasting: TastingRefOut | null;
    /**
     * Blind Warning
     *
     * BR-CAT-15: дегустация не выбрана
     */
    blind_warning: string | null;
    author_type: SetAuthorType;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Author Label
     *
     * Бренд / винодельня / имя эксперта
     */
    author_label: string;
    /**
     * Composer Label
     */
    composer_label: string;
    author_winery: RefOut | null;
    author_expert: RefOut | null;
    /**
     * Items
     */
    items: Array<SetItemOut>;
    /**
     * Items Count
     */
    items_count: number;
    items_cost: Money;
    /**
     * Items Cost Label
     */
    items_cost_label: string;
    /**
     * Markup Pct
     */
    markup_pct: number | null;
    /**
     * Markup Label
     */
    markup_label: string | null;
    /**
     * Total Volume Ml
     */
    total_volume_ml: number;
    /**
     * Total Volume Label
     */
    total_volume_label: string;
    /**
     * Summary Label
     *
     * «N бут. · X л · цена бутылок Y» / пусто
     */
    summary_label: string;
    /**
     * Countries
     */
    countries: Array<CountryRefOut>;
    /**
     * Regions
     */
    regions: Array<RefOut>;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * «например {цена бутылок × 1,15}»
     */
    price_placeholder: Money | null;
    /**
     * Stock
     */
    stock: number | null;
    /**
     * Stock Reserved
     */
    stock_reserved: number;
    /**
     * Available
     */
    available: number | null;
    /**
     * Sales Channels
     */
    sales_channels: Array<SalesChannel>;
    /**
     * Sales Channel Labels
     */
    sales_channel_labels: Array<string>;
    /**
     * Delivery Options
     */
    delivery_options: Array<DeliveryOption>;
    /**
     * Delivery Labels
     */
    delivery_labels: Array<string>;
    description: LocalizedText | null;
    links: SetLinksOut;
    /**
     * Media
     */
    media: Array<CatalogMediaOut>;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Seller Label
     */
    seller_label: string | null;
    status: CatalogStatus;
    /**
     * Status Label
     */
    status_label: string;
    display_status: CatalogStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Sold Count
     */
    sold_count: number;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Published At
     */
    published_at: string | null;
    review: ReviewNoteOut | null;
    archive_reason: AppModulesCatModelsArchiveReason | null;
    /**
     * Archive Reason Label
     */
    archive_reason_label: string | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    revision: RevisionOut | null;
    revision_preview?: SetAdmin | null;
    can: SetCan;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * SetApp
 */
export type SetApp = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    price: Money;
};

/**
 * SetAuthorType
 */
export const SetAuthorType = {
    Platform: 'platform',
    Winery: 'winery',
    Expert: 'expert'
} as const;

/**
 * SetAuthorType
 */
export type SetAuthorType = typeof SetAuthorType[keyof typeof SetAuthorType];

/**
 * SetCan
 */
export type SetCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Edit Items
     *
     * «Изменить состав» (BR-CAT-20)
     */
    edit_items?: boolean;
    /**
     * Edit Media
     */
    edit_media?: boolean;
    /**
     * Submit
     */
    submit?: boolean;
    /**
     * Publish
     */
    publish?: boolean;
    /**
     * Return
     */
    return?: boolean;
    /**
     * Reject
     */
    reject?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Awaiting Decision
     */
    awaiting_decision?: boolean;
};

/**
 * SetCreateIn
 */
export type SetCreateIn = {
    /**
     * До 120 символов
     */
    name: LocalizedText;
    format?: SetFormat;
    /**
     * Blind Tasting Id
     */
    blind_tasting_id?: string | null;
    /**
     * Author Winery Id
     *
     * Винодел с несколькими винодельнями выбирает свою
     */
    author_winery_id?: string | null;
    /**
     * Items
     */
    items?: Array<SetItemIn>;
    /**
     * Sales Channels
     */
    sales_channels?: Array<SalesChannel>;
    /**
     * Delivery Options
     */
    delivery_options?: Array<DeliveryOption>;
    /**
     * По умолчанию — цена бутылок
     */
    price?: MoneyIn | null;
    /**
     * Stock
     *
     * Пусто — остаток не ведётся
     */
    stock?: number | null;
    description: LocalizedText;
    /**
     * Media
     */
    media?: Array<AppModulesCatSchemasAdminMediaIn>;
    /**
     * Link Winery Id
     */
    link_winery_id?: string | null;
    /**
     * Link Expert Id
     */
    link_expert_id?: string | null;
    /**
     * Link Course Id
     */
    link_course_id?: string | null;
    /**
     * Link Lesson Id
     */
    link_lesson_id?: string | null;
};

/**
 * SetDetailOut
 */
export type SetDetailOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    format: SetFormat;
    /**
     * Blind Tasting Id
     */
    blind_tasting_id: string | null;
    /**
     * Over Label
     */
    over_label: string;
    author_type: SetAuthorType;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Эксперт или винодельня-автор
     */
    author: AppModulesCatSchemasAppNamedRef | null;
    /**
     * Author Line
     *
     * «Собрал: …» / «Автор сета: …» / «Винодельня: …»
     */
    author_line: string;
    /**
     * Sold Count
     */
    sold_count: number;
    /**
     * Orders Line
     *
     * «{N} заказов · {автор}»
     */
    orders_line: string;
    /**
     * Description
     */
    description: string | null;
    /**
     * Media
     */
    media: Array<MediaAppOut>;
    /**
     * Items
     */
    items: Array<SetItemAppOut>;
    /**
     * Items Count
     */
    items_count: number;
    /**
     * Items Label
     */
    items_label: string;
    /**
     * Countries
     */
    countries: Array<CountryOut>;
    /**
     * Regions
     */
    regions: Array<AppModulesCatSchemasAppNamedRef>;
    /**
     * Composition Label
     *
     * «Состав: N вин по 100 мл»
     */
    composition_label: string;
    /**
     * Total Volume Ml
     */
    total_volume_ml: number;
    /**
     * Seller Label
     */
    seller_label: string | null;
    /**
     * Ship Incl
     */
    ship_incl: string | null;
    /**
     * Ship Fact
     */
    ship_fact: string | null;
    /**
     * Age Check
     */
    age_check: string;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    items_cost: Money;
    /**
     * Вслепую: «выгода Σ позиций − цена», если > 0
     */
    savings: Money | null;
    /**
     * Available
     */
    available: number | null;
    /**
     * Max Qty
     */
    max_qty: number;
    /**
     * In Stock
     */
    in_stock: boolean;
    /**
     * Purchase Action
     */
    purchase_action: 'cart' | 'partner' | 'none';
    /**
     * Purchase Label
     */
    purchase_label: string | null;
    /**
     * Link Lesson Id
     */
    link_lesson_id: string | null;
    /**
     * Link Course Id
     */
    link_course_id: string | null;
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Is Saved
     *
     * Сет в «Сохранённом» пользователя (закладка CNT)
     */
    is_saved: boolean;
    /**
     * Bought
     */
    bought: boolean;
    /**
     * Xp Kind
     */
    xp_kind?: 'set';
};

/**
 * SetFormat
 */
export const SetFormat = { Open: 'open', Blind: 'blind' } as const;

/**
 * SetFormat
 */
export type SetFormat = typeof SetFormat[keyof typeof SetFormat];

/**
 * SetItemAppOut
 */
export type SetItemAppOut = {
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    /**
     * Title
     *
     * Название вина; для сета вслепую — «Вино N»
     */
    title: string;
    portion: SetPortion;
    /**
     * Portion Label
     */
    portion_label: string;
    /**
     * Volume Ml
     */
    volume_ml: number;
    portion_price: Money;
    /**
     * Portion Price Label
     */
    portion_price_label: string;
    /**
     * Item Id
     *
     * Для образца сета вслепую (`set_item`)
     */
    item_id: string | null;
    /**
     * null для сета вслепую (BR-CAT-16)
     */
    wine: AppModulesCatSchemasAppNamedRef | null;
    /**
     * Subtitle
     *
     * «{винодельня} · {регион}»; null вслепую
     */
    subtitle: string | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Wine Available
     *
     * Вино опубликовано — переход в `bottle:<id>`
     */
    wine_available: boolean;
};

/**
 * SetItemIn
 */
export type SetItemIn = {
    /**
     * Wine Id
     */
    wine_id: string;
    /**
     * Position
     *
     * Номер образца 1…N
     */
    position?: number | null;
    portion?: SetPortion;
    /**
     * Для бутылки по умолчанию — текущая цена вина (OQ-CAT-02)
     */
    portion_price?: MoneyIn | null;
};

/**
 * SetItemOut
 */
export type SetItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Position
     */
    position: number;
    /**
     * Position Label
     */
    position_label: string;
    wine: SetWineOut;
    portion: SetPortion;
    /**
     * Portion Label
     */
    portion_label: string;
    portion_price: Money;
    /**
     * Portion Price Label
     */
    portion_price_label: string;
    /**
     * Volume Ml
     */
    volume_ml: number;
    /**
     * Hidden From User
     *
     * Сет вслепую: «скрыто от пользователя»
     */
    hidden_from_user: boolean;
};

/**
 * SetItemsIn
 */
export type SetItemsIn = {
    /**
     * Items
     */
    items: Array<SetItemIn>;
};

/**
 * SetItemsListOut
 */
export type SetItemsListOut = {
    /**
     * Items
     */
    items: Array<SetListItem>;
};

/**
 * SetLinksOut
 */
export type SetLinksOut = {
    winery: RefOut | null;
    expert: RefOut | null;
    course: RefOut | null;
    lesson: RefOut | null;
};

/**
 * SetListItem
 */
export type SetListItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    format: SetFormat;
    /**
     * Title
     *
     * Вслепую — «Три вина {слово эфира}», иначе название
     */
    title: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Meta
     */
    meta: string;
    author_type: SetAuthorType;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Over Label
     */
    over_label: string;
    /**
     * Portion Chip
     *
     * «6 × 100 мл», если порции одинаковые
     */
    portion_chip: string | null;
    /**
     * Items Count
     */
    items_count: number;
    /**
     * Regions
     */
    regions: Array<AppModulesCatSchemasAppNamedRef>;
    /**
     * Cover Url
     */
    cover_url: string | null;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Sold Count
     */
    sold_count: number;
    /**
     * Orders Label
     */
    orders_label: string | null;
    /**
     * Available
     */
    available: number | null;
    /**
     * Purchase Action
     */
    purchase_action: 'cart' | 'partner' | 'none';
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Blind Tasting Id
     */
    blind_tasting_id: string | null;
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Xp Kind
     */
    xp_kind?: 'set';
};

/**
 * SetPageOut
 */
export type SetPageOut = {
    /**
     * Items
     */
    items: Array<SetListItem>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * SetPatchIn
 */
export type SetPatchIn = {
    name?: LocalizedText | null;
    format?: SetFormat | null;
    /**
     * Blind Tasting Id
     */
    blind_tasting_id?: string | null;
    /**
     * Sales Channels
     */
    sales_channels?: Array<SalesChannel> | null;
    /**
     * Delivery Options
     */
    delivery_options?: Array<DeliveryOption> | null;
    price?: MoneyIn | null;
    /**
     * Stock
     */
    stock?: number | null;
    description?: LocalizedText | null;
    /**
     * Link Winery Id
     */
    link_winery_id?: string | null;
    /**
     * Link Expert Id
     */
    link_expert_id?: string | null;
    /**
     * Link Course Id
     */
    link_course_id?: string | null;
    /**
     * Link Lesson Id
     */
    link_lesson_id?: string | null;
};

/**
 * SetPortion
 */
export const SetPortion = {
    Bottle: 'bottle',
    Ml100: 'ml_100',
    Ml250: 'ml_250'
} as const;

/**
 * SetPortion
 */
export type SetPortion = typeof SetPortion[keyof typeof SetPortion];

/**
 * SetRefOut
 */
export type SetRefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Sku
     */
    sku: string;
    /**
     * Name
     */
    name: string;
    /**
     * Format
     */
    format: string;
    /**
     * Published
     */
    published: boolean;
};

/**
 * SetRowOut
 */
export type SetRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Sku
     */
    sku: string;
    /**
     * Name
     */
    name: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Items Count
     */
    items_count: number;
    /**
     * Items Label
     */
    items_label: string;
    /**
     * Regions Label
     */
    regions_label: string;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Available
     */
    available: number | null;
    /**
     * Available Label
     *
     * Остаток или «—» без учёта остатка
     */
    available_label: string;
    format: SetFormat;
    /**
     * Format Label
     */
    format_label: string;
    status: CatalogStatus;
    display_status: CatalogStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * SetSalesOut
 */
export type SetSalesOut = {
    /**
     * Orders Count
     */
    orders_count: number;
    revenue: Money;
    /**
     * Revenue Label
     */
    revenue_label: string;
    /**
     * Sold Count
     */
    sold_count: number;
    price: Money;
    /**
     * Available
     */
    available: number | null;
};

/**
 * SetWineOut
 */
export type SetWineOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     *
     * «Название · Винтаж»
     */
    title: string;
    /**
     * Name
     */
    name: string;
    /**
     * Vintage Label
     */
    vintage_label: string;
    winery: RefOut | null;
    region: RefOut | null;
    status: CatalogStatus;
    /**
     * Status Label
     */
    status_label: string;
    price: Money;
    /**
     * Volume Ml
     */
    volume_ml: number;
};

/**
 * ShareTarget
 */
export const ShareTarget = {
    Telegram: 'telegram',
    Vk: 'vk',
    Whatsapp: 'whatsapp',
    Instagram: 'instagram',
    Ok: 'ok',
    SaveImage: 'save_image'
} as const;

/**
 * ShareTarget
 */
export type ShareTarget = typeof ShareTarget[keyof typeof ShareTarget];

/**
 * ShareTargetOut
 */
export type ShareTargetOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string;
};

/**
 * ShippingCarrier
 */
export const ShippingCarrier = { None: 'none', DhlExpress: 'dhl_express' } as const;

/**
 * ShippingCarrier
 */
export type ShippingCarrier = typeof ShippingCarrier[keyof typeof ShippingCarrier];

/**
 * ShippingCostToBuyer
 */
export const ShippingCostToBuyer = { Included: 'included' } as const;

/**
 * ShippingCostToBuyer
 */
export type ShippingCostToBuyer = typeof ShippingCostToBuyer[keyof typeof ShippingCostToBuyer];

/**
 * SimilarOut
 */
export type SimilarOut = {
    /**
     * Items
     */
    items: Array<TastingCard>;
};

/**
 * SkipReason
 */
export const SkipReason = {
    CategoryOff: 'category_off',
    NoChannel: 'no_channel',
    NoConsent: 'no_consent',
    ConditionFailed: 'condition_failed',
    UserBlocked: 'user_blocked',
    UserDeleted: 'user_deleted',
    TemplateDisabled: 'template_disabled'
} as const;

/**
 * SkipReason
 */
export type SkipReason = typeof SkipReason[keyof typeof SkipReason];

/**
 * SlaOut
 */
export type SlaOut = {
    /**
     * Started At
     */
    started_at: string;
    /**
     * Due At
     *
     * Срок первого ответа; null — ответ уже дан, обращение на паузе или решено
     */
    due_at: string | null;
    /**
     * Paused Since
     */
    paused_since: string | null;
    /**
     * First Response At
     */
    first_response_at: string | null;
    /**
     * Overdue
     */
    overdue: boolean;
};

/**
 * SlaUnit
 */
export const SlaUnit = {
    Minutes: 'minutes',
    Hours: 'hours',
    BusinessDays: 'business_days'
} as const;

/**
 * SlaUnit
 */
export type SlaUnit = typeof SlaUnit[keyof typeof SlaUnit];

/**
 * SlotDayOut
 */
export type SlotDayOut = {
    /**
     * Date
     */
    date: string;
    /**
     * Slots
     */
    slots: Array<ExcursionSlotOut>;
};

/**
 * SlotIn
 */
export type SlotIn = {
    /**
     * Weekday
     */
    weekday: number;
    /**
     * Start
     */
    start: string;
    /**
     * End
     */
    end: string;
    /**
     * Seats
     */
    seats: number;
    /**
     * Excursion Id
     */
    excursion_id?: string | null;
};

/**
 * SlotOut
 */
export type SlotOut = {
    /**
     * Weekday
     */
    weekday: number;
    /**
     * Start
     */
    start: string;
    /**
     * End
     */
    end: string;
    /**
     * Seats
     */
    seats: number;
    /**
     * Excursion Id
     */
    excursion_id: string | null;
};

/**
 * SlotsOut
 */
export type SlotsOut = {
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Days
     */
    days: Array<SlotDayOut>;
};

/**
 * SocialAuthOut
 */
export type SocialAuthOut = {
    tokens: TokensOut;
    user: UserMeOut;
    guest_merge: GuestMergeOut;
    /**
     * Profile Completion Required
     */
    profile_completion_required: boolean;
    /**
     * Missing
     */
    missing: Array<string>;
    /**
     * Is New
     *
     * Аккаунт создан этим входом
     */
    is_new: boolean;
    /**
     * Linked By Email
     *
     * Вход привязан к аккаунту с той же почтой
     */
    linked_by_email: boolean;
    prefill: PrefillOut | null;
};

/**
 * SocialCompleteIn
 *
 * Дозаполнение после соцвхода (4.6.4): поля шага «О вас» без пароля.
 */
export type SocialCompleteIn = {
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Birth Date
     */
    birth_date: string;
    phone: PhoneIn;
    /**
     * Country Code
     */
    country_code?: string | null;
    /**
     * City Name
     */
    city_name: string;
    /**
     * City Suggestion Id
     */
    city_suggestion_id?: string | null;
    /**
     * Avatar File Id
     */
    avatar_file_id?: string | null;
    /**
     * Email Verification Token
     *
     * Почта, подтверждённая кодом, — если провайдер не вернул подтверждённую
     */
    email_verification_token?: string | null;
    /**
     * Consents
     */
    consents?: Array<ConsentItemIn>;
};

/**
 * SocialCompleteOut
 */
export type SocialCompleteOut = {
    user: UserMeOut;
    /**
     * Account Switched
     *
     * Подтверждённая почта принадлежит другому аккаунту: вход привязан к нему, черновой аккаунт удалён (BR-INT-SVC-17); новые токены — в `tokens`
     */
    account_switched: boolean;
    tokens: TokensOut | null;
};

/**
 * SocialLoginIn
 *
 * Результат нативного SDK провайдера (4.8.2): ID-токен (Google, Apple) или код авторизации
 * (VK, Яндекс, Google/Apple web); у Яндекса без `code_verifier` — токен LoginSDK.
 */
export type SocialLoginIn = {
    /**
     * Credential
     */
    credential: string;
    /**
     * Code Verifier
     */
    code_verifier?: string | null;
    /**
     * Redirect Uri
     */
    redirect_uri?: string | null;
    /**
     * Nonce
     */
    nonce?: string | null;
    /**
     * Device Id
     *
     * VK ID: device_id из SDK/колбэка
     */
    device_id?: string | null;
    apple_user?: AppleUserIn | null;
};

/**
 * SocialOut
 */
export type SocialOut = {
    /**
     * Kind
     */
    kind: 'telegram' | 'vk' | 'video' | 'site';
    /**
     * Label
     */
    label: string;
    /**
     * Url
     */
    url: string;
};

/**
 * Soil
 */
export const Soil = {
    LimestoneClay: 'limestone_clay',
    Marl: 'marl',
    Sandstone: 'sandstone',
    Gravel: 'gravel',
    Chernozem: 'chernozem',
    Volcanic: 'volcanic'
} as const;

/**
 * Soil
 */
export type Soil = typeof Soil[keyof typeof Soil];

/**
 * SoonChipOut
 */
export type SoonChipOut = {
    /**
     * Code
     */
    code: string;
    /**
     * Name
     */
    name: string;
};

/**
 * SpecCategoryKind
 */
export const SpecCategoryKind = {
    WineType: 'wine_type',
    Country: 'country',
    Region: 'region'
} as const;

/**
 * SpecCategoryKind
 */
export type SpecCategoryKind = typeof SpecCategoryKind[keyof typeof SpecCategoryKind];

/**
 * SpecStatus
 */
export const SpecStatus = { Active: 'active', Draft: 'draft' } as const;

/**
 * SpecStatus
 */
export type SpecStatus = typeof SpecStatus[keyof typeof SpecStatus];

/**
 * StampOut
 */
export type StampOut = {
    /**
     * Level
     */
    level: 'bronze' | 'gold' | null;
    /**
     * Image Url
     */
    image_url: string | null;
};

/**
 * StampRegionOut
 */
export type StampRegionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * StampRowOut
 */
export type StampRowOut = {
    /**
     * Id
     */
    id: string;
    user: GamUserRefOut;
    region: StampRegionOut;
    /**
     * Level
     */
    level: 'bronze' | 'gold';
    /**
     * Level Label
     */
    level_label: string;
    /**
     * Basis
     *
     * «За что» (BR-GAM-55)
     */
    basis: string;
    /**
     * Xp
     *
     * 0 — правило было выключено
     */
    xp: number;
    /**
     * Xp Label
     */
    xp_label: string;
    /**
     * Awarded At
     */
    awarded_at: string;
    /**
     * When Label
     */
    when_label: string;
};

/**
 * StampSlotOut
 */
export type StampSlotOut = {
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Region Name
     */
    region_name: string;
    /**
     * Level
     */
    level: 'bronze' | 'gold';
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Awarded At
     */
    awarded_at: string;
    /**
     * Label
     *
     * «Анапа исследован 12.09.2026»
     */
    label: string;
    /**
     * Explain
     */
    explain: string;
};

/**
 * StampsOut
 */
export type StampsOut = {
    /**
     * Items
     */
    items: Array<GamStampOut>;
    /**
     * Gold
     */
    gold: number;
    /**
     * Bronze
     */
    bronze: number;
    /**
     * Empty Text
     */
    empty_text: string;
};

/**
 * StampsPageOut
 */
export type StampsPageOut = {
    /**
     * Items
     */
    items: Array<StampRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
};

/**
 * StarShareOut
 */
export type StarShareOut = {
    /**
     * Stars
     */
    stars: number;
    /**
     * Count
     */
    count: number;
    /**
     * Pct
     */
    pct: number;
};

/**
 * StatsLevelOut
 */
export type StatsLevelOut = {
    /**
     * Level Id
     */
    level_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Users
     */
    users: number;
};

/**
 * StatsStampsOut
 */
export type StatsStampsOut = {
    /**
     * Bronze
     */
    bronze: number;
    /**
     * Gold
     */
    gold: number;
};

/**
 * StatsTierOut
 */
export type StatsTierOut = {
    /**
     * Tier Id
     */
    tier_id: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Title
     */
    title: string;
    /**
     * Users
     */
    users: number;
};

/**
 * StatusActorKind
 */
export const StatusActorKind = {
    User: 'user',
    Support: 'support',
    System: 'system'
} as const;

/**
 * StatusActorKind
 */
export type StatusActorKind = typeof StatusActorKind[keyof typeof StatusActorKind];

/**
 * StayIn
 */
export type StayIn = {
    hotel: LocalizedText;
    /**
     * Stay
     */
    stay?: Array<PairIn>;
    /**
     * Rooms
     */
    rooms: Array<TripRoomIn>;
    /**
     * Kids Pct Under 6
     */
    kids_pct_under_6: number;
    /**
     * Kids Pct 7 12
     */
    kids_pct_7_12: number;
    /**
     * Kids Pct 13 17
     */
    kids_pct_13_17: number;
};

/**
 * StayOut
 */
export type StayOut = {
    /**
     * Rooms
     */
    rooms: Array<StayRoomOut>;
};

/**
 * StayRoomOut
 */
export type StayRoomOut = {
    /**
     * Name
     */
    name: string | null;
    /**
     * Type
     */
    type: string;
    /**
     * Type Label
     */
    type_label: string;
    /**
     * Beds
     */
    beds: number;
    price_per_night: Money;
};

/**
 * StepIn
 */
export type StepIn = {
    title: LocalizedText;
    text: LocalizedText;
};

/**
 * StepOut
 */
export type StepOut = {
    title: LocalizedText;
    text: LocalizedText;
};

/**
 * StockIn
 */
export type StockIn = {
    /**
     * Stock
     */
    stock: number;
};

/**
 * StoreCode
 */
export const StoreCode = {
    Rustore: 'rustore',
    AppStore: 'app_store',
    GooglePlay: 'google_play'
} as const;

/**
 * StoreCode
 */
export type StoreCode = typeof StoreCode[keyof typeof StoreCode];

/**
 * StoreProductIn
 */
export type StoreProductIn = {
    /**
     * Product Id
     */
    product_id: string;
    /**
     * Active
     */
    active?: boolean;
};

/**
 * StoreProductOut
 */
export type StoreProductOut = {
    /**
     * Product Id
     */
    product_id: string;
    /**
     * Active
     */
    active: boolean;
};

/**
 * StoreProductsOut
 */
export type StoreProductsOut = {
    /**
     * Available
     *
     * false — модуль покупок в сторах (INT-PAY) не подключён: ID продуктов не хранятся, их проверка при публикации не применяется
     */
    available: boolean;
    /**
     * Stores
     *
     * Сторы инсталляции (`billing.stores`)
     */
    stores: Array<string>;
    /**
     * Items
     */
    items: {
        [key: string]: StoreProductOut;
    };
    /**
     * Hint
     */
    hint: string | null;
};

/**
 * StoreTilesOut
 *
 * Плитки карточки стора (15.12.2).
 */
export type StoreTilesOut = {
    /**
     * Products Active
     *
     * Продуктов заведено (активных)
     */
    products_active: number;
    /**
     * Unacknowledged
     *
     * Google: покупок без acknowledge
     */
    unacknowledged: number;
    /**
     * Last Notification At
     *
     * Последнее уведомление стора
     */
    last_notification_at: string | null;
    /**
     * Notifications Verified At
     *
     * Когда пришло тестовое уведомление (`TEST`) — режим уведомлений проверен
     */
    notifications_verified_at: string | null;
};

/**
 * StoryAdminOut
 */
export type StoryAdminOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
    status: StoryStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Subtitle
     */
    subtitle: string;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Items
     */
    items: Array<StoryItemOut>;
    /**
     * Frames
     */
    frames: number;
    /**
     * Frame Seconds
     */
    frame_seconds: number;
    /**
     * Duration Label
     */
    duration_label: string;
    /**
     * Total Label
     *
     * «всего {м:сс}»
     */
    total_label: string;
    /**
     * Note
     */
    note: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Link Label
     */
    link_label: string | null;
    ttl: StoryTtl;
    /**
     * Ttl Label
     */
    ttl_label: string;
    /**
     * Published At
     */
    published_at: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Expires Label
     *
     * «Истекает» / «Истекла»
     */
    expires_label: string;
    /**
     * Views Count
     */
    views_count: number;
    /**
     * Link Taps Count
     */
    link_taps_count: number;
    /**
     * Deleted By Author
     */
    deleted_by_author: boolean;
    panels: StoryPanelsOut;
    /**
     * History
     */
    history: Array<ContentHistoryOut>;
    can: StoryCan;
    /**
     * Toast
     */
    toast?: string | null;
};

/**
 * StoryBubbleOut
 */
export type StoryBubbleOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Label
     */
    label: string;
    /**
     * Image Url
     */
    image_url: string | null;
};

/**
 * StoryCan
 */
export type StoryCan = {
    /**
     * Hide
     */
    hide?: boolean;
    /**
     * Unhide
     */
    unhide?: boolean;
};

/**
 * StoryCreatedOut
 */
export type StoryCreatedOut = {
    /**
     * Id
     */
    id: string;
    status: StoryStatus;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Title
     */
    title: string;
    /**
     * Toast
     */
    toast: string;
};

/**
 * StoryItemIn
 */
export type StoryItemIn = {
    kind: StoryItemKind;
    /**
     * Text
     */
    text?: string | null;
    /**
     * Color
     */
    color?: string | null;
    /**
     * Emoji
     */
    emoji?: string | null;
    link_target_type?: StoryLinkTarget | null;
    /**
     * Link Target Id
     */
    link_target_id?: string | null;
    /**
     * Link Url
     */
    link_url?: string | null;
    /**
     * X
     */
    x?: number;
    /**
     * Y
     */
    y?: number;
};

/**
 * StoryItemKind
 */
export const StoryItemKind = {
    Text: 'text',
    Emoji: 'emoji',
    Link: 'link'
} as const;

/**
 * StoryItemKind
 */
export type StoryItemKind = typeof StoryItemKind[keyof typeof StoryItemKind];

/**
 * StoryItemOut
 */
export type StoryItemOut = {
    /**
     * Id
     */
    id: string;
    kind: StoryItemKind;
    /**
     * Text
     */
    text: string | null;
    /**
     * Color
     */
    color: string | null;
    /**
     * Emoji
     */
    emoji: string | null;
    link: StoryLinkOut | null;
    /**
     * X
     */
    x: number;
    /**
     * Y
     */
    y: number;
    /**
     * Z
     */
    z: number;
};

/**
 * StoryLinkOut
 */
export type StoryLinkOut = {
    target_type: StoryLinkTarget;
    /**
     * Target Id
     */
    target_id: string | null;
    /**
     * Url
     */
    url: string | null;
    /**
     * Label
     */
    label: string;
    /**
     * Target
     *
     * Экран приложения; внешняя ссылка — null
     */
    target: string | null;
};

/**
 * StoryLinkTarget
 */
export const StoryLinkTarget = {
    Wine: 'wine',
    Set: 'set',
    Drop: 'drop',
    Tasting: 'tasting',
    Excursion: 'excursion',
    Trip: 'trip',
    Course: 'course',
    Lesson: 'lesson',
    Publication: 'publication',
    External: 'external'
} as const;

/**
 * StoryLinkTarget
 */
export type StoryLinkTarget = typeof StoryLinkTarget[keyof typeof StoryLinkTarget];

/**
 * StoryOut
 */
export type StoryOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Items
     */
    items: Array<StoryItemOut>;
    /**
     * Published At
     */
    published_at: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Frame Seconds
     */
    frame_seconds: number;
    /**
     * Seen
     */
    seen: boolean;
    /**
     * Left Label
     *
     * «осталось»: «18 ч» / «7 дн» (BR-CNT-57)
     */
    left_label: string;
};

/**
 * StoryPanelsOut
 */
export type StoryPanelsOut = {
    /**
     * Views
     */
    views: number;
    /**
     * Views Note
     */
    views_note: string;
    /**
     * Link Taps
     */
    link_taps: number;
    /**
     * Link Taps Note
     */
    link_taps_note: string;
    /**
     * Author Member Id
     */
    author_member_id: string;
    /**
     * Author Name
     */
    author_name: string;
    link: ContentRefOut | null;
    /**
     * Link Note
     */
    link_note: string | null;
};

/**
 * StoryRingOut
 */
export type StoryRingOut = {
    /**
     * Author Member Id
     */
    author_member_id: string;
    /**
     * Name
     *
     * Первое слово имени
     */
    name: string;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * Initial
     */
    initial: string;
    /**
     * Role Label
     */
    role_label: string;
    /**
     * All Seen
     */
    all_seen: boolean;
    /**
     * Latest Published At
     */
    latest_published_at: string;
    /**
     * Target
     */
    target: string;
};

/**
 * StoryRowOut
 */
export type StoryRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Duration Label
     *
     * «м:сс» — 0:06 на кадр
     */
    duration_label: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Link Label
     *
     * Подпись стикера, «Ссылка: {домен}» или null
     */
    link_label: string | null;
    ttl: StoryTtl;
    /**
     * Ttl Label
     */
    ttl_label: string;
    /**
     * Published At
     */
    published_at: string;
    /**
     * Views Count
     */
    views_count: number;
    status: StoryStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Deleted By Author
     */
    deleted_by_author: boolean;
    can: StoryCan;
};

/**
 * StoryStatus
 */
export const StoryStatus = {
    Active: 'active',
    Expired: 'expired',
    Hidden: 'hidden'
} as const;

/**
 * StoryStatus
 */
export type StoryStatus = typeof StoryStatus[keyof typeof StoryStatus];

/**
 * StoryTtl
 */
export const StoryTtl = { H24: 'h24', Week: 'week' } as const;

/**
 * StoryTtl
 */
export type StoryTtl = typeof StoryTtl[keyof typeof StoryTtl];

/**
 * StoryViewsIn
 */
export type StoryViewsIn = {
    /**
     * Story Ids
     */
    story_ids: Array<string>;
};

/**
 * StudioCommentOut
 */
export type StudioCommentOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Author Name
     */
    author_name: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Time Label
     */
    time_label: string;
    /**
     * Text
     */
    text: string;
    reply: CommentReplyOut | null;
};

/**
 * StudioCommentsOut
 */
export type StudioCommentsOut = {
    publication: StudioCommentsPublicationOut;
    /**
     * Items
     */
    items: Array<StudioCommentOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Empty Text
     */
    empty_text: string | null;
};

/**
 * StudioCommentsPublicationOut
 */
export type StudioCommentsPublicationOut = {
    /**
     * Id
     */
    id: string;
    kind: PublicationKind;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     *
     * «{N} просмотров · {N} комментариев»
     */
    meta: string;
};

/**
 * StudioOut
 */
export type StudioOut = {
    /**
     * Title
     *
     * «Кабинет эксперта» / «Кабинет автора»
     */
    title: string;
    /**
     * Name
     */
    name: string;
    /**
     * Role Label
     */
    role_label: string;
    /**
     * Subtitle
     *
     * «{роль} · подтверждённый автор»
     */
    subtitle: string;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * Initial
     */
    initial: string;
    /**
     * Subscribers
     */
    subscribers: number;
    /**
     * Publications
     */
    publications: number;
    /**
     * Views
     */
    views: number;
    /**
     * Likes
     */
    likes: number;
    /**
     * Subscribers Label
     */
    subscribers_label: string;
    /**
     * Publications Label
     */
    publications_label: string;
    /**
     * Views Label
     */
    views_label: string;
    /**
     * Likes Label
     */
    likes_label: string;
};

/**
 * StudioPublicationIn
 */
export type StudioPublicationIn = {
    /**
     * Title
     */
    title: string;
    /**
     * Body
     *
     * PublicationBody `{blocks: [...]}` (12.3.2.1)
     */
    body: {
        [key: string]: unknown;
    };
    /**
     * Media Ids
     */
    media_ids?: Array<string>;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Wine Ids
     */
    wine_ids?: Array<string>;
    /**
     * Experiences
     */
    experiences?: Array<ExperienceTagIn>;
    /**
     * Winery Id
     */
    winery_id?: string | null;
};

/**
 * StudioPublicationOut
 */
export type StudioPublicationOut = {
    card: PublicationCardOut;
    status: PublicationStatus;
    /**
     * Draft Note
     */
    draft_note: string | null;
    /**
     * Tags
     */
    tags: Array<ContentRefOut>;
    /**
     * Views Label
     */
    views_label: string;
    /**
     * Likes Label
     */
    likes_label: string;
    /**
     * Comments Label
     */
    comments_label: string;
    /**
     * Comments Target
     */
    comments_target: string;
};

/**
 * StudioPublicationsOut
 */
export type StudioPublicationsOut = {
    /**
     * Items
     */
    items: Array<StudioPublicationOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Empty Text
     */
    empty_text: string | null;
};

/**
 * StudioStoryIn
 */
export type StudioStoryIn = {
    /**
     * Image Media Id
     */
    image_media_id?: string | null;
    ttl?: StoryTtl;
    /**
     * Items
     */
    items?: Array<StoryItemIn>;
    /**
     * Winery Id
     */
    winery_id?: string | null;
};

/**
 * StudioStoryOut
 */
export type StudioStoryOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Items
     */
    items: Array<StoryItemOut>;
    /**
     * Published At
     */
    published_at: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Frame Seconds
     */
    frame_seconds: number;
    /**
     * Seen
     */
    seen: boolean;
    /**
     * Left Label
     *
     * «осталось»: «18 ч» / «7 дн» (BR-CNT-57)
     */
    left_label: string;
    status: StoryStatus;
    /**
     * Views Count
     */
    views_count: number;
    /**
     * Views Label
     */
    views_label: string;
};

/**
 * StudyCardOut
 */
export type StudyCardOut = {
    /**
     * Course Id
     */
    course_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Meta
     */
    meta: string;
    /**
     * Done
     */
    done: number;
    /**
     * Lessons Total
     */
    lessons_total: number;
    /**
     * Opened
     */
    opened: boolean;
    /**
     * Target
     */
    target: string;
};

/**
 * SubscriptionLogRowOut
 */
export type SubscriptionLogRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * User Id
     */
    user_id: string | null;
    /**
     * User Name
     *
     * Имя или «Гость»
     */
    user_name: string;
    /**
     * Author Member Id
     */
    author_member_id: string;
    /**
     * Author Name
     */
    author_name: string;
    /**
     * Author Role
     */
    author_role: string;
    /**
     * Author Role Label
     */
    author_role_label: string;
    /**
     * Notifications
     */
    notifications: boolean;
    /**
     * Notifications Label
     */
    notifications_label: string;
    /**
     * At
     */
    at: string;
    /**
     * Status
     */
    status: string;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * SummaryBonusesOut
 */
export type SummaryBonusesOut = {
    /**
     * Earned
     */
    earned: number;
    /**
     * Spent
     */
    spent: number;
    /**
     * Balance
     */
    balance: number;
    /**
     * Label
     */
    label: string;
};

/**
 * SummaryDiscountsOut
 */
export type SummaryDiscountsOut = {
    /**
     * Learning Pct
     */
    learning_pct: number;
    /**
     * Wine Pct
     */
    wine_pct: number;
    /**
     * Label
     */
    label: string;
};

/**
 * SummaryFirstRegionOut
 */
export type SummaryFirstRegionOut = {
    /**
     * Status
     */
    status: string;
    /**
     * Pct
     */
    pct: number | null;
    /**
     * Used At
     */
    used_at: string | null;
    /**
     * Label
     */
    label: string;
};

/**
 * SummaryLevelOut
 */
export type SummaryLevelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string | null;
    /**
     * Min Xp
     */
    min_xp: number;
};

/**
 * SummaryLinksOut
 */
export type SummaryLinksOut = {
    xplog: GamSectionLinkOut;
    stamplog: GamSectionLinkOut;
};

/**
 * SummaryNeedOut
 */
export type SummaryNeedOut = {
    /**
     * K
     */
    k: string;
    /**
     * V
     */
    v: number;
    /**
     * All
     */
    all: number;
    /**
     * Done
     */
    done: boolean;
    /**
     * Label
     */
    label: string;
};

/**
 * SummaryRowOut
 */
export type SummaryRowOut = {
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * SummaryStampsOut
 */
export type SummaryStampsOut = {
    /**
     * Bronze
     */
    bronze: number;
    /**
     * Gold
     */
    gold: number;
};

/**
 * SummaryTierOut
 */
export type SummaryTierOut = {
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Title
     */
    title: string;
    /**
     * Label
     */
    label?: string | null;
};

/**
 * SupportChannel
 */
export const SupportChannel = {
    App: 'app',
    Email: 'email',
    Phone: 'phone'
} as const;

/**
 * SupportChannel
 */
export type SupportChannel = typeof SupportChannel[keyof typeof SupportChannel];

/**
 * SupportHoursOut
 */
export type SupportHoursOut = {
    /**
     * From
     */
    from: string;
    /**
     * To
     */
    to: string;
    /**
     * Timezone
     */
    timezone: string;
};

/**
 * SupportPriorityCode
 */
export const SupportPriorityCode = {
    Low: 'low',
    Normal: 'normal',
    High: 'high',
    Urgent: 'urgent'
} as const;

/**
 * SupportPriorityCode
 */
export type SupportPriorityCode = typeof SupportPriorityCode[keyof typeof SupportPriorityCode];

/**
 * SupportTopicAppOut
 */
export type SupportTopicAppOut = {
    code: SupportTopicCode;
    /**
     * Name
     */
    name: string;
    /**
     * Needs Ref
     */
    needs_ref: boolean;
};

/**
 * SupportTopicCode
 */
export const SupportTopicCode = {
    Order: 'order',
    Pay: 'pay',
    Book: 'book',
    Trip: 'trip',
    Learn: 'learn',
    Login: 'login',
    Other: 'other'
} as const;

/**
 * SupportTopicCode
 */
export type SupportTopicCode = typeof SupportTopicCode[keyof typeof SupportTopicCode];

/**
 * SupportTopicsOut
 */
export type SupportTopicsOut = {
    /**
     * Items
     */
    items: Array<SupportTopicAppOut>;
};

/**
 * SuppressionCan
 */
export type SuppressionCan = {
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * SuppressionCreateIn
 */
export type SuppressionCreateIn = {
    /**
     * Email
     */
    email: string;
    scope: SuppressionScope;
    /**
     * Note
     */
    note?: string | null;
};

/**
 * SuppressionOut
 */
export type SuppressionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Email
     */
    email: string;
    scope: SuppressionScope;
    /**
     * Scope Label
     */
    scope_label: string;
    reason: SuppressionReason;
    /**
     * Reason Label
     */
    reason_label: string;
    /**
     * Source Message Id
     */
    source_message_id: string | null;
    /**
     * Note
     */
    note: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Expires At
     */
    expires_at: string | null;
    can: SuppressionCan;
};

/**
 * SuppressionReason
 */
export const SuppressionReason = {
    HardBounce: 'hard_bounce',
    Complaint: 'complaint',
    Unsubscribed: 'unsubscribed',
    RelayDisabled: 'relay_disabled',
    Manual: 'manual'
} as const;

/**
 * SuppressionReason
 */
export type SuppressionReason = typeof SuppressionReason[keyof typeof SuppressionReason];

/**
 * SuppressionScope
 */
export const SuppressionScope = { All: 'all', Marketing: 'marketing' } as const;

/**
 * SuppressionScope
 */
export type SuppressionScope = typeof SuppressionScope[keyof typeof SuppressionScope];

/**
 * SystemCountryListOut
 */
export type SystemCountryListOut = {
    /**
     * Items
     */
    items: Array<SystemCountryOut>;
};

/**
 * SystemCountryOut
 */
export type SystemCountryOut = {
    /**
     * Code
     */
    code: string;
    name: LocalizedText | null;
    /**
     * In Catalog
     *
     * Страна уже добавлена в справочник «Страны»
     */
    in_catalog: boolean;
};

/**
 * TagOptionOut
 */
export type TagOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Type
     */
    type: string;
};

/**
 * TargetOut
 */
export type TargetOut = {
    type: BookingTarget;
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Section
     */
    section: string;
};

/**
 * TaskOut
 */
export type TaskOut = {
    /**
     * Kind
     */
    kind: string;
    /**
     * Group
     */
    group: 'study' | 'taste' | 'gold' | 'extra';
    /**
     * Ref Type
     */
    ref_type: string;
    /**
     * Ref Id
     */
    ref_id: string | null;
    /**
     * Title
     *
     * Заголовок-CTA вида задания
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Done
     */
    done: boolean;
    /**
     * Is New
     */
    is_new: boolean;
    /**
     * When
     */
    when: string | null;
    /**
     * Target
     */
    target: string | null;
};

/**
 * TasteCardOut
 */
export type TasteCardOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Text
     */
    text: string;
    /**
     * Meta
     */
    meta: string;
    /**
     * Xp
     */
    xp: number | null;
    /**
     * Done
     */
    done: boolean;
    /**
     * Target
     */
    target: string;
    /**
     * Sheet Title
     */
    sheet_title: string | null;
    /**
     * Tasted
     */
    tasted: Array<TastedWineOut>;
};

/**
 * TasteChar
 */
export const TasteChar = {
    Sweetness: 'sweetness',
    Acidity: 'acidity',
    Body: 'body',
    Tannin: 'tannin',
    Aroma: 'aroma',
    Finish: 'finish'
} as const;

/**
 * TasteChar
 */
export type TasteChar = typeof TasteChar[keyof typeof TasteChar];

/**
 * TasteIn
 */
export type TasteIn = {
    /**
     * Sweetness
     */
    sweetness: number;
    /**
     * Acidity
     */
    acidity: number;
    /**
     * Tannins
     */
    tannins: number;
    /**
     * Body
     */
    body: number;
    /**
     * Fruit
     */
    fruit: number;
    /**
     * Oak
     */
    oak: number;
    /**
     * Freshness
     */
    freshness: number;
    /**
     * Aroma
     */
    aroma: number;
};

/**
 * TasteOut
 */
export type TasteOut = {
    /**
     * Sweetness
     */
    sweetness: number;
    /**
     * Acidity
     */
    acidity: number;
    /**
     * Tannins
     */
    tannins: number;
    /**
     * Body
     */
    body: number;
    /**
     * Fruit
     */
    fruit: number;
    /**
     * Oak
     */
    oak: number;
    /**
     * Freshness
     */
    freshness: number;
    /**
     * Aroma
     */
    aroma: number;
};

/**
 * TastePatchIn
 */
export type TastePatchIn = {
    /**
     * Sweetness
     */
    sweetness?: number | null;
    /**
     * Acidity
     */
    acidity?: number | null;
    /**
     * Tannins
     */
    tannins?: number | null;
    /**
     * Body
     */
    body?: number | null;
    /**
     * Fruit
     */
    fruit?: number | null;
    /**
     * Oak
     */
    oak?: number | null;
    /**
     * Freshness
     */
    freshness?: number | null;
    /**
     * Aroma
     */
    aroma?: number | null;
};

/**
 * TasteScaleOut
 */
export type TasteScaleOut = {
    /**
     * Axis
     */
    axis: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     *
     * 0…5
     */
    value: number;
    /**
     * Value Label
     *
     * Левый полюс / «средне» / правый полюс
     */
    value_label: string;
};

/**
 * TasteStepOut
 */
export type TasteStepOut = {
    /**
     * Done
     */
    done: boolean;
    /**
     * N
     */
    n: number;
};

/**
 * TasteValues
 */
export type TasteValues = {
    /**
     * Sweetness
     */
    sweetness: number;
    /**
     * Acidity
     */
    acidity: number;
    /**
     * Tannins
     */
    tannins: number;
    /**
     * Body
     */
    body: number;
    /**
     * Fruit
     */
    fruit: number;
    /**
     * Oak
     */
    oak: number;
    /**
     * Freshness
     */
    freshness: number;
    /**
     * Aroma
     */
    aroma: number;
};

/**
 * TastedWineOut
 */
export type TastedWineOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Rating
     */
    rating: number | null;
    /**
     * Accuracy Label
     */
    accuracy_label: string | null;
};

/**
 * TastingAdmin
 */
export type TastingAdmin = {
    /**
     * Id
     */
    id: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Subtitle
     */
    subtitle: string;
    title: LocalizedText;
    format: TastingFormat;
    /**
     * Format Label
     */
    format_label: string;
    kind: TastingKind;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Starts At
     */
    starts_at: string;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Date Label
     */
    date_label: string;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Seats Max
     */
    seats_max: number;
    /**
     * Seats Taken
     */
    seats_taken: number;
    /**
     * Seats Label
     */
    seats_label: string;
    /**
     * Waitlist Count
     */
    waitlist_count: number;
    /**
     * Waitlist Label
     */
    waitlist_label: string | null;
    region: RefOut | null;
    host: HostOut | null;
    /**
     * Conference Url
     */
    conference_url: string | null;
    /**
     * Address
     */
    address: string | null;
    venue_winery: RefOut | null;
    /**
     * Place Label
     */
    place_label: string;
    /**
     * Is Paid
     */
    is_paid: boolean;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Access Label
     */
    access_label: string;
    /**
     * Cancel Policy Text
     */
    cancel_policy_text: string | null;
    lifecycle: EventLifecycle;
    /**
     * Lifecycle Label
     */
    lifecycle_label: string;
    /**
     * Cancelled At
     */
    cancelled_at: string | null;
    /**
     * Cancel Reason
     */
    cancel_reason: string | null;
    description: LocalizedText | null;
    /**
     * Details
     */
    details: Array<PairOut>;
    /**
     * Steps
     */
    steps: Array<StepOut>;
    /**
     * Media
     */
    media: Array<EventMediaOut>;
    /**
     * Wines
     */
    wines: Array<TastingWineOut>;
    /**
     * Sets
     */
    sets: Array<SetRefOut>;
    /**
     * Reference Filled
     */
    reference_filled: number;
    /**
     * Reference Total
     */
    reference_total: number;
    /**
     * Reference Label
     */
    reference_label: string | null;
    /**
     * Summary
     */
    summary: Array<ExpTileOut>;
    moderation: ModerationOut;
    /**
     * Archived At
     */
    archived_at: string | null;
    archive_reason: EventArchiveReason | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    can: CardCan;
};

/**
 * TastingCard
 */
export type TastingCard = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    format: TastingFormat;
    kind: TastingKind;
    /**
     * Is Blind
     */
    is_blind: boolean;
    /**
     * Starts At
     */
    starts_at: string;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Overline
     */
    overline: string;
    /**
     * Meta
     */
    meta: string;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Seats Free
     */
    seats_free: number;
    /**
     * Seats Max
     */
    seats_max: number;
    /**
     * Seats Label
     */
    seats_label: string;
    /**
     * Is Full
     */
    is_full: boolean;
    lifecycle: EventLifecycle;
    region: RefOut | null;
    host: HostOut | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Attended
     */
    attended?: boolean;
    /**
     * Status Chip
     */
    status_chip?: string | null;
};

/**
 * TastingCreateIn
 */
export type TastingCreateIn = {
    title: LocalizedText;
    format: TastingFormat;
    kind: TastingKind;
    /**
     * Starts At
     */
    starts_at: string;
    /**
     * Timezone
     */
    timezone?: string | null;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Seats Max
     */
    seats_max: number;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Host Id
     *
     * Пусто у не-admin — ведущий он сам
     */
    host_id?: string | null;
    /**
     * Conference Url
     */
    conference_url?: string | null;
    /**
     * Address
     */
    address?: string | null;
    /**
     * Venue Winery Id
     */
    venue_winery_id?: string | null;
    /**
     * Is Paid
     */
    is_paid?: boolean;
    price?: Money | null;
    description?: LocalizedText | null;
    /**
     * Details
     *
     * Пусто — тексты BR-EXP-05
     */
    details?: Array<PairIn> | null;
    /**
     * Steps
     */
    steps?: Array<StepIn> | null;
    /**
     * Wines
     */
    wines?: Array<TastingWineIn>;
    /**
     * Set Ids
     */
    set_ids?: Array<string>;
    /**
     * Media
     */
    media?: Array<EventMediaIn>;
};

/**
 * TastingDetail
 */
export type TastingDetail = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    format: TastingFormat;
    kind: TastingKind;
    /**
     * Is Blind
     */
    is_blind: boolean;
    /**
     * Starts At
     */
    starts_at: string;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Duration Min
     */
    duration_min: number;
    /**
     * Overline
     */
    overline: string;
    /**
     * Meta
     */
    meta: string;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Seats Free
     */
    seats_free: number;
    /**
     * Seats Max
     */
    seats_max: number;
    /**
     * Seats Label
     */
    seats_label: string;
    /**
     * Is Full
     */
    is_full: boolean;
    lifecycle: EventLifecycle;
    region: RefOut | null;
    host: HostOut | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Attended
     */
    attended?: boolean;
    /**
     * Status Chip
     */
    status_chip?: string | null;
    /**
     * Published
     *
     * false — снято или в архиве: открыт по брони (9.6)
     */
    published: boolean;
    /**
     * Description
     */
    description: string | null;
    /**
     * Caption
     */
    caption: string;
    /**
     * Price Unit Label
     */
    price_unit_label: string | null;
    /**
     * Access Label
     */
    access_label: string | null;
    /**
     * Cancel Policy Text
     */
    cancel_policy_text: string;
    /**
     * Details
     */
    details: Array<AppPair>;
    /**
     * Steps
     */
    steps: Array<AppStep>;
    /**
     * Media
     */
    media: Array<AppMedia>;
    /**
     * Wines
     */
    wines: Array<TastingWineApp>;
    /**
     * Sets
     */
    sets: Array<SetApp>;
    kit: KitOut | null;
    venue: RefOut | null;
    /**
     * Address
     */
    address: string | null;
    /**
     * One Seat
     */
    one_seat: boolean;
    /**
     * One Seat Label
     */
    one_seat_label: string | null;
    me: MeOut | null;
};

/**
 * TastingFormat
 */
export const TastingFormat = { Online: 'online', Offline: 'offline' } as const;

/**
 * TastingFormat
 */
export type TastingFormat = typeof TastingFormat[keyof typeof TastingFormat];

/**
 * TastingKind
 */
export const TastingKind = { Open: 'open', Blind: 'blind' } as const;

/**
 * TastingKind
 */
export type TastingKind = typeof TastingKind[keyof typeof TastingKind];

/**
 * TastingPatchIn
 */
export type TastingPatchIn = {
    title?: LocalizedText | null;
    format?: TastingFormat | null;
    kind?: TastingKind | null;
    /**
     * Starts At
     */
    starts_at?: string | null;
    /**
     * Timezone
     */
    timezone?: string | null;
    /**
     * Duration Min
     */
    duration_min?: number | null;
    /**
     * Seats Max
     */
    seats_max?: number | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Host Id
     */
    host_id?: string | null;
    /**
     * Conference Url
     */
    conference_url?: string | null;
    /**
     * Address
     */
    address?: string | null;
    /**
     * Venue Winery Id
     */
    venue_winery_id?: string | null;
    /**
     * Is Paid
     */
    is_paid?: boolean | null;
    price?: Money | null;
};

/**
 * TastingRefOut
 */
export type TastingRefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Starts At
     */
    starts_at: string | null;
    /**
     * Is Blind
     */
    is_blind: boolean;
};

/**
 * TastingRow
 */
export type TastingRow = {
    /**
     * Id
     */
    id: string;
    title: LocalizedText;
    /**
     * Title Text
     */
    title_text: string;
    format: TastingFormat;
    /**
     * Format Label
     */
    format_label: string;
    kind: TastingKind;
    /**
     * Kind Label
     */
    kind_label: string;
    host: RefOut | null;
    /**
     * Place Label
     */
    place_label: string;
    region: RefOut | null;
    /**
     * Starts At
     */
    starts_at: string;
    /**
     * Timezone
     */
    timezone: string;
    /**
     * Date Label
     */
    date_label: string;
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Seats Taken
     */
    seats_taken: number;
    /**
     * Seats Max
     */
    seats_max: number;
    /**
     * Seats Label
     */
    seats_label: string;
    moderation_status: ModerationStatus;
    lifecycle: EventLifecycle;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Is Live
     */
    is_live: boolean;
    /**
     * Archived
     */
    archived: boolean;
    can: RowCan;
};

/**
 * TastingWineApp
 *
 * Вино события. Вслепую до раскрытия — только номер (BR-EXP-22).
 */
export type TastingWineApp = {
    /**
     * Position
     */
    position: number;
    /**
     * Label
     */
    label: string;
    /**
     * Kit Position
     */
    kit_position?: number | null;
    /**
     * Revealed
     */
    revealed?: boolean;
    wine?: WineRefApp | null;
    /**
     * Grape
     */
    grape?: string | null;
    /**
     * Country
     */
    country?: string | null;
    /**
     * Region
     */
    region?: string | null;
    /**
     * Expert Word
     */
    expert_word?: string | null;
};

/**
 * TastingWineIn
 */
export type TastingWineIn = {
    /**
     * Wine Id
     */
    wine_id: string;
    /**
     * Ref Sweetness
     */
    ref_sweetness?: number | null;
    /**
     * Ref Acidity
     */
    ref_acidity?: number | null;
    /**
     * Ref Body
     */
    ref_body?: number | null;
    /**
     * Ref Tannin
     */
    ref_tannin?: number | null;
    /**
     * Ref Aroma
     */
    ref_aroma?: number | null;
    /**
     * Ref Finish
     */
    ref_finish?: number | null;
    /**
     * Ref Grape
     */
    ref_grape?: string | null;
    /**
     * Ref Country Id
     */
    ref_country_id?: string | null;
    /**
     * Ref Region Id
     */
    ref_region_id?: string | null;
    ref_climate?: RefClimate | null;
    expert_word?: LocalizedText | null;
    /**
     * Kit Position
     */
    kit_position?: number | null;
};

/**
 * TastingWineOut
 */
export type TastingWineOut = {
    /**
     * Position
     */
    position: number;
    wine: WineRefOut | null;
    /**
     * Ref Sweetness
     */
    ref_sweetness: number | null;
    /**
     * Ref Acidity
     */
    ref_acidity: number | null;
    /**
     * Ref Body
     */
    ref_body: number | null;
    /**
     * Ref Tannin
     */
    ref_tannin: number | null;
    /**
     * Ref Aroma
     */
    ref_aroma: number | null;
    /**
     * Ref Finish
     */
    ref_finish: number | null;
    /**
     * Ref Grape
     */
    ref_grape: string | null;
    ref_country: RefOut | null;
    ref_region: RefOut | null;
    ref_climate: RefClimate | null;
    /**
     * Ref Climate Label
     */
    ref_climate_label: string | null;
    expert_word: LocalizedText | null;
    /**
     * Kit Position
     */
    kit_position: number | null;
    /**
     * Reference Filled
     */
    reference_filled: boolean;
    /**
     * Reference Label
     *
     * «скрыто до раскрытия» / «эталон не заполнен»
     */
    reference_label: string;
};

/**
 * TastingWinesIn
 */
export type TastingWinesIn = {
    /**
     * Wines
     */
    wines?: Array<TastingWineIn>;
    /**
     * Set Ids
     */
    set_ids?: Array<string>;
};

/**
 * TeamMemberSource
 */
export const TeamMemberSource = { AdminRegistration: 'admin_registration', ExpertApplication: 'expert_application' } as const;

/**
 * TeamMemberSource
 */
export type TeamMemberSource = typeof TeamMemberSource[keyof typeof TeamMemberSource];

/**
 * TeamMemberStatus
 */
export const TeamMemberStatus = {
    Active: 'active',
    PendingReview: 'pending_review',
    NeedsInfo: 'needs_info',
    Rejected: 'rejected',
    Blocked: 'blocked'
} as const;

/**
 * TeamMemberStatus
 */
export type TeamMemberStatus = typeof TeamMemberStatus[keyof typeof TeamMemberStatus];

/**
 * TeamRole
 */
export const TeamRole = {
    Admin: 'admin',
    Winemaker: 'winemaker',
    Expert: 'expert'
} as const;

/**
 * TeamRole
 */
export type TeamRole = typeof TeamRole[keyof typeof TeamRole];

/**
 * TemplateCan
 */
export type TemplateCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Enable
     */
    enable?: boolean;
    /**
     * Disable
     */
    disable?: boolean;
    /**
     * Reset
     */
    reset?: boolean;
};

/**
 * TemplateCardOut
 */
export type TemplateCardOut = {
    /**
     * Code
     */
    code: string;
    section: NotificationSection;
    /**
     * Section Label
     */
    section_label: string;
    /**
     * Name
     *
     * «Уведомление» — на языке админки
     */
    name: string;
    channels: NotificationChannels;
    /**
     * Channels Label
     *
     * «Пуш», «Почта», «Пуш и почта»
     */
    channels_label: string;
    /**
     * Trigger Event
     */
    trigger_event: string;
    /**
     * Trigger Label
     *
     * «Когда отправляется»
     */
    trigger_label: string;
    /**
     * Body
     *
     * «Текст» — плейсхолдеры в фигурных скобках
     */
    body: string;
    audience: NotificationAudience;
    /**
     * Audience Label
     *
     * «Кому»
     */
    audience_label: string;
    preference_category: PreferenceCategory;
    /**
     * Category Label
     */
    category_label: string;
    inbox_kind: InboxKind;
    /**
     * Is Enabled
     */
    is_enabled: boolean;
    /**
     * Customized
     *
     * Тексты изменены в админке (не тексты релиза)
     */
    customized: boolean;
    /**
     * Managed
     *
     * false — письмо отправляет не SRV (код подтверждения): только справка
     */
    managed: boolean;
    /**
     * Sent 30D
     *
     * «Отправлено за 30 дней» — срабатываний со статусом done
     */
    sent_30d: number;
    can: TemplateCan;
    /**
     * «Уведомление» на языках рынка (правится)
     */
    texts_name: LocalizedText;
    /**
     * «Текст» на языках рынка (правится)
     */
    texts_body: LocalizedText;
    /**
     * Variants
     *
     * Варианты текста из каталога («Для правок», «онлайн», «без скидки на обучение»)
     */
    variants: {
        [key: string]: LocalizedText;
    };
    /**
     * Placeholders
     *
     * Плейсхолдеры, которые подставляются автоматически (правка не может добавить другие)
     */
    placeholders: Array<string>;
    /**
     * Email Layout
     *
     * Шаблон письма Mailer (17.9.3)
     */
    email_layout: string | null;
    /**
     * Deeplink
     *
     * Маршрут приложения
     */
    deeplink: string | null;
    /**
     * Feed Label
     *
     * Надпись записи ленты
     */
    feed_label: string | null;
    /**
     * Customized At
     */
    customized_at: string | null;
    stats: TemplateStatsOut;
    /**
     * History
     */
    history: Array<TemplateHistoryOut>;
};

/**
 * TemplateHistoryOut
 */
export type TemplateHistoryOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Actor Name
     */
    actor_name: string;
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Summary
     */
    summary: string;
};

/**
 * TemplatePatchIn
 *
 * Правка текстов системного уведомления (момент отправки, каналы и получатели — релизом).
 * Тексты — на всех языках рынка; плейсхолдеры — только из текста релиза.
 */
export type TemplatePatchIn = {
    name?: LocalizedText | null;
    body?: LocalizedText | null;
    /**
     * Variants
     */
    variants?: {
        [key: string]: LocalizedText;
    } | null;
};

/**
 * TemplateRowOut
 */
export type TemplateRowOut = {
    /**
     * Code
     */
    code: string;
    section: NotificationSection;
    /**
     * Section Label
     */
    section_label: string;
    /**
     * Name
     *
     * «Уведомление» — на языке админки
     */
    name: string;
    channels: NotificationChannels;
    /**
     * Channels Label
     *
     * «Пуш», «Почта», «Пуш и почта»
     */
    channels_label: string;
    /**
     * Trigger Event
     */
    trigger_event: string;
    /**
     * Trigger Label
     *
     * «Когда отправляется»
     */
    trigger_label: string;
    /**
     * Body
     *
     * «Текст» — плейсхолдеры в фигурных скобках
     */
    body: string;
    audience: NotificationAudience;
    /**
     * Audience Label
     *
     * «Кому»
     */
    audience_label: string;
    preference_category: PreferenceCategory;
    /**
     * Category Label
     */
    category_label: string;
    inbox_kind: InboxKind;
    /**
     * Is Enabled
     */
    is_enabled: boolean;
    /**
     * Customized
     *
     * Тексты изменены в админке (не тексты релиза)
     */
    customized: boolean;
    /**
     * Managed
     *
     * false — письмо отправляет не SRV (код подтверждения): только справка
     */
    managed: boolean;
    /**
     * Sent 30D
     *
     * «Отправлено за 30 дней» — срабатываний со статусом done
     */
    sent_30d: number;
    can: TemplateCan;
};

/**
 * TemplateStatsOut
 */
export type TemplateStatsOut = {
    /**
     * Sent 30D
     */
    sent_30d: number;
    /**
     * Skipped 30D
     */
    skipped_30d: number;
    /**
     * Scheduled
     */
    scheduled: number;
};

/**
 * TemplatesCountersOut
 */
export type TemplatesCountersOut = {
    /**
     * Total
     *
     * «Системные ({N})» — без фильтров
     */
    total: number;
    /**
     * Push
     */
    push: number;
    /**
     * Email
     */
    email: number;
    /**
     * By Section
     *
     * «{раздел} · {N}» с учётом канала
     */
    by_section: Array<SectionCountOut>;
};

/**
 * TemplatesPageOut
 */
export type TemplatesPageOut = {
    /**
     * Items
     */
    items: Array<TemplateRowOut>;
    /**
     * Total
     */
    total: number;
    counters: TemplatesCountersOut;
    /**
     * Footer
     *
     * Подпись под таблицей «Системные» (13.7.3.1)
     */
    footer: string;
    can?: ListCan;
};

/**
 * TestCan
 */
export type TestCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Submit
     */
    submit?: boolean;
    /**
     * Submit Edits
     */
    submit_edits?: boolean;
    /**
     * Waiting
     *
     * Эксперт: неактивная кнопка «Ждёт решения администратора»
     */
    waiting?: boolean;
    /**
     * Decide
     */
    decide?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Edit Price
     */
    edit_price?: boolean;
    /**
     * Edit Store Products
     */
    edit_store_products?: boolean;
    /**
     * Edit Questions
     */
    edit_questions?: boolean;
};

/**
 * TestCreateIn
 */
export type TestCreateIn = {
    title: LocalizedText;
    kind: TestKind;
    /**
     * Country Id
     *
     * Обязательна для «Квиз страны»
     */
    country_id?: string | null;
    /**
     * Media
     */
    media?: Array<MediaItemIn>;
};

/**
 * TestKind
 */
export const TestKind = {
    LessonTest: 'lesson_test',
    CountryQuiz: 'country_quiz',
    LevelCheck: 'level_check'
} as const;

/**
 * TestKind
 */
export type TestKind = typeof TestKind[keyof typeof TestKind];

/**
 * TestOptionIn
 */
export type TestOptionIn = {
    /**
     * Id
     */
    id?: string | null;
    text: LocalizedText;
    /**
     * Is Correct
     */
    is_correct?: boolean;
};

/**
 * TestOptionOut
 */
export type TestOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Position
     */
    position: number;
    /**
     * Letter
     *
     * А, Б, В… (A, B, C… для en/es)
     */
    letter: string;
    text: LocalizedText | null;
    /**
     * Is Correct
     */
    is_correct: boolean;
};

/**
 * TestOut
 */
export type TestOut = {
    /**
     * Id
     */
    id: string;
    title: LocalizedText | null;
    kind: TestKind;
    /**
     * Kind Label
     */
    kind_label: string;
    country: LearnRefOut | null;
    author: AuthorOut;
    /**
     * Sample Size
     */
    sample_size: number | null;
    /**
     * Questions Count
     */
    questions_count: number;
    /**
     * Questions
     */
    questions: Array<QuestionOut>;
    /**
     * Without Correct
     */
    without_correct: number;
    /**
     * Topics
     *
     * Уже введённые темы (подсказки поля «Тема»)
     */
    topics: Array<string>;
    /**
     * Media
     */
    media: Array<LearnMediaOut>;
    usage: TestUsageOut;
    /**
     * Moderation Note
     */
    moderation_note: string | null;
    status: ContentStatus;
    /**
     * Status Label
     */
    status_label: string;
    display_status: ContentStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    published_revision: LearnRevisionOut | null;
    pending_revision: LearnRevisionOut | null;
    /**
     * Has Unsubmitted Changes
     */
    has_unsubmitted_changes: boolean;
    /**
     * Edits Hint
     */
    edits_hint: string | null;
    /**
     * First Published At
     */
    first_published_at: string | null;
    archive_reason: LearnArchiveReason | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Usages
     */
    usages: Array<LearnUsageItemOut>;
    can: TestCan;
    /**
     * Message
     */
    message?: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * TestPassOut
 */
export type TestPassOut = {
    /**
     * Passed
     */
    passed: number;
    /**
     * Total
     */
    total: number;
    /**
     * Label
     */
    label: string;
};

/**
 * TestPatchIn
 */
export type TestPatchIn = {
    title?: LocalizedText | null;
    /**
     * Country Id
     */
    country_id?: string | null;
};

/**
 * TestRowOut
 */
export type TestRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Author Label
     */
    author_label: string;
    /**
     * Region Label
     *
     * «Страна · {страна}» для квиза, иначе «Без региона»
     */
    region_label: string;
    /**
     * Country Id
     */
    country_id: string | null;
    /**
     * Questions Count
     */
    questions_count: number;
    /**
     * Links Label
     */
    links_label: string;
    kind: TestKind;
    /**
     * Kind Label
     */
    kind_label: string;
    status: ContentStatus;
    display_status: ContentStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * TestUsageOut
 */
export type TestUsageOut = {
    /**
     * Lessons
     */
    lessons: Array<UsageLessonOut>;
    /**
     * Courses
     */
    courses: Array<UsageCourseOut>;
    /**
     * Countries
     */
    countries: Array<UsageCountryOut>;
    /**
     * Level Check
     *
     * «Проверка уровня в «Пути»» для проверки уровня
     */
    level_check: string | null;
};

/**
 * TicketArchiveIn
 */
export type TicketArchiveIn = {
    /**
     * Reason
     */
    reason: string;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * TicketAttachmentOut
 */
export type TicketAttachmentOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Url
     *
     * Подписанная ссылка на 15 минут (персональный файл)
     */
    url: string | null;
    /**
     * Content Type
     */
    content_type: string | null;
};

/**
 * TicketCanOut
 */
export type TicketCanOut = {
    /**
     * Add Message
     *
     * «Дополнить заявку» (статус не «Решено»)
     */
    add_message: boolean;
    /**
     * Reopen
     *
     * «Вопрос не решён»
     */
    reopen: boolean;
    /**
     * New Ticket
     *
     * «Новая заявка» в решённой заявке
     */
    new_ticket: boolean;
};

/**
 * TicketCountsOut
 */
export type TicketCountsOut = {
    /**
     * New
     */
    new: number;
    /**
     * Work
     */
    work: number;
    /**
     * Done
     */
    done: number;
};

/**
 * TicketListOut
 */
export type TicketListOut = {
    /**
     * Items
     */
    items: Array<TicketShortOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    counts: TicketCountsOut;
    /**
     * Total
     */
    total: number;
    /**
     * Total Label
     *
     * «3 заявки»
     */
    total_label: string;
    /**
     * Open Count
     *
     * Открытые: «Новое» и «Решается»
     */
    open_count: number;
    /**
     * Summary Label
     *
     * Подпись строки «Поддержка» в настройках
     */
    summary_label: string;
};

/**
 * TicketMessageIn
 */
export type TicketMessageIn = {
    /**
     * Text
     */
    text: string;
};

/**
 * TicketMessageOut
 */
export type TicketMessageOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Kind
     */
    kind: 'initial' | 'addition';
    /**
     * Author
     */
    author: 'user';
    /**
     * Text
     */
    text: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Attachments
     */
    attachments: Array<TicketAttachmentOut>;
};

/**
 * TicketOut
 *
 * `Ticket` (пример 4.8.2.3): `status` — пользовательский (`new` | `work` | `done`).
 */
export type TicketOut = {
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     */
    number_label: string;
    topic: TicketTopicOut;
    /**
     * Subject
     */
    subject: string;
    /**
     * Status
     */
    status: 'new' | 'work' | 'done';
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Status Changed At
     */
    status_changed_at: string;
    /**
     * Reply Email
     */
    reply_email: string;
    /**
     * Ref Text
     */
    ref_text: string | null;
    /**
     * Timeline
     */
    timeline: Array<TicketTimelineStepOut>;
    /**
     * Messages
     */
    messages: Array<TicketMessageOut>;
    /**
     * Replies
     */
    replies: Array<TicketReplyOut>;
    can: TicketCanOut;
};

/**
 * TicketPatchIn
 *
 * `{topic_code?, priority_code?, related?}`; `related: null` — убрать связи.
 */
export type TicketPatchIn = {
    topic_code?: SupportTopicCode | null;
    priority_code?: SupportPriorityCode | null;
    /**
     * Related
     */
    related?: Array<RelatedItem> | null;
};

/**
 * TicketReplyIn
 */
export type TicketReplyIn = {
    /**
     * Text
     */
    text: string;
    /**
     * Next Status
     */
    next_status: 'in_progress' | 'waiting_user' | 'resolved';
};

/**
 * TicketReplyOut
 */
export type TicketReplyOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Text
     */
    text: string;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Author Name
     *
     * «Поддержка GWine · Ирина»
     */
    author_name: string;
};

/**
 * TicketShortOut
 */
export type TicketShortOut = {
    /**
     * Number
     */
    number: number;
    /**
     * Number Label
     *
     * «№ 1107»
     */
    number_label: string;
    topic: TicketTopicOut;
    /**
     * Subject
     */
    subject: string;
    /**
     * Preview
     *
     * «Ответ: {текст}» (есть ответ и статус не «Новое») или начало «Подробностей»
     */
    preview: string;
    /**
     * Status
     */
    status: 'new' | 'work' | 'done';
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Status Changed At
     */
    status_changed_at: string;
    /**
     * Created At
     */
    created_at: string;
};

/**
 * TicketStatus
 */
export const TicketStatus = {
    New: 'new',
    InProgress: 'in_progress',
    WaitingUser: 'waiting_user',
    Overdue: 'overdue',
    Resolved: 'resolved',
    Closed: 'closed'
} as const;

/**
 * TicketStatus
 */
export type TicketStatus = typeof TicketStatus[keyof typeof TicketStatus];

/**
 * TicketTimelineStepOut
 */
export type TicketTimelineStepOut = {
    /**
     * Status
     */
    status: 'new' | 'work' | 'done';
    /**
     * At
     */
    at: string;
    /**
     * Label
     */
    label: string;
    /**
     * Caption
     */
    caption: string;
    /**
     * Current
     */
    current: boolean;
};

/**
 * TicketTopicOut
 */
export type TicketTopicOut = {
    code: SupportTopicCode;
    /**
     * Name
     */
    name: string;
};

/**
 * TicketTransitionIn
 */
export type TicketTransitionIn = {
    /**
     * Action
     */
    action: 'resolve' | 'wait_user' | 'reopen' | 'close';
};

/**
 * TicketUserOut
 */
export type TicketUserOut = {
    /**
     * Kind
     */
    kind: 'user' | 'guest' | 'contact';
    /**
     * Id
     *
     * Пользователь — ссылка на карточку
     */
    id: string | null;
    /**
     * Full Name
     */
    full_name: string | null;
    /**
     * Email
     */
    email: string | null;
    /**
     * Label
     *
     * ФИО пользователя / почта гостя / имя контакта
     */
    label: string;
};

/**
 * TierCreateIn
 */
export type TierCreateIn = {
    /**
     * «VIII Мастер»: римская цифра и роль
     */
    title: LocalizedText;
    /**
     * Image File Id
     */
    image_file_id?: string | null;
    /**
     * Xp Min
     */
    xp_min: number;
    /**
     * Accuracy Min
     */
    accuracy_min: number | string;
    /**
     * До 300
     */
    unlocks?: LocalizedText | null;
};

/**
 * TierGateKey
 */
export const TierGateKey = {
    Calibration: 'calibration',
    BlindOnline: 'blind_online',
    BlindOffline: 'blind_offline',
    Specializations: 'specializations',
    DropEarlyAccess: 'drop_early_access',
    ExpertApplication: 'expert_application'
} as const;

/**
 * TierGateKey
 */
export type TierGateKey = typeof TierGateKey[keyof typeof TierGateKey];

/**
 * TierGateOut
 */
export type TierGateOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Description
     */
    description: string;
    /**
     * Owner Module
     */
    owner_module: string;
    min_tier: GamGateTierOut | null;
    /**
     * Users Allowed
     */
    users_allowed: number;
    /**
     * Users Allowed Label
     */
    users_allowed_label: string;
    /**
     * Updated At
     */
    updated_at: string | null;
    updated_by: UpdatedByOut | null;
    /**
     * Configured
     */
    configured: boolean;
    /**
     * Toast
     */
    toast?: string | null;
};

/**
 * TierGatePatchIn
 */
export type TierGatePatchIn = {
    /**
     * Min Tier Id
     */
    min_tier_id: string;
};

/**
 * TierGatesOut
 */
export type TierGatesOut = {
    /**
     * Items
     */
    items: Array<TierGateOut>;
    /**
     * Tiers
     */
    tiers: Array<TierOptionOut>;
};

/**
 * TierOptionOut
 */
export type TierOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Title
     */
    title: string;
    /**
     * Users At Least
     *
     * Ступень и выше — пользователей (модалка)
     */
    users_at_least: number;
    /**
     * Users Label
     */
    users_label: string;
};

/**
 * TierOut
 */
export type TierOut = {
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    /**
     * Name
     */
    name: string;
    /**
     * Label
     *
     * «Ступень паспорта IV «Ценитель» и выше»
     */
    label: string;
};

/**
 * TierPatchIn
 */
export type TierPatchIn = {
    title?: LocalizedText | null;
    /**
     * Image File Id
     */
    image_file_id?: string | null;
    /**
     * Xp Min
     */
    xp_min?: number | null;
    /**
     * Accuracy Min
     */
    accuracy_min?: number | string | null;
    unlocks?: LocalizedText | null;
};

/**
 * TierRowOut
 */
export type TierRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Rank
     */
    rank: number;
    /**
     * Roman
     */
    roman: string;
    name: LocalizedText;
    /**
     * «III Первооткрыватель» на языках рынка
     */
    title: LocalizedText;
    /**
     * Title Label
     */
    title_label: string;
    /**
     * Image File Id
     */
    image_file_id: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Xp Min
     */
    xp_min: number;
    /**
     * Accuracy Min
     */
    accuracy_min: number;
    unlocks: LocalizedText | null;
    /**
     * Unlocks Label
     */
    unlocks_label: string | null;
    /**
     * Gates
     *
     * Функции по ступени (гейты с этим порогом)
     */
    gates: Array<GateTitleOut>;
    /**
     * Functions Label
     */
    functions_label: string;
    /**
     * Users Count
     */
    users_count: number;
    /**
     * Is First
     */
    is_first: boolean;
    can: GamRowCan;
};

/**
 * TiersPageOut
 */
export type TiersPageOut = {
    /**
     * Items
     */
    items: Array<TierRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
};

/**
 * TileOut
 */
export type TileOut = {
    /**
     * Done
     */
    done: number;
    /**
     * All
     */
    all: number;
    /**
     * Label
     */
    label: string;
};

/**
 * TileSource
 *
 * Источник тайлов фолбэка карты (А.4 `maps.fallback_tiles`, BR-GEO-64).
 */
export type TileSource = {
    /**
     * Url Template
     */
    url_template: string;
    /**
     * Max Zoom
     */
    max_zoom: number;
    /**
     * Attribution
     */
    attribution: string;
};

/**
 * TilesOut
 */
export type TilesOut = {
    lessons: TileOut;
    wines: TileOut;
    visits: TileOut;
    extra: TileOut;
};

/**
 * TilesVisibilityOut
 */
export type TilesVisibilityOut = {
    /**
     * Lessons
     *
     * Плитка «уроки» (gam.learning_discount_channels не пуст)
     */
    lessons: boolean;
    /**
     * Wine
     *
     * Плитка «вино» (gam.wine_discount_applies)
     */
    wine: boolean;
};

/**
 * TimezoneOut
 */
export type TimezoneOut = {
    /**
     * Tz
     */
    tz: string;
    /**
     * Label
     */
    label: string;
};

/**
 * TokenIn
 *
 * Токен из ссылки «Отписаться от рассылок» (страница отписки, BR-SRV-57).
 */
export type TokenIn = {
    /**
     * Token
     */
    token: string;
};

/**
 * TokensOut
 */
export type TokensOut = {
    /**
     * Access Token
     */
    access_token: string;
    /**
     * Access Expires In
     *
     * Срок жизни access-токена, секунды
     */
    access_expires_in: number;
    /**
     * Refresh Token
     */
    refresh_token: string;
    /**
     * Refresh Expires In
     *
     * Срок жизни refresh-токена, секунды
     */
    refresh_expires_in: number;
};

/**
 * TopicOut
 */
export type TopicOut = {
    code: SupportTopicCode;
    admin_name: LocalizedText;
    app_name: LocalizedText | null;
    /**
     * Label
     */
    label: string;
    /**
     * Needs Ref
     */
    needs_ref: boolean;
    /**
     * Position
     */
    position: number;
};

/**
 * TranslationBatchCan
 */
export type TranslationBatchCan = {
    /**
     * Cancel
     */
    cancel?: boolean;
};

/**
 * TranslationBatchIn
 */
export type TranslationBatchIn = {
    /**
     * Entity Types
     *
     * «Что переводить» — типы сущностей
     */
    entity_types: Array<string>;
    /**
     * Only Missing
     *
     * «Только непереведённое»: поля `pending` (в т. ч. с ошибкой перевода); выключено — также `auto`; `manual` пакет не перезаписывает никогда (BR-INT-SVC-74)
     */
    only_missing?: boolean;
};

/**
 * TranslationBatchOut
 */
export type TranslationBatchOut = {
    /**
     * Id
     */
    id: string;
    scope: TranslationBatchScopeOut;
    /**
     * Scope Label
     *
     * «Что переводить» — подписи типов через запятую
     */
    scope_label: string;
    status: TranslationBatchStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Total Jobs
     */
    total_jobs: number;
    /**
     * Done Jobs
     */
    done_jobs: number;
    /**
     * Failed Jobs
     */
    failed_jobs: number;
    /**
     * Total Chars
     */
    total_chars: number;
    /**
     * Progress Label
     */
    progress_label: string;
    /**
     * Started By
     */
    started_by: string;
    /**
     * Started By Name
     */
    started_by_name: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Finished At
     */
    finished_at: string | null;
    can: TranslationBatchCan;
};

/**
 * TranslationBatchScopeOut
 */
export type TranslationBatchScopeOut = {
    /**
     * Entity Types
     */
    entity_types: Array<string>;
    /**
     * Only Missing
     */
    only_missing: boolean;
    /**
     * Statuses
     */
    statuses: Array<string>;
};

/**
 * TranslationBatchStatus
 */
export const TranslationBatchStatus = {
    Queued: 'queued',
    Running: 'running',
    Completed: 'completed',
    CompletedWithErrors: 'completed_with_errors',
    Cancelled: 'cancelled'
} as const;

/**
 * TranslationBatchStatus
 */
export type TranslationBatchStatus = typeof TranslationBatchStatus[keyof typeof TranslationBatchStatus];

/**
 * TranslationBatchesPage
 */
export type TranslationBatchesPage = {
    /**
     * Items
     */
    items: Array<TranslationBatchOut>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
    /**
     * Entity Types
     *
     * Варианты поля «Что переводить» (по умолчанию — все)
     */
    entity_types: Array<TranslationEntityTypeOut>;
};

/**
 * TranslationEntityTypeOut
 */
export type TranslationEntityTypeOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
};

/**
 * TranslationEstimateOut
 */
export type TranslationEstimateOut = {
    /**
     * Fields
     */
    fields: number;
    /**
     * Chars
     */
    chars: number;
    /**
     * Label
     */
    label: string;
};

/**
 * TranslationJobCan
 */
export type TranslationJobCan = {
    /**
     * Retry
     */
    retry?: boolean;
};

/**
 * TranslationJobOut
 */
export type TranslationJobOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Type Label
     */
    entity_type_label: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Entity Title
     *
     * Название объекта (колонка «Объект»)
     */
    entity_title: string | null;
    /**
     * Field Path
     */
    field_path: string;
    source_lang: ContentLanguage;
    target_lang: ContentLanguage;
    /**
     * Direction Label
     */
    direction_label: string;
    mode: TranslationMode;
    status: TranslationJobStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Attempts
     */
    attempts: number;
    /**
     * Chars
     */
    chars: number;
    /**
     * Batch Id
     */
    batch_id: string | null;
    /**
     * Error Code
     */
    error_code: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Finished At
     */
    finished_at: string | null;
    can: TranslationJobCan;
};

/**
 * TranslationJobStatus
 */
export const TranslationJobStatus = {
    Queued: 'queued',
    InProgress: 'in_progress',
    Done: 'done',
    Failed: 'failed',
    Cancelled: 'cancelled'
} as const;

/**
 * TranslationJobStatus
 */
export type TranslationJobStatus = typeof TranslationJobStatus[keyof typeof TranslationJobStatus];

/**
 * TranslationJobsPage
 */
export type TranslationJobsPage = {
    /**
     * Items
     */
    items: Array<TranslationJobOut>;
    /**
     * Total
     */
    total: number;
    can?: ListCan;
};

/**
 * TranslationMode
 */
export const TranslationMode = { Translate: 'translate', Copy: 'copy' } as const;

/**
 * TranslationMode
 */
export type TranslationMode = typeof TranslationMode[keyof typeof TranslationMode];

/**
 * TranslationStatus
 */
export const TranslationStatus = {
    Auto: 'auto',
    Manual: 'manual',
    Pending: 'pending'
} as const;

/**
 * TranslationStatus
 */
export type TranslationStatus = typeof TranslationStatus[keyof typeof TranslationStatus];

/**
 * TranslationTilesOut
 *
 * Плитки карточки переводчика (17.12.6).
 */
export type TranslationTilesOut = {
    /**
     * Chars 30D
     *
     * Символов переведено за 30 дней
     */
    chars_30d: number;
    /**
     * Queued
     *
     * Задач в очереди и в работе
     */
    queued: number;
    /**
     * Failed
     *
     * Ошибок перевода (задачи в статусе «Ошибка»)
     */
    failed: number;
    /**
     * Untranslated Fields
     *
     * Полей без перевода (`pending`) в контенте; null — автоперевод контента выключен (RU, D14)
     */
    untranslated_fields: number | null;
};

/**
 * TripAdmin
 */
export type TripAdmin = {
    /**
     * Id
     */
    id: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Subtitle
     */
    subtitle: string;
    title: LocalizedText;
    region: RefOut | null;
    /**
     * Duration Days
     */
    duration_days: number;
    /**
     * Duration Label
     */
    duration_label: string;
    /**
     * Group Size
     */
    group_size: number;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    cancel_policy: TripCancelPolicy;
    /**
     * Cancel Policy Label
     */
    cancel_policy_label: string;
    host: HostOut | null;
    /**
     * Wineries
     */
    wineries: Array<RefOut>;
    about: LocalizedText | null;
    hotel: LocalizedText;
    /**
     * Stay
     */
    stay: Array<PairOut>;
    /**
     * Rooms
     */
    rooms: Array<TripRoomOut>;
    /**
     * Kids Pct Under 6
     */
    kids_pct_under_6: number;
    /**
     * Kids Pct 7 12
     */
    kids_pct_7_12: number;
    /**
     * Kids Pct 13 17
     */
    kids_pct_13_17: number;
    /**
     * Kids Label
     */
    kids_label: string;
    /**
     * Departures
     */
    departures: Array<DepartureOut>;
    /**
     * When Label
     */
    when_label: string;
    /**
     * Program
     */
    program: Array<StepOut>;
    /**
     * Included
     */
    included: Array<PairOut>;
    /**
     * How To Book
     */
    how_to_book: Array<StepOut>;
    /**
     * Media
     */
    media: Array<EventMediaOut>;
    lifecycle: EventLifecycle;
    /**
     * Lifecycle Label
     */
    lifecycle_label: string;
    /**
     * Pending Requests
     */
    pending_requests: number;
    /**
     * Summary
     */
    summary: Array<ExpTileOut>;
    /**
     * Cancelled At
     */
    cancelled_at: string | null;
    /**
     * Cancel Reason
     */
    cancel_reason: string | null;
    moderation: ModerationOut;
    /**
     * Archived At
     */
    archived_at: string | null;
    archive_reason: EventArchiveReason | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
    can: CardCan;
};

/**
 * TripCalcOut
 */
export type TripCalcOut = {
    adults_sum: Money;
    /**
     * Children
     */
    children: Array<ChildCalcOut>;
    single_supplement: Money;
    total: Money;
};

/**
 * TripCancelPolicy
 */
export const TripCancelPolicy = {
    Free14dThen50: 'free_14d_then_50',
    Free30dThen50: 'free_30d_then_50',
    Free7dThenNone: 'free_7d_then_none',
    NoneAfterApproval: 'none_after_approval'
} as const;

/**
 * TripCancelPolicy
 */
export type TripCancelPolicy = typeof TripCancelPolicy[keyof typeof TripCancelPolicy];

/**
 * TripCard
 */
export type TripCard = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Meta
     */
    meta: string;
    region: RefOut | null;
    /**
     * Duration Days
     */
    duration_days: number;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Group Size
     */
    group_size: number;
    lifecycle: EventLifecycle;
    nearest_departure: DepartureApp | null;
    /**
     * Attended
     */
    attended?: boolean;
    /**
     * Status Chip
     */
    status_chip?: string | null;
};

/**
 * TripContentIn
 */
export type TripContentIn = {
    about?: LocalizedText | null;
    /**
     * Program
     */
    program?: Array<StepIn> | null;
    /**
     * Included
     */
    included?: Array<PairIn> | null;
    /**
     * How To Book
     */
    how_to_book?: Array<StepIn> | null;
    /**
     * Media
     */
    media?: Array<EventMediaIn> | null;
};

/**
 * TripCreateIn
 */
export type TripCreateIn = {
    title: LocalizedText;
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Duration Days
     */
    duration_days: number;
    /**
     * Group Size
     */
    group_size: number;
    price: Money;
    cancel_policy?: TripCancelPolicy;
    /**
     * Host Id
     */
    host_id?: string | null;
    /**
     * Winery Ids
     */
    winery_ids?: Array<string>;
    about?: LocalizedText | null;
    hotel: LocalizedText;
    /**
     * Rooms
     *
     * Пусто — все типы, 3/2/2/1
     */
    rooms?: Array<TripRoomIn> | null;
    /**
     * Kids Pct Under 6
     */
    kids_pct_under_6?: number;
    /**
     * Kids Pct 7 12
     */
    kids_pct_7_12?: number;
    /**
     * Kids Pct 13 17
     */
    kids_pct_13_17?: number;
    /**
     * Departures
     */
    departures?: Array<DepartureIn>;
    /**
     * Stay
     */
    stay?: Array<PairIn> | null;
    /**
     * Program
     */
    program?: Array<StepIn> | null;
    /**
     * Included
     */
    included?: Array<PairIn> | null;
    /**
     * How To Book
     */
    how_to_book?: Array<StepIn> | null;
    /**
     * Media
     */
    media?: Array<EventMediaIn>;
};

/**
 * TripDetail
 */
export type TripDetail = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Overline
     */
    overline: string;
    /**
     * Meta
     */
    meta: string;
    region: RefOut | null;
    /**
     * Duration Days
     */
    duration_days: number;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Group Size
     */
    group_size: number;
    lifecycle: EventLifecycle;
    nearest_departure: DepartureApp | null;
    /**
     * Attended
     */
    attended?: boolean;
    /**
     * Status Chip
     */
    status_chip?: string | null;
    /**
     * Published
     */
    published: boolean;
    /**
     * About
     */
    about: string | null;
    host: HostOut | null;
    /**
     * Wineries
     */
    wineries: Array<RefOut>;
    /**
     * Hotel
     */
    hotel: string;
    /**
     * Stay
     */
    stay: Array<AppPair>;
    /**
     * Rooms
     */
    rooms: Array<RoomApp>;
    /**
     * Kids Label
     */
    kids_label: string;
    /**
     * Program
     */
    program: Array<AppStep>;
    /**
     * Included
     */
    included: Array<AppPair>;
    /**
     * How To Book
     */
    how_to_book: Array<AppStep>;
    /**
     * Departures
     */
    departures: Array<DepartureApp>;
    /**
     * Media
     */
    media: Array<AppMedia>;
    /**
     * Cancel Policy Text
     */
    cancel_policy_text: string;
    /**
     * Seats Label
     */
    seats_label: string;
    me: MeOut | null;
};

/**
 * TripPatchIn
 */
export type TripPatchIn = {
    title?: LocalizedText | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Duration Days
     */
    duration_days?: number | null;
    /**
     * Group Size
     */
    group_size?: number | null;
    price?: Money | null;
    cancel_policy?: TripCancelPolicy | null;
    /**
     * Host Id
     */
    host_id?: string | null;
    /**
     * Winery Ids
     */
    winery_ids?: Array<string> | null;
    about?: LocalizedText | null;
};

/**
 * TripRequestIn
 */
export type TripRequestIn = {
    /**
     * Departure Id
     */
    departure_id?: string | null;
    /**
     * Adults
     */
    adults?: number;
    /**
     * Children
     */
    children?: Array<ChildAgeGroup>;
    room_type?: RoomKind;
    /**
     * Note
     */
    note?: string | null;
};

/**
 * TripRequestsOut
 *
 * Заявки на поездку для «Моих заказов» (ORD 7.6.6, «Используемые API»).
 */
export type TripRequestsOut = {
    /**
     * Items
     */
    items: Array<BookingOut>;
};

/**
 * TripRoomIn
 */
export type TripRoomIn = {
    type: RoomKind;
    /**
     * Enabled
     */
    enabled?: boolean;
    /**
     * Count Per Departure
     */
    count_per_departure?: number | null;
    /**
     * Single Supplement Pct
     */
    single_supplement_pct?: number | null;
};

/**
 * TripRoomOut
 */
export type TripRoomOut = {
    type: RoomKind;
    /**
     * Type Label
     */
    type_label: string;
    /**
     * Description
     */
    description: string;
    /**
     * Capacity
     */
    capacity: number;
    /**
     * Enabled
     */
    enabled: boolean;
    /**
     * Count Per Departure
     */
    count_per_departure: number | null;
    /**
     * Single Supplement Pct
     */
    single_supplement_pct: number | null;
    /**
     * Label
     */
    label: string;
};

/**
 * TripRow
 */
export type TripRow = {
    /**
     * Id
     */
    id: string;
    title: LocalizedText;
    /**
     * Title Text
     */
    title_text: string;
    region: RefOut | null;
    /**
     * Duration Days
     */
    duration_days: number;
    /**
     * Duration Label
     */
    duration_label: string;
    /**
     * When Label
     */
    when_label: string;
    /**
     * Wineries Label
     */
    wineries_label: string;
    host: RefOut | null;
    /**
     * Group Label
     */
    group_label: string;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    lifecycle: EventLifecycle;
    moderation_status: ModerationStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Is Live
     */
    is_live: boolean;
    /**
     * Archived
     */
    archived: boolean;
    can: RowCan;
};

/**
 * TripSeason
 */
export const TripSeason = {
    AllYear: 'all_year',
    AprOct: 'apr_oct',
    MaySep: 'may_sep',
    SepNov: 'sep_nov'
} as const;

/**
 * TripSeason
 */
export type TripSeason = typeof TripSeason[keyof typeof TripSeason];

/**
 * TrySetOut
 */
export type TrySetOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Over Label
     */
    over_label: string;
    /**
     * Author Label
     */
    author_label: string;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
};

/**
 * TryWineryOut
 */
export type TryWineryOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * «от {минимальная цена визита}» (EXP)
     */
    visit_from: Money | null;
};

/**
 * TstAccuracyLogRowOut
 */
export type TstAccuracyLogRowOut = {
    /**
     * Id
     */
    id: string;
    user: TstNamedRef;
    /**
     * Points
     */
    points: number;
    /**
     * Points Label
     *
     * «+0,85», «+2», «+0»
     */
    points_label: string;
    kind: AccuracyKind;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * For Label
     *
     * «С экспертом · <вино>» / «Вслепую · <событие> · вино N»
     */
    for_label: string;
    link: TstLinkOut | null;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Accuracy Label
     */
    accuracy_label: string;
    /**
     * Successful
     */
    successful: boolean;
    /**
     * Completed At
     */
    completed_at: string;
};

/**
 * TstAccuracySummaryOut
 */
export type TstAccuracySummaryOut = {
    /**
     * Points Total
     */
    points_total: number;
    /**
     * Points Label
     */
    points_label: string;
    /**
     * Progress Label
     *
     * «<баллы> / <порог следующего>»
     */
    progress_label: string;
    /**
     * Successful Blind
     */
    successful_blind: number;
    level: TstLevelOut | null;
    next_level: TstNextLevelOut | null;
    /**
     * Max Level Text
     */
    max_level_text: string | null;
    /**
     * Current Accuracy
     */
    current_accuracy: number | null;
    /**
     * Current Label
     */
    current_label: string | null;
    /**
     * Delta
     */
    delta: number | null;
    /**
     * Delta Label
     */
    delta_label: string | null;
    /**
     * Recent
     *
     * Последние 12 — слева старые, справа новые
     */
    recent: Array<TstRecentOut>;
    /**
     * Legend
     */
    legend: Array<TstLegendOut>;
    /**
     * Skills
     */
    skills: Array<TstSkillOut>;
    /**
     * Specializations Top
     */
    specializations_top: Array<TstSpecTopOut>;
    /**
     * Levels
     */
    levels: Array<TstLevelOut>;
    /**
     * Levels Title
     */
    levels_title: string;
};

/**
 * TstAppliedRowOut
 */
export type TstAppliedRowOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * TstAromaOut
 */
export type TstAromaOut = {
    /**
     * Up
     */
    up: number;
    /**
     * Down
     */
    down: number;
    /**
     * Total
     */
    total: number;
    /**
     * Caption
     *
     * «нравится в N из M» / «нет оценок» …
     */
    caption: string;
    /**
     * Up Pct
     */
    up_pct: number;
    /**
     * Down Pct
     */
    down_pct: number;
    key: AromaGroup;
    /**
     * Label
     */
    label: string;
};

/**
 * TstBadgeOut
 */
export type TstBadgeOut = {
    /**
     * Label
     */
    label: string;
    /**
     * Tone
     */
    tone: 'positive' | 'neutral' | 'gold' | 'negative';
};

/**
 * TstBlindAnswerIn
 */
export type TstBlindAnswerIn = {
    /**
     * Sensory
     *
     * Ответы 1–5 по применимым параметрам
     */
    sensory: {
        [key in SensoryParam]?: number;
    };
    identification: TstBlindIdentificationIn;
};

/**
 * TstBlindCurrentOut
 */
export type TstBlindCurrentOut = {
    /**
     * Position
     */
    position: number;
    /**
     * Cover Number
     */
    cover_number: number;
    /**
     * Title
     *
     * «Вино N» или «Вино k из M»
     */
    title: string;
    /**
     * Params
     */
    params: Array<TstParamOut>;
    /**
     * Identification
     */
    identification: Array<TstBlindFieldOut>;
};

/**
 * TstBlindEventOut
 */
export type TstBlindEventOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Format
     */
    format: 'online' | 'offline';
};

/**
 * TstBlindFieldOut
 */
export type TstBlindFieldOut = {
    key: IdentificationField;
    /**
     * Label
     */
    label: string;
    /**
     * Options
     */
    options: Array<TstOptionOut>;
};

/**
 * TstBlindFormOut
 */
export type TstBlindFormOut = {
    /**
     * State
     */
    state: 'need_booking' | 'waiting' | 'open' | 'in_progress' | 'completed' | 'closed';
    event: TstBlindEventOut;
    /**
     * Opens At
     */
    opens_at: string | null;
    /**
     * Closes At
     */
    closes_at: string | null;
    /**
     * Wines Total
     */
    wines_total: number;
    /**
     * Wines Done
     */
    wines_done: number;
    current: TstBlindCurrentOut | null;
};

/**
 * TstBlindIdentificationIn
 */
export type TstBlindIdentificationIn = {
    /**
     * Grape
     *
     * id сорта справочника или название
     */
    grape: string;
    /**
     * Country Id
     */
    country_id: string;
    /**
     * Region Id
     */
    region_id?: string | null;
    climate?: BlindClimate | null;
};

/**
 * TstBlindResultOut
 */
export type TstBlindResultOut = {
    event: TstBlindEventOut;
    /**
     * Wines
     */
    wines: Array<TstBlindWineResultOut>;
    /**
     * Accuracy
     */
    accuracy: number | null;
    /**
     * Points Total
     */
    points_total: number;
    /**
     * Points Label
     */
    points_label: string;
    /**
     * Wines Done
     */
    wines_done: number;
    /**
     * Wines Total
     */
    wines_total: number;
    /**
     * Completed
     */
    completed: boolean;
    /**
     * Rated Label
     *
     * «Оценено k из M · средняя точность N%»
     */
    rated_label: string;
    /**
     * Sum Caption
     */
    sum_caption: string;
};

/**
 * TstBlindRevealOut
 */
export type TstBlindRevealOut = {
    /**
     * Position
     */
    position: number;
    /**
     * Cover Number
     */
    cover_number: number;
    /**
     * Title
     */
    title: string;
    wine: TstWineOut | null;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Accuracy Label
     */
    accuracy_label: string;
    /**
     * Sensory
     */
    sensory: number;
    /**
     * Identification
     */
    identification: number;
    /**
     * Stats Label
     *
     * «сенсорика N% · определение N%»
     */
    stats_label: string;
    /**
     * Points
     */
    points: number;
    /**
     * Points Label
     */
    points_label: string;
    /**
     * Why Points
     */
    why_points: string;
    /**
     * Param Accuracy
     */
    param_accuracy: {
        [key: string]: number;
    };
    /**
     * Identification Match
     */
    identification_match: {
        [key: string]: boolean;
    };
    /**
     * Identification Rows
     */
    identification_rows: Array<TstIdentificationRowOut>;
    /**
     * Params
     */
    params: Array<TstCompareOut>;
    /**
     * Reference
     */
    reference: {
        [key: string]: number | null;
    };
    /**
     * Expert Word
     */
    expert_word: string | null;
    expert: TstExpertOut | null;
    /**
     * Submitted At
     */
    submitted_at: string;
    /**
     * Is Last
     */
    is_last: boolean;
    /**
     * Next Position
     */
    next_position: number | null;
};

/**
 * TstBlindWineAdminOut
 */
export type TstBlindWineAdminOut = {
    /**
     * Position
     */
    position: number;
    /**
     * Cover Number
     */
    cover_number: number;
    wine: TstWineOut | null;
    /**
     * Reference
     */
    reference: {
        [key: string]: number | null;
    };
    /**
     * Params
     */
    params: Array<TstParamValueOut>;
    /**
     * Ref Grape
     */
    ref_grape: string | null;
    ref_country: TstNamedRef | null;
    ref_region: TstNamedRef | null;
    ref_climate: BlindClimate | null;
    /**
     * Ref Climate Label
     */
    ref_climate_label: string | null;
    /**
     * Identification Label
     *
     * «<сорт> · <страна> · <регион> · <климат>»
     */
    identification_label: string;
    expert_word: LocalizedText | null;
    /**
     * Reference Filled
     */
    reference_filled: boolean;
    /**
     * Reference Label
     */
    reference_label: string;
    /**
     * Answers
     *
     * Ответов участников по этому вину
     */
    answers: number;
    /**
     * Locked
     *
     * Эталон нельзя менять (есть ответы, BR-TST-53)
     */
    locked: boolean;
};

/**
 * TstBlindWineIn
 */
export type TstBlindWineIn = {
    /**
     * Position
     */
    position: number;
    /**
     * Cover Number
     *
     * По умолчанию — номер
     */
    cover_number?: number | null;
    /**
     * Wine Id
     */
    wine_id: string;
    reference: TstReferenceIn;
    /**
     * Ref Grape
     */
    ref_grape?: string | null;
    /**
     * Ref Country Id
     */
    ref_country_id?: string | null;
    /**
     * Ref Region Id
     */
    ref_region_id?: string | null;
    ref_climate?: BlindClimate | null;
    expert_word?: LocalizedText | null;
};

/**
 * TstBlindWineResultOut
 */
export type TstBlindWineResultOut = {
    /**
     * Position
     */
    position: number;
    /**
     * Cover Number
     */
    cover_number: number;
    /**
     * Title
     */
    title: string;
    wine: TstWineOut | null;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Accuracy Label
     */
    accuracy_label: string;
    /**
     * Sensory
     */
    sensory: number;
    /**
     * Identification
     */
    identification: number;
    /**
     * Stats Label
     *
     * «сенсорика N% · определение N%»
     */
    stats_label: string;
    /**
     * Points
     */
    points: number;
    /**
     * Points Label
     */
    points_label: string;
    /**
     * Why Points
     */
    why_points: string;
    /**
     * Param Accuracy
     */
    param_accuracy: {
        [key: string]: number;
    };
    /**
     * Identification Match
     */
    identification_match: {
        [key: string]: boolean;
    };
    /**
     * Identification Rows
     */
    identification_rows: Array<TstIdentificationRowOut>;
    /**
     * Params
     */
    params: Array<TstCompareOut>;
    /**
     * Reference
     */
    reference: {
        [key: string]: number | null;
    };
    /**
     * Expert Word
     */
    expert_word: string | null;
    expert: TstExpertOut | null;
    /**
     * Submitted At
     */
    submitted_at: string;
};

/**
 * TstBlindWinesIn
 */
export type TstBlindWinesIn = {
    /**
     * Items
     */
    items: Array<TstBlindWineIn>;
};

/**
 * TstBlindWinesOut
 */
export type TstBlindWinesOut = {
    /**
     * Tasting Id
     */
    tasting_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Format
     */
    format: 'online' | 'offline';
    /**
     * Items
     */
    items: Array<TstBlindWineAdminOut>;
    /**
     * Summary Label
     *
     * «N вин · эталон заполнен для X из Y»
     */
    summary_label: string;
    /**
     * Started
     */
    started: boolean;
    /**
     * Can Update
     */
    can_update: boolean;
};

/**
 * TstCalibrationAdminOut
 */
export type TstCalibrationAdminOut = {
    /**
     * Id
     */
    id: string;
    wine: TstWineOut | null;
    /**
     * Wine Id
     */
    wine_id: string;
    expert: TstNamedRef;
    region: TstNamedRef | null;
    /**
     * Region Manual
     */
    region_manual: boolean;
    /**
     * Reference
     */
    reference: {
        [key: string]: number | null;
    };
    /**
     * Reference Filled
     */
    reference_filled: boolean;
    /**
     * Reference Label
     *
     * «скрыто до ответа» / «эталон не заполнен»
     */
    reference_label: string;
    /**
     * Params
     */
    params: Array<TstParamValueOut>;
    expert_word: LocalizedText | null;
    status: CalibrationStatus;
    /**
     * Status Label
     */
    status_label: string;
    archive_reason: CalibrationArchiveReason | null;
    /**
     * Archive Reason Label
     */
    archive_reason_label: string | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Service Comment
     */
    service_comment: string | null;
    /**
     * Published At
     */
    published_at: string | null;
    /**
     * Passed Count
     */
    passed_count: number;
    /**
     * Avg Accuracy
     */
    avg_accuracy: number | null;
    /**
     * Avg Label
     */
    avg_label: string;
    /**
     * Param Stats
     */
    param_stats: Array<TstParamStatOut>;
    /**
     * Param Stats Empty
     */
    param_stats_empty: string | null;
    /**
     * History
     */
    history: Array<TstHistoryItemOut>;
    links: TstCalibrationLinksOut;
    can: TstCalibrationCan;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * TstCalibrationArchiveIn
 */
export type TstCalibrationArchiveIn = {
    reason: CalibrationArchiveReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * TstCalibrationCan
 */
export type TstCalibrationCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Publish
     */
    publish?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Change Expert
     */
    change_expert?: boolean;
};

/**
 * TstCalibrationCreateIn
 */
export type TstCalibrationCreateIn = {
    /**
     * Wine Id
     *
     * Опубликованное вино каталога
     */
    wine_id: string;
    /**
     * Expert Id
     *
     * Участник с ролью «Эксперт»; эксперт — только он сам
     */
    expert_id?: string | null;
    reference: TstReferenceIn;
    /**
     * До 300 символов
     */
    expert_word?: LocalizedText | null;
};

/**
 * TstCalibrationFormOut
 */
export type TstCalibrationFormOut = {
    /**
     * Id
     */
    id: string;
    wine: TstWineOut;
    expert: TstExpertOut;
    /**
     * Params
     */
    params: Array<TstParamOut>;
    /**
     * Params Count
     */
    params_count: number;
    /**
     * How Text
     */
    how_text: string;
    /**
     * Passed
     */
    passed: boolean;
    /**
     * Passed Label
     */
    passed_label: string | null;
    /**
     * Result Accuracy
     */
    result_accuracy: number | null;
};

/**
 * TstCalibrationItemOut
 */
export type TstCalibrationItemOut = {
    /**
     * Id
     */
    id: string;
    wine: TstWineOut;
    expert: TstExpertOut;
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Overline
     *
     * «С экспертом · <имя эксперта>»
     */
    overline: string;
    /**
     * Passed
     */
    passed: boolean;
    my_result: TstMyCalibrationOut | null;
    /**
     * Value Label
     *
     * «86%» или «до 1 балла»
     */
    value_label: string;
    /**
     * Caption
     *
     * «+0,85 балла» или «не пройдена»
     */
    caption: string;
    /**
     * Target
     *
     * `calib-res:<id>` / `calib:<id>`
     */
    target: string;
};

/**
 * TstCalibrationLinksOut
 */
export type TstCalibrationLinksOut = {
    wine: TstLinkOut;
    expert: TstLinkOut;
    region: TstLinkOut | null;
};

/**
 * TstCalibrationListOut
 */
export type TstCalibrationListOut = {
    /**
     * Items
     */
    items: Array<TstCalibrationItemOut>;
};

/**
 * TstCalibrationPatchIn
 */
export type TstCalibrationPatchIn = {
    /**
     * Wine Id
     */
    wine_id?: string | null;
    /**
     * Expert Id
     *
     * Меняет только администратор
     */
    expert_id?: string | null;
    reference?: TstReferenceIn | null;
    expert_word?: LocalizedText | null;
    /**
     * Status
     */
    status?: 'draft' | 'published' | null;
    /**
     * Service Comment
     */
    service_comment?: string | null;
};

/**
 * TstCalibrationReferenceIn
 *
 * Модалка «Эталон эксперта» (BR-TST-50).
 */
export type TstCalibrationReferenceIn = {
    /**
     * Wine Id
     */
    wine_id: string;
    reference: TstReferenceIn;
};

/**
 * TstCalibrationResultOut
 */
export type TstCalibrationResultOut = {
    /**
     * Calibration Id
     */
    calibration_id: string;
    wine: TstWineOut;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Accuracy Label
     */
    accuracy_label: string;
    /**
     * Points
     */
    points: number;
    /**
     * Points Label
     *
     * «+0,85 балла точности вкуса»
     */
    points_label: string;
    /**
     * Param Accuracy
     */
    param_accuracy: {
        [key: string]: number;
    };
    /**
     * Answers
     */
    answers: {
        [key: string]: number;
    };
    /**
     * Reference
     */
    reference: {
        [key: string]: number;
    };
    /**
     * Params
     */
    params: Array<TstCompareOut>;
    expert: TstExpertOut;
    /**
     * Expert Word
     */
    expert_word: string | null;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * Submitted At
     */
    submitted_at: string;
    summary_changed: TstSummaryChangedOut;
};

/**
 * TstCalibrationRowCan
 */
export type TstCalibrationRowCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * TstCalibrationRowOut
 */
export type TstCalibrationRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Number
     *
     * Порядковый номер строки
     */
    number: number;
    wine: TstNamedRef;
    expert: TstNamedRef;
    region: TstNamedRef | null;
    /**
     * Reference Label
     *
     * «сл · кисл · тело · тан · аром · посл», пусто — «—»
     */
    reference_label: string;
    /**
     * Passed Count
     */
    passed_count: number;
    /**
     * Avg Accuracy
     */
    avg_accuracy: number | null;
    /**
     * Avg Label
     */
    avg_label: string;
    status: CalibrationStatus;
    /**
     * Status Label
     */
    status_label: string;
    can: TstCalibrationRowCan;
};

/**
 * TstCharOut
 */
export type TstCharOut = {
    /**
     * Up
     */
    up: number;
    /**
     * Down
     */
    down: number;
    /**
     * Total
     */
    total: number;
    /**
     * Caption
     *
     * «нравится в N из M» / «нет оценок» …
     */
    caption: string;
    /**
     * Up Pct
     */
    up_pct: number;
    /**
     * Down Pct
     */
    down_pct: number;
    key: TasteChar;
    /**
     * Label
     */
    label: string;
};

/**
 * TstCompareOut
 *
 * Строка «Вы и эксперт»: ваша отметка, эталон, точность параметра и чип.
 */
export type TstCompareOut = {
    key: SensoryParam;
    /**
     * Label
     */
    label: string;
    /**
     * Low
     */
    low: string;
    /**
     * High
     */
    high: string;
    /**
     * You
     */
    you: number;
    /**
     * Expert
     */
    expert: number;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Caption
     *
     * «вы X · эксперт Y»
     */
    caption: string;
    /**
     * Chip Label
     *
     * «Сладость 100%» (первое слово названия)
     */
    chip_label: string;
    /**
     * Tone
     */
    tone: 'positive' | 'negative' | 'neutral';
};

/**
 * TstConfirmNameIn
 *
 * Подтверждение удаления вводом точного названия (как `ConfirmNameIn` CAT и GEO).
 */
export type TstConfirmNameIn = {
    /**
     * Confirm Name
     */
    confirm_name: string;
};

/**
 * TstDiaryEntryCardOut
 */
export type TstDiaryEntryCardOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Kind
     */
    kind: 'tasting' | 'unrated';
    /**
     * Title
     */
    title: string;
    badge: TstBadgeOut;
    /**
     * Subtitle
     */
    subtitle: string;
    source: DiarySource;
    /**
     * Fields
     */
    fields: Array<TstFieldRowOut>;
    recognition: TstRecognitionBlockOut | null;
    taste: TstDiaryTasteOut;
    /**
     * History
     */
    history: Array<TstDiaryHistoryOut>;
    panels: TstDiaryPanelsOut;
};

/**
 * TstDiaryHistoryOut
 */
export type TstDiaryHistoryOut = {
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Text
     */
    text: string;
};

/**
 * TstDiaryListOut
 */
export type TstDiaryListOut = {
    summary: TstDiarySummaryOut;
    /**
     * Unrated
     *
     * Вина без оценки — на первой странице
     */
    unrated: Array<TstDiaryRowOut>;
    /**
     * Items
     */
    items: Array<TstDiaryRowOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * TstDiaryLogRowOut
 */
export type TstDiaryLogRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Kind
     */
    kind: 'tasting' | 'unrated';
    user: TstNamedRef;
    /**
     * Wine Id
     */
    wine_id: string | null;
    /**
     * Diary Wine Id
     */
    diary_wine_id: string;
    /**
     * Wine Label
     *
     * Название с винтажом или «Без названия»
     */
    wine_label: string;
    /**
     * Score
     */
    score: number | null;
    /**
     * Score Label
     */
    score_label: string;
    /**
     * Taste Label
     *
     * Шесть значений `wine_profile` через « · »
     */
    taste_label: string;
    /**
     * Aromas Label
     */
    aromas_label: string;
    /**
     * Note
     */
    note: string | null;
    /**
     * Recognized
     */
    recognized: boolean;
    /**
     * Recognition Label
     */
    recognition_label: string;
    /**
     * Occurred At
     */
    occurred_at: string;
};

/**
 * TstDiaryPanelsOut
 */
export type TstDiaryPanelsOut = {
    /**
     * Entries Count
     */
    entries_count: number;
    /**
     * Entries Rated Label
     */
    entries_rated_label: string;
    entries_link: TstLinkOut;
    /**
     * Avg Score
     */
    avg_score: number | null;
    /**
     * Avg Label
     */
    avg_label: string;
    wine: TstLinkOut | null;
    /**
     * Wine Title
     */
    wine_title: string | null;
    user: TstLinkOut;
    /**
     * User Name
     */
    user_name: string;
};

/**
 * TstDiaryRowOut
 *
 * Строка дневника (10.6.2): вино без оценки или с оценкой.
 */
export type TstDiaryRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Status
     */
    status: 'rated' | 'unrated';
    /**
     * Wine Id
     */
    wine_id: string | null;
    /**
     * Title
     *
     * Название или «Без названия»
     */
    title: string;
    /**
     * Name
     */
    name: string | null;
    /**
     * Vintage
     */
    vintage: string | null;
    /**
     * Grape
     */
    grape: string | null;
    type: WineType;
    /**
     * Type Label
     */
    type_label: string;
    region: TstNamedRef | null;
    /**
     * Region Text
     */
    region_text: string | null;
    /**
     * Overline
     *
     * Без оценки: «<тип> · <регион | без региона>»
     */
    overline: string | null;
    /**
     * Meta
     *
     * «<сорт> · <винтаж> · <дата>» / «<сорт> · сохранено <дата>»
     */
    meta: string;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * Illustration
     */
    illustration: 'red' | 'white';
    /**
     * Last Score
     */
    last_score: number | null;
    /**
     * Score Label
     */
    score_label: string | null;
    /**
     * Last Tasted At
     */
    last_tasted_at: string | null;
    /**
     * Saved At
     */
    saved_at: string;
    /**
     * Tastings Count
     */
    tastings_count: number;
    /**
     * History
     */
    history: Array<TstScorePointOut>;
    /**
     * History Label
     *
     * Если дегустаций больше одной
     */
    history_label: string | null;
    /**
     * Likes
     */
    likes: string | null;
    /**
     * Dislikes
     */
    dislikes: string | null;
    /**
     * Aromas
     */
    aromas: string | null;
    /**
     * Action Label
     *
     * «Оценить» у вина без оценки
     */
    action_label: string | null;
    /**
     * Status Label
     *
     * «Не оценено»
     */
    status_label: string | null;
    /**
     * Target
     *
     * Маршрут: `diary-quick:rate.<id>` / `diary-quick:view.<id>`
     */
    target: string;
};

/**
 * TstDiarySavedOut
 */
export type TstDiarySavedOut = {
    diary_wine: TstDiaryWineBriefOut;
    tasting: TstTastingBriefOut | null;
    /**
     * Created
     *
     * false — запись добавлена к вину, уже бывшему в дневнике
     */
    created: boolean;
    /**
     * Xp Awarded
     *
     * XP за запись (GAM); null — не начислено
     */
    xp_awarded: number | null;
    /**
     * Xp Total
     */
    xp_total: number | null;
    profile_summary: TstProfileSummaryOut;
};

/**
 * TstDiarySummaryOut
 */
export type TstDiarySummaryOut = {
    /**
     * Wines
     */
    wines: number;
    /**
     * Tastings
     */
    tastings: number;
    /**
     * Regions
     */
    regions: number;
    /**
     * Types
     */
    types: Array<TstDiaryTypeOut>;
    /**
     * Label
     *
     * «N вин · N дегустаций · N регионов»
     */
    label: string;
};

/**
 * TstDiaryTasteOut
 */
export type TstDiaryTasteOut = {
    /**
     * Weight Label
     */
    weight_label: string;
    entry: TstTasteRowOut;
    current: TstTasteCurrentOut | null;
    /**
     * Footer
     */
    footer: string;
};

/**
 * TstDiaryTypeOut
 */
export type TstDiaryTypeOut = {
    value: WineType;
    /**
     * Label
     */
    label: string;
};

/**
 * TstDiaryWineBriefOut
 */
export type TstDiaryWineBriefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Status
     */
    status: 'rated' | 'unrated';
    /**
     * Name
     */
    name: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Tastings Count
     */
    tastings_count: number;
};

/**
 * TstDiaryWineCreateIn
 */
export type TstDiaryWineCreateIn = {
    /**
     * Rated
     *
     * true — «Сохранить в дневник», false — «Сохранить без оценки»
     */
    rated: boolean;
    /**
     * Wine Id
     *
     * Вино каталога (карточка вина CAT)
     */
    wine_id?: string | null;
    /**
     * Recognition Id
     *
     * Распознавание, данные которого подставлены («Оценить»)
     */
    recognition_id?: string | null;
    /**
     * Label Photo Id
     *
     * Фото этикетки: `upload_id` загрузки `tst_label_photo`
     */
    label_photo_id?: string | null;
    /**
     * Name
     */
    name?: string | null;
    /**
     * Vintage
     */
    vintage?: string | null;
    /**
     * Grape
     */
    grape?: string | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Region Text
     */
    region_text?: string | null;
    /**
     * По умолчанию — тип вина каталога или «Красное»
     */
    type?: WineType | null;
    /**
     * Обязательно при `rated`
     */
    tasting?: TstTastingIn | null;
};

/**
 * TstDiaryWineDetailOut
 *
 * Запись для режимов `rate` / `view` (10.6.3).
 */
export type TstDiaryWineDetailOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Status
     */
    status: 'rated' | 'unrated';
    /**
     * Wine Id
     */
    wine_id: string | null;
    /**
     * Recognition Id
     */
    recognition_id: string | null;
    /**
     * Name
     */
    name: string | null;
    /**
     * Title
     */
    title: string;
    /**
     * Vintage
     */
    vintage: string | null;
    /**
     * Grape
     */
    grape: string | null;
    region: TstNamedRef | null;
    /**
     * Region Text
     */
    region_text: string | null;
    /**
     * Region Hint
     */
    region_hint: string;
    type: WineType;
    /**
     * Type Label
     */
    type_label: string;
    /**
     * Tannin Applicable
     */
    tannin_applicable: boolean;
    label_photo: TstFileOut | null;
    source: DiarySource;
    /**
     * Saved At
     */
    saved_at: string;
    last_tasting: TstTastingOut | null;
    /**
     * History
     */
    history: Array<TstScorePointOut>;
    wine: TstWineOut | null;
};

/**
 * TstDiaryWinePatchIn
 *
 * «Сохранить изменения»: поля вина и последней дегустации (BR-TST-01).
 */
export type TstDiaryWinePatchIn = {
    /**
     * Name
     */
    name?: string | null;
    /**
     * Vintage
     */
    vintage?: string | null;
    /**
     * Grape
     */
    grape?: string | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Region Text
     */
    region_text?: string | null;
    type?: WineType | null;
    /**
     * Label Photo Id
     */
    label_photo_id?: string | null;
    /**
     * Score
     */
    score?: number | string | null;
    /**
     * Liked
     */
    liked?: boolean | null;
    votes?: TstVotesIn | null;
    /**
     * Aromas Liked
     */
    aromas_liked?: Array<AromaGroup> | null;
    /**
     * Aromas Disliked
     */
    aromas_disliked?: Array<AromaGroup> | null;
    /**
     * Note
     */
    note?: string | null;
};

/**
 * TstEvolutionOut
 */
export type TstEvolutionOut = {
    /**
     * Up
     */
    up: Array<string>;
    /**
     * Down
     */
    down: Array<string>;
    /**
     * Text
     *
     * «↑ кислотность · ↓ дуб» или «сдвигов пока нет»
     */
    text: string;
};

/**
 * TstExpertOptionOut
 */
export type TstExpertOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * TstExpertOut
 */
export type TstExpertOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Photo Url
     */
    photo_url: string | null;
};

/**
 * TstFieldRowOut
 */
export type TstFieldRowOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * TstFileOut
 */
export type TstFileOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Url
     */
    url: string | null;
};

/**
 * TstHistoryItemOut
 */
export type TstHistoryItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Actor Name
     */
    actor_name: string;
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Summary
     */
    summary: string;
};

/**
 * TstIdentificationRowOut
 */
export type TstIdentificationRowOut = {
    key: IdentificationField;
    /**
     * Label
     */
    label: string;
    /**
     * You
     */
    you: string;
    /**
     * Right
     */
    right: string;
    /**
     * Ok
     */
    ok: boolean;
    /**
     * Caption
     *
     * «вы: … · верно: …»
     */
    caption: string;
};

/**
 * TstImageOut
 */
export type TstImageOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Url
     */
    url: string | null;
    /**
     * Name
     */
    name: string | null;
};

/**
 * TstLegendOut
 */
export type TstLegendOut = {
    /**
     * Kind
     */
    kind: 'calibration' | 'blind';
    /**
     * Label
     */
    label: string;
};

/**
 * TstLevelIn
 */
export type TstLevelIn = {
    name: LocalizedText;
    /**
     * Image Id
     *
     * PNG или SVG до 1 МБ (`tst_badge`)
     */
    image_id?: string | null;
    /**
     * Min Points
     */
    min_points: number | string;
    /**
     * Min Successful Blind
     */
    min_successful_blind?: number | null;
    unlocks_text?: LocalizedText | null;
};

/**
 * TstLevelOut
 */
export type TstLevelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Min Points
     */
    min_points: number;
    /**
     * Min Points Label
     */
    min_points_label: string;
    /**
     * Min Successful Blind
     */
    min_successful_blind: number;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Unlocks Text
     */
    unlocks_text: string | null;
    /**
     * Reached
     */
    reached: boolean;
    /**
     * Current
     */
    current: boolean;
    /**
     * Status Label
     *
     * «уровень открыт» / «ещё N балла» / «нужны закрытые…»
     */
    status_label: string;
    /**
     * Blind Label
     */
    blind_label: string;
};

/**
 * TstLevelPatchIn
 */
export type TstLevelPatchIn = {
    name?: LocalizedText | null;
    /**
     * Image Id
     */
    image_id?: string | null;
    /**
     * Min Points
     */
    min_points?: number | string | null;
    /**
     * Min Successful Blind
     */
    min_successful_blind?: number | null;
    unlocks_text?: LocalizedText | null;
};

/**
 * TstLevelRowOut
 */
export type TstLevelRowOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText;
    /**
     * Name Label
     */
    name_label: string;
    image: TstImageOut | null;
    /**
     * Min Points
     */
    min_points: number;
    /**
     * Min Points Label
     */
    min_points_label: string;
    /**
     * Min Successful Blind
     */
    min_successful_blind: number | null;
    /**
     * Min Successful Label
     */
    min_successful_label: string;
    unlocks_text: LocalizedText | null;
    /**
     * Unlocks Label
     */
    unlocks_label: string | null;
    /**
     * Users Count
     */
    users_count: number;
    /**
     * Is Base
     */
    is_base: boolean;
    can: TstRowCan;
    /**
     * Delete Hint
     */
    delete_hint: string | null;
};

/**
 * TstLinkOut
 *
 * Переход в раздел админки или на экран приложения.
 */
export type TstLinkOut = {
    /**
     * Section
     */
    section: string;
    /**
     * Id
     */
    id?: string | null;
    /**
     * Filters
     */
    filters?: {
        [key: string]: string;
    };
};

/**
 * TstMyCalibrationOut
 */
export type TstMyCalibrationOut = {
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Points
     */
    points: number;
};

/**
 * TstMyResultOut
 */
export type TstMyResultOut = {
    /**
     * Id
     */
    id: string;
    kind: AccuracyKind;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     *
     * «<дата> · <Открытая | Закрытая · онлайн | …>»
     */
    meta: string;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Accuracy Label
     */
    accuracy_label: string;
    /**
     * Highlighted
     *
     * ≥ 80%
     */
    highlighted: boolean;
    /**
     * Points
     */
    points: number;
    /**
     * Points Label
     */
    points_label: string;
    /**
     * Completed At
     */
    completed_at: string;
    /**
     * Wines
     */
    wines: number;
    /**
     * Target
     */
    target: string;
};

/**
 * TstMyResultsOut
 */
export type TstMyResultsOut = {
    summary: TstMyResultsSummaryOut;
    /**
     * Items
     */
    items: Array<TstMyResultOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * TstMyResultsSummaryOut
 */
export type TstMyResultsSummaryOut = {
    /**
     * Count
     */
    count: number;
    /**
     * Avg Accuracy
     */
    avg_accuracy: number | null;
    /**
     * Points
     */
    points: number;
    /**
     * Count Label
     */
    count_label: string;
    /**
     * Avg Label
     */
    avg_label: string;
    /**
     * Points Label
     */
    points_label: string;
};

/**
 * TstNamedRef
 */
export type TstNamedRef = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * TstNextLevelOut
 */
export type TstNextLevelOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Min Points
     */
    min_points: number;
    /**
     * Min Successful Blind
     */
    min_successful_blind: number;
    /**
     * Need Points
     */
    need_points: number;
    /**
     * Need Successful Blind
     */
    need_successful_blind: number;
    /**
     * Progress
     */
    progress: number;
    /**
     * Until Label
     *
     * «До «…» ещё N балла»
     */
    until_label: string;
    /**
     * Need Blind Label
     *
     * «Успешные закрытые k / n»
     */
    need_blind_label: string | null;
};

/**
 * TstOptionOut
 */
export type TstOptionOut = {
    /**
     * Value
     */
    value: string;
    /**
     * Label
     */
    label: string;
};

/**
 * TstPage[TstAccuracyLogRowOut]
 */
export type TstPageTstAccuracyLogRowOut = {
    /**
     * Items
     */
    items: Array<TstAccuracyLogRowOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только ваши материалы…» для эксперта
     */
    notice?: string | null;
};

/**
 * TstPage[TstCalibrationRowOut]
 */
export type TstPageTstCalibrationRowOut = {
    /**
     * Items
     */
    items: Array<TstCalibrationRowOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только ваши материалы…» для эксперта
     */
    notice?: string | null;
};

/**
 * TstPage[TstDiaryLogRowOut]
 */
export type TstPageTstDiaryLogRowOut = {
    /**
     * Items
     */
    items: Array<TstDiaryLogRowOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только ваши материалы…» для эксперта
     */
    notice?: string | null;
};

/**
 * TstPage[TstLevelRowOut]
 */
export type TstPageTstLevelRowOut = {
    /**
     * Items
     */
    items: Array<TstLevelRowOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только ваши материалы…» для эксперта
     */
    notice?: string | null;
};

/**
 * TstPage[TstSpecLogRowOut]
 */
export type TstPageTstSpecLogRowOut = {
    /**
     * Items
     */
    items: Array<TstSpecLogRowOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только ваши материалы…» для эксперта
     */
    notice?: string | null;
};

/**
 * TstPage[TstSpecRowOut]
 */
export type TstPageTstSpecRowOut = {
    /**
     * Items
     */
    items: Array<TstSpecRowOut>;
    /**
     * Total
     *
     * Всего записей по фильтрам
     */
    total: number;
    can?: ListCan;
    /**
     * Notice
     *
     * Баннер «Показаны только ваши материалы…» для эксперта
     */
    notice?: string | null;
};

/**
 * TstParamOut
 *
 * Параметр сенсорики и подписи полюсов шкалы 1–5 (10.3.1).
 */
export type TstParamOut = {
    key: SensoryParam;
    /**
     * Label
     */
    label: string;
    /**
     * Low
     */
    low: string;
    /**
     * High
     */
    high: string;
};

/**
 * TstParamStatOut
 */
export type TstParamStatOut = {
    key: SensoryParam;
    /**
     * Label
     */
    label: string;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Accuracy Label
     */
    accuracy_label: string;
    /**
     * Shift
     */
    shift: number;
    /**
     * Caption
     *
     * «чаще всего точно» / «занижают на N балл(а)» / …
     */
    caption: string;
};

/**
 * TstParamValueOut
 */
export type TstParamValueOut = {
    key: SensoryParam;
    /**
     * Label
     */
    label: string;
    /**
     * Low
     */
    low: string;
    /**
     * High
     */
    high: string;
    /**
     * Value
     */
    value: number | null;
};

/**
 * TstPickOut
 */
export type TstPickOut = {
    /**
     * Kind
     */
    kind: 'fit' | 'trial';
    /**
     * Label
     *
     * «В профиль» / «Пробное»
     */
    label: string;
    wine: WineListItem;
};

/**
 * TstProfileSummaryOut
 *
 * Экран итога записи (10.6.4): «Выводы».
 */
export type TstProfileSummaryOut = {
    /**
     * Tastings Count
     */
    tastings_count: number;
    /**
     * Wines Count
     */
    wines_count: number;
    /**
     * Regions Count
     */
    regions_count: number;
    /**
     * Liked Top
     *
     * До 2 элементов «Чаще нравится» строчными
     */
    liked_top: Array<string>;
    /**
     * Liked Text
     *
     * «кислотность, тело» или «собираем данные»
     */
    liked_text: string;
    /**
     * Based On
     *
     * «Основано на N винах и N дегустациях — …»
     */
    based_on: string;
};

/**
 * TstPurchasedWineOut
 */
export type TstPurchasedWineOut = {
    /**
     * Wine Id
     */
    wine_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Winery
     */
    winery: string | null;
    /**
     * Region
     */
    region: string | null;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * In Diary
     */
    in_diary: boolean;
    /**
     * Diary Wine Id
     */
    diary_wine_id: string | null;
};

/**
 * TstRecentOut
 */
export type TstRecentOut = {
    kind: AccuracyKind;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Points
     */
    points: number;
    /**
     * Completed At
     */
    completed_at: string;
};

/**
 * TstRecognitionApplyOut
 *
 * Что «Оценить» подставляет в форму (BR-TST-43).
 */
export type TstRecognitionApplyOut = {
    /**
     * Name
     */
    name: string | null;
    /**
     * Grape
     */
    grape: string | null;
    type: WineType | null;
    region: TstNamedRef | null;
    /**
     * Region Text
     */
    region_text: string | null;
    /**
     * Region Hint
     */
    region_hint: string | null;
    /**
     * Wine Id
     */
    wine_id: string | null;
    /**
     * Banner
     *
     * «Подставили данные: <название>, <винтаж>»
     */
    banner: string;
    /**
     * Text
     */
    text: string;
};

/**
 * TstRecognitionBlockOut
 */
export type TstRecognitionBlockOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Caption
     */
    caption: string;
    /**
     * Status
     */
    status: string;
    /**
     * Applied Caption
     */
    applied_caption: string;
    /**
     * Applied
     */
    applied: Array<TstAppliedRowOut>;
    /**
     * Response
     */
    response: Array<TstAppliedRowOut>;
    /**
     * Photo Url
     */
    photo_url: string | null;
    /**
     * Footer
     */
    footer: string;
};

/**
 * TstRecognitionCardOut
 *
 * Данные карточки вина каталога для листа «Распознано по фото» (D12).
 */
export type TstRecognitionCardOut = {
    /**
     * Name
     */
    name: string;
    /**
     * Vintage
     */
    vintage: string | null;
    /**
     * Winery
     */
    winery: string | null;
    /**
     * Winery About
     */
    winery_about: string | null;
    region: TstNamedRef | null;
    type: TstTypeRefOut | null;
    /**
     * Grapes
     */
    grapes: Array<string>;
    /**
     * Abv
     */
    abv: number | null;
    /**
     * Serve
     */
    serve: string | null;
    /**
     * Decant
     */
    decant: string | null;
    /**
     * Aroma
     */
    aroma: string | null;
    /**
     * Taste
     */
    taste: string | null;
    /**
     * Food
     */
    food: string | null;
    /**
     * Photo Url
     */
    photo_url: string | null;
};

/**
 * TstRecognitionFeedbackIn
 */
export type TstRecognitionFeedbackIn = {
    /**
     * Verdict
     */
    verdict: 'accepted' | 'wrong_wine';
};

/**
 * TstRecognitionMatchOut
 */
export type TstRecognitionMatchOut = {
    /**
     * Wine Id
     */
    wine_id: string;
    /**
     * Score
     */
    score: number;
    card: TstRecognitionCardOut;
};

/**
 * TstRecognitionOut
 */
export type TstRecognitionOut = {
    /**
     * Id
     */
    id: string;
    status: RecognitionStatus;
    /**
     * Lang
     */
    lang: string;
    translation_status: RecognitionTranslation;
    /**
     * Translation Note
     */
    translation_note: string | null;
    match: TstRecognitionMatchOut | null;
    result: TstRecognitionResultOut | null;
    apply: TstRecognitionApplyOut | null;
    /**
     * Message
     */
    message: string | null;
};

/**
 * TstRecognitionResultOut
 */
export type TstRecognitionResultOut = {
    /**
     * Name
     */
    name: string | null;
    /**
     * Producer
     */
    producer: string | null;
    /**
     * Vintage
     */
    vintage: number | null;
    /**
     * Appellation
     */
    appellation: string | null;
    /**
     * Country
     */
    country: string | null;
    /**
     * Region Id
     */
    region_id: string | null;
    type: WineType | null;
    /**
     * Type Label
     */
    type_label: string | null;
    /**
     * Grapes
     */
    grapes: Array<string>;
    /**
     * Abv
     */
    abv: number | null;
    /**
     * Aroma Taste
     */
    aroma_taste: string | null;
    /**
     * Food
     */
    food: string | null;
    /**
     * Serve
     */
    serve: string | null;
    /**
     * Decant
     */
    decant: string | null;
};

/**
 * TstReferenceIn
 *
 * Оценка эксперта по шести параметрам, целые 1–5; танины — только для красных, оранжевых и
 * креплёных вин (BR-TST-20).
 */
export type TstReferenceIn = {
    /**
     * Sweetness
     */
    sweetness?: number | null;
    /**
     * Acidity
     */
    acidity?: number | null;
    /**
     * Body
     */
    body?: number | null;
    /**
     * Tannin
     */
    tannin?: number | null;
    /**
     * Aroma Intensity
     */
    aroma_intensity?: number | null;
    /**
     * Finish
     */
    finish?: number | null;
};

/**
 * TstRegionOptionOut
 */
export type TstRegionOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Country Id
     */
    country_id: string;
};

/**
 * TstRowCan
 */
export type TstRowCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * TstScorePointOut
 */
export type TstScorePointOut = {
    /**
     * Tasted At
     */
    tasted_at: string;
    /**
     * Score
     */
    score: number;
    /**
     * Label
     *
     * «12.10.2026 — 4,3»
     */
    label: string;
};

/**
 * TstSkillOut
 */
export type TstSkillOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Source
     */
    source: 'sensory' | 'identification';
    /**
     * Value
     */
    value: number;
};

/**
 * TstSpecDetailOut
 */
export type TstSpecDetailOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    category_kind: SpecCategoryKind;
    /**
     * Category Label
     */
    category_label: string;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Ok
     */
    ok: number;
    /**
     * Need
     */
    need: number;
    /**
     * Window
     */
    window: number;
    /**
     * Avg Accuracy
     */
    avg_accuracy: number | null;
    /**
     * Awarded
     */
    awarded: boolean;
    /**
     * Awarded At
     */
    awarded_at: string | null;
    /**
     * Value Label
     *
     * «получена» или «k/10»
     */
    value_label: string;
    /**
     * Caption
     *
     * «<категория> · точность N%»
     */
    caption: string;
    /**
     * Progress
     */
    progress: number;
    /**
     * Tier Note
     *
     * Условия выполнены, но ступень паспорта ниже порога
     */
    tier_note: string | null;
    /**
     * Condition Text
     */
    condition_text: string | null;
    /**
     * Condition Hint
     */
    condition_hint: string;
    /**
     * Window Caption
     */
    window_caption: string;
    /**
     * Window Results
     */
    window_results: Array<TstSpecWindowRowOut>;
    /**
     * Current Label
     */
    current_label: string | null;
    /**
     * Find Label
     */
    find_label: string;
    /**
     * Target
     *
     * `tastings:<specialization_id>`
     */
    target: string;
};

/**
 * TstSpecIn
 */
export type TstSpecIn = {
    name: LocalizedText;
    /**
     * Image Id
     */
    image_id?: string | null;
    category_kind: SpecCategoryKind;
    /**
     * Category Value
     *
     * Тип вина (`red` …) или id страны / региона GEO
     */
    category_value: string;
    condition_text?: LocalizedText | null;
    status?: SpecStatus;
};

/**
 * TstSpecItemOut
 */
export type TstSpecItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    category_kind: SpecCategoryKind;
    /**
     * Category Label
     */
    category_label: string;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Ok
     */
    ok: number;
    /**
     * Need
     */
    need: number;
    /**
     * Window
     */
    window: number;
    /**
     * Avg Accuracy
     */
    avg_accuracy: number | null;
    /**
     * Awarded
     */
    awarded: boolean;
    /**
     * Awarded At
     */
    awarded_at: string | null;
    /**
     * Value Label
     *
     * «получена» или «k/10»
     */
    value_label: string;
    /**
     * Caption
     *
     * «<категория> · точность N%»
     */
    caption: string;
    /**
     * Progress
     */
    progress: number;
    /**
     * Tier Note
     *
     * Условия выполнены, но ступень паспорта ниже порога
     */
    tier_note: string | null;
};

/**
 * TstSpecListOut
 */
export type TstSpecListOut = {
    /**
     * Items
     */
    items: Array<TstSpecItemOut>;
};

/**
 * TstSpecLogRowOut
 */
export type TstSpecLogRowOut = {
    /**
     * Id
     */
    id: string;
    user: TstNamedRef;
    specialization: TstNamedRef;
    category_kind: SpecCategoryKind;
    /**
     * Category Label
     */
    category_label: string;
    /**
     * Ok Count
     */
    ok_count: number;
    /**
     * Window Count
     */
    window_count: number;
    /**
     * Avg Accuracy
     */
    avg_accuracy: number;
    /**
     * Result Label
     *
     * «K зачётных из N · средняя точность M %»
     */
    result_label: string;
    result_link: TstLinkOut;
    /**
     * Awarded At
     */
    awarded_at: string;
};

/**
 * TstSpecPatchIn
 */
export type TstSpecPatchIn = {
    name?: LocalizedText | null;
    /**
     * Image Id
     */
    image_id?: string | null;
    category_kind?: SpecCategoryKind | null;
    /**
     * Category Value
     */
    category_value?: string | null;
    condition_text?: LocalizedText | null;
    status?: SpecStatus | null;
};

/**
 * TstSpecRowOut
 */
export type TstSpecRowOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText;
    /**
     * Name Label
     */
    name_label: string;
    image: TstImageOut | null;
    category_kind: SpecCategoryKind;
    /**
     * Category Label
     */
    category_label: string;
    /**
     * Category Value
     */
    category_value: string;
    /**
     * Category Value Label
     */
    category_value_label: string;
    condition_text: LocalizedText | null;
    /**
     * Condition Label
     */
    condition_label: string | null;
    status: SpecStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Awarded Count
     */
    awarded_count: number;
    /**
     * In Progress Count
     */
    in_progress_count: number;
    can: TstRowCan;
    /**
     * Delete Hint
     */
    delete_hint: string | null;
};

/**
 * TstSpecTopOut
 */
export type TstSpecTopOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Ok
     */
    ok: number;
    /**
     * Need
     */
    need: number;
    /**
     * Avg Accuracy
     */
    avg_accuracy: number | null;
    /**
     * Awarded
     */
    awarded: boolean;
};

/**
 * TstSpecWindowRowOut
 */
export type TstSpecWindowRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Wine
     */
    wine: string;
    /**
     * Completed At
     */
    completed_at: string;
    /**
     * Date Label
     */
    date_label: string;
    kind: AccuracyKind;
    /**
     * Kind Label
     */
    kind_label: string;
    /**
     * Country
     */
    country: string | null;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Accuracy Label
     */
    accuracy_label: string;
    /**
     * Ok
     */
    ok: boolean;
};

/**
 * TstSummaryChangedOut
 */
export type TstSummaryChangedOut = {
    /**
     * Points Total
     */
    points_total: number;
    /**
     * Level
     */
    level: string | null;
    /**
     * Level Up
     */
    level_up: boolean;
};

/**
 * TstSummaryResultOut
 */
export type TstSummaryResultOut = {
    /**
     * Id
     */
    id: string;
    kind: AccuracyKind;
    /**
     * Title
     */
    title: string;
    /**
     * Meta
     */
    meta: string;
    /**
     * Accuracy
     */
    accuracy: number;
    /**
     * Accuracy Label
     */
    accuracy_label: string;
    /**
     * Points Label
     */
    points_label: string;
    /**
     * Completed At
     */
    completed_at: string;
};

/**
 * TstSummaryRowOut
 */
export type TstSummaryRowOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * TstTasteCurrentOut
 */
export type TstTasteCurrentOut = {
    /**
     * Caption
     */
    caption: string;
    /**
     * Badge
     */
    badge: string;
    /**
     * Params
     */
    params: Array<TstParamValueOut>;
    /**
     * Favorite Label
     */
    favorite_label: string | null;
};

/**
 * TstTasteProfileOut
 */
export type TstTasteProfileOut = {
    /**
     * Empty
     *
     * В дневнике нет ни одного вина с оценкой
     */
    empty: boolean;
    /**
     * Wines Count
     */
    wines_count: number;
    /**
     * Tastings Count
     */
    tastings_count: number;
    /**
     * Regions Count
     */
    regions_count: number;
    /**
     * Liked Phrases
     */
    liked_phrases: Array<string>;
    /**
     * Disliked Phrases
     */
    disliked_phrases: Array<string>;
    /**
     * Liked Text
     *
     * До 4 формулировок через « · » или «пока нет данных»
     */
    liked_text: string;
    /**
     * Disliked Text
     */
    disliked_text: string;
    /**
     * Только при ≥ 8 дегустациях
     */
    evolution: TstEvolutionOut | null;
    /**
     * Diary Caption
     */
    diary_caption: string;
    /**
     * Last Wines
     */
    last_wines: Array<TstDiaryRowOut>;
    /**
     * Chars
     */
    chars: Array<TstCharOut>;
    /**
     * Aromas
     */
    aromas: Array<TstAromaOut>;
    /**
     * Axes
     *
     * Числовой профиль по 8 осям (≥ 3 вин)
     */
    axes: {
        [key: string]: number;
    } | null;
    /**
     * Picks
     */
    picks: Array<TstPickOut>;
    /**
     * Picks Text
     */
    picks_text: string | null;
};

/**
 * TstTasteReferenceOut
 *
 * Справочники форм разделов TST (API-03).
 */
export type TstTasteReferenceOut = {
    /**
     * Experts
     */
    experts: Array<TstExpertOptionOut>;
    /**
     * Params
     */
    params: Array<TstParamOut>;
    /**
     * Wine Types
     */
    wine_types: Array<TstOptionOut>;
    /**
     * Calibration Statuses
     */
    calibration_statuses: Array<TstOptionOut>;
    /**
     * Archive Reasons
     */
    archive_reasons: Array<TstOptionOut>;
    /**
     * Categories
     */
    categories: Array<TstOptionOut>;
    /**
     * Spec Statuses
     */
    spec_statuses: Array<TstOptionOut>;
    /**
     * Countries
     */
    countries: Array<TstNamedRef>;
    /**
     * Regions
     */
    regions: Array<TstRegionOptionOut>;
    /**
     * Climates
     */
    climates: Array<TstOptionOut>;
};

/**
 * TstTasteRowOut
 */
export type TstTasteRowOut = {
    /**
     * Caption
     */
    caption: string;
    /**
     * Badge
     */
    badge: string;
    /**
     * Params
     */
    params: Array<TstParamValueOut>;
};

/**
 * TstTastingBriefOut
 */
export type TstTastingBriefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Score
     */
    score: number;
    /**
     * Tasted At
     */
    tasted_at: string;
};

/**
 * TstTastingIn
 */
export type TstTastingIn = {
    /**
     * Score
     *
     * Оценка 1,0–5,0, шаг 0,1
     */
    score: number | string;
    /**
     * Liked
     *
     * «Понравилось» / «Не понравилось»
     */
    liked?: boolean;
    votes?: TstVotesIn;
    /**
     * Aromas Liked
     *
     * Только при `votes.aroma = 1`
     */
    aromas_liked?: Array<AromaGroup>;
    /**
     * Aromas Disliked
     *
     * Только при `votes.aroma = -1`
     */
    aromas_disliked?: Array<AromaGroup>;
    /**
     * Note
     *
     * Личная заметка
     */
    note?: string | null;
};

/**
 * TstTastingOut
 */
export type TstTastingOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Tasted At
     */
    tasted_at: string;
    /**
     * Score
     */
    score: number;
    /**
     * Score Label
     */
    score_label: string;
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Votes
     */
    votes: {
        [key: string]: number;
    };
    /**
     * Aromas Liked
     */
    aromas_liked: Array<AromaGroup>;
    /**
     * Aromas Disliked
     */
    aromas_disliked: Array<AromaGroup>;
    /**
     * Note
     */
    note: string | null;
};

/**
 * TstTypeRefOut
 */
export type TstTypeRefOut = {
    code: WineType;
    /**
     * Name
     */
    name: string;
};

/**
 * TstUserTasteSummaryOut
 */
export type TstUserTasteSummaryOut = {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Rows
     */
    rows: Array<TstSummaryRowOut>;
    /**
     * Results
     */
    results: Array<TstSummaryResultOut>;
    all_results: TstLinkOut;
};

/**
 * TstVotesIn
 *
 * «Нравится» (1) / «не нравится» (−1) / пропуск (0) по шести характеристикам (BR-TST-05).
 */
export type TstVotesIn = {
    /**
     * Sweetness
     */
    sweetness?: -1 | 0 | 1;
    /**
     * Acidity
     */
    acidity?: -1 | 0 | 1;
    /**
     * Body
     */
    body?: -1 | 0 | 1;
    /**
     * Tannin
     */
    tannin?: -1 | 0 | 1;
    /**
     * Aroma
     */
    aroma?: -1 | 0 | 1;
    /**
     * Finish
     */
    finish?: -1 | 0 | 1;
};

/**
 * TstWineOptionOut
 */
export type TstWineOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    region: TstNamedRef | null;
    type: WineType | null;
    /**
     * Type Label
     */
    type_label: string | null;
    /**
     * Tannin Applicable
     */
    tannin_applicable: boolean;
};

/**
 * TstWineOptionsOut
 */
export type TstWineOptionsOut = {
    /**
     * Items
     */
    items: Array<TstWineOptionOut>;
};

/**
 * TstWineOut
 *
 * Карточка вина каталога для экранов TST: фото, «<винодельня> · <регион>», сорта.
 */
export type TstWineOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Vintage
     *
     * «2018» или «NV»
     */
    vintage: string | null;
    /**
     * Title
     *
     * «<название> <винтаж>»
     */
    title: string;
    /**
     * Photo Url
     */
    photo_url: string | null;
    winery: TstNamedRef | null;
    region: TstNamedRef | null;
    /**
     * Place
     *
     * «<винодельня> · <регион>»
     */
    place: string | null;
    /**
     * Grapes
     *
     * Сорта купажа через « · »
     */
    grapes: string | null;
    type: WineType | null;
    /**
     * Type Label
     */
    type_label: string | null;
    /**
     * Published
     */
    published: boolean;
};

/**
 * UnpublishIn
 */
export type UnpublishIn = {
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * UnpublishRegionIn
 */
export type UnpublishRegionIn = {
    reason: HideReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * UnreadCountOut
 */
export type UnreadCountOut = {
    /**
     * Count
     */
    count: number;
};

/**
 * UnsubscribeOut
 */
export type UnsubscribeOut = {
    /**
     * Status
     */
    status: 'unsubscribed' | 'subscribed';
};

/**
 * UpdatedByOut
 */
export type UpdatedByOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * UpdatesSeenIn
 */
export type UpdatesSeenIn = {
    /**
     * Country Code
     */
    country_code: string;
    section: SeenSection;
};

/**
 * UploadCreateIn
 */
export type UploadCreateIn = {
    /**
     * Purpose
     *
     * Назначение: avatar, support_attachment, image, …
     */
    purpose: string;
    /**
     * Content Type
     */
    content_type: string;
    /**
     * Size
     *
     * Размер файла, байты
     */
    size: number;
};

/**
 * UploadTicketOut
 */
export type UploadTicketOut = {
    /**
     * Upload Id
     *
     * Идентификатор файла — передаётся в сущность (`*_file_id`)
     */
    upload_id: string;
    /**
     * Url
     *
     * Куда загрузить файл (presigned URL, 15 минут)
     */
    url: string;
    /**
     * Method
     */
    method: string;
    /**
     * Headers
     *
     * Заголовки, которые нужно передать при загрузке
     */
    headers: {
        [key: string]: string;
    };
    /**
     * Expires At
     */
    expires_at: string;
};

/**
 * UsageCountryOut
 */
export type UsageCountryOut = {
    /**
     * Country Id
     */
    country_id: string;
    /**
     * Code
     */
    code: string;
    /**
     * Title
     */
    title: string;
    /**
     * Label
     */
    label: string;
};

/**
 * UsageCourseOut
 */
export type UsageCourseOut = {
    /**
     * Course Id
     */
    course_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Section
     */
    section: string;
    /**
     * Label
     */
    label: string;
};

/**
 * UsageItemOut
 */
export type UsageItemOut = {
    /**
     * Kind
     */
    kind: string;
    /**
     * Label
     */
    label: string;
    /**
     * Count
     */
    count: number;
    /**
     * Detail
     */
    detail: string | null;
};

/**
 * UsageLessonOut
 */
export type UsageLessonOut = {
    /**
     * Lesson Id
     */
    lesson_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Is Required
     */
    is_required: boolean;
    /**
     * Label
     */
    label: string;
};

/**
 * UsageOut
 */
export type UsageOut = {
    /**
     * Items
     */
    items: Array<UsageItemOut>;
    /**
     * Deletable
     *
     * Записи нет в использовании и статус позволяет удаление
     */
    deletable: boolean;
};

/**
 * UserAccountOut
 */
export type UserAccountOut = {
    registration_method: RegistrationMethod;
    /**
     * Registration Method Label
     */
    registration_method_label: string;
    /**
     * Email Verified At
     */
    email_verified_at: string | null;
    /**
     * Identities
     */
    identities: Array<LinkedIdentityOut>;
    /**
     * First Login At
     */
    first_login_at: string | null;
    /**
     * Last Activity At
     */
    last_activity_at: string | null;
    devices: DevicesSummaryOut;
    /**
     * Payment Methods
     *
     * Сохранённые способы оплаты (INT-PAY), маскированные; null — модуль платежей не подключён
     */
    payment_methods: Array<string> | null;
};

/**
 * UserActivityOut
 */
export type UserActivityOut = {
    /**
     * Groups
     *
     * Группы модулей-владельцев; модуль без провайдера пока не показывает группу
     */
    groups: Array<ActivityGroupOut>;
};

/**
 * UserBlockIn
 */
export type UserBlockIn = {
    /**
     * Reason
     *
     * Необязательно
     */
    reason?: string | null;
};

/**
 * UserCard
 */
export type UserCard = {
    /**
     * Id
     */
    id: string;
    /**
     * Full Name
     */
    full_name: string | null;
    /**
     * Email
     */
    email: string;
    status: UserStatus;
    /**
     * Status Label
     */
    status_label: string;
    phone: PhoneOut | null;
    /**
     * Phone Display
     */
    phone_display: string | null;
    /**
     * Birth Date
     */
    birth_date: string | null;
    /**
     * Avatar Url
     */
    avatar_url: string | null;
    /**
     * Country Code
     */
    country_code: string | null;
    /**
     * Country Name
     */
    country_name: string | null;
    currency: Currency;
    /**
     * City Name
     */
    city_name: string | null;
    /**
     * City Suggestion Id
     */
    city_suggestion_id: string | null;
    locale: Language;
    /**
     * Locale Label
     */
    locale_label: string;
    /**
     * Goals
     */
    goals: Array<OnboardingGoalOut>;
    consents: UserConsentsOut;
    account: UserAccountOut;
    notifications: NotificationsOut;
    /**
     * Passport Tier
     *
     * GAM; null — нет данных
     */
    passport_tier: string | null;
    /**
     * Team Member Id
     */
    team_member_id: string | null;
    /**
     * Block Reason
     */
    block_reason: string | null;
    /**
     * Blocked At
     */
    blocked_at: string | null;
    blocked_by: AppModulesAccSchemasCommonNamedRef | null;
    /**
     * Profile Completed At
     */
    profile_completed_at: string | null;
    /**
     * Registered At
     */
    registered_at: string;
    can: UserCardCan;
};

/**
 * UserCardCan
 */
export type UserCardCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Update Country
     */
    update_country?: boolean;
    /**
     * Update Locale
     */
    update_locale?: boolean;
    /**
     * Block
     */
    block?: boolean;
    /**
     * Unblock
     */
    unblock?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * UserConsentsOut
 */
export type UserConsentsOut = {
    /**
     * null — «Нет согласия»
     */
    personal_data: AppModulesAccSchemasUsersConsentOut | null;
    privacy_policy: AppModulesAccSchemasUsersConsentOut | null;
    age_confirmation: AppModulesAccSchemasUsersConsentOut | null;
};

/**
 * UserDeleteIn
 */
export type UserDeleteIn = {
    /**
     * Confirm Email
     *
     * Почта пользователя — подтверждение
     */
    confirm_email: string;
    /**
     * Source
     */
    source: 'support' | 'web';
    /**
     * Ticket Number
     *
     * Обращение T-{number} этого пользователя
     */
    ticket_number?: number | null;
};

/**
 * UserHistoryItem
 */
export type UserHistoryItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    source_module: ActivityModule;
    /**
     * Event Type
     */
    event_type: string;
    /**
     * Title
     */
    title: string;
    /**
     * Details
     */
    details: string | null;
    /**
     * Object Ref
     */
    object_ref: {
        [key: string]: unknown;
    } | null;
};

/**
 * UserMeLabels
 *
 * Подписи строк «Настроек» (4.6.12) на языке запроса.
 */
export type UserMeLabels = {
    /**
     * Profile
     *
     * «Алина Строганова · фото загружено» / «… · без фото»
     */
    profile: string;
    /**
     * Email
     *
     * «alina@mail.ru · подтверждена»
     */
    email: string;
    /**
     * Password
     *
     * «Изменён вчера» / «Не задан»
     */
    password: string;
    /**
     * Location
     *
     * «Россия · ₽ · Краснодар»
     */
    location: string;
    /**
     * Language
     *
     * Название текущего языка
     */
    language: string;
};

/**
 * UserMeOut
 *
 * `UserMe` (пример 4.8.2.1) + поля для экранов профиля и настроек.
 */
export type UserMeOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Email
     *
     * null — почта ещё не указана (соцвход без почты)
     */
    email: string | null;
    /**
     * Email Verified
     */
    email_verified: boolean;
    /**
     * Full Name
     */
    full_name: string | null;
    /**
     * Initial
     *
     * Первая буква имени — вместо фото
     */
    initial: string | null;
    /**
     * Birth Date
     */
    birth_date: string | null;
    phone: PhoneOut | null;
    /**
     * Phone Display
     *
     * «+7 926 771-05-19»
     */
    phone_display: string | null;
    /**
     * Country Code
     */
    country_code: string | null;
    /**
     * City Name
     */
    city_name: string | null;
    /**
     * City Suggestion Id
     */
    city_suggestion_id: string | null;
    /**
     * Avatar Url
     */
    avatar_url: string | null;
    /**
     * Avatar Thumb Url
     *
     * 128 × 128 (когда готов вариант)
     */
    avatar_thumb_url: string | null;
    locale: Language;
    currency: Currency;
    /**
     * Profile Complete
     */
    profile_complete: boolean;
    /**
     * Missing
     *
     * Незаполненные обязательные поля (BR-ACC-12): email, full_name, birth_date, phone, country_code, city_name, consents
     */
    missing: Array<string>;
    /**
     * Has Password
     */
    has_password: boolean;
    /**
     * Password Changed At
     */
    password_changed_at: string | null;
    /**
     * Registration Method
     */
    registration_method: string;
    /**
     * Roles
     *
     * Активные роли в команде: expert, winemaker, admin
     */
    roles: Array<string>;
    /**
     * Expert Mode
     */
    expert_mode: boolean;
    /**
     * Expert Badge
     *
     * Показывать «Эксперт · подтверждён»
     */
    expert_badge: boolean;
    /**
     * Created At
     */
    created_at: string;
    labels: UserMeLabels;
};

/**
 * UserPatchIn
 *
 * `{full_name?, phone?, country_code?, city_name?, locale?}`. Отсутствующее поле не меняется;
 * `null` недопустим — поля обязательны для полного профиля (BR-ACC-12).
 */
export type UserPatchIn = {
    /**
     * Full Name
     */
    full_name?: string | null;
    phone?: PhoneIn | null;
    /**
     * Country Code
     */
    country_code?: string | null;
    /**
     * City Name
     */
    city_name?: string | null;
    locale?: Language | null;
};

/**
 * UserRow
 *
 * Строка реестра. Показатели модулей ORD/EXP/GAM/TST — null, пока модуль не подключён.
 */
export type UserRow = {
    /**
     * Id
     */
    id: string;
    /**
     * Full Name
     *
     * null — «не указано»
     */
    full_name: string | null;
    /**
     * Email
     */
    email: string;
    phone: PhoneOut | null;
    /**
     * Phone Display
     */
    phone_display: string | null;
    /**
     * Birth Date
     */
    birth_date: string | null;
    /**
     * Country Code
     */
    country_code: string | null;
    /**
     * Country Name
     *
     * Название страны (GEO); null — нет данных GEO
     */
    country_name: string | null;
    /**
     * City Name
     */
    city_name: string | null;
    /**
     * Orders Count
     *
     * ORD: оплаченные заказы
     */
    orders_count: number | null;
    /**
     * Bookings Count
     *
     * EXP: брони
     */
    bookings_count: number | null;
    /**
     * ORD: сумма оплаченных заказов за вычетом возвратов
     */
    orders_sum: Money | null;
    /**
     * Passport Tier
     *
     * GAM: ступень паспорта, «III Первооткрыватель»
     */
    passport_tier: string | null;
    /**
     * Xp
     *
     * GAM
     */
    xp: number | null;
    /**
     * Accuracy
     *
     * TST: баллы точности вкуса
     */
    accuracy: number | null;
    /**
     * Bonus Balance
     *
     * GAM: баланс бонусов
     */
    bonus_balance: number | null;
    /**
     * Last Activity At
     *
     * null — «ещё не входил»
     */
    last_activity_at: string | null;
    /**
     * Registered At
     */
    registered_at: string;
    status: UserStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Team Member Id
     *
     * Участник команды — ссылка «Карточка в команде»
     */
    team_member_id: string | null;
    can: UserRowCan;
};

/**
 * UserRowCan
 */
export type UserRowCan = {
    /**
     * View
     */
    view?: boolean;
    /**
     * Update
     */
    update?: boolean;
    /**
     * Block
     */
    block?: boolean;
    /**
     * Unblock
     */
    unblock?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
};

/**
 * UserStatus
 */
export const UserStatus = {
    Active: 'active',
    Blocked: 'blocked',
    Deleted: 'deleted'
} as const;

/**
 * UserStatus
 */
export type UserStatus = typeof UserStatus[keyof typeof UserStatus];

/**
 * UserSummaryOut
 */
export type UserSummaryOut = {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Xp
     */
    xp: number;
    /**
     * Xp Max
     */
    xp_max: number;
    level: SummaryLevelOut | null;
    /**
     * Level Label
     */
    level_label: string | null;
    /**
     * Held
     */
    held: boolean;
    discounts: SummaryDiscountsOut;
    first_region: SummaryFirstRegionOut;
    bonuses: SummaryBonusesOut;
    tier: SummaryTierOut | null;
    next_tier: SummaryTierOut | null;
    /**
     * Need
     */
    need: Array<SummaryNeedOut>;
    /**
     * Accuracy Pct
     */
    accuracy_pct: number;
    stamps: SummaryStampsOut;
    /**
     * Stamps Label
     */
    stamps_label: string;
    /**
     * Regions In Work
     */
    regions_in_work: number;
    /**
     * Perfect Results
     */
    perfect_results: number;
    links: SummaryLinksOut;
};

/**
 * VerificationOut
 */
export type VerificationOut = {
    /**
     * Verification Token
     *
     * Подтверждённая почта для следующего шага (30 мин)
     */
    verification_token: string;
    /**
     * Expires In
     */
    expires_in: number;
};

/**
 * VideoAssetCan
 */
export type VideoAssetCan = {
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Playback
     */
    playback?: boolean;
};

/**
 * VideoAssetOut
 */
export type VideoAssetOut = {
    /**
     * Id
     */
    id: string;
    owner_type: VideoOwnerType;
    /**
     * Owner Id
     */
    owner_id: string;
    status: VideoStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Duration Sec
     */
    duration_sec: number | null;
    /**
     * Duration Label
     */
    duration_label: string | null;
    /**
     * Max Resolution
     */
    max_resolution: string | null;
    /**
     * Aspect Ratio
     */
    aspect_ratio: string | null;
    /**
     * Poster Url
     *
     * Кадр-превью (у Mux — с подписью на срок ссылки)
     */
    poster_url: string | null;
    /**
     * Error Message
     */
    error_message: string | null;
    /**
     * Ready At
     */
    ready_at: string | null;
    source: VideoSource;
    /**
     * Source File Name
     */
    source_file_name: string | null;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Replaced By Id
     */
    replaced_by_id: string | null;
    can: VideoAssetCan;
};

/**
 * VideoClientOut
 */
export type VideoClientOut = {
    /**
     * Playback Rates
     */
    playback_rates: Array<number>;
};

/**
 * VideoImportIn
 */
export type VideoImportIn = {
    owner_type: VideoOwnerType;
    /**
     * Owner Id
     */
    owner_id: string;
    /**
     * Url
     *
     * Прямая ссылка на файл `https://…/lesson.mp4`; RU — также видео своего проекта Kinescope `https://kinescope.io/<id>`
     */
    url: string;
};

/**
 * VideoImportOut
 */
export type VideoImportOut = {
    /**
     * Video Id
     */
    video_id: string;
    status: VideoStatus;
    /**
     * Status Label
     */
    status_label: string;
};

/**
 * VideoOut
 */
export type VideoOut = {
    /**
     * Available
     *
     * false — видеохостинг (INT-SVC) не подключён
     */
    available: boolean;
    /**
     * Video Asset Id
     */
    video_asset_id: string | null;
    /**
     * Status
     *
     * waiting_upload | processing | ready | errored | deleted
     */
    status: string | null;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Duration Sec
     */
    duration_sec: number | null;
    /**
     * Poster Url
     */
    poster_url: string | null;
};

/**
 * VideoOwnerType
 */
export const VideoOwnerType = { Lesson: 'lesson', IntroLesson: 'intro_lesson' } as const;

/**
 * VideoOwnerType
 */
export type VideoOwnerType = typeof VideoOwnerType[keyof typeof VideoOwnerType];

/**
 * VideoPreviewOut
 *
 * Предпросмотр в админке — та же подписанная ссылка, что в приложении (17.13.3).
 */
export type VideoPreviewOut = {
    /**
     * Video Asset Id
     */
    video_asset_id: string;
    /**
     * Hls Url
     */
    hls_url: string;
    /**
     * Expires At
     */
    expires_at: string;
    /**
     * Duration Sec
     */
    duration_sec: number | null;
    /**
     * Thumbnail Url
     */
    thumbnail_url: string | null;
};

/**
 * VideoProgressIn
 */
export type VideoProgressIn = {
    /**
     * Video Asset Id
     */
    video_asset_id: string;
    /**
     * Position Sec
     */
    position_sec: number;
    /**
     * Watched Delta Sec
     */
    watched_delta_sec: number;
};

/**
 * VideoProgressOut
 */
export type VideoProgressOut = {
    /**
     * Position Sec
     */
    position_sec: number;
    /**
     * Completed
     */
    completed: boolean;
};

/**
 * VideoProvider
 */
export const VideoProvider = { Kinescope: 'kinescope', Mux: 'mux' } as const;

/**
 * VideoProvider
 */
export type VideoProvider = typeof VideoProvider[keyof typeof VideoProvider];

/**
 * VideoSource
 */
export const VideoSource = { Upload: 'upload', UrlImport: 'url_import' } as const;

/**
 * VideoSource
 */
export type VideoSource = typeof VideoSource[keyof typeof VideoSource];

/**
 * VideoStatus
 */
export const VideoStatus = {
    WaitingUpload: 'waiting_upload',
    Processing: 'processing',
    Ready: 'ready',
    Errored: 'errored',
    Deleted: 'deleted'
} as const;

/**
 * VideoStatus
 */
export type VideoStatus = typeof VideoStatus[keyof typeof VideoStatus];

/**
 * VideoTilesOut
 *
 * Плитки карточки видеохостинга (17.13.8).
 */
export type VideoTilesOut = {
    /**
     * Ready
     *
     * Видео готово
     */
    ready: number;
    /**
     * Processing
     *
     * В загрузке и обработке
     */
    processing: number;
    /**
     * Errored
     *
     * Ошибок обработки
     */
    errored: number;
    /**
     * Total Hours
     *
     * Общая длительность готовых видео, ч
     */
    total_hours: number;
    /**
     * Views 30D
     *
     * Просмотров за 30 дней (LRN VideoProgress)
     */
    views_30d: number;
};

/**
 * VideoUploadIn
 */
export type VideoUploadIn = {
    owner_type: VideoOwnerType;
    /**
     * Owner Id
     *
     * Урок (LRN)
     */
    owner_id: string;
    /**
     * File Name
     */
    file_name: string;
    /**
     * File Size
     *
     * Байт; не больше `video.max_upload_gb`
     */
    file_size: number;
    /**
     * Mime
     */
    mime: string;
};

/**
 * VideoUploadOut
 */
export type VideoUploadOut = {
    /**
     * Video Id
     */
    video_id: string;
    /**
     * Upload Url
     *
     * Куда грузить файл напрямую (не через наш сервер)
     */
    upload_url: string;
    /**
     * Protocol
     *
     * `tus` (Kinescope) / `put` (Mux, докачка Content-Range)
     */
    protocol: string;
    /**
     * Headers
     */
    headers?: {
        [key: string]: string;
    } | null;
    /**
     * Expires At
     */
    expires_at: string;
};

/**
 * VisitFormatOut
 */
export type VisitFormatOut = {
    /**
     * Excursion Id
     */
    excursion_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    price: Money | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
};

/**
 * VisitMode
 */
export const VisitMode = {
    Open: 'open',
    ByAppointment: 'by_appointment',
    Closed: 'closed'
} as const;

/**
 * VisitMode
 */
export type VisitMode = typeof VisitMode[keyof typeof VisitMode];

/**
 * VisitStepOut
 */
export type VisitStepOut = {
    /**
     * Done
     */
    done: boolean;
    /**
     * N
     */
    n: number;
    /**
     * Has Options
     */
    has_options: boolean;
};

/**
 * WatchIn
 */
export type WatchIn = {
    /**
     * Scope
     */
    scope: 'bottles' | 'sets' | 'drops';
    /**
     * Region Id
     */
    region_id?: string | null;
};

/**
 * WatchListOut
 */
export type WatchListOut = {
    /**
     * Items
     */
    items: Array<WatchOut>;
};

/**
 * WatchOut
 */
export type WatchOut = {
    /**
     * Scope
     */
    scope: 'bottles' | 'sets' | 'drops';
    /**
     * Region Id
     */
    region_id: string | null;
};

/**
 * WeekDayOut
 */
export type WeekDayOut = {
    weekday: Weekday;
    /**
     * Weekday Label
     */
    weekday_label: string;
    /**
     * Open
     */
    open: boolean;
    /**
     * Slots Label
     */
    slots_label: string;
    /**
     * Count Label
     */
    count_label: string;
};

/**
 * WeekSlotIn
 */
export type WeekSlotIn = {
    weekday: Weekday;
    /**
     * Start Time
     */
    start_time: string;
    /**
     * End Time
     */
    end_time: string;
    /**
     * Seats
     */
    seats?: number | null;
};

/**
 * WeekSlotOut
 */
export type WeekSlotOut = {
    weekday: Weekday;
    /**
     * Weekday Label
     */
    weekday_label: string;
    /**
     * Start Time
     */
    start_time: string;
    /**
     * End Time
     */
    end_time: string;
    /**
     * Seats
     */
    seats: number | null;
};

/**
 * WeekSlotsIn
 */
export type WeekSlotsIn = {
    /**
     * Slots
     */
    slots: Array<WeekSlotIn>;
};

/**
 * Weekday
 */
export const Weekday = {
    Mon: 'mon',
    Tue: 'tue',
    Wed: 'wed',
    Thu: 'thu',
    Fri: 'fri',
    Sat: 'sat',
    Sun: 'sun'
} as const;

/**
 * Weekday
 */
export type Weekday = typeof Weekday[keyof typeof Weekday];

/**
 * WhereToTryOut
 */
export type WhereToTryOut = {
    /**
     * Sets
     */
    sets: Array<TrySetOut>;
    winery: TryWineryOut | null;
};

/**
 * WinOut
 */
export type WinOut = {
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string;
    /**
     * Go
     */
    go: string;
    /**
     * Go Label
     */
    go_label: string;
};

/**
 * WineAdmin
 */
export type WineAdmin = {
    /**
     * Id
     */
    id: string;
    /**
     * Sku
     */
    sku: string;
    /**
     * Name
     */
    name: string;
    /**
     * Title
     *
     * «{название} · {винтаж}»
     */
    title: string;
    /**
     * Vintage
     */
    vintage: number | null;
    /**
     * Is Non Vintage
     */
    is_non_vintage: boolean;
    /**
     * Vintage Label
     */
    vintage_label: string;
    winery: RefOut | null;
    region: RefOut | null;
    country: CountryRefOut | null;
    origin_classification: AppModulesCatSchemasAdminOriginRefOut | null;
    kind: WineKind;
    /**
     * Kind Label
     */
    kind_label: string;
    sugar: WineSugar;
    /**
     * Sugar Label
     */
    sugar_label: string;
    /**
     * Type Label
     */
    type_label: string;
    color: WineColor;
    /**
     * Color Label
     */
    color_label: string;
    /**
     * Grapes
     */
    grapes: Array<GrapeShareOut>;
    /**
     * Grapes Label
     */
    grapes_label: string;
    /**
     * Abv
     */
    abv: number;
    aging: LocalizedText | null;
    serve_temp: ServeTemp;
    /**
     * Serve Temp Label
     */
    serve_temp_label: string;
    decanting: Decanting;
    /**
     * Decanting Label
     */
    decanting_label: string;
    aging_potential: AgingPotential;
    /**
     * Aging Potential Label
     */
    aging_potential_label: string;
    /**
     * Pairing Categories
     */
    pairing_categories: Array<PairingCategory>;
    /**
     * Pairing Labels
     */
    pairing_labels: Array<string>;
    pairing_custom: LocalizedText | null;
    taste: TasteOut;
    taste_scale5: TasteOut;
    /**
     * Notes
     */
    notes: Array<LocalizedText>;
    description: LocalizedText | null;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    scheduled_price: ScheduledPriceOut | null;
    volume: BottleVolume;
    /**
     * Volume Label
     */
    volume_label: string;
    /**
     * Stock
     */
    stock: number;
    /**
     * Stock Reserved
     */
    stock_reserved: number;
    /**
     * Available
     */
    available: number;
    /**
     * Sales Channels
     */
    sales_channels: Array<SalesChannel>;
    /**
     * Sales Channel Labels
     */
    sales_channel_labels: Array<string>;
    /**
     * Channels Label
     *
     * «Все каналы» или подписи через « · »
     */
    channels_label: string;
    /**
     * Delivery Options
     */
    delivery_options: Array<DeliveryOption>;
    /**
     * Delivery Labels
     */
    delivery_labels: Array<string>;
    /**
     * Seller Label
     *
     * BR-CAT-32; RU — имя партнёра из INT
     */
    seller_label: string | null;
    /**
     * Media
     */
    media: Array<CatalogMediaOut>;
    /**
     * Cover Url
     */
    cover_url: string | null;
    status: CatalogStatus;
    /**
     * Status Label
     */
    status_label: string;
    display_status: CatalogStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Published At
     */
    published_at: string | null;
    review: ReviewNoteOut | null;
    archive_reason: AppModulesCatModelsArchiveReason | null;
    /**
     * Archive Reason Label
     */
    archive_reason_label: string | null;
    /**
     * Archive Comment
     */
    archive_comment: string | null;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Создатель записи (владелец для эксперта)
     */
    author: RefOut | null;
    revision: RevisionOut | null;
    /**
     * Карточка с применённой ревизией (что увидит приложение)
     */
    revision_preview?: WineAdmin | null;
    can: WineCan;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * WineCan
 */
export type WineCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Edit Location
     *
     * Винодельня и регион правятся только в черновике
     */
    edit_location?: boolean;
    /**
     * Change Price
     */
    change_price?: boolean;
    /**
     * Edit Stock
     */
    edit_stock?: boolean;
    /**
     * Edit Media
     */
    edit_media?: boolean;
    /**
     * Submit
     */
    submit?: boolean;
    /**
     * Publish
     */
    publish?: boolean;
    /**
     * Return
     */
    return?: boolean;
    /**
     * Reject
     */
    reject?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Attach
     */
    attach?: boolean;
    /**
     * Awaiting Decision
     *
     * Автор: «Ждёт решения администратора» (неактивная кнопка)
     */
    awaiting_decision?: boolean;
    /**
     * View Reviews
     *
     * Панель «Отзывы пользователей»
     */
    view_reviews?: boolean;
};

/**
 * WineCardOut
 *
 * `GET /admin/wines/{id}` — `{wine, revision?, permissions}` (6.8.3).
 */
export type WineCardOut = {
    wine: WineAdmin;
    revision: RevisionOut | null;
    permissions: WineCan;
};

/**
 * WineColor
 */
export const WineColor = {
    Straw: 'straw',
    Golden: 'golden',
    Amber: 'amber',
    Salmon: 'salmon',
    RosePetal: 'rose_petal',
    Ruby: 'ruby',
    Garnet: 'garnet',
    Purple: 'purple',
    GarnetRubyRim: 'garnet_ruby_rim'
} as const;

/**
 * WineColor
 */
export type WineColor = typeof WineColor[keyof typeof WineColor];

/**
 * WineCreateIn
 */
export type WineCreateIn = {
    /**
     * Name
     */
    name: string;
    /**
     * Vintage
     *
     * Год 1900…текущий или «NV» (BR-CAT-03)
     */
    vintage: string | number;
    /**
     * Winery Id
     */
    winery_id: string;
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Origin Classification Id
     *
     * null — «Без классификации»
     */
    origin_classification_id?: string | null;
    kind: WineKind;
    sugar: WineSugar;
    color: WineColor;
    /**
     * Grapes
     */
    grapes?: Array<GrapeShareIn>;
    /**
     * Abv
     *
     * 0…25, один знак после запятой («13,5»)
     */
    abv: string | number;
    aging?: LocalizedText | null;
    serve_temp: ServeTemp;
    decanting: Decanting;
    aging_potential: AgingPotential;
    /**
     * Pairing Categories
     */
    pairing_categories?: Array<PairingCategory>;
    pairing_custom?: LocalizedText | null;
    /**
     * Sales Channels
     */
    sales_channels?: Array<SalesChannel>;
    price: MoneyIn;
    volume: BottleVolume;
    /**
     * Stock
     */
    stock: number;
    /**
     * Delivery Options
     */
    delivery_options?: Array<DeliveryOption>;
    taste: TasteIn;
    /**
     * Notes
     */
    notes?: Array<LocalizedText>;
    description: LocalizedText;
    /**
     * Media
     */
    media?: Array<AppModulesCatSchemasAdminMediaIn>;
};

/**
 * WineDetailOut
 *
 * `WineDetail` (6.8.1, пример).
 */
export type WineDetailOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Vintage
     */
    vintage: number | null;
    /**
     * Is Non Vintage
     */
    is_non_vintage: boolean;
    /**
     * Vintage Label
     */
    vintage_label: string;
    /**
     * Type Label
     */
    type_label: string;
    winery: AppModulesCatSchemasAppNamedRef | null;
    region: AppModulesCatSchemasAppNamedRef | null;
    country: CountryOut | null;
    /**
     * Origin Classification
     */
    origin_classification: string | null;
    /**
     * Grapes
     */
    grapes: Array<GrapeShareAppOut>;
    kind: WineKind;
    sugar: WineSugar;
    color: WineColor;
    /**
     * Abv
     */
    abv: number;
    volume: BottleVolume;
    /**
     * Aging
     */
    aging: string | null;
    serve_temp: ServeTemp;
    decanting: Decanting;
    aging_potential: AgingPotential;
    /**
     * Pairings
     */
    pairings: Array<string>;
    /**
     * Notes
     */
    notes: Array<string>;
    /**
     * Description
     */
    description: string | null;
    taste: TasteValues;
    taste_scale5: TasteValues;
    /**
     * Media
     */
    media: Array<MediaAppOut>;
    offer: OfferOut;
    /**
     * Заполняется при state ≠ buy
     */
    where_to_try: WhereToTryOut | null;
    /**
     * In Sets
     */
    in_sets: Array<AppModulesCatSchemasAppNamedRef>;
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Is Saved
     *
     * Вино в «Сохранённом» пользователя (закладка CNT)
     */
    is_saved: boolean;
    /**
     * In Diary
     */
    in_diary: boolean;
    /**
     * Bought
     */
    bought: boolean;
    /**
     * Xp Kind
     */
    xp_kind?: 'wine';
    /**
     * Subtitle
     *
     * «{тип} · {винтаж} · {объём} · {крепость}»
     */
    subtitle: string;
    /**
     * Characteristics
     */
    characteristics: Array<CharacteristicOut>;
    /**
     * Taste Scales
     */
    taste_scales: Array<TasteScaleOut>;
    /**
     * Stock Alert
     *
     * «Сообщим о поступлении» уже включено
     */
    stock_alert: boolean;
    /**
     * «Смотреть сет» для state = set
     */
    first_set: AppModulesCatSchemasAppNamedRef | null;
};

/**
 * WineDiscountOut
 */
export type WineDiscountOut = {
    /**
     * Pct
     */
    pct: number;
    /**
     * Applies On Market
     */
    applies_on_market: boolean;
};

/**
 * WineItemsOut
 */
export type WineItemsOut = {
    /**
     * Items
     */
    items: Array<WineListItem>;
};

/**
 * WineKind
 */
export const WineKind = {
    Red: 'red',
    White: 'white',
    Rose: 'rose',
    Sparkling: 'sparkling',
    Orange: 'orange',
    Fortified: 'fortified'
} as const;

/**
 * WineKind
 */
export type WineKind = typeof WineKind[keyof typeof WineKind];

/**
 * WineLinksOut
 */
export type WineLinksOut = {
    /**
     * Sales Places
     */
    sales_places: Array<AppModulesCatSchemasAdminLinkItemOut>;
    /**
     * Learning
     */
    learning: Array<AppModulesCatSchemasAdminLinkItemOut>;
    /**
     * Только admin (дневники — персональные данные)
     */
    reviews: AppModulesCatSchemasAdminReviewsOut | null;
    likes: LikesOut;
};

/**
 * WineListItem
 *
 * Строка вкладки «Вина», мини-карточка (похожие, регион, винодельня), поиск.
 */
export type WineListItem = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Vintage
     */
    vintage: number | null;
    /**
     * Is Non Vintage
     */
    is_non_vintage: boolean;
    /**
     * Vintage Label
     */
    vintage_label: string;
    /**
     * Overline
     *
     * «{винодельня} · {регион}»
     */
    overline: string;
    winery: AppModulesCatSchemasAppNamedRef | null;
    region: AppModulesCatSchemasAppNamedRef | null;
    /**
     * Type Label
     */
    type_label: string;
    kind: WineKind;
    /**
     * Main Grape
     */
    main_grape: string | null;
    /**
     * Meta
     *
     * «{тип} · {основной сорт}[ · есть в сете «…»]»
     */
    meta: string;
    /**
     * BR-CAT-36: сет с этим вином и его регионом
     */
    in_set: AppModulesCatSchemasAppNamedRef | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
    offer_state: OfferState;
    /**
     * Offer Label
     */
    offer_label: string;
    /**
     * Badge
     *
     * Подпись состояния, если не «В продаже»
     */
    badge: string | null;
    /**
     * Chips
     */
    chips: Array<string>;
    /**
     * Только для `buy`
     */
    price: Money | null;
    /**
     * Price Label
     */
    price_label: string | null;
    /**
     * Purchase Action
     */
    purchase_action: 'cart' | 'partner' | 'none';
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Liked
     */
    liked: boolean;
    /**
     * Likes Count
     */
    likes_count: number;
    /**
     * Xp Kind
     */
    xp_kind?: 'wine';
};

/**
 * WinePageOut
 */
export type WinePageOut = {
    /**
     * Items
     */
    items: Array<WineListItem>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Sort
     *
     * Применённая сортировка
     */
    sort: 'fit' | 'price_asc' | 'price_desc';
};

/**
 * WinePartnerStatus
 */
export const WinePartnerStatus = { Active: 'active', Draft: 'draft' } as const;

/**
 * WinePartnerStatus
 */
export type WinePartnerStatus = typeof WinePartnerStatus[keyof typeof WinePartnerStatus];

/**
 * WinePatchIn
 *
 * Частичный WineCreate. Цена — `POST …/price-changes`, остаток — `PATCH …/stock`, медиа —
 * `/admin/catalog/wine/{id}/media` (здесь не принимаются: `unknown_field`).
 */
export type WinePatchIn = {
    /**
     * Name
     */
    name?: string | null;
    /**
     * Vintage
     */
    vintage?: string | number | null;
    /**
     * Winery Id
     */
    winery_id?: string | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    /**
     * Origin Classification Id
     */
    origin_classification_id?: string | null;
    kind?: WineKind | null;
    sugar?: WineSugar | null;
    color?: WineColor | null;
    /**
     * Grapes
     */
    grapes?: Array<GrapeShareIn> | null;
    /**
     * Abv
     */
    abv?: string | number | null;
    aging?: LocalizedText | null;
    serve_temp?: ServeTemp | null;
    decanting?: Decanting | null;
    aging_potential?: AgingPotential | null;
    /**
     * Pairing Categories
     */
    pairing_categories?: Array<PairingCategory> | null;
    pairing_custom?: LocalizedText | null;
    /**
     * Sales Channels
     */
    sales_channels?: Array<SalesChannel> | null;
    volume?: BottleVolume | null;
    /**
     * Delivery Options
     */
    delivery_options?: Array<DeliveryOption> | null;
    taste?: TastePatchIn | null;
    /**
     * Notes
     */
    notes?: Array<LocalizedText> | null;
    description?: LocalizedText | null;
};

/**
 * WineRefApp
 */
export type WineRefApp = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
};

/**
 * WineRefOut
 */
export type WineRefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Kind
     */
    kind: string;
    /**
     * Published
     */
    published: boolean;
};

/**
 * WineRowOut
 */
export type WineRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Sku
     */
    sku: string;
    /**
     * Name
     */
    name: string;
    /**
     * Vintage Label
     */
    vintage_label: string;
    /**
     * Type Label
     */
    type_label: string;
    region: RefOut | null;
    winery: RefOut | null;
    price: Money;
    /**
     * Price Label
     */
    price_label: string;
    /**
     * Available
     */
    available: number;
    /**
     * Channels Label
     */
    channels_label: string;
    status: CatalogStatus;
    display_status: CatalogStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * WineSalesMode
 */
export const WineSalesMode = { Partner: 'partner', Platform: 'platform' } as const;

/**
 * WineSalesMode
 */
export type WineSalesMode = typeof WineSalesMode[keyof typeof WineSalesMode];

/**
 * WineSalesOut
 */
export type WineSalesOut = {
    /**
     * Days
     */
    days: number;
    /**
     * Published
     */
    published: boolean;
    /**
     * Bottles
     */
    bottles: number;
    /**
     * Orders Count
     */
    orders_count: number;
    revenue: Money;
    /**
     * Revenue Label
     */
    revenue_label: string;
    avg_check: Money | null;
    /**
     * Avg Check Label
     */
    avg_check_label: string | null;
    /**
     * By Channel
     */
    by_channel: Array<ChannelSalesOut>;
    price: Money;
    /**
     * Available
     */
    available: number;
};

/**
 * WineStyle
 */
export const WineStyle = {
    RedDry: 'red_dry',
    WhiteDry: 'white_dry',
    Rose: 'rose',
    Sparkling: 'sparkling',
    FortifiedDessert: 'fortified_dessert'
} as const;

/**
 * WineStyle
 */
export type WineStyle = typeof WineStyle[keyof typeof WineStyle];

/**
 * WineSugar
 */
export const WineSugar = {
    BrutNature: 'brut_nature',
    ExtraBrut: 'extra_brut',
    Brut: 'brut',
    Dry: 'dry',
    SemiDry: 'semi_dry',
    SemiSweet: 'semi_sweet',
    Sweet: 'sweet'
} as const;

/**
 * WineSugar
 */
export type WineSugar = typeof WineSugar[keyof typeof WineSugar];

/**
 * WineType
 *
 * Тип вина — значения общие с CAT (`WineKind`).
 */
export const WineType = {
    Red: 'red',
    White: 'white',
    Rose: 'rose',
    Sparkling: 'sparkling',
    Orange: 'orange',
    Fortified: 'fortified'
} as const;

/**
 * WineType
 *
 * Тип вина — значения общие с CAT (`WineKind`).
 */
export type WineType = typeof WineType[keyof typeof WineType];

/**
 * WineryCan
 */
export type WineryCan = {
    /**
     * Update
     */
    update?: boolean;
    /**
     * Edit Guests
     */
    edit_guests?: boolean;
    /**
     * Submit
     */
    submit?: boolean;
    /**
     * Publish
     */
    publish?: boolean;
    /**
     * Return
     */
    return?: boolean;
    /**
     * Reject
     */
    reject?: boolean;
    /**
     * Unpublish
     */
    unpublish?: boolean;
    /**
     * Archive
     */
    archive?: boolean;
    /**
     * Restore
     */
    restore?: boolean;
    /**
     * Delete
     */
    delete?: boolean;
    /**
     * Attach
     */
    attach?: boolean;
    /**
     * Awaiting Decision
     *
     * Винодел: карточка на модерации — «Ждёт решения администратора»
     */
    awaiting_decision?: boolean;
};

/**
 * WineryCardOut
 */
export type WineryCardOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    region: ZoneBrief | null;
    location: GeoPointOut;
    /**
     * Distance Km
     */
    distance_km: number;
    /**
     * Site Url
     */
    site_url: string | null;
    /**
     * Site Domain
     */
    site_domain: string | null;
    visit_mode: VisitMode;
    /**
     * Visit Mode Label
     */
    visit_mode_label: string;
    visit_from: Money | null;
    /**
     * Visit Free
     */
    visit_free: boolean;
    rating: RatingOut | null;
    /**
     * Photos
     */
    photos: Array<string>;
    /**
     * Description
     */
    description: string | null;
    /**
     * Facts
     */
    facts: Array<AppModulesGeoSchemasAppFactOut>;
    /**
     * Winemakers
     */
    winemakers: Array<AppModulesGeoSchemasAppWinemakerOut>;
    /**
     * Wines Count
     */
    wines_count: number;
    /**
     * Wines
     *
     * Карточки вин (CAT)
     */
    wines: Array<{
        [key: string]: unknown;
    }>;
    /**
     * Visit Formats
     */
    visit_formats: Array<VisitFormatOut>;
    /**
     * Tastings
     *
     * Офлайн-дегустации в винодельне (EXP)
     */
    tastings: Array<{
        [key: string]: unknown;
    }>;
    stay: StayOut | null;
    /**
     * Booking Phone
     */
    booking_phone: string | null;
    /**
     * Open Hours
     */
    open_hours: Array<OpenHoursOut>;
    /**
     * Hours Summary
     */
    hours_summary: string | null;
    how_to_get: HowToGetOut;
    /**
     * Gallery
     */
    gallery: Array<GalleryItemOut>;
    /**
     * Chronicle Count
     */
    chronicle_count: number;
    /**
     * Is Saved
     */
    is_saved: boolean;
};

/**
 * WineryCreateIn
 */
export type WineryCreateIn = {
    name: LocalizedText;
    /**
     * Region Id
     */
    region_id: string;
    visit_mode: VisitMode;
    /**
     * Group Capacity
     */
    group_capacity?: number | null;
    address: LocalizedText;
    location: GeoPointIn;
    /**
     * Site Url
     */
    site_url?: string | null;
    description: LocalizedText;
    road?: LocalizedText | null;
    transfer?: LocalizedText | null;
    /**
     * Founded Year
     */
    founded_year: number;
    /**
     * Vineyard Area Ha
     */
    vineyard_area_ha: number | string;
    /**
     * Grape Ids
     */
    grape_ids?: Array<string>;
    /**
     * Booking Phone
     */
    booking_phone?: string | null;
    /**
     * Stay Enabled
     */
    stay_enabled?: boolean;
    /**
     * Media
     */
    media?: Array<AppModulesGeoSchemasCommonMediaIn>;
};

/**
 * WineryDetailOut
 */
export type WineryDetailOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
    region: WineryRegionOut;
    region_link_source: LinkSource;
    /**
     * Winemakers
     */
    winemakers: Array<AppModulesGeoSchemasAdminWinemakerOut>;
    /**
     * Winemakers Label
     */
    winemakers_label: string;
    visit_mode: VisitMode;
    /**
     * Group Capacity
     */
    group_capacity: number | null;
    address: LocalizedText | null;
    location: GeoPointOut;
    /**
     * Site Url
     */
    site_url: string | null;
    description: LocalizedText | null;
    road: LocalizedText | null;
    transfer: LocalizedText | null;
    /**
     * Founded Year
     */
    founded_year: number;
    /**
     * Vineyard Area Ha
     */
    vineyard_area_ha: number;
    /**
     * Grape Ids
     */
    grape_ids: Array<string>;
    /**
     * Grapes
     */
    grapes: Array<GrapeOut>;
    /**
     * Booking Phone
     */
    booking_phone: string | null;
    /**
     * Stay Enabled
     */
    stay_enabled: boolean;
    guests: GuestsOut;
    /**
     * Facts
     */
    facts: Array<AppModulesGeoSchemasAdminFactOut>;
    /**
     * Media
     */
    media: Array<MediaOut>;
    status: WineryStatus;
    /**
     * Status Label
     */
    status_label: string;
    moderation: ModerationNoteOut | null;
    archive_reason: AppModulesGeoModelsArchiveReason | null;
    /**
     * Status Comment
     */
    status_comment: string | null;
    /**
     * Has Published Version
     */
    has_published_version: boolean;
    /**
     * Has Unpublished Changes
     *
     * Рабочая версия отличается от опубликованной (BR-GEO-55)
     */
    has_unpublished_changes: boolean;
    /**
     * Published At
     */
    published_at: string | null;
    rating: WineryRatingOut | null;
    /**
     * Links Counts
     */
    links_counts: {
        [key: string]: number;
    };
    can: WineryCan;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * WineryListOut
 */
export type WineryListOut = {
    /**
     * Items
     */
    items: Array<AppModulesGeoSchemasAppWineryRowOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
    /**
     * Total
     */
    total: number;
};

/**
 * WineryOptionOut
 */
export type WineryOptionOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Region Id
     */
    region_id: string;
};

/**
 * WineryPageOut
 */
export type WineryPageOut = {
    /**
     * Items
     */
    items: Array<AppModulesGeoSchemasAdminWineryRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
    /**
     * Notice
     *
     * Баннер винодела над реестром (правило «только свои»)
     */
    notice?: string | null;
};

/**
 * WineryPatchIn
 */
export type WineryPatchIn = {
    name?: LocalizedText | null;
    /**
     * Region Id
     */
    region_id?: string | null;
    visit_mode?: VisitMode | null;
    /**
     * Group Capacity
     */
    group_capacity?: number | null;
    address?: LocalizedText | null;
    location?: GeoPointIn | null;
    /**
     * Site Url
     */
    site_url?: string | null;
    description?: LocalizedText | null;
    road?: LocalizedText | null;
    transfer?: LocalizedText | null;
    /**
     * Founded Year
     */
    founded_year?: number | null;
    /**
     * Vineyard Area Ha
     */
    vineyard_area_ha?: number | string | null;
    /**
     * Grape Ids
     */
    grape_ids?: Array<string> | null;
    /**
     * Booking Phone
     */
    booking_phone?: string | null;
    /**
     * Stay Enabled
     */
    stay_enabled?: boolean | null;
};

/**
 * WineryPinOut
 */
export type WineryPinOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    location: GeoPointOut;
    /**
     * Distance Km
     */
    distance_km: number;
    visit_mode: VisitMode;
    rating: RatingOut | null;
    visit_from: Money | null;
    /**
     * Teaser
     */
    teaser: string | null;
};

/**
 * WineryRatingOut
 */
export type WineryRatingOut = {
    /**
     * Avg
     */
    avg: number;
    /**
     * Count
     */
    count: number;
};

/**
 * WineryRegionOut
 */
export type WineryRegionOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
    status: RegionStatus;
};

/**
 * WineryStatus
 */
export const WineryStatus = {
    Draft: 'draft',
    OnModeration: 'on_moderation',
    NeedsRework: 'needs_rework',
    Published: 'published',
    Unpublished: 'unpublished',
    Rejected: 'rejected',
    Archived: 'archived'
} as const;

/**
 * WineryStatus
 */
export type WineryStatus = typeof WineryStatus[keyof typeof WineryStatus];

/**
 * WorldOut
 */
export type WorldOut = {
    /**
     * Regions In Work
     */
    regions_in_work: number;
    /**
     * Regions Total
     */
    regions_total: number;
    /**
     * Header
     */
    header: string;
    tiles: WorldTilesOut;
    /**
     * Tiles View
     */
    tiles_view: Array<WorldTileOut>;
    /**
     * Progress
     */
    progress: Array<WorldProgressOut>;
};

/**
 * WorldProgressOut
 */
export type WorldProgressOut = {
    /**
     * Region Id
     */
    region_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Status
     */
    status: string;
    /**
     * Status Text
     */
    status_text: string;
    /**
     * Pct
     */
    pct: number;
    /**
     * Target
     */
    target: string;
};

/**
 * WorldTileOut
 */
export type WorldTileOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Value
     */
    value: number;
    /**
     * Label
     */
    label: string;
    /**
     * Caption
     */
    caption: string;
    /**
     * Target
     */
    target: string;
};

/**
 * WorldTilesOut
 */
export type WorldTilesOut = {
    /**
     * Wines
     */
    wines: number;
    /**
     * Tastings
     */
    tastings: number;
    /**
     * Regions Done
     */
    regions_done: number;
    /**
     * Regions Exploring
     */
    regions_exploring: number;
    /**
     * Visits
     */
    visits: number;
    /**
     * Saved Wineries
     */
    saved_wineries: number;
    /**
     * Lessons
     */
    lessons: number;
    /**
     * Intro Lessons
     */
    intro_lessons: number;
    /**
     * Subscriptions
     */
    subscriptions: number;
    /**
     * Experiences
     */
    experiences: number;
};

/**
 * WorldTitleOut
 */
export type WorldTitleOut = {
    /**
     * Overline
     */
    overline: string;
    /**
     * Title
     */
    title: string;
};

/**
 * XpEventType
 */
export const XpEventType = {
    LessonCompleted: 'lesson_completed',
    TestPerfect: 'test_perfect',
    DiaryEntryCreated: 'diary_entry_created',
    WineOrderDelivered: 'wine_order_delivered',
    SetDelivered: 'set_delivered',
    TastingOnlineAttended: 'tasting_online_attended',
    TastingOfflineAttended: 'tasting_offline_attended',
    ExcursionAttended: 'excursion_attended',
    TripCompleted: 'trip_completed',
    StampBronze: 'stamp_bronze',
    StampGold: 'stamp_gold'
} as const;

/**
 * XpEventType
 */
export type XpEventType = typeof XpEventType[keyof typeof XpEventType];

/**
 * XpGroupOut
 */
export type XpGroupOut = {
    /**
     * Key
     */
    key: string;
    /**
     * Title
     */
    title: string;
    /**
     * Items
     */
    items: Array<XpTileOut>;
};

/**
 * XpLedgerPageOut
 */
export type XpLedgerPageOut = {
    /**
     * Items
     */
    items: Array<XpLedgerRowOut>;
    /**
     * Total
     */
    total: number;
    can: ListCan;
};

/**
 * XpLedgerRowOut
 */
export type XpLedgerRowOut = {
    /**
     * Id
     */
    id: string;
    user: GamUserRefOut;
    /**
     * Amount
     */
    amount: number;
    /**
     * Amount Label
     *
     * «+50» / «−50»
     */
    amount_label: string;
    /**
     * Kind
     */
    kind: 'accrual' | 'reversal';
    /**
     * Title
     *
     * «За что»: «Урок пройден · «…»», у сторно — «Отмена: …»
     */
    title: string;
    object: LedgerObjectOut;
    /**
     * Event Type
     */
    event_type: string;
    /**
     * Event Label
     */
    event_label: string;
    /**
     * Section
     */
    section: string;
    /**
     * Section Label
     */
    section_label: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * When Label
     *
     * «сегодня · ЧЧ:ММ», «вчера · ЧЧ:ММ», «D месяц»
     */
    when_label: string;
};

/**
 * XpLevelCreateIn
 */
export type XpLevelCreateIn = {
    /**
     * «Уровень», до 60
     */
    name: LocalizedText;
    /**
     * До 80
     */
    subtitle?: LocalizedText | null;
    /**
     * Image File Id
     *
     * Загрузка `gam_icon` (PNG/SVG)
     */
    image_file_id?: string | null;
    /**
     * Min Xp
     */
    min_xp: number;
    /**
     * Пункты через « · », до 300
     */
    perks?: LocalizedText | null;
    /**
     * Learning Discount Pct
     */
    learning_discount_pct?: number | string | null;
    /**
     * Wine Discount Pct
     */
    wine_discount_pct?: number | string | null;
    one_time_reward_type?: OneTimeRewardType;
    /**
     * One Time Reward Value
     *
     * Скидка 1…50 (%) или шаг 100…100 000 XP на 1 бонус
     */
    one_time_reward_value?: number | null;
    /**
     * До 120
     */
    one_time_reward_text?: LocalizedText | null;
};

/**
 * XpLevelOut
 */
export type XpLevelOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText;
    /**
     * Name Label
     */
    name_label: string;
    subtitle: LocalizedText | null;
    /**
     * Image File Id
     */
    image_file_id: string | null;
    /**
     * Image Url
     */
    image_url: string | null;
    /**
     * Min Xp
     */
    min_xp: number;
    perks: LocalizedText | null;
    /**
     * Perks List
     */
    perks_list: Array<string>;
    /**
     * Learning Discount Pct
     */
    learning_discount_pct: number | null;
    /**
     * Wine Discount Pct
     */
    wine_discount_pct: number | null;
    /**
     * Learning Label
     *
     * «5 %» или «—»
     */
    learning_label: string;
    /**
     * Wine Label
     */
    wine_label: string;
    one_time_reward_type: OneTimeRewardType;
    /**
     * One Time Reward Type Label
     */
    one_time_reward_type_label: string;
    /**
     * One Time Reward Value
     */
    one_time_reward_value: number | null;
    one_time_reward_text: LocalizedText | null;
    /**
     * One Time Reward Label
     *
     * Колонка «Разовая награда» (текст или автотекст)
     */
    one_time_reward_label: string;
    /**
     * Users Count
     */
    users_count: number;
    /**
     * Is Start
     */
    is_start: boolean;
    can: GamRowCan;
};

/**
 * XpLevelPatchIn
 */
export type XpLevelPatchIn = {
    name?: LocalizedText | null;
    subtitle?: LocalizedText | null;
    /**
     * Image File Id
     */
    image_file_id?: string | null;
    /**
     * Min Xp
     */
    min_xp?: number | null;
    perks?: LocalizedText | null;
    /**
     * Learning Discount Pct
     */
    learning_discount_pct?: number | string | null;
    /**
     * Wine Discount Pct
     */
    wine_discount_pct?: number | string | null;
    one_time_reward_type?: OneTimeRewardType | null;
    /**
     * One Time Reward Value
     */
    one_time_reward_value?: number | null;
    one_time_reward_text?: LocalizedText | null;
};

/**
 * XpLevelsPageOut
 */
export type XpLevelsPageOut = {
    /**
     * Items
     */
    items: Array<XpLevelOut>;
    /**
     * Total
     */
    total: number;
    /**
     * Intro Lessons Total
     *
     * Число уроков вводного курса (подзаголовок)
     */
    intro_lessons_total: number;
    can: ListCan;
};

/**
 * XpPairOut
 */
export type XpPairOut = {
    /**
     * Got
     */
    got: number;
    /**
     * Total
     */
    total: number;
    /**
     * Label
     */
    label: string;
};

/**
 * XpRuleCardOut
 */
export type XpRuleCardOut = {
    /**
     * Id
     */
    id: string;
    event_type: XpEventType;
    /**
     * Event Label
     *
     * Системное название типа события
     */
    event_label: string;
    /**
     * Title
     *
     * «Событие»
     */
    title: string;
    /**
     * Xp
     */
    xp: number;
    /**
     * Repeat Text
     *
     * «Повторяемость» — задаётся системой по типу
     */
    repeat_text: string;
    /**
     * Conditions
     */
    conditions: string;
    /**
     * Unique Key Text
     *
     * «Уникальный ключ» — задаётся системой по типу
     */
    unique_key_text: string;
    status: XpRuleStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Section
     */
    section: string;
    /**
     * Section Label
     */
    section_label: string;
    /**
     * Version
     */
    version: number;
    /**
     * Activated At
     */
    activated_at: string | null;
    /**
     * Disabled At
     */
    disabled_at: string | null;
    /**
     * Ledger Count
     *
     * Начислено записей по правилу
     */
    ledger_count: number;
    can: RuleCan;
    /**
     * «Открыть в журнале» → «Начисления XP»
     */
    ledger_link: GamSectionLinkOut;
    /**
     * History
     */
    history: Array<GamRuleHistoryEntryOut>;
};

/**
 * XpRuleCreateIn
 */
export type XpRuleCreateIn = {
    event_type: XpEventType;
    /**
     * Title
     */
    title: string;
    /**
     * Xp
     */
    xp: number;
    /**
     * Conditions
     */
    conditions: string;
    status?: XpRuleStatus;
    /**
     * Confirm Replace
     *
     * Подтверждение: действующее правило типа будет выключено (иначе 409)
     */
    confirm_replace?: boolean;
};

/**
 * XpRuleOut
 */
export type XpRuleOut = {
    /**
     * Id
     */
    id: string;
    event_type: XpEventType;
    /**
     * Event Label
     *
     * Системное название типа события
     */
    event_label: string;
    /**
     * Title
     *
     * «Событие»
     */
    title: string;
    /**
     * Xp
     */
    xp: number;
    /**
     * Repeat Text
     *
     * «Повторяемость» — задаётся системой по типу
     */
    repeat_text: string;
    /**
     * Conditions
     */
    conditions: string;
    /**
     * Unique Key Text
     *
     * «Уникальный ключ» — задаётся системой по типу
     */
    unique_key_text: string;
    status: XpRuleStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Section
     */
    section: string;
    /**
     * Section Label
     */
    section_label: string;
    /**
     * Version
     */
    version: number;
    /**
     * Activated At
     */
    activated_at: string | null;
    /**
     * Disabled At
     */
    disabled_at: string | null;
    /**
     * Ledger Count
     *
     * Начислено записей по правилу
     */
    ledger_count: number;
    can: RuleCan;
};

/**
 * XpRulePatchIn
 */
export type XpRulePatchIn = {
    /**
     * Title
     */
    title?: string | null;
    /**
     * Xp
     */
    xp?: number | null;
    /**
     * Conditions
     */
    conditions?: string | null;
    status?: XpRuleStatus | null;
    /**
     * Confirm Replace
     */
    confirm_replace?: boolean;
};

/**
 * XpRuleStatus
 */
export const XpRuleStatus = {
    Draft: 'draft',
    Active: 'active',
    Disabled: 'disabled'
} as const;

/**
 * XpRuleStatus
 */
export type XpRuleStatus = typeof XpRuleStatus[keyof typeof XpRuleStatus];

/**
 * XpRulesPageOut
 */
export type XpRulesPageOut = {
    /**
     * Items
     */
    items: Array<XpRuleOut>;
    /**
     * Total
     */
    total: number;
    /**
     * Active Count
     *
     * Число активных правил (подзаголовок «· N правил»)
     */
    active_count: number;
    can: ListCan;
};

/**
 * XpTileOut
 */
export type XpTileOut = {
    /**
     * Event Type
     */
    event_type: string;
    /**
     * Xp
     */
    xp: number;
    /**
     * Xp Label
     *
     * «+50 XP»
     */
    xp_label: string;
    /**
     * Group
     */
    group: 'study' | 'diary' | 'experiences' | 'stamps';
    /**
     * Title
     */
    title: string;
    /**
     * Hint
     */
    hint: string;
};

/**
 * ZoneBrief
 */
export type ZoneBrief = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * ZoneInfoOut
 */
export type ZoneInfoOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    center: GeoPointOut;
    /**
     * Regions Count
     */
    regions_count: number;
    country: CountryBrief;
};

/**
 * ZoneOut
 */
export type ZoneOut = {
    zone: ZoneInfoOut;
    /**
     * Regions
     */
    regions: Array<AppModulesGeoSchemasAppRegionRowOut>;
    /**
     * Show You Here
     */
    show_you_here: boolean;
    you_here: GeoPointOut | null;
    /**
     * Bounds
     */
    bounds: {
        [key: string]: {
            [key: string]: number;
        };
    } | null;
};

/**
 * ZonePinOut
 */
export type ZonePinOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    center: GeoPointOut;
    /**
     * Regions Count
     */
    regions_count: number;
    /**
     * Wineries Count
     */
    wineries_count: number;
    /**
     * Min Distance Km
     */
    min_distance_km: number;
    /**
     * Max Distance Km
     */
    max_distance_km: number;
    /**
     * Cover Url
     */
    cover_url: string | null;
    /**
     * Nearest Region Id
     */
    nearest_region_id: string;
};

/**
 * ZoneType
 */
export const ZoneType = {
    WineZone: 'wine_zone',
    Appellation: 'appellation',
    Subregion: 'subregion'
} as const;

/**
 * ZoneType
 */
export type ZoneType = typeof ZoneType[keyof typeof ZoneType];

/**
 * ZonesOut
 */
export type ZonesOut = {
    country: CountryBrief;
    /**
     * Items
     */
    items: Array<ZonePinOut>;
    /**
     * Regions Count
     */
    regions_count: number;
    /**
     * Min Distance Km
     */
    min_distance_km: number | null;
    /**
     * Max Distance Km
     */
    max_distance_km: number | null;
    /**
     * Show You Here
     */
    show_you_here: boolean;
    you_here: GeoPointOut | null;
    /**
     * Bounds
     */
    bounds: {
        [key: string]: {
            [key: string]: number;
        };
    } | null;
};

/**
 * HistoryItemOut
 */
export type AppIntegrationsSchemasHistoryItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Occurred At
     */
    occurred_at: string;
    /**
     * Actor Name
     */
    actor_name: string;
    /**
     * Action Label
     */
    action_label: string;
    /**
     * Summary
     */
    summary: string;
};

/**
 * AppMarketOut
 *
 * ARCH-11 `market`: базовые параметры инсталляции.
 */
export type AppMarketSchemasAppMarketOut = {
    /**
     * Code
     */
    code: string;
    /**
     * Default Country
     */
    default_country: string;
    default_city: AppMarketSchemasCityOut;
    /**
     * Phone Code
     */
    phone_code: string;
    /**
     * Platforms
     */
    platforms: Array<ClientPlatform>;
    /**
     * Timezone
     */
    timezone: string;
};

/**
 * CityOut
 */
export type AppMarketSchemasCityOut = {
    /**
     * Name
     */
    name: string;
    /**
     * Lat
     */
    lat: number;
    /**
     * Lng
     */
    lng: number;
};

/**
 * CityOut
 */
export type AppModulesAccSchemasAppCityOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Is Default
     */
    is_default: boolean;
};

/**
 * ConsentOut
 */
export type AppModulesAccSchemasAppConsentOut = {
    kind: ConsentKind;
    /**
     * Document Version Id
     */
    document_version_id: string | null;
    /**
     * Document Version Label
     */
    document_version_label: string | null;
    /**
     * Accepted At
     */
    accepted_at: string;
};

/**
 * TicketCreateIn
 */
export type AppModulesAccSchemasAppTicketCreateIn = {
    topic_code: SupportTopicCode;
    /**
     * Subject
     */
    subject: string;
    /**
     * Details
     */
    details: string;
    /**
     * Ref Text
     */
    ref_text?: string | null;
    /**
     * Attachment File Ids
     */
    attachment_file_ids?: Array<string>;
    /**
     * Reply Email
     *
     * Почта для ответа (гость)
     */
    reply_email?: string | null;
};

/**
 * StatusOut
 */
export type AppModulesAccSchemasCareStatusOut = {
    code: TicketStatus;
    /**
     * Label
     */
    label: string;
    /**
     * Description
     */
    description: string;
};

/**
 * TicketCreateIn
 *
 * «Новое обращение»: найденный пользователь (`user_id`) или почта + имя (`contact_*`).
 */
export type AppModulesAccSchemasCareTicketCreateIn = {
    /**
     * User Id
     */
    user_id?: string | null;
    /**
     * Contact Email
     */
    contact_email?: string | null;
    /**
     * Contact Name
     */
    contact_name?: string | null;
    channel: SupportChannel;
    topic_code: SupportTopicCode;
    priority_code: SupportPriorityCode;
    /**
     * Subject
     */
    subject: string;
    /**
     * First Message
     */
    first_message: string;
    /**
     * Related
     */
    related?: Array<RelatedItem> | null;
};

/**
 * FileRefOut
 */
export type AppModulesAccSchemasCommonFileRefOut = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * Url
     *
     * Ссылка на файл (публичный — постоянная)
     */
    url: string | null;
    /**
     * Content Type
     */
    content_type?: string | null;
    /**
     * Width
     */
    width?: number | null;
    /**
     * Height
     */
    height?: number | null;
};

/**
 * NamedRef
 */
export type AppModulesAccSchemasCommonNamedRef = {
    /**
     * Id
     */
    id: string;
    /**
     * Full Name
     */
    full_name: string;
};

/**
 * ConsentOut
 */
export type AppModulesAccSchemasUsersConsentOut = {
    /**
     * Accepted At
     */
    accepted_at: string;
    /**
     * Version Label
     */
    version_label: string | null;
    /**
     * Document Version Id
     */
    document_version_id: string | null;
};

/**
 * ArchiveReason
 */
export const AppModulesCatModelsArchiveReason = {
    NotRelevant: 'not_relevant',
    Duplicate: 'duplicate',
    ContractTerminated: 'contract_terminated',
    DataError: 'data_error'
} as const;

/**
 * ArchiveReason
 */
export type AppModulesCatModelsArchiveReason = typeof AppModulesCatModelsArchiveReason[keyof typeof AppModulesCatModelsArchiveReason];

/**
 * ArchiveIn
 */
export type AppModulesCatSchemasAdminArchiveIn = {
    reason: AppModulesCatModelsArchiveReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * ConfirmNameIn
 */
export type AppModulesCatSchemasAdminConfirmNameIn = {
    /**
     * Confirm Name
     *
     * Точное название записи (BR-CAT-28)
     */
    confirm_name: string;
};

/**
 * HistoryItemOut
 */
export type AppModulesCatSchemasAdminHistoryItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * At
     */
    at: string;
    /**
     * Actor Name
     */
    actor_name: string;
    /**
     * Text
     */
    text: string;
};

/**
 * HistoryPageOut
 */
export type AppModulesCatSchemasAdminHistoryPageOut = {
    /**
     * Items
     */
    items: Array<AppModulesCatSchemasAdminHistoryItemOut>;
    /**
     * Total
     */
    total: number;
};

/**
 * LinkItemOut
 */
export type AppModulesCatSchemasAdminLinkItemOut = {
    /**
     * Kind
     */
    kind: string;
    /**
     * Id
     */
    id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Detail
     */
    detail: string;
    /**
     * Tag
     */
    tag: string;
};

/**
 * MediaIn
 */
export type AppModulesCatSchemasAdminMediaIn = {
    media_type?: MediaType;
    /**
     * Не указано — первое фото становится обложкой (BR-CAT-11)
     */
    placement?: MediaPlacement | null;
    /**
     * File Id
     *
     * `upload_id` загрузки `cat_image`
     */
    file_id?: string | null;
    /**
     * Source Url
     *
     * Ссылка на файл
     */
    source_url?: string | null;
    /**
     * Подпись, до 200
     */
    caption?: LocalizedText | null;
};

/**
 * MediaPatchIn
 */
export type AppModulesCatSchemasAdminMediaPatchIn = {
    /**
     * File Id
     */
    file_id?: string | null;
    /**
     * Source Url
     */
    source_url?: string | null;
    caption?: LocalizedText | null;
    placement?: MediaPlacement | null;
};

/**
 * OriginRefOut
 */
export type AppModulesCatSchemasAdminOriginRefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Code
     */
    code: string;
    /**
     * Full Name
     */
    full_name: string;
    /**
     * Label
     */
    label: string;
};

/**
 * ReviewsOut
 */
export type AppModulesCatSchemasAdminReviewsOut = {
    /**
     * Diary Entries
     *
     * TST; null — дневник не подключён
     */
    diary_entries: number | null;
    /**
     * Avg Rating
     */
    avg_rating: number | null;
    /**
     * Repeat Purchase Pct
     *
     * Доля покупателей с 2+ заказами вина; null — продаж нет
     */
    repeat_purchase_pct: number | null;
};

/**
 * StatusOut
 */
export type AppModulesCatSchemasAdminStatusOut = {
    status: CatalogStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Плашка с учётом ревизии (6.5.1)
     */
    display_status: CatalogStatus;
    /**
     * Display Status Label
     */
    display_status_label: string;
    revision_state: RevisionState | null;
};

/**
 * NamedRef
 */
export type AppModulesCatSchemasAppNamedRef = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
};

/**
 * SearchOut
 */
export type AppModulesCatSchemasAppSearchOut = {
    /**
     * Wines
     */
    wines: Array<WineListItem>;
    /**
     * Sets
     */
    sets: Array<SetListItem>;
};

/**
 * ArchiveReason
 */
export const AppModulesGeoModelsArchiveReason = {
    NotRelevant: 'not_relevant',
    Duplicate: 'duplicate',
    PartnerTerminated: 'partner_terminated',
    DataError: 'data_error'
} as const;

/**
 * ArchiveReason
 */
export type AppModulesGeoModelsArchiveReason = typeof AppModulesGeoModelsArchiveReason[keyof typeof AppModulesGeoModelsArchiveReason];

/**
 * AppMarketOut
 *
 * Строка реестра и карточка страны приложения.
 *
 * Языки интерфейса, валюта и возраст покупки вина — параметры инсталляции (MarketConfig),
 * одинаковые для всех стран приложения; в записи страны они не хранятся и не правятся.
 */
export type AppModulesGeoSchemasAdminAppMarketOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Country Code
     *
     * ISO 3166-1 alpha-2, верхний регистр (как в ACC)
     */
    country_code: string;
    /**
     * Название страны (системный справочник)
     */
    name: LocalizedText | null;
    /**
     * Languages
     *
     * Языки интерфейса — `locale.languages` рынка
     */
    languages: Array<string>;
    /**
     * Languages Label
     */
    languages_label: string;
    /**
     * Currency
     *
     * Валюта цен — `currency` рынка
     */
    currency: string;
    /**
     * Currency Label
     */
    currency_label: string;
    /**
     * Wine Purchase Min Age
     *
     * Покупка вина с — `age.min_age` рынка
     */
    wine_purchase_min_age: number;
    /**
     * Wine Purchase Min Age Label
     */
    wine_purchase_min_age_label: string;
    /**
     * Phone Code
     *
     * Телефонный код страны
     */
    phone_code: string | null;
    status: AppMarketStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Is Default
     *
     * Страна по умолчанию рынка (`geo.default_country`)
     */
    is_default: boolean;
    /**
     * Sort Order
     */
    sort_order: number;
    /**
     * Note
     */
    note: string | null;
    /**
     * Config Note
     *
     * Пояснение для полей из конфигурации рынка (языки, валюта, возраст)
     */
    config_note: string;
    can: AppMarketCan;
    /**
     * Created At
     */
    created_at: string;
    /**
     * Updated At
     */
    updated_at: string;
};

/**
 * ArchiveIn
 */
export type AppModulesGeoSchemasAdminArchiveIn = {
    reason: AppModulesGeoModelsArchiveReason;
    /**
     * Comment
     */
    comment?: string | null;
};

/**
 * ConfirmNameIn
 */
export type AppModulesGeoSchemasAdminConfirmNameIn = {
    /**
     * Confirm Name
     *
     * Точное название записи (BR-GEO-50)
     */
    confirm_name: string;
};

/**
 * FactOut
 */
export type AppModulesGeoSchemasAdminFactOut = {
    /**
     * Id
     */
    id: string;
    title: LocalizedText | null;
    text: LocalizedText | null;
    /**
     * Position
     */
    position: number;
};

/**
 * HistoryItemOut
 */
export type AppModulesGeoSchemasAdminHistoryItemOut = {
    /**
     * Id
     */
    id: string;
    /**
     * At
     */
    at: string;
    /**
     * Actor Name
     */
    actor_name: string;
    /**
     * Action
     */
    action: string;
};

/**
 * HistoryPageOut
 */
export type AppModulesGeoSchemasAdminHistoryPageOut = {
    /**
     * Items
     */
    items: Array<AppModulesGeoSchemasAdminHistoryItemOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * LinkItemOut
 */
export type AppModulesGeoSchemasAdminLinkItemOut = {
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Title
     */
    title: string;
    /**
     * Subtitle
     */
    subtitle: string | null;
    /**
     * Source
     */
    source: 'auto' | 'manual';
    /**
     * Can Detach
     */
    can_detach: boolean;
};

/**
 * OriginRefOut
 */
export type AppModulesGeoSchemasAdminOriginRefOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Code
     */
    code: string;
    full_name: LocalizedText | null;
    level: OriginLevel;
};

/**
 * RegionCountsOut
 */
export type AppModulesGeoSchemasAdminRegionCountsOut = {
    /**
     * Wineries
     *
     * Винодельни региона, кроме архивных
     */
    wineries: number;
    /**
     * Wines
     *
     * Вина региона, кроме архивных (CAT)
     */
    wines: number;
    /**
     * Learning
     *
     * Курсы, уроки и тесты региона (LRN)
     */
    learning: number;
    /**
     * Experiences
     *
     * Дегустации, экскурсии и поездки региона (EXP)
     */
    experiences: number;
};

/**
 * RegionRowOut
 */
export type AppModulesGeoSchemasAdminRegionRowOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
    country: CountryRef;
    counts: AppModulesGeoSchemasAdminRegionCountsOut;
    status: RegionStatus;
    /**
     * Status Label
     */
    status_label: string;
    /**
     * Sort Order
     */
    sort_order: number;
    can: RegionCan;
};

/**
 * WinemakerOut
 */
export type AppModulesGeoSchemasAdminWinemakerOut = {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Name
     */
    name: string | null;
};

/**
 * WineryRowOut
 */
export type AppModulesGeoSchemasAdminWineryRowOut = {
    /**
     * Id
     */
    id: string;
    name: LocalizedText | null;
    region: WineryRegionOut;
    /**
     * Winemakers
     */
    winemakers: Array<string>;
    /**
     * Winemakers Label
     *
     * ФИО через запятую или «не назначен»
     */
    winemakers_label: string;
    /**
     * Wines Count
     *
     * Вина винодельни, кроме архивных (CAT)
     */
    wines_count: number;
    visit_mode: VisitMode;
    /**
     * Visit Mode Label
     */
    visit_mode_label: string;
    status: WineryStatus;
    /**
     * Status Label
     */
    status_label: string;
    can: WineryCan;
};

/**
 * FactOut
 */
export type AppModulesGeoSchemasAppFactOut = {
    /**
     * Label
     */
    label: string;
    /**
     * Value
     */
    value: string;
};

/**
 * RegionCountsOut
 */
export type AppModulesGeoSchemasAppRegionCountsOut = {
    /**
     * Wineries
     */
    wineries: number;
    /**
     * Lessons
     */
    lessons: number;
    /**
     * Events
     */
    events: number;
};

/**
 * RegionRowOut
 */
export type AppModulesGeoSchemasAppRegionRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    country: CountryBrief;
    map_zone: ZoneBrief | null;
    /**
     * Cover Url
     */
    cover_url: string | null;
    center: GeoPointOut;
    /**
     * Distance Km
     */
    distance_km: number;
    counts: AppModulesGeoSchemasAppRegionCountsOut;
    /**
     * Early Free
     */
    early_free: boolean;
    /**
     * Is Done
     */
    is_done: boolean;
};

/**
 * ReviewsOut
 */
export type AppModulesGeoSchemasAppReviewsOut = {
    summary: ReviewSummaryOut | null;
    mine: ReviewOut | null;
    /**
     * Can Review
     */
    can_review: boolean;
    /**
     * Items
     */
    items: Array<ReviewOut>;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * SearchOut
 */
export type AppModulesGeoSchemasAppSearchOut = {
    /**
     * Query
     */
    query: string;
    counts: SearchCountsOut;
    groups: SearchGroupsOut;
    /**
     * Next Cursor
     */
    next_cursor: string | null;
};

/**
 * WinemakerOut
 */
export type AppModulesGeoSchemasAppWinemakerOut = {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Member Id
     *
     * Участник команды — страница винодела `maker:` (CNT)
     */
    member_id?: string | null;
    /**
     * Name
     */
    name: string | null;
    /**
     * Role
     */
    role: string;
    /**
     * Since
     */
    since: string | null;
    /**
     * Followers
     */
    followers: number | null;
    /**
     * Avatar Url
     */
    avatar_url: string | null;
};

/**
 * WineryRowOut
 */
export type AppModulesGeoSchemasAppWineryRowOut = {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    region: ZoneBrief | null;
    location: GeoPointOut;
    /**
     * Distance Km
     */
    distance_km: number;
    /**
     * Cover Url
     */
    cover_url: string | null;
    visit_mode: VisitMode;
    rating: RatingOut | null;
    /**
     * «визит от» (BR-GEO-37); null — не выводится
     */
    visit_from: Money | null;
    /**
     * Visit Free
     *
     * Все опубликованные экскурсии бесплатны
     */
    visit_free: boolean;
    /**
     * Is Visited
     */
    is_visited: boolean;
};

/**
 * FileRefOut
 */
export type AppModulesGeoSchemasCommonFileRefOut = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * Url
     */
    url: string | null;
    /**
     * Preview Url
     */
    preview_url: string | null;
    /**
     * Width
     */
    width: number | null;
    /**
     * Height
     */
    height: number | null;
};

/**
 * MediaIn
 *
 * Медиа (5.8.2): `{file_id, role, caption, slot}`; видео — ссылкой `url`.
 */
export type AppModulesGeoSchemasCommonMediaIn = {
    /**
     * cover | gallery | video | illustration | stamp_bronze | stamp_gold | specialization (если не задан — берётся role)
     */
    slot?: MediaSlot | null;
    /**
     * MediaItem.role (5.3.1)
     */
    role?: MediaRole | null;
    /**
     * File Id
     *
     * upload_id загруженного файла
     */
    file_id?: string | null;
    /**
     * Url
     *
     * Ссылка на видео
     */
    url?: string | null;
    caption?: LocalizedText | null;
};

/**
 * MediaPatchIn
 *
 * «Заменить» (новый `file_id`/`url` на то же место с той же подписью), подпись, позиция,
 * «Сделать обложкой» (`slot = cover`).
 */
export type AppModulesGeoSchemasCommonMediaPatchIn = {
    /**
     * File Id
     */
    file_id?: string | null;
    /**
     * Url
     */
    url?: string | null;
    caption?: LocalizedText | null;
    slot?: MediaSlot | null;
    role?: MediaRole | null;
    /**
     * Position
     */
    position?: number | null;
};

export type AppMarketConfigGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/config';
};

export type AppMarketConfigGetErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppMarketConfigGetError = AppMarketConfigGetErrors[keyof AppMarketConfigGetErrors];

export type AppMarketConfigGetResponses = {
    /**
     * Successful Response
     */
    200: AppConfigOut;
};

export type AppMarketConfigGetResponse = AppMarketConfigGetResponses[keyof AppMarketConfigGetResponses];

export type AppCoreUploadsCreateData = {
    body: UploadCreateIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/uploads';
};

export type AppCoreUploadsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppCoreUploadsCreateError = AppCoreUploadsCreateErrors[keyof AppCoreUploadsCreateErrors];

export type AppCoreUploadsCreateResponses = {
    /**
     * Successful Response
     */
    201: UploadTicketOut;
};

export type AppCoreUploadsCreateResponse = AppCoreUploadsCreateResponses[keyof AppCoreUploadsCreateResponses];

export type AppCoreUploadsCreateFilesData = {
    body: UploadCreateIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/files';
};

export type AppCoreUploadsCreateFilesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppCoreUploadsCreateFilesError = AppCoreUploadsCreateFilesErrors[keyof AppCoreUploadsCreateFilesErrors];

export type AppCoreUploadsCreateFilesResponses = {
    /**
     * Successful Response
     */
    201: UploadTicketOut;
};

export type AppCoreUploadsCreateFilesResponse = AppCoreUploadsCreateFilesResponses[keyof AppCoreUploadsCreateFilesResponses];

export type AppCoreUploadsCompleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/app/uploads/{upload_id}/complete';
};

export type AppCoreUploadsCompleteErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCoreUploadsCompleteError = AppCoreUploadsCompleteErrors[keyof AppCoreUploadsCompleteErrors];

export type AppCoreUploadsCompleteResponses = {
    /**
     * Successful Response
     */
    200: FileOut;
};

export type AppCoreUploadsCompleteResponse = AppCoreUploadsCompleteResponses[keyof AppCoreUploadsCompleteResponses];

export type AppIntClientConfigGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/integrations/client-config';
};

export type AppIntClientConfigGetErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppIntClientConfigGetError = AppIntClientConfigGetErrors[keyof AppIntClientConfigGetErrors];

export type AppIntClientConfigGetResponses = {
    /**
     * Successful Response
     */
    200: ClientIntegrationsOut;
};

export type AppIntClientConfigGetResponse = AppIntClientConfigGetResponses[keyof AppIntClientConfigGetResponses];

export type AppAccDeviceUpdateData = {
    body: DevicePatchIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/devices/current';
};

export type AppAccDeviceUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccDeviceUpdateError = AppAccDeviceUpdateErrors[keyof AppAccDeviceUpdateErrors];

export type AppAccDeviceUpdateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppAccDeviceUpdateResponse = AppAccDeviceUpdateResponses[keyof AppAccDeviceUpdateResponses];

export type AppAccDeviceRegisterData = {
    body: DeviceRegisterIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/devices/current';
};

export type AppAccDeviceRegisterErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccDeviceRegisterError = AppAccDeviceRegisterErrors[keyof AppAccDeviceRegisterErrors];

export type AppAccDeviceRegisterResponses = {
    /**
     * Successful Response
     */
    200: DeviceOut;
};

export type AppAccDeviceRegisterResponse = AppAccDeviceRegisterResponses[keyof AppAccDeviceRegisterResponses];

export type AppAccOnboardingGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/onboarding';
};

export type AppAccOnboardingGetErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccOnboardingGetError = AppAccOnboardingGetErrors[keyof AppAccOnboardingGetErrors];

export type AppAccOnboardingGetResponses = {
    /**
     * Successful Response
     */
    200: OnboardingAppOut;
};

export type AppAccOnboardingGetResponse = AppAccOnboardingGetResponses[keyof AppAccOnboardingGetResponses];

export type AppAccGuestAgeGateData = {
    body: AgeGateIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/guest/age-gate';
};

export type AppAccGuestAgeGateErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccGuestAgeGateError = AppAccGuestAgeGateErrors[keyof AppAccGuestAgeGateErrors];

export type AppAccGuestAgeGateResponses = {
    /**
     * Successful Response
     */
    200: AgeGateOut;
};

export type AppAccGuestAgeGateResponse = AppAccGuestAgeGateResponses[keyof AppAccGuestAgeGateResponses];

export type AppAccGuestConsentsCreateData = {
    body: GuestConsentsIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/guest/consents';
};

export type AppAccGuestConsentsCreateErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccGuestConsentsCreateError = AppAccGuestConsentsCreateErrors[keyof AppAccGuestConsentsCreateErrors];

export type AppAccGuestConsentsCreateResponses = {
    /**
     * Successful Response
     */
    201: ConsentsOut;
};

export type AppAccGuestConsentsCreateResponse = AppAccGuestConsentsCreateResponses[keyof AppAccGuestConsentsCreateResponses];

export type AppAccOnboardingGoalsSaveData = {
    body: OnboardingGoalsIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/onboarding/goals';
};

export type AppAccOnboardingGoalsSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccOnboardingGoalsSaveError = AppAccOnboardingGoalsSaveErrors[keyof AppAccOnboardingGoalsSaveErrors];

export type AppAccOnboardingGoalsSaveResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppAccOnboardingGoalsSaveResponse = AppAccOnboardingGoalsSaveResponses[keyof AppAccOnboardingGoalsSaveResponses];

export type AppAccGuestLocationUpdateData = {
    body: GuestLocationIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/guest/location';
};

export type AppAccGuestLocationUpdateErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccGuestLocationUpdateError = AppAccGuestLocationUpdateErrors[keyof AppAccGuestLocationUpdateErrors];

export type AppAccGuestLocationUpdateResponses = {
    /**
     * Successful Response
     */
    200: GuestLocationOut;
};

export type AppAccGuestLocationUpdateResponse = AppAccGuestLocationUpdateResponses[keyof AppAccGuestLocationUpdateResponses];

export type AppAccAuthCodesSendData = {
    body: CodeRequestIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/codes';
};

export type AppAccAuthCodesSendErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppAccAuthCodesSendError = AppAccAuthCodesSendErrors[keyof AppAccAuthCodesSendErrors];

export type AppAccAuthCodesSendResponses = {
    /**
     * Successful Response
     */
    202: CodeSentOut;
};

export type AppAccAuthCodesSendResponse = AppAccAuthCodesSendResponses[keyof AppAccAuthCodesSendResponses];

export type AppAccAuthCodesVerifyData = {
    body: CodeVerifyIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/codes/verify';
};

export type AppAccAuthCodesVerifyErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthCodesVerifyError = AppAccAuthCodesVerifyErrors[keyof AppAccAuthCodesVerifyErrors];

export type AppAccAuthCodesVerifyResponses = {
    /**
     * Successful Response
     */
    200: VerificationOut;
};

export type AppAccAuthCodesVerifyResponse = AppAccAuthCodesVerifyResponses[keyof AppAccAuthCodesVerifyResponses];

export type AppAccAuthRegisterData = {
    body: RegisterIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/register';
};

export type AppAccAuthRegisterErrors = {
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthRegisterError = AppAccAuthRegisterErrors[keyof AppAccAuthRegisterErrors];

export type AppAccAuthRegisterResponses = {
    /**
     * Successful Response
     */
    201: AuthOut;
};

export type AppAccAuthRegisterResponse = AppAccAuthRegisterResponses[keyof AppAccAuthRegisterResponses];

export type AppAccAuthLoginPasswordData = {
    body: PasswordLoginIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/login/password';
};

export type AppAccAuthLoginPasswordErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthLoginPasswordError = AppAccAuthLoginPasswordErrors[keyof AppAccAuthLoginPasswordErrors];

export type AppAccAuthLoginPasswordResponses = {
    /**
     * Successful Response
     */
    200: AuthOut;
};

export type AppAccAuthLoginPasswordResponse = AppAccAuthLoginPasswordResponses[keyof AppAccAuthLoginPasswordResponses];

export type AppAccAuthLoginCodeData = {
    body: CodeLoginIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/login/code';
};

export type AppAccAuthLoginCodeErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthLoginCodeError = AppAccAuthLoginCodeErrors[keyof AppAccAuthLoginCodeErrors];

export type AppAccAuthLoginCodeResponses = {
    /**
     * Successful Response
     */
    200: AuthOut;
};

export type AppAccAuthLoginCodeResponse = AppAccAuthLoginCodeResponses[keyof AppAccAuthLoginCodeResponses];

export type AppAccAuthSocialCompleteData = {
    body: SocialCompleteIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/social/complete';
};

export type AppAccAuthSocialCompleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthSocialCompleteError = AppAccAuthSocialCompleteErrors[keyof AppAccAuthSocialCompleteErrors];

export type AppAccAuthSocialCompleteResponses = {
    /**
     * Successful Response
     */
    200: SocialCompleteOut;
};

export type AppAccAuthSocialCompleteResponse = AppAccAuthSocialCompleteResponses[keyof AppAccAuthSocialCompleteResponses];

export type AppAccAuthSocialLoginData = {
    body: SocialLoginIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        provider: OAuthProvider;
    };
    query?: never;
    url: '/api/v1/app/auth/social/{provider}';
};

export type AppAccAuthSocialLoginErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка провайдера
     */
    502: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppAccAuthSocialLoginError = AppAccAuthSocialLoginErrors[keyof AppAccAuthSocialLoginErrors];

export type AppAccAuthSocialLoginResponses = {
    /**
     * Successful Response
     */
    200: SocialAuthOut;
};

export type AppAccAuthSocialLoginResponse = AppAccAuthSocialLoginResponses[keyof AppAccAuthSocialLoginResponses];

export type AppAccAuthOauthHandoffData = {
    body: OAuthHandoffIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/oauth/handoff';
};

export type AppAccAuthOauthHandoffErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthOauthHandoffError = AppAccAuthOauthHandoffErrors[keyof AppAccAuthOauthHandoffErrors];

export type AppAccAuthOauthHandoffResponses = {
    /**
     * Successful Response
     */
    200: OAuthResultOut;
};

export type AppAccAuthOauthHandoffResponse = AppAccAuthOauthHandoffResponses[keyof AppAccAuthOauthHandoffResponses];

export type AppAccAuthOauthPollData = {
    body: OAuthPollIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/oauth/poll';
};

export type AppAccAuthOauthPollErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthOauthPollError = AppAccAuthOauthPollErrors[keyof AppAccAuthOauthPollErrors];

export type AppAccAuthOauthPollResponses = {
    /**
     * Successful Response
     */
    200: OAuthResultOut;
};

export type AppAccAuthOauthPollResponse = AppAccAuthOauthPollResponses[keyof AppAccAuthOauthPollResponses];

export type AppAccAuthOauthStartData = {
    body: OAuthStartIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        provider: OAuthProvider;
    };
    query?: never;
    url: '/api/v1/app/auth/oauth/{provider}/start';
};

export type AppAccAuthOauthStartErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppAccAuthOauthStartError = AppAccAuthOauthStartErrors[keyof AppAccAuthOauthStartErrors];

export type AppAccAuthOauthStartResponses = {
    /**
     * Successful Response
     */
    200: OAuthStartOut;
};

export type AppAccAuthOauthStartResponse = AppAccAuthOauthStartResponses[keyof AppAccAuthOauthStartResponses];

export type AppAccAuthOauthCompleteData = {
    body: OAuthCompleteIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        provider: OAuthProvider;
    };
    query?: never;
    url: '/api/v1/app/auth/oauth/{provider}/complete';
};

export type AppAccAuthOauthCompleteErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка провайдера
     */
    502: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppAccAuthOauthCompleteError = AppAccAuthOauthCompleteErrors[keyof AppAccAuthOauthCompleteErrors];

export type AppAccAuthOauthCompleteResponses = {
    /**
     * Successful Response
     */
    200: OAuthResultOut;
};

export type AppAccAuthOauthCompleteResponse = AppAccAuthOauthCompleteResponses[keyof AppAccAuthOauthCompleteResponses];

export type AppAccAuthPasswordResetData = {
    body: PasswordResetIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/password/reset';
};

export type AppAccAuthPasswordResetErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthPasswordResetError = AppAccAuthPasswordResetErrors[keyof AppAccAuthPasswordResetErrors];

export type AppAccAuthPasswordResetResponses = {
    /**
     * Successful Response
     */
    200: AuthOut;
};

export type AppAccAuthPasswordResetResponse = AppAccAuthPasswordResetResponses[keyof AppAccAuthPasswordResetResponses];

export type AppAccAuthRefreshData = {
    body: AppRefreshIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/refresh';
};

export type AppAccAuthRefreshErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccAuthRefreshError = AppAccAuthRefreshErrors[keyof AppAccAuthRefreshErrors];

export type AppAccAuthRefreshResponses = {
    /**
     * Successful Response
     */
    200: AppRefreshOut;
};

export type AppAccAuthRefreshResponse = AppAccAuthRefreshResponses[keyof AppAccAuthRefreshResponses];

export type AppAccAuthLogoutData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/logout';
};

export type AppAccAuthLogoutErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccAuthLogoutError = AppAccAuthLogoutErrors[keyof AppAccAuthLogoutErrors];

export type AppAccAuthLogoutResponses = {
    /**
     * Выход выполнен
     */
    204: void;
};

export type AppAccAuthLogoutResponse = AppAccAuthLogoutResponses[keyof AppAccAuthLogoutResponses];

export type AppAccMeDeleteData = {
    body: DeleteMeIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me';
};

export type AppAccMeDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccMeDeleteError = AppAccMeDeleteErrors[keyof AppAccMeDeleteErrors];

export type AppAccMeDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppAccMeDeleteResponse = AppAccMeDeleteResponses[keyof AppAccMeDeleteResponses];

export type AppAccMeGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me';
};

export type AppAccMeGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccMeGetError = AppAccMeGetErrors[keyof AppAccMeGetErrors];

export type AppAccMeGetResponses = {
    /**
     * Successful Response
     */
    200: UserMeOut;
};

export type AppAccMeGetResponse = AppAccMeGetResponses[keyof AppAccMeGetResponses];

export type AppAccMeUpdateData = {
    body: MePatchIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me';
};

export type AppAccMeUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccMeUpdateError = AppAccMeUpdateErrors[keyof AppAccMeUpdateErrors];

export type AppAccMeUpdateResponses = {
    /**
     * Successful Response
     */
    200: UserMeOut;
};

export type AppAccMeUpdateResponse = AppAccMeUpdateResponses[keyof AppAccMeUpdateResponses];

export type AppAccMeAvatarDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me/avatar';
};

export type AppAccMeAvatarDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccMeAvatarDeleteError = AppAccMeAvatarDeleteErrors[keyof AppAccMeAvatarDeleteErrors];

export type AppAccMeAvatarDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppAccMeAvatarDeleteResponse = AppAccMeAvatarDeleteResponses[keyof AppAccMeAvatarDeleteResponses];

export type AppAccMeAvatarSetData = {
    body: AvatarIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me/avatar';
};

export type AppAccMeAvatarSetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccMeAvatarSetError = AppAccMeAvatarSetErrors[keyof AppAccMeAvatarSetErrors];

export type AppAccMeAvatarSetResponses = {
    /**
     * Successful Response
     */
    200: AvatarOut;
};

export type AppAccMeAvatarSetResponse = AppAccMeAvatarSetResponses[keyof AppAccMeAvatarSetResponses];

export type AppAccMeLocationUpdateData = {
    body: MeLocationIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me/location';
};

export type AppAccMeLocationUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccMeLocationUpdateError = AppAccMeLocationUpdateErrors[keyof AppAccMeLocationUpdateErrors];

export type AppAccMeLocationUpdateResponses = {
    /**
     * Successful Response
     */
    200: UserMeOut;
};

export type AppAccMeLocationUpdateResponse = AppAccMeLocationUpdateResponses[keyof AppAccMeLocationUpdateResponses];

export type AppAccMeEmailChangeRequestData = {
    body: EmailChangeIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me/email/change-request';
};

export type AppAccMeEmailChangeRequestErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppAccMeEmailChangeRequestError = AppAccMeEmailChangeRequestErrors[keyof AppAccMeEmailChangeRequestErrors];

export type AppAccMeEmailChangeRequestResponses = {
    /**
     * Successful Response
     */
    202: CodeSentOut;
};

export type AppAccMeEmailChangeRequestResponse = AppAccMeEmailChangeRequestResponses[keyof AppAccMeEmailChangeRequestResponses];

export type AppAccMeEmailConfirmData = {
    body: EmailConfirmIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me/email/confirm';
};

export type AppAccMeEmailConfirmErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccMeEmailConfirmError = AppAccMeEmailConfirmErrors[keyof AppAccMeEmailConfirmErrors];

export type AppAccMeEmailConfirmResponses = {
    /**
     * Successful Response
     */
    200: UserMeOut;
};

export type AppAccMeEmailConfirmResponse = AppAccMeEmailConfirmResponses[keyof AppAccMeEmailConfirmResponses];

export type AppAccMePasswordChangeData = {
    body: MePasswordIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me/password';
};

export type AppAccMePasswordChangeErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccMePasswordChangeError = AppAccMePasswordChangeErrors[keyof AppAccMePasswordChangeErrors];

export type AppAccMePasswordChangeResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppAccMePasswordChangeResponse = AppAccMePasswordChangeResponses[keyof AppAccMePasswordChangeResponses];

export type AppAccMeDeletionCheckData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/me/deletion-check';
};

export type AppAccMeDeletionCheckErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccMeDeletionCheckError = AppAccMeDeletionCheckErrors[keyof AppAccMeDeletionCheckErrors];

export type AppAccMeDeletionCheckResponses = {
    /**
     * Successful Response
     */
    200: DeletionCheckOut;
};

export type AppAccMeDeletionCheckResponse = AppAccMeDeletionCheckResponses[keyof AppAccMeDeletionCheckResponses];

export type AppAccSettingsNotificationsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/settings/notifications';
};

export type AppAccSettingsNotificationsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccSettingsNotificationsGetError = AppAccSettingsNotificationsGetErrors[keyof AppAccSettingsNotificationsGetErrors];

export type AppAccSettingsNotificationsGetResponses = {
    /**
     * Successful Response
     */
    200: NotificationSettingsOut;
};

export type AppAccSettingsNotificationsGetResponse = AppAccSettingsNotificationsGetResponses[keyof AppAccSettingsNotificationsGetResponses];

export type AppAccSettingsNotificationsSaveData = {
    body: NotificationSettingsIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/settings/notifications';
};

export type AppAccSettingsNotificationsSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccSettingsNotificationsSaveError = AppAccSettingsNotificationsSaveErrors[keyof AppAccSettingsNotificationsSaveErrors];

export type AppAccSettingsNotificationsSaveResponses = {
    /**
     * Successful Response
     */
    200: NotificationSettingsOut;
};

export type AppAccSettingsNotificationsSaveResponse = AppAccSettingsNotificationsSaveResponses[keyof AppAccSettingsNotificationsSaveResponses];

export type AppAccDictionariesCitiesData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Country
         *
         * ISO 3166-1 alpha-2
         */
        country?: string | null;
        /**
         * Q
         */
        q?: string | null;
    };
    url: '/api/v1/app/dictionaries/cities';
};

export type AppAccDictionariesCitiesErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccDictionariesCitiesError = AppAccDictionariesCitiesErrors[keyof AppAccDictionariesCitiesErrors];

export type AppAccDictionariesCitiesResponses = {
    /**
     * Successful Response
     */
    200: CitiesOut;
};

export type AppAccDictionariesCitiesResponse = AppAccDictionariesCitiesResponses[keyof AppAccDictionariesCitiesResponses];

export type AppAccDictionariesPhoneCodesData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/dictionaries/phone-codes';
};

export type AppAccDictionariesPhoneCodesErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccDictionariesPhoneCodesError = AppAccDictionariesPhoneCodesErrors[keyof AppAccDictionariesPhoneCodesErrors];

export type AppAccDictionariesPhoneCodesResponses = {
    /**
     * Successful Response
     */
    200: PhoneCodesOut;
};

export type AppAccDictionariesPhoneCodesResponse = AppAccDictionariesPhoneCodesResponses[keyof AppAccDictionariesPhoneCodesResponses];

export type AppAccSupportTopicsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/support/topics';
};

export type AppAccSupportTopicsListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccSupportTopicsListError = AppAccSupportTopicsListErrors[keyof AppAccSupportTopicsListErrors];

export type AppAccSupportTopicsListResponses = {
    /**
     * Successful Response
     */
    200: SupportTopicsOut;
};

export type AppAccSupportTopicsListResponse = AppAccSupportTopicsListResponses[keyof AppAccSupportTopicsListResponses];

export type AppAccSupportTicketsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Status
         */
        status?: 'new' | 'work' | 'done' | null;
        /**
         * Summary
         *
         * Только счётчики
         */
        summary?: boolean;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/support/tickets';
};

export type AppAccSupportTicketsListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccSupportTicketsListError = AppAccSupportTicketsListErrors[keyof AppAccSupportTicketsListErrors];

export type AppAccSupportTicketsListResponses = {
    /**
     * Successful Response
     */
    200: TicketListOut;
};

export type AppAccSupportTicketsListResponse = AppAccSupportTicketsListResponses[keyof AppAccSupportTicketsListResponses];

export type AppAccSupportTicketsCreateData = {
    body: AppModulesAccSchemasAppTicketCreateIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/support/tickets';
};

export type AppAccSupportTicketsCreateErrors = {
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppAccSupportTicketsCreateError = AppAccSupportTicketsCreateErrors[keyof AppAccSupportTicketsCreateErrors];

export type AppAccSupportTicketsCreateResponses = {
    /**
     * Successful Response
     */
    201: TicketOut;
};

export type AppAccSupportTicketsCreateResponse = AppAccSupportTicketsCreateResponses[keyof AppAccSupportTicketsCreateResponses];

export type AppAccSupportTicketsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Number
         *
         * Номер заявки («№ 1107»)
         */
        number: number;
    };
    query?: never;
    url: '/api/v1/app/support/tickets/{number}';
};

export type AppAccSupportTicketsGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccSupportTicketsGetError = AppAccSupportTicketsGetErrors[keyof AppAccSupportTicketsGetErrors];

export type AppAccSupportTicketsGetResponses = {
    /**
     * Successful Response
     */
    200: TicketOut;
};

export type AppAccSupportTicketsGetResponse = AppAccSupportTicketsGetResponses[keyof AppAccSupportTicketsGetResponses];

export type AppAccSupportTicketsAddMessageData = {
    body: TicketMessageIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Number
         *
         * Номер заявки («№ 1107»)
         */
        number: number;
    };
    query?: never;
    url: '/api/v1/app/support/tickets/{number}/messages';
};

export type AppAccSupportTicketsAddMessageErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccSupportTicketsAddMessageError = AppAccSupportTicketsAddMessageErrors[keyof AppAccSupportTicketsAddMessageErrors];

export type AppAccSupportTicketsAddMessageResponses = {
    /**
     * Successful Response
     */
    201: TicketOut;
};

export type AppAccSupportTicketsAddMessageResponse = AppAccSupportTicketsAddMessageResponses[keyof AppAccSupportTicketsAddMessageResponses];

export type AppAccSupportTicketsReopenData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Number
         *
         * Номер заявки («№ 1107»)
         */
        number: number;
    };
    query?: never;
    url: '/api/v1/app/support/tickets/{number}/reopen';
};

export type AppAccSupportTicketsReopenErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppAccSupportTicketsReopenError = AppAccSupportTicketsReopenErrors[keyof AppAccSupportTicketsReopenErrors];

export type AppAccSupportTicketsReopenResponses = {
    /**
     * Successful Response
     */
    200: TicketOut;
};

export type AppAccSupportTicketsReopenResponse = AppAccSupportTicketsReopenResponses[keyof AppAccSupportTicketsReopenResponses];

export type AppGeoConfigGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/geo/config';
};

export type AppGeoConfigGetErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoConfigGetError = AppGeoConfigGetErrors[keyof AppGeoConfigGetErrors];

export type AppGeoConfigGetResponses = {
    /**
     * Successful Response
     */
    200: GeoConfigOut;
};

export type AppGeoConfigGetResponse = AppGeoConfigGetResponses[keyof AppGeoConfigGetResponses];

export type AppGeoCountriesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/countries';
};

export type AppGeoCountriesListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoCountriesListError = AppGeoCountriesListErrors[keyof AppGeoCountriesListErrors];

export type AppGeoCountriesListResponses = {
    /**
     * Successful Response
     */
    200: AppCountryListOut;
};

export type AppGeoCountriesListResponse = AppGeoCountriesListResponses[keyof AppGeoCountriesListResponses];

export type AppGeoDictionariesCountriesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/dictionaries/countries';
};

export type AppGeoDictionariesCountriesListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoDictionariesCountriesListError = AppGeoDictionariesCountriesListErrors[keyof AppGeoDictionariesCountriesListErrors];

export type AppGeoDictionariesCountriesListResponses = {
    /**
     * Successful Response
     */
    200: AppDictionaryCountriesOut;
};

export type AppGeoDictionariesCountriesListResponse = AppGeoDictionariesCountriesListResponses[keyof AppGeoDictionariesCountriesListResponses];

export type AppGeoRegionsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Country
         *
         * Код страны или `all`
         */
        country?: string | null;
        /**
         * Status
         *
         * all | done
         */
        status?: 'all' | 'done';
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/regions';
};

export type AppGeoRegionsListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoRegionsListError = AppGeoRegionsListErrors[keyof AppGeoRegionsListErrors];

export type AppGeoRegionsListResponses = {
    /**
     * Successful Response
     */
    200: RegionListOut;
};

export type AppGeoRegionsListResponse = AppGeoRegionsListResponses[keyof AppGeoRegionsListResponses];

export type AppGeoRegionsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: {
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
    };
    url: '/api/v1/app/regions/{region_id}';
};

export type AppGeoRegionsGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoRegionsGetError = AppGeoRegionsGetErrors[keyof AppGeoRegionsGetErrors];

export type AppGeoRegionsGetResponses = {
    /**
     * Successful Response
     */
    200: RegionOverviewOut;
};

export type AppGeoRegionsGetResponse = AppGeoRegionsGetResponses[keyof AppGeoRegionsGetResponses];

export type AppGeoRegionsPointsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: {
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
    };
    url: '/api/v1/app/regions/{region_id}/points';
};

export type AppGeoRegionsPointsErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoRegionsPointsError = AppGeoRegionsPointsErrors[keyof AppGeoRegionsPointsErrors];

export type AppGeoRegionsPointsResponses = {
    /**
     * Successful Response
     */
    200: RegionPointsOut;
};

export type AppGeoRegionsPointsResponse = AppGeoRegionsPointsResponses[keyof AppGeoRegionsPointsResponses];

export type AppGeoMapZonesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Country
         */
        country?: string | null;
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
    };
    url: '/api/v1/app/map/zones';
};

export type AppGeoMapZonesListErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoMapZonesListError = AppGeoMapZonesListErrors[keyof AppGeoMapZonesListErrors];

export type AppGeoMapZonesListResponses = {
    /**
     * Successful Response
     */
    200: ZonesOut;
};

export type AppGeoMapZonesListResponse = AppGeoMapZonesListResponses[keyof AppGeoMapZonesListResponses];

export type AppGeoMapZonesGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Zone Id
         */
        zone_id: string;
    };
    query?: {
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
    };
    url: '/api/v1/app/map/zones/{zone_id}';
};

export type AppGeoMapZonesGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoMapZonesGetError = AppGeoMapZonesGetErrors[keyof AppGeoMapZonesGetErrors];

export type AppGeoMapZonesGetResponses = {
    /**
     * Successful Response
     */
    200: ZoneOut;
};

export type AppGeoMapZonesGetResponse = AppGeoMapZonesGetResponses[keyof AppGeoMapZonesGetResponses];

export type AppGeoWineriesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Visit Mode
         *
         * open | by_appointment
         */
        visit_mode?: 'open' | 'by_appointment' | null;
        /**
         * Visited
         */
        visited?: boolean;
        /**
         * Sort
         *
         * near | rating | cheap | dear
         */
        sort?: 'near' | 'rating' | 'cheap' | 'dear';
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/wineries';
};

export type AppGeoWineriesListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoWineriesListError = AppGeoWineriesListErrors[keyof AppGeoWineriesListErrors];

export type AppGeoWineriesListResponses = {
    /**
     * Successful Response
     */
    200: WineryListOut;
};

export type AppGeoWineriesListResponse = AppGeoWineriesListResponses[keyof AppGeoWineriesListResponses];

export type AppGeoWineriesGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: {
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
    };
    url: '/api/v1/app/wineries/{winery_id}';
};

export type AppGeoWineriesGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoWineriesGetError = AppGeoWineriesGetErrors[keyof AppGeoWineriesGetErrors];

export type AppGeoWineriesGetResponses = {
    /**
     * Successful Response
     */
    200: WineryCardOut;
};

export type AppGeoWineriesGetResponse = AppGeoWineriesGetResponses[keyof AppGeoWineriesGetResponses];

export type AppGeoReviewsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: {
        /**
         * Cursor
         *
         * Курсор
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер порции (по умолчанию 5)
         */
        limit?: number;
    };
    url: '/api/v1/app/wineries/{winery_id}/reviews';
};

export type AppGeoReviewsListErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoReviewsListError = AppGeoReviewsListErrors[keyof AppGeoReviewsListErrors];

export type AppGeoReviewsListResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAppReviewsOut;
};

export type AppGeoReviewsListResponse = AppGeoReviewsListResponses[keyof AppGeoReviewsListResponses];

export type AppGeoReviewsMeGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/app/wineries/{winery_id}/reviews/me';
};

export type AppGeoReviewsMeGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoReviewsMeGetError = AppGeoReviewsMeGetErrors[keyof AppGeoReviewsMeGetErrors];

export type AppGeoReviewsMeGetResponses = {
    /**
     * Successful Response
     */
    200: MyReviewOut;
};

export type AppGeoReviewsMeGetResponse = AppGeoReviewsMeGetResponses[keyof AppGeoReviewsMeGetResponses];

export type AppGeoReviewsMePutData = {
    body: ReviewIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/app/wineries/{winery_id}/reviews/me';
};

export type AppGeoReviewsMePutErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoReviewsMePutError = AppGeoReviewsMePutErrors[keyof AppGeoReviewsMePutErrors];

export type AppGeoReviewsMePutResponses = {
    /**
     * Successful Response
     */
    200: ReviewSavedOut;
    /**
     * Created
     */
    201: ReviewSavedOut;
};

export type AppGeoReviewsMePutResponse = AppGeoReviewsMePutResponses[keyof AppGeoReviewsMePutResponses];

export type AppGeoSearchData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Q
         */
        q?: string | null;
        /**
         * Group
         */
        group?: 'all' | 'regions' | 'wines' | 'sets' | 'wineries' | 'experiences' | 'lessons' | 'people';
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/search';
};

export type AppGeoSearchErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoSearchError = AppGeoSearchErrors[keyof AppGeoSearchErrors];

export type AppGeoSearchResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAppSearchOut;
};

export type AppGeoSearchResponse = AppGeoSearchResponses[keyof AppGeoSearchResponses];

export type AppGeoSearchPopularData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
    };
    url: '/api/v1/app/search/popular';
};

export type AppGeoSearchPopularErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoSearchPopularError = AppGeoSearchPopularErrors[keyof AppGeoSearchPopularErrors];

export type AppGeoSearchPopularResponses = {
    /**
     * Successful Response
     */
    200: PopularOut;
};

export type AppGeoSearchPopularResponse = AppGeoSearchPopularResponses[keyof AppGeoSearchPopularResponses];

export type AppGeoNotifySubscriptionsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/notify-subscriptions';
};

export type AppGeoNotifySubscriptionsListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoNotifySubscriptionsListError = AppGeoNotifySubscriptionsListErrors[keyof AppGeoNotifySubscriptionsListErrors];

export type AppGeoNotifySubscriptionsListResponses = {
    /**
     * Successful Response
     */
    200: NotifySubscriptionListOut;
};

export type AppGeoNotifySubscriptionsListResponse = AppGeoNotifySubscriptionsListResponses[keyof AppGeoNotifySubscriptionsListResponses];

export type AppGeoNotifySubscriptionsDisableData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        topic: NotifyTopic;
    };
    query?: {
        /**
         * Region Id
         */
        region_id?: string | null;
    };
    url: '/api/v1/app/notify-subscriptions/{topic}';
};

export type AppGeoNotifySubscriptionsDisableErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoNotifySubscriptionsDisableError = AppGeoNotifySubscriptionsDisableErrors[keyof AppGeoNotifySubscriptionsDisableErrors];

export type AppGeoNotifySubscriptionsDisableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppGeoNotifySubscriptionsDisableResponse = AppGeoNotifySubscriptionsDisableResponses[keyof AppGeoNotifySubscriptionsDisableResponses];

export type AppGeoNotifySubscriptionsEnableData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        topic: NotifyTopic;
    };
    query?: {
        /**
         * Region Id
         */
        region_id?: string | null;
    };
    url: '/api/v1/app/notify-subscriptions/{topic}';
};

export type AppGeoNotifySubscriptionsEnableErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGeoNotifySubscriptionsEnableError = AppGeoNotifySubscriptionsEnableErrors[keyof AppGeoNotifySubscriptionsEnableErrors];

export type AppGeoNotifySubscriptionsEnableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppGeoNotifySubscriptionsEnableResponse = AppGeoNotifySubscriptionsEnableResponses[keyof AppGeoNotifySubscriptionsEnableResponses];

export type AppSrvNotificationsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Tab
         *
         * all | orders | events | learning
         */
        tab?: 'all' | 'orders' | 'events' | 'learning';
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/notifications';
};

export type AppSrvNotificationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppSrvNotificationsListError = AppSrvNotificationsListErrors[keyof AppSrvNotificationsListErrors];

export type AppSrvNotificationsListResponses = {
    /**
     * Successful Response
     */
    200: FeedPageOut;
};

export type AppSrvNotificationsListResponse = AppSrvNotificationsListResponses[keyof AppSrvNotificationsListResponses];

export type AppSrvNotificationsUnreadCountData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/notifications/unread-count';
};

export type AppSrvNotificationsUnreadCountErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppSrvNotificationsUnreadCountError = AppSrvNotificationsUnreadCountErrors[keyof AppSrvNotificationsUnreadCountErrors];

export type AppSrvNotificationsUnreadCountResponses = {
    /**
     * Successful Response
     */
    200: UnreadCountOut;
};

export type AppSrvNotificationsUnreadCountResponse = AppSrvNotificationsUnreadCountResponses[keyof AppSrvNotificationsUnreadCountResponses];

export type AppSrvNotificationsReadData = {
    body: ReadIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/notifications/read';
};

export type AppSrvNotificationsReadErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppSrvNotificationsReadError = AppSrvNotificationsReadErrors[keyof AppSrvNotificationsReadErrors];

export type AppSrvNotificationsReadResponses = {
    /**
     * Successful Response
     */
    200: ReadOut;
};

export type AppSrvNotificationsReadResponse = AppSrvNotificationsReadResponses[keyof AppSrvNotificationsReadResponses];

export type AppSrvNotificationSettingsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/notification-settings';
};

export type AppSrvNotificationSettingsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppSrvNotificationSettingsGetError = AppSrvNotificationSettingsGetErrors[keyof AppSrvNotificationSettingsGetErrors];

export type AppSrvNotificationSettingsGetResponses = {
    /**
     * Successful Response
     */
    200: NotificationPreferencesOut;
};

export type AppSrvNotificationSettingsGetResponse = AppSrvNotificationSettingsGetResponses[keyof AppSrvNotificationSettingsGetResponses];

export type AppSrvNotificationSettingsUpdateData = {
    body: NotificationSettingsPatchIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/notification-settings';
};

export type AppSrvNotificationSettingsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppSrvNotificationSettingsUpdateError = AppSrvNotificationSettingsUpdateErrors[keyof AppSrvNotificationSettingsUpdateErrors];

export type AppSrvNotificationSettingsUpdateResponses = {
    /**
     * Successful Response
     */
    200: NotificationPreferencesOut;
};

export type AppSrvNotificationSettingsUpdateResponse = AppSrvNotificationSettingsUpdateResponses[keyof AppSrvNotificationSettingsUpdateResponses];

export type AppCatCountsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/catalog/counts';
};

export type AppCatCountsErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatCountsError = AppCatCountsErrors[keyof AppCatCountsErrors];

export type AppCatCountsResponses = {
    /**
     * Successful Response
     */
    200: CountsOut;
};

export type AppCatCountsResponse = AppCatCountsResponses[keyof AppCatCountsResponses];

export type AppCatFacetsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Tab
         */
        tab?: 'bottles' | 'sets' | 'drops';
    };
    url: '/api/v1/app/catalog/facets';
};

export type AppCatFacetsErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatFacetsError = AppCatFacetsErrors[keyof AppCatFacetsErrors];

export type AppCatFacetsResponses = {
    /**
     * Successful Response
     */
    200: FacetsOut;
};

export type AppCatFacetsResponse = AppCatFacetsResponses[keyof AppCatFacetsResponses];

export type AppCatSearchData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Q
         */
        q?: string | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v1/app/catalog/search';
};

export type AppCatSearchErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatSearchError = AppCatSearchErrors[keyof AppCatSearchErrors];

export type AppCatSearchResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAppSearchOut;
};

export type AppCatSearchResponse = AppCatSearchResponses[keyof AppCatSearchResponses];

export type AppCatWinesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Winery Id
         */
        winery_id?: string | null;
        /**
         * Grape Id
         */
        grape_id?: string | null;
        /**
         * Kind
         */
        kind?: WineKind | null;
        /**
         * Taste
         */
        taste?: 'fresh' | 'body' | 'fruit' | 'oak' | 'aroma' | null;
        /**
         * Sort
         */
        sort?: 'fit' | 'price_asc' | 'price_desc' | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/catalog/wines';
};

export type AppCatWinesListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatWinesListError = AppCatWinesListErrors[keyof AppCatWinesListErrors];

export type AppCatWinesListResponses = {
    /**
     * Successful Response
     */
    200: WinePageOut;
};

export type AppCatWinesListResponse = AppCatWinesListResponses[keyof AppCatWinesListResponses];

export type AppCatWinesGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/app/catalog/wines/{wine_id}';
};

export type AppCatWinesGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatWinesGetError = AppCatWinesGetErrors[keyof AppCatWinesGetErrors];

export type AppCatWinesGetResponses = {
    /**
     * Successful Response
     */
    200: WineDetailOut;
};

export type AppCatWinesGetResponse = AppCatWinesGetResponses[keyof AppCatWinesGetResponses];

export type AppCatWinesSimilarData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: {
        /**
         * Limit
         *
         * Сколько карточек, по умолчанию 6
         */
        limit?: number;
    };
    url: '/api/v1/app/catalog/wines/{wine_id}/similar';
};

export type AppCatWinesSimilarErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatWinesSimilarError = AppCatWinesSimilarErrors[keyof AppCatWinesSimilarErrors];

export type AppCatWinesSimilarResponses = {
    /**
     * Successful Response
     */
    200: WineItemsOut;
};

export type AppCatWinesSimilarResponse = AppCatWinesSimilarResponses[keyof AppCatWinesSimilarResponses];

export type AppCatStockAlertDisableData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/app/catalog/wines/{wine_id}/stock-alert';
};

export type AppCatStockAlertDisableErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatStockAlertDisableError = AppCatStockAlertDisableErrors[keyof AppCatStockAlertDisableErrors];

export type AppCatStockAlertDisableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCatStockAlertDisableResponse = AppCatStockAlertDisableResponses[keyof AppCatStockAlertDisableResponses];

export type AppCatStockAlertEnableData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/app/catalog/wines/{wine_id}/stock-alert';
};

export type AppCatStockAlertEnableErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatStockAlertEnableError = AppCatStockAlertEnableErrors[keyof AppCatStockAlertEnableErrors];

export type AppCatStockAlertEnableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCatStockAlertEnableResponse = AppCatStockAlertEnableResponses[keyof AppCatStockAlertEnableResponses];

export type AppCatSetsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Author Type
         */
        author_type?: SetAuthorType | null;
        /**
         * Format
         */
        format?: SetFormat | null;
        /**
         * Lesson Id
         */
        lesson_id?: string | null;
        /**
         * Course Id
         */
        course_id?: string | null;
        /**
         * Blind Tasting Id
         */
        blind_tasting_id?: string | null;
        /**
         * Tasting Id
         */
        tasting_id?: string | null;
        /**
         * Sort
         */
        sort?: 'price_asc' | 'price_desc' | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/catalog/sets';
};

export type AppCatSetsListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatSetsListError = AppCatSetsListErrors[keyof AppCatSetsListErrors];

export type AppCatSetsListResponses = {
    /**
     * Successful Response
     */
    200: SetPageOut;
};

export type AppCatSetsListResponse = AppCatSetsListResponses[keyof AppCatSetsListResponses];

export type AppCatSetsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/app/catalog/sets/{set_id}';
};

export type AppCatSetsGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatSetsGetError = AppCatSetsGetErrors[keyof AppCatSetsGetErrors];

export type AppCatSetsGetResponses = {
    /**
     * Successful Response
     */
    200: SetDetailOut;
};

export type AppCatSetsGetResponse = AppCatSetsGetResponses[keyof AppCatSetsGetResponses];

export type AppCatSetsSimilarData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: {
        /**
         * Limit
         *
         * Сколько карточек, по умолчанию 6
         */
        limit?: number;
    };
    url: '/api/v1/app/catalog/sets/{set_id}/similar';
};

export type AppCatSetsSimilarErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatSetsSimilarError = AppCatSetsSimilarErrors[keyof AppCatSetsSimilarErrors];

export type AppCatSetsSimilarResponses = {
    /**
     * Successful Response
     */
    200: SetItemsListOut;
};

export type AppCatSetsSimilarResponse = AppCatSetsSimilarResponses[keyof AppCatSetsSimilarResponses];

export type AppCatDropsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * State
         */
        state?: 'live' | 'upcoming' | 'sold' | null;
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/catalog/drops';
};

export type AppCatDropsListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatDropsListError = AppCatDropsListErrors[keyof AppCatDropsListErrors];

export type AppCatDropsListResponses = {
    /**
     * Successful Response
     */
    200: DropPageOut;
};

export type AppCatDropsListResponse = AppCatDropsListResponses[keyof AppCatDropsListResponses];

export type AppCatDropsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/app/catalog/drops/{drop_id}';
};

export type AppCatDropsGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatDropsGetError = AppCatDropsGetErrors[keyof AppCatDropsGetErrors];

export type AppCatDropsGetResponses = {
    /**
     * Successful Response
     */
    200: DropDetailOut;
};

export type AppCatDropsGetResponse = AppCatDropsGetResponses[keyof AppCatDropsGetResponses];

export type AppCatDropReminderDisableData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/app/catalog/drops/{drop_id}/reminder';
};

export type AppCatDropReminderDisableErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatDropReminderDisableError = AppCatDropReminderDisableErrors[keyof AppCatDropReminderDisableErrors];

export type AppCatDropReminderDisableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCatDropReminderDisableResponse = AppCatDropReminderDisableResponses[keyof AppCatDropReminderDisableResponses];

export type AppCatDropReminderEnableData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/app/catalog/drops/{drop_id}/reminder';
};

export type AppCatDropReminderEnableErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatDropReminderEnableError = AppCatDropReminderEnableErrors[keyof AppCatDropReminderEnableErrors];

export type AppCatDropReminderEnableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCatDropReminderEnableResponse = AppCatDropReminderEnableResponses[keyof AppCatDropReminderEnableResponses];

export type AppCatLikesRemoveData = {
    body: LikeIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/catalog/likes';
};

export type AppCatLikesRemoveErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatLikesRemoveError = AppCatLikesRemoveErrors[keyof AppCatLikesRemoveErrors];

export type AppCatLikesRemoveResponses = {
    /**
     * Successful Response
     */
    200: LikeOut;
};

export type AppCatLikesRemoveResponse = AppCatLikesRemoveResponses[keyof AppCatLikesRemoveResponses];

export type AppCatLikesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/catalog/likes';
};

export type AppCatLikesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatLikesListError = AppCatLikesListErrors[keyof AppCatLikesListErrors];

export type AppCatLikesListResponses = {
    /**
     * Successful Response
     */
    200: LikedPageOut;
};

export type AppCatLikesListResponse = AppCatLikesListResponses[keyof AppCatLikesListResponses];

export type AppCatLikesAddData = {
    body: LikeIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/catalog/likes';
};

export type AppCatLikesAddErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatLikesAddError = AppCatLikesAddErrors[keyof AppCatLikesAddErrors];

export type AppCatLikesAddResponses = {
    /**
     * Successful Response
     */
    200: LikeOut;
};

export type AppCatLikesAddResponse = AppCatLikesAddResponses[keyof AppCatLikesAddResponses];

export type AppCatWatchesDisableData = {
    body: WatchIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/catalog/watches';
};

export type AppCatWatchesDisableErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatWatchesDisableError = AppCatWatchesDisableErrors[keyof AppCatWatchesDisableErrors];

export type AppCatWatchesDisableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCatWatchesDisableResponse = AppCatWatchesDisableResponses[keyof AppCatWatchesDisableResponses];

export type AppCatWatchesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/catalog/watches';
};

export type AppCatWatchesListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatWatchesListError = AppCatWatchesListErrors[keyof AppCatWatchesListErrors];

export type AppCatWatchesListResponses = {
    /**
     * Successful Response
     */
    200: WatchListOut;
};

export type AppCatWatchesListResponse = AppCatWatchesListResponses[keyof AppCatWatchesListResponses];

export type AppCatWatchesEnableData = {
    body: WatchIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/catalog/watches';
};

export type AppCatWatchesEnableErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatWatchesEnableError = AppCatWatchesEnableErrors[keyof AppCatWatchesEnableErrors];

export type AppCatWatchesEnableResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCatWatchesEnableResponse = AppCatWatchesEnableResponses[keyof AppCatWatchesEnableResponses];

export type AppCatAvailabilityCheckData = {
    body: AvailabilityIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/catalog/availability-check';
};

export type AppCatAvailabilityCheckErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCatAvailabilityCheckError = AppCatAvailabilityCheckErrors[keyof AppCatAvailabilityCheckErrors];

export type AppCatAvailabilityCheckResponses = {
    /**
     * Successful Response
     */
    200: AvailabilityOut;
};

export type AppCatAvailabilityCheckResponse = AppCatAvailabilityCheckResponses[keyof AppCatAvailabilityCheckResponses];

export type AppExpTastingsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Format
         */
        format?: TastingFormat | null;
        /**
         * Kind
         */
        kind?: TastingKind | null;
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Wine Type
         */
        wine_type?: string | null;
        /**
         * Country Id
         */
        country_id?: string | null;
        /**
         * Status
         */
        status?: string | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/experiences/tastings';
};

export type AppExpTastingsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpTastingsListError = AppExpTastingsListErrors[keyof AppExpTastingsListErrors];

export type AppExpTastingsListResponses = {
    /**
     * Successful Response
     */
    200: CursorPageTastingCard;
};

export type AppExpTastingsListResponse = AppExpTastingsListResponses[keyof AppExpTastingsListResponses];

export type AppExpTastingsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/tastings/{tasting_id}';
};

export type AppExpTastingsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpTastingsGetError = AppExpTastingsGetErrors[keyof AppExpTastingsGetErrors];

export type AppExpTastingsGetResponses = {
    /**
     * Successful Response
     */
    200: TastingDetail;
};

export type AppExpTastingsGetResponse = AppExpTastingsGetResponses[keyof AppExpTastingsGetResponses];

export type AppExpTastingsSimilarData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/tastings/{tasting_id}/similar';
};

export type AppExpTastingsSimilarErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpTastingsSimilarError = AppExpTastingsSimilarErrors[keyof AppExpTastingsSimilarErrors];

export type AppExpTastingsSimilarResponses = {
    /**
     * Successful Response
     */
    200: SimilarOut;
};

export type AppExpTastingsSimilarResponse = AppExpTastingsSimilarResponses[keyof AppExpTastingsSimilarResponses];

export type AppExpWaitlistLeaveData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/tastings/{tasting_id}/waitlist';
};

export type AppExpWaitlistLeaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpWaitlistLeaveError = AppExpWaitlistLeaveErrors[keyof AppExpWaitlistLeaveErrors];

export type AppExpWaitlistLeaveResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppExpWaitlistLeaveResponse = AppExpWaitlistLeaveResponses[keyof AppExpWaitlistLeaveResponses];

export type AppExpWaitlistJoinData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/tastings/{tasting_id}/waitlist';
};

export type AppExpWaitlistJoinErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpWaitlistJoinError = AppExpWaitlistJoinErrors[keyof AppExpWaitlistJoinErrors];

export type AppExpWaitlistJoinResponses = {
    /**
     * Successful Response
     */
    201: BookingOut;
};

export type AppExpWaitlistJoinResponse = AppExpWaitlistJoinResponses[keyof AppExpWaitlistJoinResponses];

export type AppExpExcursionsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Winery Id
         */
        winery_id?: string | null;
        /**
         * Status
         */
        status?: string | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/experiences/excursions';
};

export type AppExpExcursionsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpExcursionsListError = AppExpExcursionsListErrors[keyof AppExpExcursionsListErrors];

export type AppExpExcursionsListResponses = {
    /**
     * Successful Response
     */
    200: CursorPageExcursionCard;
};

export type AppExpExcursionsListResponse = AppExpExcursionsListResponses[keyof AppExpExcursionsListResponses];

export type AppExpExcursionsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/excursions/{excursion_id}';
};

export type AppExpExcursionsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpExcursionsGetError = AppExpExcursionsGetErrors[keyof AppExpExcursionsGetErrors];

export type AppExpExcursionsGetResponses = {
    /**
     * Successful Response
     */
    200: ExcursionDetail;
};

export type AppExpExcursionsGetResponse = AppExpExcursionsGetResponses[keyof AppExpExcursionsGetResponses];

export type AppExpExcursionSlotsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: {
        /**
         * From
         */
        from?: string | null;
        /**
         * To
         */
        to?: string | null;
    };
    url: '/api/v1/app/experiences/excursions/{excursion_id}/slots';
};

export type AppExpExcursionSlotsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpExcursionSlotsError = AppExpExcursionSlotsErrors[keyof AppExpExcursionSlotsErrors];

export type AppExpExcursionSlotsResponses = {
    /**
     * Successful Response
     */
    200: SlotsOut;
};

export type AppExpExcursionSlotsResponse = AppExpExcursionSlotsResponses[keyof AppExpExcursionSlotsResponses];

export type AppExpWineryFormatsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/wineries/{winery_id}/formats';
};

export type AppExpWineryFormatsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpWineryFormatsError = AppExpWineryFormatsErrors[keyof AppExpWineryFormatsErrors];

export type AppExpWineryFormatsResponses = {
    /**
     * Successful Response
     */
    200: FormatsOut;
};

export type AppExpWineryFormatsResponse = AppExpWineryFormatsResponses[keyof AppExpWineryFormatsResponses];

export type AppExpTripsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Status
         */
        status?: string | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/experiences/trips';
};

export type AppExpTripsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpTripsListError = AppExpTripsListErrors[keyof AppExpTripsListErrors];

export type AppExpTripsListResponses = {
    /**
     * Successful Response
     */
    200: CursorPageTripCard;
};

export type AppExpTripsListResponse = AppExpTripsListResponses[keyof AppExpTripsListResponses];

export type AppExpTripsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/trips/{trip_id}';
};

export type AppExpTripsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpTripsGetError = AppExpTripsGetErrors[keyof AppExpTripsGetErrors];

export type AppExpTripsGetResponses = {
    /**
     * Successful Response
     */
    200: TripDetail;
};

export type AppExpTripsGetResponse = AppExpTripsGetResponses[keyof AppExpTripsGetResponses];

export type AppExpTripRequestCreateData = {
    body: TripRequestIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/trips/{trip_id}/requests';
};

export type AppExpTripRequestCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpTripRequestCreateError = AppExpTripRequestCreateErrors[keyof AppExpTripRequestCreateErrors];

export type AppExpTripRequestCreateResponses = {
    /**
     * Successful Response
     */
    201: BookingOut;
};

export type AppExpTripRequestCreateResponse = AppExpTripRequestCreateResponses[keyof AppExpTripRequestCreateResponses];

export type AppExpTripRequestsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Status
         *
         * review — «На согласовании», agreed — «Можно оплатить», paid — «Оплачено»; без фильтра — все активные
         */
        status?: string | null;
    };
    url: '/api/v1/app/trip-requests';
};

export type AppExpTripRequestsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpTripRequestsListError = AppExpTripRequestsListErrors[keyof AppExpTripRequestsListErrors];

export type AppExpTripRequestsListResponses = {
    /**
     * Successful Response
     */
    200: TripRequestsOut;
};

export type AppExpTripRequestsListResponse = AppExpTripRequestsListResponses[keyof AppExpTripRequestsListResponses];

export type AppExpTripRequestsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/app/trip-requests/{trip_id}';
};

export type AppExpTripRequestsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpTripRequestsGetError = AppExpTripRequestsGetErrors[keyof AppExpTripRequestsGetErrors];

export type AppExpTripRequestsGetResponses = {
    /**
     * Successful Response
     */
    200: BookingOut;
};

export type AppExpTripRequestsGetResponse = AppExpTripRequestsGetResponses[keyof AppExpTripRequestsGetResponses];

export type AppExpEventsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * From
         */
        from?: string | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v1/app/experiences/events';
};

export type AppExpEventsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpEventsListError = AppExpEventsListErrors[keyof AppExpEventsListErrors];

export type AppExpEventsListResponses = {
    /**
     * Successful Response
     */
    200: EventsOut;
};

export type AppExpEventsListResponse = AppExpEventsListResponses[keyof AppExpEventsListResponses];

export type AppExpBookingsCreateData = {
    body: BookingCreateIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/experiences/bookings';
};

export type AppExpBookingsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppExpBookingsCreateError = AppExpBookingsCreateErrors[keyof AppExpBookingsCreateErrors];

export type AppExpBookingsCreateResponses = {
    /**
     * Successful Response
     */
    201: BookingCreatedOut;
};

export type AppExpBookingsCreateResponse = AppExpBookingsCreateResponses[keyof AppExpBookingsCreateResponses];

export type AppExpMyData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Kind
         */
        kind?: BookingTarget | null;
        /**
         * Status
         *
         * registered — «Записан», attended — «Участвовал» (только с токеном)
         */
        status?: string | null;
    };
    url: '/api/v1/app/experiences/my';
};

export type AppExpMyErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpMyError = AppExpMyErrors[keyof AppExpMyErrors];

export type AppExpMyResponses = {
    /**
     * Successful Response
     */
    200: MyOut;
};

export type AppExpMyResponse = AppExpMyResponses[keyof AppExpMyResponses];

export type AppExpBookingsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/bookings/{booking_id}';
};

export type AppExpBookingsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpBookingsGetError = AppExpBookingsGetErrors[keyof AppExpBookingsGetErrors];

export type AppExpBookingsGetResponses = {
    /**
     * Successful Response
     */
    200: BookingOut;
};

export type AppExpBookingsGetResponse = AppExpBookingsGetResponses[keyof AppExpBookingsGetResponses];

export type AppExpBookingsUpdateData = {
    body: TripRequestIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/bookings/{booking_id}';
};

export type AppExpBookingsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpBookingsUpdateError = AppExpBookingsUpdateErrors[keyof AppExpBookingsUpdateErrors];

export type AppExpBookingsUpdateResponses = {
    /**
     * Successful Response
     */
    200: BookingOut;
};

export type AppExpBookingsUpdateResponse = AppExpBookingsUpdateResponses[keyof AppExpBookingsUpdateResponses];

export type AppExpBookingsCancelPreviewData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/bookings/{booking_id}/cancel-preview';
};

export type AppExpBookingsCancelPreviewErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpBookingsCancelPreviewError = AppExpBookingsCancelPreviewErrors[keyof AppExpBookingsCancelPreviewErrors];

export type AppExpBookingsCancelPreviewResponses = {
    /**
     * Successful Response
     */
    200: CancelPreviewOut;
};

export type AppExpBookingsCancelPreviewResponse = AppExpBookingsCancelPreviewResponses[keyof AppExpBookingsCancelPreviewResponses];

export type AppExpBookingsCancelData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/bookings/{booking_id}/cancel';
};

export type AppExpBookingsCancelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpBookingsCancelError = AppExpBookingsCancelErrors[keyof AppExpBookingsCancelErrors];

export type AppExpBookingsCancelResponses = {
    /**
     * Successful Response
     */
    200: BookingOut;
};

export type AppExpBookingsCancelResponse = AppExpBookingsCancelResponses[keyof AppExpBookingsCancelResponses];

export type AppExpBookingsPayData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/bookings/{booking_id}/pay';
};

export type AppExpBookingsPayErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppExpBookingsPayError = AppExpBookingsPayErrors[keyof AppExpBookingsPayErrors];

export type AppExpBookingsPayResponses = {
    /**
     * Successful Response
     */
    200: PayOut;
};

export type AppExpBookingsPayResponse = AppExpBookingsPayResponses[keyof AppExpBookingsPayResponses];

export type AppExpBookingsConferenceData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/bookings/{booking_id}/conference';
};

export type AppExpBookingsConferenceErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpBookingsConferenceError = AppExpBookingsConferenceErrors[keyof AppExpBookingsConferenceErrors];

export type AppExpBookingsConferenceResponses = {
    /**
     * Successful Response
     */
    200: ConferenceOut;
};

export type AppExpBookingsConferenceResponse = AppExpBookingsConferenceResponses[keyof AppExpBookingsConferenceResponses];

export type AppExpBookingsCalendarData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/app/experiences/bookings/{booking_id}/calendar.ics';
};

export type AppExpBookingsCalendarErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpBookingsCalendarError = AppExpBookingsCalendarErrors[keyof AppExpBookingsCalendarErrors];

export type AppExpBookingsCalendarResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AppExpAlertsDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query: {
        kind: BookingTarget;
        /**
         * Region Id
         */
        region_id?: string | null;
    };
    url: '/api/v1/app/experiences/alerts';
};

export type AppExpAlertsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpAlertsDeleteError = AppExpAlertsDeleteErrors[keyof AppExpAlertsDeleteErrors];

export type AppExpAlertsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppExpAlertsDeleteResponse = AppExpAlertsDeleteResponses[keyof AppExpAlertsDeleteResponses];

export type AppExpAlertsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/experiences/alerts';
};

export type AppExpAlertsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpAlertsListError = AppExpAlertsListErrors[keyof AppExpAlertsListErrors];

export type AppExpAlertsListResponses = {
    /**
     * Successful Response
     */
    200: AlertsOut;
};

export type AppExpAlertsListResponse = AppExpAlertsListResponses[keyof AppExpAlertsListResponses];

export type AppExpAlertsCreateData = {
    body: AlertIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/experiences/alerts';
};

export type AppExpAlertsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppExpAlertsCreateError = AppExpAlertsCreateErrors[keyof AppExpAlertsCreateErrors];

export type AppExpAlertsCreateResponses = {
    /**
     * Successful Response
     */
    201: unknown;
};

export type AppLrnPathData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/path';
};

export type AppLrnPathErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnPathError = AppLrnPathErrors[keyof AppLrnPathErrors];

export type AppLrnPathResponses = {
    /**
     * Successful Response
     */
    200: PathOut;
};

export type AppLrnPathResponse = AppLrnPathResponses[keyof AppLrnPathResponses];

export type AppLrnPathUpdatesSeenData = {
    body: UpdatesSeenIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/path/updates-seen';
};

export type AppLrnPathUpdatesSeenErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnPathUpdatesSeenError = AppLrnPathUpdatesSeenErrors[keyof AppLrnPathUpdatesSeenErrors];

export type AppLrnPathUpdatesSeenResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppLrnPathUpdatesSeenResponse = AppLrnPathUpdatesSeenResponses[keyof AppLrnPathUpdatesSeenResponses];

export type AppLrnPathCountryData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Code
         */
        code: string;
    };
    query?: never;
    url: '/api/v1/app/path/countries/{code}';
};

export type AppLrnPathCountryErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnPathCountryError = AppLrnPathCountryErrors[keyof AppLrnPathCountryErrors];

export type AppLrnPathCountryResponses = {
    /**
     * Successful Response
     */
    200: CountryScreenOut;
};

export type AppLrnPathCountryResponse = AppLrnPathCountryResponses[keyof AppLrnPathCountryResponses];

export type AppLrnPathRegionSummaryData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/app/path/regions/{region_id}/summary';
};

export type AppLrnPathRegionSummaryErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnPathRegionSummaryError = AppLrnPathRegionSummaryErrors[keyof AppLrnPathRegionSummaryErrors];

export type AppLrnPathRegionSummaryResponses = {
    /**
     * Successful Response
     */
    200: RegionSummaryOut;
};

export type AppLrnPathRegionSummaryResponse = AppLrnPathRegionSummaryResponses[keyof AppLrnPathRegionSummaryResponses];

export type AppLrnPathRegionRouteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/app/path/regions/{region_id}/route';
};

export type AppLrnPathRegionRouteErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnPathRegionRouteError = AppLrnPathRegionRouteErrors[keyof AppLrnPathRegionRouteErrors];

export type AppLrnPathRegionRouteResponses = {
    /**
     * Successful Response
     */
    200: RouteOut;
};

export type AppLrnPathRegionRouteResponse = AppLrnPathRegionRouteResponses[keyof AppLrnPathRegionRouteResponses];

export type AppLrnCoursesIntroData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/courses/intro';
};

export type AppLrnCoursesIntroErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnCoursesIntroError = AppLrnCoursesIntroErrors[keyof AppLrnCoursesIntroErrors];

export type AppLrnCoursesIntroResponses = {
    /**
     * Successful Response
     */
    200: IntroCourseOut;
};

export type AppLrnCoursesIntroResponse = AppLrnCoursesIntroResponses[keyof AppLrnCoursesIntroResponses];

export type AppLrnCoursesGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/app/courses/{course_id}';
};

export type AppLrnCoursesGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnCoursesGetError = AppLrnCoursesGetErrors[keyof AppLrnCoursesGetErrors];

export type AppLrnCoursesGetResponses = {
    /**
     * Successful Response
     */
    200: CourseScreenOut;
};

export type AppLrnCoursesGetResponse = AppLrnCoursesGetResponses[keyof AppLrnCoursesGetResponses];

export type AppLrnCoursesLikeDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/app/courses/{course_id}/reactions/like';
};

export type AppLrnCoursesLikeDeleteErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnCoursesLikeDeleteError = AppLrnCoursesLikeDeleteErrors[keyof AppLrnCoursesLikeDeleteErrors];

export type AppLrnCoursesLikeDeleteResponses = {
    /**
     * Successful Response
     */
    200: CourseLikeOut;
};

export type AppLrnCoursesLikeDeleteResponse = AppLrnCoursesLikeDeleteResponses[keyof AppLrnCoursesLikeDeleteResponses];

export type AppLrnCoursesLikeSetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/app/courses/{course_id}/reactions/like';
};

export type AppLrnCoursesLikeSetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnCoursesLikeSetError = AppLrnCoursesLikeSetErrors[keyof AppLrnCoursesLikeSetErrors];

export type AppLrnCoursesLikeSetResponses = {
    /**
     * Successful Response
     */
    200: CourseLikeOut;
};

export type AppLrnCoursesLikeSetResponse = AppLrnCoursesLikeSetResponses[keyof AppLrnCoursesLikeSetResponses];

export type AppLrnRegionsCourseData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/app/regions/{region_id}/course';
};

export type AppLrnRegionsCourseErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnRegionsCourseError = AppLrnRegionsCourseErrors[keyof AppLrnRegionsCourseErrors];

export type AppLrnRegionsCourseResponses = {
    /**
     * Successful Response
     */
    200: RegionCourseOut;
};

export type AppLrnRegionsCourseResponse = AppLrnRegionsCourseResponses[keyof AppLrnRegionsCourseResponses];

export type AppLrnLessonsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: {
        /**
         * Course Id
         *
         * Курс-контекст
         */
        course_id?: string | null;
    };
    url: '/api/v1/app/lessons/{lesson_id}';
};

export type AppLrnLessonsGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnLessonsGetError = AppLrnLessonsGetErrors[keyof AppLrnLessonsGetErrors];

export type AppLrnLessonsGetResponses = {
    /**
     * Successful Response
     */
    200: LessonScreenOut;
};

export type AppLrnLessonsGetResponse = AppLrnLessonsGetResponses[keyof AppLrnLessonsGetResponses];

export type AppLrnLessonsCompleteData = {
    body: CompleteIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/app/lessons/{lesson_id}/complete';
};

export type AppLrnLessonsCompleteErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnLessonsCompleteError = AppLrnLessonsCompleteErrors[keyof AppLrnLessonsCompleteErrors];

export type AppLrnLessonsCompleteResponses = {
    /**
     * Successful Response
     */
    200: CompleteOut;
};

export type AppLrnLessonsCompleteResponse = AppLrnLessonsCompleteResponses[keyof AppLrnLessonsCompleteResponses];

export type AppLrnLessonsPlaybackData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/app/lessons/{lesson_id}/playback';
};

export type AppLrnLessonsPlaybackErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppLrnLessonsPlaybackError = AppLrnLessonsPlaybackErrors[keyof AppLrnLessonsPlaybackErrors];

export type AppLrnLessonsPlaybackResponses = {
    /**
     * Successful Response
     */
    200: PlaybackOut;
};

export type AppLrnLessonsPlaybackResponse = AppLrnLessonsPlaybackResponses[keyof AppLrnLessonsPlaybackResponses];

export type AppLrnLessonsVideoProgressData = {
    body: VideoProgressIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/app/lessons/{lesson_id}/video-progress';
};

export type AppLrnLessonsVideoProgressErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnLessonsVideoProgressError = AppLrnLessonsVideoProgressErrors[keyof AppLrnLessonsVideoProgressErrors];

export type AppLrnLessonsVideoProgressResponses = {
    /**
     * Successful Response
     */
    200: VideoProgressOut;
};

export type AppLrnLessonsVideoProgressResponse = AppLrnLessonsVideoProgressResponses[keyof AppLrnLessonsVideoProgressResponses];

export type AppLrnLessonsReactionDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
        kind: ReactionKind;
    };
    query?: never;
    url: '/api/v1/app/lessons/{lesson_id}/reactions/{kind}';
};

export type AppLrnLessonsReactionDeleteErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnLessonsReactionDeleteError = AppLrnLessonsReactionDeleteErrors[keyof AppLrnLessonsReactionDeleteErrors];

export type AppLrnLessonsReactionDeleteResponses = {
    /**
     * Successful Response
     */
    200: ReactionsOut;
};

export type AppLrnLessonsReactionDeleteResponse = AppLrnLessonsReactionDeleteResponses[keyof AppLrnLessonsReactionDeleteResponses];

export type AppLrnLessonsReactionSetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
        kind: ReactionKind;
    };
    query?: never;
    url: '/api/v1/app/lessons/{lesson_id}/reactions/{kind}';
};

export type AppLrnLessonsReactionSetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnLessonsReactionSetError = AppLrnLessonsReactionSetErrors[keyof AppLrnLessonsReactionSetErrors];

export type AppLrnLessonsReactionSetResponses = {
    /**
     * Successful Response
     */
    200: ReactionsOut;
};

export type AppLrnLessonsReactionSetResponse = AppLrnLessonsReactionSetResponses[keyof AppLrnLessonsReactionSetResponses];

export type AppLrnTestsAttemptsCreateData = {
    body: AttemptStartIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/app/tests/{test_id}/attempts';
};

export type AppLrnTestsAttemptsCreateErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnTestsAttemptsCreateError = AppLrnTestsAttemptsCreateErrors[keyof AppLrnTestsAttemptsCreateErrors];

export type AppLrnTestsAttemptsCreateResponses = {
    /**
     * Successful Response
     */
    201: AttemptStartOut;
};

export type AppLrnTestsAttemptsCreateResponse = AppLrnTestsAttemptsCreateResponses[keyof AppLrnTestsAttemptsCreateResponses];

export type AppLrnLevelCheckAttemptsCreateData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/level-check/attempts';
};

export type AppLrnLevelCheckAttemptsCreateErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnLevelCheckAttemptsCreateError = AppLrnLevelCheckAttemptsCreateErrors[keyof AppLrnLevelCheckAttemptsCreateErrors];

export type AppLrnLevelCheckAttemptsCreateResponses = {
    /**
     * Successful Response
     */
    201: AttemptStartOut;
};

export type AppLrnLevelCheckAttemptsCreateResponse = AppLrnLevelCheckAttemptsCreateResponses[keyof AppLrnLevelCheckAttemptsCreateResponses];

export type AppLrnLevelCheckGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/level-check';
};

export type AppLrnLevelCheckGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnLevelCheckGetError = AppLrnLevelCheckGetErrors[keyof AppLrnLevelCheckGetErrors];

export type AppLrnLevelCheckGetResponses = {
    /**
     * Successful Response
     */
    200: LevelCheckResultOut;
};

export type AppLrnLevelCheckGetResponse = AppLrnLevelCheckGetResponses[keyof AppLrnLevelCheckGetResponses];

export type AppLrnTestAttemptsAnswerData = {
    body: AnswerIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Attempt Id
         */
        attempt_id: string;
    };
    query?: never;
    url: '/api/v1/app/test-attempts/{attempt_id}/answers';
};

export type AppLrnTestAttemptsAnswerErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnTestAttemptsAnswerError = AppLrnTestAttemptsAnswerErrors[keyof AppLrnTestAttemptsAnswerErrors];

export type AppLrnTestAttemptsAnswerResponses = {
    /**
     * Successful Response
     */
    200: AnswerOut;
};

export type AppLrnTestAttemptsAnswerResponse = AppLrnTestAttemptsAnswerResponses[keyof AppLrnTestAttemptsAnswerResponses];

export type AppLrnTestAttemptsFinishData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Attempt Id
         */
        attempt_id: string;
    };
    query?: never;
    url: '/api/v1/app/test-attempts/{attempt_id}/finish';
};

export type AppLrnTestAttemptsFinishErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnTestAttemptsFinishError = AppLrnTestAttemptsFinishErrors[keyof AppLrnTestAttemptsFinishErrors];

export type AppLrnTestAttemptsFinishResponses = {
    /**
     * Successful Response
     */
    200: FinishOut;
};

export type AppLrnTestAttemptsFinishResponse = AppLrnTestAttemptsFinishResponses[keyof AppLrnTestAttemptsFinishResponses];

export type AppLrnOffersData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query: {
        /**
         * Items
         *
         * course:{id},lesson:{id}
         */
        items: string;
    };
    url: '/api/v1/app/learning/offers';
};

export type AppLrnOffersErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnOffersError = AppLrnOffersErrors[keyof AppLrnOffersErrors];

export type AppLrnOffersResponses = {
    /**
     * Successful Response
     */
    200: LearnOffersOut;
};

export type AppLrnOffersResponse = AppLrnOffersResponses[keyof AppLrnOffersResponses];

export type AppLrnEntitlementsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/learning/entitlements';
};

export type AppLrnEntitlementsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnEntitlementsListError = AppLrnEntitlementsListErrors[keyof AppLrnEntitlementsListErrors];

export type AppLrnEntitlementsListResponses = {
    /**
     * Successful Response
     */
    200: EntitlementPageOut;
};

export type AppLrnEntitlementsListResponse = AppLrnEntitlementsListResponses[keyof AppLrnEntitlementsListResponses];

export type AppLrnSavedLessonsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/learning/saved-lessons';
};

export type AppLrnSavedLessonsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppLrnSavedLessonsListError = AppLrnSavedLessonsListErrors[keyof AppLrnSavedLessonsListErrors];

export type AppLrnSavedLessonsListResponses = {
    /**
     * Successful Response
     */
    200: SavedLessonPageOut;
};

export type AppLrnSavedLessonsListResponse = AppLrnSavedLessonsListResponses[keyof AppLrnSavedLessonsListResponses];

export type AppTstTasteProfileGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/taste/profile';
};

export type AppTstTasteProfileGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstTasteProfileGetError = AppTstTasteProfileGetErrors[keyof AppTstTasteProfileGetErrors];

export type AppTstTasteProfileGetResponses = {
    /**
     * Successful Response
     */
    200: TstTasteProfileOut;
};

export type AppTstTasteProfileGetResponse = AppTstTasteProfileGetResponses[keyof AppTstTasteProfileGetResponses];

export type AppTstDiaryWinesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Type
         */
        type?: WineType | null;
        /**
         * Sort
         */
        sort?: 'new' | 'high' | 'low';
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/diary/wines';
};

export type AppTstDiaryWinesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstDiaryWinesListError = AppTstDiaryWinesListErrors[keyof AppTstDiaryWinesListErrors];

export type AppTstDiaryWinesListResponses = {
    /**
     * Successful Response
     */
    200: TstDiaryListOut;
};

export type AppTstDiaryWinesListResponse = AppTstDiaryWinesListResponses[keyof AppTstDiaryWinesListResponses];

export type AppTstDiaryWinesCreateData = {
    body: TstDiaryWineCreateIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/diary/wines';
};

export type AppTstDiaryWinesCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstDiaryWinesCreateError = AppTstDiaryWinesCreateErrors[keyof AppTstDiaryWinesCreateErrors];

export type AppTstDiaryWinesCreateResponses = {
    /**
     * Successful Response
     */
    201: TstDiarySavedOut;
};

export type AppTstDiaryWinesCreateResponse = AppTstDiaryWinesCreateResponses[keyof AppTstDiaryWinesCreateResponses];

export type AppTstDiaryPurchasedWinesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/diary/purchased-wines';
};

export type AppTstDiaryPurchasedWinesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstDiaryPurchasedWinesListError = AppTstDiaryPurchasedWinesListErrors[keyof AppTstDiaryPurchasedWinesListErrors];

export type AppTstDiaryPurchasedWinesListResponses = {
    /**
     * Successful Response
     */
    200: CursorPageTstPurchasedWineOut;
};

export type AppTstDiaryPurchasedWinesListResponse = AppTstDiaryPurchasedWinesListResponses[keyof AppTstDiaryPurchasedWinesListResponses];

export type AppTstDiaryWinesGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Diary Wine Id
         */
        diary_wine_id: string;
    };
    query?: never;
    url: '/api/v1/app/diary/wines/{diary_wine_id}';
};

export type AppTstDiaryWinesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstDiaryWinesGetError = AppTstDiaryWinesGetErrors[keyof AppTstDiaryWinesGetErrors];

export type AppTstDiaryWinesGetResponses = {
    /**
     * Successful Response
     */
    200: TstDiaryWineDetailOut;
};

export type AppTstDiaryWinesGetResponse = AppTstDiaryWinesGetResponses[keyof AppTstDiaryWinesGetResponses];

export type AppTstDiaryWinesUpdateData = {
    body: TstDiaryWinePatchIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Diary Wine Id
         */
        diary_wine_id: string;
    };
    query?: never;
    url: '/api/v1/app/diary/wines/{diary_wine_id}';
};

export type AppTstDiaryWinesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstDiaryWinesUpdateError = AppTstDiaryWinesUpdateErrors[keyof AppTstDiaryWinesUpdateErrors];

export type AppTstDiaryWinesUpdateResponses = {
    /**
     * Successful Response
     */
    200: TstDiaryWineDetailOut;
};

export type AppTstDiaryWinesUpdateResponse = AppTstDiaryWinesUpdateResponses[keyof AppTstDiaryWinesUpdateResponses];

export type AppTstDiaryTastingsCreateData = {
    body: TstTastingIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Diary Wine Id
         */
        diary_wine_id: string;
    };
    query?: never;
    url: '/api/v1/app/diary/wines/{diary_wine_id}/tastings';
};

export type AppTstDiaryTastingsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstDiaryTastingsCreateError = AppTstDiaryTastingsCreateErrors[keyof AppTstDiaryTastingsCreateErrors];

export type AppTstDiaryTastingsCreateResponses = {
    /**
     * Successful Response
     */
    201: TstDiarySavedOut;
};

export type AppTstDiaryTastingsCreateResponse = AppTstDiaryTastingsCreateResponses[keyof AppTstDiaryTastingsCreateResponses];

export type AppTstCalibrationsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Region Id
         */
        region_id?: string | null;
    };
    url: '/api/v1/app/calibrations';
};

export type AppTstCalibrationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstCalibrationsListError = AppTstCalibrationsListErrors[keyof AppTstCalibrationsListErrors];

export type AppTstCalibrationsListResponses = {
    /**
     * Successful Response
     */
    200: TstCalibrationListOut;
};

export type AppTstCalibrationsListResponse = AppTstCalibrationsListResponses[keyof AppTstCalibrationsListResponses];

export type AppTstCalibrationsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/app/calibrations/{calibration_id}';
};

export type AppTstCalibrationsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstCalibrationsGetError = AppTstCalibrationsGetErrors[keyof AppTstCalibrationsGetErrors];

export type AppTstCalibrationsGetResponses = {
    /**
     * Successful Response
     */
    200: TstCalibrationFormOut;
};

export type AppTstCalibrationsGetResponse = AppTstCalibrationsGetResponses[keyof AppTstCalibrationsGetResponses];

export type AppTstCalibrationResultsCreateData = {
    body: {
        /**
         * Фото вина
         */
        photo: Blob | File;
        /**
         * JSON: {"sweetness":1,"acidity":3,"body":3,"tannin":4,"aroma_intensity":4,"finish":5}
         */
        answers: string;
    };
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/app/calibrations/{calibration_id}/results';
};

export type AppTstCalibrationResultsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstCalibrationResultsCreateError = AppTstCalibrationResultsCreateErrors[keyof AppTstCalibrationResultsCreateErrors];

export type AppTstCalibrationResultsCreateResponses = {
    /**
     * Successful Response
     */
    201: TstCalibrationResultOut;
};

export type AppTstCalibrationResultsCreateResponse = AppTstCalibrationResultsCreateResponses[keyof AppTstCalibrationResultsCreateResponses];

export type AppTstCalibrationResultGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/app/calibrations/{calibration_id}/result';
};

export type AppTstCalibrationResultGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstCalibrationResultGetError = AppTstCalibrationResultGetErrors[keyof AppTstCalibrationResultGetErrors];

export type AppTstCalibrationResultGetResponses = {
    /**
     * Successful Response
     */
    200: TstCalibrationResultOut;
};

export type AppTstCalibrationResultGetResponse = AppTstCalibrationResultGetResponses[keyof AppTstCalibrationResultGetResponses];

export type AppTstBlindFormGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/app/blind-tastings/{tasting_id}/form';
};

export type AppTstBlindFormGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstBlindFormGetError = AppTstBlindFormGetErrors[keyof AppTstBlindFormGetErrors];

export type AppTstBlindFormGetResponses = {
    /**
     * Successful Response
     */
    200: TstBlindFormOut;
};

export type AppTstBlindFormGetResponse = AppTstBlindFormGetResponses[keyof AppTstBlindFormGetResponses];

export type AppTstBlindAnswersCreateData = {
    body: TstBlindAnswerIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
        /**
         * Position
         */
        position: number;
    };
    query?: never;
    url: '/api/v1/app/blind-tastings/{tasting_id}/wines/{position}/answers';
};

export type AppTstBlindAnswersCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstBlindAnswersCreateError = AppTstBlindAnswersCreateErrors[keyof AppTstBlindAnswersCreateErrors];

export type AppTstBlindAnswersCreateResponses = {
    /**
     * Successful Response
     */
    201: TstBlindRevealOut;
};

export type AppTstBlindAnswersCreateResponse = AppTstBlindAnswersCreateResponses[keyof AppTstBlindAnswersCreateResponses];

export type AppTstBlindResultGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/app/blind-tastings/{tasting_id}/result';
};

export type AppTstBlindResultGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstBlindResultGetError = AppTstBlindResultGetErrors[keyof AppTstBlindResultGetErrors];

export type AppTstBlindResultGetResponses = {
    /**
     * Successful Response
     */
    200: TstBlindResultOut;
};

export type AppTstBlindResultGetResponse = AppTstBlindResultGetResponses[keyof AppTstBlindResultGetResponses];

export type AppTstAccuracySummaryGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/accuracy/summary';
};

export type AppTstAccuracySummaryGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstAccuracySummaryGetError = AppTstAccuracySummaryGetErrors[keyof AppTstAccuracySummaryGetErrors];

export type AppTstAccuracySummaryGetResponses = {
    /**
     * Successful Response
     */
    200: TstAccuracySummaryOut;
};

export type AppTstAccuracySummaryGetResponse = AppTstAccuracySummaryGetResponses[keyof AppTstAccuracySummaryGetResponses];

export type AppTstAccuracyResultsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Kind
         */
        kind?: 'all' | 'calibration' | 'blind';
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/accuracy/results';
};

export type AppTstAccuracyResultsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstAccuracyResultsListError = AppTstAccuracyResultsListErrors[keyof AppTstAccuracyResultsListErrors];

export type AppTstAccuracyResultsListResponses = {
    /**
     * Successful Response
     */
    200: TstMyResultsOut;
};

export type AppTstAccuracyResultsListResponse = AppTstAccuracyResultsListResponses[keyof AppTstAccuracyResultsListResponses];

export type AppTstSpecializationsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Awarded
         */
        awarded?: boolean;
    };
    url: '/api/v1/app/specializations';
};

export type AppTstSpecializationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstSpecializationsListError = AppTstSpecializationsListErrors[keyof AppTstSpecializationsListErrors];

export type AppTstSpecializationsListResponses = {
    /**
     * Successful Response
     */
    200: TstSpecListOut;
};

export type AppTstSpecializationsListResponse = AppTstSpecializationsListResponses[keyof AppTstSpecializationsListResponses];

export type AppTstSpecializationsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Specialization Id
         */
        specialization_id: string;
    };
    query?: never;
    url: '/api/v1/app/specializations/{specialization_id}';
};

export type AppTstSpecializationsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstSpecializationsGetError = AppTstSpecializationsGetErrors[keyof AppTstSpecializationsGetErrors];

export type AppTstSpecializationsGetResponses = {
    /**
     * Successful Response
     */
    200: TstSpecDetailOut;
};

export type AppTstSpecializationsGetResponse = AppTstSpecializationsGetResponses[keyof AppTstSpecializationsGetResponses];

export type AppTstRecognitionLabelsCreateData = {
    body: {
        /**
         * Фото этикетки
         */
        image: Blob | File;
    };
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/recognition/labels';
};

export type AppTstRecognitionLabelsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppTstRecognitionLabelsCreateError = AppTstRecognitionLabelsCreateErrors[keyof AppTstRecognitionLabelsCreateErrors];

export type AppTstRecognitionLabelsCreateResponses = {
    /**
     * Successful Response
     */
    200: TstRecognitionOut;
};

export type AppTstRecognitionLabelsCreateResponse = AppTstRecognitionLabelsCreateResponses[keyof AppTstRecognitionLabelsCreateResponses];

export type AppTstRecognitionLabelsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Recognition Id
         */
        recognition_id: string;
    };
    query?: never;
    url: '/api/v1/app/recognition/labels/{recognition_id}';
};

export type AppTstRecognitionLabelsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstRecognitionLabelsGetError = AppTstRecognitionLabelsGetErrors[keyof AppTstRecognitionLabelsGetErrors];

export type AppTstRecognitionLabelsGetResponses = {
    /**
     * Successful Response
     */
    200: TstRecognitionOut;
};

export type AppTstRecognitionLabelsGetResponse = AppTstRecognitionLabelsGetResponses[keyof AppTstRecognitionLabelsGetResponses];

export type AppTstRecognitionFeedbackCreateData = {
    body: TstRecognitionFeedbackIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Recognition Id
         */
        recognition_id: string;
    };
    query?: never;
    url: '/api/v1/app/recognition/labels/{recognition_id}/feedback';
};

export type AppTstRecognitionFeedbackCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppTstRecognitionFeedbackCreateError = AppTstRecognitionFeedbackCreateErrors[keyof AppTstRecognitionFeedbackCreateErrors];

export type AppTstRecognitionFeedbackCreateResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppTstRecognitionFeedbackCreateResponse = AppTstRecognitionFeedbackCreateResponses[keyof AppTstRecognitionFeedbackCreateResponses];

export type AppGamConfigData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/config';
};

export type AppGamConfigErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamConfigError = AppGamConfigErrors[keyof AppGamConfigErrors];

export type AppGamConfigResponses = {
    /**
     * Successful Response
     */
    200: GamConfigOut;
};

export type AppGamConfigResponse = AppGamConfigResponses[keyof AppGamConfigResponses];

export type AppGamMeData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/me';
};

export type AppGamMeErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamMeError = AppGamMeErrors[keyof AppGamMeErrors];

export type AppGamMeResponses = {
    /**
     * Successful Response
     */
    200: GamMeOut;
};

export type AppGamMeResponse = AppGamMeResponses[keyof AppGamMeResponses];

export type AppGamMeUpdateData = {
    body: GamMePatchIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/me';
};

export type AppGamMeUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamMeUpdateError = AppGamMeUpdateErrors[keyof AppGamMeUpdateErrors];

export type AppGamMeUpdateResponses = {
    /**
     * Successful Response
     */
    200: GamMeOut;
};

export type AppGamMeUpdateResponse = AppGamMeUpdateResponses[keyof AppGamMeUpdateResponses];

export type AppGamLedgerData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Cursor
         */
        cursor?: string | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v1/app/gam/ledger';
};

export type AppGamLedgerErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamLedgerError = AppGamLedgerErrors[keyof AppGamLedgerErrors];

export type AppGamLedgerResponses = {
    /**
     * Successful Response
     */
    200: LedgerPageOut;
};

export type AppGamLedgerResponse = AppGamLedgerResponses[keyof AppGamLedgerResponses];

export type AppGamRewardsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/rewards';
};

export type AppGamRewardsErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamRewardsError = AppGamRewardsErrors[keyof AppGamRewardsErrors];

export type AppGamRewardsResponses = {
    /**
     * Successful Response
     */
    200: RewardsOut;
};

export type AppGamRewardsResponse = AppGamRewardsResponses[keyof AppGamRewardsResponses];

export type AppGamExchangesData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/gam/exchanges';
};

export type AppGamExchangesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamExchangesError = AppGamExchangesErrors[keyof AppGamExchangesErrors];

export type AppGamExchangesResponses = {
    /**
     * Successful Response
     */
    200: GamExchangesPageOut;
};

export type AppGamExchangesResponse = AppGamExchangesResponses[keyof AppGamExchangesResponses];

export type AppGamExchangesCreateData = {
    body: ExchangeCreateIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/exchanges';
};

export type AppGamExchangesCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamExchangesCreateError = AppGamExchangesCreateErrors[keyof AppGamExchangesCreateErrors];

export type AppGamExchangesCreateResponses = {
    /**
     * Successful Response
     */
    201: ExchangeCreatedOut;
};

export type AppGamExchangesCreateResponse = AppGamExchangesCreateResponses[keyof AppGamExchangesCreateResponses];

export type AppGamPassportData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/passport';
};

export type AppGamPassportErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamPassportError = AppGamPassportErrors[keyof AppGamPassportErrors];

export type AppGamPassportResponses = {
    /**
     * Successful Response
     */
    200: PassportOut;
};

export type AppGamPassportResponse = AppGamPassportResponses[keyof AppGamPassportResponses];

export type AppGamStampsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/stamps';
};

export type AppGamStampsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamStampsError = AppGamStampsErrors[keyof AppGamStampsErrors];

export type AppGamStampsResponses = {
    /**
     * Successful Response
     */
    200: StampsOut;
};

export type AppGamStampsResponse = AppGamStampsResponses[keyof AppGamStampsResponses];

export type AppGamRegionsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Country
         */
        country?: string | null;
    };
    url: '/api/v1/app/gam/regions';
};

export type AppGamRegionsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamRegionsError = AppGamRegionsErrors[keyof AppGamRegionsErrors];

export type AppGamRegionsResponses = {
    /**
     * Successful Response
     */
    200: RegionsOut;
};

export type AppGamRegionsResponse = AppGamRegionsResponses[keyof AppGamRegionsResponses];

export type AppGamRegionData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/app/gam/regions/{region_id}';
};

export type AppGamRegionErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamRegionError = AppGamRegionErrors[keyof AppGamRegionErrors];

export type AppGamRegionResponses = {
    /**
     * Successful Response
     */
    200: RegionProgressOut;
};

export type AppGamRegionResponse = AppGamRegionResponses[keyof AppGamRegionResponses];

export type AppGamWorldData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/world';
};

export type AppGamWorldErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamWorldError = AppGamWorldErrors[keyof AppGamWorldErrors];

export type AppGamWorldResponses = {
    /**
     * Successful Response
     */
    200: WorldOut;
};

export type AppGamWorldResponse = AppGamWorldResponses[keyof AppGamWorldResponses];

export type AppGamAchievementsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/gam/achievements';
};

export type AppGamAchievementsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamAchievementsError = AppGamAchievementsErrors[keyof AppGamAchievementsErrors];

export type AppGamAchievementsResponses = {
    /**
     * Successful Response
     */
    200: AchievementsOut;
};

export type AppGamAchievementsResponse = AppGamAchievementsResponses[keyof AppGamAchievementsResponses];

export type AppGamHistoryData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Year
         */
        year?: number | null;
        /**
         * Month
         */
        month?: number | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/gam/history';
};

export type AppGamHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppGamHistoryError = AppGamHistoryErrors[keyof AppGamHistoryErrors];

export type AppGamHistoryResponses = {
    /**
     * Successful Response
     */
    200: GamHistoryPageOut;
};

export type AppGamHistoryResponse = AppGamHistoryResponses[keyof AppGamHistoryResponses];

export type AppCntFeedData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Cursor
         *
         * Курсор
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–50
         */
        limit?: number;
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
    };
    url: '/api/v1/app/feed';
};

export type AppCntFeedErrors = {
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntFeedError = AppCntFeedErrors[keyof AppCntFeedErrors];

export type AppCntFeedResponses = {
    /**
     * Successful Response
     */
    200: FeedOut;
};

export type AppCntFeedResponse = AppCntFeedResponses[keyof AppCntFeedResponses];

export type AppCntStoriesViewsData = {
    body: StoryViewsIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/stories/views';
};

export type AppCntStoriesViewsErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStoriesViewsError = AppCntStoriesViewsErrors[keyof AppCntStoriesViewsErrors];

export type AppCntStoriesViewsResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCntStoriesViewsResponse = AppCntStoriesViewsResponses[keyof AppCntStoriesViewsResponses];

export type AppCntStoryLinkTapData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Story Id
         */
        story_id: string;
    };
    query?: never;
    url: '/api/v1/app/stories/{story_id}/link-tap';
};

export type AppCntStoryLinkTapErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStoryLinkTapError = AppCntStoryLinkTapErrors[keyof AppCntStoryLinkTapErrors];

export type AppCntStoryLinkTapResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCntStoryLinkTapResponse = AppCntStoryLinkTapResponses[keyof AppCntStoryLinkTapResponses];

export type AppCntAuthorStoriesData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/app/authors/{member_id}/stories';
};

export type AppCntAuthorStoriesErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntAuthorStoriesError = AppCntAuthorStoriesErrors[keyof AppCntAuthorStoriesErrors];

export type AppCntAuthorStoriesResponses = {
    /**
     * Successful Response
     */
    200: AuthorStoriesOut;
};

export type AppCntAuthorStoriesResponse = AppCntAuthorStoriesResponses[keyof AppCntAuthorStoriesResponses];

export type AppCntPeopleData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Tab
         *
         * experts | makers
         */
        tab?: 'experts' | 'makers';
        /**
         * Region Id
         *
         * Фильтр по региону
         */
        region_id?: string | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/people';
};

export type AppCntPeopleErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntPeopleError = AppCntPeopleErrors[keyof AppCntPeopleErrors];

export type AppCntPeopleResponses = {
    /**
     * Successful Response
     */
    200: PeopleOut;
};

export type AppCntPeopleResponse = AppCntPeopleResponses[keyof AppCntPeopleResponses];

export type AppCntExpertPageData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/app/experts/{member_id}';
};

export type AppCntExpertPageErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntExpertPageError = AppCntExpertPageErrors[keyof AppCntExpertPageErrors];

export type AppCntExpertPageResponses = {
    /**
     * Successful Response
     */
    200: AuthorPageOut;
};

export type AppCntExpertPageResponse = AppCntExpertPageResponses[keyof AppCntExpertPageResponses];

export type AppCntMakerPageData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: {
        /**
         * Lat
         *
         * Широта точки пользователя (2 знака)
         */
        lat?: number | null;
        /**
         * Lng
         *
         * Долгота точки пользователя (2 знака)
         */
        lng?: number | null;
    };
    url: '/api/v1/app/makers/{member_id}';
};

export type AppCntMakerPageErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntMakerPageError = AppCntMakerPageErrors[keyof AppCntMakerPageErrors];

export type AppCntMakerPageResponses = {
    /**
     * Successful Response
     */
    200: AuthorPageOut;
};

export type AppCntMakerPageResponse = AppCntMakerPageResponses[keyof AppCntMakerPageResponses];

export type AppCntUnfollowData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/app/follows/{member_id}';
};

export type AppCntUnfollowErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntUnfollowError = AppCntUnfollowErrors[keyof AppCntUnfollowErrors];

export type AppCntUnfollowResponses = {
    /**
     * Successful Response
     */
    200: FollowStateOut;
};

export type AppCntUnfollowResponse = AppCntUnfollowResponses[keyof AppCntUnfollowResponses];

export type AppCntFollowData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/app/follows/{member_id}';
};

export type AppCntFollowErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntFollowError = AppCntFollowErrors[keyof AppCntFollowErrors];

export type AppCntFollowResponses = {
    /**
     * Successful Response
     */
    200: FollowStateOut;
};

export type AppCntFollowResponse = AppCntFollowResponses[keyof AppCntFollowResponses];

export type AppCntPublicationsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query: {
        /**
         * Scope
         *
         * Список: people | region | winery | author | wine | experience
         */
        scope: 'people' | 'region' | 'winery' | 'author' | 'wine' | 'experience';
        /**
         * Region Id
         */
        region_id?: string | null;
        /**
         * Winery Id
         */
        winery_id?: string | null;
        /**
         * Author Member Id
         */
        author_member_id?: string | null;
        /**
         * Wine Id
         */
        wine_id?: string | null;
        /**
         * Experience Type
         *
         * Вид впечатления
         */
        experience_type?: 'tasting' | 'excursion' | 'trip' | null;
        /**
         * Experience Id
         */
        experience_id?: string | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/publications';
};

export type AppCntPublicationsListErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntPublicationsListError = AppCntPublicationsListErrors[keyof AppCntPublicationsListErrors];

export type AppCntPublicationsListResponses = {
    /**
     * Successful Response
     */
    200: PublicationListOut;
};

export type AppCntPublicationsListResponse = AppCntPublicationsListResponses[keyof AppCntPublicationsListResponses];

export type AppCntPublicationsViewsData = {
    body: PublicationViewsIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/publications/views';
};

export type AppCntPublicationsViewsErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntPublicationsViewsError = AppCntPublicationsViewsErrors[keyof AppCntPublicationsViewsErrors];

export type AppCntPublicationsViewsResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCntPublicationsViewsResponse = AppCntPublicationsViewsResponses[keyof AppCntPublicationsViewsResponses];

export type AppCntPublicationGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/app/publications/{publication_id}';
};

export type AppCntPublicationGetErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntPublicationGetError = AppCntPublicationGetErrors[keyof AppCntPublicationGetErrors];

export type AppCntPublicationGetResponses = {
    /**
     * Successful Response
     */
    200: PublicationScreenOut;
};

export type AppCntPublicationGetResponse = AppCntPublicationGetResponses[keyof AppCntPublicationGetResponses];

export type AppCntPublicationUnlikeData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/app/publications/{publication_id}/like';
};

export type AppCntPublicationUnlikeErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntPublicationUnlikeError = AppCntPublicationUnlikeErrors[keyof AppCntPublicationUnlikeErrors];

export type AppCntPublicationUnlikeResponses = {
    /**
     * Successful Response
     */
    200: LikeStateOut;
};

export type AppCntPublicationUnlikeResponse = AppCntPublicationUnlikeResponses[keyof AppCntPublicationUnlikeResponses];

export type AppCntPublicationLikeData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/app/publications/{publication_id}/like';
};

export type AppCntPublicationLikeErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntPublicationLikeError = AppCntPublicationLikeErrors[keyof AppCntPublicationLikeErrors];

export type AppCntPublicationLikeResponses = {
    /**
     * Successful Response
     */
    200: LikeStateOut;
};

export type AppCntPublicationLikeResponse = AppCntPublicationLikeResponses[keyof AppCntPublicationLikeResponses];

export type AppCntCommentsListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: {
        /**
         * Cursor
         *
         * Курсор
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер порции, по умолчанию 20
         */
        limit?: number;
    };
    url: '/api/v1/app/publications/{publication_id}/comments';
};

export type AppCntCommentsListErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntCommentsListError = AppCntCommentsListErrors[keyof AppCntCommentsListErrors];

export type AppCntCommentsListResponses = {
    /**
     * Successful Response
     */
    200: CommentListOut;
};

export type AppCntCommentsListResponse = AppCntCommentsListResponses[keyof AppCntCommentsListResponses];

export type AppCntCommentCreateData = {
    body: PublicationCommentIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/app/publications/{publication_id}/comments';
};

export type AppCntCommentCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppCntCommentCreateError = AppCntCommentCreateErrors[keyof AppCntCommentCreateErrors];

export type AppCntCommentCreateResponses = {
    /**
     * Successful Response
     */
    201: CommentOut;
};

export type AppCntCommentCreateResponse = AppCntCommentCreateResponses[keyof AppCntCommentCreateResponses];

export type AppCntSavedListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Type
         *
         * Вкладка
         */
        type?: 'wine' | 'set' | 'experience' | 'winery' | 'lesson';
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/saved';
};

export type AppCntSavedListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntSavedListError = AppCntSavedListErrors[keyof AppCntSavedListErrors];

export type AppCntSavedListResponses = {
    /**
     * Successful Response
     */
    200: SavedOut;
};

export type AppCntSavedListResponse = AppCntSavedListResponses[keyof AppCntSavedListResponses];

export type AppCntSavedCreateData = {
    body: SavedIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/saved';
};

export type AppCntSavedCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntSavedCreateError = AppCntSavedCreateErrors[keyof AppCntSavedCreateErrors];

export type AppCntSavedCreateResponses = {
    /**
     * Successful Response
     */
    200: SavedStateOut;
};

export type AppCntSavedCreateResponse = AppCntSavedCreateResponses[keyof AppCntSavedCreateResponses];

export type AppCntSavedDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Target Type
         */
        target_type: 'wine' | 'set' | 'drop' | 'tasting' | 'excursion' | 'trip' | 'winery' | 'lesson';
        /**
         * Target Id
         */
        target_id: string;
    };
    query?: never;
    url: '/api/v1/app/saved/{target_type}/{target_id}';
};

export type AppCntSavedDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntSavedDeleteError = AppCntSavedDeleteErrors[keyof AppCntSavedDeleteErrors];

export type AppCntSavedDeleteResponses = {
    /**
     * Successful Response
     */
    200: SavedStateOut;
};

export type AppCntSavedDeleteResponse = AppCntSavedDeleteResponses[keyof AppCntSavedDeleteResponses];

export type AppCntExpertApplicationWithdrawData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/expert-application';
};

export type AppCntExpertApplicationWithdrawErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntExpertApplicationWithdrawError = AppCntExpertApplicationWithdrawErrors[keyof AppCntExpertApplicationWithdrawErrors];

export type AppCntExpertApplicationWithdrawResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCntExpertApplicationWithdrawResponse = AppCntExpertApplicationWithdrawResponses[keyof AppCntExpertApplicationWithdrawResponses];

export type AppCntExpertApplicationGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/expert-application';
};

export type AppCntExpertApplicationGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntExpertApplicationGetError = AppCntExpertApplicationGetErrors[keyof AppCntExpertApplicationGetErrors];

export type AppCntExpertApplicationGetResponses = {
    /**
     * Successful Response
     */
    200: ApplicationStateOut;
};

export type AppCntExpertApplicationGetResponse = AppCntExpertApplicationGetResponses[keyof AppCntExpertApplicationGetResponses];

export type AppCntExpertApplicationSubmitData = {
    body: ApplicationIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/expert-application';
};

export type AppCntExpertApplicationSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntExpertApplicationSubmitError = AppCntExpertApplicationSubmitErrors[keyof AppCntExpertApplicationSubmitErrors];

export type AppCntExpertApplicationSubmitResponses = {
    /**
     * Successful Response
     */
    201: ApplicationOut;
};

export type AppCntExpertApplicationSubmitResponse = AppCntExpertApplicationSubmitResponses[keyof AppCntExpertApplicationSubmitResponses];

export type AppCntExpertApplicationResubmitData = {
    body: ApplicationIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/expert-application';
};

export type AppCntExpertApplicationResubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntExpertApplicationResubmitError = AppCntExpertApplicationResubmitErrors[keyof AppCntExpertApplicationResubmitErrors];

export type AppCntExpertApplicationResubmitResponses = {
    /**
     * Successful Response
     */
    200: ApplicationOut;
};

export type AppCntExpertApplicationResubmitResponse = AppCntExpertApplicationResubmitResponses[keyof AppCntExpertApplicationResubmitResponses];

export type AppCntLegalDocumentsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/legal-documents';
};

export type AppCntLegalDocumentsErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntLegalDocumentsError = AppCntLegalDocumentsErrors[keyof AppCntLegalDocumentsErrors];

export type AppCntLegalDocumentsResponses = {
    /**
     * Response App Cnt Legal Documents
     *
     * Successful Response
     */
    200: Array<LegalDocumentOut>;
};

export type AppCntLegalDocumentsResponse = AppCntLegalDocumentsResponses[keyof AppCntLegalDocumentsResponses];

export type AppCntLegalDocumentsCurrentData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/legal-documents/current';
};

export type AppCntLegalDocumentsCurrentErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntLegalDocumentsCurrentError = AppCntLegalDocumentsCurrentErrors[keyof AppCntLegalDocumentsCurrentErrors];

export type AppCntLegalDocumentsCurrentResponses = {
    /**
     * Response App Cnt Legal Documents Current
     *
     * Successful Response
     */
    200: Array<LegalDocumentOut>;
};

export type AppCntLegalDocumentsCurrentResponse = AppCntLegalDocumentsCurrentResponses[keyof AppCntLegalDocumentsCurrentResponses];

export type AppCntLegalDocumentsAcceptData = {
    body: LegalAcceptIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/legal-documents/accept';
};

export type AppCntLegalDocumentsAcceptErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntLegalDocumentsAcceptError = AppCntLegalDocumentsAcceptErrors[keyof AppCntLegalDocumentsAcceptErrors];

export type AppCntLegalDocumentsAcceptResponses = {
    /**
     * Response App Cnt Legal Documents Accept
     *
     * Successful Response
     */
    200: Array<LegalDocumentOut>;
};

export type AppCntLegalDocumentsAcceptResponse = AppCntLegalDocumentsAcceptResponses[keyof AppCntLegalDocumentsAcceptResponses];

export type AppCntStudioData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/studio';
};

export type AppCntStudioErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioError = AppCntStudioErrors[keyof AppCntStudioErrors];

export type AppCntStudioResponses = {
    /**
     * Successful Response
     */
    200: StudioOut;
};

export type AppCntStudioResponse = AppCntStudioResponses[keyof AppCntStudioResponses];

export type AppCntStudioPublicationsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/studio/publications';
};

export type AppCntStudioPublicationsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioPublicationsError = AppCntStudioPublicationsErrors[keyof AppCntStudioPublicationsErrors];

export type AppCntStudioPublicationsResponses = {
    /**
     * Successful Response
     */
    200: StudioPublicationsOut;
};

export type AppCntStudioPublicationsResponse = AppCntStudioPublicationsResponses[keyof AppCntStudioPublicationsResponses];

export type AppCntStudioPublicationCreateData = {
    body: StudioPublicationIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/studio/publications';
};

export type AppCntStudioPublicationCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioPublicationCreateError = AppCntStudioPublicationCreateErrors[keyof AppCntStudioPublicationCreateErrors];

export type AppCntStudioPublicationCreateResponses = {
    /**
     * Successful Response
     */
    201: PublicationCreatedOut;
};

export type AppCntStudioPublicationCreateResponse = AppCntStudioPublicationCreateResponses[keyof AppCntStudioPublicationCreateResponses];

export type AppCntStudioPublicationDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/app/studio/publications/{publication_id}';
};

export type AppCntStudioPublicationDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioPublicationDeleteError = AppCntStudioPublicationDeleteErrors[keyof AppCntStudioPublicationDeleteErrors];

export type AppCntStudioPublicationDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCntStudioPublicationDeleteResponse = AppCntStudioPublicationDeleteResponses[keyof AppCntStudioPublicationDeleteResponses];

export type AppCntStudioCommentsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: {
        /**
         * Cursor
         *
         * Курсор
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер порции, по умолчанию 20
         */
        limit?: number;
    };
    url: '/api/v1/app/studio/publications/{publication_id}/comments';
};

export type AppCntStudioCommentsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioCommentsError = AppCntStudioCommentsErrors[keyof AppCntStudioCommentsErrors];

export type AppCntStudioCommentsResponses = {
    /**
     * Successful Response
     */
    200: StudioCommentsOut;
};

export type AppCntStudioCommentsResponse = AppCntStudioCommentsResponses[keyof AppCntStudioCommentsResponses];

export type AppCntStudioCommentReplyData = {
    body: ReplyIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Comment Id
         */
        comment_id: string;
    };
    query?: never;
    url: '/api/v1/app/studio/comments/{comment_id}/reply';
};

export type AppCntStudioCommentReplyErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioCommentReplyError = AppCntStudioCommentReplyErrors[keyof AppCntStudioCommentReplyErrors];

export type AppCntStudioCommentReplyResponses = {
    /**
     * Successful Response
     */
    201: CommentReplyOut;
};

export type AppCntStudioCommentReplyResponse = AppCntStudioCommentReplyResponses[keyof AppCntStudioCommentReplyResponses];

export type AppCntStudioStoriesData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/studio/stories';
};

export type AppCntStudioStoriesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioStoriesError = AppCntStudioStoriesErrors[keyof AppCntStudioStoriesErrors];

export type AppCntStudioStoriesResponses = {
    /**
     * Response App Cnt Studio Stories
     *
     * Successful Response
     */
    200: Array<StudioStoryOut>;
};

export type AppCntStudioStoriesResponse = AppCntStudioStoriesResponses[keyof AppCntStudioStoriesResponses];

export type AppCntStudioStoryCreateData = {
    body: StudioStoryIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/studio/stories';
};

export type AppCntStudioStoryCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioStoryCreateError = AppCntStudioStoryCreateErrors[keyof AppCntStudioStoryCreateErrors];

export type AppCntStudioStoryCreateResponses = {
    /**
     * Successful Response
     */
    201: StoryCreatedOut;
};

export type AppCntStudioStoryCreateResponse = AppCntStudioStoryCreateResponses[keyof AppCntStudioStoryCreateResponses];

export type AppCntStudioStoryDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Story Id
         */
        story_id: string;
    };
    query?: never;
    url: '/api/v1/app/studio/stories/{story_id}';
};

export type AppCntStudioStoryDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioStoryDeleteError = AppCntStudioStoryDeleteErrors[keyof AppCntStudioStoryDeleteErrors];

export type AppCntStudioStoryDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppCntStudioStoryDeleteResponse = AppCntStudioStoryDeleteResponses[keyof AppCntStudioStoryDeleteResponses];

export type AppCntStudioTagOptionsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query: {
        /**
         * Type
         */
        type: 'region' | 'wine' | 'experience';
        /**
         * Q
         */
        q?: string | null;
        /**
         * Region Id
         */
        region_id?: string | null;
    };
    url: '/api/v1/app/studio/tag-options';
};

export type AppCntStudioTagOptionsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioTagOptionsError = AppCntStudioTagOptionsErrors[keyof AppCntStudioTagOptionsErrors];

export type AppCntStudioTagOptionsResponses = {
    /**
     * Response App Cnt Studio Tag Options
     *
     * Successful Response
     */
    200: Array<TagOptionOut>;
};

export type AppCntStudioTagOptionsResponse = AppCntStudioTagOptionsResponses[keyof AppCntStudioTagOptionsResponses];

export type AppCntStudioLinkOptionsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Q
         */
        q?: string | null;
    };
    url: '/api/v1/app/studio/link-options';
};

export type AppCntStudioLinkOptionsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
};

export type AppCntStudioLinkOptionsError = AppCntStudioLinkOptionsErrors[keyof AppCntStudioLinkOptionsErrors];

export type AppCntStudioLinkOptionsResponses = {
    /**
     * Response App Cnt Studio Link Options
     *
     * Successful Response
     */
    200: Array<LinkOptionOut>;
};

export type AppCntStudioLinkOptionsResponse = AppCntStudioLinkOptionsResponses[keyof AppCntStudioLinkOptionsResponses];

export type AppOrdCartGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/cart';
};

export type AppOrdCartGetErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdCartGetError = AppOrdCartGetErrors[keyof AppOrdCartGetErrors];

export type AppOrdCartGetResponses = {
    /**
     * Successful Response
     */
    200: OrdCartOut;
};

export type AppOrdCartGetResponse = AppOrdCartGetResponses[keyof AppOrdCartGetResponses];

export type AppOrdCartItemDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Item Key
         */
        item_key: string;
    };
    query?: never;
    url: '/api/v1/app/cart/items/{item_key}';
};

export type AppOrdCartItemDeleteErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdCartItemDeleteError = AppOrdCartItemDeleteErrors[keyof AppOrdCartItemDeleteErrors];

export type AppOrdCartItemDeleteResponses = {
    /**
     * Successful Response
     */
    200: OrdCartOut;
};

export type AppOrdCartItemDeleteResponse = AppOrdCartItemDeleteResponses[keyof AppOrdCartItemDeleteResponses];

export type AppOrdCartItemPutData = {
    body: OrdCartItemIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Item Key
         */
        item_key: string;
    };
    query?: never;
    url: '/api/v1/app/cart/items/{item_key}';
};

export type AppOrdCartItemPutErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdCartItemPutError = AppOrdCartItemPutErrors[keyof AppOrdCartItemPutErrors];

export type AppOrdCartItemPutResponses = {
    /**
     * Successful Response
     */
    200: OrdCartOut;
};

export type AppOrdCartItemPutResponse = AppOrdCartItemPutResponses[keyof AppOrdCartItemPutResponses];

export type AppOrdCartMergeData = {
    body: OrdCartMergeIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/cart/merge';
};

export type AppOrdCartMergeErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdCartMergeError = AppOrdCartMergeErrors[keyof AppOrdCartMergeErrors];

export type AppOrdCartMergeResponses = {
    /**
     * Successful Response
     */
    200: OrdCartOut;
};

export type AppOrdCartMergeResponse = AppOrdCartMergeResponses[keyof AppOrdCartMergeResponses];

export type AppOrdCheckoutDiscountsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/checkout/discounts';
};

export type AppOrdCheckoutDiscountsErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdCheckoutDiscountsError = AppOrdCheckoutDiscountsErrors[keyof AppOrdCheckoutDiscountsErrors];

export type AppOrdCheckoutDiscountsResponses = {
    /**
     * Successful Response
     */
    200: OrdDiscountsOut;
};

export type AppOrdCheckoutDiscountsResponse = AppOrdCheckoutDiscountsResponses[keyof AppOrdCheckoutDiscountsResponses];

export type AppOrdCheckoutQuoteData = {
    body: OrdQuoteIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/checkout/quote';
};

export type AppOrdCheckoutQuoteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdCheckoutQuoteError = AppOrdCheckoutQuoteErrors[keyof AppOrdCheckoutQuoteErrors];

export type AppOrdCheckoutQuoteResponses = {
    /**
     * Successful Response
     */
    200: OrdQuoteOut;
};

export type AppOrdCheckoutQuoteResponse = AppOrdCheckoutQuoteResponses[keyof AppOrdCheckoutQuoteResponses];

export type AppOrdCheckoutCreateData = {
    body: OrdCheckoutIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/checkout';
};

export type AppOrdCheckoutCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdCheckoutCreateError = AppOrdCheckoutCreateErrors[keyof AppOrdCheckoutCreateErrors];

export type AppOrdCheckoutCreateResponses = {
    /**
     * Successful Response
     */
    201: OrdCheckoutOut;
};

export type AppOrdCheckoutCreateResponse = AppOrdCheckoutCreateResponses[keyof AppOrdCheckoutCreateResponses];

export type AppOrdCheckoutGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Checkout Id
         */
        checkout_id: string;
    };
    query?: never;
    url: '/api/v1/app/checkout/{checkout_id}';
};

export type AppOrdCheckoutGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdCheckoutGetError = AppOrdCheckoutGetErrors[keyof AppOrdCheckoutGetErrors];

export type AppOrdCheckoutGetResponses = {
    /**
     * Successful Response
     */
    200: OrdCheckoutStatusOut;
};

export type AppOrdCheckoutGetResponse = AppOrdCheckoutGetResponses[keyof AppOrdCheckoutGetResponses];

export type AppOrdCheckoutPaymentCreateData = {
    body: OrdPaymentIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * Checkout Id
         */
        checkout_id: string;
    };
    query?: never;
    url: '/api/v1/app/checkout/{checkout_id}/payments';
};

export type AppOrdCheckoutPaymentCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdCheckoutPaymentCreateError = AppOrdCheckoutPaymentCreateErrors[keyof AppOrdCheckoutPaymentCreateErrors];

export type AppOrdCheckoutPaymentCreateResponses = {
    /**
     * Successful Response
     */
    201: OrdAttemptView;
};

export type AppOrdCheckoutPaymentCreateResponse = AppOrdCheckoutPaymentCreateResponses[keyof AppOrdCheckoutPaymentCreateResponses];

export type AppOrdCheckoutClientResultData = {
    body: OrdClientResultIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Checkout Id
         */
        checkout_id: string;
        /**
         * Attempt Id
         */
        attempt_id: string;
    };
    query?: never;
    url: '/api/v1/app/checkout/{checkout_id}/payments/{attempt_id}/client-result';
};

export type AppOrdCheckoutClientResultErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdCheckoutClientResultError = AppOrdCheckoutClientResultErrors[keyof AppOrdCheckoutClientResultErrors];

export type AppOrdCheckoutClientResultResponses = {
    /**
     * Successful Response
     */
    200: OrdAttemptView;
};

export type AppOrdCheckoutClientResultResponse = AppOrdCheckoutClientResultResponses[keyof AppOrdCheckoutClientResultResponses];

export type AppOrdPaymentMethodsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Group
         *
         * learning | wine | experience
         */
        group?: CartGroup | null;
    };
    url: '/api/v1/app/payment-methods';
};

export type AppOrdPaymentMethodsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdPaymentMethodsError = AppOrdPaymentMethodsErrors[keyof AppOrdPaymentMethodsErrors];

export type AppOrdPaymentMethodsResponses = {
    /**
     * Successful Response
     */
    200: OrdPaymentMethodsOut;
};

export type AppOrdPaymentMethodsResponse = AppOrdPaymentMethodsResponses[keyof AppOrdPaymentMethodsResponses];

export type AppOrdAddressesListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/shipping-addresses';
};

export type AppOrdAddressesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdAddressesListError = AppOrdAddressesListErrors[keyof AppOrdAddressesListErrors];

export type AppOrdAddressesListResponses = {
    /**
     * Successful Response
     */
    200: OrdAddressesOut;
};

export type AppOrdAddressesListResponse = AppOrdAddressesListResponses[keyof AppOrdAddressesListResponses];

export type AppOrdAddressesCreateData = {
    body: OrdAddressIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/shipping-addresses';
};

export type AppOrdAddressesCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdAddressesCreateError = AppOrdAddressesCreateErrors[keyof AppOrdAddressesCreateErrors];

export type AppOrdAddressesCreateResponses = {
    /**
     * Successful Response
     */
    201: OrdAddressOut;
};

export type AppOrdAddressesCreateResponse = AppOrdAddressesCreateResponses[keyof AppOrdAddressesCreateResponses];

export type AppOrdAddressesDeleteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Address Id
         */
        address_id: string;
    };
    query?: never;
    url: '/api/v1/app/shipping-addresses/{address_id}';
};

export type AppOrdAddressesDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdAddressesDeleteError = AppOrdAddressesDeleteErrors[keyof AppOrdAddressesDeleteErrors];

export type AppOrdAddressesDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppOrdAddressesDeleteResponse = AppOrdAddressesDeleteResponses[keyof AppOrdAddressesDeleteResponses];

export type AppOrdAddressesUpdateData = {
    body: OrdAddressPatchIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Address Id
         */
        address_id: string;
    };
    query?: never;
    url: '/api/v1/app/shipping-addresses/{address_id}';
};

export type AppOrdAddressesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdAddressesUpdateError = AppOrdAddressesUpdateErrors[keyof AppOrdAddressesUpdateErrors];

export type AppOrdAddressesUpdateResponses = {
    /**
     * Successful Response
     */
    200: OrdAddressOut;
};

export type AppOrdAddressesUpdateResponse = AppOrdAddressesUpdateResponses[keyof AppOrdAddressesUpdateResponses];

export type AppOrdShippingQuoteData = {
    body: OrdShippingQuoteIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/shipping/quote';
};

export type AppOrdShippingQuoteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdShippingQuoteError = AppOrdShippingQuoteErrors[keyof AppOrdShippingQuoteErrors];

export type AppOrdShippingQuoteResponses = {
    /**
     * Successful Response
     */
    200: OrdShippingQuoteOut;
};

export type AppOrdShippingQuoteResponse = AppOrdShippingQuoteResponses[keyof AppOrdShippingQuoteResponses];

export type AppOrdShippingValidateData = {
    body: OrdAddressValidateIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/shipping/address/validate';
};

export type AppOrdShippingValidateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdShippingValidateError = AppOrdShippingValidateErrors[keyof AppOrdShippingValidateErrors];

export type AppOrdShippingValidateResponses = {
    /**
     * Successful Response
     */
    200: OrdAddressValidateOut;
};

export type AppOrdShippingValidateResponse = AppOrdShippingValidateResponses[keyof AppOrdShippingValidateResponses];

export type AppOrdOrdersListData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: {
        /**
         * Group
         *
         * learning | wine | experience | bonus
         */
        group?: OrderGroup | null;
        /**
         * Flow
         */
        flow?: Flow | null;
        /**
         * Ref
         */
        ref?: string | null;
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/app/orders';
};

export type AppOrdOrdersListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdOrdersListError = AppOrdOrdersListErrors[keyof AppOrdOrdersListErrors];

export type AppOrdOrdersListResponses = {
    /**
     * Successful Response
     */
    200: OrdOrdersPage;
};

export type AppOrdOrdersListResponse = AppOrdOrdersListResponses[keyof AppOrdOrdersListResponses];

export type AppOrdOrdersOwnedRefsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/orders/owned-refs';
};

export type AppOrdOrdersOwnedRefsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdOrdersOwnedRefsError = AppOrdOrdersOwnedRefsErrors[keyof AppOrdOrdersOwnedRefsErrors];

export type AppOrdOrdersOwnedRefsResponses = {
    /**
     * Successful Response
     */
    200: OrdOwnedRefsOut;
};

export type AppOrdOrdersOwnedRefsResponse = AppOrdOrdersOwnedRefsResponses[keyof AppOrdOrdersOwnedRefsResponses];

export type AppOrdOrdersGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/app/orders/{order_id}';
};

export type AppOrdOrdersGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdOrdersGetError = AppOrdOrdersGetErrors[keyof AppOrdOrdersGetErrors];

export type AppOrdOrdersGetResponses = {
    /**
     * Successful Response
     */
    200: OrdOrderDetail;
};

export type AppOrdOrdersGetResponse = AppOrdOrdersGetResponses[keyof AppOrdOrdersGetResponses];

export type AppOrdOrdersCancelQuoteData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/app/orders/{order_id}/cancel-quote';
};

export type AppOrdOrdersCancelQuoteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdOrdersCancelQuoteError = AppOrdOrdersCancelQuoteErrors[keyof AppOrdOrdersCancelQuoteErrors];

export type AppOrdOrdersCancelQuoteResponses = {
    /**
     * Successful Response
     */
    200: OrdCancelQuoteOut;
};

export type AppOrdOrdersCancelQuoteResponse = AppOrdOrdersCancelQuoteResponses[keyof AppOrdOrdersCancelQuoteResponses];

export type AppOrdOrdersCancelData = {
    body: OrdCancelIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/app/orders/{order_id}/cancel';
};

export type AppOrdOrdersCancelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdOrdersCancelError = AppOrdOrdersCancelErrors[keyof AppOrdOrdersCancelErrors];

export type AppOrdOrdersCancelResponses = {
    /**
     * Successful Response
     */
    200: OrdOrderDetail;
};

export type AppOrdOrdersCancelResponse = AppOrdOrdersCancelResponses[keyof AppOrdOrdersCancelResponses];

export type AppOrdOrdersReceiptsData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/app/orders/{order_id}/receipts';
};

export type AppOrdOrdersReceiptsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdOrdersReceiptsError = AppOrdOrdersReceiptsErrors[keyof AppOrdOrdersReceiptsErrors];

export type AppOrdOrdersReceiptsResponses = {
    /**
     * Successful Response
     */
    200: OrdReceiptsOut;
};

export type AppOrdOrdersReceiptsResponse = AppOrdOrdersReceiptsResponses[keyof AppOrdOrdersReceiptsResponses];

export type AppOrdOrdersReceiptResendData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/app/orders/{order_id}/receipt';
};

export type AppOrdOrdersReceiptResendErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdOrdersReceiptResendError = AppOrdOrdersReceiptResendErrors[keyof AppOrdOrdersReceiptResendErrors];

export type AppOrdOrdersReceiptResendResponses = {
    /**
     * Successful Response
     */
    202: OrdAcceptedOut;
};

export type AppOrdOrdersReceiptResendResponse = AppOrdOrdersReceiptResendResponses[keyof AppOrdOrdersReceiptResendResponses];

export type AppOrdOrdersTrackingData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/app/orders/{order_id}/tracking';
};

export type AppOrdOrdersTrackingErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdOrdersTrackingError = AppOrdOrdersTrackingErrors[keyof AppOrdOrdersTrackingErrors];

export type AppOrdOrdersTrackingResponses = {
    /**
     * Successful Response
     */
    200: OrdTrackingOut;
};

export type AppOrdOrdersTrackingResponse = AppOrdOrdersTrackingResponses[keyof AppOrdOrdersTrackingResponses];

export type AppOrdPartnerHandoffCreateData = {
    body: OrdHandoffIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/partner/handoffs';
};

export type AppOrdPartnerHandoffCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppOrdPartnerHandoffCreateError = AppOrdPartnerHandoffCreateErrors[keyof AppOrdPartnerHandoffCreateErrors];

export type AppOrdPartnerHandoffCreateResponses = {
    /**
     * Successful Response
     */
    201: OrdHandoffOut;
};

export type AppOrdPartnerHandoffCreateResponse = AppOrdPartnerHandoffCreateResponses[keyof AppOrdPartnerHandoffCreateResponses];

export type AppOrdPartnerHandoffGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path: {
        /**
         * Handoff Id
         */
        handoff_id: string;
    };
    query?: never;
    url: '/api/v1/app/partner/handoffs/{handoff_id}';
};

export type AppOrdPartnerHandoffGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppOrdPartnerHandoffGetError = AppOrdPartnerHandoffGetErrors[keyof AppOrdPartnerHandoffGetErrors];

export type AppOrdPartnerHandoffGetResponses = {
    /**
     * Successful Response
     */
    200: OrdHandoffStatusOut;
};

export type AppOrdPartnerHandoffGetResponse = AppOrdPartnerHandoffGetResponses[keyof AppOrdPartnerHandoffGetResponses];

export type AppIntBillingProductsGetData = {
    body?: never;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query: {
        /**
         * Items
         *
         * lesson:{id},region_course:{id}
         */
        items: string;
    };
    url: '/api/v1/app/billing/products';
};

export type AppIntBillingProductsGetErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppIntBillingProductsGetError = AppIntBillingProductsGetErrors[keyof AppIntBillingProductsGetErrors];

export type AppIntBillingProductsGetResponses = {
    /**
     * Successful Response
     */
    200: BillingProductsOut;
};

export type AppIntBillingProductsGetResponse = AppIntBillingProductsGetResponses[keyof AppIntBillingProductsGetResponses];

export type AppIntBillingProductsSeenData = {
    body: BillingProductsSeenIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/billing/products/seen';
};

export type AppIntBillingProductsSeenErrors = {
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AppIntBillingProductsSeenError = AppIntBillingProductsSeenErrors[keyof AppIntBillingProductsSeenErrors];

export type AppIntBillingProductsSeenResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AppIntBillingProductsSeenResponse = AppIntBillingProductsSeenResponses[keyof AppIntBillingProductsSeenResponses];

export type AppIntBillingIntentsCreateData = {
    body: BillingIntentIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/billing/intents';
};

export type AppIntBillingIntentsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppIntBillingIntentsCreateError = AppIntBillingIntentsCreateErrors[keyof AppIntBillingIntentsCreateErrors];

export type AppIntBillingIntentsCreateResponses = {
    /**
     * Successful Response
     */
    201: BillingIntentOut;
};

export type AppIntBillingIntentsCreateResponse = AppIntBillingIntentsCreateResponses[keyof AppIntBillingIntentsCreateResponses];

export type AppIntBillingPurchasesVerifyData = {
    body: BillingPurchaseIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/billing/purchases';
};

export type AppIntBillingPurchasesVerifyErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppIntBillingPurchasesVerifyError = AppIntBillingPurchasesVerifyErrors[keyof AppIntBillingPurchasesVerifyErrors];

export type AppIntBillingPurchasesVerifyResponses = {
    /**
     * Successful Response
     */
    200: BillingPurchaseOut;
};

export type AppIntBillingPurchasesVerifyResponse = AppIntBillingPurchasesVerifyResponses[keyof AppIntBillingPurchasesVerifyResponses];

export type AppIntBillingSyncData = {
    body: BillingSyncIn;
    headers: {
        /**
         * X-Device-Id
         *
         * uuid установки приложения
         */
        'X-Device-Id': string;
        /**
         * X-Client-Platform
         *
         * pwa | android_rustore | ios_appstore | android_gplay (из platforms рынка)
         */
        'X-Client-Platform': string;
        /**
         * X-Client-Version
         *
         * Семантическая версия сборки
         */
        'X-Client-Version': string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/billing/sync';
};

export type AppIntBillingSyncErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Версия клиента ниже минимальной (`client_update_required`)
     */
    426: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AppIntBillingSyncError = AppIntBillingSyncErrors[keyof AppIntBillingSyncErrors];

export type AppIntBillingSyncResponses = {
    /**
     * Successful Response
     */
    200: BillingSyncOut;
};

export type AppIntBillingSyncResponse = AppIntBillingSyncResponses[keyof AppIntBillingSyncResponses];

export type AppIntEmailUnsubscribePageData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Token
         */
        token: string;
    };
    url: '/api/v1/app/email/unsubscribe';
};

export type AppIntEmailUnsubscribePageErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AppIntEmailUnsubscribePageError = AppIntEmailUnsubscribePageErrors[keyof AppIntEmailUnsubscribePageErrors];

export type AppIntEmailUnsubscribePageResponses = {
    /**
     * Страница результата
     */
    200: string;
};

export type AppIntEmailUnsubscribePageResponse = AppIntEmailUnsubscribePageResponses[keyof AppIntEmailUnsubscribePageResponses];

export type AppIntEmailUnsubscribeOneClickData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Token
         */
        token: string;
    };
    url: '/api/v1/app/email/unsubscribe';
};

export type AppIntEmailUnsubscribeOneClickErrors = {
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AppIntEmailUnsubscribeOneClickError = AppIntEmailUnsubscribeOneClickErrors[keyof AppIntEmailUnsubscribeOneClickErrors];

export type AppIntEmailUnsubscribeOneClickResponses = {
    /**
     * Отписка выполнена
     */
    200: string;
};

export type AppIntEmailUnsubscribeOneClickResponse = AppIntEmailUnsubscribeOneClickResponses[keyof AppIntEmailUnsubscribeOneClickResponses];

export type AppSrvNotificationsUnsubscribeData = {
    body: TokenIn;
    path?: never;
    query?: never;
    url: '/api/v1/app/notifications/unsubscribe';
};

export type AppSrvNotificationsUnsubscribeErrors = {
    /**
     * Неверный запрос
     */
    400: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AppSrvNotificationsUnsubscribeError = AppSrvNotificationsUnsubscribeErrors[keyof AppSrvNotificationsUnsubscribeErrors];

export type AppSrvNotificationsUnsubscribeResponses = {
    /**
     * Successful Response
     */
    200: UnsubscribeOut;
};

export type AppSrvNotificationsUnsubscribeResponse = AppSrvNotificationsUnsubscribeResponses[keyof AppSrvNotificationsUnsubscribeResponses];

export type AppSrvNotificationsResubscribeData = {
    body: TokenIn;
    path?: never;
    query?: never;
    url: '/api/v1/app/notifications/resubscribe';
};

export type AppSrvNotificationsResubscribeErrors = {
    /**
     * Неверный запрос
     */
    400: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AppSrvNotificationsResubscribeError = AppSrvNotificationsResubscribeErrors[keyof AppSrvNotificationsResubscribeErrors];

export type AppSrvNotificationsResubscribeResponses = {
    /**
     * Successful Response
     */
    200: UnsubscribeOut;
};

export type AppSrvNotificationsResubscribeResponse = AppSrvNotificationsResubscribeResponses[keyof AppSrvNotificationsResubscribeResponses];

export type AppAccAuthOauthAppleFormPostData = {
    body: {
        code?: string;
        id_token?: string;
        state?: string;
        /**
         * JSON (первый вход)
         */
        user?: string;
        error?: string;
    };
    path?: never;
    query?: never;
    url: '/api/v1/app/auth/oauth/apple/form-post';
};

export type AdminMarketConfigGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/config';
};

export type AdminMarketConfigGetResponses = {
    /**
     * Successful Response
     */
    200: AdminConfigOut;
};

export type AdminMarketConfigGetResponse = AdminMarketConfigGetResponses[keyof AdminMarketConfigGetResponses];

export type AdminAccAuthLoginData = {
    body: AdminLoginIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/auth/login';
};

export type AdminAccAuthLoginErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AdminAccAuthLoginError = AdminAccAuthLoginErrors[keyof AdminAccAuthLoginErrors];

export type AdminAccAuthLoginResponses = {
    /**
     * Successful Response
     */
    200: AdminLoginOut;
};

export type AdminAccAuthLoginResponse = AdminAccAuthLoginResponses[keyof AdminAccAuthLoginResponses];

export type AdminAccAuthRefreshData = {
    body: RefreshIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/auth/refresh';
};

export type AdminAccAuthRefreshErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccAuthRefreshError = AdminAccAuthRefreshErrors[keyof AdminAccAuthRefreshErrors];

export type AdminAccAuthRefreshResponses = {
    /**
     * Successful Response
     */
    200: AdminRefreshOut;
};

export type AdminAccAuthRefreshResponse = AdminAccAuthRefreshResponses[keyof AdminAccAuthRefreshResponses];

export type AdminAccAuthLogoutData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/auth/logout';
};

export type AdminAccAuthLogoutErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
};

export type AdminAccAuthLogoutError = AdminAccAuthLogoutErrors[keyof AdminAccAuthLogoutErrors];

export type AdminAccAuthLogoutResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAccAuthLogoutResponse = AdminAccAuthLogoutResponses[keyof AdminAccAuthLogoutResponses];

export type AdminAccMeGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/me';
};

export type AdminAccMeGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
};

export type AdminAccMeGetError = AdminAccMeGetErrors[keyof AdminAccMeGetErrors];

export type AdminAccMeGetResponses = {
    /**
     * Successful Response
     */
    200: AdminMeOut;
};

export type AdminAccMeGetResponse = AdminAccMeGetResponses[keyof AdminAccMeGetResponses];

export type AdminAccMeUpdateData = {
    body: AdminMePatchIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/me';
};

export type AdminAccMeUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccMeUpdateError = AdminAccMeUpdateErrors[keyof AdminAccMeUpdateErrors];

export type AdminAccMeUpdateResponses = {
    /**
     * Successful Response
     */
    200: AdminMeOut;
};

export type AdminAccMeUpdateResponse = AdminAccMeUpdateResponses[keyof AdminAccMeUpdateResponses];

export type AdminAccMePasswordChangeData = {
    body: PasswordChangeIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/me/password';
};

export type AdminAccMePasswordChangeErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AdminAccMePasswordChangeError = AdminAccMePasswordChangeErrors[keyof AdminAccMePasswordChangeErrors];

export type AdminAccMePasswordChangeResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAccMePasswordChangeResponse = AdminAccMePasswordChangeResponses[keyof AdminAccMePasswordChangeResponses];

export type AdminAccMeAuthorProfileGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/me/author-profile';
};

export type AdminAccMeAuthorProfileGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
};

export type AdminAccMeAuthorProfileGetError = AdminAccMeAuthorProfileGetErrors[keyof AdminAccMeAuthorProfileGetErrors];

export type AdminAccMeAuthorProfileGetResponses = {
    /**
     * Successful Response
     */
    200: AuthorProfileOut;
};

export type AdminAccMeAuthorProfileGetResponse = AdminAccMeAuthorProfileGetResponses[keyof AdminAccMeAuthorProfileGetResponses];

export type AdminAccMeAuthorProfileSaveData = {
    body: AuthorProfileIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/me/author-profile';
};

export type AdminAccMeAuthorProfileSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccMeAuthorProfileSaveError = AdminAccMeAuthorProfileSaveErrors[keyof AdminAccMeAuthorProfileSaveErrors];

export type AdminAccMeAuthorProfileSaveResponses = {
    /**
     * Successful Response
     */
    200: AuthorProfileOut;
};

export type AdminAccMeAuthorProfileSaveResponse = AdminAccMeAuthorProfileSaveResponses[keyof AdminAccMeAuthorProfileSaveResponses];

export type AdminAccMeAuthorProfilePublishData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/me/author-profile/publish';
};

export type AdminAccMeAuthorProfilePublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccMeAuthorProfilePublishError = AdminAccMeAuthorProfilePublishErrors[keyof AdminAccMeAuthorProfilePublishErrors];

export type AdminAccMeAuthorProfilePublishResponses = {
    /**
     * Successful Response
     */
    200: AuthorProfileOut;
};

export type AdminAccMeAuthorProfilePublishResponse = AdminAccMeAuthorProfilePublishResponses[keyof AdminAccMeAuthorProfilePublishResponses];

export type AdminAccMeAuthorProfileHideData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/me/author-profile/hide';
};

export type AdminAccMeAuthorProfileHideErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
};

export type AdminAccMeAuthorProfileHideError = AdminAccMeAuthorProfileHideErrors[keyof AdminAccMeAuthorProfileHideErrors];

export type AdminAccMeAuthorProfileHideResponses = {
    /**
     * Successful Response
     */
    200: AuthorProfileOut;
};

export type AdminAccMeAuthorProfileHideResponse = AdminAccMeAuthorProfileHideResponses[keyof AdminAccMeAuthorProfileHideResponses];

export type AdminAccUsersListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: accuracy, birth_date, bonus_balance, bookings_count, city_name, country_code, email, full_name, last_activity_at, orders_count, orders_sum, passport_tier, phone, registered_at, status, xp
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, blocked)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, blocked); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Full Name]
         *
         * Фильтр по full_name
         */
        'filter[full_name]'?: string | null;
        /**
         * Filter[Full Name][]
         *
         * Фильтр по full_name; несколько значений
         */
        'filter[full_name][]'?: Array<string> | null;
        /**
         * Filter[Email]
         *
         * Фильтр по email
         */
        'filter[email]'?: string | null;
        /**
         * Filter[Email][]
         *
         * Фильтр по email; несколько значений
         */
        'filter[email][]'?: Array<string> | null;
        /**
         * Filter[Phone]
         *
         * Фильтр по phone
         */
        'filter[phone]'?: string | null;
        /**
         * Filter[Phone][]
         *
         * Фильтр по phone; несколько значений
         */
        'filter[phone][]'?: Array<string> | null;
        /**
         * Filter[Country Code]
         *
         * Фильтр по country_code
         */
        'filter[country_code]'?: string | null;
        /**
         * Filter[Country Code][]
         *
         * Фильтр по country_code; несколько значений
         */
        'filter[country_code][]'?: Array<string> | null;
        /**
         * Filter[City Name]
         *
         * Фильтр по city_name
         */
        'filter[city_name]'?: string | null;
        /**
         * Filter[City Name][]
         *
         * Фильтр по city_name; несколько значений
         */
        'filter[city_name][]'?: Array<string> | null;
        /**
         * Filter[Birth Date]
         *
         * Фильтр по birth_date
         */
        'filter[birth_date]'?: string | null;
        /**
         * Filter[Birth Date][]
         *
         * Фильтр по birth_date; несколько значений
         */
        'filter[birth_date][]'?: Array<string> | null;
        /**
         * Filter[Registration Method]
         *
         * Фильтр по registration_method (email, vk, yandex, google, apple, team)
         */
        'filter[registration_method]'?: string | null;
        /**
         * Filter[Registration Method][]
         *
         * Фильтр по registration_method (email, vk, yandex, google, apple, team); несколько значений
         */
        'filter[registration_method][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/users';
};

export type AdminAccUsersListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersListError = AdminAccUsersListErrors[keyof AdminAccUsersListErrors];

export type AdminAccUsersListResponses = {
    /**
     * Successful Response
     */
    200: PageUserRow;
};

export type AdminAccUsersListResponse = AdminAccUsersListResponses[keyof AdminAccUsersListResponses];

export type AdminAccUsersExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: accuracy, birth_date, bonus_balance, bookings_count, city_name, country_code, email, full_name, last_activity_at, orders_count, orders_sum, passport_tier, phone, registered_at, status, xp
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, blocked)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, blocked); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Full Name]
         *
         * Фильтр по full_name
         */
        'filter[full_name]'?: string | null;
        /**
         * Filter[Full Name][]
         *
         * Фильтр по full_name; несколько значений
         */
        'filter[full_name][]'?: Array<string> | null;
        /**
         * Filter[Email]
         *
         * Фильтр по email
         */
        'filter[email]'?: string | null;
        /**
         * Filter[Email][]
         *
         * Фильтр по email; несколько значений
         */
        'filter[email][]'?: Array<string> | null;
        /**
         * Filter[Phone]
         *
         * Фильтр по phone
         */
        'filter[phone]'?: string | null;
        /**
         * Filter[Phone][]
         *
         * Фильтр по phone; несколько значений
         */
        'filter[phone][]'?: Array<string> | null;
        /**
         * Filter[Country Code]
         *
         * Фильтр по country_code
         */
        'filter[country_code]'?: string | null;
        /**
         * Filter[Country Code][]
         *
         * Фильтр по country_code; несколько значений
         */
        'filter[country_code][]'?: Array<string> | null;
        /**
         * Filter[City Name]
         *
         * Фильтр по city_name
         */
        'filter[city_name]'?: string | null;
        /**
         * Filter[City Name][]
         *
         * Фильтр по city_name; несколько значений
         */
        'filter[city_name][]'?: Array<string> | null;
        /**
         * Filter[Birth Date]
         *
         * Фильтр по birth_date
         */
        'filter[birth_date]'?: string | null;
        /**
         * Filter[Birth Date][]
         *
         * Фильтр по birth_date; несколько значений
         */
        'filter[birth_date][]'?: Array<string> | null;
        /**
         * Filter[Registration Method]
         *
         * Фильтр по registration_method (email, vk, yandex, google, apple, team)
         */
        'filter[registration_method]'?: string | null;
        /**
         * Filter[Registration Method][]
         *
         * Фильтр по registration_method (email, vk, yandex, google, apple, team); несколько значений
         */
        'filter[registration_method][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/users/export';
};

export type AdminAccUsersExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersExportError = AdminAccUsersExportErrors[keyof AdminAccUsersExportErrors];

export type AdminAccUsersExportResponses = {
    /**
     * CSV текущей выборки
     */
    200: unknown;
};

export type AdminAccUsersGetData = {
    body?: never;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/admin/users/{user_id}';
};

export type AdminAccUsersGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersGetError = AdminAccUsersGetErrors[keyof AdminAccUsersGetErrors];

export type AdminAccUsersGetResponses = {
    /**
     * Successful Response
     */
    200: UserCard;
};

export type AdminAccUsersGetResponse = AdminAccUsersGetResponses[keyof AdminAccUsersGetResponses];

export type AdminAccUsersUpdateData = {
    body: UserPatchIn;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/admin/users/{user_id}';
};

export type AdminAccUsersUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersUpdateError = AdminAccUsersUpdateErrors[keyof AdminAccUsersUpdateErrors];

export type AdminAccUsersUpdateResponses = {
    /**
     * Successful Response
     */
    200: UserCard;
};

export type AdminAccUsersUpdateResponse = AdminAccUsersUpdateResponses[keyof AdminAccUsersUpdateResponses];

export type AdminAccUsersActivityData = {
    body?: never;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/admin/users/{user_id}/activity';
};

export type AdminAccUsersActivityErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersActivityError = AdminAccUsersActivityErrors[keyof AdminAccUsersActivityErrors];

export type AdminAccUsersActivityResponses = {
    /**
     * Successful Response
     */
    200: UserActivityOut;
};

export type AdminAccUsersActivityResponse = AdminAccUsersActivityResponses[keyof AdminAccUsersActivityResponses];

export type AdminAccUsersHistoryData = {
    body?: never;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/admin/users/{user_id}/history';
};

export type AdminAccUsersHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersHistoryError = AdminAccUsersHistoryErrors[keyof AdminAccUsersHistoryErrors];

export type AdminAccUsersHistoryResponses = {
    /**
     * Successful Response
     */
    200: CursorPageUserHistoryItem;
};

export type AdminAccUsersHistoryResponse = AdminAccUsersHistoryResponses[keyof AdminAccUsersHistoryResponses];

export type AdminAccUsersBlockData = {
    body: UserBlockIn;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/admin/users/{user_id}/block';
};

export type AdminAccUsersBlockErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersBlockError = AdminAccUsersBlockErrors[keyof AdminAccUsersBlockErrors];

export type AdminAccUsersBlockResponses = {
    /**
     * Successful Response
     */
    200: UserCard;
};

export type AdminAccUsersBlockResponse = AdminAccUsersBlockResponses[keyof AdminAccUsersBlockResponses];

export type AdminAccUsersUnblockData = {
    body?: never;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/admin/users/{user_id}/unblock';
};

export type AdminAccUsersUnblockErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersUnblockError = AdminAccUsersUnblockErrors[keyof AdminAccUsersUnblockErrors];

export type AdminAccUsersUnblockResponses = {
    /**
     * Successful Response
     */
    200: UserCard;
};

export type AdminAccUsersUnblockResponse = AdminAccUsersUnblockResponses[keyof AdminAccUsersUnblockResponses];

export type AdminAccUsersDeleteData = {
    body: UserDeleteIn;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/admin/users/{user_id}/delete';
};

export type AdminAccUsersDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccUsersDeleteError = AdminAccUsersDeleteErrors[keyof AdminAccUsersDeleteErrors];

export type AdminAccUsersDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAccUsersDeleteResponse = AdminAccUsersDeleteResponses[keyof AdminAccUsersDeleteResponses];

export type AdminAccTeamListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: email, full_name, last_login_at, registered_at, role, status
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Role]
         *
         * Фильтр по role (admin, winemaker, expert)
         */
        'filter[role]'?: string | null;
        /**
         * Filter[Role][]
         *
         * Фильтр по role (admin, winemaker, expert); несколько значений
         */
        'filter[role][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, pending_review, needs_info, rejected, blocked)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, pending_review, needs_info, rejected, blocked); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/team';
};

export type AdminAccTeamListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamListError = AdminAccTeamListErrors[keyof AdminAccTeamListErrors];

export type AdminAccTeamListResponses = {
    /**
     * Successful Response
     */
    200: PageMemberRow;
};

export type AdminAccTeamListResponse = AdminAccTeamListResponses[keyof AdminAccTeamListResponses];

export type AdminAccTeamCreateData = {
    body: MemberCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/team';
};

export type AdminAccTeamCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamCreateError = AdminAccTeamCreateErrors[keyof AdminAccTeamCreateErrors];

export type AdminAccTeamCreateResponses = {
    /**
     * Successful Response
     */
    201: MemberCreatedOut;
};

export type AdminAccTeamCreateResponse = AdminAccTeamCreateResponses[keyof AdminAccTeamCreateResponses];

export type AdminAccTeamGetData = {
    body?: never;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/team/{member_id}';
};

export type AdminAccTeamGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamGetError = AdminAccTeamGetErrors[keyof AdminAccTeamGetErrors];

export type AdminAccTeamGetResponses = {
    /**
     * Successful Response
     */
    200: MemberCard;
};

export type AdminAccTeamGetResponse = AdminAccTeamGetResponses[keyof AdminAccTeamGetResponses];

export type AdminAccTeamUpdateData = {
    body: MemberPatchIn;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/team/{member_id}';
};

export type AdminAccTeamUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamUpdateError = AdminAccTeamUpdateErrors[keyof AdminAccTeamUpdateErrors];

export type AdminAccTeamUpdateResponses = {
    /**
     * Successful Response
     */
    200: MemberCard;
};

export type AdminAccTeamUpdateResponse = AdminAccTeamUpdateResponses[keyof AdminAccTeamUpdateResponses];

export type AdminAccTeamResetPasswordData = {
    body?: never;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/team/{member_id}/reset-password';
};

export type AdminAccTeamResetPasswordErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamResetPasswordError = AdminAccTeamResetPasswordErrors[keyof AdminAccTeamResetPasswordErrors];

export type AdminAccTeamResetPasswordResponses = {
    /**
     * Successful Response
     */
    200: PasswordResetOut;
};

export type AdminAccTeamResetPasswordResponse = AdminAccTeamResetPasswordResponses[keyof AdminAccTeamResetPasswordResponses];

export type AdminAccTeamBlockData = {
    body: MemberBlockIn;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/team/{member_id}/block';
};

export type AdminAccTeamBlockErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamBlockError = AdminAccTeamBlockErrors[keyof AdminAccTeamBlockErrors];

export type AdminAccTeamBlockResponses = {
    /**
     * Successful Response
     */
    200: MemberCard;
};

export type AdminAccTeamBlockResponse = AdminAccTeamBlockResponses[keyof AdminAccTeamBlockResponses];

export type AdminAccTeamUnblockData = {
    body?: never;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/team/{member_id}/unblock';
};

export type AdminAccTeamUnblockErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamUnblockError = AdminAccTeamUnblockErrors[keyof AdminAccTeamUnblockErrors];

export type AdminAccTeamUnblockResponses = {
    /**
     * Successful Response
     */
    200: MemberCard;
};

export type AdminAccTeamUnblockResponse = AdminAccTeamUnblockResponses[keyof AdminAccTeamUnblockResponses];

export type AdminAccTeamApplicationDecisionData = {
    body: ApplicationDecisionIn;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/team/{member_id}/application-decision';
};

export type AdminAccTeamApplicationDecisionErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamApplicationDecisionError = AdminAccTeamApplicationDecisionErrors[keyof AdminAccTeamApplicationDecisionErrors];

export type AdminAccTeamApplicationDecisionResponses = {
    /**
     * Successful Response
     */
    200: MemberCard;
};

export type AdminAccTeamApplicationDecisionResponse = AdminAccTeamApplicationDecisionResponses[keyof AdminAccTeamApplicationDecisionResponses];

export type AdminAccTeamOwnedSummaryData = {
    body?: never;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/team/{member_id}/owned-summary';
};

export type AdminAccTeamOwnedSummaryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamOwnedSummaryError = AdminAccTeamOwnedSummaryErrors[keyof AdminAccTeamOwnedSummaryErrors];

export type AdminAccTeamOwnedSummaryResponses = {
    /**
     * Successful Response
     */
    200: OwnedSummaryOut;
};

export type AdminAccTeamOwnedSummaryResponse = AdminAccTeamOwnedSummaryResponses[keyof AdminAccTeamOwnedSummaryResponses];

export type AdminAccTeamAuthorProfileSaveData = {
    body: AuthorProfileIn;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/team/{member_id}/author-profile';
};

export type AdminAccTeamAuthorProfileSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccTeamAuthorProfileSaveError = AdminAccTeamAuthorProfileSaveErrors[keyof AdminAccTeamAuthorProfileSaveErrors];

export type AdminAccTeamAuthorProfileSaveResponses = {
    /**
     * Successful Response
     */
    200: AuthorProfileOut;
};

export type AdminAccTeamAuthorProfileSaveResponse = AdminAccTeamAuthorProfileSaveResponses[keyof AdminAccTeamAuthorProfileSaveResponses];

export type AdminAccCareTicketsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: assignee, channel, created_at, number, priority, sla, sla_due_at, status, subject, topic, user, wait
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (new, in_progress, waiting_user, overdue, resolved, closed)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (new, in_progress, waiting_user, overdue, resolved, closed); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Archived]
         *
         * Архивные (по умолчанию скрыты)
         */
        'filter[archived]'?: string | null;
        /**
         * Filter[Archived][]
         *
         * Архивные (по умолчанию скрыты); несколько значений
         */
        'filter[archived][]'?: Array<string> | null;
        /**
         * Filter[Priority]
         *
         * Фильтр по priority (low, normal, high, urgent)
         */
        'filter[priority]'?: string | null;
        /**
         * Filter[Priority][]
         *
         * Фильтр по priority (low, normal, high, urgent); несколько значений
         */
        'filter[priority][]'?: Array<string> | null;
        /**
         * Filter[Topic]
         *
         * Фильтр по topic (order, pay, book, trip, learn, login, other)
         */
        'filter[topic]'?: string | null;
        /**
         * Filter[Topic][]
         *
         * Фильтр по topic (order, pay, book, trip, learn, login, other); несколько значений
         */
        'filter[topic][]'?: Array<string> | null;
        /**
         * Filter[Channel]
         *
         * Фильтр по channel (app, email, phone)
         */
        'filter[channel]'?: string | null;
        /**
         * Filter[Channel][]
         *
         * Фильтр по channel (app, email, phone); несколько значений
         */
        'filter[channel][]'?: Array<string> | null;
        /**
         * Filter[Assignee Id]
         *
         * Фильтр по assignee_id
         */
        'filter[assignee_id]'?: string | null;
        /**
         * Filter[Assignee Id][]
         *
         * Фильтр по assignee_id; несколько значений
         */
        'filter[assignee_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/care/tickets';
};

export type AdminAccCareTicketsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccCareTicketsListError = AdminAccCareTicketsListErrors[keyof AdminAccCareTicketsListErrors];

export type AdminAccCareTicketsListResponses = {
    /**
     * Successful Response
     */
    200: PageCareRow;
};

export type AdminAccCareTicketsListResponse = AdminAccCareTicketsListResponses[keyof AdminAccCareTicketsListResponses];

export type AdminAccCareTicketsCreateData = {
    body: AppModulesAccSchemasCareTicketCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/care/tickets';
};

export type AdminAccCareTicketsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccCareTicketsCreateError = AdminAccCareTicketsCreateErrors[keyof AdminAccCareTicketsCreateErrors];

export type AdminAccCareTicketsCreateResponses = {
    /**
     * Successful Response
     */
    201: CareTicket;
};

export type AdminAccCareTicketsCreateResponse = AdminAccCareTicketsCreateResponses[keyof AdminAccCareTicketsCreateResponses];

export type AdminAccCareDictionariesGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/care/dictionaries';
};

export type AdminAccCareDictionariesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
};

export type AdminAccCareDictionariesGetError = AdminAccCareDictionariesGetErrors[keyof AdminAccCareDictionariesGetErrors];

export type AdminAccCareDictionariesGetResponses = {
    /**
     * Successful Response
     */
    200: CareDictionariesOut;
};

export type AdminAccCareDictionariesGetResponse = AdminAccCareDictionariesGetResponses[keyof AdminAccCareDictionariesGetResponses];

export type AdminAccCareTicketsGetData = {
    body?: never;
    path: {
        /**
         * Ticket Id
         */
        ticket_id: string;
    };
    query?: never;
    url: '/api/v1/admin/care/tickets/{ticket_id}';
};

export type AdminAccCareTicketsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccCareTicketsGetError = AdminAccCareTicketsGetErrors[keyof AdminAccCareTicketsGetErrors];

export type AdminAccCareTicketsGetResponses = {
    /**
     * Successful Response
     */
    200: CareTicket;
};

export type AdminAccCareTicketsGetResponse = AdminAccCareTicketsGetResponses[keyof AdminAccCareTicketsGetResponses];

export type AdminAccCareTicketsUpdateData = {
    body: TicketPatchIn;
    path: {
        /**
         * Ticket Id
         */
        ticket_id: string;
    };
    query?: never;
    url: '/api/v1/admin/care/tickets/{ticket_id}';
};

export type AdminAccCareTicketsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccCareTicketsUpdateError = AdminAccCareTicketsUpdateErrors[keyof AdminAccCareTicketsUpdateErrors];

export type AdminAccCareTicketsUpdateResponses = {
    /**
     * Successful Response
     */
    200: CareTicket;
};

export type AdminAccCareTicketsUpdateResponse = AdminAccCareTicketsUpdateResponses[keyof AdminAccCareTicketsUpdateResponses];

export type AdminAccCareTicketsTakeData = {
    body?: never;
    path: {
        /**
         * Ticket Id
         */
        ticket_id: string;
    };
    query?: never;
    url: '/api/v1/admin/care/tickets/{ticket_id}/take';
};

export type AdminAccCareTicketsTakeErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccCareTicketsTakeError = AdminAccCareTicketsTakeErrors[keyof AdminAccCareTicketsTakeErrors];

export type AdminAccCareTicketsTakeResponses = {
    /**
     * Successful Response
     */
    200: CareTicket;
};

export type AdminAccCareTicketsTakeResponse = AdminAccCareTicketsTakeResponses[keyof AdminAccCareTicketsTakeResponses];

export type AdminAccCareTicketsReplyData = {
    body: TicketReplyIn;
    path: {
        /**
         * Ticket Id
         */
        ticket_id: string;
    };
    query?: never;
    url: '/api/v1/admin/care/tickets/{ticket_id}/reply';
};

export type AdminAccCareTicketsReplyErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccCareTicketsReplyError = AdminAccCareTicketsReplyErrors[keyof AdminAccCareTicketsReplyErrors];

export type AdminAccCareTicketsReplyResponses = {
    /**
     * Successful Response
     */
    200: CareTicket;
};

export type AdminAccCareTicketsReplyResponse = AdminAccCareTicketsReplyResponses[keyof AdminAccCareTicketsReplyResponses];

export type AdminAccCareTicketsTransitionData = {
    body: TicketTransitionIn;
    path: {
        /**
         * Ticket Id
         */
        ticket_id: string;
    };
    query?: never;
    url: '/api/v1/admin/care/tickets/{ticket_id}/transition';
};

export type AdminAccCareTicketsTransitionErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccCareTicketsTransitionError = AdminAccCareTicketsTransitionErrors[keyof AdminAccCareTicketsTransitionErrors];

export type AdminAccCareTicketsTransitionResponses = {
    /**
     * Successful Response
     */
    200: CareTicket;
};

export type AdminAccCareTicketsTransitionResponse = AdminAccCareTicketsTransitionResponses[keyof AdminAccCareTicketsTransitionResponses];

export type AdminAccCareTicketsArchiveData = {
    body: TicketArchiveIn;
    path: {
        /**
         * Ticket Id
         */
        ticket_id: string;
    };
    query?: never;
    url: '/api/v1/admin/care/tickets/{ticket_id}/archive';
};

export type AdminAccCareTicketsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccCareTicketsArchiveError = AdminAccCareTicketsArchiveErrors[keyof AdminAccCareTicketsArchiveErrors];

export type AdminAccCareTicketsArchiveResponses = {
    /**
     * Successful Response
     */
    200: CareTicket;
};

export type AdminAccCareTicketsArchiveResponse = AdminAccCareTicketsArchiveResponses[keyof AdminAccCareTicketsArchiveResponses];

export type AdminAccOnboardingScreensListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: name, position, status
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (shown, hidden)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (shown, hidden); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/onboarding/screens';
};

export type AdminAccOnboardingScreensListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccOnboardingScreensListError = AdminAccOnboardingScreensListErrors[keyof AdminAccOnboardingScreensListErrors];

export type AdminAccOnboardingScreensListResponses = {
    /**
     * Successful Response
     */
    200: PageOnboardingScreenRow;
};

export type AdminAccOnboardingScreensListResponse = AdminAccOnboardingScreensListResponses[keyof AdminAccOnboardingScreensListResponses];

export type AdminAccOnboardingScreensGetData = {
    body?: never;
    path: {
        /**
         * Screen Id
         */
        screen_id: string;
    };
    query?: never;
    url: '/api/v1/admin/onboarding/screens/{screen_id}';
};

export type AdminAccOnboardingScreensGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccOnboardingScreensGetError = AdminAccOnboardingScreensGetErrors[keyof AdminAccOnboardingScreensGetErrors];

export type AdminAccOnboardingScreensGetResponses = {
    /**
     * Successful Response
     */
    200: OnboardingScreenOut;
};

export type AdminAccOnboardingScreensGetResponse = AdminAccOnboardingScreensGetResponses[keyof AdminAccOnboardingScreensGetResponses];

export type AdminAccOnboardingScreensUpdateData = {
    body: OnboardingScreenPatchIn;
    path: {
        /**
         * Screen Id
         */
        screen_id: string;
    };
    query?: never;
    url: '/api/v1/admin/onboarding/screens/{screen_id}';
};

export type AdminAccOnboardingScreensUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccOnboardingScreensUpdateError = AdminAccOnboardingScreensUpdateErrors[keyof AdminAccOnboardingScreensUpdateErrors];

export type AdminAccOnboardingScreensUpdateResponses = {
    /**
     * Successful Response
     */
    200: OnboardingScreenOut;
};

export type AdminAccOnboardingScreensUpdateResponse = AdminAccOnboardingScreensUpdateResponses[keyof AdminAccOnboardingScreensUpdateResponses];

export type AdminAccOnboardingScreensMoveData = {
    body: OnboardingMoveIn;
    path: {
        /**
         * Screen Id
         */
        screen_id: string;
    };
    query?: never;
    url: '/api/v1/admin/onboarding/screens/{screen_id}/move';
};

export type AdminAccOnboardingScreensMoveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccOnboardingScreensMoveError = AdminAccOnboardingScreensMoveErrors[keyof AdminAccOnboardingScreensMoveErrors];

export type AdminAccOnboardingScreensMoveResponses = {
    /**
     * Successful Response
     */
    200: OnboardingMoveOut;
};

export type AdminAccOnboardingScreensMoveResponse = AdminAccOnboardingScreensMoveResponses[keyof AdminAccOnboardingScreensMoveResponses];

export type AdminAccOnboardingScreensVisibilityData = {
    body: OnboardingVisibilityIn;
    path: {
        /**
         * Screen Id
         */
        screen_id: string;
    };
    query?: never;
    url: '/api/v1/admin/onboarding/screens/{screen_id}/visibility';
};

export type AdminAccOnboardingScreensVisibilityErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccOnboardingScreensVisibilityError = AdminAccOnboardingScreensVisibilityErrors[keyof AdminAccOnboardingScreensVisibilityErrors];

export type AdminAccOnboardingScreensVisibilityResponses = {
    /**
     * Successful Response
     */
    200: OnboardingScreenOut;
};

export type AdminAccOnboardingScreensVisibilityResponse = AdminAccOnboardingScreensVisibilityResponses[keyof AdminAccOnboardingScreensVisibilityResponses];

export type AdminAccOnboardingItemsCreateData = {
    body: OnboardingItemCreateIn;
    path: {
        /**
         * Screen Id
         */
        screen_id: string;
    };
    query?: never;
    url: '/api/v1/admin/onboarding/screens/{screen_id}/items';
};

export type AdminAccOnboardingItemsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccOnboardingItemsCreateError = AdminAccOnboardingItemsCreateErrors[keyof AdminAccOnboardingItemsCreateErrors];

export type AdminAccOnboardingItemsCreateResponses = {
    /**
     * Successful Response
     */
    201: OnboardingItemOut;
};

export type AdminAccOnboardingItemsCreateResponse = AdminAccOnboardingItemsCreateResponses[keyof AdminAccOnboardingItemsCreateResponses];

export type AdminAccOnboardingItemsDeleteData = {
    body?: never;
    path: {
        /**
         * Item Id
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v1/admin/onboarding/items/{item_id}';
};

export type AdminAccOnboardingItemsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccOnboardingItemsDeleteError = AdminAccOnboardingItemsDeleteErrors[keyof AdminAccOnboardingItemsDeleteErrors];

export type AdminAccOnboardingItemsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminAccOnboardingItemsDeleteResponse = AdminAccOnboardingItemsDeleteResponses[keyof AdminAccOnboardingItemsDeleteResponses];

export type AdminAccOnboardingItemsUpdateData = {
    body: OnboardingItemPatchIn;
    path: {
        /**
         * Item Id
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v1/admin/onboarding/items/{item_id}';
};

export type AdminAccOnboardingItemsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminAccOnboardingItemsUpdateError = AdminAccOnboardingItemsUpdateErrors[keyof AdminAccOnboardingItemsUpdateErrors];

export type AdminAccOnboardingItemsUpdateResponses = {
    /**
     * Successful Response
     */
    200: OnboardingItemOut;
};

export type AdminAccOnboardingItemsUpdateResponse = AdminAccOnboardingItemsUpdateResponses[keyof AdminAccOnboardingItemsUpdateResponses];

export type AdminCoreUploadsCreateData = {
    body: UploadCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/uploads';
};

export type AdminCoreUploadsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AdminCoreUploadsCreateError = AdminCoreUploadsCreateErrors[keyof AdminCoreUploadsCreateErrors];

export type AdminCoreUploadsCreateResponses = {
    /**
     * Successful Response
     */
    201: UploadTicketOut;
};

export type AdminCoreUploadsCreateResponse = AdminCoreUploadsCreateResponses[keyof AdminCoreUploadsCreateResponses];

export type AdminCoreUploadsCompleteData = {
    body?: never;
    path: {
        /**
         * Upload Id
         */
        upload_id: string;
    };
    query?: never;
    url: '/api/v1/admin/uploads/{upload_id}/complete';
};

export type AdminCoreUploadsCompleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCoreUploadsCompleteError = AdminCoreUploadsCompleteErrors[keyof AdminCoreUploadsCompleteErrors];

export type AdminCoreUploadsCompleteResponses = {
    /**
     * Successful Response
     */
    200: FileOut;
};

export type AdminCoreUploadsCompleteResponse = AdminCoreUploadsCompleteResponses[keyof AdminCoreUploadsCompleteResponses];

export type AdminIntMailCapacityGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Recipients
         *
         * Число получателей
         */
        recipients?: number;
    };
    url: '/api/v1/admin/integrations/mail/capacity';
};

export type AdminIntMailCapacityGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntMailCapacityGetError = AdminIntMailCapacityGetErrors[keyof AdminIntMailCapacityGetErrors];

export type AdminIntMailCapacityGetResponses = {
    /**
     * Successful Response
     */
    200: CapacityOut;
};

export type AdminIntMailCapacityGetResponse = AdminIntMailCapacityGetResponses[keyof AdminIntMailCapacityGetResponses];

export type AdminIntIntegrationsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filter[Status]
         *
         * not_configured | connected | error | disabled
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Port]
         *
         * Порт: payment, auth, mail, …
         */
        'filter[port]'?: string | null;
        /**
         * Q
         *
         * Поиск по названию
         */
        q?: string | null;
    };
    url: '/api/v1/admin/integrations';
};

export type AdminIntIntegrationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntIntegrationsListError = AdminIntIntegrationsListErrors[keyof AdminIntIntegrationsListErrors];

export type AdminIntIntegrationsListResponses = {
    /**
     * Successful Response
     */
    200: IntegrationsPage;
};

export type AdminIntIntegrationsListResponse = AdminIntIntegrationsListResponses[keyof AdminIntIntegrationsListResponses];

export type AdminIntIntegrationsGetData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}';
};

export type AdminIntIntegrationsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntIntegrationsGetError = AdminIntIntegrationsGetErrors[keyof AdminIntIntegrationsGetErrors];

export type AdminIntIntegrationsGetResponses = {
    /**
     * Successful Response
     */
    200: IntegrationCard;
};

export type AdminIntIntegrationsGetResponse = AdminIntIntegrationsGetResponses[keyof AdminIntIntegrationsGetResponses];

export type AdminIntIntegrationsUpdateData = {
    body: IntegrationUpdateIn;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}';
};

export type AdminIntIntegrationsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntIntegrationsUpdateError = AdminIntIntegrationsUpdateErrors[keyof AdminIntIntegrationsUpdateErrors];

export type AdminIntIntegrationsUpdateResponses = {
    /**
     * Successful Response
     */
    200: IntegrationSaveOut;
};

export type AdminIntIntegrationsUpdateResponse = AdminIntIntegrationsUpdateResponses[keyof AdminIntIntegrationsUpdateResponses];

export type AdminIntIntegrationsTestData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/test';
};

export type AdminIntIntegrationsTestErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntIntegrationsTestError = AdminIntIntegrationsTestErrors[keyof AdminIntIntegrationsTestErrors];

export type AdminIntIntegrationsTestResponses = {
    /**
     * Successful Response
     */
    200: CheckResultOut;
};

export type AdminIntIntegrationsTestResponse = AdminIntIntegrationsTestResponses[keyof AdminIntIntegrationsTestResponses];

export type AdminIntIntegrationsEnableData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/enable';
};

export type AdminIntIntegrationsEnableErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntIntegrationsEnableError = AdminIntIntegrationsEnableErrors[keyof AdminIntIntegrationsEnableErrors];

export type AdminIntIntegrationsEnableResponses = {
    /**
     * Successful Response
     */
    200: IntegrationCard;
};

export type AdminIntIntegrationsEnableResponse = AdminIntIntegrationsEnableResponses[keyof AdminIntIntegrationsEnableResponses];

export type AdminIntIntegrationsDisableData = {
    body: ConfirmIn;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/disable';
};

export type AdminIntIntegrationsDisableErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntIntegrationsDisableError = AdminIntIntegrationsDisableErrors[keyof AdminIntIntegrationsDisableErrors];

export type AdminIntIntegrationsDisableResponses = {
    /**
     * Successful Response
     */
    200: IntegrationCard;
};

export type AdminIntIntegrationsDisableResponse = AdminIntIntegrationsDisableResponses[keyof AdminIntIntegrationsDisableResponses];

export type AdminIntIntegrationsActivateData = {
    body: ConfirmIn;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/activate';
};

export type AdminIntIntegrationsActivateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntIntegrationsActivateError = AdminIntIntegrationsActivateErrors[keyof AdminIntIntegrationsActivateErrors];

export type AdminIntIntegrationsActivateResponses = {
    /**
     * Successful Response
     */
    200: IntegrationCard;
};

export type AdminIntIntegrationsActivateResponse = AdminIntIntegrationsActivateResponses[keyof AdminIntIntegrationsActivateResponses];

export type AdminIntIntegrationsActionData = {
    /**
     * Body
     */
    body?: IntegrationActionIn | null;
    path: {
        /**
         * Key
         */
        key: string;
        /**
         * Action
         */
        action: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/actions/{action}';
};

export type AdminIntIntegrationsActionErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntIntegrationsActionError = AdminIntIntegrationsActionErrors[keyof AdminIntIntegrationsActionErrors];

export type AdminIntIntegrationsActionResponses = {
    /**
     * Successful Response
     */
    200: ActionResultOut;
};

export type AdminIntIntegrationsActionResponse = AdminIntIntegrationsActionResponses[keyof AdminIntIntegrationsActionResponses];

export type AdminIntAlertsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: first_seen_at, last_seen_at, occurrences, severity
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (open, resolved)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (open, resolved); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Integration Key]
         *
         * Фильтр по integration_key
         */
        'filter[integration_key]'?: string | null;
        /**
         * Filter[Integration Key][]
         *
         * Фильтр по integration_key; несколько значений
         */
        'filter[integration_key][]'?: Array<string> | null;
        /**
         * Filter[Severity]
         *
         * Фильтр по severity (critical, warning)
         */
        'filter[severity]'?: string | null;
        /**
         * Filter[Severity][]
         *
         * Фильтр по severity (critical, warning); несколько значений
         */
        'filter[severity][]'?: Array<string> | null;
        /**
         * Filter[Type]
         *
         * Фильтр по type
         */
        'filter[type]'?: string | null;
        /**
         * Filter[Type][]
         *
         * Фильтр по type; несколько значений
         */
        'filter[type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/integration-alerts';
};

export type AdminIntAlertsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntAlertsListError = AdminIntAlertsListErrors[keyof AdminIntAlertsListErrors];

export type AdminIntAlertsListResponses = {
    /**
     * Successful Response
     */
    200: PageAlertOut;
};

export type AdminIntAlertsListResponse = AdminIntAlertsListResponses[keyof AdminIntAlertsListResponses];

export type AdminIntAlertsResolveData = {
    /**
     * Body
     */
    body?: AlertResolveIn | null;
    path: {
        /**
         * Alert Id
         */
        alert_id: string;
    };
    query?: never;
    url: '/api/v1/admin/integration-alerts/{alert_id}/resolve';
};

export type AdminIntAlertsResolveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntAlertsResolveError = AdminIntAlertsResolveErrors[keyof AdminIntAlertsResolveErrors];

export type AdminIntAlertsResolveResponses = {
    /**
     * Successful Response
     */
    200: AlertOut;
};

export type AdminIntAlertsResolveResponse = AdminIntAlertsResolveResponses[keyof AdminIntAlertsResolveResponses];

export type AdminIntLogsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: duration_ms, requested_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Integration Key]
         *
         * Фильтр по integration_key
         */
        'filter[integration_key]'?: string | null;
        /**
         * Filter[Integration Key][]
         *
         * Фильтр по integration_key; несколько значений
         */
        'filter[integration_key][]'?: Array<string> | null;
        /**
         * Filter[Direction]
         *
         * Фильтр по direction (outbound, inbound, internal, job)
         */
        'filter[direction]'?: string | null;
        /**
         * Filter[Direction][]
         *
         * Фильтр по direction (outbound, inbound, internal, job); несколько значений
         */
        'filter[direction][]'?: Array<string> | null;
        /**
         * Filter[Result]
         *
         * Фильтр по result (success, provider_error, client_error, server_error, timeout, network_error, rejected, duplicate, skipped)
         */
        'filter[result]'?: string | null;
        /**
         * Filter[Result][]
         *
         * Фильтр по result (success, provider_error, client_error, server_error, timeout, network_error, rejected, duplicate, skipped); несколько значений
         */
        'filter[result][]'?: Array<string> | null;
        /**
         * Filter[Operation]
         *
         * Фильтр по operation
         */
        'filter[operation]'?: string | null;
        /**
         * Filter[Operation][]
         *
         * Фильтр по operation; несколько значений
         */
        'filter[operation][]'?: Array<string> | null;
        /**
         * Filter[Environment]
         *
         * Фильтр по environment (test, prod)
         */
        'filter[environment]'?: string | null;
        /**
         * Filter[Environment][]
         *
         * Фильтр по environment (test, prod); несколько значений
         */
        'filter[environment][]'?: Array<string> | null;
        /**
         * Filter[Category]
         *
         * Фильтр по category (financial, technical)
         */
        'filter[category]'?: string | null;
        /**
         * Filter[Category][]
         *
         * Фильтр по category (financial, technical); несколько значений
         */
        'filter[category][]'?: Array<string> | null;
        /**
         * Filter[From]
         *
         * Время запроса с (ISO 8601)
         */
        'filter[from]'?: string | null;
        /**
         * Filter[From][]
         *
         * Время запроса с (ISO 8601); несколько значений
         */
        'filter[from][]'?: Array<string> | null;
        /**
         * Filter[To]
         *
         * Время запроса по (ISO 8601)
         */
        'filter[to]'?: string | null;
        /**
         * Filter[To][]
         *
         * Время запроса по (ISO 8601); несколько значений
         */
        'filter[to][]'?: Array<string> | null;
        /**
         * Filter[Trace Id]
         *
         * Фильтр по trace_id
         */
        'filter[trace_id]'?: string | null;
        /**
         * Filter[Trace Id][]
         *
         * Фильтр по trace_id; несколько значений
         */
        'filter[trace_id][]'?: Array<string> | null;
        /**
         * Filter[Order Id]
         *
         * Фильтр по order_id
         */
        'filter[order_id]'?: string | null;
        /**
         * Filter[Order Id][]
         *
         * Фильтр по order_id; несколько значений
         */
        'filter[order_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Entity Type]
         *
         * Фильтр по entity_type
         */
        'filter[entity_type]'?: string | null;
        /**
         * Filter[Entity Type][]
         *
         * Фильтр по entity_type; несколько значений
         */
        'filter[entity_type][]'?: Array<string> | null;
        /**
         * Filter[Entity Id]
         *
         * Фильтр по entity_id
         */
        'filter[entity_id]'?: string | null;
        /**
         * Filter[Entity Id][]
         *
         * Фильтр по entity_id; несколько значений
         */
        'filter[entity_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/integration-logs';
};

export type AdminIntLogsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntLogsListError = AdminIntLogsListErrors[keyof AdminIntLogsListErrors];

export type AdminIntLogsListResponses = {
    /**
     * Successful Response
     */
    200: LogsPage;
};

export type AdminIntLogsListResponse = AdminIntLogsListResponses[keyof AdminIntLogsListResponses];

export type AdminIntLogsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: duration_ms, requested_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Integration Key]
         *
         * Фильтр по integration_key
         */
        'filter[integration_key]'?: string | null;
        /**
         * Filter[Integration Key][]
         *
         * Фильтр по integration_key; несколько значений
         */
        'filter[integration_key][]'?: Array<string> | null;
        /**
         * Filter[Direction]
         *
         * Фильтр по direction (outbound, inbound, internal, job)
         */
        'filter[direction]'?: string | null;
        /**
         * Filter[Direction][]
         *
         * Фильтр по direction (outbound, inbound, internal, job); несколько значений
         */
        'filter[direction][]'?: Array<string> | null;
        /**
         * Filter[Result]
         *
         * Фильтр по result (success, provider_error, client_error, server_error, timeout, network_error, rejected, duplicate, skipped)
         */
        'filter[result]'?: string | null;
        /**
         * Filter[Result][]
         *
         * Фильтр по result (success, provider_error, client_error, server_error, timeout, network_error, rejected, duplicate, skipped); несколько значений
         */
        'filter[result][]'?: Array<string> | null;
        /**
         * Filter[Operation]
         *
         * Фильтр по operation
         */
        'filter[operation]'?: string | null;
        /**
         * Filter[Operation][]
         *
         * Фильтр по operation; несколько значений
         */
        'filter[operation][]'?: Array<string> | null;
        /**
         * Filter[Environment]
         *
         * Фильтр по environment (test, prod)
         */
        'filter[environment]'?: string | null;
        /**
         * Filter[Environment][]
         *
         * Фильтр по environment (test, prod); несколько значений
         */
        'filter[environment][]'?: Array<string> | null;
        /**
         * Filter[Category]
         *
         * Фильтр по category (financial, technical)
         */
        'filter[category]'?: string | null;
        /**
         * Filter[Category][]
         *
         * Фильтр по category (financial, technical); несколько значений
         */
        'filter[category][]'?: Array<string> | null;
        /**
         * Filter[From]
         *
         * Время запроса с (ISO 8601)
         */
        'filter[from]'?: string | null;
        /**
         * Filter[From][]
         *
         * Время запроса с (ISO 8601); несколько значений
         */
        'filter[from][]'?: Array<string> | null;
        /**
         * Filter[To]
         *
         * Время запроса по (ISO 8601)
         */
        'filter[to]'?: string | null;
        /**
         * Filter[To][]
         *
         * Время запроса по (ISO 8601); несколько значений
         */
        'filter[to][]'?: Array<string> | null;
        /**
         * Filter[Trace Id]
         *
         * Фильтр по trace_id
         */
        'filter[trace_id]'?: string | null;
        /**
         * Filter[Trace Id][]
         *
         * Фильтр по trace_id; несколько значений
         */
        'filter[trace_id][]'?: Array<string> | null;
        /**
         * Filter[Order Id]
         *
         * Фильтр по order_id
         */
        'filter[order_id]'?: string | null;
        /**
         * Filter[Order Id][]
         *
         * Фильтр по order_id; несколько значений
         */
        'filter[order_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Entity Type]
         *
         * Фильтр по entity_type
         */
        'filter[entity_type]'?: string | null;
        /**
         * Filter[Entity Type][]
         *
         * Фильтр по entity_type; несколько значений
         */
        'filter[entity_type][]'?: Array<string> | null;
        /**
         * Filter[Entity Id]
         *
         * Фильтр по entity_id
         */
        'filter[entity_id]'?: string | null;
        /**
         * Filter[Entity Id][]
         *
         * Фильтр по entity_id; несколько значений
         */
        'filter[entity_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/integration-logs/export';
};

export type AdminIntLogsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntLogsExportError = AdminIntLogsExportErrors[keyof AdminIntLogsExportErrors];

export type AdminIntLogsExportResponses = {
    /**
     * CSV текущей выборки
     */
    200: unknown;
};

export type AdminIntLogsGetData = {
    body?: never;
    path: {
        /**
         * Log Id
         */
        log_id: string;
    };
    query?: never;
    url: '/api/v1/admin/integration-logs/{log_id}';
};

export type AdminIntLogsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntLogsGetError = AdminIntLogsGetErrors[keyof AdminIntLogsGetErrors];

export type AdminIntLogsGetResponses = {
    /**
     * Successful Response
     */
    200: LogCard;
};

export type AdminIntLogsGetResponse = AdminIntLogsGetResponses[keyof AdminIntLogsGetResponses];

export type AdminIntMailSuppressionsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, email
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Reason]
         *
         * Фильтр по reason (hard_bounce, complaint, unsubscribed, relay_disabled, manual)
         */
        'filter[reason]'?: string | null;
        /**
         * Filter[Reason][]
         *
         * Фильтр по reason (hard_bounce, complaint, unsubscribed, relay_disabled, manual); несколько значений
         */
        'filter[reason][]'?: Array<string> | null;
        /**
         * Filter[Scope]
         *
         * Фильтр по scope (all, marketing)
         */
        'filter[scope]'?: string | null;
        /**
         * Filter[Scope][]
         *
         * Фильтр по scope (all, marketing); несколько значений
         */
        'filter[scope][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/mail/suppressions';
};

export type AdminIntMailSuppressionsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntMailSuppressionsListError = AdminIntMailSuppressionsListErrors[keyof AdminIntMailSuppressionsListErrors];

export type AdminIntMailSuppressionsListResponses = {
    /**
     * Successful Response
     */
    200: PageSuppressionOut;
};

export type AdminIntMailSuppressionsListResponse = AdminIntMailSuppressionsListResponses[keyof AdminIntMailSuppressionsListResponses];

export type AdminIntMailSuppressionsCreateData = {
    body: SuppressionCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/mail/suppressions';
};

export type AdminIntMailSuppressionsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntMailSuppressionsCreateError = AdminIntMailSuppressionsCreateErrors[keyof AdminIntMailSuppressionsCreateErrors];

export type AdminIntMailSuppressionsCreateResponses = {
    /**
     * Successful Response
     */
    201: SuppressionOut;
};

export type AdminIntMailSuppressionsCreateResponse = AdminIntMailSuppressionsCreateResponses[keyof AdminIntMailSuppressionsCreateResponses];

export type AdminIntMailSuppressionsDeleteData = {
    body?: never;
    path: {
        /**
         * Suppression Id
         */
        suppression_id: string;
    };
    query?: never;
    url: '/api/v1/admin/mail/suppressions/{suppression_id}';
};

export type AdminIntMailSuppressionsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntMailSuppressionsDeleteError = AdminIntMailSuppressionsDeleteErrors[keyof AdminIntMailSuppressionsDeleteErrors];

export type AdminIntMailSuppressionsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminIntMailSuppressionsDeleteResponse = AdminIntMailSuppressionsDeleteResponses[keyof AdminIntMailSuppressionsDeleteResponses];

export type AdminGeoDictionariesRegionsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Country Id
         *
         * Только регионы страны
         */
        country_id?: string | null;
    };
    url: '/api/v1/admin/dictionaries/regions';
};

export type AdminGeoDictionariesRegionsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoDictionariesRegionsListError = AdminGeoDictionariesRegionsListErrors[keyof AdminGeoDictionariesRegionsListErrors];

export type AdminGeoDictionariesRegionsListResponses = {
    /**
     * Successful Response
     */
    200: RegionDictionaryOut;
};

export type AdminGeoDictionariesRegionsListResponse = AdminGeoDictionariesRegionsListResponses[keyof AdminGeoDictionariesRegionsListResponses];

export type AdminGeoSystemCountriesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Q
         *
         * Поиск по названию
         */
        q?: string | null;
    };
    url: '/api/v1/admin/system-countries';
};

export type AdminGeoSystemCountriesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoSystemCountriesListError = AdminGeoSystemCountriesListErrors[keyof AdminGeoSystemCountriesListErrors];

export type AdminGeoSystemCountriesListResponses = {
    /**
     * Successful Response
     */
    200: SystemCountryListOut;
};

export type AdminGeoSystemCountriesListResponse = AdminGeoSystemCountriesListResponses[keyof AdminGeoSystemCountriesListResponses];

export type AdminGeoCountriesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, name, regions_count, sort_order
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Code]
         *
         * Код страны
         */
        'filter[code]'?: string | null;
        /**
         * Filter[Code][]
         *
         * Код страны; несколько значений
         */
        'filter[code][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/countries';
};

export type AdminGeoCountriesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountriesListError = AdminGeoCountriesListErrors[keyof AdminGeoCountriesListErrors];

export type AdminGeoCountriesListResponses = {
    /**
     * Successful Response
     */
    200: PageCountryRowOut;
};

export type AdminGeoCountriesListResponse = AdminGeoCountriesListResponses[keyof AdminGeoCountriesListResponses];

export type AdminGeoCountriesCreateData = {
    body: CountryCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/countries';
};

export type AdminGeoCountriesCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountriesCreateError = AdminGeoCountriesCreateErrors[keyof AdminGeoCountriesCreateErrors];

export type AdminGeoCountriesCreateResponses = {
    /**
     * Successful Response
     */
    201: CountryDetailOut;
};

export type AdminGeoCountriesCreateResponse = AdminGeoCountriesCreateResponses[keyof AdminGeoCountriesCreateResponses];

export type AdminGeoCountriesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, name, regions_count, sort_order
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Code]
         *
         * Код страны
         */
        'filter[code]'?: string | null;
        /**
         * Filter[Code][]
         *
         * Код страны; несколько значений
         */
        'filter[code][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/countries/export';
};

export type AdminGeoCountriesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountriesExportError = AdminGeoCountriesExportErrors[keyof AdminGeoCountriesExportErrors];

export type AdminGeoCountriesExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGeoCountriesDeleteData = {
    body: AppModulesGeoSchemasAdminConfirmNameIn;
    path: {
        /**
         * Country Id
         */
        country_id: string;
    };
    query?: never;
    url: '/api/v1/admin/countries/{country_id}';
};

export type AdminGeoCountriesDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountriesDeleteError = AdminGeoCountriesDeleteErrors[keyof AdminGeoCountriesDeleteErrors];

export type AdminGeoCountriesDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoCountriesDeleteResponse = AdminGeoCountriesDeleteResponses[keyof AdminGeoCountriesDeleteResponses];

export type AdminGeoCountriesGetData = {
    body?: never;
    path: {
        /**
         * Country Id
         */
        country_id: string;
    };
    query?: never;
    url: '/api/v1/admin/countries/{country_id}';
};

export type AdminGeoCountriesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountriesGetError = AdminGeoCountriesGetErrors[keyof AdminGeoCountriesGetErrors];

export type AdminGeoCountriesGetResponses = {
    /**
     * Successful Response
     */
    200: CountryDetailOut;
};

export type AdminGeoCountriesGetResponse = AdminGeoCountriesGetResponses[keyof AdminGeoCountriesGetResponses];

export type AdminGeoCountriesUpdateData = {
    body: CountryPatchIn;
    path: {
        /**
         * Country Id
         */
        country_id: string;
    };
    query?: never;
    url: '/api/v1/admin/countries/{country_id}';
};

export type AdminGeoCountriesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountriesUpdateError = AdminGeoCountriesUpdateErrors[keyof AdminGeoCountriesUpdateErrors];

export type AdminGeoCountriesUpdateResponses = {
    /**
     * Successful Response
     */
    200: CountryDetailOut;
};

export type AdminGeoCountriesUpdateResponse = AdminGeoCountriesUpdateResponses[keyof AdminGeoCountriesUpdateResponses];

export type AdminGeoCountryFactsCreateData = {
    body: FactIn;
    path: {
        /**
         * Country Id
         */
        country_id: string;
    };
    query?: never;
    url: '/api/v1/admin/countries/{country_id}/facts';
};

export type AdminGeoCountryFactsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountryFactsCreateError = AdminGeoCountryFactsCreateErrors[keyof AdminGeoCountryFactsCreateErrors];

export type AdminGeoCountryFactsCreateResponses = {
    /**
     * Successful Response
     */
    201: AppModulesGeoSchemasAdminFactOut;
};

export type AdminGeoCountryFactsCreateResponse = AdminGeoCountryFactsCreateResponses[keyof AdminGeoCountryFactsCreateResponses];

export type AdminGeoCountryFactsDeleteData = {
    body?: never;
    path: {
        /**
         * Country Id
         */
        country_id: string;
        /**
         * Fact Id
         */
        fact_id: string;
    };
    query?: never;
    url: '/api/v1/admin/countries/{country_id}/facts/{fact_id}';
};

export type AdminGeoCountryFactsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountryFactsDeleteError = AdminGeoCountryFactsDeleteErrors[keyof AdminGeoCountryFactsDeleteErrors];

export type AdminGeoCountryFactsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoCountryFactsDeleteResponse = AdminGeoCountryFactsDeleteResponses[keyof AdminGeoCountryFactsDeleteResponses];

export type AdminGeoCountryFactsUpdateData = {
    body: FactPatchIn;
    path: {
        /**
         * Country Id
         */
        country_id: string;
        /**
         * Fact Id
         */
        fact_id: string;
    };
    query?: never;
    url: '/api/v1/admin/countries/{country_id}/facts/{fact_id}';
};

export type AdminGeoCountryFactsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountryFactsUpdateError = AdminGeoCountryFactsUpdateErrors[keyof AdminGeoCountryFactsUpdateErrors];

export type AdminGeoCountryFactsUpdateResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAdminFactOut;
};

export type AdminGeoCountryFactsUpdateResponse = AdminGeoCountryFactsUpdateResponses[keyof AdminGeoCountryFactsUpdateResponses];

export type AdminGeoCountryQuizzesAttachData = {
    body: QuizzesAttachIn;
    path: {
        /**
         * Country Id
         */
        country_id: string;
    };
    query?: never;
    url: '/api/v1/admin/countries/{country_id}/quizzes';
};

export type AdminGeoCountryQuizzesAttachErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountryQuizzesAttachError = AdminGeoCountryQuizzesAttachErrors[keyof AdminGeoCountryQuizzesAttachErrors];

export type AdminGeoCountryQuizzesAttachResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoCountryQuizzesAttachResponse = AdminGeoCountryQuizzesAttachResponses[keyof AdminGeoCountryQuizzesAttachResponses];

export type AdminGeoCountryQuizzesDetachData = {
    body?: never;
    path: {
        /**
         * Country Id
         */
        country_id: string;
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/countries/{country_id}/quizzes/{test_id}';
};

export type AdminGeoCountryQuizzesDetachErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoCountryQuizzesDetachError = AdminGeoCountryQuizzesDetachErrors[keyof AdminGeoCountryQuizzesDetachErrors];

export type AdminGeoCountryQuizzesDetachResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoCountryQuizzesDetachResponse = AdminGeoCountryQuizzesDetachResponses[keyof AdminGeoCountryQuizzesDetachResponses];

export type AdminGeoOriginsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: code, country, created_at, full_name, level
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Country Id]
         *
         * Фильтр по country_id
         */
        'filter[country_id]'?: string | null;
        /**
         * Filter[Country Id][]
         *
         * Фильтр по country_id; несколько значений
         */
        'filter[country_id][]'?: Array<string> | null;
        /**
         * Filter[Level]
         *
         * Фильтр по level (national, highest, regional, local, land)
         */
        'filter[level]'?: string | null;
        /**
         * Filter[Level][]
         *
         * Фильтр по level (national, highest, regional, local, land); несколько значений
         */
        'filter[level][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/origins';
};

export type AdminGeoOriginsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoOriginsListError = AdminGeoOriginsListErrors[keyof AdminGeoOriginsListErrors];

export type AdminGeoOriginsListResponses = {
    /**
     * Successful Response
     */
    200: PageOriginOut;
};

export type AdminGeoOriginsListResponse = AdminGeoOriginsListResponses[keyof AdminGeoOriginsListResponses];

export type AdminGeoOriginsCreateData = {
    body: OriginCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/origins';
};

export type AdminGeoOriginsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoOriginsCreateError = AdminGeoOriginsCreateErrors[keyof AdminGeoOriginsCreateErrors];

export type AdminGeoOriginsCreateResponses = {
    /**
     * Successful Response
     */
    201: OriginOut;
};

export type AdminGeoOriginsCreateResponse = AdminGeoOriginsCreateResponses[keyof AdminGeoOriginsCreateResponses];

export type AdminGeoOriginsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: code, country, created_at, full_name, level
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Country Id]
         *
         * Фильтр по country_id
         */
        'filter[country_id]'?: string | null;
        /**
         * Filter[Country Id][]
         *
         * Фильтр по country_id; несколько значений
         */
        'filter[country_id][]'?: Array<string> | null;
        /**
         * Filter[Level]
         *
         * Фильтр по level (national, highest, regional, local, land)
         */
        'filter[level]'?: string | null;
        /**
         * Filter[Level][]
         *
         * Фильтр по level (national, highest, regional, local, land); несколько значений
         */
        'filter[level][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/origins/export';
};

export type AdminGeoOriginsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoOriginsExportError = AdminGeoOriginsExportErrors[keyof AdminGeoOriginsExportErrors];

export type AdminGeoOriginsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGeoOriginsUpdateData = {
    body: OriginPatchIn;
    path: {
        /**
         * Origin Id
         */
        origin_id: string;
    };
    query?: never;
    url: '/api/v1/admin/origins/{origin_id}';
};

export type AdminGeoOriginsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoOriginsUpdateError = AdminGeoOriginsUpdateErrors[keyof AdminGeoOriginsUpdateErrors];

export type AdminGeoOriginsUpdateResponses = {
    /**
     * Successful Response
     */
    200: OriginOut;
};

export type AdminGeoOriginsUpdateResponse = AdminGeoOriginsUpdateResponses[keyof AdminGeoOriginsUpdateResponses];

export type AdminGeoMapZonesListData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Country Id
         */
        country_id: string;
    };
    url: '/api/v1/admin/map-zones';
};

export type AdminGeoMapZonesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoMapZonesListError = AdminGeoMapZonesListErrors[keyof AdminGeoMapZonesListErrors];

export type AdminGeoMapZonesListResponses = {
    /**
     * Successful Response
     */
    200: MapZoneListOut;
};

export type AdminGeoMapZonesListResponse = AdminGeoMapZonesListResponses[keyof AdminGeoMapZonesListResponses];

export type AdminGeoRegionsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: country, created_at, name, sort_order, status, updated_at, wineries
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Country Id]
         *
         * Фильтр по country_id
         */
        'filter[country_id]'?: string | null;
        /**
         * Filter[Country Id][]
         *
         * Фильтр по country_id; несколько значений
         */
        'filter[country_id][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, published, hidden, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, published, hidden, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Map Zone Id]
         *
         * Фильтр по map_zone_id
         */
        'filter[map_zone_id]'?: string | null;
        /**
         * Filter[Map Zone Id][]
         *
         * Фильтр по map_zone_id; несколько значений
         */
        'filter[map_zone_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/regions';
};

export type AdminGeoRegionsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsListError = AdminGeoRegionsListErrors[keyof AdminGeoRegionsListErrors];

export type AdminGeoRegionsListResponses = {
    /**
     * Successful Response
     */
    200: PageRegionRowOut;
};

export type AdminGeoRegionsListResponse = AdminGeoRegionsListResponses[keyof AdminGeoRegionsListResponses];

export type AdminGeoRegionsCreateData = {
    body: RegionCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/regions';
};

export type AdminGeoRegionsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsCreateError = AdminGeoRegionsCreateErrors[keyof AdminGeoRegionsCreateErrors];

export type AdminGeoRegionsCreateResponses = {
    /**
     * Successful Response
     */
    201: RegionDetailOut;
};

export type AdminGeoRegionsCreateResponse = AdminGeoRegionsCreateResponses[keyof AdminGeoRegionsCreateResponses];

export type AdminGeoRegionsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: country, created_at, name, sort_order, status, updated_at, wineries
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Country Id]
         *
         * Фильтр по country_id
         */
        'filter[country_id]'?: string | null;
        /**
         * Filter[Country Id][]
         *
         * Фильтр по country_id; несколько значений
         */
        'filter[country_id][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, published, hidden, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, published, hidden, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Map Zone Id]
         *
         * Фильтр по map_zone_id
         */
        'filter[map_zone_id]'?: string | null;
        /**
         * Filter[Map Zone Id][]
         *
         * Фильтр по map_zone_id; несколько значений
         */
        'filter[map_zone_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/regions/export';
};

export type AdminGeoRegionsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsExportError = AdminGeoRegionsExportErrors[keyof AdminGeoRegionsExportErrors];

export type AdminGeoRegionsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGeoRegionsDeleteData = {
    body: AppModulesGeoSchemasAdminConfirmNameIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}';
};

export type AdminGeoRegionsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsDeleteError = AdminGeoRegionsDeleteErrors[keyof AdminGeoRegionsDeleteErrors];

export type AdminGeoRegionsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoRegionsDeleteResponse = AdminGeoRegionsDeleteResponses[keyof AdminGeoRegionsDeleteResponses];

export type AdminGeoRegionsGetData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}';
};

export type AdminGeoRegionsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsGetError = AdminGeoRegionsGetErrors[keyof AdminGeoRegionsGetErrors];

export type AdminGeoRegionsGetResponses = {
    /**
     * Successful Response
     */
    200: RegionDetailOut;
};

export type AdminGeoRegionsGetResponse = AdminGeoRegionsGetResponses[keyof AdminGeoRegionsGetResponses];

export type AdminGeoRegionsUpdateData = {
    body: RegionPatchIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}';
};

export type AdminGeoRegionsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsUpdateError = AdminGeoRegionsUpdateErrors[keyof AdminGeoRegionsUpdateErrors];

export type AdminGeoRegionsUpdateResponses = {
    /**
     * Successful Response
     */
    200: RegionDetailOut;
};

export type AdminGeoRegionsUpdateResponse = AdminGeoRegionsUpdateResponses[keyof AdminGeoRegionsUpdateResponses];

export type AdminGeoRegionsContourSetData = {
    body: ContourIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/contour';
};

export type AdminGeoRegionsContourSetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsContourSetError = AdminGeoRegionsContourSetErrors[keyof AdminGeoRegionsContourSetErrors];

export type AdminGeoRegionsContourSetResponses = {
    /**
     * Successful Response
     */
    200: RegionDetailOut;
};

export type AdminGeoRegionsContourSetResponse = AdminGeoRegionsContourSetResponses[keyof AdminGeoRegionsContourSetResponses];

export type AdminGeoRegionsPublishData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/publish';
};

export type AdminGeoRegionsPublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsPublishError = AdminGeoRegionsPublishErrors[keyof AdminGeoRegionsPublishErrors];

export type AdminGeoRegionsPublishResponses = {
    /**
     * Successful Response
     */
    200: RegionDetailOut;
};

export type AdminGeoRegionsPublishResponse = AdminGeoRegionsPublishResponses[keyof AdminGeoRegionsPublishResponses];

export type AdminGeoRegionsUnpublishData = {
    body: UnpublishRegionIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/unpublish';
};

export type AdminGeoRegionsUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsUnpublishError = AdminGeoRegionsUnpublishErrors[keyof AdminGeoRegionsUnpublishErrors];

export type AdminGeoRegionsUnpublishResponses = {
    /**
     * Successful Response
     */
    200: RegionDetailOut;
};

export type AdminGeoRegionsUnpublishResponse = AdminGeoRegionsUnpublishResponses[keyof AdminGeoRegionsUnpublishResponses];

export type AdminGeoRegionsArchiveData = {
    body: AppModulesGeoSchemasAdminArchiveIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/archive';
};

export type AdminGeoRegionsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsArchiveError = AdminGeoRegionsArchiveErrors[keyof AdminGeoRegionsArchiveErrors];

export type AdminGeoRegionsArchiveResponses = {
    /**
     * Successful Response
     */
    200: RegionDetailOut;
};

export type AdminGeoRegionsArchiveResponse = AdminGeoRegionsArchiveResponses[keyof AdminGeoRegionsArchiveResponses];

export type AdminGeoRegionsRestoreData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/restore';
};

export type AdminGeoRegionsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsRestoreError = AdminGeoRegionsRestoreErrors[keyof AdminGeoRegionsRestoreErrors];

export type AdminGeoRegionsRestoreResponses = {
    /**
     * Successful Response
     */
    200: RegionDetailOut;
};

export type AdminGeoRegionsRestoreResponse = AdminGeoRegionsRestoreResponses[keyof AdminGeoRegionsRestoreResponses];

export type AdminGeoRegionFactsCreateData = {
    body: FactIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/facts';
};

export type AdminGeoRegionFactsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionFactsCreateError = AdminGeoRegionFactsCreateErrors[keyof AdminGeoRegionFactsCreateErrors];

export type AdminGeoRegionFactsCreateResponses = {
    /**
     * Successful Response
     */
    201: AppModulesGeoSchemasAdminFactOut;
};

export type AdminGeoRegionFactsCreateResponse = AdminGeoRegionFactsCreateResponses[keyof AdminGeoRegionFactsCreateResponses];

export type AdminGeoRegionFactsDeleteData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
        /**
         * Fact Id
         */
        fact_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/facts/{fact_id}';
};

export type AdminGeoRegionFactsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionFactsDeleteError = AdminGeoRegionFactsDeleteErrors[keyof AdminGeoRegionFactsDeleteErrors];

export type AdminGeoRegionFactsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoRegionFactsDeleteResponse = AdminGeoRegionFactsDeleteResponses[keyof AdminGeoRegionFactsDeleteResponses];

export type AdminGeoRegionFactsUpdateData = {
    body: FactPatchIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
        /**
         * Fact Id
         */
        fact_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/facts/{fact_id}';
};

export type AdminGeoRegionFactsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionFactsUpdateError = AdminGeoRegionFactsUpdateErrors[keyof AdminGeoRegionFactsUpdateErrors];

export type AdminGeoRegionFactsUpdateResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAdminFactOut;
};

export type AdminGeoRegionFactsUpdateResponse = AdminGeoRegionFactsUpdateResponses[keyof AdminGeoRegionFactsUpdateResponses];

export type AdminGeoRegionMediaCreateData = {
    body: AppModulesGeoSchemasCommonMediaIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/media';
};

export type AdminGeoRegionMediaCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionMediaCreateError = AdminGeoRegionMediaCreateErrors[keyof AdminGeoRegionMediaCreateErrors];

export type AdminGeoRegionMediaCreateResponses = {
    /**
     * Successful Response
     */
    201: MediaOut;
};

export type AdminGeoRegionMediaCreateResponse = AdminGeoRegionMediaCreateResponses[keyof AdminGeoRegionMediaCreateResponses];

export type AdminGeoRegionMediaDeleteData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
        /**
         * Media Id
         */
        media_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/media/{media_id}';
};

export type AdminGeoRegionMediaDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionMediaDeleteError = AdminGeoRegionMediaDeleteErrors[keyof AdminGeoRegionMediaDeleteErrors];

export type AdminGeoRegionMediaDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoRegionMediaDeleteResponse = AdminGeoRegionMediaDeleteResponses[keyof AdminGeoRegionMediaDeleteResponses];

export type AdminGeoRegionMediaUpdateData = {
    body: AppModulesGeoSchemasCommonMediaPatchIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
        /**
         * Media Id
         */
        media_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/media/{media_id}';
};

export type AdminGeoRegionMediaUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionMediaUpdateError = AdminGeoRegionMediaUpdateErrors[keyof AdminGeoRegionMediaUpdateErrors];

export type AdminGeoRegionMediaUpdateResponses = {
    /**
     * Successful Response
     */
    200: MediaOut;
};

export type AdminGeoRegionMediaUpdateResponse = AdminGeoRegionMediaUpdateResponses[keyof AdminGeoRegionMediaUpdateResponses];

export type AdminGeoRegionLinksListData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query: {
        /**
         * Tab
         */
        tab: string;
    };
    url: '/api/v1/admin/regions/{region_id}/links';
};

export type AdminGeoRegionLinksListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionLinksListError = AdminGeoRegionLinksListErrors[keyof AdminGeoRegionLinksListErrors];

export type AdminGeoRegionLinksListResponses = {
    /**
     * Successful Response
     */
    200: LinkListOut;
};

export type AdminGeoRegionLinksListResponse = AdminGeoRegionLinksListResponses[keyof AdminGeoRegionLinksListResponses];

export type AdminGeoRegionLinksAttachData = {
    body: LinkAttachIn;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/links';
};

export type AdminGeoRegionLinksAttachErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionLinksAttachError = AdminGeoRegionLinksAttachErrors[keyof AdminGeoRegionLinksAttachErrors];

export type AdminGeoRegionLinksAttachResponses = {
    /**
     * Successful Response
     */
    201: AppModulesGeoSchemasAdminLinkItemOut;
};

export type AdminGeoRegionLinksAttachResponse = AdminGeoRegionLinksAttachResponses[keyof AdminGeoRegionLinksAttachResponses];

export type AdminGeoRegionLinksCandidatesData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query: {
        /**
         * Tab
         */
        tab: string;
        /**
         * Q
         */
        q?: string | null;
    };
    url: '/api/v1/admin/regions/{region_id}/link-candidates';
};

export type AdminGeoRegionLinksCandidatesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionLinksCandidatesError = AdminGeoRegionLinksCandidatesErrors[keyof AdminGeoRegionLinksCandidatesErrors];

export type AdminGeoRegionLinksCandidatesResponses = {
    /**
     * Successful Response
     */
    200: LinkCandidateListOut;
};

export type AdminGeoRegionLinksCandidatesResponse = AdminGeoRegionLinksCandidatesResponses[keyof AdminGeoRegionLinksCandidatesResponses];

export type AdminGeoRegionLinksDetachData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
        /**
         * Entity Type
         */
        entity_type: string;
        /**
         * Entity Id
         */
        entity_id: string;
    };
    query?: never;
    url: '/api/v1/admin/regions/{region_id}/links/{entity_type}/{entity_id}';
};

export type AdminGeoRegionLinksDetachErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionLinksDetachError = AdminGeoRegionLinksDetachErrors[keyof AdminGeoRegionLinksDetachErrors];

export type AdminGeoRegionLinksDetachResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoRegionLinksDetachResponse = AdminGeoRegionLinksDetachResponses[keyof AdminGeoRegionLinksDetachResponses];

export type AdminGeoRegionsHistoryData = {
    body?: never;
    path: {
        /**
         * Region Id
         */
        region_id: string;
    };
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/admin/regions/{region_id}/history';
};

export type AdminGeoRegionsHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoRegionsHistoryError = AdminGeoRegionsHistoryErrors[keyof AdminGeoRegionsHistoryErrors];

export type AdminGeoRegionsHistoryResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAdminHistoryPageOut;
};

export type AdminGeoRegionsHistoryResponse = AdminGeoRegionsHistoryResponses[keyof AdminGeoRegionsHistoryResponses];

export type AdminGeoWineriesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, name, region, status, updated_at, visit_mode
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_rework, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_rework, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Фильтр по region_id
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Фильтр по region_id; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Visit Mode]
         *
         * Фильтр по visit_mode (open, by_appointment, closed)
         */
        'filter[visit_mode]'?: string | null;
        /**
         * Filter[Visit Mode][]
         *
         * Фильтр по visit_mode (open, by_appointment, closed); несколько значений
         */
        'filter[visit_mode][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/wineries';
};

export type AdminGeoWineriesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesListError = AdminGeoWineriesListErrors[keyof AdminGeoWineriesListErrors];

export type AdminGeoWineriesListResponses = {
    /**
     * Successful Response
     */
    200: WineryPageOut;
};

export type AdminGeoWineriesListResponse = AdminGeoWineriesListResponses[keyof AdminGeoWineriesListResponses];

export type AdminGeoWineriesCreateData = {
    body: WineryCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/wineries';
};

export type AdminGeoWineriesCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesCreateError = AdminGeoWineriesCreateErrors[keyof AdminGeoWineriesCreateErrors];

export type AdminGeoWineriesCreateResponses = {
    /**
     * Successful Response
     */
    201: WineryDetailOut;
};

export type AdminGeoWineriesCreateResponse = AdminGeoWineriesCreateResponses[keyof AdminGeoWineriesCreateResponses];

export type AdminGeoWineriesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, name, region, status, updated_at, visit_mode
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_rework, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_rework, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Фильтр по region_id
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Фильтр по region_id; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Visit Mode]
         *
         * Фильтр по visit_mode (open, by_appointment, closed)
         */
        'filter[visit_mode]'?: string | null;
        /**
         * Filter[Visit Mode][]
         *
         * Фильтр по visit_mode (open, by_appointment, closed); несколько значений
         */
        'filter[visit_mode][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/wineries/export';
};

export type AdminGeoWineriesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesExportError = AdminGeoWineriesExportErrors[keyof AdminGeoWineriesExportErrors];

export type AdminGeoWineriesExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGeoWineriesDeleteData = {
    body: AppModulesGeoSchemasAdminConfirmNameIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}';
};

export type AdminGeoWineriesDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesDeleteError = AdminGeoWineriesDeleteErrors[keyof AdminGeoWineriesDeleteErrors];

export type AdminGeoWineriesDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoWineriesDeleteResponse = AdminGeoWineriesDeleteResponses[keyof AdminGeoWineriesDeleteResponses];

export type AdminGeoWineriesGetData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}';
};

export type AdminGeoWineriesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesGetError = AdminGeoWineriesGetErrors[keyof AdminGeoWineriesGetErrors];

export type AdminGeoWineriesGetResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesGetResponse = AdminGeoWineriesGetResponses[keyof AdminGeoWineriesGetResponses];

export type AdminGeoWineriesUpdateData = {
    body: WineryPatchIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}';
};

export type AdminGeoWineriesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesUpdateError = AdminGeoWineriesUpdateErrors[keyof AdminGeoWineriesUpdateErrors];

export type AdminGeoWineriesUpdateResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesUpdateResponse = AdminGeoWineriesUpdateResponses[keyof AdminGeoWineriesUpdateResponses];

export type AdminGeoWineriesGuestsUpdateData = {
    body: GuestsIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/guests';
};

export type AdminGeoWineriesGuestsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesGuestsUpdateError = AdminGeoWineriesGuestsUpdateErrors[keyof AdminGeoWineriesGuestsUpdateErrors];

export type AdminGeoWineriesGuestsUpdateResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesGuestsUpdateResponse = AdminGeoWineriesGuestsUpdateResponses[keyof AdminGeoWineriesGuestsUpdateResponses];

export type AdminGeoWineriesSubmitData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/submit';
};

export type AdminGeoWineriesSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesSubmitError = AdminGeoWineriesSubmitErrors[keyof AdminGeoWineriesSubmitErrors];

export type AdminGeoWineriesSubmitResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesSubmitResponse = AdminGeoWineriesSubmitResponses[keyof AdminGeoWineriesSubmitResponses];

export type AdminGeoWineriesPublishData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/publish';
};

export type AdminGeoWineriesPublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesPublishError = AdminGeoWineriesPublishErrors[keyof AdminGeoWineriesPublishErrors];

export type AdminGeoWineriesPublishResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesPublishResponse = AdminGeoWineriesPublishResponses[keyof AdminGeoWineriesPublishResponses];

export type AdminGeoWineriesReturnData = {
    body: CommentIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/return';
};

export type AdminGeoWineriesReturnErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesReturnError = AdminGeoWineriesReturnErrors[keyof AdminGeoWineriesReturnErrors];

export type AdminGeoWineriesReturnResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesReturnResponse = AdminGeoWineriesReturnResponses[keyof AdminGeoWineriesReturnResponses];

export type AdminGeoWineriesRejectData = {
    body: CommentIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/reject';
};

export type AdminGeoWineriesRejectErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesRejectError = AdminGeoWineriesRejectErrors[keyof AdminGeoWineriesRejectErrors];

export type AdminGeoWineriesRejectResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesRejectResponse = AdminGeoWineriesRejectResponses[keyof AdminGeoWineriesRejectResponses];

export type AdminGeoWineriesUnpublishData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/unpublish';
};

export type AdminGeoWineriesUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesUnpublishError = AdminGeoWineriesUnpublishErrors[keyof AdminGeoWineriesUnpublishErrors];

export type AdminGeoWineriesUnpublishResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesUnpublishResponse = AdminGeoWineriesUnpublishResponses[keyof AdminGeoWineriesUnpublishResponses];

export type AdminGeoWineriesArchiveData = {
    body: AppModulesGeoSchemasAdminArchiveIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/archive';
};

export type AdminGeoWineriesArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesArchiveError = AdminGeoWineriesArchiveErrors[keyof AdminGeoWineriesArchiveErrors];

export type AdminGeoWineriesArchiveResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesArchiveResponse = AdminGeoWineriesArchiveResponses[keyof AdminGeoWineriesArchiveResponses];

export type AdminGeoWineriesRestoreData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/restore';
};

export type AdminGeoWineriesRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesRestoreError = AdminGeoWineriesRestoreErrors[keyof AdminGeoWineriesRestoreErrors];

export type AdminGeoWineriesRestoreResponses = {
    /**
     * Successful Response
     */
    200: WineryDetailOut;
};

export type AdminGeoWineriesRestoreResponse = AdminGeoWineriesRestoreResponses[keyof AdminGeoWineriesRestoreResponses];

export type AdminGeoWineryFactsCreateData = {
    body: FactIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/facts';
};

export type AdminGeoWineryFactsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryFactsCreateError = AdminGeoWineryFactsCreateErrors[keyof AdminGeoWineryFactsCreateErrors];

export type AdminGeoWineryFactsCreateResponses = {
    /**
     * Successful Response
     */
    201: AppModulesGeoSchemasAdminFactOut;
};

export type AdminGeoWineryFactsCreateResponse = AdminGeoWineryFactsCreateResponses[keyof AdminGeoWineryFactsCreateResponses];

export type AdminGeoWineryFactsDeleteData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
        /**
         * Fact Id
         */
        fact_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/facts/{fact_id}';
};

export type AdminGeoWineryFactsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryFactsDeleteError = AdminGeoWineryFactsDeleteErrors[keyof AdminGeoWineryFactsDeleteErrors];

export type AdminGeoWineryFactsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoWineryFactsDeleteResponse = AdminGeoWineryFactsDeleteResponses[keyof AdminGeoWineryFactsDeleteResponses];

export type AdminGeoWineryFactsUpdateData = {
    body: FactPatchIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
        /**
         * Fact Id
         */
        fact_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/facts/{fact_id}';
};

export type AdminGeoWineryFactsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryFactsUpdateError = AdminGeoWineryFactsUpdateErrors[keyof AdminGeoWineryFactsUpdateErrors];

export type AdminGeoWineryFactsUpdateResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAdminFactOut;
};

export type AdminGeoWineryFactsUpdateResponse = AdminGeoWineryFactsUpdateResponses[keyof AdminGeoWineryFactsUpdateResponses];

export type AdminGeoWineryMediaCreateData = {
    body: AppModulesGeoSchemasCommonMediaIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/media';
};

export type AdminGeoWineryMediaCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryMediaCreateError = AdminGeoWineryMediaCreateErrors[keyof AdminGeoWineryMediaCreateErrors];

export type AdminGeoWineryMediaCreateResponses = {
    /**
     * Successful Response
     */
    201: MediaOut;
};

export type AdminGeoWineryMediaCreateResponse = AdminGeoWineryMediaCreateResponses[keyof AdminGeoWineryMediaCreateResponses];

export type AdminGeoWineryMediaDeleteData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
        /**
         * Media Id
         */
        media_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/media/{media_id}';
};

export type AdminGeoWineryMediaDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryMediaDeleteError = AdminGeoWineryMediaDeleteErrors[keyof AdminGeoWineryMediaDeleteErrors];

export type AdminGeoWineryMediaDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoWineryMediaDeleteResponse = AdminGeoWineryMediaDeleteResponses[keyof AdminGeoWineryMediaDeleteResponses];

export type AdminGeoWineryMediaUpdateData = {
    body: AppModulesGeoSchemasCommonMediaPatchIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
        /**
         * Media Id
         */
        media_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/media/{media_id}';
};

export type AdminGeoWineryMediaUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryMediaUpdateError = AdminGeoWineryMediaUpdateErrors[keyof AdminGeoWineryMediaUpdateErrors];

export type AdminGeoWineryMediaUpdateResponses = {
    /**
     * Successful Response
     */
    200: MediaOut;
};

export type AdminGeoWineryMediaUpdateResponse = AdminGeoWineryMediaUpdateResponses[keyof AdminGeoWineryMediaUpdateResponses];

export type AdminGeoWineryReviewsListData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/admin/wineries/{winery_id}/reviews';
};

export type AdminGeoWineryReviewsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryReviewsListError = AdminGeoWineryReviewsListErrors[keyof AdminGeoWineryReviewsListErrors];

export type AdminGeoWineryReviewsListResponses = {
    /**
     * Successful Response
     */
    200: AdminReviewsOut;
};

export type AdminGeoWineryReviewsListResponse = AdminGeoWineryReviewsListResponses[keyof AdminGeoWineryReviewsListResponses];

export type AdminGeoWineryLinksListData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query: {
        /**
         * Tab
         */
        tab: string;
    };
    url: '/api/v1/admin/wineries/{winery_id}/links';
};

export type AdminGeoWineryLinksListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryLinksListError = AdminGeoWineryLinksListErrors[keyof AdminGeoWineryLinksListErrors];

export type AdminGeoWineryLinksListResponses = {
    /**
     * Successful Response
     */
    200: LinkListOut;
};

export type AdminGeoWineryLinksListResponse = AdminGeoWineryLinksListResponses[keyof AdminGeoWineryLinksListResponses];

export type AdminGeoWineryLinksAttachData = {
    body: LinkAttachIn;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/links';
};

export type AdminGeoWineryLinksAttachErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryLinksAttachError = AdminGeoWineryLinksAttachErrors[keyof AdminGeoWineryLinksAttachErrors];

export type AdminGeoWineryLinksAttachResponses = {
    /**
     * Successful Response
     */
    201: AppModulesGeoSchemasAdminLinkItemOut;
};

export type AdminGeoWineryLinksAttachResponse = AdminGeoWineryLinksAttachResponses[keyof AdminGeoWineryLinksAttachResponses];

export type AdminGeoWineryLinksCandidatesData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query: {
        /**
         * Tab
         */
        tab: string;
        /**
         * Q
         */
        q?: string | null;
    };
    url: '/api/v1/admin/wineries/{winery_id}/link-candidates';
};

export type AdminGeoWineryLinksCandidatesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryLinksCandidatesError = AdminGeoWineryLinksCandidatesErrors[keyof AdminGeoWineryLinksCandidatesErrors];

export type AdminGeoWineryLinksCandidatesResponses = {
    /**
     * Successful Response
     */
    200: LinkCandidateListOut;
};

export type AdminGeoWineryLinksCandidatesResponse = AdminGeoWineryLinksCandidatesResponses[keyof AdminGeoWineryLinksCandidatesResponses];

export type AdminGeoWineryLinksDetachData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
        /**
         * Entity Type
         */
        entity_type: string;
        /**
         * Entity Id
         */
        entity_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wineries/{winery_id}/links/{entity_type}/{entity_id}';
};

export type AdminGeoWineryLinksDetachErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineryLinksDetachError = AdminGeoWineryLinksDetachErrors[keyof AdminGeoWineryLinksDetachErrors];

export type AdminGeoWineryLinksDetachResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGeoWineryLinksDetachResponse = AdminGeoWineryLinksDetachResponses[keyof AdminGeoWineryLinksDetachResponses];

export type AdminGeoWineriesHistoryData = {
    body?: never;
    path: {
        /**
         * Winery Id
         */
        winery_id: string;
    };
    query?: {
        /**
         * Cursor
         *
         * Курсор следующей страницы
         */
        cursor?: string | null;
        /**
         * Limit
         *
         * Размер страницы, 1–100
         */
        limit?: number;
    };
    url: '/api/v1/admin/wineries/{winery_id}/history';
};

export type AdminGeoWineriesHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoWineriesHistoryError = AdminGeoWineriesHistoryErrors[keyof AdminGeoWineriesHistoryErrors];

export type AdminGeoWineriesHistoryResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAdminHistoryPageOut;
};

export type AdminGeoWineriesHistoryResponse = AdminGeoWineriesHistoryResponses[keyof AdminGeoWineriesHistoryResponses];

export type AdminGeoAppMarketsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, name, sort_order, status
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Статус страны приложения (active, soon)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус страны приложения (active, soon); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/app-markets';
};

export type AdminGeoAppMarketsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoAppMarketsListError = AdminGeoAppMarketsListErrors[keyof AdminGeoAppMarketsListErrors];

export type AdminGeoAppMarketsListResponses = {
    /**
     * Successful Response
     */
    200: PageAppMarketOut;
};

export type AdminGeoAppMarketsListResponse = AdminGeoAppMarketsListResponses[keyof AdminGeoAppMarketsListResponses];

export type AdminGeoAppMarketsCreateData = {
    body: AppMarketCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/app-markets';
};

export type AdminGeoAppMarketsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoAppMarketsCreateError = AdminGeoAppMarketsCreateErrors[keyof AdminGeoAppMarketsCreateErrors];

export type AdminGeoAppMarketsCreateResponses = {
    /**
     * Successful Response
     */
    201: AppModulesGeoSchemasAdminAppMarketOut;
};

export type AdminGeoAppMarketsCreateResponse = AdminGeoAppMarketsCreateResponses[keyof AdminGeoAppMarketsCreateResponses];

export type AdminGeoAppMarketsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, name, sort_order, status
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Статус страны приложения (active, soon)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус страны приложения (active, soon); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/app-markets/export';
};

export type AdminGeoAppMarketsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoAppMarketsExportError = AdminGeoAppMarketsExportErrors[keyof AdminGeoAppMarketsExportErrors];

export type AdminGeoAppMarketsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGeoAppMarketsGetData = {
    body?: never;
    path: {
        /**
         * Market Id
         */
        market_id: string;
    };
    query?: never;
    url: '/api/v1/admin/app-markets/{market_id}';
};

export type AdminGeoAppMarketsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoAppMarketsGetError = AdminGeoAppMarketsGetErrors[keyof AdminGeoAppMarketsGetErrors];

export type AdminGeoAppMarketsGetResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAdminAppMarketOut;
};

export type AdminGeoAppMarketsGetResponse = AdminGeoAppMarketsGetResponses[keyof AdminGeoAppMarketsGetResponses];

export type AdminGeoAppMarketsUpdateData = {
    body: AppMarketPatchIn;
    path: {
        /**
         * Market Id
         */
        market_id: string;
    };
    query?: never;
    url: '/api/v1/admin/app-markets/{market_id}';
};

export type AdminGeoAppMarketsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoAppMarketsUpdateError = AdminGeoAppMarketsUpdateErrors[keyof AdminGeoAppMarketsUpdateErrors];

export type AdminGeoAppMarketsUpdateResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAdminAppMarketOut;
};

export type AdminGeoAppMarketsUpdateResponse = AdminGeoAppMarketsUpdateResponses[keyof AdminGeoAppMarketsUpdateResponses];

export type AdminGeoAppMarketsSetStatusData = {
    body: AppMarketStatusIn;
    path: {
        /**
         * Market Id
         */
        market_id: string;
    };
    query?: never;
    url: '/api/v1/admin/app-markets/{market_id}/status';
};

export type AdminGeoAppMarketsSetStatusErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGeoAppMarketsSetStatusError = AdminGeoAppMarketsSetStatusErrors[keyof AdminGeoAppMarketsSetStatusErrors];

export type AdminGeoAppMarketsSetStatusResponses = {
    /**
     * Successful Response
     */
    200: AppModulesGeoSchemasAdminAppMarketOut;
};

export type AdminGeoAppMarketsSetStatusResponse = AdminGeoAppMarketsSetStatusResponses[keyof AdminGeoAppMarketsSetStatusResponses];

export type AdminCatReferenceGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Region Id
         *
         * Регион для классификаций
         */
        region_id?: string | null;
    };
    url: '/api/v1/admin/catalog/reference';
};

export type AdminCatReferenceGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatReferenceGetError = AdminCatReferenceGetErrors[keyof AdminCatReferenceGetErrors];

export type AdminCatReferenceGetResponses = {
    /**
     * Successful Response
     */
    200: ReferenceOut;
};

export type AdminCatReferenceGetResponse = AdminCatReferenceGetResponses[keyof AdminCatReferenceGetResponses];

export type AdminCatWinePickerData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Q
         */
        q?: string | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v1/admin/catalog/wine-picker';
};

export type AdminCatWinePickerErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinePickerError = AdminCatWinePickerErrors[keyof AdminCatWinePickerErrors];

export type AdminCatWinePickerResponses = {
    /**
     * Successful Response
     */
    200: PickerOut;
};

export type AdminCatWinePickerResponse = AdminCatWinePickerResponses[keyof AdminCatWinePickerResponses];

export type AdminCatDropTargetsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Q
         */
        q?: string | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v1/admin/catalog/drop-targets';
};

export type AdminCatDropTargetsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropTargetsError = AdminCatDropTargetsErrors[keyof AdminCatDropTargetsErrors];

export type AdminCatDropTargetsResponses = {
    /**
     * Successful Response
     */
    200: DropTargetsOut;
};

export type AdminCatDropTargetsResponse = AdminCatDropTargetsResponses[keyof AdminCatDropTargetsResponses];

export type AdminCatMediaListData = {
    body?: never;
    path: {
        owner_type: CatalogType;
        /**
         * Record Id
         */
        record_id: string;
    };
    query?: never;
    url: '/api/v1/admin/catalog/{owner_type}/{record_id}/media';
};

export type AdminCatMediaListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatMediaListError = AdminCatMediaListErrors[keyof AdminCatMediaListErrors];

export type AdminCatMediaListResponses = {
    /**
     * Successful Response
     */
    200: MediaListOut;
};

export type AdminCatMediaListResponse = AdminCatMediaListResponses[keyof AdminCatMediaListResponses];

export type AdminCatMediaCreateData = {
    body: AppModulesCatSchemasAdminMediaIn;
    path: {
        owner_type: CatalogType;
        /**
         * Record Id
         */
        record_id: string;
    };
    query?: never;
    url: '/api/v1/admin/catalog/{owner_type}/{record_id}/media';
};

export type AdminCatMediaCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatMediaCreateError = AdminCatMediaCreateErrors[keyof AdminCatMediaCreateErrors];

export type AdminCatMediaCreateResponses = {
    /**
     * Successful Response
     */
    201: CatalogMediaOut;
};

export type AdminCatMediaCreateResponse = AdminCatMediaCreateResponses[keyof AdminCatMediaCreateResponses];

export type AdminCatMediaDeleteData = {
    body?: never;
    path: {
        owner_type: CatalogType;
        /**
         * Record Id
         */
        record_id: string;
        /**
         * Media Id
         */
        media_id: string;
    };
    query?: never;
    url: '/api/v1/admin/catalog/{owner_type}/{record_id}/media/{media_id}';
};

export type AdminCatMediaDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatMediaDeleteError = AdminCatMediaDeleteErrors[keyof AdminCatMediaDeleteErrors];

export type AdminCatMediaDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminCatMediaDeleteResponse = AdminCatMediaDeleteResponses[keyof AdminCatMediaDeleteResponses];

export type AdminCatMediaUpdateData = {
    body: AppModulesCatSchemasAdminMediaPatchIn;
    path: {
        owner_type: CatalogType;
        /**
         * Record Id
         */
        record_id: string;
        /**
         * Media Id
         */
        media_id: string;
    };
    query?: never;
    url: '/api/v1/admin/catalog/{owner_type}/{record_id}/media/{media_id}';
};

export type AdminCatMediaUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatMediaUpdateError = AdminCatMediaUpdateErrors[keyof AdminCatMediaUpdateErrors];

export type AdminCatMediaUpdateResponses = {
    /**
     * Successful Response
     */
    200: CatalogMediaOut;
};

export type AdminCatMediaUpdateResponse = AdminCatMediaUpdateResponses[keyof AdminCatMediaUpdateResponses];

export type AdminCatWinesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: available, created_at, name, price, region, status, type, updated_at, vintage, winery
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Статус с учётом ревизии (быстрые вкладки) (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус с учётом ревизии (быстрые вкладки) (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (red, white, rose, sparkling, orange, fortified)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (red, white, rose, sparkling, orange, fortified); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Фильтр по region_id
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Фильтр по region_id; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Winery Id]
         *
         * Фильтр по winery_id
         */
        'filter[winery_id]'?: string | null;
        /**
         * Filter[Winery Id][]
         *
         * Фильтр по winery_id; несколько значений
         */
        'filter[winery_id][]'?: Array<string> | null;
        /**
         * Filter[Name]
         *
         * Название (подстрока)
         */
        'filter[name]'?: string | null;
        /**
         * Filter[Name][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[name][]'?: Array<string> | null;
        /**
         * Filter[Region]
         *
         * Регион (подстрока)
         */
        'filter[region]'?: string | null;
        /**
         * Filter[Region][]
         *
         * Регион (подстрока); несколько значений
         */
        'filter[region][]'?: Array<string> | null;
        /**
         * Filter[Winery]
         *
         * Винодельня (подстрока)
         */
        'filter[winery]'?: string | null;
        /**
         * Filter[Winery][]
         *
         * Винодельня (подстрока); несколько значений
         */
        'filter[winery][]'?: Array<string> | null;
        /**
         * Filter[Sku]
         *
         * Артикул (подстрока)
         */
        'filter[sku]'?: string | null;
        /**
         * Filter[Sku][]
         *
         * Артикул (подстрока); несколько значений
         */
        'filter[sku][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/wines';
};

export type AdminCatWinesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesListError = AdminCatWinesListErrors[keyof AdminCatWinesListErrors];

export type AdminCatWinesListResponses = {
    /**
     * Successful Response
     */
    200: CatalogPageOutWineRowOut;
};

export type AdminCatWinesListResponse = AdminCatWinesListResponses[keyof AdminCatWinesListResponses];

export type AdminCatWinesCreateData = {
    body: WineCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/wines';
};

export type AdminCatWinesCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesCreateError = AdminCatWinesCreateErrors[keyof AdminCatWinesCreateErrors];

export type AdminCatWinesCreateResponses = {
    /**
     * Successful Response
     */
    201: WineAdmin;
};

export type AdminCatWinesCreateResponse = AdminCatWinesCreateResponses[keyof AdminCatWinesCreateResponses];

export type AdminCatWinesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: available, created_at, name, price, region, status, type, updated_at, vintage, winery
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Статус с учётом ревизии (быстрые вкладки) (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус с учётом ревизии (быстрые вкладки) (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (red, white, rose, sparkling, orange, fortified)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (red, white, rose, sparkling, orange, fortified); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Фильтр по region_id
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Фильтр по region_id; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Winery Id]
         *
         * Фильтр по winery_id
         */
        'filter[winery_id]'?: string | null;
        /**
         * Filter[Winery Id][]
         *
         * Фильтр по winery_id; несколько значений
         */
        'filter[winery_id][]'?: Array<string> | null;
        /**
         * Filter[Name]
         *
         * Название (подстрока)
         */
        'filter[name]'?: string | null;
        /**
         * Filter[Name][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[name][]'?: Array<string> | null;
        /**
         * Filter[Region]
         *
         * Регион (подстрока)
         */
        'filter[region]'?: string | null;
        /**
         * Filter[Region][]
         *
         * Регион (подстрока); несколько значений
         */
        'filter[region][]'?: Array<string> | null;
        /**
         * Filter[Winery]
         *
         * Винодельня (подстрока)
         */
        'filter[winery]'?: string | null;
        /**
         * Filter[Winery][]
         *
         * Винодельня (подстрока); несколько значений
         */
        'filter[winery][]'?: Array<string> | null;
        /**
         * Filter[Sku]
         *
         * Артикул (подстрока)
         */
        'filter[sku]'?: string | null;
        /**
         * Filter[Sku][]
         *
         * Артикул (подстрока); несколько значений
         */
        'filter[sku][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/wines/export';
};

export type AdminCatWinesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesExportError = AdminCatWinesExportErrors[keyof AdminCatWinesExportErrors];

export type AdminCatWinesExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminCatWinesDeleteData = {
    body: AppModulesCatSchemasAdminConfirmNameIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}';
};

export type AdminCatWinesDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesDeleteError = AdminCatWinesDeleteErrors[keyof AdminCatWinesDeleteErrors];

export type AdminCatWinesDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminCatWinesDeleteResponse = AdminCatWinesDeleteResponses[keyof AdminCatWinesDeleteResponses];

export type AdminCatWinesGetData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}';
};

export type AdminCatWinesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesGetError = AdminCatWinesGetErrors[keyof AdminCatWinesGetErrors];

export type AdminCatWinesGetResponses = {
    /**
     * Successful Response
     */
    200: WineCardOut;
};

export type AdminCatWinesGetResponse = AdminCatWinesGetResponses[keyof AdminCatWinesGetResponses];

export type AdminCatWinesUpdateData = {
    body: WinePatchIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}';
};

export type AdminCatWinesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesUpdateError = AdminCatWinesUpdateErrors[keyof AdminCatWinesUpdateErrors];

export type AdminCatWinesUpdateResponses = {
    /**
     * Successful Response
     */
    200: WineAdmin;
};

export type AdminCatWinesUpdateResponse = AdminCatWinesUpdateResponses[keyof AdminCatWinesUpdateResponses];

export type AdminCatWinesGrapesSetData = {
    body: GrapesIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/grapes';
};

export type AdminCatWinesGrapesSetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesGrapesSetError = AdminCatWinesGrapesSetErrors[keyof AdminCatWinesGrapesSetErrors];

export type AdminCatWinesGrapesSetResponses = {
    /**
     * Successful Response
     */
    200: WineAdmin;
};

export type AdminCatWinesGrapesSetResponse = AdminCatWinesGrapesSetResponses[keyof AdminCatWinesGrapesSetResponses];

export type AdminCatWinesNotesSetData = {
    body: NotesIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/notes';
};

export type AdminCatWinesNotesSetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesNotesSetError = AdminCatWinesNotesSetErrors[keyof AdminCatWinesNotesSetErrors];

export type AdminCatWinesNotesSetResponses = {
    /**
     * Successful Response
     */
    200: WineAdmin;
};

export type AdminCatWinesNotesSetResponse = AdminCatWinesNotesSetResponses[keyof AdminCatWinesNotesSetResponses];

export type AdminCatWinesPairingsSetData = {
    body: PairingsIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/pairings';
};

export type AdminCatWinesPairingsSetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesPairingsSetError = AdminCatWinesPairingsSetErrors[keyof AdminCatWinesPairingsSetErrors];

export type AdminCatWinesPairingsSetResponses = {
    /**
     * Successful Response
     */
    200: WineAdmin;
};

export type AdminCatWinesPairingsSetResponse = AdminCatWinesPairingsSetResponses[keyof AdminCatWinesPairingsSetResponses];

export type AdminCatWinesPriceChangeData = {
    body: PriceChangeIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/price-changes';
};

export type AdminCatWinesPriceChangeErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesPriceChangeError = AdminCatWinesPriceChangeErrors[keyof AdminCatWinesPriceChangeErrors];

export type AdminCatWinesPriceChangeResponses = {
    /**
     * Successful Response
     */
    201: PriceChangeOut;
};

export type AdminCatWinesPriceChangeResponse = AdminCatWinesPriceChangeResponses[keyof AdminCatWinesPriceChangeResponses];

export type AdminCatWinesStockUpdateData = {
    body: StockIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/stock';
};

export type AdminCatWinesStockUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesStockUpdateError = AdminCatWinesStockUpdateErrors[keyof AdminCatWinesStockUpdateErrors];

export type AdminCatWinesStockUpdateResponses = {
    /**
     * Successful Response
     */
    200: WineAdmin;
};

export type AdminCatWinesStockUpdateResponse = AdminCatWinesStockUpdateResponses[keyof AdminCatWinesStockUpdateResponses];

export type AdminCatWinesSalesData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: {
        /**
         * Days
         */
        days?: number;
    };
    url: '/api/v1/admin/wines/{wine_id}/sales';
};

export type AdminCatWinesSalesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesSalesError = AdminCatWinesSalesErrors[keyof AdminCatWinesSalesErrors];

export type AdminCatWinesSalesResponses = {
    /**
     * Successful Response
     */
    200: WineSalesOut;
};

export type AdminCatWinesSalesResponse = AdminCatWinesSalesResponses[keyof AdminCatWinesSalesResponses];

export type AdminCatWinesLinksData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/links';
};

export type AdminCatWinesLinksErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesLinksError = AdminCatWinesLinksErrors[keyof AdminCatWinesLinksErrors];

export type AdminCatWinesLinksResponses = {
    /**
     * Successful Response
     */
    200: WineLinksOut;
};

export type AdminCatWinesLinksResponse = AdminCatWinesLinksResponses[keyof AdminCatWinesLinksResponses];

export type AdminCatWinesAttachData = {
    body: AttachIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/attach';
};

export type AdminCatWinesAttachErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesAttachError = AdminCatWinesAttachErrors[keyof AdminCatWinesAttachErrors];

export type AdminCatWinesAttachResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminCatWinesAttachResponse = AdminCatWinesAttachResponses[keyof AdminCatWinesAttachResponses];

export type AdminCatSetsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, available, created_at, format, items_count, name, price, status, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Format]
         *
         * Фильтр по format (open, blind)
         */
        'filter[format]'?: string | null;
        /**
         * Filter[Format][]
         *
         * Фильтр по format (open, blind); несколько значений
         */
        'filter[format][]'?: Array<string> | null;
        /**
         * Filter[Author Type]
         *
         * Фильтр по author_type (platform, winery, expert)
         */
        'filter[author_type]'?: string | null;
        /**
         * Filter[Author Type][]
         *
         * Фильтр по author_type (platform, winery, expert); несколько значений
         */
        'filter[author_type][]'?: Array<string> | null;
        /**
         * Filter[Name]
         *
         * Название (подстрока)
         */
        'filter[name]'?: string | null;
        /**
         * Filter[Name][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[name][]'?: Array<string> | null;
        /**
         * Filter[Sku]
         *
         * Артикул (подстрока)
         */
        'filter[sku]'?: string | null;
        /**
         * Filter[Sku][]
         *
         * Артикул (подстрока); несколько значений
         */
        'filter[sku][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/sets';
};

export type AdminCatSetsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsListError = AdminCatSetsListErrors[keyof AdminCatSetsListErrors];

export type AdminCatSetsListResponses = {
    /**
     * Successful Response
     */
    200: CatalogPageOutSetRowOut;
};

export type AdminCatSetsListResponse = AdminCatSetsListResponses[keyof AdminCatSetsListResponses];

export type AdminCatSetsCreateData = {
    body: SetCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/sets';
};

export type AdminCatSetsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsCreateError = AdminCatSetsCreateErrors[keyof AdminCatSetsCreateErrors];

export type AdminCatSetsCreateResponses = {
    /**
     * Successful Response
     */
    201: SetAdmin;
};

export type AdminCatSetsCreateResponse = AdminCatSetsCreateResponses[keyof AdminCatSetsCreateResponses];

export type AdminCatSetsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, available, created_at, format, items_count, name, price, status, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Format]
         *
         * Фильтр по format (open, blind)
         */
        'filter[format]'?: string | null;
        /**
         * Filter[Format][]
         *
         * Фильтр по format (open, blind); несколько значений
         */
        'filter[format][]'?: Array<string> | null;
        /**
         * Filter[Author Type]
         *
         * Фильтр по author_type (platform, winery, expert)
         */
        'filter[author_type]'?: string | null;
        /**
         * Filter[Author Type][]
         *
         * Фильтр по author_type (platform, winery, expert); несколько значений
         */
        'filter[author_type][]'?: Array<string> | null;
        /**
         * Filter[Name]
         *
         * Название (подстрока)
         */
        'filter[name]'?: string | null;
        /**
         * Filter[Name][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[name][]'?: Array<string> | null;
        /**
         * Filter[Sku]
         *
         * Артикул (подстрока)
         */
        'filter[sku]'?: string | null;
        /**
         * Filter[Sku][]
         *
         * Артикул (подстрока); несколько значений
         */
        'filter[sku][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/sets/export';
};

export type AdminCatSetsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsExportError = AdminCatSetsExportErrors[keyof AdminCatSetsExportErrors];

export type AdminCatSetsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminCatSetsDeleteData = {
    body: AppModulesCatSchemasAdminConfirmNameIn;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}';
};

export type AdminCatSetsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsDeleteError = AdminCatSetsDeleteErrors[keyof AdminCatSetsDeleteErrors];

export type AdminCatSetsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminCatSetsDeleteResponse = AdminCatSetsDeleteResponses[keyof AdminCatSetsDeleteResponses];

export type AdminCatSetsGetData = {
    body?: never;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}';
};

export type AdminCatSetsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsGetError = AdminCatSetsGetErrors[keyof AdminCatSetsGetErrors];

export type AdminCatSetsGetResponses = {
    /**
     * Successful Response
     */
    200: SetAdmin;
};

export type AdminCatSetsGetResponse = AdminCatSetsGetResponses[keyof AdminCatSetsGetResponses];

export type AdminCatSetsUpdateData = {
    body: SetPatchIn;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}';
};

export type AdminCatSetsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsUpdateError = AdminCatSetsUpdateErrors[keyof AdminCatSetsUpdateErrors];

export type AdminCatSetsUpdateResponses = {
    /**
     * Successful Response
     */
    200: SetAdmin;
};

export type AdminCatSetsUpdateResponse = AdminCatSetsUpdateResponses[keyof AdminCatSetsUpdateResponses];

export type AdminCatSetsItemsSetData = {
    body: SetItemsIn;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/items';
};

export type AdminCatSetsItemsSetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsItemsSetError = AdminCatSetsItemsSetErrors[keyof AdminCatSetsItemsSetErrors];

export type AdminCatSetsItemsSetResponses = {
    /**
     * Successful Response
     */
    200: SetAdmin;
};

export type AdminCatSetsItemsSetResponse = AdminCatSetsItemsSetResponses[keyof AdminCatSetsItemsSetResponses];

export type AdminCatSetsSalesData = {
    body?: never;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/sales';
};

export type AdminCatSetsSalesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsSalesError = AdminCatSetsSalesErrors[keyof AdminCatSetsSalesErrors];

export type AdminCatSetsSalesResponses = {
    /**
     * Successful Response
     */
    200: SetSalesOut;
};

export type AdminCatSetsSalesResponse = AdminCatSetsSalesResponses[keyof AdminCatSetsSalesResponses];

export type AdminCatDropsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: batch_qty, created_at, name, price, remaining, sales_state, start_at, status, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Sales State]
         *
         * Фильтр по sales_state (pending, upcoming, early_access, active, sold_out, ended)
         */
        'filter[sales_state]'?: string | null;
        /**
         * Filter[Sales State][]
         *
         * Фильтр по sales_state (pending, upcoming, early_access, active, sold_out, ended); несколько значений
         */
        'filter[sales_state][]'?: Array<string> | null;
        /**
         * Filter[Target Type]
         *
         * Фильтр по target_type (wine, set)
         */
        'filter[target_type]'?: string | null;
        /**
         * Filter[Target Type][]
         *
         * Фильтр по target_type (wine, set); несколько значений
         */
        'filter[target_type][]'?: Array<string> | null;
        /**
         * Filter[Target Wine Id]
         *
         * Фильтр по target_wine_id
         */
        'filter[target_wine_id]'?: string | null;
        /**
         * Filter[Target Wine Id][]
         *
         * Фильтр по target_wine_id; несколько значений
         */
        'filter[target_wine_id][]'?: Array<string> | null;
        /**
         * Filter[Target Set Id]
         *
         * Фильтр по target_set_id
         */
        'filter[target_set_id]'?: string | null;
        /**
         * Filter[Target Set Id][]
         *
         * Фильтр по target_set_id; несколько значений
         */
        'filter[target_set_id][]'?: Array<string> | null;
        /**
         * Filter[Name]
         *
         * Название (подстрока)
         */
        'filter[name]'?: string | null;
        /**
         * Filter[Name][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[name][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/drops';
};

export type AdminCatDropsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsListError = AdminCatDropsListErrors[keyof AdminCatDropsListErrors];

export type AdminCatDropsListResponses = {
    /**
     * Successful Response
     */
    200: CatalogPageOutDropRowOut;
};

export type AdminCatDropsListResponse = AdminCatDropsListResponses[keyof AdminCatDropsListResponses];

export type AdminCatDropsCreateData = {
    body: DropCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/drops';
};

export type AdminCatDropsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsCreateError = AdminCatDropsCreateErrors[keyof AdminCatDropsCreateErrors];

export type AdminCatDropsCreateResponses = {
    /**
     * Successful Response
     */
    201: DropAdmin;
};

export type AdminCatDropsCreateResponse = AdminCatDropsCreateResponses[keyof AdminCatDropsCreateResponses];

export type AdminCatDropsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: batch_qty, created_at, name, price, remaining, sales_state, start_at, status, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Sales State]
         *
         * Фильтр по sales_state (pending, upcoming, early_access, active, sold_out, ended)
         */
        'filter[sales_state]'?: string | null;
        /**
         * Filter[Sales State][]
         *
         * Фильтр по sales_state (pending, upcoming, early_access, active, sold_out, ended); несколько значений
         */
        'filter[sales_state][]'?: Array<string> | null;
        /**
         * Filter[Target Type]
         *
         * Фильтр по target_type (wine, set)
         */
        'filter[target_type]'?: string | null;
        /**
         * Filter[Target Type][]
         *
         * Фильтр по target_type (wine, set); несколько значений
         */
        'filter[target_type][]'?: Array<string> | null;
        /**
         * Filter[Target Wine Id]
         *
         * Фильтр по target_wine_id
         */
        'filter[target_wine_id]'?: string | null;
        /**
         * Filter[Target Wine Id][]
         *
         * Фильтр по target_wine_id; несколько значений
         */
        'filter[target_wine_id][]'?: Array<string> | null;
        /**
         * Filter[Target Set Id]
         *
         * Фильтр по target_set_id
         */
        'filter[target_set_id]'?: string | null;
        /**
         * Filter[Target Set Id][]
         *
         * Фильтр по target_set_id; несколько значений
         */
        'filter[target_set_id][]'?: Array<string> | null;
        /**
         * Filter[Name]
         *
         * Название (подстрока)
         */
        'filter[name]'?: string | null;
        /**
         * Filter[Name][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[name][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/drops/export';
};

export type AdminCatDropsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsExportError = AdminCatDropsExportErrors[keyof AdminCatDropsExportErrors];

export type AdminCatDropsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminCatDropsDeleteData = {
    body: AppModulesCatSchemasAdminConfirmNameIn;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}';
};

export type AdminCatDropsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsDeleteError = AdminCatDropsDeleteErrors[keyof AdminCatDropsDeleteErrors];

export type AdminCatDropsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminCatDropsDeleteResponse = AdminCatDropsDeleteResponses[keyof AdminCatDropsDeleteResponses];

export type AdminCatDropsGetData = {
    body?: never;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}';
};

export type AdminCatDropsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsGetError = AdminCatDropsGetErrors[keyof AdminCatDropsGetErrors];

export type AdminCatDropsGetResponses = {
    /**
     * Successful Response
     */
    200: DropAdmin;
};

export type AdminCatDropsGetResponse = AdminCatDropsGetResponses[keyof AdminCatDropsGetResponses];

export type AdminCatDropsUpdateData = {
    body: DropPatchIn;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}';
};

export type AdminCatDropsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsUpdateError = AdminCatDropsUpdateErrors[keyof AdminCatDropsUpdateErrors];

export type AdminCatDropsUpdateResponses = {
    /**
     * Successful Response
     */
    200: DropAdmin;
};

export type AdminCatDropsUpdateResponse = AdminCatDropsUpdateResponses[keyof AdminCatDropsUpdateResponses];

export type AdminCatWinesSubmitData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/submit';
};

export type AdminCatWinesSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesSubmitError = AdminCatWinesSubmitErrors[keyof AdminCatWinesSubmitErrors];

export type AdminCatWinesSubmitResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatWinesSubmitResponse = AdminCatWinesSubmitResponses[keyof AdminCatWinesSubmitResponses];

export type AdminCatWinesPublishData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/publish';
};

export type AdminCatWinesPublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesPublishError = AdminCatWinesPublishErrors[keyof AdminCatWinesPublishErrors];

export type AdminCatWinesPublishResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatWinesPublishResponse = AdminCatWinesPublishResponses[keyof AdminCatWinesPublishResponses];

export type AdminCatWinesReturnData = {
    body: ReviewNoteIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/return';
};

export type AdminCatWinesReturnErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesReturnError = AdminCatWinesReturnErrors[keyof AdminCatWinesReturnErrors];

export type AdminCatWinesReturnResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatWinesReturnResponse = AdminCatWinesReturnResponses[keyof AdminCatWinesReturnResponses];

export type AdminCatWinesRejectData = {
    body: ReviewNoteIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/reject';
};

export type AdminCatWinesRejectErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesRejectError = AdminCatWinesRejectErrors[keyof AdminCatWinesRejectErrors];

export type AdminCatWinesRejectResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatWinesRejectResponse = AdminCatWinesRejectResponses[keyof AdminCatWinesRejectResponses];

export type AdminCatWinesUnpublishData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/unpublish';
};

export type AdminCatWinesUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesUnpublishError = AdminCatWinesUnpublishErrors[keyof AdminCatWinesUnpublishErrors];

export type AdminCatWinesUnpublishResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatWinesUnpublishResponse = AdminCatWinesUnpublishResponses[keyof AdminCatWinesUnpublishResponses];

export type AdminCatWinesArchiveData = {
    body: AppModulesCatSchemasAdminArchiveIn;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/archive';
};

export type AdminCatWinesArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesArchiveError = AdminCatWinesArchiveErrors[keyof AdminCatWinesArchiveErrors];

export type AdminCatWinesArchiveResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatWinesArchiveResponse = AdminCatWinesArchiveResponses[keyof AdminCatWinesArchiveResponses];

export type AdminCatWinesRestoreData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/restore';
};

export type AdminCatWinesRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesRestoreError = AdminCatWinesRestoreErrors[keyof AdminCatWinesRestoreErrors];

export type AdminCatWinesRestoreResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatWinesRestoreResponse = AdminCatWinesRestoreResponses[keyof AdminCatWinesRestoreResponses];

export type AdminCatWinesUsageData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wines/{wine_id}/usage';
};

export type AdminCatWinesUsageErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesUsageError = AdminCatWinesUsageErrors[keyof AdminCatWinesUsageErrors];

export type AdminCatWinesUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageOut;
};

export type AdminCatWinesUsageResponse = AdminCatWinesUsageResponses[keyof AdminCatWinesUsageResponses];

export type AdminCatWinesHistoryData = {
    body?: never;
    path: {
        /**
         * Wine Id
         */
        wine_id: string;
    };
    query?: {
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/wines/{wine_id}/history';
};

export type AdminCatWinesHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatWinesHistoryError = AdminCatWinesHistoryErrors[keyof AdminCatWinesHistoryErrors];

export type AdminCatWinesHistoryResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminHistoryPageOut;
};

export type AdminCatWinesHistoryResponse = AdminCatWinesHistoryResponses[keyof AdminCatWinesHistoryResponses];

export type AdminCatSetsSubmitData = {
    body?: never;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/submit';
};

export type AdminCatSetsSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsSubmitError = AdminCatSetsSubmitErrors[keyof AdminCatSetsSubmitErrors];

export type AdminCatSetsSubmitResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatSetsSubmitResponse = AdminCatSetsSubmitResponses[keyof AdminCatSetsSubmitResponses];

export type AdminCatSetsPublishData = {
    body?: never;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/publish';
};

export type AdminCatSetsPublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsPublishError = AdminCatSetsPublishErrors[keyof AdminCatSetsPublishErrors];

export type AdminCatSetsPublishResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatSetsPublishResponse = AdminCatSetsPublishResponses[keyof AdminCatSetsPublishResponses];

export type AdminCatSetsReturnData = {
    body: ReviewNoteIn;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/return';
};

export type AdminCatSetsReturnErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsReturnError = AdminCatSetsReturnErrors[keyof AdminCatSetsReturnErrors];

export type AdminCatSetsReturnResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatSetsReturnResponse = AdminCatSetsReturnResponses[keyof AdminCatSetsReturnResponses];

export type AdminCatSetsRejectData = {
    body: ReviewNoteIn;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/reject';
};

export type AdminCatSetsRejectErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsRejectError = AdminCatSetsRejectErrors[keyof AdminCatSetsRejectErrors];

export type AdminCatSetsRejectResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatSetsRejectResponse = AdminCatSetsRejectResponses[keyof AdminCatSetsRejectResponses];

export type AdminCatSetsUnpublishData = {
    body?: never;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/unpublish';
};

export type AdminCatSetsUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsUnpublishError = AdminCatSetsUnpublishErrors[keyof AdminCatSetsUnpublishErrors];

export type AdminCatSetsUnpublishResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatSetsUnpublishResponse = AdminCatSetsUnpublishResponses[keyof AdminCatSetsUnpublishResponses];

export type AdminCatSetsArchiveData = {
    body: AppModulesCatSchemasAdminArchiveIn;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/archive';
};

export type AdminCatSetsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsArchiveError = AdminCatSetsArchiveErrors[keyof AdminCatSetsArchiveErrors];

export type AdminCatSetsArchiveResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatSetsArchiveResponse = AdminCatSetsArchiveResponses[keyof AdminCatSetsArchiveResponses];

export type AdminCatSetsRestoreData = {
    body?: never;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/restore';
};

export type AdminCatSetsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsRestoreError = AdminCatSetsRestoreErrors[keyof AdminCatSetsRestoreErrors];

export type AdminCatSetsRestoreResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatSetsRestoreResponse = AdminCatSetsRestoreResponses[keyof AdminCatSetsRestoreResponses];

export type AdminCatSetsUsageData = {
    body?: never;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: never;
    url: '/api/v1/admin/sets/{set_id}/usage';
};

export type AdminCatSetsUsageErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsUsageError = AdminCatSetsUsageErrors[keyof AdminCatSetsUsageErrors];

export type AdminCatSetsUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageOut;
};

export type AdminCatSetsUsageResponse = AdminCatSetsUsageResponses[keyof AdminCatSetsUsageResponses];

export type AdminCatSetsHistoryData = {
    body?: never;
    path: {
        /**
         * Set Id
         */
        set_id: string;
    };
    query?: {
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/sets/{set_id}/history';
};

export type AdminCatSetsHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatSetsHistoryError = AdminCatSetsHistoryErrors[keyof AdminCatSetsHistoryErrors];

export type AdminCatSetsHistoryResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminHistoryPageOut;
};

export type AdminCatSetsHistoryResponse = AdminCatSetsHistoryResponses[keyof AdminCatSetsHistoryResponses];

export type AdminCatDropsSubmitData = {
    body?: never;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}/submit';
};

export type AdminCatDropsSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsSubmitError = AdminCatDropsSubmitErrors[keyof AdminCatDropsSubmitErrors];

export type AdminCatDropsSubmitResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatDropsSubmitResponse = AdminCatDropsSubmitResponses[keyof AdminCatDropsSubmitResponses];

export type AdminCatDropsPublishData = {
    body?: never;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}/publish';
};

export type AdminCatDropsPublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsPublishError = AdminCatDropsPublishErrors[keyof AdminCatDropsPublishErrors];

export type AdminCatDropsPublishResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatDropsPublishResponse = AdminCatDropsPublishResponses[keyof AdminCatDropsPublishResponses];

export type AdminCatDropsReturnData = {
    body: ReviewNoteIn;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}/return';
};

export type AdminCatDropsReturnErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsReturnError = AdminCatDropsReturnErrors[keyof AdminCatDropsReturnErrors];

export type AdminCatDropsReturnResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatDropsReturnResponse = AdminCatDropsReturnResponses[keyof AdminCatDropsReturnResponses];

export type AdminCatDropsRejectData = {
    body: ReviewNoteIn;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}/reject';
};

export type AdminCatDropsRejectErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsRejectError = AdminCatDropsRejectErrors[keyof AdminCatDropsRejectErrors];

export type AdminCatDropsRejectResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatDropsRejectResponse = AdminCatDropsRejectResponses[keyof AdminCatDropsRejectResponses];

export type AdminCatDropsUnpublishData = {
    body?: never;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}/unpublish';
};

export type AdminCatDropsUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsUnpublishError = AdminCatDropsUnpublishErrors[keyof AdminCatDropsUnpublishErrors];

export type AdminCatDropsUnpublishResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatDropsUnpublishResponse = AdminCatDropsUnpublishResponses[keyof AdminCatDropsUnpublishResponses];

export type AdminCatDropsArchiveData = {
    body: AppModulesCatSchemasAdminArchiveIn;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}/archive';
};

export type AdminCatDropsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsArchiveError = AdminCatDropsArchiveErrors[keyof AdminCatDropsArchiveErrors];

export type AdminCatDropsArchiveResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatDropsArchiveResponse = AdminCatDropsArchiveResponses[keyof AdminCatDropsArchiveResponses];

export type AdminCatDropsRestoreData = {
    body?: never;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}/restore';
};

export type AdminCatDropsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsRestoreError = AdminCatDropsRestoreErrors[keyof AdminCatDropsRestoreErrors];

export type AdminCatDropsRestoreResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminStatusOut;
};

export type AdminCatDropsRestoreResponse = AdminCatDropsRestoreResponses[keyof AdminCatDropsRestoreResponses];

export type AdminCatDropsUsageData = {
    body?: never;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: never;
    url: '/api/v1/admin/drops/{drop_id}/usage';
};

export type AdminCatDropsUsageErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsUsageError = AdminCatDropsUsageErrors[keyof AdminCatDropsUsageErrors];

export type AdminCatDropsUsageResponses = {
    /**
     * Successful Response
     */
    200: UsageOut;
};

export type AdminCatDropsUsageResponse = AdminCatDropsUsageResponses[keyof AdminCatDropsUsageResponses];

export type AdminCatDropsHistoryData = {
    body?: never;
    path: {
        /**
         * Drop Id
         */
        drop_id: string;
    };
    query?: {
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/drops/{drop_id}/history';
};

export type AdminCatDropsHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCatDropsHistoryError = AdminCatDropsHistoryErrors[keyof AdminCatDropsHistoryErrors];

export type AdminCatDropsHistoryResponses = {
    /**
     * Successful Response
     */
    200: AppModulesCatSchemasAdminHistoryPageOut;
};

export type AdminCatDropsHistoryResponse = AdminCatDropsHistoryResponses[keyof AdminCatDropsHistoryResponses];

export type AdminExpReferenceGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/experiences/reference';
};

export type AdminExpReferenceGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpReferenceGetError = AdminExpReferenceGetErrors[keyof AdminExpReferenceGetErrors];

export type AdminExpReferenceGetResponses = {
    /**
     * Successful Response
     */
    200: ExpReferenceOut;
};

export type AdminExpReferenceGetResponse = AdminExpReferenceGetResponses[keyof AdminExpReferenceGetResponses];

export type AdminExpTastingsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, format, host, kind, place, price, seats, starts_at, status, title
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Format]
         *
         * Формат (online, offline)
         */
        'filter[format]'?: string | null;
        /**
         * Filter[Format][]
         *
         * Формат (online, offline); несколько значений
         */
        'filter[format][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Вид (open, blind)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Вид (open, blind); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Lifecycle]
         *
         * Этап жизни (open, full, finished, cancelled)
         */
        'filter[lifecycle]'?: string | null;
        /**
         * Filter[Lifecycle][]
         *
         * Этап жизни (open, full, finished, cancelled); несколько значений
         */
        'filter[lifecycle][]'?: Array<string> | null;
        /**
         * Filter[Moderation Status]
         *
         * Статус модерации (draft, on_moderation, needs_revision, published, rejected, unpublished)
         */
        'filter[moderation_status]'?: string | null;
        /**
         * Filter[Moderation Status][]
         *
         * Статус модерации (draft, on_moderation, needs_revision, published, rejected, unpublished); несколько значений
         */
        'filter[moderation_status][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус строки: этап жизни опубликованных, иначе модерация; archived — архив (open, full, finished, cancelled, draft, on_moderation, needs_revision, published, rejected, unpublished, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус строки: этап жизни опубликованных, иначе модерация; archived — архив (open, full, finished, cancelled, draft, on_moderation, needs_revision, published, rejected, unpublished, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Host Id]
         *
         * Ведущий
         */
        'filter[host_id]'?: string | null;
        /**
         * Filter[Host Id][]
         *
         * Ведущий; несколько значений
         */
        'filter[host_id][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Venue Winery Id]
         *
         * Винодельня
         */
        'filter[venue_winery_id]'?: string | null;
        /**
         * Filter[Venue Winery Id][]
         *
         * Винодельня; несколько значений
         */
        'filter[venue_winery_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/tastings';
};

export type AdminExpTastingsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsListError = AdminExpTastingsListErrors[keyof AdminExpTastingsListErrors];

export type AdminExpTastingsListResponses = {
    /**
     * Successful Response
     */
    200: PageTastingRow;
};

export type AdminExpTastingsListResponse = AdminExpTastingsListResponses[keyof AdminExpTastingsListResponses];

export type AdminExpTastingsCreateData = {
    body: TastingCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/experiences/tastings';
};

export type AdminExpTastingsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsCreateError = AdminExpTastingsCreateErrors[keyof AdminExpTastingsCreateErrors];

export type AdminExpTastingsCreateResponses = {
    /**
     * Successful Response
     */
    201: TastingAdmin;
};

export type AdminExpTastingsCreateResponse = AdminExpTastingsCreateResponses[keyof AdminExpTastingsCreateResponses];

export type AdminExpTastingsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, format, host, kind, place, price, seats, starts_at, status, title
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Format]
         *
         * Формат (online, offline)
         */
        'filter[format]'?: string | null;
        /**
         * Filter[Format][]
         *
         * Формат (online, offline); несколько значений
         */
        'filter[format][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Вид (open, blind)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Вид (open, blind); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Lifecycle]
         *
         * Этап жизни (open, full, finished, cancelled)
         */
        'filter[lifecycle]'?: string | null;
        /**
         * Filter[Lifecycle][]
         *
         * Этап жизни (open, full, finished, cancelled); несколько значений
         */
        'filter[lifecycle][]'?: Array<string> | null;
        /**
         * Filter[Moderation Status]
         *
         * Статус модерации (draft, on_moderation, needs_revision, published, rejected, unpublished)
         */
        'filter[moderation_status]'?: string | null;
        /**
         * Filter[Moderation Status][]
         *
         * Статус модерации (draft, on_moderation, needs_revision, published, rejected, unpublished); несколько значений
         */
        'filter[moderation_status][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус строки: этап жизни опубликованных, иначе модерация; archived — архив (open, full, finished, cancelled, draft, on_moderation, needs_revision, published, rejected, unpublished, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус строки: этап жизни опубликованных, иначе модерация; archived — архив (open, full, finished, cancelled, draft, on_moderation, needs_revision, published, rejected, unpublished, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Host Id]
         *
         * Ведущий
         */
        'filter[host_id]'?: string | null;
        /**
         * Filter[Host Id][]
         *
         * Ведущий; несколько значений
         */
        'filter[host_id][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Venue Winery Id]
         *
         * Винодельня
         */
        'filter[venue_winery_id]'?: string | null;
        /**
         * Filter[Venue Winery Id][]
         *
         * Винодельня; несколько значений
         */
        'filter[venue_winery_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/tastings/export';
};

export type AdminExpTastingsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsExportError = AdminExpTastingsExportErrors[keyof AdminExpTastingsExportErrors];

export type AdminExpTastingsExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminExpTastingsDeleteData = {
    body?: never;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query: {
        /**
         * Confirm Title
         *
         * Точное название записи (BR-EXP-63)
         */
        confirm_title: string;
    };
    url: '/api/v1/admin/experiences/tastings/{tasting_id}';
};

export type AdminExpTastingsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsDeleteError = AdminExpTastingsDeleteErrors[keyof AdminExpTastingsDeleteErrors];

export type AdminExpTastingsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminExpTastingsDeleteResponse = AdminExpTastingsDeleteResponses[keyof AdminExpTastingsDeleteResponses];

export type AdminExpTastingsGetData = {
    body?: never;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/tastings/{tasting_id}';
};

export type AdminExpTastingsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsGetError = AdminExpTastingsGetErrors[keyof AdminExpTastingsGetErrors];

export type AdminExpTastingsGetResponses = {
    /**
     * Successful Response
     */
    200: TastingAdmin;
};

export type AdminExpTastingsGetResponse = AdminExpTastingsGetResponses[keyof AdminExpTastingsGetResponses];

export type AdminExpTastingsUpdateData = {
    body: TastingPatchIn;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/tastings/{tasting_id}';
};

export type AdminExpTastingsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsUpdateError = AdminExpTastingsUpdateErrors[keyof AdminExpTastingsUpdateErrors];

export type AdminExpTastingsUpdateResponses = {
    /**
     * Successful Response
     */
    200: TastingAdmin;
};

export type AdminExpTastingsUpdateResponse = AdminExpTastingsUpdateResponses[keyof AdminExpTastingsUpdateResponses];

export type AdminExpTastingsWinesUpdateData = {
    body: TastingWinesIn;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/tastings/{tasting_id}/wines';
};

export type AdminExpTastingsWinesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsWinesUpdateError = AdminExpTastingsWinesUpdateErrors[keyof AdminExpTastingsWinesUpdateErrors];

export type AdminExpTastingsWinesUpdateResponses = {
    /**
     * Successful Response
     */
    200: TastingAdmin;
};

export type AdminExpTastingsWinesUpdateResponse = AdminExpTastingsWinesUpdateResponses[keyof AdminExpTastingsWinesUpdateResponses];

export type AdminExpTastingsContentUpdateData = {
    body: ContentIn;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/tastings/{tasting_id}/content';
};

export type AdminExpTastingsContentUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsContentUpdateError = AdminExpTastingsContentUpdateErrors[keyof AdminExpTastingsContentUpdateErrors];

export type AdminExpTastingsContentUpdateResponses = {
    /**
     * Successful Response
     */
    200: TastingAdmin;
};

export type AdminExpTastingsContentUpdateResponse = AdminExpTastingsContentUpdateResponses[keyof AdminExpTastingsContentUpdateResponses];

export type AdminExpTastingsCancelData = {
    body: CancelEventIn;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/tastings/{tasting_id}/cancel';
};

export type AdminExpTastingsCancelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsCancelError = AdminExpTastingsCancelErrors[keyof AdminExpTastingsCancelErrors];

export type AdminExpTastingsCancelResponses = {
    /**
     * Successful Response
     */
    200: CancelEventOut;
};

export type AdminExpTastingsCancelResponse = AdminExpTastingsCancelResponses[keyof AdminExpTastingsCancelResponses];

export type AdminExpTastingsArchiveData = {
    body: EventArchiveIn;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/tastings/{tasting_id}/archive';
};

export type AdminExpTastingsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsArchiveError = AdminExpTastingsArchiveErrors[keyof AdminExpTastingsArchiveErrors];

export type AdminExpTastingsArchiveResponses = {
    /**
     * Successful Response
     */
    200: TastingAdmin;
};

export type AdminExpTastingsArchiveResponse = AdminExpTastingsArchiveResponses[keyof AdminExpTastingsArchiveResponses];

export type AdminExpTastingsRestoreData = {
    body?: never;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/tastings/{tasting_id}/restore';
};

export type AdminExpTastingsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTastingsRestoreError = AdminExpTastingsRestoreErrors[keyof AdminExpTastingsRestoreErrors];

export type AdminExpTastingsRestoreResponses = {
    /**
     * Successful Response
     */
    200: TastingAdmin;
};

export type AdminExpTastingsRestoreResponse = AdminExpTastingsRestoreResponses[keyof AdminExpTastingsRestoreResponses];

export type AdminExpExcursionsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, duration, price, slots, status, title, winery
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Статус модерации; archived — архив (draft, on_moderation, needs_revision, published, rejected, unpublished, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус модерации; archived — архив (draft, on_moderation, needs_revision, published, rejected, unpublished, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Moderation Status]
         *
         * Статус модерации (draft, on_moderation, needs_revision, published, rejected, unpublished)
         */
        'filter[moderation_status]'?: string | null;
        /**
         * Filter[Moderation Status][]
         *
         * Статус модерации (draft, on_moderation, needs_revision, published, rejected, unpublished); несколько значений
         */
        'filter[moderation_status][]'?: Array<string> | null;
        /**
         * Filter[Winery Id]
         *
         * Винодельня
         */
        'filter[winery_id]'?: string | null;
        /**
         * Filter[Winery Id][]
         *
         * Винодельня; несколько значений
         */
        'filter[winery_id][]'?: Array<string> | null;
        /**
         * Filter[Schedule Mode]
         *
         * Периодичность (once, weekly, on_request)
         */
        'filter[schedule_mode]'?: string | null;
        /**
         * Filter[Schedule Mode][]
         *
         * Периодичность (once, weekly, on_request); несколько значений
         */
        'filter[schedule_mode][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/excursions';
};

export type AdminExpExcursionsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsListError = AdminExpExcursionsListErrors[keyof AdminExpExcursionsListErrors];

export type AdminExpExcursionsListResponses = {
    /**
     * Successful Response
     */
    200: PageExcursionRow;
};

export type AdminExpExcursionsListResponse = AdminExpExcursionsListResponses[keyof AdminExpExcursionsListResponses];

export type AdminExpExcursionsCreateData = {
    body: ExcursionCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/experiences/excursions';
};

export type AdminExpExcursionsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsCreateError = AdminExpExcursionsCreateErrors[keyof AdminExpExcursionsCreateErrors];

export type AdminExpExcursionsCreateResponses = {
    /**
     * Successful Response
     */
    201: ExcursionAdmin;
};

export type AdminExpExcursionsCreateResponse = AdminExpExcursionsCreateResponses[keyof AdminExpExcursionsCreateResponses];

export type AdminExpExcursionsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, duration, price, slots, status, title, winery
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Статус модерации; archived — архив (draft, on_moderation, needs_revision, published, rejected, unpublished, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус модерации; archived — архив (draft, on_moderation, needs_revision, published, rejected, unpublished, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Moderation Status]
         *
         * Статус модерации (draft, on_moderation, needs_revision, published, rejected, unpublished)
         */
        'filter[moderation_status]'?: string | null;
        /**
         * Filter[Moderation Status][]
         *
         * Статус модерации (draft, on_moderation, needs_revision, published, rejected, unpublished); несколько значений
         */
        'filter[moderation_status][]'?: Array<string> | null;
        /**
         * Filter[Winery Id]
         *
         * Винодельня
         */
        'filter[winery_id]'?: string | null;
        /**
         * Filter[Winery Id][]
         *
         * Винодельня; несколько значений
         */
        'filter[winery_id][]'?: Array<string> | null;
        /**
         * Filter[Schedule Mode]
         *
         * Периодичность (once, weekly, on_request)
         */
        'filter[schedule_mode]'?: string | null;
        /**
         * Filter[Schedule Mode][]
         *
         * Периодичность (once, weekly, on_request); несколько значений
         */
        'filter[schedule_mode][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/excursions/export';
};

export type AdminExpExcursionsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsExportError = AdminExpExcursionsExportErrors[keyof AdminExpExcursionsExportErrors];

export type AdminExpExcursionsExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminExpExcursionsDeleteData = {
    body?: never;
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query: {
        /**
         * Confirm Title
         *
         * Точное название записи (BR-EXP-63)
         */
        confirm_title: string;
    };
    url: '/api/v1/admin/experiences/excursions/{excursion_id}';
};

export type AdminExpExcursionsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsDeleteError = AdminExpExcursionsDeleteErrors[keyof AdminExpExcursionsDeleteErrors];

export type AdminExpExcursionsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminExpExcursionsDeleteResponse = AdminExpExcursionsDeleteResponses[keyof AdminExpExcursionsDeleteResponses];

export type AdminExpExcursionsGetData = {
    body?: never;
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/excursions/{excursion_id}';
};

export type AdminExpExcursionsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsGetError = AdminExpExcursionsGetErrors[keyof AdminExpExcursionsGetErrors];

export type AdminExpExcursionsGetResponses = {
    /**
     * Successful Response
     */
    200: ExcursionAdmin;
};

export type AdminExpExcursionsGetResponse = AdminExpExcursionsGetResponses[keyof AdminExpExcursionsGetResponses];

export type AdminExpExcursionsUpdateData = {
    body: ExcursionPatchIn;
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/excursions/{excursion_id}';
};

export type AdminExpExcursionsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsUpdateError = AdminExpExcursionsUpdateErrors[keyof AdminExpExcursionsUpdateErrors];

export type AdminExpExcursionsUpdateResponses = {
    /**
     * Successful Response
     */
    200: ExcursionAdmin;
};

export type AdminExpExcursionsUpdateResponse = AdminExpExcursionsUpdateResponses[keyof AdminExpExcursionsUpdateResponses];

export type AdminExpExcursionsWeekSlotsUpdateData = {
    body: WeekSlotsIn;
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/excursions/{excursion_id}/week-slots';
};

export type AdminExpExcursionsWeekSlotsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsWeekSlotsUpdateError = AdminExpExcursionsWeekSlotsUpdateErrors[keyof AdminExpExcursionsWeekSlotsUpdateErrors];

export type AdminExpExcursionsWeekSlotsUpdateResponses = {
    /**
     * Successful Response
     */
    200: ExcursionAdmin;
};

export type AdminExpExcursionsWeekSlotsUpdateResponse = AdminExpExcursionsWeekSlotsUpdateResponses[keyof AdminExpExcursionsWeekSlotsUpdateResponses];

export type AdminExpExcursionsContentUpdateData = {
    body: ContentIn;
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/excursions/{excursion_id}/content';
};

export type AdminExpExcursionsContentUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsContentUpdateError = AdminExpExcursionsContentUpdateErrors[keyof AdminExpExcursionsContentUpdateErrors];

export type AdminExpExcursionsContentUpdateResponses = {
    /**
     * Successful Response
     */
    200: ExcursionAdmin;
};

export type AdminExpExcursionsContentUpdateResponse = AdminExpExcursionsContentUpdateResponses[keyof AdminExpExcursionsContentUpdateResponses];

export type AdminExpExcursionsCancelData = {
    body: CancelEventIn;
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/excursions/{excursion_id}/cancel';
};

export type AdminExpExcursionsCancelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsCancelError = AdminExpExcursionsCancelErrors[keyof AdminExpExcursionsCancelErrors];

export type AdminExpExcursionsCancelResponses = {
    /**
     * Successful Response
     */
    200: CancelEventOut;
};

export type AdminExpExcursionsCancelResponse = AdminExpExcursionsCancelResponses[keyof AdminExpExcursionsCancelResponses];

export type AdminExpExcursionsArchiveData = {
    body: EventArchiveIn;
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/excursions/{excursion_id}/archive';
};

export type AdminExpExcursionsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsArchiveError = AdminExpExcursionsArchiveErrors[keyof AdminExpExcursionsArchiveErrors];

export type AdminExpExcursionsArchiveResponses = {
    /**
     * Successful Response
     */
    200: ExcursionAdmin;
};

export type AdminExpExcursionsArchiveResponse = AdminExpExcursionsArchiveResponses[keyof AdminExpExcursionsArchiveResponses];

export type AdminExpExcursionsRestoreData = {
    body?: never;
    path: {
        /**
         * Excursion Id
         */
        excursion_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/excursions/{excursion_id}/restore';
};

export type AdminExpExcursionsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpExcursionsRestoreError = AdminExpExcursionsRestoreErrors[keyof AdminExpExcursionsRestoreErrors];

export type AdminExpExcursionsRestoreResponses = {
    /**
     * Successful Response
     */
    200: ExcursionAdmin;
};

export type AdminExpExcursionsRestoreResponse = AdminExpExcursionsRestoreResponses[keyof AdminExpExcursionsRestoreResponses];

export type AdminExpTripsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, duration, group, host, price, region, status, title
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Lifecycle]
         *
         * Этап жизни (open, full, finished, cancelled)
         */
        'filter[lifecycle]'?: string | null;
        /**
         * Filter[Lifecycle][]
         *
         * Этап жизни (open, full, finished, cancelled); несколько значений
         */
        'filter[lifecycle][]'?: Array<string> | null;
        /**
         * Filter[Moderation Status]
         *
         * Модерация (draft, on_moderation, needs_revision, published, rejected, unpublished)
         */
        'filter[moderation_status]'?: string | null;
        /**
         * Filter[Moderation Status][]
         *
         * Модерация (draft, on_moderation, needs_revision, published, rejected, unpublished); несколько значений
         */
        'filter[moderation_status][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус строки (open, full, finished, cancelled, draft, on_moderation, needs_revision, published, rejected, unpublished, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус строки (open, full, finished, cancelled, draft, on_moderation, needs_revision, published, rejected, unpublished, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Host Id]
         *
         * Ведущий
         */
        'filter[host_id]'?: string | null;
        /**
         * Filter[Host Id][]
         *
         * Ведущий; несколько значений
         */
        'filter[host_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/trips';
};

export type AdminExpTripsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsListError = AdminExpTripsListErrors[keyof AdminExpTripsListErrors];

export type AdminExpTripsListResponses = {
    /**
     * Successful Response
     */
    200: PageTripRow;
};

export type AdminExpTripsListResponse = AdminExpTripsListResponses[keyof AdminExpTripsListResponses];

export type AdminExpTripsCreateData = {
    body: TripCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/experiences/trips';
};

export type AdminExpTripsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsCreateError = AdminExpTripsCreateErrors[keyof AdminExpTripsCreateErrors];

export type AdminExpTripsCreateResponses = {
    /**
     * Successful Response
     */
    201: TripAdmin;
};

export type AdminExpTripsCreateResponse = AdminExpTripsCreateResponses[keyof AdminExpTripsCreateResponses];

export type AdminExpTripsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, duration, group, host, price, region, status, title
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Lifecycle]
         *
         * Этап жизни (open, full, finished, cancelled)
         */
        'filter[lifecycle]'?: string | null;
        /**
         * Filter[Lifecycle][]
         *
         * Этап жизни (open, full, finished, cancelled); несколько значений
         */
        'filter[lifecycle][]'?: Array<string> | null;
        /**
         * Filter[Moderation Status]
         *
         * Модерация (draft, on_moderation, needs_revision, published, rejected, unpublished)
         */
        'filter[moderation_status]'?: string | null;
        /**
         * Filter[Moderation Status][]
         *
         * Модерация (draft, on_moderation, needs_revision, published, rejected, unpublished); несколько значений
         */
        'filter[moderation_status][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус строки (open, full, finished, cancelled, draft, on_moderation, needs_revision, published, rejected, unpublished, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус строки (open, full, finished, cancelled, draft, on_moderation, needs_revision, published, rejected, unpublished, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Host Id]
         *
         * Ведущий
         */
        'filter[host_id]'?: string | null;
        /**
         * Filter[Host Id][]
         *
         * Ведущий; несколько значений
         */
        'filter[host_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/trips/export';
};

export type AdminExpTripsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsExportError = AdminExpTripsExportErrors[keyof AdminExpTripsExportErrors];

export type AdminExpTripsExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminExpTripsDeleteData = {
    body?: never;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query: {
        /**
         * Confirm Title
         *
         * Точное название записи (BR-EXP-63)
         */
        confirm_title: string;
    };
    url: '/api/v1/admin/experiences/trips/{trip_id}';
};

export type AdminExpTripsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsDeleteError = AdminExpTripsDeleteErrors[keyof AdminExpTripsDeleteErrors];

export type AdminExpTripsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminExpTripsDeleteResponse = AdminExpTripsDeleteResponses[keyof AdminExpTripsDeleteResponses];

export type AdminExpTripsGetData = {
    body?: never;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/trips/{trip_id}';
};

export type AdminExpTripsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsGetError = AdminExpTripsGetErrors[keyof AdminExpTripsGetErrors];

export type AdminExpTripsGetResponses = {
    /**
     * Successful Response
     */
    200: TripAdmin;
};

export type AdminExpTripsGetResponse = AdminExpTripsGetResponses[keyof AdminExpTripsGetResponses];

export type AdminExpTripsUpdateData = {
    body: TripPatchIn;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/trips/{trip_id}';
};

export type AdminExpTripsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsUpdateError = AdminExpTripsUpdateErrors[keyof AdminExpTripsUpdateErrors];

export type AdminExpTripsUpdateResponses = {
    /**
     * Successful Response
     */
    200: TripAdmin;
};

export type AdminExpTripsUpdateResponse = AdminExpTripsUpdateResponses[keyof AdminExpTripsUpdateResponses];

export type AdminExpTripsDeparturesUpdateData = {
    body: DeparturesIn;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/trips/{trip_id}/departures';
};

export type AdminExpTripsDeparturesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsDeparturesUpdateError = AdminExpTripsDeparturesUpdateErrors[keyof AdminExpTripsDeparturesUpdateErrors];

export type AdminExpTripsDeparturesUpdateResponses = {
    /**
     * Successful Response
     */
    200: TripAdmin;
};

export type AdminExpTripsDeparturesUpdateResponse = AdminExpTripsDeparturesUpdateResponses[keyof AdminExpTripsDeparturesUpdateResponses];

export type AdminExpTripsStayUpdateData = {
    body: StayIn;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/trips/{trip_id}/stay';
};

export type AdminExpTripsStayUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsStayUpdateError = AdminExpTripsStayUpdateErrors[keyof AdminExpTripsStayUpdateErrors];

export type AdminExpTripsStayUpdateResponses = {
    /**
     * Successful Response
     */
    200: TripAdmin;
};

export type AdminExpTripsStayUpdateResponse = AdminExpTripsStayUpdateResponses[keyof AdminExpTripsStayUpdateResponses];

export type AdminExpTripsContentUpdateData = {
    body: TripContentIn;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/trips/{trip_id}/content';
};

export type AdminExpTripsContentUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsContentUpdateError = AdminExpTripsContentUpdateErrors[keyof AdminExpTripsContentUpdateErrors];

export type AdminExpTripsContentUpdateResponses = {
    /**
     * Successful Response
     */
    200: TripAdmin;
};

export type AdminExpTripsContentUpdateResponse = AdminExpTripsContentUpdateResponses[keyof AdminExpTripsContentUpdateResponses];

export type AdminExpTripsCancelData = {
    body: CancelEventIn;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/trips/{trip_id}/cancel';
};

export type AdminExpTripsCancelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsCancelError = AdminExpTripsCancelErrors[keyof AdminExpTripsCancelErrors];

export type AdminExpTripsCancelResponses = {
    /**
     * Successful Response
     */
    200: CancelEventOut;
};

export type AdminExpTripsCancelResponse = AdminExpTripsCancelResponses[keyof AdminExpTripsCancelResponses];

export type AdminExpTripsArchiveData = {
    body: EventArchiveIn;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/trips/{trip_id}/archive';
};

export type AdminExpTripsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsArchiveError = AdminExpTripsArchiveErrors[keyof AdminExpTripsArchiveErrors];

export type AdminExpTripsArchiveResponses = {
    /**
     * Successful Response
     */
    200: TripAdmin;
};

export type AdminExpTripsArchiveResponse = AdminExpTripsArchiveResponses[keyof AdminExpTripsArchiveResponses];

export type AdminExpTripsRestoreData = {
    body?: never;
    path: {
        /**
         * Trip Id
         */
        trip_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/trips/{trip_id}/restore';
};

export type AdminExpTripsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpTripsRestoreError = AdminExpTripsRestoreErrors[keyof AdminExpTripsRestoreErrors];

export type AdminExpTripsRestoreResponses = {
    /**
     * Successful Response
     */
    200: TripAdmin;
};

export type AdminExpTripsRestoreResponse = AdminExpTripsRestoreResponses[keyof AdminExpTripsRestoreResponses];

export type AdminExpBookingsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: amount, created_at, number, people, status, type, user, when
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Type]
         *
         * Тип брони (tasting_online, tasting_offline, blind_online, blind_offline, excursion, trip)
         */
        'filter[type]'?: string | null;
        /**
         * Filter[Type][]
         *
         * Тип брони (tasting_online, tasting_offline, blind_online, blind_offline, excursion, trip); несколько значений
         */
        'filter[type][]'?: Array<string> | null;
        /**
         * Filter[Target Type]
         *
         * Вид объекта: tasting | excursion | trip (tasting, excursion, trip)
         */
        'filter[target_type]'?: string | null;
        /**
         * Filter[Target Type][]
         *
         * Вид объекта: tasting | excursion | trip (tasting, excursion, trip); несколько значений
         */
        'filter[target_type][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус (pending_approval, awaiting_payment, confirmed, waitlisted, attended, no_show, cancelled, cancelled_by_organizer, declined)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус (pending_approval, awaiting_payment, confirmed, waitlisted, attended, no_show, cancelled, cancelled_by_organizer, declined); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Target Id]
         *
         * Объект брони (дегустация, экскурсия, поездка)
         */
        'filter[target_id]'?: string | null;
        /**
         * Filter[Target Id][]
         *
         * Объект брони (дегустация, экскурсия, поездка); несколько значений
         */
        'filter[target_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/bookings';
};

export type AdminExpBookingsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsListError = AdminExpBookingsListErrors[keyof AdminExpBookingsListErrors];

export type AdminExpBookingsListResponses = {
    /**
     * Successful Response
     */
    200: PageBookingRow;
};

export type AdminExpBookingsListResponse = AdminExpBookingsListResponses[keyof AdminExpBookingsListResponses];

export type AdminExpBookingsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: amount, created_at, number, people, status, type, user, when
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Type]
         *
         * Тип брони (tasting_online, tasting_offline, blind_online, blind_offline, excursion, trip)
         */
        'filter[type]'?: string | null;
        /**
         * Filter[Type][]
         *
         * Тип брони (tasting_online, tasting_offline, blind_online, blind_offline, excursion, trip); несколько значений
         */
        'filter[type][]'?: Array<string> | null;
        /**
         * Filter[Target Type]
         *
         * Вид объекта: tasting | excursion | trip (tasting, excursion, trip)
         */
        'filter[target_type]'?: string | null;
        /**
         * Filter[Target Type][]
         *
         * Вид объекта: tasting | excursion | trip (tasting, excursion, trip); несколько значений
         */
        'filter[target_type][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус (pending_approval, awaiting_payment, confirmed, waitlisted, attended, no_show, cancelled, cancelled_by_organizer, declined)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус (pending_approval, awaiting_payment, confirmed, waitlisted, attended, no_show, cancelled, cancelled_by_organizer, declined); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Target Id]
         *
         * Объект брони (дегустация, экскурсия, поездка)
         */
        'filter[target_id]'?: string | null;
        /**
         * Filter[Target Id][]
         *
         * Объект брони (дегустация, экскурсия, поездка); несколько значений
         */
        'filter[target_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/bookings/export';
};

export type AdminExpBookingsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsExportError = AdminExpBookingsExportErrors[keyof AdminExpBookingsExportErrors];

export type AdminExpBookingsExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminExpBookingsGetData = {
    body?: never;
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/bookings/{booking_id}';
};

export type AdminExpBookingsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsGetError = AdminExpBookingsGetErrors[keyof AdminExpBookingsGetErrors];

export type AdminExpBookingsGetResponses = {
    /**
     * Successful Response
     */
    200: BookingAdmin;
};

export type AdminExpBookingsGetResponse = AdminExpBookingsGetResponses[keyof AdminExpBookingsGetResponses];

export type AdminExpBookingsApproveData = {
    body: ApproveIn;
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/bookings/{booking_id}/approve';
};

export type AdminExpBookingsApproveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsApproveError = AdminExpBookingsApproveErrors[keyof AdminExpBookingsApproveErrors];

export type AdminExpBookingsApproveResponses = {
    /**
     * Successful Response
     */
    200: BookingAdmin;
};

export type AdminExpBookingsApproveResponse = AdminExpBookingsApproveResponses[keyof AdminExpBookingsApproveResponses];

export type AdminExpBookingsDeclineData = {
    body: DeclineIn;
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/bookings/{booking_id}/decline';
};

export type AdminExpBookingsDeclineErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsDeclineError = AdminExpBookingsDeclineErrors[keyof AdminExpBookingsDeclineErrors];

export type AdminExpBookingsDeclineResponses = {
    /**
     * Successful Response
     */
    200: BookingAdmin;
};

export type AdminExpBookingsDeclineResponse = AdminExpBookingsDeclineResponses[keyof AdminExpBookingsDeclineResponses];

export type AdminExpBookingsCancelData = {
    body: AdminCancelIn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/bookings/{booking_id}/cancel';
};

export type AdminExpBookingsCancelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsCancelError = AdminExpBookingsCancelErrors[keyof AdminExpBookingsCancelErrors];

export type AdminExpBookingsCancelResponses = {
    /**
     * Successful Response
     */
    200: BookingAdmin;
};

export type AdminExpBookingsCancelResponse = AdminExpBookingsCancelResponses[keyof AdminExpBookingsCancelResponses];

export type AdminExpBookingsRemindPaymentData = {
    body?: never;
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/bookings/{booking_id}/remind-payment';
};

export type AdminExpBookingsRemindPaymentErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsRemindPaymentError = AdminExpBookingsRemindPaymentErrors[keyof AdminExpBookingsRemindPaymentErrors];

export type AdminExpBookingsRemindPaymentResponses = {
    /**
     * Successful Response
     */
    202: AcceptedOut;
};

export type AdminExpBookingsRemindPaymentResponse = AdminExpBookingsRemindPaymentResponses[keyof AdminExpBookingsRemindPaymentResponses];

export type AdminExpBookingsMarkAttendedData = {
    body?: never;
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/bookings/{booking_id}/mark-attended';
};

export type AdminExpBookingsMarkAttendedErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsMarkAttendedError = AdminExpBookingsMarkAttendedErrors[keyof AdminExpBookingsMarkAttendedErrors];

export type AdminExpBookingsMarkAttendedResponses = {
    /**
     * Successful Response
     */
    200: BookingAdmin;
};

export type AdminExpBookingsMarkAttendedResponse = AdminExpBookingsMarkAttendedResponses[keyof AdminExpBookingsMarkAttendedResponses];

export type AdminExpBookingsMarkNoShowData = {
    body?: never;
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/bookings/{booking_id}/mark-no-show';
};

export type AdminExpBookingsMarkNoShowErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsMarkNoShowError = AdminExpBookingsMarkNoShowErrors[keyof AdminExpBookingsMarkNoShowErrors];

export type AdminExpBookingsMarkNoShowResponses = {
    /**
     * Successful Response
     */
    200: BookingAdmin;
};

export type AdminExpBookingsMarkNoShowResponse = AdminExpBookingsMarkNoShowResponses[keyof AdminExpBookingsMarkNoShowResponses];

export type AdminExpBookingsOfferSeatData = {
    body?: never;
    path: {
        /**
         * Booking Id
         */
        booking_id: string;
    };
    query?: never;
    url: '/api/v1/admin/experiences/bookings/{booking_id}/offer-seat';
};

export type AdminExpBookingsOfferSeatErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpBookingsOfferSeatError = AdminExpBookingsOfferSeatErrors[keyof AdminExpBookingsOfferSeatErrors];

export type AdminExpBookingsOfferSeatResponses = {
    /**
     * Successful Response
     */
    200: BookingAdmin;
};

export type AdminExpBookingsOfferSeatResponse = AdminExpBookingsOfferSeatResponses[keyof AdminExpBookingsOfferSeatResponses];

export type AdminExpAttendanceListData = {
    body?: never;
    path?: never;
    query: {
        /**
         * tasting — «Статистика дегустаций», excursion, trip
         */
        kind: BookingTarget;
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: attended_at, people, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Target Id]
         *
         * Событие
         */
        'filter[target_id]'?: string | null;
        /**
         * Filter[Target Id][]
         *
         * Событие; несколько значений
         */
        'filter[target_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Type]
         *
         * Тип (tasting_online, tasting_offline, blind_online, blind_offline, excursion, trip)
         */
        'filter[type]'?: string | null;
        /**
         * Filter[Type][]
         *
         * Тип (tasting_online, tasting_offline, blind_online, blind_offline, excursion, trip); несколько значений
         */
        'filter[type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/attendance';
};

export type AdminExpAttendanceListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpAttendanceListError = AdminExpAttendanceListErrors[keyof AdminExpAttendanceListErrors];

export type AdminExpAttendanceListResponses = {
    /**
     * Successful Response
     */
    200: PageAttendanceRow;
};

export type AdminExpAttendanceListResponse = AdminExpAttendanceListResponses[keyof AdminExpAttendanceListResponses];

export type AdminExpAttendanceExportData = {
    body?: never;
    path?: never;
    query: {
        /**
         * tasting — «Статистика дегустаций», excursion, trip
         */
        kind: BookingTarget;
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: attended_at, people, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Target Id]
         *
         * Событие
         */
        'filter[target_id]'?: string | null;
        /**
         * Filter[Target Id][]
         *
         * Событие; несколько значений
         */
        'filter[target_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Type]
         *
         * Тип (tasting_online, tasting_offline, blind_online, blind_offline, excursion, trip)
         */
        'filter[type]'?: string | null;
        /**
         * Filter[Type][]
         *
         * Тип (tasting_online, tasting_offline, blind_online, blind_offline, excursion, trip); несколько значений
         */
        'filter[type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/experiences/attendance/export';
};

export type AdminExpAttendanceExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminExpAttendanceExportError = AdminExpAttendanceExportErrors[keyof AdminExpAttendanceExportErrors];

export type AdminExpAttendanceExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminSrvDashboardGetData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * View
         */
        view?: 'overview' | 'dynamics' | null;
        /**
         * Period
         */
        period?: 'week' | 'month' | 'year' | 'all' | 'custom' | null;
        /**
         * Month
         *
         * YYYY-MM
         */
        month?: string | null;
        /**
         * Year
         */
        year?: number | null;
        /**
         * From
         *
         * YYYY-MM-DD
         */
        from?: string | null;
        /**
         * To
         *
         * YYYY-MM-DD
         */
        to?: string | null;
    };
    url: '/api/v1/admin/dashboard';
};

export type AdminSrvDashboardGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvDashboardGetError = AdminSrvDashboardGetErrors[keyof AdminSrvDashboardGetErrors];

export type AdminSrvDashboardGetResponses = {
    /**
     * Successful Response
     */
    200: DashboardOut;
};

export type AdminSrvDashboardGetResponse = AdminSrvDashboardGetResponses[keyof AdminSrvDashboardGetResponses];

export type AdminSrvModerationQueueListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Tab
         *
         * waiting | returned
         */
        tab?: 'waiting' | 'returned';
        /**
         * Group
         *
         * Сегмент группы
         */
        group?: 'all' | 'catalog' | 'learning' | 'experiences' | 'people' | 'documents';
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Sort
         *
         * title, kind, author, note, age, status; `-` — убывание
         */
        sort?: string | null;
        /**
         * Page
         *
         * Страница, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * 25, 50 или 100
         */
        page_size?: number;
        /**
         * Filter[Title]
         */
        'filter[title]'?: string | null;
        /**
         * Filter[Kind]
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Author]
         */
        'filter[author]'?: string | null;
        /**
         * Filter[Note]
         */
        'filter[note]'?: string | null;
        /**
         * Filter[Age]
         */
        'filter[age]'?: string | null;
        /**
         * Filter[Status]
         */
        'filter[status]'?: string | null;
    };
    url: '/api/v1/admin/moderation/queue';
};

export type AdminSrvModerationQueueListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvModerationQueueListError = AdminSrvModerationQueueListErrors[keyof AdminSrvModerationQueueListErrors];

export type AdminSrvModerationQueueListResponses = {
    /**
     * Successful Response
     */
    200: ModerationQueuePageOut;
};

export type AdminSrvModerationQueueListResponse = AdminSrvModerationQueueListResponses[keyof AdminSrvModerationQueueListResponses];

export type AdminSrvModerationCountersGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/moderation/counters';
};

export type AdminSrvModerationCountersGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvModerationCountersGetError = AdminSrvModerationCountersGetErrors[keyof AdminSrvModerationCountersGetErrors];

export type AdminSrvModerationCountersGetResponses = {
    /**
     * Successful Response
     */
    200: ModerationCountersOut;
};

export type AdminSrvModerationCountersGetResponse = AdminSrvModerationCountersGetResponses[keyof AdminSrvModerationCountersGetResponses];

export type AdminSrvModerationDecisionsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Scope
         *
         * session | all
         */
        scope?: 'session' | 'all';
        /**
         * Group
         *
         * Сегмент группы
         */
        group?: 'all' | 'catalog' | 'learning' | 'experiences' | 'people' | 'documents';
        /**
         * Q
         */
        q?: string | null;
        /**
         * Sort
         */
        sort?: string | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         *
         * 25, 50 или 100
         */
        page_size?: number;
        /**
         * Filter[Title]
         */
        'filter[title]'?: string | null;
        /**
         * Filter[Kind]
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Decided By]
         */
        'filter[decided_by]'?: string | null;
        /**
         * Filter[Status]
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Comment]
         */
        'filter[comment]'?: string | null;
    };
    url: '/api/v1/admin/moderation/decisions';
};

export type AdminSrvModerationDecisionsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvModerationDecisionsListError = AdminSrvModerationDecisionsListErrors[keyof AdminSrvModerationDecisionsListErrors];

export type AdminSrvModerationDecisionsListResponses = {
    /**
     * Successful Response
     */
    200: ModerationDecisionsPageOut;
};

export type AdminSrvModerationDecisionsListResponse = AdminSrvModerationDecisionsListResponses[keyof AdminSrvModerationDecisionsListResponses];

export type AdminSrvModerationGetData = {
    body?: never;
    path: {
        /**
         * Entity Type
         */
        entity_type: string;
        /**
         * Entity Id
         */
        entity_id: string;
    };
    query?: never;
    url: '/api/v1/admin/moderation/{entity_type}/{entity_id}';
};

export type AdminSrvModerationGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvModerationGetError = AdminSrvModerationGetErrors[keyof AdminSrvModerationGetErrors];

export type AdminSrvModerationGetResponses = {
    /**
     * Successful Response
     */
    200: ModerationStateOut;
};

export type AdminSrvModerationGetResponse = AdminSrvModerationGetResponses[keyof AdminSrvModerationGetResponses];

export type AdminSrvModerationSubmitData = {
    /**
     * Body
     */
    body?: ModerationActionIn | null;
    path: {
        /**
         * Entity Type
         */
        entity_type: string;
        /**
         * Entity Id
         */
        entity_id: string;
    };
    query?: never;
    url: '/api/v1/admin/moderation/{entity_type}/{entity_id}/submit';
};

export type AdminSrvModerationSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvModerationSubmitError = AdminSrvModerationSubmitErrors[keyof AdminSrvModerationSubmitErrors];

export type AdminSrvModerationSubmitResponses = {
    /**
     * Successful Response
     */
    200: ModerationStateOut;
};

export type AdminSrvModerationSubmitResponse = AdminSrvModerationSubmitResponses[keyof AdminSrvModerationSubmitResponses];

export type AdminSrvModerationDecideData = {
    body: ModerationDecisionIn;
    path: {
        /**
         * Entity Type
         */
        entity_type: string;
        /**
         * Entity Id
         */
        entity_id: string;
    };
    query?: never;
    url: '/api/v1/admin/moderation/{entity_type}/{entity_id}/decision';
};

export type AdminSrvModerationDecideErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvModerationDecideError = AdminSrvModerationDecideErrors[keyof AdminSrvModerationDecideErrors];

export type AdminSrvModerationDecideResponses = {
    /**
     * Successful Response
     */
    200: ModerationStateOut;
};

export type AdminSrvModerationDecideResponse = AdminSrvModerationDecideResponses[keyof AdminSrvModerationDecideResponses];

export type AdminSrvModerationUnpublishData = {
    /**
     * Body
     */
    body?: ModerationActionIn | null;
    path: {
        /**
         * Entity Type
         */
        entity_type: string;
        /**
         * Entity Id
         */
        entity_id: string;
    };
    query?: never;
    url: '/api/v1/admin/moderation/{entity_type}/{entity_id}/unpublish';
};

export type AdminSrvModerationUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvModerationUnpublishError = AdminSrvModerationUnpublishErrors[keyof AdminSrvModerationUnpublishErrors];

export type AdminSrvModerationUnpublishResponses = {
    /**
     * Successful Response
     */
    200: ModerationStateOut;
};

export type AdminSrvModerationUnpublishResponse = AdminSrvModerationUnpublishResponses[keyof AdminSrvModerationUnpublishResponses];

export type AdminSrvAuditListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Category
         */
        category?: AuditCategory | null;
        /**
         * Actor Id
         */
        actor_id?: string | null;
        /**
         * Entity Type
         */
        entity_type?: string | null;
        /**
         * Entity Id
         */
        entity_id?: string | null;
        /**
         * From
         *
         * С даты
         */
        from?: string | null;
        /**
         * To
         *
         * По дату включительно
         */
        to?: string | null;
        /**
         * Q
         */
        q?: string | null;
        /**
         * Sort
         */
        sort?: 'occurred_at' | '-occurred_at' | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         *
         * 25, 50 или 100
         */
        page_size?: number;
        /**
         * Filter[Actor]
         */
        'filter[actor]'?: string | null;
        /**
         * Filter[Role]
         */
        'filter[role]'?: string | null;
        /**
         * Filter[Entity]
         */
        'filter[entity]'?: string | null;
        /**
         * Filter[Action]
         */
        'filter[action]'?: string | null;
        /**
         * Filter[Summary]
         */
        'filter[summary]'?: string | null;
    };
    url: '/api/v1/admin/audit';
};

export type AdminSrvAuditListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvAuditListError = AdminSrvAuditListErrors[keyof AdminSrvAuditListErrors];

export type AdminSrvAuditListResponses = {
    /**
     * Successful Response
     */
    200: AuditPageOut;
};

export type AdminSrvAuditListResponse = AdminSrvAuditListResponses[keyof AdminSrvAuditListResponses];

export type AdminSrvAuditExportData = {
    body: AuditExportIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/audit/export';
};

export type AdminSrvAuditExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvAuditExportError = AdminSrvAuditExportErrors[keyof AdminSrvAuditExportErrors];

export type AdminSrvAuditExportResponses = {
    /**
     * Successful Response
     */
    200: AuditExportOut;
};

export type AdminSrvAuditExportResponse = AdminSrvAuditExportResponses[keyof AdminSrvAuditExportResponses];

export type AdminSrvAuditExportsGetData = {
    body?: never;
    path: {
        /**
         * Export Id
         */
        export_id: string;
    };
    query?: never;
    url: '/api/v1/admin/audit/exports/{export_id}';
};

export type AdminSrvAuditExportsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvAuditExportsGetError = AdminSrvAuditExportsGetErrors[keyof AdminSrvAuditExportsGetErrors];

export type AdminSrvAuditExportsGetResponses = {
    /**
     * Successful Response
     */
    200: AuditExportOut;
};

export type AdminSrvAuditExportsGetResponse = AdminSrvAuditExportsGetResponses[keyof AdminSrvAuditExportsGetResponses];

export type AdminSrvAuditGetData = {
    body?: never;
    path: {
        /**
         * Entry Id
         */
        entry_id: string;
    };
    query?: never;
    url: '/api/v1/admin/audit/{entry_id}';
};

export type AdminSrvAuditGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvAuditGetError = AdminSrvAuditGetErrors[keyof AdminSrvAuditGetErrors];

export type AdminSrvAuditGetResponses = {
    /**
     * Successful Response
     */
    200: AuditCardOut;
};

export type AdminSrvAuditGetResponse = AdminSrvAuditGetResponses[keyof AdminSrvAuditGetResponses];

export type AdminSrvNotificationTemplatesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Channel
         */
        channel?: string | null;
        /**
         * Section
         */
        section?: NotificationSection | null;
    };
    url: '/api/v1/admin/notifications/templates';
};

export type AdminSrvNotificationTemplatesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationTemplatesListError = AdminSrvNotificationTemplatesListErrors[keyof AdminSrvNotificationTemplatesListErrors];

export type AdminSrvNotificationTemplatesListResponses = {
    /**
     * Successful Response
     */
    200: TemplatesPageOut;
};

export type AdminSrvNotificationTemplatesListResponse = AdminSrvNotificationTemplatesListResponses[keyof AdminSrvNotificationTemplatesListResponses];

export type AdminSrvNotificationDispatchesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, scheduled_for, status, template_code
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Template Code]
         *
         * Фильтр по template_code
         */
        'filter[template_code]'?: string | null;
        /**
         * Filter[Template Code][]
         *
         * Фильтр по template_code; несколько значений
         */
        'filter[template_code][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (scheduled, processing, done, skipped, cancelled)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (scheduled, processing, done, skipped, cancelled); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Device Id]
         *
         * Фильтр по device_id
         */
        'filter[device_id]'?: string | null;
        /**
         * Filter[Device Id][]
         *
         * Фильтр по device_id; несколько значений
         */
        'filter[device_id][]'?: Array<string> | null;
        /**
         * Filter[Team Member Id]
         *
         * Фильтр по team_member_id
         */
        'filter[team_member_id]'?: string | null;
        /**
         * Filter[Team Member Id][]
         *
         * Фильтр по team_member_id; несколько значений
         */
        'filter[team_member_id][]'?: Array<string> | null;
        /**
         * Filter[Channel]
         *
         * Фактический канал (push, email, feed)
         */
        'filter[channel]'?: string | null;
        /**
         * Filter[Channel][]
         *
         * Фактический канал (push, email, feed); несколько значений
         */
        'filter[channel][]'?: Array<string> | null;
        /**
         * Filter[From]
         *
         * Фильтр по from
         */
        'filter[from]'?: string | null;
        /**
         * Filter[From][]
         *
         * Фильтр по from; несколько значений
         */
        'filter[from][]'?: Array<string> | null;
        /**
         * Filter[To]
         *
         * Фильтр по to
         */
        'filter[to]'?: string | null;
        /**
         * Filter[To][]
         *
         * Фильтр по to; несколько значений
         */
        'filter[to][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/notifications/dispatches';
};

export type AdminSrvNotificationDispatchesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationDispatchesListError = AdminSrvNotificationDispatchesListErrors[keyof AdminSrvNotificationDispatchesListErrors];

export type AdminSrvNotificationDispatchesListResponses = {
    /**
     * Successful Response
     */
    200: DispatchesPageOut;
};

export type AdminSrvNotificationDispatchesListResponse = AdminSrvNotificationDispatchesListResponses[keyof AdminSrvNotificationDispatchesListResponses];

export type AdminSrvNotificationDispatchesGetData = {
    body?: never;
    path: {
        /**
         * Dispatch Id
         */
        dispatch_id: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/dispatches/{dispatch_id}';
};

export type AdminSrvNotificationDispatchesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationDispatchesGetError = AdminSrvNotificationDispatchesGetErrors[keyof AdminSrvNotificationDispatchesGetErrors];

export type AdminSrvNotificationDispatchesGetResponses = {
    /**
     * Successful Response
     */
    200: DispatchCardOut;
};

export type AdminSrvNotificationDispatchesGetResponse = AdminSrvNotificationDispatchesGetResponses[keyof AdminSrvNotificationDispatchesGetResponses];

export type AdminSrvNotificationTemplatesGetData = {
    body?: never;
    path: {
        /**
         * Code
         */
        code: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/templates/{code}';
};

export type AdminSrvNotificationTemplatesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationTemplatesGetError = AdminSrvNotificationTemplatesGetErrors[keyof AdminSrvNotificationTemplatesGetErrors];

export type AdminSrvNotificationTemplatesGetResponses = {
    /**
     * Successful Response
     */
    200: TemplateCardOut;
};

export type AdminSrvNotificationTemplatesGetResponse = AdminSrvNotificationTemplatesGetResponses[keyof AdminSrvNotificationTemplatesGetResponses];

export type AdminSrvNotificationTemplatesUpdateData = {
    body: TemplatePatchIn;
    path: {
        /**
         * Code
         */
        code: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/templates/{code}';
};

export type AdminSrvNotificationTemplatesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationTemplatesUpdateError = AdminSrvNotificationTemplatesUpdateErrors[keyof AdminSrvNotificationTemplatesUpdateErrors];

export type AdminSrvNotificationTemplatesUpdateResponses = {
    /**
     * Successful Response
     */
    200: TemplateCardOut;
};

export type AdminSrvNotificationTemplatesUpdateResponse = AdminSrvNotificationTemplatesUpdateResponses[keyof AdminSrvNotificationTemplatesUpdateResponses];

export type AdminSrvNotificationTemplatesEnableData = {
    body?: never;
    path: {
        /**
         * Code
         */
        code: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/templates/{code}/enable';
};

export type AdminSrvNotificationTemplatesEnableErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationTemplatesEnableError = AdminSrvNotificationTemplatesEnableErrors[keyof AdminSrvNotificationTemplatesEnableErrors];

export type AdminSrvNotificationTemplatesEnableResponses = {
    /**
     * Successful Response
     */
    200: TemplateCardOut;
};

export type AdminSrvNotificationTemplatesEnableResponse = AdminSrvNotificationTemplatesEnableResponses[keyof AdminSrvNotificationTemplatesEnableResponses];

export type AdminSrvNotificationTemplatesDisableData = {
    body?: never;
    path: {
        /**
         * Code
         */
        code: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/templates/{code}/disable';
};

export type AdminSrvNotificationTemplatesDisableErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationTemplatesDisableError = AdminSrvNotificationTemplatesDisableErrors[keyof AdminSrvNotificationTemplatesDisableErrors];

export type AdminSrvNotificationTemplatesDisableResponses = {
    /**
     * Successful Response
     */
    200: TemplateCardOut;
};

export type AdminSrvNotificationTemplatesDisableResponse = AdminSrvNotificationTemplatesDisableResponses[keyof AdminSrvNotificationTemplatesDisableResponses];

export type AdminSrvNotificationTemplatesResetData = {
    body?: never;
    path: {
        /**
         * Code
         */
        code: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/templates/{code}/reset';
};

export type AdminSrvNotificationTemplatesResetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationTemplatesResetError = AdminSrvNotificationTemplatesResetErrors[keyof AdminSrvNotificationTemplatesResetErrors];

export type AdminSrvNotificationTemplatesResetResponses = {
    /**
     * Successful Response
     */
    200: TemplateCardOut;
};

export type AdminSrvNotificationTemplatesResetResponse = AdminSrvNotificationTemplatesResetResponses[keyof AdminSrvNotificationTemplatesResetResponses];

export type AdminSrvNotificationCampaignsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Channel
         */
        channel?: CampaignChannel | null;
        /**
         * Status
         */
        status?: CampaignStatus | null;
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
    };
    url: '/api/v1/admin/notifications/campaigns';
};

export type AdminSrvNotificationCampaignsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationCampaignsListError = AdminSrvNotificationCampaignsListErrors[keyof AdminSrvNotificationCampaignsListErrors];

export type AdminSrvNotificationCampaignsListResponses = {
    /**
     * Successful Response
     */
    200: CampaignsPageOut;
};

export type AdminSrvNotificationCampaignsListResponse = AdminSrvNotificationCampaignsListResponses[keyof AdminSrvNotificationCampaignsListResponses];

export type AdminSrvNotificationCampaignsCreateData = {
    body: CampaignIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/notifications/campaigns';
};

export type AdminSrvNotificationCampaignsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationCampaignsCreateError = AdminSrvNotificationCampaignsCreateErrors[keyof AdminSrvNotificationCampaignsCreateErrors];

export type AdminSrvNotificationCampaignsCreateResponses = {
    /**
     * Successful Response
     */
    201: CampaignCardOut;
};

export type AdminSrvNotificationCampaignsCreateResponse = AdminSrvNotificationCampaignsCreateResponses[keyof AdminSrvNotificationCampaignsCreateResponses];

export type AdminSrvNotificationCampaignsFormData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/notifications/campaigns/form';
};

export type AdminSrvNotificationCampaignsFormErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationCampaignsFormError = AdminSrvNotificationCampaignsFormErrors[keyof AdminSrvNotificationCampaignsFormErrors];

export type AdminSrvNotificationCampaignsFormResponses = {
    /**
     * Successful Response
     */
    200: CampaignFormOut;
};

export type AdminSrvNotificationCampaignsFormResponse = AdminSrvNotificationCampaignsFormResponses[keyof AdminSrvNotificationCampaignsFormResponses];

export type AdminSrvNotificationCampaignsLinkOptionsData = {
    body?: never;
    path?: never;
    query: {
        link_type: CampaignLinkType;
        /**
         * Q
         */
        q?: string | null;
    };
    url: '/api/v1/admin/notifications/campaigns/link-options';
};

export type AdminSrvNotificationCampaignsLinkOptionsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationCampaignsLinkOptionsError = AdminSrvNotificationCampaignsLinkOptionsErrors[keyof AdminSrvNotificationCampaignsLinkOptionsErrors];

export type AdminSrvNotificationCampaignsLinkOptionsResponses = {
    /**
     * Successful Response
     */
    200: CampaignLinkOptionsOut;
};

export type AdminSrvNotificationCampaignsLinkOptionsResponse = AdminSrvNotificationCampaignsLinkOptionsResponses[keyof AdminSrvNotificationCampaignsLinkOptionsResponses];

export type AdminSrvNotificationCampaignsGetData = {
    body?: never;
    path: {
        /**
         * Campaign Id
         */
        campaign_id: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/campaigns/{campaign_id}';
};

export type AdminSrvNotificationCampaignsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationCampaignsGetError = AdminSrvNotificationCampaignsGetErrors[keyof AdminSrvNotificationCampaignsGetErrors];

export type AdminSrvNotificationCampaignsGetResponses = {
    /**
     * Successful Response
     */
    200: CampaignCardOut;
};

export type AdminSrvNotificationCampaignsGetResponse = AdminSrvNotificationCampaignsGetResponses[keyof AdminSrvNotificationCampaignsGetResponses];

export type AdminSrvNotificationCampaignsUpdateData = {
    body: CampaignPatchIn;
    path: {
        /**
         * Campaign Id
         */
        campaign_id: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/campaigns/{campaign_id}';
};

export type AdminSrvNotificationCampaignsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationCampaignsUpdateError = AdminSrvNotificationCampaignsUpdateErrors[keyof AdminSrvNotificationCampaignsUpdateErrors];

export type AdminSrvNotificationCampaignsUpdateResponses = {
    /**
     * Successful Response
     */
    200: CampaignCardOut;
};

export type AdminSrvNotificationCampaignsUpdateResponse = AdminSrvNotificationCampaignsUpdateResponses[keyof AdminSrvNotificationCampaignsUpdateResponses];

export type AdminSrvNotificationCampaignsRunGetData = {
    body?: never;
    path: {
        /**
         * Campaign Id
         */
        campaign_id: string;
        /**
         * Run Id
         */
        run_id: string;
    };
    query?: never;
    url: '/api/v1/admin/notifications/campaigns/{campaign_id}/runs/{run_id}';
};

export type AdminSrvNotificationCampaignsRunGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationCampaignsRunGetError = AdminSrvNotificationCampaignsRunGetErrors[keyof AdminSrvNotificationCampaignsRunGetErrors];

export type AdminSrvNotificationCampaignsRunGetResponses = {
    /**
     * Successful Response
     */
    200: CampaignRunReportOut;
};

export type AdminSrvNotificationCampaignsRunGetResponse = AdminSrvNotificationCampaignsRunGetResponses[keyof AdminSrvNotificationCampaignsRunGetResponses];

export type AdminSrvNotificationCampaignsActionData = {
    body?: never;
    path: {
        /**
         * Campaign Id
         */
        campaign_id: string;
        /**
         * Action
         *
         * schedule, launch, stop, resume, cancel
         */
        action: 'schedule' | 'launch' | 'stop' | 'resume' | 'cancel';
    };
    query?: never;
    url: '/api/v1/admin/notifications/campaigns/{campaign_id}/{action}';
};

export type AdminSrvNotificationCampaignsActionErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationCampaignsActionError = AdminSrvNotificationCampaignsActionErrors[keyof AdminSrvNotificationCampaignsActionErrors];

export type AdminSrvNotificationCampaignsActionResponses = {
    /**
     * Successful Response
     */
    200: CampaignCardOut;
};

export type AdminSrvNotificationCampaignsActionResponse = AdminSrvNotificationCampaignsActionResponses[keyof AdminSrvNotificationCampaignsActionResponses];

export type AdminSrvNotificationAudienceEstimateData = {
    body?: never;
    path?: never;
    query: {
        channel: CampaignChannel;
        /**
         * Segment
         */
        segment?: string | null;
        /**
         * Feature
         */
        feature?: string | null;
        /**
         * Author Member Id
         */
        author_member_id?: string | null;
        /**
         * Winery Id
         */
        winery_id?: string | null;
        /**
         * Wine Id
         */
        wine_id?: string | null;
        /**
         * Course Id
         */
        course_id?: string | null;
        /**
         * Event Type
         */
        event_type?: string | null;
        /**
         * Event Id
         */
        event_id?: string | null;
    };
    url: '/api/v1/admin/notifications/audience-estimate';
};

export type AdminSrvNotificationAudienceEstimateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminSrvNotificationAudienceEstimateError = AdminSrvNotificationAudienceEstimateErrors[keyof AdminSrvNotificationAudienceEstimateErrors];

export type AdminSrvNotificationAudienceEstimateResponses = {
    /**
     * Successful Response
     */
    200: CampaignAudienceEstimateOut;
};

export type AdminSrvNotificationAudienceEstimateResponse = AdminSrvNotificationAudienceEstimateResponses[keyof AdminSrvNotificationAudienceEstimateResponses];

export type AdminLrnLibraryData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Type
         *
         * lesson | test
         */
        type: 'lesson' | 'test';
        /**
         * Q
         */
        q?: string | null;
        /**
         * Kind
         *
         * Вид теста
         */
        kind?: TestKind | null;
    };
    url: '/api/v1/admin/learning/library';
};

export type AdminLrnLibraryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLibraryError = AdminLrnLibraryErrors[keyof AdminLrnLibraryErrors];

export type AdminLrnLibraryResponses = {
    /**
     * Successful Response
     */
    200: LibraryOut;
};

export type AdminLrnLibraryResponse = AdminLrnLibraryResponses[keyof AdminLrnLibraryResponses];

export type AdminLrnLogListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: at, lesson, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Test]
         *
         * Тест сдан / не сдан (passed, failed)
         */
        'filter[test]'?: string | null;
        /**
         * Filter[Test][]
         *
         * Тест сдан / не сдан (passed, failed); несколько значений
         */
        'filter[test][]'?: Array<string> | null;
        /**
         * Filter[Course Id]
         *
         * Курс (уроки программы)
         */
        'filter[course_id]'?: string | null;
        /**
         * Filter[Course Id][]
         *
         * Курс (уроки программы); несколько значений
         */
        'filter[course_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Lesson Id]
         *
         * Урок
         */
        'filter[lesson_id]'?: string | null;
        /**
         * Filter[Lesson Id][]
         *
         * Урок; несколько значений
         */
        'filter[lesson_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/learning/log';
};

export type AdminLrnLogListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLogListError = AdminLrnLogListErrors[keyof AdminLrnLogListErrors];

export type AdminLrnLogListResponses = {
    /**
     * Successful Response
     */
    200: LearnPageLearnLogRowOut;
};

export type AdminLrnLogListResponse = AdminLrnLogListResponses[keyof AdminLrnLogListResponses];

export type AdminLrnLogExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: at, lesson, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Test]
         *
         * Тест сдан / не сдан (passed, failed)
         */
        'filter[test]'?: string | null;
        /**
         * Filter[Test][]
         *
         * Тест сдан / не сдан (passed, failed); несколько значений
         */
        'filter[test][]'?: Array<string> | null;
        /**
         * Filter[Course Id]
         *
         * Курс (уроки программы)
         */
        'filter[course_id]'?: string | null;
        /**
         * Filter[Course Id][]
         *
         * Курс (уроки программы); несколько значений
         */
        'filter[course_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Lesson Id]
         *
         * Урок
         */
        'filter[lesson_id]'?: string | null;
        /**
         * Filter[Lesson Id][]
         *
         * Урок; несколько значений
         */
        'filter[lesson_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/learning/log/export';
};

export type AdminLrnLogExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLogExportError = AdminLrnLogExportErrors[keyof AdminLrnLogExportErrors];

export type AdminLrnLogExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminLrnCoursesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: access, author, created_at, duration, lessons_count, region, status, tests_count, title, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (intro, region_main, additional)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (intro, region_main, additional); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Автор
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Автор; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
        /**
         * Filter[Is Paid]
         *
         * Платный курс
         */
        'filter[is_paid]'?: string | null;
        /**
         * Filter[Is Paid][]
         *
         * Платный курс; несколько значений
         */
        'filter[is_paid][]'?: Array<string> | null;
        /**
         * Filter[Title]
         *
         * Название (подстрока)
         */
        'filter[title]'?: string | null;
        /**
         * Filter[Title][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[title][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/courses';
};

export type AdminLrnCoursesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesListError = AdminLrnCoursesListErrors[keyof AdminLrnCoursesListErrors];

export type AdminLrnCoursesListResponses = {
    /**
     * Successful Response
     */
    200: LearnPageCourseRowOut;
};

export type AdminLrnCoursesListResponse = AdminLrnCoursesListResponses[keyof AdminLrnCoursesListResponses];

export type AdminLrnCoursesCreateData = {
    body: CourseCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/courses';
};

export type AdminLrnCoursesCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesCreateError = AdminLrnCoursesCreateErrors[keyof AdminLrnCoursesCreateErrors];

export type AdminLrnCoursesCreateResponses = {
    /**
     * Successful Response
     */
    201: CourseOut;
};

export type AdminLrnCoursesCreateResponse = AdminLrnCoursesCreateResponses[keyof AdminLrnCoursesCreateResponses];

export type AdminLrnCoursesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: access, author, created_at, duration, lessons_count, region, status, tests_count, title, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (intro, region_main, additional)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (intro, region_main, additional); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Автор
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Автор; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
        /**
         * Filter[Is Paid]
         *
         * Платный курс
         */
        'filter[is_paid]'?: string | null;
        /**
         * Filter[Is Paid][]
         *
         * Платный курс; несколько значений
         */
        'filter[is_paid][]'?: Array<string> | null;
        /**
         * Filter[Title]
         *
         * Название (подстрока)
         */
        'filter[title]'?: string | null;
        /**
         * Filter[Title][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[title][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/courses/export';
};

export type AdminLrnCoursesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesExportError = AdminLrnCoursesExportErrors[keyof AdminLrnCoursesExportErrors];

export type AdminLrnCoursesExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminLrnCoursesDeleteData = {
    body: DeleteIn;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}';
};

export type AdminLrnCoursesDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesDeleteError = AdminLrnCoursesDeleteErrors[keyof AdminLrnCoursesDeleteErrors];

export type AdminLrnCoursesDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminLrnCoursesDeleteResponse = AdminLrnCoursesDeleteResponses[keyof AdminLrnCoursesDeleteResponses];

export type AdminLrnCoursesGetData = {
    body?: never;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}';
};

export type AdminLrnCoursesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesGetError = AdminLrnCoursesGetErrors[keyof AdminLrnCoursesGetErrors];

export type AdminLrnCoursesGetResponses = {
    /**
     * Successful Response
     */
    200: CourseOut;
};

export type AdminLrnCoursesGetResponse = AdminLrnCoursesGetResponses[keyof AdminLrnCoursesGetResponses];

export type AdminLrnCoursesUpdateData = {
    body: CoursePatchIn;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}';
};

export type AdminLrnCoursesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesUpdateError = AdminLrnCoursesUpdateErrors[keyof AdminLrnCoursesUpdateErrors];

export type AdminLrnCoursesUpdateResponses = {
    /**
     * Successful Response
     */
    200: CourseOut;
};

export type AdminLrnCoursesUpdateResponse = AdminLrnCoursesUpdateResponses[keyof AdminLrnCoursesUpdateResponses];

export type AdminLrnCoursesProgramSaveData = {
    body: ProgramIn;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}/program';
};

export type AdminLrnCoursesProgramSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesProgramSaveError = AdminLrnCoursesProgramSaveErrors[keyof AdminLrnCoursesProgramSaveErrors];

export type AdminLrnCoursesProgramSaveResponses = {
    /**
     * Successful Response
     */
    200: ProgramSavedOut;
};

export type AdminLrnCoursesProgramSaveResponse = AdminLrnCoursesProgramSaveResponses[keyof AdminLrnCoursesProgramSaveResponses];

export type AdminLrnCoursesMediaSaveData = {
    body: LearnMediaIn;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}/media';
};

export type AdminLrnCoursesMediaSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesMediaSaveError = AdminLrnCoursesMediaSaveErrors[keyof AdminLrnCoursesMediaSaveErrors];

export type AdminLrnCoursesMediaSaveResponses = {
    /**
     * Successful Response
     */
    200: CourseOut;
};

export type AdminLrnCoursesMediaSaveResponse = AdminLrnCoursesMediaSaveResponses[keyof AdminLrnCoursesMediaSaveResponses];

export type AdminLrnCoursesStatsData = {
    body?: never;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}/stats';
};

export type AdminLrnCoursesStatsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesStatsError = AdminLrnCoursesStatsErrors[keyof AdminLrnCoursesStatsErrors];

export type AdminLrnCoursesStatsResponses = {
    /**
     * Successful Response
     */
    200: CourseStatsOut;
};

export type AdminLrnCoursesStatsResponse = AdminLrnCoursesStatsResponses[keyof AdminLrnCoursesStatsResponses];

export type AdminLrnLessonsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, cost, courses_count, created_at, duration, region, status, tests_count, title, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Автор
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Автор; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
        /**
         * Filter[Is Paid]
         *
         * Платный урок
         */
        'filter[is_paid]'?: string | null;
        /**
         * Filter[Is Paid][]
         *
         * Платный урок; несколько значений
         */
        'filter[is_paid][]'?: Array<string> | null;
        /**
         * Filter[Course Id]
         *
         * Уроки курса
         */
        'filter[course_id]'?: string | null;
        /**
         * Filter[Course Id][]
         *
         * Уроки курса; несколько значений
         */
        'filter[course_id][]'?: Array<string> | null;
        /**
         * Filter[Title]
         *
         * Название (подстрока)
         */
        'filter[title]'?: string | null;
        /**
         * Filter[Title][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[title][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/lessons';
};

export type AdminLrnLessonsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsListError = AdminLrnLessonsListErrors[keyof AdminLrnLessonsListErrors];

export type AdminLrnLessonsListResponses = {
    /**
     * Successful Response
     */
    200: LearnPageLessonRowOut;
};

export type AdminLrnLessonsListResponse = AdminLrnLessonsListResponses[keyof AdminLrnLessonsListResponses];

export type AdminLrnLessonsCreateData = {
    body: LessonCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/lessons';
};

export type AdminLrnLessonsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsCreateError = AdminLrnLessonsCreateErrors[keyof AdminLrnLessonsCreateErrors];

export type AdminLrnLessonsCreateResponses = {
    /**
     * Successful Response
     */
    201: LessonOut;
};

export type AdminLrnLessonsCreateResponse = AdminLrnLessonsCreateResponses[keyof AdminLrnLessonsCreateResponses];

export type AdminLrnLessonsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, cost, courses_count, created_at, duration, region, status, tests_count, title, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Автор
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Автор; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
        /**
         * Filter[Is Paid]
         *
         * Платный урок
         */
        'filter[is_paid]'?: string | null;
        /**
         * Filter[Is Paid][]
         *
         * Платный урок; несколько значений
         */
        'filter[is_paid][]'?: Array<string> | null;
        /**
         * Filter[Course Id]
         *
         * Уроки курса
         */
        'filter[course_id]'?: string | null;
        /**
         * Filter[Course Id][]
         *
         * Уроки курса; несколько значений
         */
        'filter[course_id][]'?: Array<string> | null;
        /**
         * Filter[Title]
         *
         * Название (подстрока)
         */
        'filter[title]'?: string | null;
        /**
         * Filter[Title][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[title][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/lessons/export';
};

export type AdminLrnLessonsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsExportError = AdminLrnLessonsExportErrors[keyof AdminLrnLessonsExportErrors];

export type AdminLrnLessonsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminLrnLessonsDeleteData = {
    body: DeleteIn;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}';
};

export type AdminLrnLessonsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsDeleteError = AdminLrnLessonsDeleteErrors[keyof AdminLrnLessonsDeleteErrors];

export type AdminLrnLessonsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminLrnLessonsDeleteResponse = AdminLrnLessonsDeleteResponses[keyof AdminLrnLessonsDeleteResponses];

export type AdminLrnLessonsGetData = {
    body?: never;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}';
};

export type AdminLrnLessonsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsGetError = AdminLrnLessonsGetErrors[keyof AdminLrnLessonsGetErrors];

export type AdminLrnLessonsGetResponses = {
    /**
     * Successful Response
     */
    200: LessonOut;
};

export type AdminLrnLessonsGetResponse = AdminLrnLessonsGetResponses[keyof AdminLrnLessonsGetResponses];

export type AdminLrnLessonsUpdateData = {
    body: LessonPatchIn;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}';
};

export type AdminLrnLessonsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsUpdateError = AdminLrnLessonsUpdateErrors[keyof AdminLrnLessonsUpdateErrors];

export type AdminLrnLessonsUpdateResponses = {
    /**
     * Successful Response
     */
    200: LessonOut;
};

export type AdminLrnLessonsUpdateResponse = AdminLrnLessonsUpdateResponses[keyof AdminLrnLessonsUpdateResponses];

export type AdminLrnLessonsTestsSaveData = {
    /**
     * Body
     */
    body: Array<LessonTestIn>;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}/tests';
};

export type AdminLrnLessonsTestsSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsTestsSaveError = AdminLrnLessonsTestsSaveErrors[keyof AdminLrnLessonsTestsSaveErrors];

export type AdminLrnLessonsTestsSaveResponses = {
    /**
     * Successful Response
     */
    200: LessonTestsOut;
};

export type AdminLrnLessonsTestsSaveResponse = AdminLrnLessonsTestsSaveResponses[keyof AdminLrnLessonsTestsSaveResponses];

export type AdminLrnLessonsMediaSaveData = {
    body: LearnMediaIn;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}/media';
};

export type AdminLrnLessonsMediaSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsMediaSaveError = AdminLrnLessonsMediaSaveErrors[keyof AdminLrnLessonsMediaSaveErrors];

export type AdminLrnLessonsMediaSaveResponses = {
    /**
     * Successful Response
     */
    200: LessonOut;
};

export type AdminLrnLessonsMediaSaveResponse = AdminLrnLessonsMediaSaveResponses[keyof AdminLrnLessonsMediaSaveResponses];

export type AdminLrnLessonsStatsData = {
    body?: never;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}/stats';
};

export type AdminLrnLessonsStatsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsStatsError = AdminLrnLessonsStatsErrors[keyof AdminLrnLessonsStatsErrors];

export type AdminLrnLessonsStatsResponses = {
    /**
     * Successful Response
     */
    200: LessonStatsOut;
};

export type AdminLrnLessonsStatsResponse = AdminLrnLessonsStatsResponses[keyof AdminLrnLessonsStatsResponses];

export type AdminLrnTestsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, created_at, kind, questions_count, region, status, title, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (lesson_test, country_quiz, level_check)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (lesson_test, country_quiz, level_check); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Country Id]
         *
         * Страна квиза
         */
        'filter[country_id]'?: string | null;
        /**
         * Filter[Country Id][]
         *
         * Страна квиза; несколько значений
         */
        'filter[country_id][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Автор
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Автор; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
        /**
         * Filter[Title]
         *
         * Название (подстрока)
         */
        'filter[title]'?: string | null;
        /**
         * Filter[Title][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[title][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/tests';
};

export type AdminLrnTestsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsListError = AdminLrnTestsListErrors[keyof AdminLrnTestsListErrors];

export type AdminLrnTestsListResponses = {
    /**
     * Successful Response
     */
    200: LearnPageTestRowOut;
};

export type AdminLrnTestsListResponse = AdminLrnTestsListResponses[keyof AdminLrnTestsListResponses];

export type AdminLrnTestsCreateData = {
    body: TestCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/tests';
};

export type AdminLrnTestsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsCreateError = AdminLrnTestsCreateErrors[keyof AdminLrnTestsCreateErrors];

export type AdminLrnTestsCreateResponses = {
    /**
     * Successful Response
     */
    201: TestOut;
};

export type AdminLrnTestsCreateResponse = AdminLrnTestsCreateResponses[keyof AdminLrnTestsCreateResponses];

export type AdminLrnTestsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, created_at, kind, questions_count, region, status, title, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_moderation, needs_revision, published, unpublished, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (lesson_test, country_quiz, level_check)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (lesson_test, country_quiz, level_check); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Country Id]
         *
         * Страна квиза
         */
        'filter[country_id]'?: string | null;
        /**
         * Filter[Country Id][]
         *
         * Страна квиза; несколько значений
         */
        'filter[country_id][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Автор
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Автор; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
        /**
         * Filter[Title]
         *
         * Название (подстрока)
         */
        'filter[title]'?: string | null;
        /**
         * Filter[Title][]
         *
         * Название (подстрока); несколько значений
         */
        'filter[title][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/tests/export';
};

export type AdminLrnTestsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsExportError = AdminLrnTestsExportErrors[keyof AdminLrnTestsExportErrors];

export type AdminLrnTestsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminLrnTestsDeleteData = {
    body: DeleteIn;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}';
};

export type AdminLrnTestsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsDeleteError = AdminLrnTestsDeleteErrors[keyof AdminLrnTestsDeleteErrors];

export type AdminLrnTestsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminLrnTestsDeleteResponse = AdminLrnTestsDeleteResponses[keyof AdminLrnTestsDeleteResponses];

export type AdminLrnTestsGetData = {
    body?: never;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}';
};

export type AdminLrnTestsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsGetError = AdminLrnTestsGetErrors[keyof AdminLrnTestsGetErrors];

export type AdminLrnTestsGetResponses = {
    /**
     * Successful Response
     */
    200: TestOut;
};

export type AdminLrnTestsGetResponse = AdminLrnTestsGetResponses[keyof AdminLrnTestsGetResponses];

export type AdminLrnTestsUpdateData = {
    body: TestPatchIn;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}';
};

export type AdminLrnTestsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsUpdateError = AdminLrnTestsUpdateErrors[keyof AdminLrnTestsUpdateErrors];

export type AdminLrnTestsUpdateResponses = {
    /**
     * Successful Response
     */
    200: TestOut;
};

export type AdminLrnTestsUpdateResponse = AdminLrnTestsUpdateResponses[keyof AdminLrnTestsUpdateResponses];

export type AdminLrnTestsQuestionsSaveData = {
    /**
     * Body
     */
    body: Array<QuestionIn>;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}/questions';
};

export type AdminLrnTestsQuestionsSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsQuestionsSaveError = AdminLrnTestsQuestionsSaveErrors[keyof AdminLrnTestsQuestionsSaveErrors];

export type AdminLrnTestsQuestionsSaveResponses = {
    /**
     * Successful Response
     */
    200: QuestionsSavedOut;
};

export type AdminLrnTestsQuestionsSaveResponse = AdminLrnTestsQuestionsSaveResponses[keyof AdminLrnTestsQuestionsSaveResponses];

export type AdminLrnTestsMediaSaveData = {
    body: LearnMediaIn;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}/media';
};

export type AdminLrnTestsMediaSaveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsMediaSaveError = AdminLrnTestsMediaSaveErrors[keyof AdminLrnTestsMediaSaveErrors];

export type AdminLrnTestsMediaSaveResponses = {
    /**
     * Successful Response
     */
    200: TestOut;
};

export type AdminLrnTestsMediaSaveResponse = AdminLrnTestsMediaSaveResponses[keyof AdminLrnTestsMediaSaveResponses];

export type AdminLrnTestsUsageData = {
    body?: never;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}/usage';
};

export type AdminLrnTestsUsageErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsUsageError = AdminLrnTestsUsageErrors[keyof AdminLrnTestsUsageErrors];

export type AdminLrnTestsUsageResponses = {
    /**
     * Successful Response
     */
    200: TestUsageOut;
};

export type AdminLrnTestsUsageResponse = AdminLrnTestsUsageResponses[keyof AdminLrnTestsUsageResponses];

export type AdminLrnCoursesSubmitData = {
    body?: never;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}/submit';
};

export type AdminLrnCoursesSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesSubmitError = AdminLrnCoursesSubmitErrors[keyof AdminLrnCoursesSubmitErrors];

export type AdminLrnCoursesSubmitResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnCoursesSubmitResponse = AdminLrnCoursesSubmitResponses[keyof AdminLrnCoursesSubmitResponses];

export type AdminLrnCoursesDecisionData = {
    body: DecisionIn;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}/decision';
};

export type AdminLrnCoursesDecisionErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesDecisionError = AdminLrnCoursesDecisionErrors[keyof AdminLrnCoursesDecisionErrors];

export type AdminLrnCoursesDecisionResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnCoursesDecisionResponse = AdminLrnCoursesDecisionResponses[keyof AdminLrnCoursesDecisionResponses];

export type AdminLrnCoursesUnpublishData = {
    /**
     * Body
     */
    body?: UnpublishIn | null;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}/unpublish';
};

export type AdminLrnCoursesUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesUnpublishError = AdminLrnCoursesUnpublishErrors[keyof AdminLrnCoursesUnpublishErrors];

export type AdminLrnCoursesUnpublishResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnCoursesUnpublishResponse = AdminLrnCoursesUnpublishResponses[keyof AdminLrnCoursesUnpublishResponses];

export type AdminLrnCoursesArchiveData = {
    body: LearnArchiveIn;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}/archive';
};

export type AdminLrnCoursesArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesArchiveError = AdminLrnCoursesArchiveErrors[keyof AdminLrnCoursesArchiveErrors];

export type AdminLrnCoursesArchiveResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnCoursesArchiveResponse = AdminLrnCoursesArchiveResponses[keyof AdminLrnCoursesArchiveResponses];

export type AdminLrnCoursesRestoreData = {
    body?: never;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: never;
    url: '/api/v1/admin/courses/{course_id}/restore';
};

export type AdminLrnCoursesRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesRestoreError = AdminLrnCoursesRestoreErrors[keyof AdminLrnCoursesRestoreErrors];

export type AdminLrnCoursesRestoreResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnCoursesRestoreResponse = AdminLrnCoursesRestoreResponses[keyof AdminLrnCoursesRestoreResponses];

export type AdminLrnCoursesHistoryData = {
    body?: never;
    path: {
        /**
         * Course Id
         */
        course_id: string;
    };
    query?: {
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/courses/{course_id}/history';
};

export type AdminLrnCoursesHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnCoursesHistoryError = AdminLrnCoursesHistoryErrors[keyof AdminLrnCoursesHistoryErrors];

export type AdminLrnCoursesHistoryResponses = {
    /**
     * Successful Response
     */
    200: LearnHistoryPageOut;
};

export type AdminLrnCoursesHistoryResponse = AdminLrnCoursesHistoryResponses[keyof AdminLrnCoursesHistoryResponses];

export type AdminLrnLessonsSubmitData = {
    body?: never;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}/submit';
};

export type AdminLrnLessonsSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsSubmitError = AdminLrnLessonsSubmitErrors[keyof AdminLrnLessonsSubmitErrors];

export type AdminLrnLessonsSubmitResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnLessonsSubmitResponse = AdminLrnLessonsSubmitResponses[keyof AdminLrnLessonsSubmitResponses];

export type AdminLrnLessonsDecisionData = {
    body: DecisionIn;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}/decision';
};

export type AdminLrnLessonsDecisionErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsDecisionError = AdminLrnLessonsDecisionErrors[keyof AdminLrnLessonsDecisionErrors];

export type AdminLrnLessonsDecisionResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnLessonsDecisionResponse = AdminLrnLessonsDecisionResponses[keyof AdminLrnLessonsDecisionResponses];

export type AdminLrnLessonsUnpublishData = {
    /**
     * Body
     */
    body?: UnpublishIn | null;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}/unpublish';
};

export type AdminLrnLessonsUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsUnpublishError = AdminLrnLessonsUnpublishErrors[keyof AdminLrnLessonsUnpublishErrors];

export type AdminLrnLessonsUnpublishResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnLessonsUnpublishResponse = AdminLrnLessonsUnpublishResponses[keyof AdminLrnLessonsUnpublishResponses];

export type AdminLrnLessonsArchiveData = {
    body: LearnArchiveIn;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}/archive';
};

export type AdminLrnLessonsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsArchiveError = AdminLrnLessonsArchiveErrors[keyof AdminLrnLessonsArchiveErrors];

export type AdminLrnLessonsArchiveResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnLessonsArchiveResponse = AdminLrnLessonsArchiveResponses[keyof AdminLrnLessonsArchiveResponses];

export type AdminLrnLessonsRestoreData = {
    body?: never;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: never;
    url: '/api/v1/admin/lessons/{lesson_id}/restore';
};

export type AdminLrnLessonsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsRestoreError = AdminLrnLessonsRestoreErrors[keyof AdminLrnLessonsRestoreErrors];

export type AdminLrnLessonsRestoreResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnLessonsRestoreResponse = AdminLrnLessonsRestoreResponses[keyof AdminLrnLessonsRestoreResponses];

export type AdminLrnLessonsHistoryData = {
    body?: never;
    path: {
        /**
         * Lesson Id
         */
        lesson_id: string;
    };
    query?: {
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/lessons/{lesson_id}/history';
};

export type AdminLrnLessonsHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnLessonsHistoryError = AdminLrnLessonsHistoryErrors[keyof AdminLrnLessonsHistoryErrors];

export type AdminLrnLessonsHistoryResponses = {
    /**
     * Successful Response
     */
    200: LearnHistoryPageOut;
};

export type AdminLrnLessonsHistoryResponse = AdminLrnLessonsHistoryResponses[keyof AdminLrnLessonsHistoryResponses];

export type AdminLrnTestsSubmitData = {
    body?: never;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}/submit';
};

export type AdminLrnTestsSubmitErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsSubmitError = AdminLrnTestsSubmitErrors[keyof AdminLrnTestsSubmitErrors];

export type AdminLrnTestsSubmitResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnTestsSubmitResponse = AdminLrnTestsSubmitResponses[keyof AdminLrnTestsSubmitResponses];

export type AdminLrnTestsDecisionData = {
    body: DecisionIn;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}/decision';
};

export type AdminLrnTestsDecisionErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsDecisionError = AdminLrnTestsDecisionErrors[keyof AdminLrnTestsDecisionErrors];

export type AdminLrnTestsDecisionResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnTestsDecisionResponse = AdminLrnTestsDecisionResponses[keyof AdminLrnTestsDecisionResponses];

export type AdminLrnTestsUnpublishData = {
    /**
     * Body
     */
    body?: UnpublishIn | null;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}/unpublish';
};

export type AdminLrnTestsUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsUnpublishError = AdminLrnTestsUnpublishErrors[keyof AdminLrnTestsUnpublishErrors];

export type AdminLrnTestsUnpublishResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnTestsUnpublishResponse = AdminLrnTestsUnpublishResponses[keyof AdminLrnTestsUnpublishResponses];

export type AdminLrnTestsArchiveData = {
    body: LearnArchiveIn;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}/archive';
};

export type AdminLrnTestsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsArchiveError = AdminLrnTestsArchiveErrors[keyof AdminLrnTestsArchiveErrors];

export type AdminLrnTestsArchiveResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnTestsArchiveResponse = AdminLrnTestsArchiveResponses[keyof AdminLrnTestsArchiveResponses];

export type AdminLrnTestsRestoreData = {
    body?: never;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tests/{test_id}/restore';
};

export type AdminLrnTestsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsRestoreError = AdminLrnTestsRestoreErrors[keyof AdminLrnTestsRestoreErrors];

export type AdminLrnTestsRestoreResponses = {
    /**
     * Successful Response
     */
    200: LearnStatusOut;
};

export type AdminLrnTestsRestoreResponse = AdminLrnTestsRestoreResponses[keyof AdminLrnTestsRestoreResponses];

export type AdminLrnTestsHistoryData = {
    body?: never;
    path: {
        /**
         * Test Id
         */
        test_id: string;
    };
    query?: {
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/tests/{test_id}/history';
};

export type AdminLrnTestsHistoryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminLrnTestsHistoryError = AdminLrnTestsHistoryErrors[keyof AdminLrnTestsHistoryErrors];

export type AdminLrnTestsHistoryResponses = {
    /**
     * Successful Response
     */
    200: LearnHistoryPageOut;
};

export type AdminLrnTestsHistoryResponse = AdminLrnTestsHistoryResponses[keyof AdminLrnTestsHistoryResponses];

export type AdminTstReferenceGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/taste/reference';
};

export type AdminTstReferenceGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstReferenceGetError = AdminTstReferenceGetErrors[keyof AdminTstReferenceGetErrors];

export type AdminTstReferenceGetResponses = {
    /**
     * Successful Response
     */
    200: TstTasteReferenceOut;
};

export type AdminTstReferenceGetResponse = AdminTstReferenceGetResponses[keyof AdminTstReferenceGetResponses];

export type AdminTstWinesSearchData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Q
         */
        q?: string | null;
    };
    url: '/api/v1/admin/taste/wines';
};

export type AdminTstWinesSearchErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstWinesSearchError = AdminTstWinesSearchErrors[keyof AdminTstWinesSearchErrors];

export type AdminTstWinesSearchResponses = {
    /**
     * Successful Response
     */
    200: TstWineOptionsOut;
};

export type AdminTstWinesSearchResponse = AdminTstWinesSearchResponses[keyof AdminTstWinesSearchResponses];

export type AdminTstCalibrationsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: avg_accuracy, created_at, expert, passed_count, region, status, updated_at, wine
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Вкладки «Опубликованы» / «Черновики»; архив — `archived` (draft, published, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Вкладки «Опубликованы» / «Черновики»; архив — `archived` (draft, published, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Expert Id]
         *
         * Фильтр по expert_id
         */
        'filter[expert_id]'?: string | null;
        /**
         * Filter[Expert Id][]
         *
         * Фильтр по expert_id; несколько значений
         */
        'filter[expert_id][]'?: Array<string> | null;
        /**
         * Filter[Wine Id]
         *
         * Фильтр по wine_id
         */
        'filter[wine_id]'?: string | null;
        /**
         * Filter[Wine Id][]
         *
         * Фильтр по wine_id; несколько значений
         */
        'filter[wine_id][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Фильтр по region_id
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Фильтр по region_id; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/calibrations';
};

export type AdminTstCalibrationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsListError = AdminTstCalibrationsListErrors[keyof AdminTstCalibrationsListErrors];

export type AdminTstCalibrationsListResponses = {
    /**
     * Successful Response
     */
    200: TstPageTstCalibrationRowOut;
};

export type AdminTstCalibrationsListResponse = AdminTstCalibrationsListResponses[keyof AdminTstCalibrationsListResponses];

export type AdminTstCalibrationsCreateData = {
    body: TstCalibrationCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/calibrations';
};

export type AdminTstCalibrationsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsCreateError = AdminTstCalibrationsCreateErrors[keyof AdminTstCalibrationsCreateErrors];

export type AdminTstCalibrationsCreateResponses = {
    /**
     * Successful Response
     */
    201: TstCalibrationAdminOut;
};

export type AdminTstCalibrationsCreateResponse = AdminTstCalibrationsCreateResponses[keyof AdminTstCalibrationsCreateResponses];

export type AdminTstCalibrationsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: avg_accuracy, created_at, expert, passed_count, region, status, updated_at, wine
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Вкладки «Опубликованы» / «Черновики»; архив — `archived` (draft, published, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Вкладки «Опубликованы» / «Черновики»; архив — `archived` (draft, published, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Expert Id]
         *
         * Фильтр по expert_id
         */
        'filter[expert_id]'?: string | null;
        /**
         * Filter[Expert Id][]
         *
         * Фильтр по expert_id; несколько значений
         */
        'filter[expert_id][]'?: Array<string> | null;
        /**
         * Filter[Wine Id]
         *
         * Фильтр по wine_id
         */
        'filter[wine_id]'?: string | null;
        /**
         * Filter[Wine Id][]
         *
         * Фильтр по wine_id; несколько значений
         */
        'filter[wine_id][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Фильтр по region_id
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Фильтр по region_id; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/calibrations/export';
};

export type AdminTstCalibrationsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsExportError = AdminTstCalibrationsExportErrors[keyof AdminTstCalibrationsExportErrors];

export type AdminTstCalibrationsExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminTstCalibrationsDeleteData = {
    body: TstConfirmNameIn;
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/admin/calibrations/{calibration_id}';
};

export type AdminTstCalibrationsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsDeleteError = AdminTstCalibrationsDeleteErrors[keyof AdminTstCalibrationsDeleteErrors];

export type AdminTstCalibrationsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminTstCalibrationsDeleteResponse = AdminTstCalibrationsDeleteResponses[keyof AdminTstCalibrationsDeleteResponses];

export type AdminTstCalibrationsGetData = {
    body?: never;
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/admin/calibrations/{calibration_id}';
};

export type AdminTstCalibrationsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsGetError = AdminTstCalibrationsGetErrors[keyof AdminTstCalibrationsGetErrors];

export type AdminTstCalibrationsGetResponses = {
    /**
     * Successful Response
     */
    200: TstCalibrationAdminOut;
};

export type AdminTstCalibrationsGetResponse = AdminTstCalibrationsGetResponses[keyof AdminTstCalibrationsGetResponses];

export type AdminTstCalibrationsUpdateData = {
    body: TstCalibrationPatchIn;
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/admin/calibrations/{calibration_id}';
};

export type AdminTstCalibrationsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsUpdateError = AdminTstCalibrationsUpdateErrors[keyof AdminTstCalibrationsUpdateErrors];

export type AdminTstCalibrationsUpdateResponses = {
    /**
     * Successful Response
     */
    200: TstCalibrationAdminOut;
};

export type AdminTstCalibrationsUpdateResponse = AdminTstCalibrationsUpdateResponses[keyof AdminTstCalibrationsUpdateResponses];

export type AdminTstCalibrationsReferencePutData = {
    body: TstCalibrationReferenceIn;
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/admin/calibrations/{calibration_id}/reference';
};

export type AdminTstCalibrationsReferencePutErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsReferencePutError = AdminTstCalibrationsReferencePutErrors[keyof AdminTstCalibrationsReferencePutErrors];

export type AdminTstCalibrationsReferencePutResponses = {
    /**
     * Successful Response
     */
    200: TstCalibrationAdminOut;
};

export type AdminTstCalibrationsReferencePutResponse = AdminTstCalibrationsReferencePutResponses[keyof AdminTstCalibrationsReferencePutResponses];

export type AdminTstCalibrationsArchiveData = {
    body: TstCalibrationArchiveIn;
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/admin/calibrations/{calibration_id}/archive';
};

export type AdminTstCalibrationsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsArchiveError = AdminTstCalibrationsArchiveErrors[keyof AdminTstCalibrationsArchiveErrors];

export type AdminTstCalibrationsArchiveResponses = {
    /**
     * Successful Response
     */
    200: TstCalibrationAdminOut;
};

export type AdminTstCalibrationsArchiveResponse = AdminTstCalibrationsArchiveResponses[keyof AdminTstCalibrationsArchiveResponses];

export type AdminTstCalibrationsRestoreData = {
    body?: never;
    path: {
        /**
         * Calibration Id
         */
        calibration_id: string;
    };
    query?: never;
    url: '/api/v1/admin/calibrations/{calibration_id}/restore';
};

export type AdminTstCalibrationsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstCalibrationsRestoreError = AdminTstCalibrationsRestoreErrors[keyof AdminTstCalibrationsRestoreErrors];

export type AdminTstCalibrationsRestoreResponses = {
    /**
     * Successful Response
     */
    200: TstCalibrationAdminOut;
};

export type AdminTstCalibrationsRestoreResponse = AdminTstCalibrationsRestoreResponses[keyof AdminTstCalibrationsRestoreResponses];

export type AdminTstBlindWinesGetData = {
    body?: never;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tastings/{tasting_id}/blind-wines';
};

export type AdminTstBlindWinesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstBlindWinesGetError = AdminTstBlindWinesGetErrors[keyof AdminTstBlindWinesGetErrors];

export type AdminTstBlindWinesGetResponses = {
    /**
     * Successful Response
     */
    200: TstBlindWinesOut;
};

export type AdminTstBlindWinesGetResponse = AdminTstBlindWinesGetResponses[keyof AdminTstBlindWinesGetResponses];

export type AdminTstBlindWinesPutData = {
    body: TstBlindWinesIn;
    path: {
        /**
         * Tasting Id
         */
        tasting_id: string;
    };
    query?: never;
    url: '/api/v1/admin/tastings/{tasting_id}/blind-wines';
};

export type AdminTstBlindWinesPutErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstBlindWinesPutError = AdminTstBlindWinesPutErrors[keyof AdminTstBlindWinesPutErrors];

export type AdminTstBlindWinesPutResponses = {
    /**
     * Successful Response
     */
    200: TstBlindWinesOut;
};

export type AdminTstBlindWinesPutResponse = AdminTstBlindWinesPutResponses[keyof AdminTstBlindWinesPutResponses];

export type AdminTstAccuracyLevelsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: min_points, min_successful_blind, name, users_count
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
    };
    url: '/api/v1/admin/accuracy-levels';
};

export type AdminTstAccuracyLevelsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstAccuracyLevelsListError = AdminTstAccuracyLevelsListErrors[keyof AdminTstAccuracyLevelsListErrors];

export type AdminTstAccuracyLevelsListResponses = {
    /**
     * Successful Response
     */
    200: TstPageTstLevelRowOut;
};

export type AdminTstAccuracyLevelsListResponse = AdminTstAccuracyLevelsListResponses[keyof AdminTstAccuracyLevelsListResponses];

export type AdminTstAccuracyLevelsCreateData = {
    body: TstLevelIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/accuracy-levels';
};

export type AdminTstAccuracyLevelsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstAccuracyLevelsCreateError = AdminTstAccuracyLevelsCreateErrors[keyof AdminTstAccuracyLevelsCreateErrors];

export type AdminTstAccuracyLevelsCreateResponses = {
    /**
     * Successful Response
     */
    201: TstLevelRowOut;
};

export type AdminTstAccuracyLevelsCreateResponse = AdminTstAccuracyLevelsCreateResponses[keyof AdminTstAccuracyLevelsCreateResponses];

export type AdminTstAccuracyLevelsDeleteData = {
    body?: never;
    path: {
        /**
         * Level Id
         */
        level_id: string;
    };
    query?: never;
    url: '/api/v1/admin/accuracy-levels/{level_id}';
};

export type AdminTstAccuracyLevelsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstAccuracyLevelsDeleteError = AdminTstAccuracyLevelsDeleteErrors[keyof AdminTstAccuracyLevelsDeleteErrors];

export type AdminTstAccuracyLevelsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminTstAccuracyLevelsDeleteResponse = AdminTstAccuracyLevelsDeleteResponses[keyof AdminTstAccuracyLevelsDeleteResponses];

export type AdminTstAccuracyLevelsUpdateData = {
    body: TstLevelPatchIn;
    path: {
        /**
         * Level Id
         */
        level_id: string;
    };
    query?: never;
    url: '/api/v1/admin/accuracy-levels/{level_id}';
};

export type AdminTstAccuracyLevelsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstAccuracyLevelsUpdateError = AdminTstAccuracyLevelsUpdateErrors[keyof AdminTstAccuracyLevelsUpdateErrors];

export type AdminTstAccuracyLevelsUpdateResponses = {
    /**
     * Successful Response
     */
    200: TstLevelRowOut;
};

export type AdminTstAccuracyLevelsUpdateResponse = AdminTstAccuracyLevelsUpdateResponses[keyof AdminTstAccuracyLevelsUpdateResponses];

export type AdminTstSpecializationsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: awarded_count, category_kind, created_at, name, status
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Category Kind]
         *
         * Фильтр по category_kind (wine_type, country, region)
         */
        'filter[category_kind]'?: string | null;
        /**
         * Filter[Category Kind][]
         *
         * Фильтр по category_kind (wine_type, country, region); несколько значений
         */
        'filter[category_kind][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, draft)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, draft); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/specializations';
};

export type AdminTstSpecializationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstSpecializationsListError = AdminTstSpecializationsListErrors[keyof AdminTstSpecializationsListErrors];

export type AdminTstSpecializationsListResponses = {
    /**
     * Successful Response
     */
    200: TstPageTstSpecRowOut;
};

export type AdminTstSpecializationsListResponse = AdminTstSpecializationsListResponses[keyof AdminTstSpecializationsListResponses];

export type AdminTstSpecializationsCreateData = {
    body: TstSpecIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/specializations';
};

export type AdminTstSpecializationsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstSpecializationsCreateError = AdminTstSpecializationsCreateErrors[keyof AdminTstSpecializationsCreateErrors];

export type AdminTstSpecializationsCreateResponses = {
    /**
     * Successful Response
     */
    201: TstSpecRowOut;
};

export type AdminTstSpecializationsCreateResponse = AdminTstSpecializationsCreateResponses[keyof AdminTstSpecializationsCreateResponses];

export type AdminTstSpecializationsDeleteData = {
    body?: never;
    path: {
        /**
         * Specialization Id
         */
        specialization_id: string;
    };
    query?: never;
    url: '/api/v1/admin/specializations/{specialization_id}';
};

export type AdminTstSpecializationsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstSpecializationsDeleteError = AdminTstSpecializationsDeleteErrors[keyof AdminTstSpecializationsDeleteErrors];

export type AdminTstSpecializationsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminTstSpecializationsDeleteResponse = AdminTstSpecializationsDeleteResponses[keyof AdminTstSpecializationsDeleteResponses];

export type AdminTstSpecializationsUpdateData = {
    body: TstSpecPatchIn;
    path: {
        /**
         * Specialization Id
         */
        specialization_id: string;
    };
    query?: never;
    url: '/api/v1/admin/specializations/{specialization_id}';
};

export type AdminTstSpecializationsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstSpecializationsUpdateError = AdminTstSpecializationsUpdateErrors[keyof AdminTstSpecializationsUpdateErrors];

export type AdminTstSpecializationsUpdateResponses = {
    /**
     * Successful Response
     */
    200: TstSpecRowOut;
};

export type AdminTstSpecializationsUpdateResponse = AdminTstSpecializationsUpdateResponses[keyof AdminTstSpecializationsUpdateResponses];

export type AdminTstAccuracyResultsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: accuracy, completed_at, points, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Kind]
         *
         * «С экспертом» — calibration, «Вслепую» — blind (calibration, blind, blind_online, blind_offline)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * «С экспертом» — calibration, «Вслепую» — blind (calibration, blind, blind_online, blind_offline); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/accuracy-results';
};

export type AdminTstAccuracyResultsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstAccuracyResultsListError = AdminTstAccuracyResultsListErrors[keyof AdminTstAccuracyResultsListErrors];

export type AdminTstAccuracyResultsListResponses = {
    /**
     * Successful Response
     */
    200: TstPageTstAccuracyLogRowOut;
};

export type AdminTstAccuracyResultsListResponse = AdminTstAccuracyResultsListResponses[keyof AdminTstAccuracyResultsListResponses];

export type AdminTstAccuracyResultsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: accuracy, completed_at, points, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Kind]
         *
         * «С экспертом» — calibration, «Вслепую» — blind (calibration, blind, blind_online, blind_offline)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * «С экспертом» — calibration, «Вслепую» — blind (calibration, blind, blind_online, blind_offline); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/accuracy-results/export';
};

export type AdminTstAccuracyResultsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstAccuracyResultsExportError = AdminTstAccuracyResultsExportErrors[keyof AdminTstAccuracyResultsExportErrors];

export type AdminTstAccuracyResultsExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminTstUserSpecializationsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: avg_accuracy, awarded_at, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Category Kind]
         *
         * Фильтр по category_kind (wine_type, country, region)
         */
        'filter[category_kind]'?: string | null;
        /**
         * Filter[Category Kind][]
         *
         * Фильтр по category_kind (wine_type, country, region); несколько значений
         */
        'filter[category_kind][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Specialization Id]
         *
         * Фильтр по specialization_id
         */
        'filter[specialization_id]'?: string | null;
        /**
         * Filter[Specialization Id][]
         *
         * Фильтр по specialization_id; несколько значений
         */
        'filter[specialization_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/user-specializations';
};

export type AdminTstUserSpecializationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstUserSpecializationsListError = AdminTstUserSpecializationsListErrors[keyof AdminTstUserSpecializationsListErrors];

export type AdminTstUserSpecializationsListResponses = {
    /**
     * Successful Response
     */
    200: TstPageTstSpecLogRowOut;
};

export type AdminTstUserSpecializationsListResponse = AdminTstUserSpecializationsListResponses[keyof AdminTstUserSpecializationsListResponses];

export type AdminTstUserSpecializationsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: avg_accuracy, awarded_at, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Category Kind]
         *
         * Фильтр по category_kind (wine_type, country, region)
         */
        'filter[category_kind]'?: string | null;
        /**
         * Filter[Category Kind][]
         *
         * Фильтр по category_kind (wine_type, country, region); несколько значений
         */
        'filter[category_kind][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Specialization Id]
         *
         * Фильтр по specialization_id
         */
        'filter[specialization_id]'?: string | null;
        /**
         * Filter[Specialization Id][]
         *
         * Фильтр по specialization_id; несколько значений
         */
        'filter[specialization_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/user-specializations/export';
};

export type AdminTstUserSpecializationsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstUserSpecializationsExportError = AdminTstUserSpecializationsExportErrors[keyof AdminTstUserSpecializationsExportErrors];

export type AdminTstUserSpecializationsExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminTstDiaryEntriesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: occurred_at, score, user, wine
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Recognized]
         *
         * «По фото этикетки»
         */
        'filter[recognized]'?: string | null;
        /**
         * Filter[Recognized][]
         *
         * «По фото этикетки»; несколько значений
         */
        'filter[recognized][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (tasting, unrated)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (tasting, unrated); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/diary-entries';
};

export type AdminTstDiaryEntriesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstDiaryEntriesListError = AdminTstDiaryEntriesListErrors[keyof AdminTstDiaryEntriesListErrors];

export type AdminTstDiaryEntriesListResponses = {
    /**
     * Successful Response
     */
    200: TstPageTstDiaryLogRowOut;
};

export type AdminTstDiaryEntriesListResponse = AdminTstDiaryEntriesListResponses[keyof AdminTstDiaryEntriesListResponses];

export type AdminTstDiaryEntriesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: occurred_at, score, user, wine
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Recognized]
         *
         * «По фото этикетки»
         */
        'filter[recognized]'?: string | null;
        /**
         * Filter[Recognized][]
         *
         * «По фото этикетки»; несколько значений
         */
        'filter[recognized][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (tasting, unrated)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (tasting, unrated); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/diary-entries/export';
};

export type AdminTstDiaryEntriesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstDiaryEntriesExportError = AdminTstDiaryEntriesExportErrors[keyof AdminTstDiaryEntriesExportErrors];

export type AdminTstDiaryEntriesExportResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type AdminTstDiaryEntriesGetData = {
    body?: never;
    path: {
        /**
         * Entry Id
         */
        entry_id: string;
    };
    query?: never;
    url: '/api/v1/admin/diary-entries/{entry_id}';
};

export type AdminTstDiaryEntriesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstDiaryEntriesGetError = AdminTstDiaryEntriesGetErrors[keyof AdminTstDiaryEntriesGetErrors];

export type AdminTstDiaryEntriesGetResponses = {
    /**
     * Successful Response
     */
    200: TstDiaryEntryCardOut;
};

export type AdminTstDiaryEntriesGetResponse = AdminTstDiaryEntriesGetResponses[keyof AdminTstDiaryEntriesGetResponses];

export type AdminTstUserTasteSummaryGetData = {
    body?: never;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/admin/users/{user_id}/taste-summary';
};

export type AdminTstUserTasteSummaryGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminTstUserTasteSummaryGetError = AdminTstUserTasteSummaryGetErrors[keyof AdminTstUserTasteSummaryGetErrors];

export type AdminTstUserTasteSummaryGetResponses = {
    /**
     * Successful Response
     */
    200: TstUserTasteSummaryOut;
};

export type AdminTstUserTasteSummaryGetResponse = AdminTstUserTasteSummaryGetResponses[keyof AdminTstUserTasteSummaryGetResponses];

export type AdminGamXpRulesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: event_type, section, status, title, version, xp
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Section]
         *
         * Раздел типа события (learning, diary, tastings, purchases, trips, stamps)
         */
        'filter[section]'?: string | null;
        /**
         * Filter[Section][]
         *
         * Раздел типа события (learning, diary, tastings, purchases, trips, stamps); несколько значений
         */
        'filter[section][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус (draft, active, disabled)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус (draft, active, disabled); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Event Type]
         *
         * Тип события (lesson_completed, test_perfect, diary_entry_created, wine_order_delivered, set_delivered, tasting_online_attended, tasting_offline_attended, excursion_attended, trip_completed, stamp_bronze, stamp_gold)
         */
        'filter[event_type]'?: string | null;
        /**
         * Filter[Event Type][]
         *
         * Тип события (lesson_completed, test_perfect, diary_entry_created, wine_order_delivered, set_delivered, tasting_online_attended, tasting_offline_attended, excursion_attended, trip_completed, stamp_bronze, stamp_gold); несколько значений
         */
        'filter[event_type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/xp-rules';
};

export type AdminGamXpRulesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamXpRulesListError = AdminGamXpRulesListErrors[keyof AdminGamXpRulesListErrors];

export type AdminGamXpRulesListResponses = {
    /**
     * Successful Response
     */
    200: XpRulesPageOut;
};

export type AdminGamXpRulesListResponse = AdminGamXpRulesListResponses[keyof AdminGamXpRulesListResponses];

export type AdminGamXpRulesCreateData = {
    body: XpRuleCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/gam/xp-rules';
};

export type AdminGamXpRulesCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamXpRulesCreateError = AdminGamXpRulesCreateErrors[keyof AdminGamXpRulesCreateErrors];

export type AdminGamXpRulesCreateResponses = {
    /**
     * Successful Response
     */
    201: XpRuleOut;
};

export type AdminGamXpRulesCreateResponse = AdminGamXpRulesCreateResponses[keyof AdminGamXpRulesCreateResponses];

export type AdminGamXpRulesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: event_type, section, status, title, version, xp
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Section]
         *
         * Раздел типа события (learning, diary, tastings, purchases, trips, stamps)
         */
        'filter[section]'?: string | null;
        /**
         * Filter[Section][]
         *
         * Раздел типа события (learning, diary, tastings, purchases, trips, stamps); несколько значений
         */
        'filter[section][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус (draft, active, disabled)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус (draft, active, disabled); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Event Type]
         *
         * Тип события (lesson_completed, test_perfect, diary_entry_created, wine_order_delivered, set_delivered, tasting_online_attended, tasting_offline_attended, excursion_attended, trip_completed, stamp_bronze, stamp_gold)
         */
        'filter[event_type]'?: string | null;
        /**
         * Filter[Event Type][]
         *
         * Тип события (lesson_completed, test_perfect, diary_entry_created, wine_order_delivered, set_delivered, tasting_online_attended, tasting_offline_attended, excursion_attended, trip_completed, stamp_bronze, stamp_gold); несколько значений
         */
        'filter[event_type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/xp-rules/export';
};

export type AdminGamXpRulesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamXpRulesExportError = AdminGamXpRulesExportErrors[keyof AdminGamXpRulesExportErrors];

export type AdminGamXpRulesExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGamXpRulesDeleteData = {
    body?: never;
    path: {
        /**
         * Rule Id
         */
        rule_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/xp-rules/{rule_id}';
};

export type AdminGamXpRulesDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamXpRulesDeleteError = AdminGamXpRulesDeleteErrors[keyof AdminGamXpRulesDeleteErrors];

export type AdminGamXpRulesDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGamXpRulesDeleteResponse = AdminGamXpRulesDeleteResponses[keyof AdminGamXpRulesDeleteResponses];

export type AdminGamXpRulesGetData = {
    body?: never;
    path: {
        /**
         * Rule Id
         */
        rule_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/xp-rules/{rule_id}';
};

export type AdminGamXpRulesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamXpRulesGetError = AdminGamXpRulesGetErrors[keyof AdminGamXpRulesGetErrors];

export type AdminGamXpRulesGetResponses = {
    /**
     * Successful Response
     */
    200: XpRuleCardOut;
};

export type AdminGamXpRulesGetResponse = AdminGamXpRulesGetResponses[keyof AdminGamXpRulesGetResponses];

export type AdminGamXpRulesUpdateData = {
    body: XpRulePatchIn;
    path: {
        /**
         * Rule Id
         */
        rule_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/xp-rules/{rule_id}';
};

export type AdminGamXpRulesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamXpRulesUpdateError = AdminGamXpRulesUpdateErrors[keyof AdminGamXpRulesUpdateErrors];

export type AdminGamXpRulesUpdateResponses = {
    /**
     * Successful Response
     */
    200: XpRuleOut;
};

export type AdminGamXpRulesUpdateResponse = AdminGamXpRulesUpdateResponses[keyof AdminGamXpRulesUpdateResponses];

export type AdminGamXpLedgerListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: amount, event_type, occurred_at, section, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Section]
         *
         * Раздел (learning, diary, tastings, purchases, trips, stamps)
         */
        'filter[section]'?: string | null;
        /**
         * Filter[Section][]
         *
         * Раздел (learning, diary, tastings, purchases, trips, stamps); несколько значений
         */
        'filter[section][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Event Type]
         *
         * Тип события (lesson_completed, test_perfect, diary_entry_created, wine_order_delivered, set_delivered, tasting_online_attended, tasting_offline_attended, excursion_attended, trip_completed, stamp_bronze, stamp_gold)
         */
        'filter[event_type]'?: string | null;
        /**
         * Filter[Event Type][]
         *
         * Тип события (lesson_completed, test_perfect, diary_entry_created, wine_order_delivered, set_delivered, tasting_online_attended, tasting_offline_attended, excursion_attended, trip_completed, stamp_bronze, stamp_gold); несколько значений
         */
        'filter[event_type][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Начисление / сторно (accrual, reversal)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Начисление / сторно (accrual, reversal); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[From]
         *
         * С момента (ISO 8601)
         */
        'filter[from]'?: string | null;
        /**
         * Filter[From][]
         *
         * С момента (ISO 8601); несколько значений
         */
        'filter[from][]'?: Array<string> | null;
        /**
         * Filter[To]
         *
         * По момент (ISO 8601)
         */
        'filter[to]'?: string | null;
        /**
         * Filter[To][]
         *
         * По момент (ISO 8601); несколько значений
         */
        'filter[to][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/xp-ledger';
};

export type AdminGamXpLedgerListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamXpLedgerListError = AdminGamXpLedgerListErrors[keyof AdminGamXpLedgerListErrors];

export type AdminGamXpLedgerListResponses = {
    /**
     * Successful Response
     */
    200: XpLedgerPageOut;
};

export type AdminGamXpLedgerListResponse = AdminGamXpLedgerListResponses[keyof AdminGamXpLedgerListResponses];

export type AdminGamXpLedgerExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: amount, event_type, occurred_at, section, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Section]
         *
         * Раздел (learning, diary, tastings, purchases, trips, stamps)
         */
        'filter[section]'?: string | null;
        /**
         * Filter[Section][]
         *
         * Раздел (learning, diary, tastings, purchases, trips, stamps); несколько значений
         */
        'filter[section][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Event Type]
         *
         * Тип события (lesson_completed, test_perfect, diary_entry_created, wine_order_delivered, set_delivered, tasting_online_attended, tasting_offline_attended, excursion_attended, trip_completed, stamp_bronze, stamp_gold)
         */
        'filter[event_type]'?: string | null;
        /**
         * Filter[Event Type][]
         *
         * Тип события (lesson_completed, test_perfect, diary_entry_created, wine_order_delivered, set_delivered, tasting_online_attended, tasting_offline_attended, excursion_attended, trip_completed, stamp_bronze, stamp_gold); несколько значений
         */
        'filter[event_type][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Начисление / сторно (accrual, reversal)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Начисление / сторно (accrual, reversal); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[From]
         *
         * С момента (ISO 8601)
         */
        'filter[from]'?: string | null;
        /**
         * Filter[From][]
         *
         * С момента (ISO 8601); несколько значений
         */
        'filter[from][]'?: Array<string> | null;
        /**
         * Filter[To]
         *
         * По момент (ISO 8601)
         */
        'filter[to]'?: string | null;
        /**
         * Filter[To][]
         *
         * По момент (ISO 8601); несколько значений
         */
        'filter[to][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/xp-ledger/export';
};

export type AdminGamXpLedgerExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamXpLedgerExportError = AdminGamXpLedgerExportErrors[keyof AdminGamXpLedgerExportErrors];

export type AdminGamXpLedgerExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGamStampsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: awarded_at, level, region, user, xp
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Level]
         *
         * Штамп (bronze, gold)
         */
        'filter[level]'?: string | null;
        /**
         * Filter[Level][]
         *
         * Штамп (bronze, gold); несколько значений
         */
        'filter[level][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/stamps';
};

export type AdminGamStampsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamStampsListError = AdminGamStampsListErrors[keyof AdminGamStampsListErrors];

export type AdminGamStampsListResponses = {
    /**
     * Successful Response
     */
    200: StampsPageOut;
};

export type AdminGamStampsListResponse = AdminGamStampsListResponses[keyof AdminGamStampsListResponses];

export type AdminGamStampsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: awarded_at, level, region, user, xp
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Level]
         *
         * Штамп (bronze, gold)
         */
        'filter[level]'?: string | null;
        /**
         * Filter[Level][]
         *
         * Штамп (bronze, gold); несколько значений
         */
        'filter[level][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Регион
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Регион; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/stamps/export';
};

export type AdminGamStampsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamStampsExportError = AdminGamStampsExportErrors[keyof AdminGamStampsExportErrors];

export type AdminGamStampsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGamLevelsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: learning, min_xp, name, wine
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
    };
    url: '/api/v1/admin/gam/levels';
};

export type AdminGamLevelsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamLevelsListError = AdminGamLevelsListErrors[keyof AdminGamLevelsListErrors];

export type AdminGamLevelsListResponses = {
    /**
     * Successful Response
     */
    200: XpLevelsPageOut;
};

export type AdminGamLevelsListResponse = AdminGamLevelsListResponses[keyof AdminGamLevelsListResponses];

export type AdminGamLevelsCreateData = {
    body: XpLevelCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/gam/levels';
};

export type AdminGamLevelsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamLevelsCreateError = AdminGamLevelsCreateErrors[keyof AdminGamLevelsCreateErrors];

export type AdminGamLevelsCreateResponses = {
    /**
     * Successful Response
     */
    201: XpLevelOut;
};

export type AdminGamLevelsCreateResponse = AdminGamLevelsCreateResponses[keyof AdminGamLevelsCreateResponses];

export type AdminGamLevelsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: learning, min_xp, name, wine
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
    };
    url: '/api/v1/admin/gam/levels/export';
};

export type AdminGamLevelsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamLevelsExportError = AdminGamLevelsExportErrors[keyof AdminGamLevelsExportErrors];

export type AdminGamLevelsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGamLevelsDeleteData = {
    body?: never;
    path: {
        /**
         * Level Id
         */
        level_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/levels/{level_id}';
};

export type AdminGamLevelsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamLevelsDeleteError = AdminGamLevelsDeleteErrors[keyof AdminGamLevelsDeleteErrors];

export type AdminGamLevelsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGamLevelsDeleteResponse = AdminGamLevelsDeleteResponses[keyof AdminGamLevelsDeleteResponses];

export type AdminGamLevelsUpdateData = {
    body: XpLevelPatchIn;
    path: {
        /**
         * Level Id
         */
        level_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/levels/{level_id}';
};

export type AdminGamLevelsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamLevelsUpdateError = AdminGamLevelsUpdateErrors[keyof AdminGamLevelsUpdateErrors];

export type AdminGamLevelsUpdateResponses = {
    /**
     * Successful Response
     */
    200: XpLevelOut;
};

export type AdminGamLevelsUpdateResponse = AdminGamLevelsUpdateResponses[keyof AdminGamLevelsUpdateResponses];

export type AdminGamRewardsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: cost, executor, kind, sort, status, title
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Kind]
         *
         * Вид (reward, goal)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Вид (reward, goal); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус (active, hidden)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус (active, hidden); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Executor Type]
         *
         * Исполнитель (platform, expert, winery)
         */
        'filter[executor_type]'?: string | null;
        /**
         * Filter[Executor Type][]
         *
         * Исполнитель (platform, expert, winery); несколько значений
         */
        'filter[executor_type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/rewards';
};

export type AdminGamRewardsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamRewardsListError = AdminGamRewardsListErrors[keyof AdminGamRewardsListErrors];

export type AdminGamRewardsListResponses = {
    /**
     * Successful Response
     */
    200: RewardsPageOut;
};

export type AdminGamRewardsListResponse = AdminGamRewardsListResponses[keyof AdminGamRewardsListResponses];

export type AdminGamRewardsCreateData = {
    body: RewardCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/gam/rewards';
};

export type AdminGamRewardsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamRewardsCreateError = AdminGamRewardsCreateErrors[keyof AdminGamRewardsCreateErrors];

export type AdminGamRewardsCreateResponses = {
    /**
     * Successful Response
     */
    201: RewardRowOut;
};

export type AdminGamRewardsCreateResponse = AdminGamRewardsCreateResponses[keyof AdminGamRewardsCreateResponses];

export type AdminGamRewardsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: cost, executor, kind, sort, status, title
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Kind]
         *
         * Вид (reward, goal)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Вид (reward, goal); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Статус (active, hidden)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус (active, hidden); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Executor Type]
         *
         * Исполнитель (platform, expert, winery)
         */
        'filter[executor_type]'?: string | null;
        /**
         * Filter[Executor Type][]
         *
         * Исполнитель (platform, expert, winery); несколько значений
         */
        'filter[executor_type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/rewards/export';
};

export type AdminGamRewardsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamRewardsExportError = AdminGamRewardsExportErrors[keyof AdminGamRewardsExportErrors];

export type AdminGamRewardsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGamRewardsDeleteData = {
    body?: never;
    path: {
        /**
         * Reward Id
         */
        reward_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/rewards/{reward_id}';
};

export type AdminGamRewardsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamRewardsDeleteError = AdminGamRewardsDeleteErrors[keyof AdminGamRewardsDeleteErrors];

export type AdminGamRewardsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGamRewardsDeleteResponse = AdminGamRewardsDeleteResponses[keyof AdminGamRewardsDeleteResponses];

export type AdminGamRewardsUpdateData = {
    body: RewardPatchIn;
    path: {
        /**
         * Reward Id
         */
        reward_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/rewards/{reward_id}';
};

export type AdminGamRewardsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamRewardsUpdateError = AdminGamRewardsUpdateErrors[keyof AdminGamRewardsUpdateErrors];

export type AdminGamRewardsUpdateResponses = {
    /**
     * Successful Response
     */
    200: RewardRowOut;
};

export type AdminGamRewardsUpdateResponse = AdminGamRewardsUpdateResponses[keyof AdminGamRewardsUpdateResponses];

export type AdminGamTiersListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/gam/tiers';
};

export type AdminGamTiersListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamTiersListError = AdminGamTiersListErrors[keyof AdminGamTiersListErrors];

export type AdminGamTiersListResponses = {
    /**
     * Successful Response
     */
    200: TiersPageOut;
};

export type AdminGamTiersListResponse = AdminGamTiersListResponses[keyof AdminGamTiersListResponses];

export type AdminGamTiersCreateData = {
    body: TierCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/gam/tiers';
};

export type AdminGamTiersCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamTiersCreateError = AdminGamTiersCreateErrors[keyof AdminGamTiersCreateErrors];

export type AdminGamTiersCreateResponses = {
    /**
     * Successful Response
     */
    201: TierRowOut;
};

export type AdminGamTiersCreateResponse = AdminGamTiersCreateResponses[keyof AdminGamTiersCreateResponses];

export type AdminGamTiersExportData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/gam/tiers/export';
};

export type AdminGamTiersExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamTiersExportError = AdminGamTiersExportErrors[keyof AdminGamTiersExportErrors];

export type AdminGamTiersExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGamTierGatesListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/gam/tier-gates';
};

export type AdminGamTierGatesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamTierGatesListError = AdminGamTierGatesListErrors[keyof AdminGamTierGatesListErrors];

export type AdminGamTierGatesListResponses = {
    /**
     * Successful Response
     */
    200: TierGatesOut;
};

export type AdminGamTierGatesListResponse = AdminGamTierGatesListResponses[keyof AdminGamTierGatesListResponses];

export type AdminGamTierGatesUpdateData = {
    body: TierGatePatchIn;
    path: {
        key: TierGateKey;
    };
    query?: never;
    url: '/api/v1/admin/gam/tier-gates/{key}';
};

export type AdminGamTierGatesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamTierGatesUpdateError = AdminGamTierGatesUpdateErrors[keyof AdminGamTierGatesUpdateErrors];

export type AdminGamTierGatesUpdateResponses = {
    /**
     * Successful Response
     */
    200: TierGateOut;
};

export type AdminGamTierGatesUpdateResponse = AdminGamTierGatesUpdateResponses[keyof AdminGamTierGatesUpdateResponses];

export type AdminGamTiersDeleteData = {
    body?: never;
    path: {
        /**
         * Tier Id
         */
        tier_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/tiers/{tier_id}';
};

export type AdminGamTiersDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamTiersDeleteError = AdminGamTiersDeleteErrors[keyof AdminGamTiersDeleteErrors];

export type AdminGamTiersDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminGamTiersDeleteResponse = AdminGamTiersDeleteResponses[keyof AdminGamTiersDeleteResponses];

export type AdminGamTiersUpdateData = {
    body: TierPatchIn;
    path: {
        /**
         * Tier Id
         */
        tier_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/tiers/{tier_id}';
};

export type AdminGamTiersUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamTiersUpdateError = AdminGamTiersUpdateErrors[keyof AdminGamTiersUpdateErrors];

export type AdminGamTiersUpdateResponses = {
    /**
     * Successful Response
     */
    200: TierRowOut;
};

export type AdminGamTiersUpdateResponse = AdminGamTiersUpdateResponses[keyof AdminGamTiersUpdateResponses];

export type AdminGamExchangesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: cost, due_date, number, requested_at, status, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Статус (requested, paid, declined)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус (requested, paid, declined); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Overdue]
         *
         * Просроченные
         */
        'filter[overdue]'?: string | null;
        /**
         * Filter[Overdue][]
         *
         * Просроченные; несколько значений
         */
        'filter[overdue][]'?: Array<string> | null;
        /**
         * Filter[Reward Id]
         *
         * Награда
         */
        'filter[reward_id]'?: string | null;
        /**
         * Filter[Reward Id][]
         *
         * Награда; несколько значений
         */
        'filter[reward_id][]'?: Array<string> | null;
        /**
         * Filter[Executor Type]
         *
         * Исполнитель (platform, expert, winery)
         */
        'filter[executor_type]'?: string | null;
        /**
         * Filter[Executor Type][]
         *
         * Исполнитель (platform, expert, winery); несколько значений
         */
        'filter[executor_type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/exchanges';
};

export type AdminGamExchangesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamExchangesListError = AdminGamExchangesListErrors[keyof AdminGamExchangesListErrors];

export type AdminGamExchangesListResponses = {
    /**
     * Successful Response
     */
    200: GamAdminExchangesPageOut;
};

export type AdminGamExchangesListResponse = AdminGamExchangesListResponses[keyof AdminGamExchangesListResponses];

export type AdminGamExchangesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: cost, due_date, number, requested_at, status, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Статус (requested, paid, declined)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Статус (requested, paid, declined); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Пользователь
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Пользователь; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
        /**
         * Filter[Overdue]
         *
         * Просроченные
         */
        'filter[overdue]'?: string | null;
        /**
         * Filter[Overdue][]
         *
         * Просроченные; несколько значений
         */
        'filter[overdue][]'?: Array<string> | null;
        /**
         * Filter[Reward Id]
         *
         * Награда
         */
        'filter[reward_id]'?: string | null;
        /**
         * Filter[Reward Id][]
         *
         * Награда; несколько значений
         */
        'filter[reward_id][]'?: Array<string> | null;
        /**
         * Filter[Executor Type]
         *
         * Исполнитель (platform, expert, winery)
         */
        'filter[executor_type]'?: string | null;
        /**
         * Filter[Executor Type][]
         *
         * Исполнитель (platform, expert, winery); несколько значений
         */
        'filter[executor_type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/gam/exchanges/export';
};

export type AdminGamExchangesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamExchangesExportError = AdminGamExchangesExportErrors[keyof AdminGamExchangesExportErrors];

export type AdminGamExchangesExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminGamExchangesGetData = {
    body?: never;
    path: {
        /**
         * Exchange Id
         */
        exchange_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/exchanges/{exchange_id}';
};

export type AdminGamExchangesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamExchangesGetError = AdminGamExchangesGetErrors[keyof AdminGamExchangesGetErrors];

export type AdminGamExchangesGetResponses = {
    /**
     * Successful Response
     */
    200: ExchangeCardOut;
};

export type AdminGamExchangesGetResponse = AdminGamExchangesGetResponses[keyof AdminGamExchangesGetResponses];

export type AdminGamExchangesCompleteData = {
    body: ExchangeCompleteIn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * Exchange Id
         */
        exchange_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/exchanges/{exchange_id}/complete';
};

export type AdminGamExchangesCompleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamExchangesCompleteError = AdminGamExchangesCompleteErrors[keyof AdminGamExchangesCompleteErrors];

export type AdminGamExchangesCompleteResponses = {
    /**
     * Successful Response
     */
    200: ExchangeCardOut;
};

export type AdminGamExchangesCompleteResponse = AdminGamExchangesCompleteResponses[keyof AdminGamExchangesCompleteResponses];

export type AdminGamExchangesDeclineData = {
    body: ExchangeDeclineIn;
    path: {
        /**
         * Exchange Id
         */
        exchange_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/exchanges/{exchange_id}/decline';
};

export type AdminGamExchangesDeclineErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamExchangesDeclineError = AdminGamExchangesDeclineErrors[keyof AdminGamExchangesDeclineErrors];

export type AdminGamExchangesDeclineResponses = {
    /**
     * Successful Response
     */
    200: ExchangeCardOut;
};

export type AdminGamExchangesDeclineResponse = AdminGamExchangesDeclineResponses[keyof AdminGamExchangesDeclineResponses];

export type AdminGamUsersSummaryData = {
    body?: never;
    path: {
        /**
         * User Id
         */
        user_id: string;
    };
    query?: never;
    url: '/api/v1/admin/gam/users/{user_id}/summary';
};

export type AdminGamUsersSummaryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamUsersSummaryError = AdminGamUsersSummaryErrors[keyof AdminGamUsersSummaryErrors];

export type AdminGamUsersSummaryResponses = {
    /**
     * Successful Response
     */
    200: UserSummaryOut;
};

export type AdminGamUsersSummaryResponse = AdminGamUsersSummaryResponses[keyof AdminGamUsersSummaryResponses];

export type AdminGamStatsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * From
         */
        from?: string | null;
        /**
         * To
         */
        to?: string | null;
    };
    url: '/api/v1/admin/gam/stats';
};

export type AdminGamStatsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminGamStatsError = AdminGamStatsErrors[keyof AdminGamStatsErrors];

export type AdminGamStatsResponses = {
    /**
     * Successful Response
     */
    200: GamStatsOut;
};

export type AdminGamStatsResponse = AdminGamStatsResponses[keyof AdminGamStatsResponses];

export type AdminCntPublicationsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, comments, date, likes, status, title, views
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, published, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, published, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (article, news)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (article, news); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Author Kind]
         *
         * Фильтр по author_kind (platform, expert, winemaker)
         */
        'filter[author_kind]'?: string | null;
        /**
         * Filter[Author Kind][]
         *
         * Фильтр по author_kind (platform, expert, winemaker); несколько значений
         */
        'filter[author_kind][]'?: Array<string> | null;
        /**
         * Filter[Source]
         *
         * Фильтр по source (app, admin)
         */
        'filter[source]'?: string | null;
        /**
         * Filter[Source][]
         *
         * Фильтр по source (app, admin); несколько значений
         */
        'filter[source][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Фильтр по region_id
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Фильтр по region_id; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Winery Id]
         *
         * Фильтр по winery_id
         */
        'filter[winery_id]'?: string | null;
        /**
         * Filter[Winery Id][]
         *
         * Фильтр по winery_id; несколько значений
         */
        'filter[winery_id][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Фильтр по author_member_id
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Фильтр по author_member_id; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/publications';
};

export type AdminCntPublicationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsListError = AdminCntPublicationsListErrors[keyof AdminCntPublicationsListErrors];

export type AdminCntPublicationsListResponses = {
    /**
     * Successful Response
     */
    200: CntPagePublicationRowOut;
};

export type AdminCntPublicationsListResponse = AdminCntPublicationsListResponses[keyof AdminCntPublicationsListResponses];

export type AdminCntPublicationsCreateData = {
    body: PublicationCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/publications';
};

export type AdminCntPublicationsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsCreateError = AdminCntPublicationsCreateErrors[keyof AdminCntPublicationsCreateErrors];

export type AdminCntPublicationsCreateResponses = {
    /**
     * Successful Response
     */
    201: PublicationAdminOut;
};

export type AdminCntPublicationsCreateResponse = AdminCntPublicationsCreateResponses[keyof AdminCntPublicationsCreateResponses];

export type AdminCntPublicationsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, comments, date, likes, status, title, views
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, published, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, published, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Kind]
         *
         * Фильтр по kind (article, news)
         */
        'filter[kind]'?: string | null;
        /**
         * Filter[Kind][]
         *
         * Фильтр по kind (article, news); несколько значений
         */
        'filter[kind][]'?: Array<string> | null;
        /**
         * Filter[Author Kind]
         *
         * Фильтр по author_kind (platform, expert, winemaker)
         */
        'filter[author_kind]'?: string | null;
        /**
         * Filter[Author Kind][]
         *
         * Фильтр по author_kind (platform, expert, winemaker); несколько значений
         */
        'filter[author_kind][]'?: Array<string> | null;
        /**
         * Filter[Source]
         *
         * Фильтр по source (app, admin)
         */
        'filter[source]'?: string | null;
        /**
         * Filter[Source][]
         *
         * Фильтр по source (app, admin); несколько значений
         */
        'filter[source][]'?: Array<string> | null;
        /**
         * Filter[Region Id]
         *
         * Фильтр по region_id
         */
        'filter[region_id]'?: string | null;
        /**
         * Filter[Region Id][]
         *
         * Фильтр по region_id; несколько значений
         */
        'filter[region_id][]'?: Array<string> | null;
        /**
         * Filter[Winery Id]
         *
         * Фильтр по winery_id
         */
        'filter[winery_id]'?: string | null;
        /**
         * Filter[Winery Id][]
         *
         * Фильтр по winery_id; несколько значений
         */
        'filter[winery_id][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Фильтр по author_member_id
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Фильтр по author_member_id; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/publications/export';
};

export type AdminCntPublicationsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsExportError = AdminCntPublicationsExportErrors[keyof AdminCntPublicationsExportErrors];

export type AdminCntPublicationsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminCntPublicationsOptionsData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Type
         */
        type: 'region' | 'wine' | 'experience' | 'winery';
        /**
         * Q
         */
        q?: string | null;
        /**
         * Region Id
         */
        region_id?: string | null;
    };
    url: '/api/v1/admin/publications/options';
};

export type AdminCntPublicationsOptionsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsOptionsError = AdminCntPublicationsOptionsErrors[keyof AdminCntPublicationsOptionsErrors];

export type AdminCntPublicationsOptionsResponses = {
    /**
     * Response Admin Cnt Publications Options
     *
     * Successful Response
     */
    200: Array<ContentOptionOut>;
};

export type AdminCntPublicationsOptionsResponse = AdminCntPublicationsOptionsResponses[keyof AdminCntPublicationsOptionsResponses];

export type AdminCntPublicationsDeleteData = {
    body: PublicationDeleteIn;
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/admin/publications/{publication_id}';
};

export type AdminCntPublicationsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsDeleteError = AdminCntPublicationsDeleteErrors[keyof AdminCntPublicationsDeleteErrors];

export type AdminCntPublicationsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminCntPublicationsDeleteResponse = AdminCntPublicationsDeleteResponses[keyof AdminCntPublicationsDeleteResponses];

export type AdminCntPublicationsGetData = {
    body?: never;
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/admin/publications/{publication_id}';
};

export type AdminCntPublicationsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsGetError = AdminCntPublicationsGetErrors[keyof AdminCntPublicationsGetErrors];

export type AdminCntPublicationsGetResponses = {
    /**
     * Successful Response
     */
    200: PublicationAdminOut;
};

export type AdminCntPublicationsGetResponse = AdminCntPublicationsGetResponses[keyof AdminCntPublicationsGetResponses];

export type AdminCntPublicationsUpdateData = {
    body: PublicationPatchIn;
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/admin/publications/{publication_id}';
};

export type AdminCntPublicationsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsUpdateError = AdminCntPublicationsUpdateErrors[keyof AdminCntPublicationsUpdateErrors];

export type AdminCntPublicationsUpdateResponses = {
    /**
     * Successful Response
     */
    200: PublicationAdminOut;
};

export type AdminCntPublicationsUpdateResponse = AdminCntPublicationsUpdateResponses[keyof AdminCntPublicationsUpdateResponses];

export type AdminCntPublicationsPublishData = {
    body?: never;
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/admin/publications/{publication_id}/publish';
};

export type AdminCntPublicationsPublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsPublishError = AdminCntPublicationsPublishErrors[keyof AdminCntPublicationsPublishErrors];

export type AdminCntPublicationsPublishResponses = {
    /**
     * Successful Response
     */
    200: PublicationAdminOut;
};

export type AdminCntPublicationsPublishResponse = AdminCntPublicationsPublishResponses[keyof AdminCntPublicationsPublishResponses];

export type AdminCntPublicationsUnpublishData = {
    body?: never;
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/admin/publications/{publication_id}/unpublish';
};

export type AdminCntPublicationsUnpublishErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsUnpublishError = AdminCntPublicationsUnpublishErrors[keyof AdminCntPublicationsUnpublishErrors];

export type AdminCntPublicationsUnpublishResponses = {
    /**
     * Successful Response
     */
    200: PublicationAdminOut;
};

export type AdminCntPublicationsUnpublishResponse = AdminCntPublicationsUnpublishResponses[keyof AdminCntPublicationsUnpublishResponses];

export type AdminCntPublicationsArchiveData = {
    body: ContentArchiveIn;
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/admin/publications/{publication_id}/archive';
};

export type AdminCntPublicationsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsArchiveError = AdminCntPublicationsArchiveErrors[keyof AdminCntPublicationsArchiveErrors];

export type AdminCntPublicationsArchiveResponses = {
    /**
     * Successful Response
     */
    200: PublicationAdminOut;
};

export type AdminCntPublicationsArchiveResponse = AdminCntPublicationsArchiveResponses[keyof AdminCntPublicationsArchiveResponses];

export type AdminCntPublicationsRestoreData = {
    body?: never;
    path: {
        /**
         * Publication Id
         */
        publication_id: string;
    };
    query?: never;
    url: '/api/v1/admin/publications/{publication_id}/restore';
};

export type AdminCntPublicationsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntPublicationsRestoreError = AdminCntPublicationsRestoreErrors[keyof AdminCntPublicationsRestoreErrors];

export type AdminCntPublicationsRestoreResponses = {
    /**
     * Successful Response
     */
    200: PublicationAdminOut;
};

export type AdminCntPublicationsRestoreResponse = AdminCntPublicationsRestoreResponses[keyof AdminCntPublicationsRestoreResponses];

export type AdminCntStoriesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, published_at, status, title, ttl, views
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, expired, hidden)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, expired, hidden); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Ttl]
         *
         * Фильтр по ttl (h24, week)
         */
        'filter[ttl]'?: string | null;
        /**
         * Filter[Ttl][]
         *
         * Фильтр по ttl (h24, week); несколько значений
         */
        'filter[ttl][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Фильтр по author_member_id
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Фильтр по author_member_id; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/stories';
};

export type AdminCntStoriesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntStoriesListError = AdminCntStoriesListErrors[keyof AdminCntStoriesListErrors];

export type AdminCntStoriesListResponses = {
    /**
     * Successful Response
     */
    200: CntPageStoryRowOut;
};

export type AdminCntStoriesListResponse = AdminCntStoriesListResponses[keyof AdminCntStoriesListResponses];

export type AdminCntStoriesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: author, published_at, status, title, ttl, views
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, expired, hidden)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, expired, hidden); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Ttl]
         *
         * Фильтр по ttl (h24, week)
         */
        'filter[ttl]'?: string | null;
        /**
         * Filter[Ttl][]
         *
         * Фильтр по ttl (h24, week); несколько значений
         */
        'filter[ttl][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Фильтр по author_member_id
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Фильтр по author_member_id; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/stories/export';
};

export type AdminCntStoriesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntStoriesExportError = AdminCntStoriesExportErrors[keyof AdminCntStoriesExportErrors];

export type AdminCntStoriesExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminCntStoriesGetData = {
    body?: never;
    path: {
        /**
         * Story Id
         */
        story_id: string;
    };
    query?: never;
    url: '/api/v1/admin/stories/{story_id}';
};

export type AdminCntStoriesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntStoriesGetError = AdminCntStoriesGetErrors[keyof AdminCntStoriesGetErrors];

export type AdminCntStoriesGetResponses = {
    /**
     * Successful Response
     */
    200: StoryAdminOut;
};

export type AdminCntStoriesGetResponse = AdminCntStoriesGetResponses[keyof AdminCntStoriesGetResponses];

export type AdminCntStoriesHideData = {
    body?: never;
    path: {
        /**
         * Story Id
         */
        story_id: string;
    };
    query?: never;
    url: '/api/v1/admin/stories/{story_id}/hide';
};

export type AdminCntStoriesHideErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntStoriesHideError = AdminCntStoriesHideErrors[keyof AdminCntStoriesHideErrors];

export type AdminCntStoriesHideResponses = {
    /**
     * Successful Response
     */
    200: StoryAdminOut;
};

export type AdminCntStoriesHideResponse = AdminCntStoriesHideResponses[keyof AdminCntStoriesHideResponses];

export type AdminCntStoriesUnhideData = {
    body?: never;
    path: {
        /**
         * Story Id
         */
        story_id: string;
    };
    query?: never;
    url: '/api/v1/admin/stories/{story_id}/unhide';
};

export type AdminCntStoriesUnhideErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntStoriesUnhideError = AdminCntStoriesUnhideErrors[keyof AdminCntStoriesUnhideErrors];

export type AdminCntStoriesUnhideResponses = {
    /**
     * Successful Response
     */
    200: StoryAdminOut;
};

export type AdminCntStoriesUnhideResponse = AdminCntStoriesUnhideResponses[keyof AdminCntStoriesUnhideResponses];

export type AdminCntDocumentsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: effective_from, lang, name, status, version
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_review, published, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_review, published, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Doc Key]
         *
         * Фильтр по doc_key (user_agreement, privacy_policy, pd_consent, author_terms, other)
         */
        'filter[doc_key]'?: string | null;
        /**
         * Filter[Doc Key][]
         *
         * Фильтр по doc_key (user_agreement, privacy_policy, pd_consent, author_terms, other); несколько значений
         */
        'filter[doc_key][]'?: Array<string> | null;
        /**
         * Filter[Lang]
         *
         * Фильтр по lang (ru, en, es)
         */
        'filter[lang]'?: string | null;
        /**
         * Filter[Lang][]
         *
         * Фильтр по lang (ru, en, es); несколько значений
         */
        'filter[lang][]'?: Array<string> | null;
        /**
         * Filter[Owner Kind]
         *
         * Фильтр по owner_kind (platform, member, winery)
         */
        'filter[owner_kind]'?: string | null;
        /**
         * Filter[Owner Kind][]
         *
         * Фильтр по owner_kind (platform, member, winery); несколько значений
         */
        'filter[owner_kind][]'?: Array<string> | null;
        /**
         * Filter[Visibility]
         *
         * Фильтр по visibility (public, moderators)
         */
        'filter[visibility]'?: string | null;
        /**
         * Filter[Visibility][]
         *
         * Фильтр по visibility (public, moderators); несколько значений
         */
        'filter[visibility][]'?: Array<string> | null;
        /**
         * Filter[Owner Member Id]
         *
         * Фильтр по owner_member_id
         */
        'filter[owner_member_id]'?: string | null;
        /**
         * Filter[Owner Member Id][]
         *
         * Фильтр по owner_member_id; несколько значений
         */
        'filter[owner_member_id][]'?: Array<string> | null;
        /**
         * Filter[Owner Winery Id]
         *
         * Фильтр по owner_winery_id
         */
        'filter[owner_winery_id]'?: string | null;
        /**
         * Filter[Owner Winery Id][]
         *
         * Фильтр по owner_winery_id; несколько значений
         */
        'filter[owner_winery_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/documents';
};

export type AdminCntDocumentsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsListError = AdminCntDocumentsListErrors[keyof AdminCntDocumentsListErrors];

export type AdminCntDocumentsListResponses = {
    /**
     * Successful Response
     */
    200: CntPageDocumentRowOut;
};

export type AdminCntDocumentsListResponse = AdminCntDocumentsListResponses[keyof AdminCntDocumentsListResponses];

export type AdminCntDocumentsCreateData = {
    body: DocumentCreateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/documents';
};

export type AdminCntDocumentsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsCreateError = AdminCntDocumentsCreateErrors[keyof AdminCntDocumentsCreateErrors];

export type AdminCntDocumentsCreateResponses = {
    /**
     * Successful Response
     */
    201: DocumentAdminOut;
};

export type AdminCntDocumentsCreateResponse = AdminCntDocumentsCreateResponses[keyof AdminCntDocumentsCreateResponses];

export type AdminCntDocumentsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: effective_from, lang, name, status, version
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (draft, on_review, published, rejected, archived)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (draft, on_review, published, rejected, archived); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Doc Key]
         *
         * Фильтр по doc_key (user_agreement, privacy_policy, pd_consent, author_terms, other)
         */
        'filter[doc_key]'?: string | null;
        /**
         * Filter[Doc Key][]
         *
         * Фильтр по doc_key (user_agreement, privacy_policy, pd_consent, author_terms, other); несколько значений
         */
        'filter[doc_key][]'?: Array<string> | null;
        /**
         * Filter[Lang]
         *
         * Фильтр по lang (ru, en, es)
         */
        'filter[lang]'?: string | null;
        /**
         * Filter[Lang][]
         *
         * Фильтр по lang (ru, en, es); несколько значений
         */
        'filter[lang][]'?: Array<string> | null;
        /**
         * Filter[Owner Kind]
         *
         * Фильтр по owner_kind (platform, member, winery)
         */
        'filter[owner_kind]'?: string | null;
        /**
         * Filter[Owner Kind][]
         *
         * Фильтр по owner_kind (platform, member, winery); несколько значений
         */
        'filter[owner_kind][]'?: Array<string> | null;
        /**
         * Filter[Visibility]
         *
         * Фильтр по visibility (public, moderators)
         */
        'filter[visibility]'?: string | null;
        /**
         * Filter[Visibility][]
         *
         * Фильтр по visibility (public, moderators); несколько значений
         */
        'filter[visibility][]'?: Array<string> | null;
        /**
         * Filter[Owner Member Id]
         *
         * Фильтр по owner_member_id
         */
        'filter[owner_member_id]'?: string | null;
        /**
         * Filter[Owner Member Id][]
         *
         * Фильтр по owner_member_id; несколько значений
         */
        'filter[owner_member_id][]'?: Array<string> | null;
        /**
         * Filter[Owner Winery Id]
         *
         * Фильтр по owner_winery_id
         */
        'filter[owner_winery_id]'?: string | null;
        /**
         * Filter[Owner Winery Id][]
         *
         * Фильтр по owner_winery_id; несколько значений
         */
        'filter[owner_winery_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/documents/export';
};

export type AdminCntDocumentsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsExportError = AdminCntDocumentsExportErrors[keyof AdminCntDocumentsExportErrors];

export type AdminCntDocumentsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminCntDocumentsDeleteData = {
    body?: never;
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/admin/documents/{document_id}';
};

export type AdminCntDocumentsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsDeleteError = AdminCntDocumentsDeleteErrors[keyof AdminCntDocumentsDeleteErrors];

export type AdminCntDocumentsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminCntDocumentsDeleteResponse = AdminCntDocumentsDeleteResponses[keyof AdminCntDocumentsDeleteResponses];

export type AdminCntDocumentsGetData = {
    body?: never;
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/admin/documents/{document_id}';
};

export type AdminCntDocumentsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsGetError = AdminCntDocumentsGetErrors[keyof AdminCntDocumentsGetErrors];

export type AdminCntDocumentsGetResponses = {
    /**
     * Successful Response
     */
    200: DocumentAdminOut;
};

export type AdminCntDocumentsGetResponse = AdminCntDocumentsGetResponses[keyof AdminCntDocumentsGetResponses];

export type AdminCntDocumentsUpdateData = {
    body: DocumentPatchIn;
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/admin/documents/{document_id}';
};

export type AdminCntDocumentsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsUpdateError = AdminCntDocumentsUpdateErrors[keyof AdminCntDocumentsUpdateErrors];

export type AdminCntDocumentsUpdateResponses = {
    /**
     * Successful Response
     */
    200: DocumentAdminOut;
};

export type AdminCntDocumentsUpdateResponse = AdminCntDocumentsUpdateResponses[keyof AdminCntDocumentsUpdateResponses];

export type AdminCntDocumentsReplaceData = {
    body: DocumentVersionIn;
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/admin/documents/{document_id}/versions';
};

export type AdminCntDocumentsReplaceErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsReplaceError = AdminCntDocumentsReplaceErrors[keyof AdminCntDocumentsReplaceErrors];

export type AdminCntDocumentsReplaceResponses = {
    /**
     * Successful Response
     */
    200: DocumentAdminOut;
};

export type AdminCntDocumentsReplaceResponse = AdminCntDocumentsReplaceResponses[keyof AdminCntDocumentsReplaceResponses];

export type AdminCntDocumentsDecideData = {
    body: DocumentDecisionIn;
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/admin/documents/{document_id}/decision';
};

export type AdminCntDocumentsDecideErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsDecideError = AdminCntDocumentsDecideErrors[keyof AdminCntDocumentsDecideErrors];

export type AdminCntDocumentsDecideResponses = {
    /**
     * Successful Response
     */
    200: DocumentAdminOut;
};

export type AdminCntDocumentsDecideResponse = AdminCntDocumentsDecideResponses[keyof AdminCntDocumentsDecideResponses];

export type AdminCntDocumentsArchiveData = {
    body: ContentArchiveIn;
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/admin/documents/{document_id}/archive';
};

export type AdminCntDocumentsArchiveErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsArchiveError = AdminCntDocumentsArchiveErrors[keyof AdminCntDocumentsArchiveErrors];

export type AdminCntDocumentsArchiveResponses = {
    /**
     * Successful Response
     */
    200: DocumentAdminOut;
};

export type AdminCntDocumentsArchiveResponse = AdminCntDocumentsArchiveResponses[keyof AdminCntDocumentsArchiveResponses];

export type AdminCntDocumentsRestoreData = {
    body?: never;
    path: {
        /**
         * Document Id
         */
        document_id: string;
    };
    query?: never;
    url: '/api/v1/admin/documents/{document_id}/restore';
};

export type AdminCntDocumentsRestoreErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntDocumentsRestoreError = AdminCntDocumentsRestoreErrors[keyof AdminCntDocumentsRestoreErrors];

export type AdminCntDocumentsRestoreResponses = {
    /**
     * Successful Response
     */
    200: DocumentAdminOut;
};

export type AdminCntDocumentsRestoreResponse = AdminCntDocumentsRestoreResponses[keyof AdminCntDocumentsRestoreResponses];

export type AdminCntExpertApplicationsGetData = {
    body?: never;
    path: {
        /**
         * Application Id
         */
        application_id: string;
    };
    query?: never;
    url: '/api/v1/admin/expert-applications/{application_id}';
};

export type AdminCntExpertApplicationsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntExpertApplicationsGetError = AdminCntExpertApplicationsGetErrors[keyof AdminCntExpertApplicationsGetErrors];

export type AdminCntExpertApplicationsGetResponses = {
    /**
     * Successful Response
     */
    200: ApplicationAdminOut;
};

export type AdminCntExpertApplicationsGetResponse = AdminCntExpertApplicationsGetResponses[keyof AdminCntExpertApplicationsGetResponses];

export type AdminCntExpertApplicationsDecideData = {
    body: ExpertApplicationDecisionIn;
    path: {
        /**
         * Application Id
         */
        application_id: string;
    };
    query?: never;
    url: '/api/v1/admin/expert-applications/{application_id}/decision';
};

export type AdminCntExpertApplicationsDecideErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntExpertApplicationsDecideError = AdminCntExpertApplicationsDecideErrors[keyof AdminCntExpertApplicationsDecideErrors];

export type AdminCntExpertApplicationsDecideResponses = {
    /**
     * Successful Response
     */
    200: ApplicationDecisionOut;
};

export type AdminCntExpertApplicationsDecideResponse = AdminCntExpertApplicationsDecideResponses[keyof AdminCntExpertApplicationsDecideResponses];

export type AdminCntLogsSubscriptionsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: at, author, status, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Role]
         *
         * Фильтр по role (expert, winemaker)
         */
        'filter[role]'?: string | null;
        /**
         * Filter[Role][]
         *
         * Фильтр по role (expert, winemaker); несколько значений
         */
        'filter[role][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, unsubscribed)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, unsubscribed); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Фильтр по author_member_id
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Фильтр по author_member_id; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/logs/subscriptions';
};

export type AdminCntLogsSubscriptionsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntLogsSubscriptionsError = AdminCntLogsSubscriptionsErrors[keyof AdminCntLogsSubscriptionsErrors];

export type AdminCntLogsSubscriptionsResponses = {
    /**
     * Successful Response
     */
    200: CntPageSubscriptionLogRowOut;
};

export type AdminCntLogsSubscriptionsResponse = AdminCntLogsSubscriptionsResponses[keyof AdminCntLogsSubscriptionsResponses];

export type AdminCntLogsSubscriptionsExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: at, author, status, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Role]
         *
         * Фильтр по role (expert, winemaker)
         */
        'filter[role]'?: string | null;
        /**
         * Filter[Role][]
         *
         * Фильтр по role (expert, winemaker); несколько значений
         */
        'filter[role][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, unsubscribed)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, unsubscribed); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Author Member Id]
         *
         * Фильтр по author_member_id
         */
        'filter[author_member_id]'?: string | null;
        /**
         * Filter[Author Member Id][]
         *
         * Фильтр по author_member_id; несколько значений
         */
        'filter[author_member_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/logs/subscriptions/export';
};

export type AdminCntLogsSubscriptionsExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntLogsSubscriptionsExportError = AdminCntLogsSubscriptionsExportErrors[keyof AdminCntLogsSubscriptionsExportErrors];

export type AdminCntLogsSubscriptionsExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminCntLogsLikesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: at, object, object_type, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Object Type]
         *
         * Фильтр по object_type (wine, set, course, lesson, publication)
         */
        'filter[object_type]'?: string | null;
        /**
         * Filter[Object Type][]
         *
         * Фильтр по object_type (wine, set, course, lesson, publication); несколько значений
         */
        'filter[object_type][]'?: Array<string> | null;
        /**
         * Filter[Object Id]
         *
         * Фильтр по object_id
         */
        'filter[object_id]'?: string | null;
        /**
         * Filter[Object Id][]
         *
         * Фильтр по object_id; несколько значений
         */
        'filter[object_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/logs/likes';
};

export type AdminCntLogsLikesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntLogsLikesError = AdminCntLogsLikesErrors[keyof AdminCntLogsLikesErrors];

export type AdminCntLogsLikesResponses = {
    /**
     * Successful Response
     */
    200: CntPageLikeLogRowOut;
};

export type AdminCntLogsLikesResponse = AdminCntLogsLikesResponses[keyof AdminCntLogsLikesResponses];

export type AdminCntLogsLikesExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: at, object, object_type, user
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Object Type]
         *
         * Фильтр по object_type (wine, set, course, lesson, publication)
         */
        'filter[object_type]'?: string | null;
        /**
         * Filter[Object Type][]
         *
         * Фильтр по object_type (wine, set, course, lesson, publication); несколько значений
         */
        'filter[object_type][]'?: Array<string> | null;
        /**
         * Filter[Object Id]
         *
         * Фильтр по object_id
         */
        'filter[object_id]'?: string | null;
        /**
         * Filter[Object Id][]
         *
         * Фильтр по object_id; несколько значений
         */
        'filter[object_id][]'?: Array<string> | null;
        /**
         * Filter[User Id]
         *
         * Фильтр по user_id
         */
        'filter[user_id]'?: string | null;
        /**
         * Filter[User Id][]
         *
         * Фильтр по user_id; несколько значений
         */
        'filter[user_id][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/logs/likes/export';
};

export type AdminCntLogsLikesExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntLogsLikesExportError = AdminCntLogsLikesExportErrors[keyof AdminCntLogsLikesExportErrors];

export type AdminCntLogsLikesExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminCntBrandGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/brand';
};

export type AdminCntBrandGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntBrandGetError = AdminCntBrandGetErrors[keyof AdminCntBrandGetErrors];

export type AdminCntBrandGetResponses = {
    /**
     * Successful Response
     */
    200: BrandLogoOut;
};

export type AdminCntBrandGetResponse = AdminCntBrandGetResponses[keyof AdminCntBrandGetResponses];

export type AdminCntBrandLogoReplaceData = {
    body: BrandLogoIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/brand/logo';
};

export type AdminCntBrandLogoReplaceErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminCntBrandLogoReplaceError = AdminCntBrandLogoReplaceErrors[keyof AdminCntBrandLogoReplaceErrors];

export type AdminCntBrandLogoReplaceResponses = {
    /**
     * Successful Response
     */
    200: BrandLogoOut;
};

export type AdminCntBrandLogoReplaceResponse = AdminCntBrandLogoReplaceResponses[keyof AdminCntBrandLogoReplaceResponses];

export type AdminOrdOrdersListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: buyer, number, payment, placed_at, seller, status, total, type
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Group]
         *
         * Фильтр по group (learning, wine, experience, bonus)
         */
        'filter[group]'?: string | null;
        /**
         * Filter[Group][]
         *
         * Фильтр по group (learning, wine, experience, bonus); несколько значений
         */
        'filter[group][]'?: Array<string> | null;
        /**
         * Filter[Payment]
         *
         * Оплата: awaiting — ожидает оплаты, refund — возврат (awaiting, refund)
         */
        'filter[payment]'?: string | null;
        /**
         * Filter[Payment][]
         *
         * Оплата: awaiting — ожидает оплаты, refund — возврат (awaiting, refund); несколько значений
         */
        'filter[payment][]'?: Array<string> | null;
        /**
         * Filter[Type]
         *
         * Фильтр по type (wine_bottle, wine_set, wine_author_set, wine_drop, wine_blind_kit, learning_course, learning_lesson, exp_online_tasting, exp_winery_tasting, exp_blind_offline, exp_excursion, exp_trip, bonus_exchange)
         */
        'filter[type]'?: string | null;
        /**
         * Filter[Type][]
         *
         * Фильтр по type (wine_bottle, wine_set, wine_author_set, wine_drop, wine_blind_kit, learning_course, learning_lesson, exp_online_tasting, exp_winery_tasting, exp_blind_offline, exp_excursion, exp_trip, bonus_exchange); несколько значений
         */
        'filter[type][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (created, paid, packing, packed, shipped, delivered, ready_for_pickup, picked_up, access_granted, seat_confirmed, entry_confirmed, attended, no_show, agreed, seats_confirmed, trip_completed, requested, bonus_agreed, fulfilled, passed_to_partner, paid_at_partner, partner_shipping, cancelled, refunded)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (created, paid, packing, packed, shipped, delivered, ready_for_pickup, picked_up, access_granted, seat_confirmed, entry_confirmed, attended, no_show, agreed, seats_confirmed, trip_completed, requested, bonus_agreed, fulfilled, passed_to_partner, paid_at_partner, partner_shipping, cancelled, refunded); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Flow]
         *
         * Фильтр по flow (ship, pickup, kit, learn, live, visit, trip, bonus, partner)
         */
        'filter[flow]'?: string | null;
        /**
         * Filter[Flow][]
         *
         * Фильтр по flow (ship, pickup, kit, learn, live, visit, trip, bonus, partner); несколько значений
         */
        'filter[flow][]'?: Array<string> | null;
        /**
         * Filter[Buyer Id]
         *
         * Фильтр по buyer_id
         */
        'filter[buyer_id]'?: string | null;
        /**
         * Filter[Buyer Id][]
         *
         * Фильтр по buyer_id; несколько значений
         */
        'filter[buyer_id][]'?: Array<string> | null;
        /**
         * Filter[Event Id]
         *
         * Событие EXP: дегустация, экскурсия, поездка
         */
        'filter[event_id]'?: string | null;
        /**
         * Filter[Event Id][]
         *
         * Событие EXP: дегустация, экскурсия, поездка; несколько значений
         */
        'filter[event_id][]'?: Array<string> | null;
        /**
         * Filter[Partner]
         *
         * Партнёрские заказы (RU; без партнёра на рынке — пусто) (await_delivered, attention, unmatched, test)
         */
        'filter[partner]'?: string | null;
        /**
         * Filter[Partner][]
         *
         * Партнёрские заказы (RU; без партнёра на рынке — пусто) (await_delivered, attention, unmatched, test); несколько значений
         */
        'filter[partner][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/orders';
};

export type AdminOrdOrdersListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdOrdersListError = AdminOrdOrdersListErrors[keyof AdminOrdOrdersListErrors];

export type AdminOrdOrdersListResponses = {
    /**
     * Successful Response
     */
    200: OrdPageOrdAdminOrderRow;
};

export type AdminOrdOrdersListResponse = AdminOrdOrdersListResponses[keyof AdminOrdOrdersListResponses];

export type AdminOrdOrdersExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: buyer, number, payment, placed_at, seller, status, total, type
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Group]
         *
         * Фильтр по group (learning, wine, experience, bonus)
         */
        'filter[group]'?: string | null;
        /**
         * Filter[Group][]
         *
         * Фильтр по group (learning, wine, experience, bonus); несколько значений
         */
        'filter[group][]'?: Array<string> | null;
        /**
         * Filter[Payment]
         *
         * Оплата: awaiting — ожидает оплаты, refund — возврат (awaiting, refund)
         */
        'filter[payment]'?: string | null;
        /**
         * Filter[Payment][]
         *
         * Оплата: awaiting — ожидает оплаты, refund — возврат (awaiting, refund); несколько значений
         */
        'filter[payment][]'?: Array<string> | null;
        /**
         * Filter[Type]
         *
         * Фильтр по type (wine_bottle, wine_set, wine_author_set, wine_drop, wine_blind_kit, learning_course, learning_lesson, exp_online_tasting, exp_winery_tasting, exp_blind_offline, exp_excursion, exp_trip, bonus_exchange)
         */
        'filter[type]'?: string | null;
        /**
         * Filter[Type][]
         *
         * Фильтр по type (wine_bottle, wine_set, wine_author_set, wine_drop, wine_blind_kit, learning_course, learning_lesson, exp_online_tasting, exp_winery_tasting, exp_blind_offline, exp_excursion, exp_trip, bonus_exchange); несколько значений
         */
        'filter[type][]'?: Array<string> | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (created, paid, packing, packed, shipped, delivered, ready_for_pickup, picked_up, access_granted, seat_confirmed, entry_confirmed, attended, no_show, agreed, seats_confirmed, trip_completed, requested, bonus_agreed, fulfilled, passed_to_partner, paid_at_partner, partner_shipping, cancelled, refunded)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (created, paid, packing, packed, shipped, delivered, ready_for_pickup, picked_up, access_granted, seat_confirmed, entry_confirmed, attended, no_show, agreed, seats_confirmed, trip_completed, requested, bonus_agreed, fulfilled, passed_to_partner, paid_at_partner, partner_shipping, cancelled, refunded); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Flow]
         *
         * Фильтр по flow (ship, pickup, kit, learn, live, visit, trip, bonus, partner)
         */
        'filter[flow]'?: string | null;
        /**
         * Filter[Flow][]
         *
         * Фильтр по flow (ship, pickup, kit, learn, live, visit, trip, bonus, partner); несколько значений
         */
        'filter[flow][]'?: Array<string> | null;
        /**
         * Filter[Buyer Id]
         *
         * Фильтр по buyer_id
         */
        'filter[buyer_id]'?: string | null;
        /**
         * Filter[Buyer Id][]
         *
         * Фильтр по buyer_id; несколько значений
         */
        'filter[buyer_id][]'?: Array<string> | null;
        /**
         * Filter[Event Id]
         *
         * Событие EXP: дегустация, экскурсия, поездка
         */
        'filter[event_id]'?: string | null;
        /**
         * Filter[Event Id][]
         *
         * Событие EXP: дегустация, экскурсия, поездка; несколько значений
         */
        'filter[event_id][]'?: Array<string> | null;
        /**
         * Filter[Partner]
         *
         * Партнёрские заказы (RU; без партнёра на рынке — пусто) (await_delivered, attention, unmatched, test)
         */
        'filter[partner]'?: string | null;
        /**
         * Filter[Partner][]
         *
         * Партнёрские заказы (RU; без партнёра на рынке — пусто) (await_delivered, attention, unmatched, test); несколько значений
         */
        'filter[partner][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/orders/export';
};

export type AdminOrdOrdersExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdOrdersExportError = AdminOrdOrdersExportErrors[keyof AdminOrdOrdersExportErrors];

export type AdminOrdOrdersExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminOrdOrdersPartnerCreateData = {
    body: OrdPartnerOrderIn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * uuid; повтор с тем же ключом в течение 24 ч возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/admin/orders/partner';
};

export type AdminOrdOrdersPartnerCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdOrdersPartnerCreateError = AdminOrdOrdersPartnerCreateErrors[keyof AdminOrdOrdersPartnerCreateErrors];

export type AdminOrdOrdersPartnerCreateResponses = {
    /**
     * Successful Response
     */
    201: OrdCardResult;
};

export type AdminOrdOrdersPartnerCreateResponse = AdminOrdOrdersPartnerCreateResponses[keyof AdminOrdOrdersPartnerCreateResponses];

export type AdminOrdOrdersGetData = {
    body?: never;
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/admin/orders/{order_id}';
};

export type AdminOrdOrdersGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdOrdersGetError = AdminOrdOrdersGetErrors[keyof AdminOrdOrdersGetErrors];

export type AdminOrdOrdersGetResponses = {
    /**
     * Successful Response
     */
    200: OrdAdminOrderCard;
};

export type AdminOrdOrdersGetResponse = AdminOrdOrdersGetResponses[keyof AdminOrdOrdersGetResponses];

export type AdminOrdOrdersStatusData = {
    body: OrdStatusIn;
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/admin/orders/{order_id}/status';
};

export type AdminOrdOrdersStatusErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminOrdOrdersStatusError = AdminOrdOrdersStatusErrors[keyof AdminOrdOrdersStatusErrors];

export type AdminOrdOrdersStatusResponses = {
    /**
     * Successful Response
     */
    200: OrdStatusResult;
};

export type AdminOrdOrdersStatusResponse = AdminOrdOrdersStatusResponses[keyof AdminOrdOrdersStatusResponses];

export type AdminOrdOrdersCommentData = {
    body: OrdCommentIn;
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/admin/orders/{order_id}/comments';
};

export type AdminOrdOrdersCommentErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdOrdersCommentError = AdminOrdOrdersCommentErrors[keyof AdminOrdOrdersCommentErrors];

export type AdminOrdOrdersCommentResponses = {
    /**
     * Successful Response
     */
    200: OrdCardResult;
};

export type AdminOrdOrdersCommentResponse = AdminOrdOrdersCommentResponses[keyof AdminOrdOrdersCommentResponses];

export type AdminOrdOrdersPaymentReminderData = {
    body?: never;
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/admin/orders/{order_id}/payment-reminder';
};

export type AdminOrdOrdersPaymentReminderErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
};

export type AdminOrdOrdersPaymentReminderError = AdminOrdOrdersPaymentReminderErrors[keyof AdminOrdOrdersPaymentReminderErrors];

export type AdminOrdOrdersPaymentReminderResponses = {
    /**
     * Successful Response
     */
    202: OrdAcceptedOut;
};

export type AdminOrdOrdersPaymentReminderResponse = AdminOrdOrdersPaymentReminderResponses[keyof AdminOrdOrdersPaymentReminderResponses];

export type AdminOrdOrdersPaymentsData = {
    body?: never;
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/admin/orders/{order_id}/payments';
};

export type AdminOrdOrdersPaymentsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdOrdersPaymentsError = AdminOrdOrdersPaymentsErrors[keyof AdminOrdOrdersPaymentsErrors];

export type AdminOrdOrdersPaymentsResponses = {
    /**
     * Successful Response
     */
    200: OrdPaymentsBlockOut;
};

export type AdminOrdOrdersPaymentsResponse = AdminOrdOrdersPaymentsResponses[keyof AdminOrdOrdersPaymentsResponses];

export type AdminOrdOrdersPartnerLinkUserData = {
    body: OrdLinkUserIn;
    path: {
        /**
         * Order Id
         */
        order_id: string;
    };
    query?: never;
    url: '/api/v1/admin/orders/{order_id}/partner/link-user';
};

export type AdminOrdOrdersPartnerLinkUserErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdOrdersPartnerLinkUserError = AdminOrdOrdersPartnerLinkUserErrors[keyof AdminOrdOrdersPartnerLinkUserErrors];

export type AdminOrdOrdersPartnerLinkUserResponses = {
    /**
     * Successful Response
     */
    200: OrdCardResult;
};

export type AdminOrdOrdersPartnerLinkUserResponse = AdminOrdOrdersPartnerLinkUserResponses[keyof AdminOrdOrdersPartnerLinkUserResponses];

export type AdminOrdWinePartnersListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, name, status
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (active, draft)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (active, draft); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/wine-partners';
};

export type AdminOrdWinePartnersListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdWinePartnersListError = AdminOrdWinePartnersListErrors[keyof AdminOrdWinePartnersListErrors];

export type AdminOrdWinePartnersListResponses = {
    /**
     * Successful Response
     */
    200: OrdPageOrdWinePartnerOut;
};

export type AdminOrdWinePartnersListResponse = AdminOrdWinePartnersListResponses[keyof AdminOrdWinePartnersListResponses];

export type AdminOrdWinePartnersCreateData = {
    body: OrdWinePartnerIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/wine-partners';
};

export type AdminOrdWinePartnersCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdWinePartnersCreateError = AdminOrdWinePartnersCreateErrors[keyof AdminOrdWinePartnersCreateErrors];

export type AdminOrdWinePartnersCreateResponses = {
    /**
     * Successful Response
     */
    201: OrdWinePartnerOut;
};

export type AdminOrdWinePartnersCreateResponse = AdminOrdWinePartnersCreateResponses[keyof AdminOrdWinePartnersCreateResponses];

export type AdminOrdWinePartnersDeleteData = {
    body?: never;
    path: {
        /**
         * Partner Id
         */
        partner_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wine-partners/{partner_id}';
};

export type AdminOrdWinePartnersDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdWinePartnersDeleteError = AdminOrdWinePartnersDeleteErrors[keyof AdminOrdWinePartnersDeleteErrors];

export type AdminOrdWinePartnersDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminOrdWinePartnersDeleteResponse = AdminOrdWinePartnersDeleteResponses[keyof AdminOrdWinePartnersDeleteResponses];

export type AdminOrdWinePartnersUpdateData = {
    body: OrdWinePartnerPatchIn;
    path: {
        /**
         * Partner Id
         */
        partner_id: string;
    };
    query?: never;
    url: '/api/v1/admin/wine-partners/{partner_id}';
};

export type AdminOrdWinePartnersUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdWinePartnersUpdateError = AdminOrdWinePartnersUpdateErrors[keyof AdminOrdWinePartnersUpdateErrors];

export type AdminOrdWinePartnersUpdateResponses = {
    /**
     * Successful Response
     */
    200: OrdWinePartnerOut;
};

export type AdminOrdWinePartnersUpdateResponse = AdminOrdWinePartnersUpdateResponses[keyof AdminOrdWinePartnersUpdateResponses];

export type AdminOrdDeliveriesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: carrier, created_at, order, planned, recipient, status
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (preparing, in_transit, ready_for_pickup, delivered, overdue, cancelled)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (preparing, in_transit, ready_for_pickup, delivered, overdue, cancelled); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Carrier]
         *
         * Фильтр по carrier (dhl_express, pickup)
         */
        'filter[carrier]'?: string | null;
        /**
         * Filter[Carrier][]
         *
         * Фильтр по carrier (dhl_express, pickup); несколько значений
         */
        'filter[carrier][]'?: Array<string> | null;
        /**
         * Filter[Kit]
         *
         * Наборы вслепую
         */
        'filter[kit]'?: string | null;
        /**
         * Filter[Kit][]
         *
         * Наборы вслепую; несколько значений
         */
        'filter[kit][]'?: Array<string> | null;
        /**
         * Filter[Shipment]
         *
         * Отправления DHL (INT-DLV 16.10.4): ошибка создания / ждут курьера / проблема доставки / возврат отправителю (label_error, awaiting_pickup, exception, returned)
         */
        'filter[shipment]'?: string | null;
        /**
         * Filter[Shipment][]
         *
         * Отправления DHL (INT-DLV 16.10.4): ошибка создания / ждут курьера / проблема доставки / возврат отправителю (label_error, awaiting_pickup, exception, returned); несколько значений
         */
        'filter[shipment][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/deliveries';
};

export type AdminOrdDeliveriesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdDeliveriesListError = AdminOrdDeliveriesListErrors[keyof AdminOrdDeliveriesListErrors];

export type AdminOrdDeliveriesListResponses = {
    /**
     * Successful Response
     */
    200: OrdPageOrdDeliveryRow;
};

export type AdminOrdDeliveriesListResponse = AdminOrdDeliveriesListResponses[keyof AdminOrdDeliveriesListResponses];

export type AdminOrdDeliveriesGetData = {
    body?: never;
    path: {
        /**
         * Delivery Id
         */
        delivery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/deliveries/{delivery_id}';
};

export type AdminOrdDeliveriesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdDeliveriesGetError = AdminOrdDeliveriesGetErrors[keyof AdminOrdDeliveriesGetErrors];

export type AdminOrdDeliveriesGetResponses = {
    /**
     * Successful Response
     */
    200: OrdDeliveryCard;
};

export type AdminOrdDeliveriesGetResponse = AdminOrdDeliveriesGetResponses[keyof AdminOrdDeliveriesGetResponses];

export type AdminOrdDeliveriesUpdateData = {
    body: OrdDeliveryPatchIn;
    path: {
        /**
         * Delivery Id
         */
        delivery_id: string;
    };
    query?: never;
    url: '/api/v1/admin/deliveries/{delivery_id}';
};

export type AdminOrdDeliveriesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminOrdDeliveriesUpdateError = AdminOrdDeliveriesUpdateErrors[keyof AdminOrdDeliveriesUpdateErrors];

export type AdminOrdDeliveriesUpdateResponses = {
    /**
     * Successful Response
     */
    200: OrdDeliveryCard;
};

export type AdminOrdDeliveriesUpdateResponse = AdminOrdDeliveriesUpdateResponses[keyof AdminOrdDeliveriesUpdateResponses];

export type AdminOrdRevenueEntriesData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Period
         *
         * all | month (`value=YYYY-MM`) | quarter (`YYYY-Q1`) | half (`YYYY-H1`) | year (`YYYY`) | custom (`from`, `to`)
         */
        period?: string;
        /**
         * Value
         */
        value?: string | null;
        /**
         * From
         */
        from?: string | null;
        /**
         * To
         */
        to?: string | null;
        /**
         * Group
         */
        group?: CartGroup | null;
        /**
         * Recipient Id
         *
         * id участника или `platform`
         */
        recipient_id?: string | null;
        /**
         * Q
         */
        q?: string | null;
        /**
         * Payout Status
         */
        payout_status?: PayoutStatus | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/revenue/entries';
};

export type AdminOrdRevenueEntriesErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdRevenueEntriesError = AdminOrdRevenueEntriesErrors[keyof AdminOrdRevenueEntriesErrors];

export type AdminOrdRevenueEntriesResponses = {
    /**
     * Successful Response
     */
    200: OrdPageOrdRevenueRow;
};

export type AdminOrdRevenueEntriesResponse = AdminOrdRevenueEntriesResponses[keyof AdminOrdRevenueEntriesResponses];

export type AdminOrdRevenueSummaryData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Period
         *
         * all | month (`value=YYYY-MM`) | quarter (`YYYY-Q1`) | half (`YYYY-H1`) | year (`YYYY`) | custom (`from`, `to`)
         */
        period?: string;
        /**
         * Value
         */
        value?: string | null;
        /**
         * From
         */
        from?: string | null;
        /**
         * To
         */
        to?: string | null;
        /**
         * Group
         */
        group?: CartGroup | null;
        /**
         * Recipient Id
         *
         * id участника или `platform`
         */
        recipient_id?: string | null;
        /**
         * Q
         */
        q?: string | null;
        /**
         * Payout Status
         */
        payout_status?: PayoutStatus | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/revenue/summary';
};

export type AdminOrdRevenueSummaryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdRevenueSummaryError = AdminOrdRevenueSummaryErrors[keyof AdminOrdRevenueSummaryErrors];

export type AdminOrdRevenueSummaryResponses = {
    /**
     * Successful Response
     */
    200: OrdRevenueSummaryOut;
};

export type AdminOrdRevenueSummaryResponse = AdminOrdRevenueSummaryResponses[keyof AdminOrdRevenueSummaryResponses];

export type AdminOrdRevenueExportData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Period
         *
         * all | month (`value=YYYY-MM`) | quarter (`YYYY-Q1`) | half (`YYYY-H1`) | year (`YYYY`) | custom (`from`, `to`)
         */
        period?: string;
        /**
         * Value
         */
        value?: string | null;
        /**
         * From
         */
        from?: string | null;
        /**
         * To
         */
        to?: string | null;
        /**
         * Group
         */
        group?: CartGroup | null;
        /**
         * Recipient Id
         *
         * id участника или `platform`
         */
        recipient_id?: string | null;
        /**
         * Q
         */
        q?: string | null;
        /**
         * Payout Status
         */
        payout_status?: PayoutStatus | null;
        /**
         * Page
         */
        page?: number;
        /**
         * Page Size
         */
        page_size?: number;
    };
    url: '/api/v1/admin/revenue/export';
};

export type AdminOrdRevenueExportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdRevenueExportError = AdminOrdRevenueExportErrors[keyof AdminOrdRevenueExportErrors];

export type AdminOrdRevenueExportResponses = {
    /**
     * CSV (UTF-8 с BOM, `;`)
     */
    200: unknown;
};

export type AdminOrdRevenuePayoutsCreateData = {
    body: OrdPayoutIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/revenue/payouts';
};

export type AdminOrdRevenuePayoutsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdRevenuePayoutsCreateError = AdminOrdRevenuePayoutsCreateErrors[keyof AdminOrdRevenuePayoutsCreateErrors];

export type AdminOrdRevenuePayoutsCreateResponses = {
    /**
     * Successful Response
     */
    201: OrdPayoutOut;
};

export type AdminOrdRevenuePayoutsCreateResponse = AdminOrdRevenuePayoutsCreateResponses[keyof AdminOrdRevenuePayoutsCreateResponses];

export type AdminOrdRevenueSharesGetData = {
    body?: never;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/revenue/shares/{member_id}';
};

export type AdminOrdRevenueSharesGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdRevenueSharesGetError = AdminOrdRevenueSharesGetErrors[keyof AdminOrdRevenueSharesGetErrors];

export type AdminOrdRevenueSharesGetResponses = {
    /**
     * Successful Response
     */
    200: OrdSharesOut;
};

export type AdminOrdRevenueSharesGetResponse = AdminOrdRevenueSharesGetResponses[keyof AdminOrdRevenueSharesGetResponses];

export type AdminOrdRevenueSharesUpdateData = {
    body: OrdSharesIn;
    path: {
        /**
         * Member Id
         */
        member_id: string;
    };
    query?: never;
    url: '/api/v1/admin/revenue/shares/{member_id}';
};

export type AdminOrdRevenueSharesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminOrdRevenueSharesUpdateError = AdminOrdRevenueSharesUpdateErrors[keyof AdminOrdRevenueSharesUpdateErrors];

export type AdminOrdRevenueSharesUpdateResponses = {
    /**
     * Successful Response
     */
    200: OrdSharesOut;
};

export type AdminOrdRevenueSharesUpdateResponse = AdminOrdRevenueSharesUpdateResponses[keyof AdminOrdRevenueSharesUpdateResponses];

export type AdminIntBillingStoreProductsCheckData = {
    body?: never;
    path?: never;
    query: {
        store: StoreCode;
        /**
         * Product Id
         */
        product_id: string;
        /**
         * Exclude Item Id
         */
        exclude_item_id?: string | null;
    };
    url: '/api/v1/admin/billing/store-products/check';
};

export type AdminIntBillingStoreProductsCheckErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntBillingStoreProductsCheckError = AdminIntBillingStoreProductsCheckErrors[keyof AdminIntBillingStoreProductsCheckErrors];

export type AdminIntBillingStoreProductsCheckResponses = {
    /**
     * Successful Response
     */
    200: BillingProductCheckOut;
};

export type AdminIntBillingStoreProductsCheckResponse = AdminIntBillingStoreProductsCheckResponses[keyof AdminIntBillingStoreProductsCheckResponses];

export type AdminIntGeoGeocodeData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Q
         *
         * Адрес
         */
        q: string;
        /**
         * Country
         *
         * ISO alpha-2 страны
         */
        country?: string | null;
    };
    url: '/api/v1/admin/geo/geocode';
};

export type AdminIntGeoGeocodeErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntGeoGeocodeError = AdminIntGeoGeocodeErrors[keyof AdminIntGeoGeocodeErrors];

export type AdminIntGeoGeocodeResponses = {
    /**
     * Successful Response
     */
    200: GeocodeListOut;
};

export type AdminIntGeoGeocodeResponse = AdminIntGeoGeocodeResponses[keyof AdminIntGeoGeocodeResponses];

export type AdminIntGeoReverseData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Lat
         *
         * Широта WGS 84
         */
        lat: number;
        /**
         * Lon
         *
         * Долгота WGS 84
         */
        lon: number;
        /**
         * Region Id
         *
         * Регион формы — проверка страны точки
         */
        region_id?: string | null;
    };
    url: '/api/v1/admin/geo/reverse';
};

export type AdminIntGeoReverseErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntGeoReverseError = AdminIntGeoReverseErrors[keyof AdminIntGeoReverseErrors];

export type AdminIntGeoReverseResponses = {
    /**
     * Successful Response
     */
    200: GeocodeReverseOut;
};

export type AdminIntGeoReverseResponse = AdminIntGeoReverseResponses[keyof AdminIntGeoReverseResponses];

export type AdminIntGeoSuggestData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Input
         */
        input: string;
        /**
         * Session Token
         *
         * Токен сессии подсказок (uuid): создаётся при открытии поля адреса и завершается запросом деталей — тарификация по сессии
         */
        session_token: string;
        /**
         * Near Lat
         */
        near_lat?: number | null;
        /**
         * Near Lon
         */
        near_lon?: number | null;
    };
    url: '/api/v1/admin/geo/suggest';
};

export type AdminIntGeoSuggestErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntGeoSuggestError = AdminIntGeoSuggestErrors[keyof AdminIntGeoSuggestErrors];

export type AdminIntGeoSuggestResponses = {
    /**
     * Successful Response
     */
    200: GeocodeSuggestionsOut;
};

export type AdminIntGeoSuggestResponse = AdminIntGeoSuggestResponses[keyof AdminIntGeoSuggestResponses];

export type AdminIntGeoSuggestDetailsData = {
    body?: never;
    path: {
        /**
         * Suggestion Id
         */
        suggestion_id: string;
    };
    query: {
        /**
         * Session Token
         *
         * Токен сессии подсказок (uuid): создаётся при открытии поля адреса и завершается запросом деталей — тарификация по сессии
         */
        session_token: string;
    };
    url: '/api/v1/admin/geo/suggest/{suggestion_id}';
};

export type AdminIntGeoSuggestDetailsErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntGeoSuggestDetailsError = AdminIntGeoSuggestDetailsErrors[keyof AdminIntGeoSuggestDetailsErrors];

export type AdminIntGeoSuggestDetailsResponses = {
    /**
     * Successful Response
     */
    200: GeocodeDetailsOut;
};

export type AdminIntGeoSuggestDetailsResponse = AdminIntGeoSuggestDetailsResponses[keyof AdminIntGeoSuggestDetailsResponses];

export type AdminIntTranslationsRetranslateData = {
    body: RetranslateIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/translations/retranslate';
};

export type AdminIntTranslationsRetranslateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntTranslationsRetranslateError = AdminIntTranslationsRetranslateErrors[keyof AdminIntTranslationsRetranslateErrors];

export type AdminIntTranslationsRetranslateResponses = {
    /**
     * Successful Response
     */
    200: RetranslateOut;
};

export type AdminIntTranslationsRetranslateResponse = AdminIntTranslationsRetranslateResponses[keyof AdminIntTranslationsRetranslateResponses];

export type AdminIntTranslationsJobsListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: attempts, chars, created_at, finished_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (queued, in_progress, done, failed, cancelled)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (queued, in_progress, done, failed, cancelled); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[Entity Type]
         *
         * Фильтр по entity_type
         */
        'filter[entity_type]'?: string | null;
        /**
         * Filter[Entity Type][]
         *
         * Фильтр по entity_type; несколько значений
         */
        'filter[entity_type][]'?: Array<string> | null;
        /**
         * Filter[Entity Id]
         *
         * Фильтр по entity_id
         */
        'filter[entity_id]'?: string | null;
        /**
         * Filter[Entity Id][]
         *
         * Фильтр по entity_id; несколько значений
         */
        'filter[entity_id][]'?: Array<string> | null;
        /**
         * Filter[Batch Id]
         *
         * Фильтр по batch_id
         */
        'filter[batch_id]'?: string | null;
        /**
         * Filter[Batch Id][]
         *
         * Фильтр по batch_id; несколько значений
         */
        'filter[batch_id][]'?: Array<string> | null;
        /**
         * Filter[Batched]
         *
         * true — «Пакетные»
         */
        'filter[batched]'?: string | null;
        /**
         * Filter[Batched][]
         *
         * true — «Пакетные»; несколько значений
         */
        'filter[batched][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/translations/jobs';
};

export type AdminIntTranslationsJobsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntTranslationsJobsListError = AdminIntTranslationsJobsListErrors[keyof AdminIntTranslationsJobsListErrors];

export type AdminIntTranslationsJobsListResponses = {
    /**
     * Successful Response
     */
    200: TranslationJobsPage;
};

export type AdminIntTranslationsJobsListResponse = AdminIntTranslationsJobsListResponses[keyof AdminIntTranslationsJobsListResponses];

export type AdminIntTranslationsJobsRetryData = {
    body?: never;
    path: {
        /**
         * Job Id
         */
        job_id: string;
    };
    query?: never;
    url: '/api/v1/admin/translations/jobs/{job_id}/retry';
};

export type AdminIntTranslationsJobsRetryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntTranslationsJobsRetryError = AdminIntTranslationsJobsRetryErrors[keyof AdminIntTranslationsJobsRetryErrors];

export type AdminIntTranslationsJobsRetryResponses = {
    /**
     * Successful Response
     */
    200: TranslationJobOut;
};

export type AdminIntTranslationsJobsRetryResponse = AdminIntTranslationsJobsRetryResponses[keyof AdminIntTranslationsJobsRetryResponses];

export type AdminIntTranslationsBatchesEstimateData = {
    body: TranslationBatchIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/translations/batches/estimate';
};

export type AdminIntTranslationsBatchesEstimateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntTranslationsBatchesEstimateError = AdminIntTranslationsBatchesEstimateErrors[keyof AdminIntTranslationsBatchesEstimateErrors];

export type AdminIntTranslationsBatchesEstimateResponses = {
    /**
     * Successful Response
     */
    200: TranslationEstimateOut;
};

export type AdminIntTranslationsBatchesEstimateResponse = AdminIntTranslationsBatchesEstimateResponses[keyof AdminIntTranslationsBatchesEstimateResponses];

export type AdminIntTranslationsBatchesListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
    };
    url: '/api/v1/admin/translations/batches';
};

export type AdminIntTranslationsBatchesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntTranslationsBatchesListError = AdminIntTranslationsBatchesListErrors[keyof AdminIntTranslationsBatchesListErrors];

export type AdminIntTranslationsBatchesListResponses = {
    /**
     * Successful Response
     */
    200: TranslationBatchesPage;
};

export type AdminIntTranslationsBatchesListResponse = AdminIntTranslationsBatchesListResponses[keyof AdminIntTranslationsBatchesListResponses];

export type AdminIntTranslationsBatchesStartData = {
    body: TranslationBatchIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/translations/batches';
};

export type AdminIntTranslationsBatchesStartErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntTranslationsBatchesStartError = AdminIntTranslationsBatchesStartErrors[keyof AdminIntTranslationsBatchesStartErrors];

export type AdminIntTranslationsBatchesStartResponses = {
    /**
     * Successful Response
     */
    201: TranslationBatchOut;
};

export type AdminIntTranslationsBatchesStartResponse = AdminIntTranslationsBatchesStartResponses[keyof AdminIntTranslationsBatchesStartResponses];

export type AdminIntTranslationsBatchesCancelData = {
    body?: never;
    path: {
        /**
         * Batch Id
         */
        batch_id: string;
    };
    query?: never;
    url: '/api/v1/admin/translations/batches/{batch_id}/cancel';
};

export type AdminIntTranslationsBatchesCancelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntTranslationsBatchesCancelError = AdminIntTranslationsBatchesCancelErrors[keyof AdminIntTranslationsBatchesCancelErrors];

export type AdminIntTranslationsBatchesCancelResponses = {
    /**
     * Successful Response
     */
    200: TranslationBatchOut;
};

export type AdminIntTranslationsBatchesCancelResponse = AdminIntTranslationsBatchesCancelResponses[keyof AdminIntTranslationsBatchesCancelResponses];

export type AdminIntVideosUploadCreateData = {
    body: VideoUploadIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/videos/uploads';
};

export type AdminIntVideosUploadCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntVideosUploadCreateError = AdminIntVideosUploadCreateErrors[keyof AdminIntVideosUploadCreateErrors];

export type AdminIntVideosUploadCreateResponses = {
    /**
     * Successful Response
     */
    201: VideoUploadOut;
};

export type AdminIntVideosUploadCreateResponse = AdminIntVideosUploadCreateResponses[keyof AdminIntVideosUploadCreateResponses];

export type AdminIntVideosImportData = {
    body: VideoImportIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/videos/import';
};

export type AdminIntVideosImportErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntVideosImportError = AdminIntVideosImportErrors[keyof AdminIntVideosImportErrors];

export type AdminIntVideosImportResponses = {
    /**
     * Successful Response
     */
    201: VideoImportOut;
};

export type AdminIntVideosImportResponse = AdminIntVideosImportResponses[keyof AdminIntVideosImportResponses];

export type AdminIntVideosDeleteData = {
    body?: never;
    path: {
        /**
         * Video Id
         */
        video_id: string;
    };
    query?: never;
    url: '/api/v1/admin/videos/{video_id}';
};

export type AdminIntVideosDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Ошибка
     */
    502: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntVideosDeleteError = AdminIntVideosDeleteErrors[keyof AdminIntVideosDeleteErrors];

export type AdminIntVideosDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminIntVideosDeleteResponse = AdminIntVideosDeleteResponses[keyof AdminIntVideosDeleteResponses];

export type AdminIntVideosGetData = {
    body?: never;
    path: {
        /**
         * Video Id
         */
        video_id: string;
    };
    query?: never;
    url: '/api/v1/admin/videos/{video_id}';
};

export type AdminIntVideosGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntVideosGetError = AdminIntVideosGetErrors[keyof AdminIntVideosGetErrors];

export type AdminIntVideosGetResponses = {
    /**
     * Successful Response
     */
    200: VideoAssetOut;
};

export type AdminIntVideosGetResponse = AdminIntVideosGetResponses[keyof AdminIntVideosGetResponses];

export type AdminIntVideosPlaybackData = {
    body?: never;
    path: {
        /**
         * Video Id
         */
        video_id: string;
    };
    query?: never;
    url: '/api/v1/admin/videos/{video_id}/playback';
};

export type AdminIntVideosPlaybackErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntVideosPlaybackError = AdminIntVideosPlaybackErrors[keyof AdminIntVideosPlaybackErrors];

export type AdminIntVideosPlaybackResponses = {
    /**
     * Successful Response
     */
    200: VideoPreviewOut;
};

export type AdminIntVideosPlaybackResponse = AdminIntVideosPlaybackResponses[keyof AdminIntVideosPlaybackResponses];

export type AdminIntPartnerCredentialsCreateData = {
    body: IntPartnerCredentialIn;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/credentials';
};

export type AdminIntPartnerCredentialsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerCredentialsCreateError = AdminIntPartnerCredentialsCreateErrors[keyof AdminIntPartnerCredentialsCreateErrors];

export type AdminIntPartnerCredentialsCreateResponses = {
    /**
     * Successful Response
     */
    201: IntPartnerCredentialCreated;
};

export type AdminIntPartnerCredentialsCreateResponse = AdminIntPartnerCredentialsCreateResponses[keyof AdminIntPartnerCredentialsCreateResponses];

export type AdminIntPartnerCredentialsRevokeData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
        /**
         * Key Id
         */
        key_id: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/credentials/{key_id}';
};

export type AdminIntPartnerCredentialsRevokeErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerCredentialsRevokeError = AdminIntPartnerCredentialsRevokeErrors[keyof AdminIntPartnerCredentialsRevokeErrors];

export type AdminIntPartnerCredentialsRevokeResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminIntPartnerCredentialsRevokeResponse = AdminIntPartnerCredentialsRevokeResponses[keyof AdminIntPartnerCredentialsRevokeResponses];

export type AdminIntPartnerSigningRotateData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/signing-keys/rotate';
};

export type AdminIntPartnerSigningRotateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerSigningRotateError = AdminIntPartnerSigningRotateErrors[keyof AdminIntPartnerSigningRotateErrors];

export type AdminIntPartnerSigningRotateResponses = {
    /**
     * Successful Response
     */
    201: IntPartnerSigningKeyCreated;
};

export type AdminIntPartnerSigningRotateResponse = AdminIntPartnerSigningRotateResponses[keyof AdminIntPartnerSigningRotateResponses];

export type AdminIntPartnerWebhookTestData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/webhook/test';
};

export type AdminIntPartnerWebhookTestErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerWebhookTestError = AdminIntPartnerWebhookTestErrors[keyof AdminIntPartnerWebhookTestErrors];

export type AdminIntPartnerWebhookTestResponses = {
    /**
     * Successful Response
     */
    200: IntPartnerWebhookTestOut;
};

export type AdminIntPartnerWebhookTestResponse = AdminIntPartnerWebhookTestResponses[keyof AdminIntPartnerWebhookTestResponses];

export type AdminIntPartnerWebhookResendData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
        /**
         * Event Id
         */
        event_id: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/webhooks/{event_id}/resend';
};

export type AdminIntPartnerWebhookResendErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerWebhookResendError = AdminIntPartnerWebhookResendErrors[keyof AdminIntPartnerWebhookResendErrors];

export type AdminIntPartnerWebhookResendResponses = {
    /**
     * Successful Response
     */
    202: unknown;
};

export type AdminIntPartnerCatalogNotifyAllData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/catalog/notify-all';
};

export type AdminIntPartnerCatalogNotifyAllErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerCatalogNotifyAllError = AdminIntPartnerCatalogNotifyAllErrors[keyof AdminIntPartnerCatalogNotifyAllErrors];

export type AdminIntPartnerCatalogNotifyAllResponses = {
    /**
     * Successful Response
     */
    202: IntPartnerNotifyAllOut;
};

export type AdminIntPartnerCatalogNotifyAllResponse = AdminIntPartnerCatalogNotifyAllResponses[keyof AdminIntPartnerCatalogNotifyAllResponses];

export type AdminIntPartnerCatalogStateListData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: created_at, title, updated_at
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[View]
         *
         * Все / Нет у партнёра / Не сопоставлены / Сняты (not_at_partner, unmapped, removed)
         */
        'filter[view]'?: string | null;
        /**
         * Filter[View][]
         *
         * Все / Нет у партнёра / Не сопоставлены / Сняты (not_at_partner, unmapped, removed); несколько значений
         */
        'filter[view][]'?: Array<string> | null;
        /**
         * Filter[Item Type]
         *
         * Фильтр по item_type (wine, set, drop, blind_kit)
         */
        'filter[item_type]'?: string | null;
        /**
         * Filter[Item Type][]
         *
         * Фильтр по item_type (wine, set, drop, blind_kit); несколько значений
         */
        'filter[item_type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/integrations/{key}/catalog-state';
};

export type AdminIntPartnerCatalogStateListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerCatalogStateListError = AdminIntPartnerCatalogStateListErrors[keyof AdminIntPartnerCatalogStateListErrors];

export type AdminIntPartnerCatalogStateListResponses = {
    /**
     * Successful Response
     */
    200: IntPartnerCatalogStatePage;
};

export type AdminIntPartnerCatalogStateListResponse = AdminIntPartnerCatalogStateListResponses[keyof AdminIntPartnerCatalogStateListResponses];

export type AdminIntPartnerCatalogStateUpdateData = {
    body: IntPartnerSkuIn;
    path: {
        /**
         * Key
         */
        key: string;
        /**
         * Item Type
         */
        item_type: 'wine' | 'set' | 'drop' | 'blind_kit';
        /**
         * Item Id
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/catalog-state/{item_type}/{item_id}';
};

export type AdminIntPartnerCatalogStateUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerCatalogStateUpdateError = AdminIntPartnerCatalogStateUpdateErrors[keyof AdminIntPartnerCatalogStateUpdateErrors];

export type AdminIntPartnerCatalogStateUpdateResponses = {
    /**
     * Successful Response
     */
    200: IntPartnerCatalogStateRow;
};

export type AdminIntPartnerCatalogStateUpdateResponse = AdminIntPartnerCatalogStateUpdateResponses[keyof AdminIntPartnerCatalogStateUpdateResponses];

export type AdminIntPartnerHandoffsListData = {
    body?: never;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: {
        /**
         * Page
         *
         * Номер страницы, с 1
         */
        page?: number;
        /**
         * Page Size
         *
         * Размер страницы: 25, 50 или 100
         */
        page_size?: 25 | 50 | 100;
        /**
         * Sort
         *
         * Поле сортировки, `-` — по убыванию: amount, created_at, status
         */
        sort?: string | null;
        /**
         * Q
         *
         * Поиск
         */
        q?: string | null;
        /**
         * Filter[Status]
         *
         * Фильтр по status (issued, verified, held, order_received, expired, revoked)
         */
        'filter[status]'?: string | null;
        /**
         * Filter[Status][]
         *
         * Фильтр по status (issued, verified, held, order_received, expired, revoked); несколько значений
         */
        'filter[status][]'?: Array<string> | null;
        /**
         * Filter[View]
         *
         * Все / Без заказа / С заказом / Истекли (without_order, with_order, expired)
         */
        'filter[view]'?: string | null;
        /**
         * Filter[View][]
         *
         * Все / Без заказа / С заказом / Истекли (without_order, with_order, expired); несколько значений
         */
        'filter[view][]'?: Array<string> | null;
        /**
         * Filter[Item Type]
         *
         * Фильтр по item_type
         */
        'filter[item_type]'?: string | null;
        /**
         * Filter[Item Type][]
         *
         * Фильтр по item_type; несколько значений
         */
        'filter[item_type][]'?: Array<string> | null;
    };
    url: '/api/v1/admin/integrations/{key}/handoffs';
};

export type AdminIntPartnerHandoffsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerHandoffsListError = AdminIntPartnerHandoffsListErrors[keyof AdminIntPartnerHandoffsListErrors];

export type AdminIntPartnerHandoffsListResponses = {
    /**
     * Successful Response
     */
    200: IntPartnerHandoffPage;
};

export type AdminIntPartnerHandoffsListResponse = AdminIntPartnerHandoffsListResponses[keyof AdminIntPartnerHandoffsListResponses];

export type AdminIntPartnerTestLinkData = {
    body: IntPartnerTestLinkIn;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/test-link';
};

export type AdminIntPartnerTestLinkErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntPartnerTestLinkError = AdminIntPartnerTestLinkErrors[keyof AdminIntPartnerTestLinkErrors];

export type AdminIntPartnerTestLinkResponses = {
    /**
     * Successful Response
     */
    200: IntPartnerTestLinkOut;
};

export type AdminIntPartnerTestLinkResponse = AdminIntPartnerTestLinkResponses[keyof AdminIntPartnerTestLinkResponses];

export type AdminIntPartnerSignatureCheckData = {
    body: IntPartnerSignatureCheckIn;
    path: {
        /**
         * Key
         */
        key: string;
    };
    query?: never;
    url: '/api/v1/admin/integrations/{key}/signature-check';
};

export type AdminIntPartnerSignatureCheckErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPartnerSignatureCheckError = AdminIntPartnerSignatureCheckErrors[keyof AdminIntPartnerSignatureCheckErrors];

export type AdminIntPartnerSignatureCheckResponses = {
    /**
     * Successful Response
     */
    200: IntPartnerSignatureCheckOut;
};

export type AdminIntPartnerSignatureCheckResponse = AdminIntPartnerSignatureCheckResponses[keyof AdminIntPartnerSignatureCheckResponses];

export type AdminIntShipmentsGetData = {
    body?: never;
    path: {
        /**
         * Shipment Id
         */
        shipment_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipments/{shipment_id}';
};

export type AdminIntShipmentsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipmentsGetError = AdminIntShipmentsGetErrors[keyof AdminIntShipmentsGetErrors];

export type AdminIntShipmentsGetResponses = {
    /**
     * Successful Response
     */
    200: IntShipmentOut;
};

export type AdminIntShipmentsGetResponse = AdminIntShipmentsGetResponses[keyof AdminIntShipmentsGetResponses];

export type AdminIntShipmentsUpdateData = {
    body: IntShipmentPatchIn;
    path: {
        /**
         * Shipment Id
         */
        shipment_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipments/{shipment_id}';
};

export type AdminIntShipmentsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipmentsUpdateError = AdminIntShipmentsUpdateErrors[keyof AdminIntShipmentsUpdateErrors];

export type AdminIntShipmentsUpdateResponses = {
    /**
     * Successful Response
     */
    200: IntShipmentOut;
};

export type AdminIntShipmentsUpdateResponse = AdminIntShipmentsUpdateResponses[keyof AdminIntShipmentsUpdateResponses];

export type AdminIntShipmentsLabelData = {
    body?: never;
    path: {
        /**
         * Shipment Id
         */
        shipment_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipments/{shipment_id}/label';
};

export type AdminIntShipmentsLabelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipmentsLabelError = AdminIntShipmentsLabelErrors[keyof AdminIntShipmentsLabelErrors];

export type AdminIntShipmentsLabelResponses = {
    /**
     * Successful Response
     */
    200: Blob | File;
};

export type AdminIntShipmentsLabelResponse = AdminIntShipmentsLabelResponses[keyof AdminIntShipmentsLabelResponses];

export type AdminIntShipmentsRetryData = {
    body: IntShipmentRetryIn;
    path: {
        /**
         * Shipment Id
         */
        shipment_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipments/{shipment_id}/retry';
};

export type AdminIntShipmentsRetryErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipmentsRetryError = AdminIntShipmentsRetryErrors[keyof AdminIntShipmentsRetryErrors];

export type AdminIntShipmentsRetryResponses = {
    /**
     * Successful Response
     */
    202: IntShipmentAccepted;
};

export type AdminIntShipmentsRetryResponse = AdminIntShipmentsRetryResponses[keyof AdminIntShipmentsRetryResponses];

export type AdminIntShipmentsCancelData = {
    body?: never;
    path: {
        /**
         * Shipment Id
         */
        shipment_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipments/{shipment_id}/cancel';
};

export type AdminIntShipmentsCancelErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipmentsCancelError = AdminIntShipmentsCancelErrors[keyof AdminIntShipmentsCancelErrors];

export type AdminIntShipmentsCancelResponses = {
    /**
     * Successful Response
     */
    200: IntShipmentOut;
};

export type AdminIntShipmentsCancelResponse = AdminIntShipmentsCancelResponses[keyof AdminIntShipmentsCancelResponses];

export type AdminIntShipmentsRefreshTrackingData = {
    body?: never;
    path: {
        /**
         * Shipment Id
         */
        shipment_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipments/{shipment_id}/refresh-tracking';
};

export type AdminIntShipmentsRefreshTrackingErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntShipmentsRefreshTrackingError = AdminIntShipmentsRefreshTrackingErrors[keyof AdminIntShipmentsRefreshTrackingErrors];

export type AdminIntShipmentsRefreshTrackingResponses = {
    /**
     * Successful Response
     */
    200: IntShipmentTrackingOut;
};

export type AdminIntShipmentsRefreshTrackingResponse = AdminIntShipmentsRefreshTrackingResponses[keyof AdminIntShipmentsRefreshTrackingResponses];

export type AdminIntPickupsCreateData = {
    body: IntPickupIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/pickups';
};

export type AdminIntPickupsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntPickupsCreateError = AdminIntPickupsCreateErrors[keyof AdminIntPickupsCreateErrors];

export type AdminIntPickupsCreateResponses = {
    /**
     * Successful Response
     */
    201: IntPickupOut;
};

export type AdminIntPickupsCreateResponse = AdminIntPickupsCreateResponses[keyof AdminIntPickupsCreateResponses];

export type AdminIntPickupsDeleteData = {
    body?: never;
    path: {
        /**
         * Pickup Id
         */
        pickup_id: string;
    };
    query?: never;
    url: '/api/v1/admin/pickups/{pickup_id}';
};

export type AdminIntPickupsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type AdminIntPickupsDeleteError = AdminIntPickupsDeleteErrors[keyof AdminIntPickupsDeleteErrors];

export type AdminIntPickupsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminIntPickupsDeleteResponse = AdminIntPickupsDeleteResponses[keyof AdminIntPickupsDeleteResponses];

export type AdminIntShipperLocationsListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/shipper-locations';
};

export type AdminIntShipperLocationsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
};

export type AdminIntShipperLocationsListError = AdminIntShipperLocationsListErrors[keyof AdminIntShipperLocationsListErrors];

export type AdminIntShipperLocationsListResponses = {
    /**
     * Successful Response
     */
    200: IntShipperLocationsPage;
};

export type AdminIntShipperLocationsListResponse = AdminIntShipperLocationsListResponses[keyof AdminIntShipperLocationsListResponses];

export type AdminIntShipperLocationsCreateData = {
    body: IntShipperLocationIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/shipper-locations';
};

export type AdminIntShipperLocationsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipperLocationsCreateError = AdminIntShipperLocationsCreateErrors[keyof AdminIntShipperLocationsCreateErrors];

export type AdminIntShipperLocationsCreateResponses = {
    /**
     * Successful Response
     */
    201: IntShipperLocationOut;
};

export type AdminIntShipperLocationsCreateResponse = AdminIntShipperLocationsCreateResponses[keyof AdminIntShipperLocationsCreateResponses];

export type AdminIntShipperLocationsDeleteData = {
    body?: never;
    path: {
        /**
         * Location Id
         */
        location_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipper-locations/{location_id}';
};

export type AdminIntShipperLocationsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipperLocationsDeleteError = AdminIntShipperLocationsDeleteErrors[keyof AdminIntShipperLocationsDeleteErrors];

export type AdminIntShipperLocationsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminIntShipperLocationsDeleteResponse = AdminIntShipperLocationsDeleteResponses[keyof AdminIntShipperLocationsDeleteResponses];

export type AdminIntShipperLocationsGetData = {
    body?: never;
    path: {
        /**
         * Location Id
         */
        location_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipper-locations/{location_id}';
};

export type AdminIntShipperLocationsGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipperLocationsGetError = AdminIntShipperLocationsGetErrors[keyof AdminIntShipperLocationsGetErrors];

export type AdminIntShipperLocationsGetResponses = {
    /**
     * Successful Response
     */
    200: IntShipperLocationOut;
};

export type AdminIntShipperLocationsGetResponse = AdminIntShipperLocationsGetResponses[keyof AdminIntShipperLocationsGetResponses];

export type AdminIntShipperLocationsUpdateData = {
    body: IntShipperLocationIn;
    path: {
        /**
         * Location Id
         */
        location_id: string;
    };
    query?: never;
    url: '/api/v1/admin/shipper-locations/{location_id}';
};

export type AdminIntShipperLocationsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntShipperLocationsUpdateError = AdminIntShipperLocationsUpdateErrors[keyof AdminIntShipperLocationsUpdateErrors];

export type AdminIntShipperLocationsUpdateResponses = {
    /**
     * Successful Response
     */
    200: IntShipperLocationOut;
};

export type AdminIntShipperLocationsUpdateResponse = AdminIntShipperLocationsUpdateResponses[keyof AdminIntShipperLocationsUpdateResponses];

export type AdminIntPackagePresetsListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/package-presets';
};

export type AdminIntPackagePresetsListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
};

export type AdminIntPackagePresetsListError = AdminIntPackagePresetsListErrors[keyof AdminIntPackagePresetsListErrors];

export type AdminIntPackagePresetsListResponses = {
    /**
     * Successful Response
     */
    200: IntPackagePresetsPage;
};

export type AdminIntPackagePresetsListResponse = AdminIntPackagePresetsListResponses[keyof AdminIntPackagePresetsListResponses];

export type AdminIntPackagePresetsCreateData = {
    body: IntPackagePresetIn;
    path?: never;
    query?: never;
    url: '/api/v1/admin/package-presets';
};

export type AdminIntPackagePresetsCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPackagePresetsCreateError = AdminIntPackagePresetsCreateErrors[keyof AdminIntPackagePresetsCreateErrors];

export type AdminIntPackagePresetsCreateResponses = {
    /**
     * Successful Response
     */
    201: IntPackagePresetOut;
};

export type AdminIntPackagePresetsCreateResponse = AdminIntPackagePresetsCreateResponses[keyof AdminIntPackagePresetsCreateResponses];

export type AdminIntPackagePresetsDeleteData = {
    body?: never;
    path: {
        /**
         * Preset Id
         */
        preset_id: string;
    };
    query?: never;
    url: '/api/v1/admin/package-presets/{preset_id}';
};

export type AdminIntPackagePresetsDeleteErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPackagePresetsDeleteError = AdminIntPackagePresetsDeleteErrors[keyof AdminIntPackagePresetsDeleteErrors];

export type AdminIntPackagePresetsDeleteResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type AdminIntPackagePresetsDeleteResponse = AdminIntPackagePresetsDeleteResponses[keyof AdminIntPackagePresetsDeleteResponses];

export type AdminIntPackagePresetsUpdateData = {
    body: IntPackagePresetIn;
    path: {
        /**
         * Preset Id
         */
        preset_id: string;
    };
    query?: never;
    url: '/api/v1/admin/package-presets/{preset_id}';
};

export type AdminIntPackagePresetsUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntPackagePresetsUpdateError = AdminIntPackagePresetsUpdateErrors[keyof AdminIntPackagePresetsUpdateErrors];

export type AdminIntPackagePresetsUpdateResponses = {
    /**
     * Successful Response
     */
    200: IntPackagePresetOut;
};

export type AdminIntPackagePresetsUpdateResponse = AdminIntPackagePresetsUpdateResponses[keyof AdminIntPackagePresetsUpdateResponses];

export type AdminIntTaxProfilesListData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/admin/destination-tax-profiles';
};

export type AdminIntTaxProfilesListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
};

export type AdminIntTaxProfilesListError = AdminIntTaxProfilesListErrors[keyof AdminIntTaxProfilesListErrors];

export type AdminIntTaxProfilesListResponses = {
    /**
     * Successful Response
     */
    200: IntTaxProfilesPage;
};

export type AdminIntTaxProfilesListResponse = AdminIntTaxProfilesListResponses[keyof AdminIntTaxProfilesListResponses];

export type AdminIntTaxProfilesUpdateData = {
    body: IntTaxProfileIn;
    path: {
        /**
         * Country Code
         */
        country_code: string;
    };
    query?: never;
    url: '/api/v1/admin/destination-tax-profiles/{country_code}';
};

export type AdminIntTaxProfilesUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
};

export type AdminIntTaxProfilesUpdateError = AdminIntTaxProfilesUpdateErrors[keyof AdminIntTaxProfilesUpdateErrors];

export type AdminIntTaxProfilesUpdateResponses = {
    /**
     * Successful Response
     */
    200: IntTaxProfileOut;
};

export type AdminIntTaxProfilesUpdateResponse = AdminIntTaxProfilesUpdateResponses[keyof AdminIntTaxProfilesUpdateResponses];

export type PublicCoreHealthData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/health';
};

export type PublicCoreHealthErrors = {
    /**
     * Недоступна БД или Redis
     */
    503: HealthOut;
};

export type PublicCoreHealthError = PublicCoreHealthErrors[keyof PublicCoreHealthErrors];

export type PublicCoreHealthResponses = {
    /**
     * Successful Response
     */
    200: HealthOut;
};

export type PublicCoreHealthResponse = PublicCoreHealthResponses[keyof PublicCoreHealthResponses];

export type WebhooksIntAppleIdData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/apple-id';
};

export type WebhooksIntAppleIdErrors = {
    /**
     * Подпись не прошла проверку
     */
    401: unknown;
    /**
     * Интеграция не настроена или выключена
     */
    503: unknown;
};

export type WebhooksIntAppleIdResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntPaymentSberGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/sber';
};

export type WebhooksIntPaymentSberGetErrors = {
    /**
     * Подпись не прошла проверку
     */
    400: unknown;
    /**
     * Тело уведомления больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntPaymentSberGetError = WebhooksIntPaymentSberGetErrors[keyof WebhooksIntPaymentSberGetErrors];

export type WebhooksIntPaymentSberGetResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntPaymentSberData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/sber';
};

export type WebhooksIntPaymentSberErrors = {
    /**
     * Подпись не прошла проверку
     */
    400: unknown;
    /**
     * Тело уведомления больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntPaymentSberError = WebhooksIntPaymentSberErrors[keyof WebhooksIntPaymentSberErrors];

export type WebhooksIntPaymentSberResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntPaymentTbankData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/tbank';
};

export type WebhooksIntPaymentTbankErrors = {
    /**
     * Подпись не прошла проверку
     */
    400: unknown;
    /**
     * Тело уведомления больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntPaymentTbankError = WebhooksIntPaymentTbankErrors[keyof WebhooksIntPaymentTbankErrors];

export type WebhooksIntPaymentTbankResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntPaymentAlfaGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/alfa';
};

export type WebhooksIntPaymentAlfaGetErrors = {
    /**
     * Подпись не прошла проверку
     */
    400: unknown;
    /**
     * Тело уведомления больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntPaymentAlfaGetError = WebhooksIntPaymentAlfaGetErrors[keyof WebhooksIntPaymentAlfaGetErrors];

export type WebhooksIntPaymentAlfaGetResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntPaymentAlfaData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/alfa';
};

export type WebhooksIntPaymentAlfaErrors = {
    /**
     * Подпись не прошла проверку
     */
    400: unknown;
    /**
     * Тело уведомления больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntPaymentAlfaError = WebhooksIntPaymentAlfaErrors[keyof WebhooksIntPaymentAlfaErrors];

export type WebhooksIntPaymentAlfaResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntPaymentStripeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/stripe';
};

export type WebhooksIntPaymentStripeErrors = {
    /**
     * Подпись не прошла проверку
     */
    400: unknown;
    /**
     * Тело уведомления больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntPaymentStripeError = WebhooksIntPaymentStripeErrors[keyof WebhooksIntPaymentStripeErrors];

export type WebhooksIntPaymentStripeResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntBillingAppStoreData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/appstore';
};

export type WebhooksIntBillingAppStoreErrors = {
    /**
     * Подпись не прошла проверку
     */
    400: unknown;
    /**
     * Тело больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntBillingAppStoreError = WebhooksIntBillingAppStoreErrors[keyof WebhooksIntBillingAppStoreErrors];

export type WebhooksIntBillingAppStoreResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntBillingGooglePlayData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/googleplay';
};

export type WebhooksIntBillingGooglePlayErrors = {
    /**
     * Токен Pub/Sub не прошёл проверку
     */
    401: unknown;
    /**
     * Тело больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntBillingGooglePlayError = WebhooksIntBillingGooglePlayErrors[keyof WebhooksIntBillingGooglePlayErrors];

export type WebhooksIntBillingGooglePlayResponses = {
    /**
     * Successful Response
     */
    200: unknown;
    /**
     * Принято
     */
    204: void;
};

export type WebhooksIntBillingGooglePlayResponse = WebhooksIntBillingGooglePlayResponses[keyof WebhooksIntBillingGooglePlayResponses];

export type WebhooksIntBillingRustoreData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/rustore';
};

export type WebhooksIntBillingRustoreErrors = {
    /**
     * Уведомление не расшифровывается
     */
    400: unknown;
    /**
     * Нет секрета `k` в адресе
     */
    404: unknown;
    /**
     * Тело больше 256 КБ
     */
    413: unknown;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type WebhooksIntBillingRustoreError = WebhooksIntBillingRustoreErrors[keyof WebhooksIntBillingRustoreErrors];

export type WebhooksIntBillingRustoreResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type WebhooksIntVideoMuxData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/mux';
};

export type WebhooksIntVideoMuxErrors = {
    /**
     * Подпись / секрет не прошли проверку
     */
    401: unknown;
    /**
     * Видеохостинг другого рынка
     */
    404: unknown;
    /**
     * Тело больше 256 КБ
     */
    413: unknown;
    /**
     * Видеохостинг не настроен или выключен
     */
    503: unknown;
};

export type WebhooksIntVideoMuxResponses = {
    /**
     * Принято (или повтор события)
     */
    200: unknown;
};

export type WebhooksIntVideoKinescopeData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/kinescope';
};

export type WebhooksIntVideoKinescopeErrors = {
    /**
     * Подпись / секрет не прошли проверку
     */
    401: unknown;
    /**
     * Видеохостинг другого рынка
     */
    404: unknown;
    /**
     * Тело больше 256 КБ
     */
    413: unknown;
    /**
     * Видеохостинг не настроен или выключен
     */
    503: unknown;
};

export type WebhooksIntVideoKinescopeResponses = {
    /**
     * Принято (или повтор события)
     */
    200: unknown;
};

export type WebhooksIntDhlData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/webhooks/dhl';
};

export type WebhooksIntDhlErrors = {
    /**
     * Тело не разобрано
     */
    400: unknown;
    /**
     * Неверный секрет
     */
    401: unknown;
    /**
     * Трекинг DHL не подключён на инсталляции
     */
    404: unknown;
    /**
     * Тело больше 512 КБ
     */
    413: unknown;
};

export type WebhooksIntDhlResponses = {
    /**
     * Принято
     */
    200: unknown;
};

export type PartnerIntPingData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/partner/ping';
};

export type PartnerIntPingErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntPingError = PartnerIntPingErrors[keyof PartnerIntPingErrors];

export type PartnerIntPingResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiPingOut;
};

export type PartnerIntPingResponse = PartnerIntPingResponses[keyof PartnerIntPingResponses];

export type PartnerIntJwksData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/v1/partner/jwks';
};

export type PartnerIntJwksErrors = {
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
};

export type PartnerIntJwksError = PartnerIntJwksErrors[keyof PartnerIntJwksErrors];

export type PartnerIntJwksResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiJwksOut;
};

export type PartnerIntJwksResponse = PartnerIntJwksResponses[keyof PartnerIntJwksResponses];

export type PartnerIntCatalogListData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Updated Since
         */
        updated_since?: string | null;
        /**
         * Type
         */
        type?: 'wine' | 'set' | 'drop' | 'blind_kit' | null;
        /**
         * Cursor
         */
        cursor?: string | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v1/partner/catalog';
};

export type PartnerIntCatalogListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntCatalogListError = PartnerIntCatalogListErrors[keyof PartnerIntCatalogListErrors];

export type PartnerIntCatalogListResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiCatalogPage;
};

export type PartnerIntCatalogListResponse = PartnerIntCatalogListResponses[keyof PartnerIntCatalogListResponses];

export type PartnerIntCatalogAvailabilityData = {
    body: PartnerApiAvailabilityIn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * uuid или строка до 128 символов; повтор в течение 7 суток возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/partner/catalog/availability';
};

export type PartnerIntCatalogAvailabilityErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntCatalogAvailabilityError = PartnerIntCatalogAvailabilityErrors[keyof PartnerIntCatalogAvailabilityErrors];

export type PartnerIntCatalogAvailabilityResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiAvailabilityOut;
};

export type PartnerIntCatalogAvailabilityResponse = PartnerIntCatalogAvailabilityResponses[keyof PartnerIntCatalogAvailabilityResponses];

export type PartnerIntCatalogGetData = {
    body?: never;
    path: {
        /**
         * Item Id
         */
        item_id: string;
    };
    query?: never;
    url: '/api/v1/partner/catalog/{item_id}';
};

export type PartnerIntCatalogGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntCatalogGetError = PartnerIntCatalogGetErrors[keyof PartnerIntCatalogGetErrors];

export type PartnerIntCatalogGetResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiCatalogItem;
};

export type PartnerIntCatalogGetResponse = PartnerIntCatalogGetResponses[keyof PartnerIntCatalogGetResponses];

export type PartnerIntRefsVerifyData = {
    body: PartnerApiVerifyIn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * uuid или строка до 128 символов; повтор в течение 7 суток возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/partner/refs/verify';
};

export type PartnerIntRefsVerifyErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntRefsVerifyError = PartnerIntRefsVerifyErrors[keyof PartnerIntRefsVerifyErrors];

export type PartnerIntRefsVerifyResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiVerifyOut;
};

export type PartnerIntRefsVerifyResponse = PartnerIntRefsVerifyResponses[keyof PartnerIntRefsVerifyResponses];

export type PartnerIntHoldsReleaseData = {
    body?: never;
    path: {
        /**
         * Hold Id
         */
        hold_id: string;
    };
    query?: never;
    url: '/api/v1/partner/holds/{hold_id}';
};

export type PartnerIntHoldsReleaseErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntHoldsReleaseError = PartnerIntHoldsReleaseErrors[keyof PartnerIntHoldsReleaseErrors];

export type PartnerIntHoldsReleaseResponses = {
    /**
     * Successful Response
     */
    204: void;
};

export type PartnerIntHoldsReleaseResponse = PartnerIntHoldsReleaseResponses[keyof PartnerIntHoldsReleaseResponses];

export type PartnerIntOrdersListData = {
    body?: never;
    path?: never;
    query: {
        /**
         * Updated From
         */
        updated_from: string;
        /**
         * Updated To
         */
        updated_to: string;
        /**
         * Status
         */
        status?: 'awaiting_payment' | 'paid' | 'assembling' | 'handed_to_carrier' | 'in_delivery' | 'delivered' | 'cancelled' | 'refunded' | null;
        /**
         * Cursor
         */
        cursor?: string | null;
        /**
         * Limit
         */
        limit?: number;
    };
    url: '/api/v1/partner/orders';
};

export type PartnerIntOrdersListErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntOrdersListError = PartnerIntOrdersListErrors[keyof PartnerIntOrdersListErrors];

export type PartnerIntOrdersListResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiOrdersPage;
};

export type PartnerIntOrdersListResponse = PartnerIntOrdersListResponses[keyof PartnerIntOrdersListResponses];

export type PartnerIntOrdersCreateData = {
    body: PartnerApiOrderIn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * uuid или строка до 128 символов; повтор в течение 7 суток возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path?: never;
    query?: never;
    url: '/api/v1/partner/orders';
};

export type PartnerIntOrdersCreateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntOrdersCreateError = PartnerIntOrdersCreateErrors[keyof PartnerIntOrdersCreateErrors];

export type PartnerIntOrdersCreateResponses = {
    /**
     * OK
     */
    200: PartnerApiOrderCreated;
    /**
     * Successful Response
     */
    201: PartnerApiOrderCreated;
};

export type PartnerIntOrdersCreateResponse = PartnerIntOrdersCreateResponses[keyof PartnerIntOrdersCreateResponses];

export type PartnerIntOrdersGetData = {
    body?: never;
    path: {
        /**
         * Partner Order Number
         *
         * Номер у партнёра
         */
        partner_order_number: string;
    };
    query?: never;
    url: '/api/v1/partner/orders/{partner_order_number}';
};

export type PartnerIntOrdersGetErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntOrdersGetError = PartnerIntOrdersGetErrors[keyof PartnerIntOrdersGetErrors];

export type PartnerIntOrdersGetResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiOrderOut;
};

export type PartnerIntOrdersGetResponse = PartnerIntOrdersGetResponses[keyof PartnerIntOrdersGetResponses];

export type PartnerIntOrdersUpdateData = {
    body: PartnerApiOrderPatchIn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * uuid или строка до 128 символов; повтор в течение 7 суток возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * Partner Order Number
         *
         * Номер у партнёра
         */
        partner_order_number: string;
    };
    query?: never;
    url: '/api/v1/partner/orders/{partner_order_number}';
};

export type PartnerIntOrdersUpdateErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntOrdersUpdateError = PartnerIntOrdersUpdateErrors[keyof PartnerIntOrdersUpdateErrors];

export type PartnerIntOrdersUpdateResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiOrderOut;
};

export type PartnerIntOrdersUpdateResponse = PartnerIntOrdersUpdateResponses[keyof PartnerIntOrdersUpdateResponses];

export type PartnerIntOrdersEventData = {
    body: PartnerApiEventIn;
    headers?: {
        /**
         * Idempotency-Key
         *
         * uuid или строка до 128 символов; повтор в течение 7 суток возвращает исходный ответ
         */
        'Idempotency-Key'?: string | null;
    };
    path: {
        /**
         * Partner Order Number
         *
         * Номер у партнёра
         */
        partner_order_number: string;
    };
    query?: never;
    url: '/api/v1/partner/orders/{partner_order_number}/events';
};

export type PartnerIntOrdersEventErrors = {
    /**
     * Не авторизован (`unauthorized`, коды модуля)
     */
    401: ErrorResponse;
    /**
     * Нет прав (`forbidden`, коды модуля)
     */
    403: ErrorResponse;
    /**
     * Не найдено (`not_found`)
     */
    404: ErrorResponse;
    /**
     * Конфликт состояния (`conflict`, коды модуля)
     */
    409: ErrorResponse;
    /**
     * Ошибка
     */
    413: ErrorResponse;
    /**
     * Ошибка входных данных (`validation_error` с `details.fields`, коды модуля)
     */
    422: ErrorResponse;
    /**
     * Превышен лимит частоты (`rate_limited`, заголовок `Retry-After`)
     */
    429: ErrorResponse;
    /**
     * Сервис недоступен (`service_unavailable`)
     */
    503: ErrorResponse;
};

export type PartnerIntOrdersEventError = PartnerIntOrdersEventErrors[keyof PartnerIntOrdersEventErrors];

export type PartnerIntOrdersEventResponses = {
    /**
     * Successful Response
     */
    200: PartnerApiEventOut;
};

export type PartnerIntOrdersEventResponse = PartnerIntOrdersEventResponses[keyof PartnerIntOrdersEventResponses];
